/*!
 * jQuery JavaScript Library v1.12.4
 * http://jquery.com/
 *
 * Includes Sizzle.js
 * http://sizzlejs.com/
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license
 * http://jquery.org/license
 *
 * Date: 2016-05-20T17:17Z
 */


(function( global, factory ) {

	if ( typeof module === "object" && typeof module.exports === "object" ) {
		// For CommonJS and CommonJS-like environments where a proper `window`
		// is present, execute the factory and get jQuery.
		// For environments that do not have a `window` with a `document`
		// (such as Node.js), expose a factory as module.exports.
		// This accentuates the need for the creation of a real `window`.
		// e.g. var jQuery = require("jquery")(window);
		// See ticket #14549 for more info.
		module.exports = global.document ?
			factory( global, true ) :
			function( w ) {
				if ( !w.document ) {
					throw new Error( "jQuery requires a window with a document" );
				}
				return factory( w );
			};
	} else {
		factory( global );
	}

// Pass this if window is not defined yet
}(typeof window !== "undefined" ? window : this, function( window, noGlobal ) {

// Support: Firefox 18+
// Can't be in strict mode, several libs including ASP.NET trace
// the stack via arguments.caller.callee and Firefox dies if
// you try to trace through "use strict" call chains. (#13335)
//"use strict";
var deletedIds = [];

var document = window.document;

var slice = deletedIds.slice;

var concat = deletedIds.concat;

var push = deletedIds.push;

var indexOf = deletedIds.indexOf;

var class2type = {};

var toString = class2type.toString;

var hasOwn = class2type.hasOwnProperty;

var support = {};



var
	version = "1.12.4",

	// Define a local copy of jQuery
	jQuery = function( selector, context ) {

		// The jQuery object is actually just the init constructor 'enhanced'
		// Need init if jQuery is called (just allow error to be thrown if not included)
		return new jQuery.fn.init( selector, context );
	},

	// Support: Android<4.1, IE<9
	// Make sure we trim BOM and NBSP
	rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g,

	// Matches dashed string for camelizing
	rmsPrefix = /^-ms-/,
	rdashAlpha = /-([\da-z])/gi,

	// Used by jQuery.camelCase as callback to replace()
	fcamelCase = function( all, letter ) {
		return letter.toUpperCase();
	};

jQuery.fn = jQuery.prototype = {

	// The current version of jQuery being used
	jquery: version,

	constructor: jQuery,

	// Start with an empty selector
	selector: "",

	// The default length of a jQuery object is 0
	length: 0,

	toArray: function() {
		return slice.call( this );
	},

	// Get the Nth element in the matched element set OR
	// Get the whole matched element set as a clean array
	get: function( num ) {
		return num != null ?

			// Return just the one element from the set
			( num < 0 ? this[ num + this.length ] : this[ num ] ) :

			// Return all the elements in a clean array
			slice.call( this );
	},

	// Take an array of elements and push it onto the stack
	// (returning the new matched element set)
	pushStack: function( elems ) {

		// Build a new jQuery matched element set
		var ret = jQuery.merge( this.constructor(), elems );

		// Add the old object onto the stack (as a reference)
		ret.prevObject = this;
		ret.context = this.context;

		// Return the newly-formed element set
		return ret;
	},

	// Execute a callback for every element in the matched set.
	each: function( callback ) {
		return jQuery.each( this, callback );
	},

	map: function( callback ) {
		return this.pushStack( jQuery.map( this, function( elem, i ) {
			return callback.call( elem, i, elem );
		} ) );
	},

	slice: function() {
		return this.pushStack( slice.apply( this, arguments ) );
	},

	first: function() {
		return this.eq( 0 );
	},

	last: function() {
		return this.eq( -1 );
	},

	eq: function( i ) {
		var len = this.length,
			j = +i + ( i < 0 ? len : 0 );
		return this.pushStack( j >= 0 && j < len ? [ this[ j ] ] : [] );
	},

	end: function() {
		return this.prevObject || this.constructor();
	},

	// For internal use only.
	// Behaves like an Array's method, not like a jQuery method.
	push: push,
	sort: deletedIds.sort,
	splice: deletedIds.splice
};

jQuery.extend = jQuery.fn.extend = function() {
	var src, copyIsArray, copy, name, options, clone,
		target = arguments[ 0 ] || {},
		i = 1,
		length = arguments.length,
		deep = false;

	// Handle a deep copy situation
	if ( typeof target === "boolean" ) {
		deep = target;

		// skip the boolean and the target
		target = arguments[ i ] || {};
		i++;
	}

	// Handle case when target is a string or something (possible in deep copy)
	if ( typeof target !== "object" && !jQuery.isFunction( target ) ) {
		target = {};
	}

	// extend jQuery itself if only one argument is passed
	if ( i === length ) {
		target = this;
		i--;
	}

	for ( ; i < length; i++ ) {

		// Only deal with non-null/undefined values
		if ( ( options = arguments[ i ] ) != null ) {

			// Extend the base object
			for ( name in options ) {
				src = target[ name ];
				copy = options[ name ];

				// Prevent never-ending loop
				if ( target === copy ) {
					continue;
				}

				// Recurse if we're merging plain objects or arrays
				if ( deep && copy && ( jQuery.isPlainObject( copy ) ||
					( copyIsArray = jQuery.isArray( copy ) ) ) ) {

					if ( copyIsArray ) {
						copyIsArray = false;
						clone = src && jQuery.isArray( src ) ? src : [];

					} else {
						clone = src && jQuery.isPlainObject( src ) ? src : {};
					}

					// Never move original objects, clone them
					target[ name ] = jQuery.extend( deep, clone, copy );

				// Don't bring in undefined values
				} else if ( copy !== undefined ) {
					target[ name ] = copy;
				}
			}
		}
	}

	// Return the modified object
	return target;
};

jQuery.extend( {

	// Unique for each copy of jQuery on the page
	expando: "jQuery" + ( version + Math.random() ).replace( /\D/g, "" ),

	// Assume jQuery is ready without the ready module
	isReady: true,

	error: function( msg ) {
		throw new Error( msg );
	},

	noop: function() {},

	// See test/unit/core.js for details concerning isFunction.
	// Since version 1.3, DOM methods and functions like alert
	// aren't supported. They return false on IE (#2968).
	isFunction: function( obj ) {
		return jQuery.type( obj ) === "function";
	},

	isArray: Array.isArray || function( obj ) {
		return jQuery.type( obj ) === "array";
	},

	isWindow: function( obj ) {
		/* jshint eqeqeq: false */
		return obj != null && obj == obj.window;
	},

	isNumeric: function( obj ) {

		// parseFloat NaNs numeric-cast false positives (null|true|false|"")
		// ...but misinterprets leading-number strings, particularly hex literals ("0x...")
		// subtraction forces infinities to NaN
		// adding 1 corrects loss of precision from parseFloat (#15100)
		var realStringObj = obj && obj.toString();
		return !jQuery.isArray( obj ) && ( realStringObj - parseFloat( realStringObj ) + 1 ) >= 0;
	},

	isEmptyObject: function( obj ) {
		var name;
		for ( name in obj ) {
			return false;
		}
		return true;
	},

	isPlainObject: function( obj ) {
		var key;

		// Must be an Object.
		// Because of IE, we also have to check the presence of the constructor property.
		// Make sure that DOM nodes and window objects don't pass through, as well
		if ( !obj || jQuery.type( obj ) !== "object" || obj.nodeType || jQuery.isWindow( obj ) ) {
			return false;
		}

		try {

			// Not own constructor property must be Object
			if ( obj.constructor &&
				!hasOwn.call( obj, "constructor" ) &&
				!hasOwn.call( obj.constructor.prototype, "isPrototypeOf" ) ) {
				return false;
			}
		} catch ( e ) {

			// IE8,9 Will throw exceptions on certain host objects #9897
			return false;
		}

		// Support: IE<9
		// Handle iteration over inherited properties before own properties.
		if ( !support.ownFirst ) {
			for ( key in obj ) {
				return hasOwn.call( obj, key );
			}
		}

		// Own properties are enumerated firstly, so to speed up,
		// if last one is own, then all properties are own.
		for ( key in obj ) {}

		return key === undefined || hasOwn.call( obj, key );
	},

	type: function( obj ) {
		if ( obj == null ) {
			return obj + "";
		}
		return typeof obj === "object" || typeof obj === "function" ?
			class2type[ toString.call( obj ) ] || "object" :
			typeof obj;
	},

	// Workarounds based on findings by Jim Driscoll
	// http://weblogs.java.net/blog/driscoll/archive/2009/09/08/eval-javascript-global-context
	globalEval: function( data ) {
		if ( data && jQuery.trim( data ) ) {

			// We use execScript on Internet Explorer
			// We use an anonymous function so that context is window
			// rather than jQuery in Firefox
			( window.execScript || function( data ) {
				window[ "eval" ].call( window, data ); // jscs:ignore requireDotNotation
			} )( data );
		}
	},

	// Convert dashed to camelCase; used by the css and data modules
	// Microsoft forgot to hump their vendor prefix (#9572)
	camelCase: function( string ) {
		return string.replace( rmsPrefix, "ms-" ).replace( rdashAlpha, fcamelCase );
	},

	nodeName: function( elem, name ) {
		return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();
	},

	each: function( obj, callback ) {
		var length, i = 0;

		if ( isArrayLike( obj ) ) {
			length = obj.length;
			for ( ; i < length; i++ ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		} else {
			for ( i in obj ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		}

		return obj;
	},

	// Support: Android<4.1, IE<9
	trim: function( text ) {
		return text == null ?
			"" :
			( text + "" ).replace( rtrim, "" );
	},

	// results is for internal usage only
	makeArray: function( arr, results ) {
		var ret = results || [];

		if ( arr != null ) {
			if ( isArrayLike( Object( arr ) ) ) {
				jQuery.merge( ret,
					typeof arr === "string" ?
					[ arr ] : arr
				);
			} else {
				push.call( ret, arr );
			}
		}

		return ret;
	},

	inArray: function( elem, arr, i ) {
		var len;

		if ( arr ) {
			if ( indexOf ) {
				return indexOf.call( arr, elem, i );
			}

			len = arr.length;
			i = i ? i < 0 ? Math.max( 0, len + i ) : i : 0;

			for ( ; i < len; i++ ) {

				// Skip accessing in sparse arrays
				if ( i in arr && arr[ i ] === elem ) {
					return i;
				}
			}
		}

		return -1;
	},

	merge: function( first, second ) {
		var len = +second.length,
			j = 0,
			i = first.length;

		while ( j < len ) {
			first[ i++ ] = second[ j++ ];
		}

		// Support: IE<9
		// Workaround casting of .length to NaN on otherwise arraylike objects (e.g., NodeLists)
		if ( len !== len ) {
			while ( second[ j ] !== undefined ) {
				first[ i++ ] = second[ j++ ];
			}
		}

		first.length = i;

		return first;
	},

	grep: function( elems, callback, invert ) {
		var callbackInverse,
			matches = [],
			i = 0,
			length = elems.length,
			callbackExpect = !invert;

		// Go through the array, only saving the items
		// that pass the validator function
		for ( ; i < length; i++ ) {
			callbackInverse = !callback( elems[ i ], i );
			if ( callbackInverse !== callbackExpect ) {
				matches.push( elems[ i ] );
			}
		}

		return matches;
	},

	// arg is for internal usage only
	map: function( elems, callback, arg ) {
		var length, value,
			i = 0,
			ret = [];

		// Go through the array, translating each of the items to their new values
		if ( isArrayLike( elems ) ) {
			length = elems.length;
			for ( ; i < length; i++ ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}

		// Go through every key on the object,
		} else {
			for ( i in elems ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}
		}

		// Flatten any nested arrays
		return concat.apply( [], ret );
	},

	// A global GUID counter for objects
	guid: 1,

	// Bind a function to a context, optionally partially applying any
	// arguments.
	proxy: function( fn, context ) {
		var args, proxy, tmp;

		if ( typeof context === "string" ) {
			tmp = fn[ context ];
			context = fn;
			fn = tmp;
		}

		// Quick check to determine if target is callable, in the spec
		// this throws a TypeError, but we will just return undefined.
		if ( !jQuery.isFunction( fn ) ) {
			return undefined;
		}

		// Simulated bind
		args = slice.call( arguments, 2 );
		proxy = function() {
			return fn.apply( context || this, args.concat( slice.call( arguments ) ) );
		};

		// Set the guid of unique handler to the same of original handler, so it can be removed
		proxy.guid = fn.guid = fn.guid || jQuery.guid++;

		return proxy;
	},

	now: function() {
		return +( new Date() );
	},

	// jQuery.support is not used in Core but other projects attach their
	// properties to it so it needs to exist.
	support: support
} );

// JSHint would error on this code due to the Symbol not being defined in ES5.
// Defining this global in .jshintrc would create a danger of using the global
// unguarded in another place, it seems safer to just disable JSHint for these
// three lines.
/* jshint ignore: start */
if ( typeof Symbol === "function" ) {
	jQuery.fn[ Symbol.iterator ] = deletedIds[ Symbol.iterator ];
}
/* jshint ignore: end */

// Populate the class2type map
jQuery.each( "Boolean Number String Function Array Date RegExp Object Error Symbol".split( " " ),
function( i, name ) {
	class2type[ "[object " + name + "]" ] = name.toLowerCase();
} );

function isArrayLike( obj ) {

	// Support: iOS 8.2 (not reproducible in simulator)
	// `in` check used to prevent JIT error (gh-2145)
	// hasOwn isn't used here due to false negatives
	// regarding Nodelist length in IE
	var length = !!obj && "length" in obj && obj.length,
		type = jQuery.type( obj );

	if ( type === "function" || jQuery.isWindow( obj ) ) {
		return false;
	}

	return type === "array" || length === 0 ||
		typeof length === "number" && length > 0 && ( length - 1 ) in obj;
}
var Sizzle =
/*!
 * Sizzle CSS Selector Engine v2.2.1
 * http://sizzlejs.com/
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license
 * http://jquery.org/license
 *
 * Date: 2015-10-17
 */
(function( window ) {

var i,
	support,
	Expr,
	getText,
	isXML,
	tokenize,
	compile,
	select,
	outermostContext,
	sortInput,
	hasDuplicate,

	// Local document vars
	setDocument,
	document,
	docElem,
	documentIsHTML,
	rbuggyQSA,
	rbuggyMatches,
	matches,
	contains,

	// Instance-specific data
	expando = "sizzle" + 1 * new Date(),
	preferredDoc = window.document,
	dirruns = 0,
	done = 0,
	classCache = createCache(),
	tokenCache = createCache(),
	compilerCache = createCache(),
	sortOrder = function( a, b ) {
		if ( a === b ) {
			hasDuplicate = true;
		}
		return 0;
	},

	// General-purpose constants
	MAX_NEGATIVE = 1 << 31,

	// Instance methods
	hasOwn = ({}).hasOwnProperty,
	arr = [],
	pop = arr.pop,
	push_native = arr.push,
	push = arr.push,
	slice = arr.slice,
	// Use a stripped-down indexOf as it's faster than native
	// http://jsperf.com/thor-indexof-vs-for/5
	indexOf = function( list, elem ) {
		var i = 0,
			len = list.length;
		for ( ; i < len; i++ ) {
			if ( list[i] === elem ) {
				return i;
			}
		}
		return -1;
	},

	booleans = "checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|ismap|loop|multiple|open|readonly|required|scoped",

	// Regular expressions

	// http://www.w3.org/TR/css3-selectors/#whitespace
	whitespace = "[\\x20\\t\\r\\n\\f]",

	// http://www.w3.org/TR/CSS21/syndata.html#value-def-identifier
	identifier = "(?:\\\\.|[\\w-]|[^\\x00-\\xa0])+",

	// Attribute selectors: http://www.w3.org/TR/selectors/#attribute-selectors
	attributes = "\\[" + whitespace + "*(" + identifier + ")(?:" + whitespace +
		// Operator (capture 2)
		"*([*^$|!~]?=)" + whitespace +
		// "Attribute values must be CSS identifiers [capture 5] or strings [capture 3 or capture 4]"
		"*(?:'((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\"|(" + identifier + "))|)" + whitespace +
		"*\\]",

	pseudos = ":(" + identifier + ")(?:\\((" +
		// To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
		// 1. quoted (capture 3; capture 4 or capture 5)
		"('((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\")|" +
		// 2. simple (capture 6)
		"((?:\\\\.|[^\\\\()[\\]]|" + attributes + ")*)|" +
		// 3. anything else (capture 2)
		".*" +
		")\\)|)",

	// Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
	rwhitespace = new RegExp( whitespace + "+", "g" ),
	rtrim = new RegExp( "^" + whitespace + "+|((?:^|[^\\\\])(?:\\\\.)*)" + whitespace + "+$", "g" ),

	rcomma = new RegExp( "^" + whitespace + "*," + whitespace + "*" ),
	rcombinators = new RegExp( "^" + whitespace + "*([>+~]|" + whitespace + ")" + whitespace + "*" ),

	rattributeQuotes = new RegExp( "=" + whitespace + "*([^\\]'\"]*?)" + whitespace + "*\\]", "g" ),

	rpseudo = new RegExp( pseudos ),
	ridentifier = new RegExp( "^" + identifier + "$" ),

	matchExpr = {
		"ID": new RegExp( "^#(" + identifier + ")" ),
		"CLASS": new RegExp( "^\\.(" + identifier + ")" ),
		"TAG": new RegExp( "^(" + identifier + "|[*])" ),
		"ATTR": new RegExp( "^" + attributes ),
		"PSEUDO": new RegExp( "^" + pseudos ),
		"CHILD": new RegExp( "^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(" + whitespace +
			"*(even|odd|(([+-]|)(\\d*)n|)" + whitespace + "*(?:([+-]|)" + whitespace +
			"*(\\d+)|))" + whitespace + "*\\)|)", "i" ),
		"bool": new RegExp( "^(?:" + booleans + ")$", "i" ),
		// For use in libraries implementing .is()
		// We use this for POS matching in `select`
		"needsContext": new RegExp( "^" + whitespace + "*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(" +
			whitespace + "*((?:-\\d)?\\d*)" + whitespace + "*\\)|)(?=[^-]|$)", "i" )
	},

	rinputs = /^(?:input|select|textarea|button)$/i,
	rheader = /^h\d$/i,

	rnative = /^[^{]+\{\s*\[native \w/,

	// Easily-parseable/retrievable ID or TAG or CLASS selectors
	rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,

	rsibling = /[+~]/,
	rescape = /'|\\/g,

	// CSS escapes http://www.w3.org/TR/CSS21/syndata.html#escaped-characters
	runescape = new RegExp( "\\\\([\\da-f]{1,6}" + whitespace + "?|(" + whitespace + ")|.)", "ig" ),
	funescape = function( _, escaped, escapedWhitespace ) {
		var high = "0x" + escaped - 0x10000;
		// NaN means non-codepoint
		// Support: Firefox<24
		// Workaround erroneous numeric interpretation of +"0x"
		return high !== high || escapedWhitespace ?
			escaped :
			high < 0 ?
				// BMP codepoint
				String.fromCharCode( high + 0x10000 ) :
				// Supplemental Plane codepoint (surrogate pair)
				String.fromCharCode( high >> 10 | 0xD800, high & 0x3FF | 0xDC00 );
	},

	// Used for iframes
	// See setDocument()
	// Removing the function wrapper causes a "Permission Denied"
	// error in IE
	unloadHandler = function() {
		setDocument();
	};

// Optimize for push.apply( _, NodeList )
try {
	push.apply(
		(arr = slice.call( preferredDoc.childNodes )),
		preferredDoc.childNodes
	);
	// Support: Android<4.0
	// Detect silently failing push.apply
	arr[ preferredDoc.childNodes.length ].nodeType;
} catch ( e ) {
	push = { apply: arr.length ?

		// Leverage slice if possible
		function( target, els ) {
			push_native.apply( target, slice.call(els) );
		} :

		// Support: IE<9
		// Otherwise append directly
		function( target, els ) {
			var j = target.length,
				i = 0;
			// Can't trust NodeList.length
			while ( (target[j++] = els[i++]) ) {}
			target.length = j - 1;
		}
	};
}

function Sizzle( selector, context, results, seed ) {
	var m, i, elem, nid, nidselect, match, groups, newSelector,
		newContext = context && context.ownerDocument,

		// nodeType defaults to 9, since context defaults to document
		nodeType = context ? context.nodeType : 9;

	results = results || [];

	// Return early from calls with invalid selector or context
	if ( typeof selector !== "string" || !selector ||
		nodeType !== 1 && nodeType !== 9 && nodeType !== 11 ) {

		return results;
	}

	// Try to shortcut find operations (as opposed to filters) in HTML documents
	if ( !seed ) {

		if ( ( context ? context.ownerDocument || context : preferredDoc ) !== document ) {
			setDocument( context );
		}
		context = context || document;

		if ( documentIsHTML ) {

			// If the selector is sufficiently simple, try using a "get*By*" DOM method
			// (excepting DocumentFragment context, where the methods don't exist)
			if ( nodeType !== 11 && (match = rquickExpr.exec( selector )) ) {

				// ID selector
				if ( (m = match[1]) ) {

					// Document context
					if ( nodeType === 9 ) {
						if ( (elem = context.getElementById( m )) ) {

							// Support: IE, Opera, Webkit
							// TODO: identify versions
							// getElementById can match elements by name instead of ID
							if ( elem.id === m ) {
								results.push( elem );
								return results;
							}
						} else {
							return results;
						}

					// Element context
					} else {

						// Support: IE, Opera, Webkit
						// TODO: identify versions
						// getElementById can match elements by name instead of ID
						if ( newContext && (elem = newContext.getElementById( m )) &&
							contains( context, elem ) &&
							elem.id === m ) {

							results.push( elem );
							return results;
						}
					}

				// Type selector
				} else if ( match[2] ) {
					push.apply( results, context.getElementsByTagName( selector ) );
					return results;

				// Class selector
				} else if ( (m = match[3]) && support.getElementsByClassName &&
					context.getElementsByClassName ) {

					push.apply( results, context.getElementsByClassName( m ) );
					return results;
				}
			}

			// Take advantage of querySelectorAll
			if ( support.qsa &&
				!compilerCache[ selector + " " ] &&
				(!rbuggyQSA || !rbuggyQSA.test( selector )) ) {

				if ( nodeType !== 1 ) {
					newContext = context;
					newSelector = selector;

				// qSA looks outside Element context, which is not what we want
				// Thanks to Andrew Dupont for this workaround technique
				// Support: IE <=8
				// Exclude object elements
				} else if ( context.nodeName.toLowerCase() !== "object" ) {

					// Capture the context ID, setting it first if necessary
					if ( (nid = context.getAttribute( "id" )) ) {
						nid = nid.replace( rescape, "\\$&" );
					} else {
						context.setAttribute( "id", (nid = expando) );
					}

					// Prefix every selector in the list
					groups = tokenize( selector );
					i = groups.length;
					nidselect = ridentifier.test( nid ) ? "#" + nid : "[id='" + nid + "']";
					while ( i-- ) {
						groups[i] = nidselect + " " + toSelector( groups[i] );
					}
					newSelector = groups.join( "," );

					// Expand context for sibling selectors
					newContext = rsibling.test( selector ) && testContext( context.parentNode ) ||
						context;
				}

				if ( newSelector ) {
					try {
						push.apply( results,
							newContext.querySelectorAll( newSelector )
						);
						return results;
					} catch ( qsaError ) {
					} finally {
						if ( nid === expando ) {
							context.removeAttribute( "id" );
						}
					}
				}
			}
		}
	}

	// All others
	return select( selector.replace( rtrim, "$1" ), context, results, seed );
}

/**
 * Create key-value caches of limited size
 * @returns {function(string, object)} Returns the Object data after storing it on itself with
 *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
 *	deleting the oldest entry
 */
function createCache() {
	var keys = [];

	function cache( key, value ) {
		// Use (key + " ") to avoid collision with native prototype properties (see Issue #157)
		if ( keys.push( key + " " ) > Expr.cacheLength ) {
			// Only keep the most recent entries
			delete cache[ keys.shift() ];
		}
		return (cache[ key + " " ] = value);
	}
	return cache;
}

/**
 * Mark a function for special use by Sizzle
 * @param {Function} fn The function to mark
 */
function markFunction( fn ) {
	fn[ expando ] = true;
	return fn;
}

/**
 * Support testing using an element
 * @param {Function} fn Passed the created div and expects a boolean result
 */
function assert( fn ) {
	var div = document.createElement("div");

	try {
		return !!fn( div );
	} catch (e) {
		return false;
	} finally {
		// Remove from its parent by default
		if ( div.parentNode ) {
			div.parentNode.removeChild( div );
		}
		// release memory in IE
		div = null;
	}
}

/**
 * Adds the same handler for all of the specified attrs
 * @param {String} attrs Pipe-separated list of attributes
 * @param {Function} handler The method that will be applied
 */
function addHandle( attrs, handler ) {
	var arr = attrs.split("|"),
		i = arr.length;

	while ( i-- ) {
		Expr.attrHandle[ arr[i] ] = handler;
	}
}

/**
 * Checks document order of two siblings
 * @param {Element} a
 * @param {Element} b
 * @returns {Number} Returns less than 0 if a precedes b, greater than 0 if a follows b
 */
function siblingCheck( a, b ) {
	var cur = b && a,
		diff = cur && a.nodeType === 1 && b.nodeType === 1 &&
			( ~b.sourceIndex || MAX_NEGATIVE ) -
			( ~a.sourceIndex || MAX_NEGATIVE );

	// Use IE sourceIndex if available on both nodes
	if ( diff ) {
		return diff;
	}

	// Check if b follows a
	if ( cur ) {
		while ( (cur = cur.nextSibling) ) {
			if ( cur === b ) {
				return -1;
			}
		}
	}

	return a ? 1 : -1;
}

/**
 * Returns a function to use in pseudos for input types
 * @param {String} type
 */
function createInputPseudo( type ) {
	return function( elem ) {
		var name = elem.nodeName.toLowerCase();
		return name === "input" && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for buttons
 * @param {String} type
 */
function createButtonPseudo( type ) {
	return function( elem ) {
		var name = elem.nodeName.toLowerCase();
		return (name === "input" || name === "button") && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for positionals
 * @param {Function} fn
 */
function createPositionalPseudo( fn ) {
	return markFunction(function( argument ) {
		argument = +argument;
		return markFunction(function( seed, matches ) {
			var j,
				matchIndexes = fn( [], seed.length, argument ),
				i = matchIndexes.length;

			// Match elements found at the specified indexes
			while ( i-- ) {
				if ( seed[ (j = matchIndexes[i]) ] ) {
					seed[j] = !(matches[j] = seed[j]);
				}
			}
		});
	});
}

/**
 * Checks a node for validity as a Sizzle context
 * @param {Element|Object=} context
 * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
 */
function testContext( context ) {
	return context && typeof context.getElementsByTagName !== "undefined" && context;
}

// Expose support vars for convenience
support = Sizzle.support = {};

/**
 * Detects XML nodes
 * @param {Element|Object} elem An element or a document
 * @returns {Boolean} True iff elem is a non-HTML XML node
 */
isXML = Sizzle.isXML = function( elem ) {
	// documentElement is verified for cases where it doesn't yet exist
	// (such as loading iframes in IE - #4833)
	var documentElement = elem && (elem.ownerDocument || elem).documentElement;
	return documentElement ? documentElement.nodeName !== "HTML" : false;
};

/**
 * Sets document-related variables once based on the current document
 * @param {Element|Object} [doc] An element or document object to use to set the document
 * @returns {Object} Returns the current document
 */
setDocument = Sizzle.setDocument = function( node ) {
	var hasCompare, parent,
		doc = node ? node.ownerDocument || node : preferredDoc;

	// Return early if doc is invalid or already selected
	if ( doc === document || doc.nodeType !== 9 || !doc.documentElement ) {
		return document;
	}

	// Update global variables
	document = doc;
	docElem = document.documentElement;
	documentIsHTML = !isXML( document );

	// Support: IE 9-11, Edge
	// Accessing iframe documents after unload throws "permission denied" errors (jQuery #13936)
	if ( (parent = document.defaultView) && parent.top !== parent ) {
		// Support: IE 11
		if ( parent.addEventListener ) {
			parent.addEventListener( "unload", unloadHandler, false );

		// Support: IE 9 - 10 only
		} else if ( parent.attachEvent ) {
			parent.attachEvent( "onunload", unloadHandler );
		}
	}

	/* Attributes
	---------------------------------------------------------------------- */

	// Support: IE<8
	// Verify that getAttribute really returns attributes and not properties
	// (excepting IE8 booleans)
	support.attributes = assert(function( div ) {
		div.className = "i";
		return !div.getAttribute("className");
	});

	/* getElement(s)By*
	---------------------------------------------------------------------- */

	// Check if getElementsByTagName("*") returns only elements
	support.getElementsByTagName = assert(function( div ) {
		div.appendChild( document.createComment("") );
		return !div.getElementsByTagName("*").length;
	});

	// Support: IE<9
	support.getElementsByClassName = rnative.test( document.getElementsByClassName );

	// Support: IE<10
	// Check if getElementById returns elements by name
	// The broken getElementById methods don't pick up programatically-set names,
	// so use a roundabout getElementsByName test
	support.getById = assert(function( div ) {
		docElem.appendChild( div ).id = expando;
		return !document.getElementsByName || !document.getElementsByName( expando ).length;
	});

	// ID find and filter
	if ( support.getById ) {
		Expr.find["ID"] = function( id, context ) {
			if ( typeof context.getElementById !== "undefined" && documentIsHTML ) {
				var m = context.getElementById( id );
				return m ? [ m ] : [];
			}
		};
		Expr.filter["ID"] = function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				return elem.getAttribute("id") === attrId;
			};
		};
	} else {
		// Support: IE6/7
		// getElementById is not reliable as a find shortcut
		delete Expr.find["ID"];

		Expr.filter["ID"] =  function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				var node = typeof elem.getAttributeNode !== "undefined" &&
					elem.getAttributeNode("id");
				return node && node.value === attrId;
			};
		};
	}

	// Tag
	Expr.find["TAG"] = support.getElementsByTagName ?
		function( tag, context ) {
			if ( typeof context.getElementsByTagName !== "undefined" ) {
				return context.getElementsByTagName( tag );

			// DocumentFragment nodes don't have gEBTN
			} else if ( support.qsa ) {
				return context.querySelectorAll( tag );
			}
		} :

		function( tag, context ) {
			var elem,
				tmp = [],
				i = 0,
				// By happy coincidence, a (broken) gEBTN appears on DocumentFragment nodes too
				results = context.getElementsByTagName( tag );

			// Filter out possible comments
			if ( tag === "*" ) {
				while ( (elem = results[i++]) ) {
					if ( elem.nodeType === 1 ) {
						tmp.push( elem );
					}
				}

				return tmp;
			}
			return results;
		};

	// Class
	Expr.find["CLASS"] = support.getElementsByClassName && function( className, context ) {
		if ( typeof context.getElementsByClassName !== "undefined" && documentIsHTML ) {
			return context.getElementsByClassName( className );
		}
	};

	/* QSA/matchesSelector
	---------------------------------------------------------------------- */

	// QSA and matchesSelector support

	// matchesSelector(:active) reports false when true (IE9/Opera 11.5)
	rbuggyMatches = [];

	// qSa(:focus) reports false when true (Chrome 21)
	// We allow this because of a bug in IE8/9 that throws an error
	// whenever `document.activeElement` is accessed on an iframe
	// So, we allow :focus to pass through QSA all the time to avoid the IE error
	// See http://bugs.jquery.com/ticket/13378
	rbuggyQSA = [];

	if ( (support.qsa = rnative.test( document.querySelectorAll )) ) {
		// Build QSA regex
		// Regex strategy adopted from Diego Perini
		assert(function( div ) {
			// Select is set to empty string on purpose
			// This is to test IE's treatment of not explicitly
			// setting a boolean content attribute,
			// since its presence should be enough
			// http://bugs.jquery.com/ticket/12359
			docElem.appendChild( div ).innerHTML = "<a id='" + expando + "'></a>" +
				"<select id='" + expando + "-\r\\' msallowcapture=''>" +
				"<option selected=''></option></select>";

			// Support: IE8, Opera 11-12.16
			// Nothing should be selected when empty strings follow ^= or $= or *=
			// The test attribute must be unknown in Opera but "safe" for WinRT
			// http://msdn.microsoft.com/en-us/library/ie/hh465388.aspx#attribute_section
			if ( div.querySelectorAll("[msallowcapture^='']").length ) {
				rbuggyQSA.push( "[*^$]=" + whitespace + "*(?:''|\"\")" );
			}

			// Support: IE8
			// Boolean attributes and "value" are not treated correctly
			if ( !div.querySelectorAll("[selected]").length ) {
				rbuggyQSA.push( "\\[" + whitespace + "*(?:value|" + booleans + ")" );
			}

			// Support: Chrome<29, Android<4.4, Safari<7.0+, iOS<7.0+, PhantomJS<1.9.8+
			if ( !div.querySelectorAll( "[id~=" + expando + "-]" ).length ) {
				rbuggyQSA.push("~=");
			}

			// Webkit/Opera - :checked should return selected option elements
			// http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			// IE8 throws error here and will not see later tests
			if ( !div.querySelectorAll(":checked").length ) {
				rbuggyQSA.push(":checked");
			}

			// Support: Safari 8+, iOS 8+
			// https://bugs.webkit.org/show_bug.cgi?id=136851
			// In-page `selector#id sibing-combinator selector` fails
			if ( !div.querySelectorAll( "a#" + expando + "+*" ).length ) {
				rbuggyQSA.push(".#.+[+~]");
			}
		});

		assert(function( div ) {
			// Support: Windows 8 Native Apps
			// The type and name attributes are restricted during .innerHTML assignment
			var input = document.createElement("input");
			input.setAttribute( "type", "hidden" );
			div.appendChild( input ).setAttribute( "name", "D" );

			// Support: IE8
			// Enforce case-sensitivity of name attribute
			if ( div.querySelectorAll("[name=d]").length ) {
				rbuggyQSA.push( "name" + whitespace + "*[*^$|!~]?=" );
			}

			// FF 3.5 - :enabled/:disabled and hidden elements (hidden elements are still enabled)
			// IE8 throws error here and will not see later tests
			if ( !div.querySelectorAll(":enabled").length ) {
				rbuggyQSA.push( ":enabled", ":disabled" );
			}

			// Opera 10-11 does not throw on post-comma invalid pseudos
			div.querySelectorAll("*,:x");
			rbuggyQSA.push(",.*:");
		});
	}

	if ( (support.matchesSelector = rnative.test( (matches = docElem.matches ||
		docElem.webkitMatchesSelector ||
		docElem.mozMatchesSelector ||
		docElem.oMatchesSelector ||
		docElem.msMatchesSelector) )) ) {

		assert(function( div ) {
			// Check to see if it's possible to do matchesSelector
			// on a disconnected node (IE 9)
			support.disconnectedMatch = matches.call( div, "div" );

			// This should fail with an exception
			// Gecko does not error, returns false instead
			matches.call( div, "[s!='']:x" );
			rbuggyMatches.push( "!=", pseudos );
		});
	}

	rbuggyQSA = rbuggyQSA.length && new RegExp( rbuggyQSA.join("|") );
	rbuggyMatches = rbuggyMatches.length && new RegExp( rbuggyMatches.join("|") );

	/* Contains
	---------------------------------------------------------------------- */
	hasCompare = rnative.test( docElem.compareDocumentPosition );

	// Element contains another
	// Purposefully self-exclusive
	// As in, an element does not contain itself
	contains = hasCompare || rnative.test( docElem.contains ) ?
		function( a, b ) {
			var adown = a.nodeType === 9 ? a.documentElement : a,
				bup = b && b.parentNode;
			return a === bup || !!( bup && bup.nodeType === 1 && (
				adown.contains ?
					adown.contains( bup ) :
					a.compareDocumentPosition && a.compareDocumentPosition( bup ) & 16
			));
		} :
		function( a, b ) {
			if ( b ) {
				while ( (b = b.parentNode) ) {
					if ( b === a ) {
						return true;
					}
				}
			}
			return false;
		};

	/* Sorting
	---------------------------------------------------------------------- */

	// Document order sorting
	sortOrder = hasCompare ?
	function( a, b ) {

		// Flag for duplicate removal
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		// Sort on method existence if only one input has compareDocumentPosition
		var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
		if ( compare ) {
			return compare;
		}

		// Calculate position if both inputs belong to the same document
		compare = ( a.ownerDocument || a ) === ( b.ownerDocument || b ) ?
			a.compareDocumentPosition( b ) :

			// Otherwise we know they are disconnected
			1;

		// Disconnected nodes
		if ( compare & 1 ||
			(!support.sortDetached && b.compareDocumentPosition( a ) === compare) ) {

			// Choose the first element that is related to our preferred document
			if ( a === document || a.ownerDocument === preferredDoc && contains(preferredDoc, a) ) {
				return -1;
			}
			if ( b === document || b.ownerDocument === preferredDoc && contains(preferredDoc, b) ) {
				return 1;
			}

			// Maintain original order
			return sortInput ?
				( indexOf( sortInput, a ) - indexOf( sortInput, b ) ) :
				0;
		}

		return compare & 4 ? -1 : 1;
	} :
	function( a, b ) {
		// Exit early if the nodes are identical
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		var cur,
			i = 0,
			aup = a.parentNode,
			bup = b.parentNode,
			ap = [ a ],
			bp = [ b ];

		// Parentless nodes are either documents or disconnected
		if ( !aup || !bup ) {
			return a === document ? -1 :
				b === document ? 1 :
				aup ? -1 :
				bup ? 1 :
				sortInput ?
				( indexOf( sortInput, a ) - indexOf( sortInput, b ) ) :
				0;

		// If the nodes are siblings, we can do a quick check
		} else if ( aup === bup ) {
			return siblingCheck( a, b );
		}

		// Otherwise we need full lists of their ancestors for comparison
		cur = a;
		while ( (cur = cur.parentNode) ) {
			ap.unshift( cur );
		}
		cur = b;
		while ( (cur = cur.parentNode) ) {
			bp.unshift( cur );
		}

		// Walk down the tree looking for a discrepancy
		while ( ap[i] === bp[i] ) {
			i++;
		}

		return i ?
			// Do a sibling check if the nodes have a common ancestor
			siblingCheck( ap[i], bp[i] ) :

			// Otherwise nodes in our document sort first
			ap[i] === preferredDoc ? -1 :
			bp[i] === preferredDoc ? 1 :
			0;
	};

	return document;
};

Sizzle.matches = function( expr, elements ) {
	return Sizzle( expr, null, null, elements );
};

Sizzle.matchesSelector = function( elem, expr ) {
	// Set document vars if needed
	if ( ( elem.ownerDocument || elem ) !== document ) {
		setDocument( elem );
	}

	// Make sure that attribute selectors are quoted
	expr = expr.replace( rattributeQuotes, "='$1']" );

	if ( support.matchesSelector && documentIsHTML &&
		!compilerCache[ expr + " " ] &&
		( !rbuggyMatches || !rbuggyMatches.test( expr ) ) &&
		( !rbuggyQSA     || !rbuggyQSA.test( expr ) ) ) {

		try {
			var ret = matches.call( elem, expr );

			// IE 9's matchesSelector returns false on disconnected nodes
			if ( ret || support.disconnectedMatch ||
					// As well, disconnected nodes are said to be in a document
					// fragment in IE 9
					elem.document && elem.document.nodeType !== 11 ) {
				return ret;
			}
		} catch (e) {}
	}

	return Sizzle( expr, document, null, [ elem ] ).length > 0;
};

Sizzle.contains = function( context, elem ) {
	// Set document vars if needed
	if ( ( context.ownerDocument || context ) !== document ) {
		setDocument( context );
	}
	return contains( context, elem );
};

Sizzle.attr = function( elem, name ) {
	// Set document vars if needed
	if ( ( elem.ownerDocument || elem ) !== document ) {
		setDocument( elem );
	}

	var fn = Expr.attrHandle[ name.toLowerCase() ],
		// Don't get fooled by Object.prototype properties (jQuery #13807)
		val = fn && hasOwn.call( Expr.attrHandle, name.toLowerCase() ) ?
			fn( elem, name, !documentIsHTML ) :
			undefined;

	return val !== undefined ?
		val :
		support.attributes || !documentIsHTML ?
			elem.getAttribute( name ) :
			(val = elem.getAttributeNode(name)) && val.specified ?
				val.value :
				null;
};

Sizzle.error = function( msg ) {
	throw new Error( "Syntax error, unrecognized expression: " + msg );
};

/**
 * Document sorting and removing duplicates
 * @param {ArrayLike} results
 */
Sizzle.uniqueSort = function( results ) {
	var elem,
		duplicates = [],
		j = 0,
		i = 0;

	// Unless we *know* we can detect duplicates, assume their presence
	hasDuplicate = !support.detectDuplicates;
	sortInput = !support.sortStable && results.slice( 0 );
	results.sort( sortOrder );

	if ( hasDuplicate ) {
		while ( (elem = results[i++]) ) {
			if ( elem === results[ i ] ) {
				j = duplicates.push( i );
			}
		}
		while ( j-- ) {
			results.splice( duplicates[ j ], 1 );
		}
	}

	// Clear input after sorting to release objects
	// See https://github.com/jquery/sizzle/pull/225
	sortInput = null;

	return results;
};

/**
 * Utility function for retrieving the text value of an array of DOM nodes
 * @param {Array|Element} elem
 */
getText = Sizzle.getText = function( elem ) {
	var node,
		ret = "",
		i = 0,
		nodeType = elem.nodeType;

	if ( !nodeType ) {
		// If no nodeType, this is expected to be an array
		while ( (node = elem[i++]) ) {
			// Do not traverse comment nodes
			ret += getText( node );
		}
	} else if ( nodeType === 1 || nodeType === 9 || nodeType === 11 ) {
		// Use textContent for elements
		// innerText usage removed for consistency of new lines (jQuery #11153)
		if ( typeof elem.textContent === "string" ) {
			return elem.textContent;
		} else {
			// Traverse its children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				ret += getText( elem );
			}
		}
	} else if ( nodeType === 3 || nodeType === 4 ) {
		return elem.nodeValue;
	}
	// Do not include comment or processing instruction nodes

	return ret;
};

Expr = Sizzle.selectors = {

	// Can be adjusted by the user
	cacheLength: 50,

	createPseudo: markFunction,

	match: matchExpr,

	attrHandle: {},

	find: {},

	relative: {
		">": { dir: "parentNode", first: true },
		" ": { dir: "parentNode" },
		"+": { dir: "previousSibling", first: true },
		"~": { dir: "previousSibling" }
	},

	preFilter: {
		"ATTR": function( match ) {
			match[1] = match[1].replace( runescape, funescape );

			// Move the given value to match[3] whether quoted or unquoted
			match[3] = ( match[3] || match[4] || match[5] || "" ).replace( runescape, funescape );

			if ( match[2] === "~=" ) {
				match[3] = " " + match[3] + " ";
			}

			return match.slice( 0, 4 );
		},

		"CHILD": function( match ) {
			/* matches from matchExpr["CHILD"]
				1 type (only|nth|...)
				2 what (child|of-type)
				3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
				4 xn-component of xn+y argument ([+-]?\d*n|)
				5 sign of xn-component
				6 x of xn-component
				7 sign of y-component
				8 y of y-component
			*/
			match[1] = match[1].toLowerCase();

			if ( match[1].slice( 0, 3 ) === "nth" ) {
				// nth-* requires argument
				if ( !match[3] ) {
					Sizzle.error( match[0] );
				}

				// numeric x and y parameters for Expr.filter.CHILD
				// remember that false/true cast respectively to 0/1
				match[4] = +( match[4] ? match[5] + (match[6] || 1) : 2 * ( match[3] === "even" || match[3] === "odd" ) );
				match[5] = +( ( match[7] + match[8] ) || match[3] === "odd" );

			// other types prohibit arguments
			} else if ( match[3] ) {
				Sizzle.error( match[0] );
			}

			return match;
		},

		"PSEUDO": function( match ) {
			var excess,
				unquoted = !match[6] && match[2];

			if ( matchExpr["CHILD"].test( match[0] ) ) {
				return null;
			}

			// Accept quoted arguments as-is
			if ( match[3] ) {
				match[2] = match[4] || match[5] || "";

			// Strip excess characters from unquoted arguments
			} else if ( unquoted && rpseudo.test( unquoted ) &&
				// Get excess from tokenize (recursively)
				(excess = tokenize( unquoted, true )) &&
				// advance to the next closing parenthesis
				(excess = unquoted.indexOf( ")", unquoted.length - excess ) - unquoted.length) ) {

				// excess is a negative index
				match[0] = match[0].slice( 0, excess );
				match[2] = unquoted.slice( 0, excess );
			}

			// Return only captures needed by the pseudo filter method (type and argument)
			return match.slice( 0, 3 );
		}
	},

	filter: {

		"TAG": function( nodeNameSelector ) {
			var nodeName = nodeNameSelector.replace( runescape, funescape ).toLowerCase();
			return nodeNameSelector === "*" ?
				function() { return true; } :
				function( elem ) {
					return elem.nodeName && elem.nodeName.toLowerCase() === nodeName;
				};
		},

		"CLASS": function( className ) {
			var pattern = classCache[ className + " " ];

			return pattern ||
				(pattern = new RegExp( "(^|" + whitespace + ")" + className + "(" + whitespace + "|$)" )) &&
				classCache( className, function( elem ) {
					return pattern.test( typeof elem.className === "string" && elem.className || typeof elem.getAttribute !== "undefined" && elem.getAttribute("class") || "" );
				});
		},

		"ATTR": function( name, operator, check ) {
			return function( elem ) {
				var result = Sizzle.attr( elem, name );

				if ( result == null ) {
					return operator === "!=";
				}
				if ( !operator ) {
					return true;
				}

				result += "";

				return operator === "=" ? result === check :
					operator === "!=" ? result !== check :
					operator === "^=" ? check && result.indexOf( check ) === 0 :
					operator === "*=" ? check && result.indexOf( check ) > -1 :
					operator === "$=" ? check && result.slice( -check.length ) === check :
					operator === "~=" ? ( " " + result.replace( rwhitespace, " " ) + " " ).indexOf( check ) > -1 :
					operator === "|=" ? result === check || result.slice( 0, check.length + 1 ) === check + "-" :
					false;
			};
		},

		"CHILD": function( type, what, argument, first, last ) {
			var simple = type.slice( 0, 3 ) !== "nth",
				forward = type.slice( -4 ) !== "last",
				ofType = what === "of-type";

			return first === 1 && last === 0 ?

				// Shortcut for :nth-*(n)
				function( elem ) {
					return !!elem.parentNode;
				} :

				function( elem, context, xml ) {
					var cache, uniqueCache, outerCache, node, nodeIndex, start,
						dir = simple !== forward ? "nextSibling" : "previousSibling",
						parent = elem.parentNode,
						name = ofType && elem.nodeName.toLowerCase(),
						useCache = !xml && !ofType,
						diff = false;

					if ( parent ) {

						// :(first|last|only)-(child|of-type)
						if ( simple ) {
							while ( dir ) {
								node = elem;
								while ( (node = node[ dir ]) ) {
									if ( ofType ?
										node.nodeName.toLowerCase() === name :
										node.nodeType === 1 ) {

										return false;
									}
								}
								// Reverse direction for :only-* (if we haven't yet done so)
								start = dir = type === "only" && !start && "nextSibling";
							}
							return true;
						}

						start = [ forward ? parent.firstChild : parent.lastChild ];

						// non-xml :nth-child(...) stores cache data on `parent`
						if ( forward && useCache ) {

							// Seek `elem` from a previously-cached index

							// ...in a gzip-friendly way
							node = parent;
							outerCache = node[ expando ] || (node[ expando ] = {});

							// Support: IE <9 only
							// Defend against cloned attroperties (jQuery gh-1709)
							uniqueCache = outerCache[ node.uniqueID ] ||
								(outerCache[ node.uniqueID ] = {});

							cache = uniqueCache[ type ] || [];
							nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
							diff = nodeIndex && cache[ 2 ];
							node = nodeIndex && parent.childNodes[ nodeIndex ];

							while ( (node = ++nodeIndex && node && node[ dir ] ||

								// Fallback to seeking `elem` from the start
								(diff = nodeIndex = 0) || start.pop()) ) {

								// When found, cache indexes on `parent` and break
								if ( node.nodeType === 1 && ++diff && node === elem ) {
									uniqueCache[ type ] = [ dirruns, nodeIndex, diff ];
									break;
								}
							}

						} else {
							// Use previously-cached element index if available
							if ( useCache ) {
								// ...in a gzip-friendly way
								node = elem;
								outerCache = node[ expando ] || (node[ expando ] = {});

								// Support: IE <9 only
								// Defend against cloned attroperties (jQuery gh-1709)
								uniqueCache = outerCache[ node.uniqueID ] ||
									(outerCache[ node.uniqueID ] = {});

								cache = uniqueCache[ type ] || [];
								nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
								diff = nodeIndex;
							}

							// xml :nth-child(...)
							// or :nth-last-child(...) or :nth(-last)?-of-type(...)
							if ( diff === false ) {
								// Use the same loop as above to seek `elem` from the start
								while ( (node = ++nodeIndex && node && node[ dir ] ||
									(diff = nodeIndex = 0) || start.pop()) ) {

									if ( ( ofType ?
										node.nodeName.toLowerCase() === name :
										node.nodeType === 1 ) &&
										++diff ) {

										// Cache the index of each encountered element
										if ( useCache ) {
											outerCache = node[ expando ] || (node[ expando ] = {});

											// Support: IE <9 only
											// Defend against cloned attroperties (jQuery gh-1709)
											uniqueCache = outerCache[ node.uniqueID ] ||
												(outerCache[ node.uniqueID ] = {});

											uniqueCache[ type ] = [ dirruns, diff ];
										}

										if ( node === elem ) {
											break;
										}
									}
								}
							}
						}

						// Incorporate the offset, then check against cycle size
						diff -= last;
						return diff === first || ( diff % first === 0 && diff / first >= 0 );
					}
				};
		},

		"PSEUDO": function( pseudo, argument ) {
			// pseudo-class names are case-insensitive
			// http://www.w3.org/TR/selectors/#pseudo-classes
			// Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
			// Remember that setFilters inherits from pseudos
			var args,
				fn = Expr.pseudos[ pseudo ] || Expr.setFilters[ pseudo.toLowerCase() ] ||
					Sizzle.error( "unsupported pseudo: " + pseudo );

			// The user may use createPseudo to indicate that
			// arguments are needed to create the filter function
			// just as Sizzle does
			if ( fn[ expando ] ) {
				return fn( argument );
			}

			// But maintain support for old signatures
			if ( fn.length > 1 ) {
				args = [ pseudo, pseudo, "", argument ];
				return Expr.setFilters.hasOwnProperty( pseudo.toLowerCase() ) ?
					markFunction(function( seed, matches ) {
						var idx,
							matched = fn( seed, argument ),
							i = matched.length;
						while ( i-- ) {
							idx = indexOf( seed, matched[i] );
							seed[ idx ] = !( matches[ idx ] = matched[i] );
						}
					}) :
					function( elem ) {
						return fn( elem, 0, args );
					};
			}

			return fn;
		}
	},

	pseudos: {
		// Potentially complex pseudos
		"not": markFunction(function( selector ) {
			// Trim the selector passed to compile
			// to avoid treating leading and trailing
			// spaces as combinators
			var input = [],
				results = [],
				matcher = compile( selector.replace( rtrim, "$1" ) );

			return matcher[ expando ] ?
				markFunction(function( seed, matches, context, xml ) {
					var elem,
						unmatched = matcher( seed, null, xml, [] ),
						i = seed.length;

					// Match elements unmatched by `matcher`
					while ( i-- ) {
						if ( (elem = unmatched[i]) ) {
							seed[i] = !(matches[i] = elem);
						}
					}
				}) :
				function( elem, context, xml ) {
					input[0] = elem;
					matcher( input, null, xml, results );
					// Don't keep the element (issue #299)
					input[0] = null;
					return !results.pop();
				};
		}),

		"has": markFunction(function( selector ) {
			return function( elem ) {
				return Sizzle( selector, elem ).length > 0;
			};
		}),

		"contains": markFunction(function( text ) {
			text = text.replace( runescape, funescape );
			return function( elem ) {
				return ( elem.textContent || elem.innerText || getText( elem ) ).indexOf( text ) > -1;
			};
		}),

		// "Whether an element is represented by a :lang() selector
		// is based solely on the element's language value
		// being equal to the identifier C,
		// or beginning with the identifier C immediately followed by "-".
		// The matching of C against the element's language value is performed case-insensitively.
		// The identifier C does not have to be a valid language name."
		// http://www.w3.org/TR/selectors/#lang-pseudo
		"lang": markFunction( function( lang ) {
			// lang value must be a valid identifier
			if ( !ridentifier.test(lang || "") ) {
				Sizzle.error( "unsupported lang: " + lang );
			}
			lang = lang.replace( runescape, funescape ).toLowerCase();
			return function( elem ) {
				var elemLang;
				do {
					if ( (elemLang = documentIsHTML ?
						elem.lang :
						elem.getAttribute("xml:lang") || elem.getAttribute("lang")) ) {

						elemLang = elemLang.toLowerCase();
						return elemLang === lang || elemLang.indexOf( lang + "-" ) === 0;
					}
				} while ( (elem = elem.parentNode) && elem.nodeType === 1 );
				return false;
			};
		}),

		// Miscellaneous
		"target": function( elem ) {
			var hash = window.location && window.location.hash;
			return hash && hash.slice( 1 ) === elem.id;
		},

		"root": function( elem ) {
			return elem === docElem;
		},

		"focus": function( elem ) {
			return elem === document.activeElement && (!document.hasFocus || document.hasFocus()) && !!(elem.type || elem.href || ~elem.tabIndex);
		},

		// Boolean properties
		"enabled": function( elem ) {
			return elem.disabled === false;
		},

		"disabled": function( elem ) {
			return elem.disabled === true;
		},

		"checked": function( elem ) {
			// In CSS3, :checked should return both checked and selected elements
			// http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			var nodeName = elem.nodeName.toLowerCase();
			return (nodeName === "input" && !!elem.checked) || (nodeName === "option" && !!elem.selected);
		},

		"selected": function( elem ) {
			// Accessing this property makes selected-by-default
			// options in Safari work properly
			if ( elem.parentNode ) {
				elem.parentNode.selectedIndex;
			}

			return elem.selected === true;
		},

		// Contents
		"empty": function( elem ) {
			// http://www.w3.org/TR/selectors/#empty-pseudo
			// :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
			//   but not by others (comment: 8; processing instruction: 7; etc.)
			// nodeType < 6 works because attributes (2) do not appear as children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				if ( elem.nodeType < 6 ) {
					return false;
				}
			}
			return true;
		},

		"parent": function( elem ) {
			return !Expr.pseudos["empty"]( elem );
		},

		// Element/input types
		"header": function( elem ) {
			return rheader.test( elem.nodeName );
		},

		"input": function( elem ) {
			return rinputs.test( elem.nodeName );
		},

		"button": function( elem ) {
			var name = elem.nodeName.toLowerCase();
			return name === "input" && elem.type === "button" || name === "button";
		},

		"text": function( elem ) {
			var attr;
			return elem.nodeName.toLowerCase() === "input" &&
				elem.type === "text" &&

				// Support: IE<8
				// New HTML5 attribute values (e.g., "search") appear with elem.type === "text"
				( (attr = elem.getAttribute("type")) == null || attr.toLowerCase() === "text" );
		},

		// Position-in-collection
		"first": createPositionalPseudo(function() {
			return [ 0 ];
		}),

		"last": createPositionalPseudo(function( matchIndexes, length ) {
			return [ length - 1 ];
		}),

		"eq": createPositionalPseudo(function( matchIndexes, length, argument ) {
			return [ argument < 0 ? argument + length : argument ];
		}),

		"even": createPositionalPseudo(function( matchIndexes, length ) {
			var i = 0;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"odd": createPositionalPseudo(function( matchIndexes, length ) {
			var i = 1;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"lt": createPositionalPseudo(function( matchIndexes, length, argument ) {
			var i = argument < 0 ? argument + length : argument;
			for ( ; --i >= 0; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"gt": createPositionalPseudo(function( matchIndexes, length, argument ) {
			var i = argument < 0 ? argument + length : argument;
			for ( ; ++i < length; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		})
	}
};

Expr.pseudos["nth"] = Expr.pseudos["eq"];

// Add button/input type pseudos
for ( i in { radio: true, checkbox: true, file: true, password: true, image: true } ) {
	Expr.pseudos[ i ] = createInputPseudo( i );
}
for ( i in { submit: true, reset: true } ) {
	Expr.pseudos[ i ] = createButtonPseudo( i );
}

// Easy API for creating new setFilters
function setFilters() {}
setFilters.prototype = Expr.filters = Expr.pseudos;
Expr.setFilters = new setFilters();

tokenize = Sizzle.tokenize = function( selector, parseOnly ) {
	var matched, match, tokens, type,
		soFar, groups, preFilters,
		cached = tokenCache[ selector + " " ];

	if ( cached ) {
		return parseOnly ? 0 : cached.slice( 0 );
	}

	soFar = selector;
	groups = [];
	preFilters = Expr.preFilter;

	while ( soFar ) {

		// Comma and first run
		if ( !matched || (match = rcomma.exec( soFar )) ) {
			if ( match ) {
				// Don't consume trailing commas as valid
				soFar = soFar.slice( match[0].length ) || soFar;
			}
			groups.push( (tokens = []) );
		}

		matched = false;

		// Combinators
		if ( (match = rcombinators.exec( soFar )) ) {
			matched = match.shift();
			tokens.push({
				value: matched,
				// Cast descendant combinators to space
				type: match[0].replace( rtrim, " " )
			});
			soFar = soFar.slice( matched.length );
		}

		// Filters
		for ( type in Expr.filter ) {
			if ( (match = matchExpr[ type ].exec( soFar )) && (!preFilters[ type ] ||
				(match = preFilters[ type ]( match ))) ) {
				matched = match.shift();
				tokens.push({
					value: matched,
					type: type,
					matches: match
				});
				soFar = soFar.slice( matched.length );
			}
		}

		if ( !matched ) {
			break;
		}
	}

	// Return the length of the invalid excess
	// if we're just parsing
	// Otherwise, throw an error or return tokens
	return parseOnly ?
		soFar.length :
		soFar ?
			Sizzle.error( selector ) :
			// Cache the tokens
			tokenCache( selector, groups ).slice( 0 );
};

function toSelector( tokens ) {
	var i = 0,
		len = tokens.length,
		selector = "";
	for ( ; i < len; i++ ) {
		selector += tokens[i].value;
	}
	return selector;
}

function addCombinator( matcher, combinator, base ) {
	var dir = combinator.dir,
		checkNonElements = base && dir === "parentNode",
		doneName = done++;

	return combinator.first ?
		// Check against closest ancestor/preceding element
		function( elem, context, xml ) {
			while ( (elem = elem[ dir ]) ) {
				if ( elem.nodeType === 1 || checkNonElements ) {
					return matcher( elem, context, xml );
				}
			}
		} :

		// Check against all ancestor/preceding elements
		function( elem, context, xml ) {
			var oldCache, uniqueCache, outerCache,
				newCache = [ dirruns, doneName ];

			// We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
			if ( xml ) {
				while ( (elem = elem[ dir ]) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						if ( matcher( elem, context, xml ) ) {
							return true;
						}
					}
				}
			} else {
				while ( (elem = elem[ dir ]) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						outerCache = elem[ expando ] || (elem[ expando ] = {});

						// Support: IE <9 only
						// Defend against cloned attroperties (jQuery gh-1709)
						uniqueCache = outerCache[ elem.uniqueID ] || (outerCache[ elem.uniqueID ] = {});

						if ( (oldCache = uniqueCache[ dir ]) &&
							oldCache[ 0 ] === dirruns && oldCache[ 1 ] === doneName ) {

							// Assign to newCache so results back-propagate to previous elements
							return (newCache[ 2 ] = oldCache[ 2 ]);
						} else {
							// Reuse newcache so results back-propagate to previous elements
							uniqueCache[ dir ] = newCache;

							// A match means we're done; a fail means we have to keep checking
							if ( (newCache[ 2 ] = matcher( elem, context, xml )) ) {
								return true;
							}
						}
					}
				}
			}
		};
}

function elementMatcher( matchers ) {
	return matchers.length > 1 ?
		function( elem, context, xml ) {
			var i = matchers.length;
			while ( i-- ) {
				if ( !matchers[i]( elem, context, xml ) ) {
					return false;
				}
			}
			return true;
		} :
		matchers[0];
}

function multipleContexts( selector, contexts, results ) {
	var i = 0,
		len = contexts.length;
	for ( ; i < len; i++ ) {
		Sizzle( selector, contexts[i], results );
	}
	return results;
}

function condense( unmatched, map, filter, context, xml ) {
	var elem,
		newUnmatched = [],
		i = 0,
		len = unmatched.length,
		mapped = map != null;

	for ( ; i < len; i++ ) {
		if ( (elem = unmatched[i]) ) {
			if ( !filter || filter( elem, context, xml ) ) {
				newUnmatched.push( elem );
				if ( mapped ) {
					map.push( i );
				}
			}
		}
	}

	return newUnmatched;
}

function setMatcher( preFilter, selector, matcher, postFilter, postFinder, postSelector ) {
	if ( postFilter && !postFilter[ expando ] ) {
		postFilter = setMatcher( postFilter );
	}
	if ( postFinder && !postFinder[ expando ] ) {
		postFinder = setMatcher( postFinder, postSelector );
	}
	return markFunction(function( seed, results, context, xml ) {
		var temp, i, elem,
			preMap = [],
			postMap = [],
			preexisting = results.length,

			// Get initial elements from seed or context
			elems = seed || multipleContexts( selector || "*", context.nodeType ? [ context ] : context, [] ),

			// Prefilter to get matcher input, preserving a map for seed-results synchronization
			matcherIn = preFilter && ( seed || !selector ) ?
				condense( elems, preMap, preFilter, context, xml ) :
				elems,

			matcherOut = matcher ?
				// If we have a postFinder, or filtered seed, or non-seed postFilter or preexisting results,
				postFinder || ( seed ? preFilter : preexisting || postFilter ) ?

					// ...intermediate processing is necessary
					[] :

					// ...otherwise use results directly
					results :
				matcherIn;

		// Find primary matches
		if ( matcher ) {
			matcher( matcherIn, matcherOut, context, xml );
		}

		// Apply postFilter
		if ( postFilter ) {
			temp = condense( matcherOut, postMap );
			postFilter( temp, [], context, xml );

			// Un-match failing elements by moving them back to matcherIn
			i = temp.length;
			while ( i-- ) {
				if ( (elem = temp[i]) ) {
					matcherOut[ postMap[i] ] = !(matcherIn[ postMap[i] ] = elem);
				}
			}
		}

		if ( seed ) {
			if ( postFinder || preFilter ) {
				if ( postFinder ) {
					// Get the final matcherOut by condensing this intermediate into postFinder contexts
					temp = [];
					i = matcherOut.length;
					while ( i-- ) {
						if ( (elem = matcherOut[i]) ) {
							// Restore matcherIn since elem is not yet a final match
							temp.push( (matcherIn[i] = elem) );
						}
					}
					postFinder( null, (matcherOut = []), temp, xml );
				}

				// Move matched elements from seed to results to keep them synchronized
				i = matcherOut.length;
				while ( i-- ) {
					if ( (elem = matcherOut[i]) &&
						(temp = postFinder ? indexOf( seed, elem ) : preMap[i]) > -1 ) {

						seed[temp] = !(results[temp] = elem);
					}
				}
			}

		// Add elements to results, through postFinder if defined
		} else {
			matcherOut = condense(
				matcherOut === results ?
					matcherOut.splice( preexisting, matcherOut.length ) :
					matcherOut
			);
			if ( postFinder ) {
				postFinder( null, results, matcherOut, xml );
			} else {
				push.apply( results, matcherOut );
			}
		}
	});
}

function matcherFromTokens( tokens ) {
	var checkContext, matcher, j,
		len = tokens.length,
		leadingRelative = Expr.relative[ tokens[0].type ],
		implicitRelative = leadingRelative || Expr.relative[" "],
		i = leadingRelative ? 1 : 0,

		// The foundational matcher ensures that elements are reachable from top-level context(s)
		matchContext = addCombinator( function( elem ) {
			return elem === checkContext;
		}, implicitRelative, true ),
		matchAnyContext = addCombinator( function( elem ) {
			return indexOf( checkContext, elem ) > -1;
		}, implicitRelative, true ),
		matchers = [ function( elem, context, xml ) {
			var ret = ( !leadingRelative && ( xml || context !== outermostContext ) ) || (
				(checkContext = context).nodeType ?
					matchContext( elem, context, xml ) :
					matchAnyContext( elem, context, xml ) );
			// Avoid hanging onto element (issue #299)
			checkContext = null;
			return ret;
		} ];

	for ( ; i < len; i++ ) {
		if ( (matcher = Expr.relative[ tokens[i].type ]) ) {
			matchers = [ addCombinator(elementMatcher( matchers ), matcher) ];
		} else {
			matcher = Expr.filter[ tokens[i].type ].apply( null, tokens[i].matches );

			// Return special upon seeing a positional matcher
			if ( matcher[ expando ] ) {
				// Find the next relative operator (if any) for proper handling
				j = ++i;
				for ( ; j < len; j++ ) {
					if ( Expr.relative[ tokens[j].type ] ) {
						break;
					}
				}
				return setMatcher(
					i > 1 && elementMatcher( matchers ),
					i > 1 && toSelector(
						// If the preceding token was a descendant combinator, insert an implicit any-element `*`
						tokens.slice( 0, i - 1 ).concat({ value: tokens[ i - 2 ].type === " " ? "*" : "" })
					).replace( rtrim, "$1" ),
					matcher,
					i < j && matcherFromTokens( tokens.slice( i, j ) ),
					j < len && matcherFromTokens( (tokens = tokens.slice( j )) ),
					j < len && toSelector( tokens )
				);
			}
			matchers.push( matcher );
		}
	}

	return elementMatcher( matchers );
}

function matcherFromGroupMatchers( elementMatchers, setMatchers ) {
	var bySet = setMatchers.length > 0,
		byElement = elementMatchers.length > 0,
		superMatcher = function( seed, context, xml, results, outermost ) {
			var elem, j, matcher,
				matchedCount = 0,
				i = "0",
				unmatched = seed && [],
				setMatched = [],
				contextBackup = outermostContext,
				// We must always have either seed elements or outermost context
				elems = seed || byElement && Expr.find["TAG"]( "*", outermost ),
				// Use integer dirruns iff this is the outermost matcher
				dirrunsUnique = (dirruns += contextBackup == null ? 1 : Math.random() || 0.1),
				len = elems.length;

			if ( outermost ) {
				outermostContext = context === document || context || outermost;
			}

			// Add elements passing elementMatchers directly to results
			// Support: IE<9, Safari
			// Tolerate NodeList properties (IE: "length"; Safari: <number>) matching elements by id
			for ( ; i !== len && (elem = elems[i]) != null; i++ ) {
				if ( byElement && elem ) {
					j = 0;
					if ( !context && elem.ownerDocument !== document ) {
						setDocument( elem );
						xml = !documentIsHTML;
					}
					while ( (matcher = elementMatchers[j++]) ) {
						if ( matcher( elem, context || document, xml) ) {
							results.push( elem );
							break;
						}
					}
					if ( outermost ) {
						dirruns = dirrunsUnique;
					}
				}

				// Track unmatched elements for set filters
				if ( bySet ) {
					// They will have gone through all possible matchers
					if ( (elem = !matcher && elem) ) {
						matchedCount--;
					}

					// Lengthen the array for every element, matched or not
					if ( seed ) {
						unmatched.push( elem );
					}
				}
			}

			// `i` is now the count of elements visited above, and adding it to `matchedCount`
			// makes the latter nonnegative.
			matchedCount += i;

			// Apply set filters to unmatched elements
			// NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
			// equals `i`), unless we didn't visit _any_ elements in the above loop because we have
			// no element matchers and no seed.
			// Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
			// case, which will result in a "00" `matchedCount` that differs from `i` but is also
			// numerically zero.
			if ( bySet && i !== matchedCount ) {
				j = 0;
				while ( (matcher = setMatchers[j++]) ) {
					matcher( unmatched, setMatched, context, xml );
				}

				if ( seed ) {
					// Reintegrate element matches to eliminate the need for sorting
					if ( matchedCount > 0 ) {
						while ( i-- ) {
							if ( !(unmatched[i] || setMatched[i]) ) {
								setMatched[i] = pop.call( results );
							}
						}
					}

					// Discard index placeholder values to get only actual matches
					setMatched = condense( setMatched );
				}

				// Add matches to results
				push.apply( results, setMatched );

				// Seedless set matches succeeding multiple successful matchers stipulate sorting
				if ( outermost && !seed && setMatched.length > 0 &&
					( matchedCount + setMatchers.length ) > 1 ) {

					Sizzle.uniqueSort( results );
				}
			}

			// Override manipulation of globals by nested matchers
			if ( outermost ) {
				dirruns = dirrunsUnique;
				outermostContext = contextBackup;
			}

			return unmatched;
		};

	return bySet ?
		markFunction( superMatcher ) :
		superMatcher;
}

compile = Sizzle.compile = function( selector, match /* Internal Use Only */ ) {
	var i,
		setMatchers = [],
		elementMatchers = [],
		cached = compilerCache[ selector + " " ];

	if ( !cached ) {
		// Generate a function of recursive functions that can be used to check each element
		if ( !match ) {
			match = tokenize( selector );
		}
		i = match.length;
		while ( i-- ) {
			cached = matcherFromTokens( match[i] );
			if ( cached[ expando ] ) {
				setMatchers.push( cached );
			} else {
				elementMatchers.push( cached );
			}
		}

		// Cache the compiled function
		cached = compilerCache( selector, matcherFromGroupMatchers( elementMatchers, setMatchers ) );

		// Save selector and tokenization
		cached.selector = selector;
	}
	return cached;
};

/**
 * A low-level selection function that works with Sizzle's compiled
 *  selector functions
 * @param {String|Function} selector A selector or a pre-compiled
 *  selector function built with Sizzle.compile
 * @param {Element} context
 * @param {Array} [results]
 * @param {Array} [seed] A set of elements to match against
 */
select = Sizzle.select = function( selector, context, results, seed ) {
	var i, tokens, token, type, find,
		compiled = typeof selector === "function" && selector,
		match = !seed && tokenize( (selector = compiled.selector || selector) );

	results = results || [];

	// Try to minimize operations if there is only one selector in the list and no seed
	// (the latter of which guarantees us context)
	if ( match.length === 1 ) {

		// Reduce context if the leading compound selector is an ID
		tokens = match[0] = match[0].slice( 0 );
		if ( tokens.length > 2 && (token = tokens[0]).type === "ID" &&
				support.getById && context.nodeType === 9 && documentIsHTML &&
				Expr.relative[ tokens[1].type ] ) {

			context = ( Expr.find["ID"]( token.matches[0].replace(runescape, funescape), context ) || [] )[0];
			if ( !context ) {
				return results;

			// Precompiled matchers will still verify ancestry, so step up a level
			} else if ( compiled ) {
				context = context.parentNode;
			}

			selector = selector.slice( tokens.shift().value.length );
		}

		// Fetch a seed set for right-to-left matching
		i = matchExpr["needsContext"].test( selector ) ? 0 : tokens.length;
		while ( i-- ) {
			token = tokens[i];

			// Abort if we hit a combinator
			if ( Expr.relative[ (type = token.type) ] ) {
				break;
			}
			if ( (find = Expr.find[ type ]) ) {
				// Search, expanding context for leading sibling combinators
				if ( (seed = find(
					token.matches[0].replace( runescape, funescape ),
					rsibling.test( tokens[0].type ) && testContext( context.parentNode ) || context
				)) ) {

					// If seed is empty or no tokens remain, we can return early
					tokens.splice( i, 1 );
					selector = seed.length && toSelector( tokens );
					if ( !selector ) {
						push.apply( results, seed );
						return results;
					}

					break;
				}
			}
		}
	}

	// Compile and execute a filtering function if one is not provided
	// Provide `match` to avoid retokenization if we modified the selector above
	( compiled || compile( selector, match ) )(
		seed,
		context,
		!documentIsHTML,
		results,
		!context || rsibling.test( selector ) && testContext( context.parentNode ) || context
	);
	return results;
};

// One-time assignments

// Sort stability
support.sortStable = expando.split("").sort( sortOrder ).join("") === expando;

// Support: Chrome 14-35+
// Always assume duplicates if they aren't passed to the comparison function
support.detectDuplicates = !!hasDuplicate;

// Initialize against the default document
setDocument();

// Support: Webkit<537.32 - Safari 6.0.3/Chrome 25 (fixed in Chrome 27)
// Detached nodes confoundingly follow *each other*
support.sortDetached = assert(function( div1 ) {
	// Should return 1, but returns 4 (following)
	return div1.compareDocumentPosition( document.createElement("div") ) & 1;
});

// Support: IE<8
// Prevent attribute/property "interpolation"
// http://msdn.microsoft.com/en-us/library/ms536429%28VS.85%29.aspx
if ( !assert(function( div ) {
	div.innerHTML = "<a href='#'></a>";
	return div.firstChild.getAttribute("href") === "#" ;
}) ) {
	addHandle( "type|href|height|width", function( elem, name, isXML ) {
		if ( !isXML ) {
			return elem.getAttribute( name, name.toLowerCase() === "type" ? 1 : 2 );
		}
	});
}

// Support: IE<9
// Use defaultValue in place of getAttribute("value")
if ( !support.attributes || !assert(function( div ) {
	div.innerHTML = "<input/>";
	div.firstChild.setAttribute( "value", "" );
	return div.firstChild.getAttribute( "value" ) === "";
}) ) {
	addHandle( "value", function( elem, name, isXML ) {
		if ( !isXML && elem.nodeName.toLowerCase() === "input" ) {
			return elem.defaultValue;
		}
	});
}

// Support: IE<9
// Use getAttributeNode to fetch booleans when getAttribute lies
if ( !assert(function( div ) {
	return div.getAttribute("disabled") == null;
}) ) {
	addHandle( booleans, function( elem, name, isXML ) {
		var val;
		if ( !isXML ) {
			return elem[ name ] === true ? name.toLowerCase() :
					(val = elem.getAttributeNode( name )) && val.specified ?
					val.value :
				null;
		}
	});
}

return Sizzle;

})( window );



jQuery.find = Sizzle;
jQuery.expr = Sizzle.selectors;
jQuery.expr[ ":" ] = jQuery.expr.pseudos;
jQuery.uniqueSort = jQuery.unique = Sizzle.uniqueSort;
jQuery.text = Sizzle.getText;
jQuery.isXMLDoc = Sizzle.isXML;
jQuery.contains = Sizzle.contains;



var dir = function( elem, dir, until ) {
	var matched = [],
		truncate = until !== undefined;

	while ( ( elem = elem[ dir ] ) && elem.nodeType !== 9 ) {
		if ( elem.nodeType === 1 ) {
			if ( truncate && jQuery( elem ).is( until ) ) {
				break;
			}
			matched.push( elem );
		}
	}
	return matched;
};


var siblings = function( n, elem ) {
	var matched = [];

	for ( ; n; n = n.nextSibling ) {
		if ( n.nodeType === 1 && n !== elem ) {
			matched.push( n );
		}
	}

	return matched;
};


var rneedsContext = jQuery.expr.match.needsContext;

var rsingleTag = ( /^<([\w-]+)\s*\/?>(?:<\/\1>|)$/ );



var risSimple = /^.[^:#\[\.,]*$/;

// Implement the identical functionality for filter and not
function winnow( elements, qualifier, not ) {
	if ( jQuery.isFunction( qualifier ) ) {
		return jQuery.grep( elements, function( elem, i ) {
			/* jshint -W018 */
			return !!qualifier.call( elem, i, elem ) !== not;
		} );

	}

	if ( qualifier.nodeType ) {
		return jQuery.grep( elements, function( elem ) {
			return ( elem === qualifier ) !== not;
		} );

	}

	if ( typeof qualifier === "string" ) {
		if ( risSimple.test( qualifier ) ) {
			return jQuery.filter( qualifier, elements, not );
		}

		qualifier = jQuery.filter( qualifier, elements );
	}

	return jQuery.grep( elements, function( elem ) {
		return ( jQuery.inArray( elem, qualifier ) > -1 ) !== not;
	} );
}

jQuery.filter = function( expr, elems, not ) {
	var elem = elems[ 0 ];

	if ( not ) {
		expr = ":not(" + expr + ")";
	}

	return elems.length === 1 && elem.nodeType === 1 ?
		jQuery.find.matchesSelector( elem, expr ) ? [ elem ] : [] :
		jQuery.find.matches( expr, jQuery.grep( elems, function( elem ) {
			return elem.nodeType === 1;
		} ) );
};

jQuery.fn.extend( {
	find: function( selector ) {
		var i,
			ret = [],
			self = this,
			len = self.length;

		if ( typeof selector !== "string" ) {
			return this.pushStack( jQuery( selector ).filter( function() {
				for ( i = 0; i < len; i++ ) {
					if ( jQuery.contains( self[ i ], this ) ) {
						return true;
					}
				}
			} ) );
		}

		for ( i = 0; i < len; i++ ) {
			jQuery.find( selector, self[ i ], ret );
		}

		// Needed because $( selector, context ) becomes $( context ).find( selector )
		ret = this.pushStack( len > 1 ? jQuery.unique( ret ) : ret );
		ret.selector = this.selector ? this.selector + " " + selector : selector;
		return ret;
	},
	filter: function( selector ) {
		return this.pushStack( winnow( this, selector || [], false ) );
	},
	not: function( selector ) {
		return this.pushStack( winnow( this, selector || [], true ) );
	},
	is: function( selector ) {
		return !!winnow(
			this,

			// If this is a positional/relative selector, check membership in the returned set
			// so $("p:first").is("p:last") won't return true for a doc with two "p".
			typeof selector === "string" && rneedsContext.test( selector ) ?
				jQuery( selector ) :
				selector || [],
			false
		).length;
	}
} );


// Initialize a jQuery object


// A central reference to the root jQuery(document)
var rootjQuery,

	// A simple way to check for HTML strings
	// Prioritize #id over <tag> to avoid XSS via location.hash (#9521)
	// Strict HTML recognition (#11290: must start with <)
	rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]*))$/,

	init = jQuery.fn.init = function( selector, context, root ) {
		var match, elem;

		// HANDLE: $(""), $(null), $(undefined), $(false)
		if ( !selector ) {
			return this;
		}

		// init accepts an alternate rootjQuery
		// so migrate can support jQuery.sub (gh-2101)
		root = root || rootjQuery;

		// Handle HTML strings
		if ( typeof selector === "string" ) {
			if ( selector.charAt( 0 ) === "<" &&
				selector.charAt( selector.length - 1 ) === ">" &&
				selector.length >= 3 ) {

				// Assume that strings that start and end with <> are HTML and skip the regex check
				match = [ null, selector, null ];

			} else {
				match = rquickExpr.exec( selector );
			}

			// Match html or make sure no context is specified for #id
			if ( match && ( match[ 1 ] || !context ) ) {

				// HANDLE: $(html) -> $(array)
				if ( match[ 1 ] ) {
					context = context instanceof jQuery ? context[ 0 ] : context;

					// scripts is true for back-compat
					// Intentionally let the error be thrown if parseHTML is not present
					jQuery.merge( this, jQuery.parseHTML(
						match[ 1 ],
						context && context.nodeType ? context.ownerDocument || context : document,
						true
					) );

					// HANDLE: $(html, props)
					if ( rsingleTag.test( match[ 1 ] ) && jQuery.isPlainObject( context ) ) {
						for ( match in context ) {

							// Properties of context are called as methods if possible
							if ( jQuery.isFunction( this[ match ] ) ) {
								this[ match ]( context[ match ] );

							// ...and otherwise set as attributes
							} else {
								this.attr( match, context[ match ] );
							}
						}
					}

					return this;

				// HANDLE: $(#id)
				} else {
					elem = document.getElementById( match[ 2 ] );

					// Check parentNode to catch when Blackberry 4.6 returns
					// nodes that are no longer in the document #6963
					if ( elem && elem.parentNode ) {

						// Handle the case where IE and Opera return items
						// by name instead of ID
						if ( elem.id !== match[ 2 ] ) {
							return rootjQuery.find( selector );
						}

						// Otherwise, we inject the element directly into the jQuery object
						this.length = 1;
						this[ 0 ] = elem;
					}

					this.context = document;
					this.selector = selector;
					return this;
				}

			// HANDLE: $(expr, $(...))
			} else if ( !context || context.jquery ) {
				return ( context || root ).find( selector );

			// HANDLE: $(expr, context)
			// (which is just equivalent to: $(context).find(expr)
			} else {
				return this.constructor( context ).find( selector );
			}

		// HANDLE: $(DOMElement)
		} else if ( selector.nodeType ) {
			this.context = this[ 0 ] = selector;
			this.length = 1;
			return this;

		// HANDLE: $(function)
		// Shortcut for document ready
		} else if ( jQuery.isFunction( selector ) ) {
			return typeof root.ready !== "undefined" ?
				root.ready( selector ) :

				// Execute immediately if ready is not present
				selector( jQuery );
		}

		if ( selector.selector !== undefined ) {
			this.selector = selector.selector;
			this.context = selector.context;
		}

		return jQuery.makeArray( selector, this );
	};

// Give the init function the jQuery prototype for later instantiation
init.prototype = jQuery.fn;

// Initialize central reference
rootjQuery = jQuery( document );


var rparentsprev = /^(?:parents|prev(?:Until|All))/,

	// methods guaranteed to produce a unique set when starting from a unique set
	guaranteedUnique = {
		children: true,
		contents: true,
		next: true,
		prev: true
	};

jQuery.fn.extend( {
	has: function( target ) {
		var i,
			targets = jQuery( target, this ),
			len = targets.length;

		return this.filter( function() {
			for ( i = 0; i < len; i++ ) {
				if ( jQuery.contains( this, targets[ i ] ) ) {
					return true;
				}
			}
		} );
	},

	closest: function( selectors, context ) {
		var cur,
			i = 0,
			l = this.length,
			matched = [],
			pos = rneedsContext.test( selectors ) || typeof selectors !== "string" ?
				jQuery( selectors, context || this.context ) :
				0;

		for ( ; i < l; i++ ) {
			for ( cur = this[ i ]; cur && cur !== context; cur = cur.parentNode ) {

				// Always skip document fragments
				if ( cur.nodeType < 11 && ( pos ?
					pos.index( cur ) > -1 :

					// Don't pass non-elements to Sizzle
					cur.nodeType === 1 &&
						jQuery.find.matchesSelector( cur, selectors ) ) ) {

					matched.push( cur );
					break;
				}
			}
		}

		return this.pushStack( matched.length > 1 ? jQuery.uniqueSort( matched ) : matched );
	},

	// Determine the position of an element within
	// the matched set of elements
	index: function( elem ) {

		// No argument, return index in parent
		if ( !elem ) {
			return ( this[ 0 ] && this[ 0 ].parentNode ) ? this.first().prevAll().length : -1;
		}

		// index in selector
		if ( typeof elem === "string" ) {
			return jQuery.inArray( this[ 0 ], jQuery( elem ) );
		}

		// Locate the position of the desired element
		return jQuery.inArray(

			// If it receives a jQuery object, the first element is used
			elem.jquery ? elem[ 0 ] : elem, this );
	},

	add: function( selector, context ) {
		return this.pushStack(
			jQuery.uniqueSort(
				jQuery.merge( this.get(), jQuery( selector, context ) )
			)
		);
	},

	addBack: function( selector ) {
		return this.add( selector == null ?
			this.prevObject : this.prevObject.filter( selector )
		);
	}
} );

function sibling( cur, dir ) {
	do {
		cur = cur[ dir ];
	} while ( cur && cur.nodeType !== 1 );

	return cur;
}

jQuery.each( {
	parent: function( elem ) {
		var parent = elem.parentNode;
		return parent && parent.nodeType !== 11 ? parent : null;
	},
	parents: function( elem ) {
		return dir( elem, "parentNode" );
	},
	parentsUntil: function( elem, i, until ) {
		return dir( elem, "parentNode", until );
	},
	next: function( elem ) {
		return sibling( elem, "nextSibling" );
	},
	prev: function( elem ) {
		return sibling( elem, "previousSibling" );
	},
	nextAll: function( elem ) {
		return dir( elem, "nextSibling" );
	},
	prevAll: function( elem ) {
		return dir( elem, "previousSibling" );
	},
	nextUntil: function( elem, i, until ) {
		return dir( elem, "nextSibling", until );
	},
	prevUntil: function( elem, i, until ) {
		return dir( elem, "previousSibling", until );
	},
	siblings: function( elem ) {
		return siblings( ( elem.parentNode || {} ).firstChild, elem );
	},
	children: function( elem ) {
		return siblings( elem.firstChild );
	},
	contents: function( elem ) {
		return jQuery.nodeName( elem, "iframe" ) ?
			elem.contentDocument || elem.contentWindow.document :
			jQuery.merge( [], elem.childNodes );
	}
}, function( name, fn ) {
	jQuery.fn[ name ] = function( until, selector ) {
		var ret = jQuery.map( this, fn, until );

		if ( name.slice( -5 ) !== "Until" ) {
			selector = until;
		}

		if ( selector && typeof selector === "string" ) {
			ret = jQuery.filter( selector, ret );
		}

		if ( this.length > 1 ) {

			// Remove duplicates
			if ( !guaranteedUnique[ name ] ) {
				ret = jQuery.uniqueSort( ret );
			}

			// Reverse order for parents* and prev-derivatives
			if ( rparentsprev.test( name ) ) {
				ret = ret.reverse();
			}
		}

		return this.pushStack( ret );
	};
} );
var rnotwhite = ( /\S+/g );



// Convert String-formatted options into Object-formatted ones
function createOptions( options ) {
	var object = {};
	jQuery.each( options.match( rnotwhite ) || [], function( _, flag ) {
		object[ flag ] = true;
	} );
	return object;
}

/*
 * Create a callback list using the following parameters:
 *
 *	options: an optional list of space-separated options that will change how
 *			the callback list behaves or a more traditional option object
 *
 * By default a callback list will act like an event callback list and can be
 * "fired" multiple times.
 *
 * Possible options:
 *
 *	once:			will ensure the callback list can only be fired once (like a Deferred)
 *
 *	memory:			will keep track of previous values and will call any callback added
 *					after the list has been fired right away with the latest "memorized"
 *					values (like a Deferred)
 *
 *	unique:			will ensure a callback can only be added once (no duplicate in the list)
 *
 *	stopOnFalse:	interrupt callings when a callback returns false
 *
 */
jQuery.Callbacks = function( options ) {

	// Convert options from String-formatted to Object-formatted if needed
	// (we check in cache first)
	options = typeof options === "string" ?
		createOptions( options ) :
		jQuery.extend( {}, options );

	var // Flag to know if list is currently firing
		firing,

		// Last fire value for non-forgettable lists
		memory,

		// Flag to know if list was already fired
		fired,

		// Flag to prevent firing
		locked,

		// Actual callback list
		list = [],

		// Queue of execution data for repeatable lists
		queue = [],

		// Index of currently firing callback (modified by add/remove as needed)
		firingIndex = -1,

		// Fire callbacks
		fire = function() {

			// Enforce single-firing
			locked = options.once;

			// Execute callbacks for all pending executions,
			// respecting firingIndex overrides and runtime changes
			fired = firing = true;
			for ( ; queue.length; firingIndex = -1 ) {
				memory = queue.shift();
				while ( ++firingIndex < list.length ) {

					// Run callback and check for early termination
					if ( list[ firingIndex ].apply( memory[ 0 ], memory[ 1 ] ) === false &&
						options.stopOnFalse ) {

						// Jump to end and forget the data so .add doesn't re-fire
						firingIndex = list.length;
						memory = false;
					}
				}
			}

			// Forget the data if we're done with it
			if ( !options.memory ) {
				memory = false;
			}

			firing = false;

			// Clean up if we're done firing for good
			if ( locked ) {

				// Keep an empty list if we have data for future add calls
				if ( memory ) {
					list = [];

				// Otherwise, this object is spent
				} else {
					list = "";
				}
			}
		},

		// Actual Callbacks object
		self = {

			// Add a callback or a collection of callbacks to the list
			add: function() {
				if ( list ) {

					// If we have memory from a past run, we should fire after adding
					if ( memory && !firing ) {
						firingIndex = list.length - 1;
						queue.push( memory );
					}

					( function add( args ) {
						jQuery.each( args, function( _, arg ) {
							if ( jQuery.isFunction( arg ) ) {
								if ( !options.unique || !self.has( arg ) ) {
									list.push( arg );
								}
							} else if ( arg && arg.length && jQuery.type( arg ) !== "string" ) {

								// Inspect recursively
								add( arg );
							}
						} );
					} )( arguments );

					if ( memory && !firing ) {
						fire();
					}
				}
				return this;
			},

			// Remove a callback from the list
			remove: function() {
				jQuery.each( arguments, function( _, arg ) {
					var index;
					while ( ( index = jQuery.inArray( arg, list, index ) ) > -1 ) {
						list.splice( index, 1 );

						// Handle firing indexes
						if ( index <= firingIndex ) {
							firingIndex--;
						}
					}
				} );
				return this;
			},

			// Check if a given callback is in the list.
			// If no argument is given, return whether or not list has callbacks attached.
			has: function( fn ) {
				return fn ?
					jQuery.inArray( fn, list ) > -1 :
					list.length > 0;
			},

			// Remove all callbacks from the list
			empty: function() {
				if ( list ) {
					list = [];
				}
				return this;
			},

			// Disable .fire and .add
			// Abort any current/pending executions
			// Clear all callbacks and values
			disable: function() {
				locked = queue = [];
				list = memory = "";
				return this;
			},
			disabled: function() {
				return !list;
			},

			// Disable .fire
			// Also disable .add unless we have memory (since it would have no effect)
			// Abort any pending executions
			lock: function() {
				locked = true;
				if ( !memory ) {
					self.disable();
				}
				return this;
			},
			locked: function() {
				return !!locked;
			},

			// Call all callbacks with the given context and arguments
			fireWith: function( context, args ) {
				if ( !locked ) {
					args = args || [];
					args = [ context, args.slice ? args.slice() : args ];
					queue.push( args );
					if ( !firing ) {
						fire();
					}
				}
				return this;
			},

			// Call all the callbacks with the given arguments
			fire: function() {
				self.fireWith( this, arguments );
				return this;
			},

			// To know if the callbacks have already been called at least once
			fired: function() {
				return !!fired;
			}
		};

	return self;
};


jQuery.extend( {

	Deferred: function( func ) {
		var tuples = [

				// action, add listener, listener list, final state
				[ "resolve", "done", jQuery.Callbacks( "once memory" ), "resolved" ],
				[ "reject", "fail", jQuery.Callbacks( "once memory" ), "rejected" ],
				[ "notify", "progress", jQuery.Callbacks( "memory" ) ]
			],
			state = "pending",
			promise = {
				state: function() {
					return state;
				},
				always: function() {
					deferred.done( arguments ).fail( arguments );
					return this;
				},
				then: function( /* fnDone, fnFail, fnProgress */ ) {
					var fns = arguments;
					return jQuery.Deferred( function( newDefer ) {
						jQuery.each( tuples, function( i, tuple ) {
							var fn = jQuery.isFunction( fns[ i ] ) && fns[ i ];

							// deferred[ done | fail | progress ] for forwarding actions to newDefer
							deferred[ tuple[ 1 ] ]( function() {
								var returned = fn && fn.apply( this, arguments );
								if ( returned && jQuery.isFunction( returned.promise ) ) {
									returned.promise()
										.progress( newDefer.notify )
										.done( newDefer.resolve )
										.fail( newDefer.reject );
								} else {
									newDefer[ tuple[ 0 ] + "With" ](
										this === promise ? newDefer.promise() : this,
										fn ? [ returned ] : arguments
									);
								}
							} );
						} );
						fns = null;
					} ).promise();
				},

				// Get a promise for this deferred
				// If obj is provided, the promise aspect is added to the object
				promise: function( obj ) {
					return obj != null ? jQuery.extend( obj, promise ) : promise;
				}
			},
			deferred = {};

		// Keep pipe for back-compat
		promise.pipe = promise.then;

		// Add list-specific methods
		jQuery.each( tuples, function( i, tuple ) {
			var list = tuple[ 2 ],
				stateString = tuple[ 3 ];

			// promise[ done | fail | progress ] = list.add
			promise[ tuple[ 1 ] ] = list.add;

			// Handle state
			if ( stateString ) {
				list.add( function() {

					// state = [ resolved | rejected ]
					state = stateString;

				// [ reject_list | resolve_list ].disable; progress_list.lock
				}, tuples[ i ^ 1 ][ 2 ].disable, tuples[ 2 ][ 2 ].lock );
			}

			// deferred[ resolve | reject | notify ]
			deferred[ tuple[ 0 ] ] = function() {
				deferred[ tuple[ 0 ] + "With" ]( this === deferred ? promise : this, arguments );
				return this;
			};
			deferred[ tuple[ 0 ] + "With" ] = list.fireWith;
		} );

		// Make the deferred a promise
		promise.promise( deferred );

		// Call given func if any
		if ( func ) {
			func.call( deferred, deferred );
		}

		// All done!
		return deferred;
	},

	// Deferred helper
	when: function( subordinate /* , ..., subordinateN */ ) {
		var i = 0,
			resolveValues = slice.call( arguments ),
			length = resolveValues.length,

			// the count of uncompleted subordinates
			remaining = length !== 1 ||
				( subordinate && jQuery.isFunction( subordinate.promise ) ) ? length : 0,

			// the master Deferred.
			// If resolveValues consist of only a single Deferred, just use that.
			deferred = remaining === 1 ? subordinate : jQuery.Deferred(),

			// Update function for both resolve and progress values
			updateFunc = function( i, contexts, values ) {
				return function( value ) {
					contexts[ i ] = this;
					values[ i ] = arguments.length > 1 ? slice.call( arguments ) : value;
					if ( values === progressValues ) {
						deferred.notifyWith( contexts, values );

					} else if ( !( --remaining ) ) {
						deferred.resolveWith( contexts, values );
					}
				};
			},

			progressValues, progressContexts, resolveContexts;

		// add listeners to Deferred subordinates; treat others as resolved
		if ( length > 1 ) {
			progressValues = new Array( length );
			progressContexts = new Array( length );
			resolveContexts = new Array( length );
			for ( ; i < length; i++ ) {
				if ( resolveValues[ i ] && jQuery.isFunction( resolveValues[ i ].promise ) ) {
					resolveValues[ i ].promise()
						.progress( updateFunc( i, progressContexts, progressValues ) )
						.done( updateFunc( i, resolveContexts, resolveValues ) )
						.fail( deferred.reject );
				} else {
					--remaining;
				}
			}
		}

		// if we're not waiting on anything, resolve the master
		if ( !remaining ) {
			deferred.resolveWith( resolveContexts, resolveValues );
		}

		return deferred.promise();
	}
} );


// The deferred used on DOM ready
var readyList;

jQuery.fn.ready = function( fn ) {

	// Add the callback
	jQuery.ready.promise().done( fn );

	return this;
};

jQuery.extend( {

	// Is the DOM ready to be used? Set to true once it occurs.
	isReady: false,

	// A counter to track how many items to wait for before
	// the ready event fires. See #6781
	readyWait: 1,

	// Hold (or release) the ready event
	holdReady: function( hold ) {
		if ( hold ) {
			jQuery.readyWait++;
		} else {
			jQuery.ready( true );
		}
	},

	// Handle when the DOM is ready
	ready: function( wait ) {

		// Abort if there are pending holds or we're already ready
		if ( wait === true ? --jQuery.readyWait : jQuery.isReady ) {
			return;
		}

		// Remember that the DOM is ready
		jQuery.isReady = true;

		// If a normal DOM Ready event fired, decrement, and wait if need be
		if ( wait !== true && --jQuery.readyWait > 0 ) {
			return;
		}

		// If there are functions bound, to execute
		readyList.resolveWith( document, [ jQuery ] );

		// Trigger any bound ready events
		if ( jQuery.fn.triggerHandler ) {
			jQuery( document ).triggerHandler( "ready" );
			jQuery( document ).off( "ready" );
		}
	}
} );

/**
 * Clean-up method for dom ready events
 */
function detach() {
	if ( document.addEventListener ) {
		document.removeEventListener( "DOMContentLoaded", completed );
		window.removeEventListener( "load", completed );

	} else {
		document.detachEvent( "onreadystatechange", completed );
		window.detachEvent( "onload", completed );
	}
}

/**
 * The ready event handler and self cleanup method
 */
function completed() {

	// readyState === "complete" is good enough for us to call the dom ready in oldIE
	if ( document.addEventListener ||
		window.event.type === "load" ||
		document.readyState === "complete" ) {

		detach();
		jQuery.ready();
	}
}

jQuery.ready.promise = function( obj ) {
	if ( !readyList ) {

		readyList = jQuery.Deferred();

		// Catch cases where $(document).ready() is called
		// after the browser event has already occurred.
		// Support: IE6-10
		// Older IE sometimes signals "interactive" too soon
		if ( document.readyState === "complete" ||
			( document.readyState !== "loading" && !document.documentElement.doScroll ) ) {

			// Handle it asynchronously to allow scripts the opportunity to delay ready
			window.setTimeout( jQuery.ready );

		// Standards-based browsers support DOMContentLoaded
		} else if ( document.addEventListener ) {

			// Use the handy event callback
			document.addEventListener( "DOMContentLoaded", completed );

			// A fallback to window.onload, that will always work
			window.addEventListener( "load", completed );

		// If IE event model is used
		} else {

			// Ensure firing before onload, maybe late but safe also for iframes
			document.attachEvent( "onreadystatechange", completed );

			// A fallback to window.onload, that will always work
			window.attachEvent( "onload", completed );

			// If IE and not a frame
			// continually check to see if the document is ready
			var top = false;

			try {
				top = window.frameElement == null && document.documentElement;
			} catch ( e ) {}

			if ( top && top.doScroll ) {
				( function doScrollCheck() {
					if ( !jQuery.isReady ) {

						try {

							// Use the trick by Diego Perini
							// http://javascript.nwbox.com/IEContentLoaded/
							top.doScroll( "left" );
						} catch ( e ) {
							return window.setTimeout( doScrollCheck, 50 );
						}

						// detach all dom ready events
						detach();

						// and execute any waiting functions
						jQuery.ready();
					}
				} )();
			}
		}
	}
	return readyList.promise( obj );
};

// Kick off the DOM ready check even if the user does not
jQuery.ready.promise();




// Support: IE<9
// Iteration over object's inherited properties before its own
var i;
for ( i in jQuery( support ) ) {
	break;
}
support.ownFirst = i === "0";

// Note: most support tests are defined in their respective modules.
// false until the test is run
support.inlineBlockNeedsLayout = false;

// Execute ASAP in case we need to set body.style.zoom
jQuery( function() {

	// Minified: var a,b,c,d
	var val, div, body, container;

	body = document.getElementsByTagName( "body" )[ 0 ];
	if ( !body || !body.style ) {

		// Return for frameset docs that don't have a body
		return;
	}

	// Setup
	div = document.createElement( "div" );
	container = document.createElement( "div" );
	container.style.cssText = "position:absolute;border:0;width:0;height:0;top:0;left:-9999px";
	body.appendChild( container ).appendChild( div );

	if ( typeof div.style.zoom !== "undefined" ) {

		// Support: IE<8
		// Check if natively block-level elements act like inline-block
		// elements when setting their display to 'inline' and giving
		// them layout
		div.style.cssText = "display:inline;margin:0;border:0;padding:1px;width:1px;zoom:1";

		support.inlineBlockNeedsLayout = val = div.offsetWidth === 3;
		if ( val ) {

			// Prevent IE 6 from affecting layout for positioned elements #11048
			// Prevent IE from shrinking the body in IE 7 mode #12869
			// Support: IE<8
			body.style.zoom = 1;
		}
	}

	body.removeChild( container );
} );


( function() {
	var div = document.createElement( "div" );

	// Support: IE<9
	support.deleteExpando = true;
	try {
		delete div.test;
	} catch ( e ) {
		support.deleteExpando = false;
	}

	// Null elements to avoid leaks in IE.
	div = null;
} )();
var acceptData = function( elem ) {
	var noData = jQuery.noData[ ( elem.nodeName + " " ).toLowerCase() ],
		nodeType = +elem.nodeType || 1;

	// Do not set data on non-element DOM nodes because it will not be cleared (#8335).
	return nodeType !== 1 && nodeType !== 9 ?
		false :

		// Nodes accept data unless otherwise specified; rejection can be conditional
		!noData || noData !== true && elem.getAttribute( "classid" ) === noData;
};




var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
	rmultiDash = /([A-Z])/g;

function dataAttr( elem, key, data ) {

	// If nothing was found internally, try to fetch any
	// data from the HTML5 data-* attribute
	if ( data === undefined && elem.nodeType === 1 ) {

		var name = "data-" + key.replace( rmultiDash, "-$1" ).toLowerCase();

		data = elem.getAttribute( name );

		if ( typeof data === "string" ) {
			try {
				data = data === "true" ? true :
					data === "false" ? false :
					data === "null" ? null :

					// Only convert to a number if it doesn't change the string
					+data + "" === data ? +data :
					rbrace.test( data ) ? jQuery.parseJSON( data ) :
					data;
			} catch ( e ) {}

			// Make sure we set the data so it isn't changed later
			jQuery.data( elem, key, data );

		} else {
			data = undefined;
		}
	}

	return data;
}

// checks a cache object for emptiness
function isEmptyDataObject( obj ) {
	var name;
	for ( name in obj ) {

		// if the public data object is empty, the private is still empty
		if ( name === "data" && jQuery.isEmptyObject( obj[ name ] ) ) {
			continue;
		}
		if ( name !== "toJSON" ) {
			return false;
		}
	}

	return true;
}

function internalData( elem, name, data, pvt /* Internal Use Only */ ) {
	if ( !acceptData( elem ) ) {
		return;
	}

	var ret, thisCache,
		internalKey = jQuery.expando,

		// We have to handle DOM nodes and JS objects differently because IE6-7
		// can't GC object references properly across the DOM-JS boundary
		isNode = elem.nodeType,

		// Only DOM nodes need the global jQuery cache; JS object data is
		// attached directly to the object so GC can occur automatically
		cache = isNode ? jQuery.cache : elem,

		// Only defining an ID for JS objects if its cache already exists allows
		// the code to shortcut on the same path as a DOM node with no cache
		id = isNode ? elem[ internalKey ] : elem[ internalKey ] && internalKey;

	// Avoid doing any more work than we need to when trying to get data on an
	// object that has no data at all
	if ( ( !id || !cache[ id ] || ( !pvt && !cache[ id ].data ) ) &&
		data === undefined && typeof name === "string" ) {
		return;
	}

	if ( !id ) {

		// Only DOM nodes need a new unique ID for each element since their data
		// ends up in the global cache
		if ( isNode ) {
			id = elem[ internalKey ] = deletedIds.pop() || jQuery.guid++;
		} else {
			id = internalKey;
		}
	}

	if ( !cache[ id ] ) {

		// Avoid exposing jQuery metadata on plain JS objects when the object
		// is serialized using JSON.stringify
		cache[ id ] = isNode ? {} : { toJSON: jQuery.noop };
	}

	// An object can be passed to jQuery.data instead of a key/value pair; this gets
	// shallow copied over onto the existing cache
	if ( typeof name === "object" || typeof name === "function" ) {
		if ( pvt ) {
			cache[ id ] = jQuery.extend( cache[ id ], name );
		} else {
			cache[ id ].data = jQuery.extend( cache[ id ].data, name );
		}
	}

	thisCache = cache[ id ];

	// jQuery data() is stored in a separate object inside the object's internal data
	// cache in order to avoid key collisions between internal data and user-defined
	// data.
	if ( !pvt ) {
		if ( !thisCache.data ) {
			thisCache.data = {};
		}

		thisCache = thisCache.data;
	}

	if ( data !== undefined ) {
		thisCache[ jQuery.camelCase( name ) ] = data;
	}

	// Check for both converted-to-camel and non-converted data property names
	// If a data property was specified
	if ( typeof name === "string" ) {

		// First Try to find as-is property data
		ret = thisCache[ name ];

		// Test for null|undefined property data
		if ( ret == null ) {

			// Try to find the camelCased property
			ret = thisCache[ jQuery.camelCase( name ) ];
		}
	} else {
		ret = thisCache;
	}

	return ret;
}

function internalRemoveData( elem, name, pvt ) {
	if ( !acceptData( elem ) ) {
		return;
	}

	var thisCache, i,
		isNode = elem.nodeType,

		// See jQuery.data for more information
		cache = isNode ? jQuery.cache : elem,
		id = isNode ? elem[ jQuery.expando ] : jQuery.expando;

	// If there is already no cache entry for this object, there is no
	// purpose in continuing
	if ( !cache[ id ] ) {
		return;
	}

	if ( name ) {

		thisCache = pvt ? cache[ id ] : cache[ id ].data;

		if ( thisCache ) {

			// Support array or space separated string names for data keys
			if ( !jQuery.isArray( name ) ) {

				// try the string as a key before any manipulation
				if ( name in thisCache ) {
					name = [ name ];
				} else {

					// split the camel cased version by spaces unless a key with the spaces exists
					name = jQuery.camelCase( name );
					if ( name in thisCache ) {
						name = [ name ];
					} else {
						name = name.split( " " );
					}
				}
			} else {

				// If "name" is an array of keys...
				// When data is initially created, via ("key", "val") signature,
				// keys will be converted to camelCase.
				// Since there is no way to tell _how_ a key was added, remove
				// both plain key and camelCase key. #12786
				// This will only penalize the array argument path.
				name = name.concat( jQuery.map( name, jQuery.camelCase ) );
			}

			i = name.length;
			while ( i-- ) {
				delete thisCache[ name[ i ] ];
			}

			// If there is no data left in the cache, we want to continue
			// and let the cache object itself get destroyed
			if ( pvt ? !isEmptyDataObject( thisCache ) : !jQuery.isEmptyObject( thisCache ) ) {
				return;
			}
		}
	}

	// See jQuery.data for more information
	if ( !pvt ) {
		delete cache[ id ].data;

		// Don't destroy the parent cache unless the internal data object
		// had been the only thing left in it
		if ( !isEmptyDataObject( cache[ id ] ) ) {
			return;
		}
	}

	// Destroy the cache
	if ( isNode ) {
		jQuery.cleanData( [ elem ], true );

	// Use delete when supported for expandos or `cache` is not a window per isWindow (#10080)
	/* jshint eqeqeq: false */
	} else if ( support.deleteExpando || cache != cache.window ) {
		/* jshint eqeqeq: true */
		delete cache[ id ];

	// When all else fails, undefined
	} else {
		cache[ id ] = undefined;
	}
}

jQuery.extend( {
	cache: {},

	// The following elements (space-suffixed to avoid Object.prototype collisions)
	// throw uncatchable exceptions if you attempt to set expando properties
	noData: {
		"applet ": true,
		"embed ": true,

		// ...but Flash objects (which have this classid) *can* handle expandos
		"object ": "clsid:D27CDB6E-AE6D-11cf-96B8-444553540000"
	},

	hasData: function( elem ) {
		elem = elem.nodeType ? jQuery.cache[ elem[ jQuery.expando ] ] : elem[ jQuery.expando ];
		return !!elem && !isEmptyDataObject( elem );
	},

	data: function( elem, name, data ) {
		return internalData( elem, name, data );
	},

	removeData: function( elem, name ) {
		return internalRemoveData( elem, name );
	},

	// For internal use only.
	_data: function( elem, name, data ) {
		return internalData( elem, name, data, true );
	},

	_removeData: function( elem, name ) {
		return internalRemoveData( elem, name, true );
	}
} );

jQuery.fn.extend( {
	data: function( key, value ) {
		var i, name, data,
			elem = this[ 0 ],
			attrs = elem && elem.attributes;

		// Special expections of .data basically thwart jQuery.access,
		// so implement the relevant behavior ourselves

		// Gets all values
		if ( key === undefined ) {
			if ( this.length ) {
				data = jQuery.data( elem );

				if ( elem.nodeType === 1 && !jQuery._data( elem, "parsedAttrs" ) ) {
					i = attrs.length;
					while ( i-- ) {

						// Support: IE11+
						// The attrs elements can be null (#14894)
						if ( attrs[ i ] ) {
							name = attrs[ i ].name;
							if ( name.indexOf( "data-" ) === 0 ) {
								name = jQuery.camelCase( name.slice( 5 ) );
								dataAttr( elem, name, data[ name ] );
							}
						}
					}
					jQuery._data( elem, "parsedAttrs", true );
				}
			}

			return data;
		}

		// Sets multiple values
		if ( typeof key === "object" ) {
			return this.each( function() {
				jQuery.data( this, key );
			} );
		}

		return arguments.length > 1 ?

			// Sets one value
			this.each( function() {
				jQuery.data( this, key, value );
			} ) :

			// Gets one value
			// Try to fetch any internally stored data first
			elem ? dataAttr( elem, key, jQuery.data( elem, key ) ) : undefined;
	},

	removeData: function( key ) {
		return this.each( function() {
			jQuery.removeData( this, key );
		} );
	}
} );


jQuery.extend( {
	queue: function( elem, type, data ) {
		var queue;

		if ( elem ) {
			type = ( type || "fx" ) + "queue";
			queue = jQuery._data( elem, type );

			// Speed up dequeue by getting out quickly if this is just a lookup
			if ( data ) {
				if ( !queue || jQuery.isArray( data ) ) {
					queue = jQuery._data( elem, type, jQuery.makeArray( data ) );
				} else {
					queue.push( data );
				}
			}
			return queue || [];
		}
	},

	dequeue: function( elem, type ) {
		type = type || "fx";

		var queue = jQuery.queue( elem, type ),
			startLength = queue.length,
			fn = queue.shift(),
			hooks = jQuery._queueHooks( elem, type ),
			next = function() {
				jQuery.dequeue( elem, type );
			};

		// If the fx queue is dequeued, always remove the progress sentinel
		if ( fn === "inprogress" ) {
			fn = queue.shift();
			startLength--;
		}

		if ( fn ) {

			// Add a progress sentinel to prevent the fx queue from being
			// automatically dequeued
			if ( type === "fx" ) {
				queue.unshift( "inprogress" );
			}

			// clear up the last queue stop function
			delete hooks.stop;
			fn.call( elem, next, hooks );
		}

		if ( !startLength && hooks ) {
			hooks.empty.fire();
		}
	},

	// not intended for public consumption - generates a queueHooks object,
	// or returns the current one
	_queueHooks: function( elem, type ) {
		var key = type + "queueHooks";
		return jQuery._data( elem, key ) || jQuery._data( elem, key, {
			empty: jQuery.Callbacks( "once memory" ).add( function() {
				jQuery._removeData( elem, type + "queue" );
				jQuery._removeData( elem, key );
			} )
		} );
	}
} );

jQuery.fn.extend( {
	queue: function( type, data ) {
		var setter = 2;

		if ( typeof type !== "string" ) {
			data = type;
			type = "fx";
			setter--;
		}

		if ( arguments.length < setter ) {
			return jQuery.queue( this[ 0 ], type );
		}

		return data === undefined ?
			this :
			this.each( function() {
				var queue = jQuery.queue( this, type, data );

				// ensure a hooks for this queue
				jQuery._queueHooks( this, type );

				if ( type === "fx" && queue[ 0 ] !== "inprogress" ) {
					jQuery.dequeue( this, type );
				}
			} );
	},
	dequeue: function( type ) {
		return this.each( function() {
			jQuery.dequeue( this, type );
		} );
	},
	clearQueue: function( type ) {
		return this.queue( type || "fx", [] );
	},

	// Get a promise resolved when queues of a certain type
	// are emptied (fx is the type by default)
	promise: function( type, obj ) {
		var tmp,
			count = 1,
			defer = jQuery.Deferred(),
			elements = this,
			i = this.length,
			resolve = function() {
				if ( !( --count ) ) {
					defer.resolveWith( elements, [ elements ] );
				}
			};

		if ( typeof type !== "string" ) {
			obj = type;
			type = undefined;
		}
		type = type || "fx";

		while ( i-- ) {
			tmp = jQuery._data( elements[ i ], type + "queueHooks" );
			if ( tmp && tmp.empty ) {
				count++;
				tmp.empty.add( resolve );
			}
		}
		resolve();
		return defer.promise( obj );
	}
} );


( function() {
	var shrinkWrapBlocksVal;

	support.shrinkWrapBlocks = function() {
		if ( shrinkWrapBlocksVal != null ) {
			return shrinkWrapBlocksVal;
		}

		// Will be changed later if needed.
		shrinkWrapBlocksVal = false;

		// Minified: var b,c,d
		var div, body, container;

		body = document.getElementsByTagName( "body" )[ 0 ];
		if ( !body || !body.style ) {

			// Test fired too early or in an unsupported environment, exit.
			return;
		}

		// Setup
		div = document.createElement( "div" );
		container = document.createElement( "div" );
		container.style.cssText = "position:absolute;border:0;width:0;height:0;top:0;left:-9999px";
		body.appendChild( container ).appendChild( div );

		// Support: IE6
		// Check if elements with layout shrink-wrap their children
		if ( typeof div.style.zoom !== "undefined" ) {

			// Reset CSS: box-sizing; display; margin; border
			div.style.cssText =

				// Support: Firefox<29, Android 2.3
				// Vendor-prefix box-sizing
				"-webkit-box-sizing:content-box;-moz-box-sizing:content-box;" +
				"box-sizing:content-box;display:block;margin:0;border:0;" +
				"padding:1px;width:1px;zoom:1";
			div.appendChild( document.createElement( "div" ) ).style.width = "5px";
			shrinkWrapBlocksVal = div.offsetWidth !== 3;
		}

		body.removeChild( container );

		return shrinkWrapBlocksVal;
	};

} )();
var pnum = ( /[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/ ).source;

var rcssNum = new RegExp( "^(?:([+-])=|)(" + pnum + ")([a-z%]*)$", "i" );


var cssExpand = [ "Top", "Right", "Bottom", "Left" ];

var isHidden = function( elem, el ) {

		// isHidden might be called from jQuery#filter function;
		// in that case, element will be second argument
		elem = el || elem;
		return jQuery.css( elem, "display" ) === "none" ||
			!jQuery.contains( elem.ownerDocument, elem );
	};



function adjustCSS( elem, prop, valueParts, tween ) {
	var adjusted,
		scale = 1,
		maxIterations = 20,
		currentValue = tween ?
			function() { return tween.cur(); } :
			function() { return jQuery.css( elem, prop, "" ); },
		initial = currentValue(),
		unit = valueParts && valueParts[ 3 ] || ( jQuery.cssNumber[ prop ] ? "" : "px" ),

		// Starting value computation is required for potential unit mismatches
		initialInUnit = ( jQuery.cssNumber[ prop ] || unit !== "px" && +initial ) &&
			rcssNum.exec( jQuery.css( elem, prop ) );

	if ( initialInUnit && initialInUnit[ 3 ] !== unit ) {

		// Trust units reported by jQuery.css
		unit = unit || initialInUnit[ 3 ];

		// Make sure we update the tween properties later on
		valueParts = valueParts || [];

		// Iteratively approximate from a nonzero starting point
		initialInUnit = +initial || 1;

		do {

			// If previous iteration zeroed out, double until we get *something*.
			// Use string for doubling so we don't accidentally see scale as unchanged below
			scale = scale || ".5";

			// Adjust and apply
			initialInUnit = initialInUnit / scale;
			jQuery.style( elem, prop, initialInUnit + unit );

		// Update scale, tolerating zero or NaN from tween.cur()
		// Break the loop if scale is unchanged or perfect, or if we've just had enough.
		} while (
			scale !== ( scale = currentValue() / initial ) && scale !== 1 && --maxIterations
		);
	}

	if ( valueParts ) {
		initialInUnit = +initialInUnit || +initial || 0;

		// Apply relative offset (+=/-=) if specified
		adjusted = valueParts[ 1 ] ?
			initialInUnit + ( valueParts[ 1 ] + 1 ) * valueParts[ 2 ] :
			+valueParts[ 2 ];
		if ( tween ) {
			tween.unit = unit;
			tween.start = initialInUnit;
			tween.end = adjusted;
		}
	}
	return adjusted;
}


// Multifunctional method to get and set values of a collection
// The value/s can optionally be executed if it's a function
var access = function( elems, fn, key, value, chainable, emptyGet, raw ) {
	var i = 0,
		length = elems.length,
		bulk = key == null;

	// Sets many values
	if ( jQuery.type( key ) === "object" ) {
		chainable = true;
		for ( i in key ) {
			access( elems, fn, i, key[ i ], true, emptyGet, raw );
		}

	// Sets one value
	} else if ( value !== undefined ) {
		chainable = true;

		if ( !jQuery.isFunction( value ) ) {
			raw = true;
		}

		if ( bulk ) {

			// Bulk operations run against the entire set
			if ( raw ) {
				fn.call( elems, value );
				fn = null;

			// ...except when executing function values
			} else {
				bulk = fn;
				fn = function( elem, key, value ) {
					return bulk.call( jQuery( elem ), value );
				};
			}
		}

		if ( fn ) {
			for ( ; i < length; i++ ) {
				fn(
					elems[ i ],
					key,
					raw ? value : value.call( elems[ i ], i, fn( elems[ i ], key ) )
				);
			}
		}
	}

	return chainable ?
		elems :

		// Gets
		bulk ?
			fn.call( elems ) :
			length ? fn( elems[ 0 ], key ) : emptyGet;
};
var rcheckableType = ( /^(?:checkbox|radio)$/i );

var rtagName = ( /<([\w:-]+)/ );

var rscriptType = ( /^$|\/(?:java|ecma)script/i );

var rleadingWhitespace = ( /^\s+/ );

var nodeNames = "abbr|article|aside|audio|bdi|canvas|data|datalist|" +
		"details|dialog|figcaption|figure|footer|header|hgroup|main|" +
		"mark|meter|nav|output|picture|progress|section|summary|template|time|video";



function createSafeFragment( document ) {
	var list = nodeNames.split( "|" ),
		safeFrag = document.createDocumentFragment();

	if ( safeFrag.createElement ) {
		while ( list.length ) {
			safeFrag.createElement(
				list.pop()
			);
		}
	}
	return safeFrag;
}


( function() {
	var div = document.createElement( "div" ),
		fragment = document.createDocumentFragment(),
		input = document.createElement( "input" );

	// Setup
	div.innerHTML = "  <link/><table></table><a href='/a'>a</a><input type='checkbox'/>";

	// IE strips leading whitespace when .innerHTML is used
	support.leadingWhitespace = div.firstChild.nodeType === 3;

	// Make sure that tbody elements aren't automatically inserted
	// IE will insert them into empty tables
	support.tbody = !div.getElementsByTagName( "tbody" ).length;

	// Make sure that link elements get serialized correctly by innerHTML
	// This requires a wrapper element in IE
	support.htmlSerialize = !!div.getElementsByTagName( "link" ).length;

	// Makes sure cloning an html5 element does not cause problems
	// Where outerHTML is undefined, this still works
	support.html5Clone =
		document.createElement( "nav" ).cloneNode( true ).outerHTML !== "<:nav></:nav>";

	// Check if a disconnected checkbox will retain its checked
	// value of true after appended to the DOM (IE6/7)
	input.type = "checkbox";
	input.checked = true;
	fragment.appendChild( input );
	support.appendChecked = input.checked;

	// Make sure textarea (and checkbox) defaultValue is properly cloned
	// Support: IE6-IE11+
	div.innerHTML = "<textarea>x</textarea>";
	support.noCloneChecked = !!div.cloneNode( true ).lastChild.defaultValue;

	// #11217 - WebKit loses check when the name is after the checked attribute
	fragment.appendChild( div );

	// Support: Windows Web Apps (WWA)
	// `name` and `type` must use .setAttribute for WWA (#14901)
	input = document.createElement( "input" );
	input.setAttribute( "type", "radio" );
	input.setAttribute( "checked", "checked" );
	input.setAttribute( "name", "t" );

	div.appendChild( input );

	// Support: Safari 5.1, iOS 5.1, Android 4.x, Android 2.3
	// old WebKit doesn't clone checked state correctly in fragments
	support.checkClone = div.cloneNode( true ).cloneNode( true ).lastChild.checked;

	// Support: IE<9
	// Cloned elements keep attachEvent handlers, we use addEventListener on IE9+
	support.noCloneEvent = !!div.addEventListener;

	// Support: IE<9
	// Since attributes and properties are the same in IE,
	// cleanData must set properties to undefined rather than use removeAttribute
	div[ jQuery.expando ] = 1;
	support.attributes = !div.getAttribute( jQuery.expando );
} )();


// We have to close these tags to support XHTML (#13200)
var wrapMap = {
	option: [ 1, "<select multiple='multiple'>", "</select>" ],
	legend: [ 1, "<fieldset>", "</fieldset>" ],
	area: [ 1, "<map>", "</map>" ],

	// Support: IE8
	param: [ 1, "<object>", "</object>" ],
	thead: [ 1, "<table>", "</table>" ],
	tr: [ 2, "<table><tbody>", "</tbody></table>" ],
	col: [ 2, "<table><tbody></tbody><colgroup>", "</colgroup></table>" ],
	td: [ 3, "<table><tbody><tr>", "</tr></tbody></table>" ],

	// IE6-8 can't serialize link, script, style, or any html5 (NoScope) tags,
	// unless wrapped in a div with non-breaking characters in front of it.
	_default: support.htmlSerialize ? [ 0, "", "" ] : [ 1, "X<div>", "</div>" ]
};

// Support: IE8-IE9
wrapMap.optgroup = wrapMap.option;

wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
wrapMap.th = wrapMap.td;


function getAll( context, tag ) {
	var elems, elem,
		i = 0,
		found = typeof context.getElementsByTagName !== "undefined" ?
			context.getElementsByTagName( tag || "*" ) :
			typeof context.querySelectorAll !== "undefined" ?
				context.querySelectorAll( tag || "*" ) :
				undefined;

	if ( !found ) {
		for ( found = [], elems = context.childNodes || context;
			( elem = elems[ i ] ) != null;
			i++
		) {
			if ( !tag || jQuery.nodeName( elem, tag ) ) {
				found.push( elem );
			} else {
				jQuery.merge( found, getAll( elem, tag ) );
			}
		}
	}

	return tag === undefined || tag && jQuery.nodeName( context, tag ) ?
		jQuery.merge( [ context ], found ) :
		found;
}


// Mark scripts as having already been evaluated
function setGlobalEval( elems, refElements ) {
	var elem,
		i = 0;
	for ( ; ( elem = elems[ i ] ) != null; i++ ) {
		jQuery._data(
			elem,
			"globalEval",
			!refElements || jQuery._data( refElements[ i ], "globalEval" )
		);
	}
}


var rhtml = /<|&#?\w+;/,
	rtbody = /<tbody/i;

function fixDefaultChecked( elem ) {
	if ( rcheckableType.test( elem.type ) ) {
		elem.defaultChecked = elem.checked;
	}
}

function buildFragment( elems, context, scripts, selection, ignored ) {
	var j, elem, contains,
		tmp, tag, tbody, wrap,
		l = elems.length,

		// Ensure a safe fragment
		safe = createSafeFragment( context ),

		nodes = [],
		i = 0;

	for ( ; i < l; i++ ) {
		elem = elems[ i ];

		if ( elem || elem === 0 ) {

			// Add nodes directly
			if ( jQuery.type( elem ) === "object" ) {
				jQuery.merge( nodes, elem.nodeType ? [ elem ] : elem );

			// Convert non-html into a text node
			} else if ( !rhtml.test( elem ) ) {
				nodes.push( context.createTextNode( elem ) );

			// Convert html into DOM nodes
			} else {
				tmp = tmp || safe.appendChild( context.createElement( "div" ) );

				// Deserialize a standard representation
				tag = ( rtagName.exec( elem ) || [ "", "" ] )[ 1 ].toLowerCase();
				wrap = wrapMap[ tag ] || wrapMap._default;

				tmp.innerHTML = wrap[ 1 ] + jQuery.htmlPrefilter( elem ) + wrap[ 2 ];

				// Descend through wrappers to the right content
				j = wrap[ 0 ];
				while ( j-- ) {
					tmp = tmp.lastChild;
				}

				// Manually add leading whitespace removed by IE
				if ( !support.leadingWhitespace && rleadingWhitespace.test( elem ) ) {
					nodes.push( context.createTextNode( rleadingWhitespace.exec( elem )[ 0 ] ) );
				}

				// Remove IE's autoinserted <tbody> from table fragments
				if ( !support.tbody ) {

					// String was a <table>, *may* have spurious <tbody>
					elem = tag === "table" && !rtbody.test( elem ) ?
						tmp.firstChild :

						// String was a bare <thead> or <tfoot>
						wrap[ 1 ] === "<table>" && !rtbody.test( elem ) ?
							tmp :
							0;

					j = elem && elem.childNodes.length;
					while ( j-- ) {
						if ( jQuery.nodeName( ( tbody = elem.childNodes[ j ] ), "tbody" ) &&
							!tbody.childNodes.length ) {

							elem.removeChild( tbody );
						}
					}
				}

				jQuery.merge( nodes, tmp.childNodes );

				// Fix #12392 for WebKit and IE > 9
				tmp.textContent = "";

				// Fix #12392 for oldIE
				while ( tmp.firstChild ) {
					tmp.removeChild( tmp.firstChild );
				}

				// Remember the top-level container for proper cleanup
				tmp = safe.lastChild;
			}
		}
	}

	// Fix #11356: Clear elements from fragment
	if ( tmp ) {
		safe.removeChild( tmp );
	}

	// Reset defaultChecked for any radios and checkboxes
	// about to be appended to the DOM in IE 6/7 (#8060)
	if ( !support.appendChecked ) {
		jQuery.grep( getAll( nodes, "input" ), fixDefaultChecked );
	}

	i = 0;
	while ( ( elem = nodes[ i++ ] ) ) {

		// Skip elements already in the context collection (trac-4087)
		if ( selection && jQuery.inArray( elem, selection ) > -1 ) {
			if ( ignored ) {
				ignored.push( elem );
			}

			continue;
		}

		contains = jQuery.contains( elem.ownerDocument, elem );

		// Append to fragment
		tmp = getAll( safe.appendChild( elem ), "script" );

		// Preserve script evaluation history
		if ( contains ) {
			setGlobalEval( tmp );
		}

		// Capture executables
		if ( scripts ) {
			j = 0;
			while ( ( elem = tmp[ j++ ] ) ) {
				if ( rscriptType.test( elem.type || "" ) ) {
					scripts.push( elem );
				}
			}
		}
	}

	tmp = null;

	return safe;
}


( function() {
	var i, eventName,
		div = document.createElement( "div" );

	// Support: IE<9 (lack submit/change bubble), Firefox (lack focus(in | out) events)
	for ( i in { submit: true, change: true, focusin: true } ) {
		eventName = "on" + i;

		if ( !( support[ i ] = eventName in window ) ) {

			// Beware of CSP restrictions (https://developer.mozilla.org/en/Security/CSP)
			div.setAttribute( eventName, "t" );
			support[ i ] = div.attributes[ eventName ].expando === false;
		}
	}

	// Null elements to avoid leaks in IE.
	div = null;
} )();


var rformElems = /^(?:input|select|textarea)$/i,
	rkeyEvent = /^key/,
	rmouseEvent = /^(?:mouse|pointer|contextmenu|drag|drop)|click/,
	rfocusMorph = /^(?:focusinfocus|focusoutblur)$/,
	rtypenamespace = /^([^.]*)(?:\.(.+)|)/;

function returnTrue() {
	return true;
}

function returnFalse() {
	return false;
}

// Support: IE9
// See #13393 for more info
function safeActiveElement() {
	try {
		return document.activeElement;
	} catch ( err ) { }
}

function on( elem, types, selector, data, fn, one ) {
	var origFn, type;

	// Types can be a map of types/handlers
	if ( typeof types === "object" ) {

		// ( types-Object, selector, data )
		if ( typeof selector !== "string" ) {

			// ( types-Object, data )
			data = data || selector;
			selector = undefined;
		}
		for ( type in types ) {
			on( elem, type, selector, data, types[ type ], one );
		}
		return elem;
	}

	if ( data == null && fn == null ) {

		// ( types, fn )
		fn = selector;
		data = selector = undefined;
	} else if ( fn == null ) {
		if ( typeof selector === "string" ) {

			// ( types, selector, fn )
			fn = data;
			data = undefined;
		} else {

			// ( types, data, fn )
			fn = data;
			data = selector;
			selector = undefined;
		}
	}
	if ( fn === false ) {
		fn = returnFalse;
	} else if ( !fn ) {
		return elem;
	}

	if ( one === 1 ) {
		origFn = fn;
		fn = function( event ) {

			// Can use an empty set, since event contains the info
			jQuery().off( event );
			return origFn.apply( this, arguments );
		};

		// Use same guid so caller can remove using origFn
		fn.guid = origFn.guid || ( origFn.guid = jQuery.guid++ );
	}
	return elem.each( function() {
		jQuery.event.add( this, types, fn, data, selector );
	} );
}

/*
 * Helper functions for managing events -- not part of the public interface.
 * Props to Dean Edwards' addEvent library for many of the ideas.
 */
jQuery.event = {

	global: {},

	add: function( elem, types, handler, data, selector ) {
		var tmp, events, t, handleObjIn,
			special, eventHandle, handleObj,
			handlers, type, namespaces, origType,
			elemData = jQuery._data( elem );

		// Don't attach events to noData or text/comment nodes (but allow plain objects)
		if ( !elemData ) {
			return;
		}

		// Caller can pass in an object of custom data in lieu of the handler
		if ( handler.handler ) {
			handleObjIn = handler;
			handler = handleObjIn.handler;
			selector = handleObjIn.selector;
		}

		// Make sure that the handler has a unique ID, used to find/remove it later
		if ( !handler.guid ) {
			handler.guid = jQuery.guid++;
		}

		// Init the element's event structure and main handler, if this is the first
		if ( !( events = elemData.events ) ) {
			events = elemData.events = {};
		}
		if ( !( eventHandle = elemData.handle ) ) {
			eventHandle = elemData.handle = function( e ) {

				// Discard the second event of a jQuery.event.trigger() and
				// when an event is called after a page has unloaded
				return typeof jQuery !== "undefined" &&
					( !e || jQuery.event.triggered !== e.type ) ?
					jQuery.event.dispatch.apply( eventHandle.elem, arguments ) :
					undefined;
			};

			// Add elem as a property of the handle fn to prevent a memory leak
			// with IE non-native events
			eventHandle.elem = elem;
		}

		// Handle multiple events separated by a space
		types = ( types || "" ).match( rnotwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// There *must* be a type, no attaching namespace-only handlers
			if ( !type ) {
				continue;
			}

			// If event changes its type, use the special event handlers for the changed type
			special = jQuery.event.special[ type ] || {};

			// If selector defined, determine special event api type, otherwise given type
			type = ( selector ? special.delegateType : special.bindType ) || type;

			// Update special based on newly reset type
			special = jQuery.event.special[ type ] || {};

			// handleObj is passed to all event handlers
			handleObj = jQuery.extend( {
				type: type,
				origType: origType,
				data: data,
				handler: handler,
				guid: handler.guid,
				selector: selector,
				needsContext: selector && jQuery.expr.match.needsContext.test( selector ),
				namespace: namespaces.join( "." )
			}, handleObjIn );

			// Init the event handler queue if we're the first
			if ( !( handlers = events[ type ] ) ) {
				handlers = events[ type ] = [];
				handlers.delegateCount = 0;

				// Only use addEventListener/attachEvent if the special events handler returns false
				if ( !special.setup ||
					special.setup.call( elem, data, namespaces, eventHandle ) === false ) {

					// Bind the global event handler to the element
					if ( elem.addEventListener ) {
						elem.addEventListener( type, eventHandle, false );

					} else if ( elem.attachEvent ) {
						elem.attachEvent( "on" + type, eventHandle );
					}
				}
			}

			if ( special.add ) {
				special.add.call( elem, handleObj );

				if ( !handleObj.handler.guid ) {
					handleObj.handler.guid = handler.guid;
				}
			}

			// Add to the element's handler list, delegates in front
			if ( selector ) {
				handlers.splice( handlers.delegateCount++, 0, handleObj );
			} else {
				handlers.push( handleObj );
			}

			// Keep track of which events have ever been used, for event optimization
			jQuery.event.global[ type ] = true;
		}

		// Nullify elem to prevent memory leaks in IE
		elem = null;
	},

	// Detach an event or set of events from an element
	remove: function( elem, types, handler, selector, mappedTypes ) {
		var j, handleObj, tmp,
			origCount, t, events,
			special, handlers, type,
			namespaces, origType,
			elemData = jQuery.hasData( elem ) && jQuery._data( elem );

		if ( !elemData || !( events = elemData.events ) ) {
			return;
		}

		// Once for each type.namespace in types; type may be omitted
		types = ( types || "" ).match( rnotwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// Unbind all events (on this namespace, if provided) for the element
			if ( !type ) {
				for ( type in events ) {
					jQuery.event.remove( elem, type + types[ t ], handler, selector, true );
				}
				continue;
			}

			special = jQuery.event.special[ type ] || {};
			type = ( selector ? special.delegateType : special.bindType ) || type;
			handlers = events[ type ] || [];
			tmp = tmp[ 2 ] &&
				new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" );

			// Remove matching events
			origCount = j = handlers.length;
			while ( j-- ) {
				handleObj = handlers[ j ];

				if ( ( mappedTypes || origType === handleObj.origType ) &&
					( !handler || handler.guid === handleObj.guid ) &&
					( !tmp || tmp.test( handleObj.namespace ) ) &&
					( !selector || selector === handleObj.selector ||
						selector === "**" && handleObj.selector ) ) {
					handlers.splice( j, 1 );

					if ( handleObj.selector ) {
						handlers.delegateCount--;
					}
					if ( special.remove ) {
						special.remove.call( elem, handleObj );
					}
				}
			}

			// Remove generic event handler if we removed something and no more handlers exist
			// (avoids potential for endless recursion during removal of special event handlers)
			if ( origCount && !handlers.length ) {
				if ( !special.teardown ||
					special.teardown.call( elem, namespaces, elemData.handle ) === false ) {

					jQuery.removeEvent( elem, type, elemData.handle );
				}

				delete events[ type ];
			}
		}

		// Remove the expando if it's no longer used
		if ( jQuery.isEmptyObject( events ) ) {
			delete elemData.handle;

			// removeData also checks for emptiness and clears the expando if empty
			// so use it instead of delete
			jQuery._removeData( elem, "events" );
		}
	},

	trigger: function( event, data, elem, onlyHandlers ) {
		var handle, ontype, cur,
			bubbleType, special, tmp, i,
			eventPath = [ elem || document ],
			type = hasOwn.call( event, "type" ) ? event.type : event,
			namespaces = hasOwn.call( event, "namespace" ) ? event.namespace.split( "." ) : [];

		cur = tmp = elem = elem || document;

		// Don't do events on text and comment nodes
		if ( elem.nodeType === 3 || elem.nodeType === 8 ) {
			return;
		}

		// focus/blur morphs to focusin/out; ensure we're not firing them right now
		if ( rfocusMorph.test( type + jQuery.event.triggered ) ) {
			return;
		}

		if ( type.indexOf( "." ) > -1 ) {

			// Namespaced trigger; create a regexp to match event type in handle()
			namespaces = type.split( "." );
			type = namespaces.shift();
			namespaces.sort();
		}
		ontype = type.indexOf( ":" ) < 0 && "on" + type;

		// Caller can pass in a jQuery.Event object, Object, or just an event type string
		event = event[ jQuery.expando ] ?
			event :
			new jQuery.Event( type, typeof event === "object" && event );

		// Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
		event.isTrigger = onlyHandlers ? 2 : 3;
		event.namespace = namespaces.join( "." );
		event.rnamespace = event.namespace ?
			new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" ) :
			null;

		// Clean up the event in case it is being reused
		event.result = undefined;
		if ( !event.target ) {
			event.target = elem;
		}

		// Clone any incoming data and prepend the event, creating the handler arg list
		data = data == null ?
			[ event ] :
			jQuery.makeArray( data, [ event ] );

		// Allow special events to draw outside the lines
		special = jQuery.event.special[ type ] || {};
		if ( !onlyHandlers && special.trigger && special.trigger.apply( elem, data ) === false ) {
			return;
		}

		// Determine event propagation path in advance, per W3C events spec (#9951)
		// Bubble up to document, then to window; watch for a global ownerDocument var (#9724)
		if ( !onlyHandlers && !special.noBubble && !jQuery.isWindow( elem ) ) {

			bubbleType = special.delegateType || type;
			if ( !rfocusMorph.test( bubbleType + type ) ) {
				cur = cur.parentNode;
			}
			for ( ; cur; cur = cur.parentNode ) {
				eventPath.push( cur );
				tmp = cur;
			}

			// Only add window if we got to document (e.g., not plain obj or detached DOM)
			if ( tmp === ( elem.ownerDocument || document ) ) {
				eventPath.push( tmp.defaultView || tmp.parentWindow || window );
			}
		}

		// Fire handlers on the event path
		i = 0;
		while ( ( cur = eventPath[ i++ ] ) && !event.isPropagationStopped() ) {

			event.type = i > 1 ?
				bubbleType :
				special.bindType || type;

			// jQuery handler
			handle = ( jQuery._data( cur, "events" ) || {} )[ event.type ] &&
				jQuery._data( cur, "handle" );

			if ( handle ) {
				handle.apply( cur, data );
			}

			// Native handler
			handle = ontype && cur[ ontype ];
			if ( handle && handle.apply && acceptData( cur ) ) {
				event.result = handle.apply( cur, data );
				if ( event.result === false ) {
					event.preventDefault();
				}
			}
		}
		event.type = type;

		// If nobody prevented the default action, do it now
		if ( !onlyHandlers && !event.isDefaultPrevented() ) {

			if (
				( !special._default ||
				 special._default.apply( eventPath.pop(), data ) === false
				) && acceptData( elem )
			) {

				// Call a native DOM method on the target with the same name name as the event.
				// Can't use an .isFunction() check here because IE6/7 fails that test.
				// Don't do default actions on window, that's where global variables be (#6170)
				if ( ontype && elem[ type ] && !jQuery.isWindow( elem ) ) {

					// Don't re-trigger an onFOO event when we call its FOO() method
					tmp = elem[ ontype ];

					if ( tmp ) {
						elem[ ontype ] = null;
					}

					// Prevent re-triggering of the same event, since we already bubbled it above
					jQuery.event.triggered = type;
					try {
						elem[ type ]();
					} catch ( e ) {

						// IE<9 dies on focus/blur to hidden element (#1486,#12518)
						// only reproducible on winXP IE8 native, not IE9 in IE8 mode
					}
					jQuery.event.triggered = undefined;

					if ( tmp ) {
						elem[ ontype ] = tmp;
					}
				}
			}
		}

		return event.result;
	},

	dispatch: function( event ) {

		// Make a writable jQuery.Event from the native event object
		event = jQuery.event.fix( event );

		var i, j, ret, matched, handleObj,
			handlerQueue = [],
			args = slice.call( arguments ),
			handlers = ( jQuery._data( this, "events" ) || {} )[ event.type ] || [],
			special = jQuery.event.special[ event.type ] || {};

		// Use the fix-ed jQuery.Event rather than the (read-only) native event
		args[ 0 ] = event;
		event.delegateTarget = this;

		// Call the preDispatch hook for the mapped type, and let it bail if desired
		if ( special.preDispatch && special.preDispatch.call( this, event ) === false ) {
			return;
		}

		// Determine handlers
		handlerQueue = jQuery.event.handlers.call( this, event, handlers );

		// Run delegates first; they may want to stop propagation beneath us
		i = 0;
		while ( ( matched = handlerQueue[ i++ ] ) && !event.isPropagationStopped() ) {
			event.currentTarget = matched.elem;

			j = 0;
			while ( ( handleObj = matched.handlers[ j++ ] ) &&
				!event.isImmediatePropagationStopped() ) {

				// Triggered event must either 1) have no namespace, or 2) have namespace(s)
				// a subset or equal to those in the bound event (both can have no namespace).
				if ( !event.rnamespace || event.rnamespace.test( handleObj.namespace ) ) {

					event.handleObj = handleObj;
					event.data = handleObj.data;

					ret = ( ( jQuery.event.special[ handleObj.origType ] || {} ).handle ||
						handleObj.handler ).apply( matched.elem, args );

					if ( ret !== undefined ) {
						if ( ( event.result = ret ) === false ) {
							event.preventDefault();
							event.stopPropagation();
						}
					}
				}
			}
		}

		// Call the postDispatch hook for the mapped type
		if ( special.postDispatch ) {
			special.postDispatch.call( this, event );
		}

		return event.result;
	},

	handlers: function( event, handlers ) {
		var i, matches, sel, handleObj,
			handlerQueue = [],
			delegateCount = handlers.delegateCount,
			cur = event.target;

		// Support (at least): Chrome, IE9
		// Find delegate handlers
		// Black-hole SVG <use> instance trees (#13180)
		//
		// Support: Firefox<=42+
		// Avoid non-left-click in FF but don't block IE radio events (#3861, gh-2343)
		if ( delegateCount && cur.nodeType &&
			( event.type !== "click" || isNaN( event.button ) || event.button < 1 ) ) {

			/* jshint eqeqeq: false */
			for ( ; cur != this; cur = cur.parentNode || this ) {
				/* jshint eqeqeq: true */

				// Don't check non-elements (#13208)
				// Don't process clicks on disabled elements (#6911, #8165, #11382, #11764)
				if ( cur.nodeType === 1 && ( cur.disabled !== true || event.type !== "click" ) ) {
					matches = [];
					for ( i = 0; i < delegateCount; i++ ) {
						handleObj = handlers[ i ];

						// Don't conflict with Object.prototype properties (#13203)
						sel = handleObj.selector + " ";

						if ( matches[ sel ] === undefined ) {
							matches[ sel ] = handleObj.needsContext ?
								jQuery( sel, this ).index( cur ) > -1 :
								jQuery.find( sel, this, null, [ cur ] ).length;
						}
						if ( matches[ sel ] ) {
							matches.push( handleObj );
						}
					}
					if ( matches.length ) {
						handlerQueue.push( { elem: cur, handlers: matches } );
					}
				}
			}
		}

		// Add the remaining (directly-bound) handlers
		if ( delegateCount < handlers.length ) {
			handlerQueue.push( { elem: this, handlers: handlers.slice( delegateCount ) } );
		}

		return handlerQueue;
	},

	fix: function( event ) {
		if ( event[ jQuery.expando ] ) {
			return event;
		}

		// Create a writable copy of the event object and normalize some properties
		var i, prop, copy,
			type = event.type,
			originalEvent = event,
			fixHook = this.fixHooks[ type ];

		if ( !fixHook ) {
			this.fixHooks[ type ] = fixHook =
				rmouseEvent.test( type ) ? this.mouseHooks :
				rkeyEvent.test( type ) ? this.keyHooks :
				{};
		}
		copy = fixHook.props ? this.props.concat( fixHook.props ) : this.props;

		event = new jQuery.Event( originalEvent );

		i = copy.length;
		while ( i-- ) {
			prop = copy[ i ];
			event[ prop ] = originalEvent[ prop ];
		}

		// Support: IE<9
		// Fix target property (#1925)
		if ( !event.target ) {
			event.target = originalEvent.srcElement || document;
		}

		// Support: Safari 6-8+
		// Target should not be a text node (#504, #13143)
		if ( event.target.nodeType === 3 ) {
			event.target = event.target.parentNode;
		}

		// Support: IE<9
		// For mouse/key events, metaKey==false if it's undefined (#3368, #11328)
		event.metaKey = !!event.metaKey;

		return fixHook.filter ? fixHook.filter( event, originalEvent ) : event;
	},

	// Includes some event props shared by KeyEvent and MouseEvent
	props: ( "altKey bubbles cancelable ctrlKey currentTarget detail eventPhase " +
		"metaKey relatedTarget shiftKey target timeStamp view which" ).split( " " ),

	fixHooks: {},

	keyHooks: {
		props: "char charCode key keyCode".split( " " ),
		filter: function( event, original ) {

			// Add which for key events
			if ( event.which == null ) {
				event.which = original.charCode != null ? original.charCode : original.keyCode;
			}

			return event;
		}
	},

	mouseHooks: {
		props: ( "button buttons clientX clientY fromElement offsetX offsetY " +
			"pageX pageY screenX screenY toElement" ).split( " " ),
		filter: function( event, original ) {
			var body, eventDoc, doc,
				button = original.button,
				fromElement = original.fromElement;

			// Calculate pageX/Y if missing and clientX/Y available
			if ( event.pageX == null && original.clientX != null ) {
				eventDoc = event.target.ownerDocument || document;
				doc = eventDoc.documentElement;
				body = eventDoc.body;

				event.pageX = original.clientX +
					( doc && doc.scrollLeft || body && body.scrollLeft || 0 ) -
					( doc && doc.clientLeft || body && body.clientLeft || 0 );
				event.pageY = original.clientY +
					( doc && doc.scrollTop  || body && body.scrollTop  || 0 ) -
					( doc && doc.clientTop  || body && body.clientTop  || 0 );
			}

			// Add relatedTarget, if necessary
			if ( !event.relatedTarget && fromElement ) {
				event.relatedTarget = fromElement === event.target ?
					original.toElement :
					fromElement;
			}

			// Add which for click: 1 === left; 2 === middle; 3 === right
			// Note: button is not normalized, so don't use it
			if ( !event.which && button !== undefined ) {
				event.which = ( button & 1 ? 1 : ( button & 2 ? 3 : ( button & 4 ? 2 : 0 ) ) );
			}

			return event;
		}
	},

	special: {
		load: {

			// Prevent triggered image.load events from bubbling to window.load
			noBubble: true
		},
		focus: {

			// Fire native event if possible so blur/focus sequence is correct
			trigger: function() {
				if ( this !== safeActiveElement() && this.focus ) {
					try {
						this.focus();
						return false;
					} catch ( e ) {

						// Support: IE<9
						// If we error on focus to hidden element (#1486, #12518),
						// let .trigger() run the handlers
					}
				}
			},
			delegateType: "focusin"
		},
		blur: {
			trigger: function() {
				if ( this === safeActiveElement() && this.blur ) {
					this.blur();
					return false;
				}
			},
			delegateType: "focusout"
		},
		click: {

			// For checkbox, fire native event so checked state will be right
			trigger: function() {
				if ( jQuery.nodeName( this, "input" ) && this.type === "checkbox" && this.click ) {
					this.click();
					return false;
				}
			},

			// For cross-browser consistency, don't fire native .click() on links
			_default: function( event ) {
				return jQuery.nodeName( event.target, "a" );
			}
		},

		beforeunload: {
			postDispatch: function( event ) {

				// Support: Firefox 20+
				// Firefox doesn't alert if the returnValue field is not set.
				if ( event.result !== undefined && event.originalEvent ) {
					event.originalEvent.returnValue = event.result;
				}
			}
		}
	},

	// Piggyback on a donor event to simulate a different one
	simulate: function( type, elem, event ) {
		var e = jQuery.extend(
			new jQuery.Event(),
			event,
			{
				type: type,
				isSimulated: true

				// Previously, `originalEvent: {}` was set here, so stopPropagation call
				// would not be triggered on donor event, since in our own
				// jQuery.event.stopPropagation function we had a check for existence of
				// originalEvent.stopPropagation method, so, consequently it would be a noop.
				//
				// Guard for simulated events was moved to jQuery.event.stopPropagation function
				// since `originalEvent` should point to the original event for the
				// constancy with other events and for more focused logic
			}
		);

		jQuery.event.trigger( e, null, elem );

		if ( e.isDefaultPrevented() ) {
			event.preventDefault();
		}
	}
};

jQuery.removeEvent = document.removeEventListener ?
	function( elem, type, handle ) {

		// This "if" is needed for plain objects
		if ( elem.removeEventListener ) {
			elem.removeEventListener( type, handle );
		}
	} :
	function( elem, type, handle ) {
		var name = "on" + type;

		if ( elem.detachEvent ) {

			// #8545, #7054, preventing memory leaks for custom events in IE6-8
			// detachEvent needed property on element, by name of that event,
			// to properly expose it to GC
			if ( typeof elem[ name ] === "undefined" ) {
				elem[ name ] = null;
			}

			elem.detachEvent( name, handle );
		}
	};

jQuery.Event = function( src, props ) {

	// Allow instantiation without the 'new' keyword
	if ( !( this instanceof jQuery.Event ) ) {
		return new jQuery.Event( src, props );
	}

	// Event object
	if ( src && src.type ) {
		this.originalEvent = src;
		this.type = src.type;

		// Events bubbling up the document may have been marked as prevented
		// by a handler lower down the tree; reflect the correct value.
		this.isDefaultPrevented = src.defaultPrevented ||
				src.defaultPrevented === undefined &&

				// Support: IE < 9, Android < 4.0
				src.returnValue === false ?
			returnTrue :
			returnFalse;

	// Event type
	} else {
		this.type = src;
	}

	// Put explicitly provided properties onto the event object
	if ( props ) {
		jQuery.extend( this, props );
	}

	// Create a timestamp if incoming event doesn't have one
	this.timeStamp = src && src.timeStamp || jQuery.now();

	// Mark it as fixed
	this[ jQuery.expando ] = true;
};

// jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
// http://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
jQuery.Event.prototype = {
	constructor: jQuery.Event,
	isDefaultPrevented: returnFalse,
	isPropagationStopped: returnFalse,
	isImmediatePropagationStopped: returnFalse,

	preventDefault: function() {
		var e = this.originalEvent;

		this.isDefaultPrevented = returnTrue;
		if ( !e ) {
			return;
		}

		// If preventDefault exists, run it on the original event
		if ( e.preventDefault ) {
			e.preventDefault();

		// Support: IE
		// Otherwise set the returnValue property of the original event to false
		} else {
			e.returnValue = false;
		}
	},
	stopPropagation: function() {
		var e = this.originalEvent;

		this.isPropagationStopped = returnTrue;

		if ( !e || this.isSimulated ) {
			return;
		}

		// If stopPropagation exists, run it on the original event
		if ( e.stopPropagation ) {
			e.stopPropagation();
		}

		// Support: IE
		// Set the cancelBubble property of the original event to true
		e.cancelBubble = true;
	},
	stopImmediatePropagation: function() {
		var e = this.originalEvent;

		this.isImmediatePropagationStopped = returnTrue;

		if ( e && e.stopImmediatePropagation ) {
			e.stopImmediatePropagation();
		}

		this.stopPropagation();
	}
};

// Create mouseenter/leave events using mouseover/out and event-time checks
// so that event delegation works in jQuery.
// Do the same for pointerenter/pointerleave and pointerover/pointerout
//
// Support: Safari 7 only
// Safari sends mouseenter too often; see:
// https://code.google.com/p/chromium/issues/detail?id=470258
// for the description of the bug (it existed in older Chrome versions as well).
jQuery.each( {
	mouseenter: "mouseover",
	mouseleave: "mouseout",
	pointerenter: "pointerover",
	pointerleave: "pointerout"
}, function( orig, fix ) {
	jQuery.event.special[ orig ] = {
		delegateType: fix,
		bindType: fix,

		handle: function( event ) {
			var ret,
				target = this,
				related = event.relatedTarget,
				handleObj = event.handleObj;

			// For mouseenter/leave call the handler if related is outside the target.
			// NB: No relatedTarget if the mouse left/entered the browser window
			if ( !related || ( related !== target && !jQuery.contains( target, related ) ) ) {
				event.type = handleObj.origType;
				ret = handleObj.handler.apply( this, arguments );
				event.type = fix;
			}
			return ret;
		}
	};
} );

// IE submit delegation
if ( !support.submit ) {

	jQuery.event.special.submit = {
		setup: function() {

			// Only need this for delegated form submit events
			if ( jQuery.nodeName( this, "form" ) ) {
				return false;
			}

			// Lazy-add a submit handler when a descendant form may potentially be submitted
			jQuery.event.add( this, "click._submit keypress._submit", function( e ) {

				// Node name check avoids a VML-related crash in IE (#9807)
				var elem = e.target,
					form = jQuery.nodeName( elem, "input" ) || jQuery.nodeName( elem, "button" ) ?

						// Support: IE <=8
						// We use jQuery.prop instead of elem.form
						// to allow fixing the IE8 delegated submit issue (gh-2332)
						// by 3rd party polyfills/workarounds.
						jQuery.prop( elem, "form" ) :
						undefined;

				if ( form && !jQuery._data( form, "submit" ) ) {
					jQuery.event.add( form, "submit._submit", function( event ) {
						event._submitBubble = true;
					} );
					jQuery._data( form, "submit", true );
				}
			} );

			// return undefined since we don't need an event listener
		},

		postDispatch: function( event ) {

			// If form was submitted by the user, bubble the event up the tree
			if ( event._submitBubble ) {
				delete event._submitBubble;
				if ( this.parentNode && !event.isTrigger ) {
					jQuery.event.simulate( "submit", this.parentNode, event );
				}
			}
		},

		teardown: function() {

			// Only need this for delegated form submit events
			if ( jQuery.nodeName( this, "form" ) ) {
				return false;
			}

			// Remove delegated handlers; cleanData eventually reaps submit handlers attached above
			jQuery.event.remove( this, "._submit" );
		}
	};
}

// IE change delegation and checkbox/radio fix
if ( !support.change ) {

	jQuery.event.special.change = {

		setup: function() {

			if ( rformElems.test( this.nodeName ) ) {

				// IE doesn't fire change on a check/radio until blur; trigger it on click
				// after a propertychange. Eat the blur-change in special.change.handle.
				// This still fires onchange a second time for check/radio after blur.
				if ( this.type === "checkbox" || this.type === "radio" ) {
					jQuery.event.add( this, "propertychange._change", function( event ) {
						if ( event.originalEvent.propertyName === "checked" ) {
							this._justChanged = true;
						}
					} );
					jQuery.event.add( this, "click._change", function( event ) {
						if ( this._justChanged && !event.isTrigger ) {
							this._justChanged = false;
						}

						// Allow triggered, simulated change events (#11500)
						jQuery.event.simulate( "change", this, event );
					} );
				}
				return false;
			}

			// Delegated event; lazy-add a change handler on descendant inputs
			jQuery.event.add( this, "beforeactivate._change", function( e ) {
				var elem = e.target;

				if ( rformElems.test( elem.nodeName ) && !jQuery._data( elem, "change" ) ) {
					jQuery.event.add( elem, "change._change", function( event ) {
						if ( this.parentNode && !event.isSimulated && !event.isTrigger ) {
							jQuery.event.simulate( "change", this.parentNode, event );
						}
					} );
					jQuery._data( elem, "change", true );
				}
			} );
		},

		handle: function( event ) {
			var elem = event.target;

			// Swallow native change events from checkbox/radio, we already triggered them above
			if ( this !== elem || event.isSimulated || event.isTrigger ||
				( elem.type !== "radio" && elem.type !== "checkbox" ) ) {

				return event.handleObj.handler.apply( this, arguments );
			}
		},

		teardown: function() {
			jQuery.event.remove( this, "._change" );

			return !rformElems.test( this.nodeName );
		}
	};
}

// Support: Firefox
// Firefox doesn't have focus(in | out) events
// Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
//
// Support: Chrome, Safari
// focus(in | out) events fire after focus & blur events,
// which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
// Related ticket - https://code.google.com/p/chromium/issues/detail?id=449857
if ( !support.focusin ) {
	jQuery.each( { focus: "focusin", blur: "focusout" }, function( orig, fix ) {

		// Attach a single capturing handler on the document while someone wants focusin/focusout
		var handler = function( event ) {
			jQuery.event.simulate( fix, event.target, jQuery.event.fix( event ) );
		};

		jQuery.event.special[ fix ] = {
			setup: function() {
				var doc = this.ownerDocument || this,
					attaches = jQuery._data( doc, fix );

				if ( !attaches ) {
					doc.addEventListener( orig, handler, true );
				}
				jQuery._data( doc, fix, ( attaches || 0 ) + 1 );
			},
			teardown: function() {
				var doc = this.ownerDocument || this,
					attaches = jQuery._data( doc, fix ) - 1;

				if ( !attaches ) {
					doc.removeEventListener( orig, handler, true );
					jQuery._removeData( doc, fix );
				} else {
					jQuery._data( doc, fix, attaches );
				}
			}
		};
	} );
}

jQuery.fn.extend( {

	on: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn );
	},
	one: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn, 1 );
	},
	off: function( types, selector, fn ) {
		var handleObj, type;
		if ( types && types.preventDefault && types.handleObj ) {

			// ( event )  dispatched jQuery.Event
			handleObj = types.handleObj;
			jQuery( types.delegateTarget ).off(
				handleObj.namespace ?
					handleObj.origType + "." + handleObj.namespace :
					handleObj.origType,
				handleObj.selector,
				handleObj.handler
			);
			return this;
		}
		if ( typeof types === "object" ) {

			// ( types-object [, selector] )
			for ( type in types ) {
				this.off( type, selector, types[ type ] );
			}
			return this;
		}
		if ( selector === false || typeof selector === "function" ) {

			// ( types [, fn] )
			fn = selector;
			selector = undefined;
		}
		if ( fn === false ) {
			fn = returnFalse;
		}
		return this.each( function() {
			jQuery.event.remove( this, types, fn, selector );
		} );
	},

	trigger: function( type, data ) {
		return this.each( function() {
			jQuery.event.trigger( type, data, this );
		} );
	},
	triggerHandler: function( type, data ) {
		var elem = this[ 0 ];
		if ( elem ) {
			return jQuery.event.trigger( type, data, elem, true );
		}
	}
} );


var rinlinejQuery = / jQuery\d+="(?:null|\d+)"/g,
	rnoshimcache = new RegExp( "<(?:" + nodeNames + ")[\\s/>]", "i" ),
	rxhtmlTag = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([\w:-]+)[^>]*)\/>/gi,

	// Support: IE 10-11, Edge 10240+
	// In IE/Edge using regex groups here causes severe slowdowns.
	// See https://connect.microsoft.com/IE/feedback/details/1736512/
	rnoInnerhtml = /<script|<style|<link/i,

	// checked="checked" or checked
	rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,
	rscriptTypeMasked = /^true\/(.*)/,
	rcleanScript = /^\s*<!(?:\[CDATA\[|--)|(?:\]\]|--)>\s*$/g,
	safeFragment = createSafeFragment( document ),
	fragmentDiv = safeFragment.appendChild( document.createElement( "div" ) );

// Support: IE<8
// Manipulating tables requires a tbody
function manipulationTarget( elem, content ) {
	return jQuery.nodeName( elem, "table" ) &&
		jQuery.nodeName( content.nodeType !== 11 ? content : content.firstChild, "tr" ) ?

		elem.getElementsByTagName( "tbody" )[ 0 ] ||
			elem.appendChild( elem.ownerDocument.createElement( "tbody" ) ) :
		elem;
}

// Replace/restore the type attribute of script elements for safe DOM manipulation
function disableScript( elem ) {
	elem.type = ( jQuery.find.attr( elem, "type" ) !== null ) + "/" + elem.type;
	return elem;
}
function restoreScript( elem ) {
	var match = rscriptTypeMasked.exec( elem.type );
	if ( match ) {
		elem.type = match[ 1 ];
	} else {
		elem.removeAttribute( "type" );
	}
	return elem;
}

function cloneCopyEvent( src, dest ) {
	if ( dest.nodeType !== 1 || !jQuery.hasData( src ) ) {
		return;
	}

	var type, i, l,
		oldData = jQuery._data( src ),
		curData = jQuery._data( dest, oldData ),
		events = oldData.events;

	if ( events ) {
		delete curData.handle;
		curData.events = {};

		for ( type in events ) {
			for ( i = 0, l = events[ type ].length; i < l; i++ ) {
				jQuery.event.add( dest, type, events[ type ][ i ] );
			}
		}
	}

	// make the cloned public data object a copy from the original
	if ( curData.data ) {
		curData.data = jQuery.extend( {}, curData.data );
	}
}

function fixCloneNodeIssues( src, dest ) {
	var nodeName, e, data;

	// We do not need to do anything for non-Elements
	if ( dest.nodeType !== 1 ) {
		return;
	}

	nodeName = dest.nodeName.toLowerCase();

	// IE6-8 copies events bound via attachEvent when using cloneNode.
	if ( !support.noCloneEvent && dest[ jQuery.expando ] ) {
		data = jQuery._data( dest );

		for ( e in data.events ) {
			jQuery.removeEvent( dest, e, data.handle );
		}

		// Event data gets referenced instead of copied if the expando gets copied too
		dest.removeAttribute( jQuery.expando );
	}

	// IE blanks contents when cloning scripts, and tries to evaluate newly-set text
	if ( nodeName === "script" && dest.text !== src.text ) {
		disableScript( dest ).text = src.text;
		restoreScript( dest );

	// IE6-10 improperly clones children of object elements using classid.
	// IE10 throws NoModificationAllowedError if parent is null, #12132.
	} else if ( nodeName === "object" ) {
		if ( dest.parentNode ) {
			dest.outerHTML = src.outerHTML;
		}

		// This path appears unavoidable for IE9. When cloning an object
		// element in IE9, the outerHTML strategy above is not sufficient.
		// If the src has innerHTML and the destination does not,
		// copy the src.innerHTML into the dest.innerHTML. #10324
		if ( support.html5Clone && ( src.innerHTML && !jQuery.trim( dest.innerHTML ) ) ) {
			dest.innerHTML = src.innerHTML;
		}

	} else if ( nodeName === "input" && rcheckableType.test( src.type ) ) {

		// IE6-8 fails to persist the checked state of a cloned checkbox
		// or radio button. Worse, IE6-7 fail to give the cloned element
		// a checked appearance if the defaultChecked value isn't also set

		dest.defaultChecked = dest.checked = src.checked;

		// IE6-7 get confused and end up setting the value of a cloned
		// checkbox/radio button to an empty string instead of "on"
		if ( dest.value !== src.value ) {
			dest.value = src.value;
		}

	// IE6-8 fails to return the selected option to the default selected
	// state when cloning options
	} else if ( nodeName === "option" ) {
		dest.defaultSelected = dest.selected = src.defaultSelected;

	// IE6-8 fails to set the defaultValue to the correct value when
	// cloning other types of input fields
	} else if ( nodeName === "input" || nodeName === "textarea" ) {
		dest.defaultValue = src.defaultValue;
	}
}

function domManip( collection, args, callback, ignored ) {

	// Flatten any nested arrays
	args = concat.apply( [], args );

	var first, node, hasScripts,
		scripts, doc, fragment,
		i = 0,
		l = collection.length,
		iNoClone = l - 1,
		value = args[ 0 ],
		isFunction = jQuery.isFunction( value );

	// We can't cloneNode fragments that contain checked, in WebKit
	if ( isFunction ||
			( l > 1 && typeof value === "string" &&
				!support.checkClone && rchecked.test( value ) ) ) {
		return collection.each( function( index ) {
			var self = collection.eq( index );
			if ( isFunction ) {
				args[ 0 ] = value.call( this, index, self.html() );
			}
			domManip( self, args, callback, ignored );
		} );
	}

	if ( l ) {
		fragment = buildFragment( args, collection[ 0 ].ownerDocument, false, collection, ignored );
		first = fragment.firstChild;

		if ( fragment.childNodes.length === 1 ) {
			fragment = first;
		}

		// Require either new content or an interest in ignored elements to invoke the callback
		if ( first || ignored ) {
			scripts = jQuery.map( getAll( fragment, "script" ), disableScript );
			hasScripts = scripts.length;

			// Use the original fragment for the last item
			// instead of the first because it can end up
			// being emptied incorrectly in certain situations (#8070).
			for ( ; i < l; i++ ) {
				node = fragment;

				if ( i !== iNoClone ) {
					node = jQuery.clone( node, true, true );

					// Keep references to cloned scripts for later restoration
					if ( hasScripts ) {

						// Support: Android<4.1, PhantomJS<2
						// push.apply(_, arraylike) throws on ancient WebKit
						jQuery.merge( scripts, getAll( node, "script" ) );
					}
				}

				callback.call( collection[ i ], node, i );
			}

			if ( hasScripts ) {
				doc = scripts[ scripts.length - 1 ].ownerDocument;

				// Reenable scripts
				jQuery.map( scripts, restoreScript );

				// Evaluate executable scripts on first document insertion
				for ( i = 0; i < hasScripts; i++ ) {
					node = scripts[ i ];
					if ( rscriptType.test( node.type || "" ) &&
						!jQuery._data( node, "globalEval" ) &&
						jQuery.contains( doc, node ) ) {

						if ( node.src ) {

							// Optional AJAX dependency, but won't run scripts if not present
							if ( jQuery._evalUrl ) {
								jQuery._evalUrl( node.src );
							}
						} else {
							jQuery.globalEval(
								( node.text || node.textContent || node.innerHTML || "" )
									.replace( rcleanScript, "" )
							);
						}
					}
				}
			}

			// Fix #11809: Avoid leaking memory
			fragment = first = null;
		}
	}

	return collection;
}

function remove( elem, selector, keepData ) {
	var node,
		elems = selector ? jQuery.filter( selector, elem ) : elem,
		i = 0;

	for ( ; ( node = elems[ i ] ) != null; i++ ) {

		if ( !keepData && node.nodeType === 1 ) {
			jQuery.cleanData( getAll( node ) );
		}

		if ( node.parentNode ) {
			if ( keepData && jQuery.contains( node.ownerDocument, node ) ) {
				setGlobalEval( getAll( node, "script" ) );
			}
			node.parentNode.removeChild( node );
		}
	}

	return elem;
}

jQuery.extend( {
	htmlPrefilter: function( html ) {
		return html.replace( rxhtmlTag, "<$1></$2>" );
	},

	clone: function( elem, dataAndEvents, deepDataAndEvents ) {
		var destElements, node, clone, i, srcElements,
			inPage = jQuery.contains( elem.ownerDocument, elem );

		if ( support.html5Clone || jQuery.isXMLDoc( elem ) ||
			!rnoshimcache.test( "<" + elem.nodeName + ">" ) ) {

			clone = elem.cloneNode( true );

		// IE<=8 does not properly clone detached, unknown element nodes
		} else {
			fragmentDiv.innerHTML = elem.outerHTML;
			fragmentDiv.removeChild( clone = fragmentDiv.firstChild );
		}

		if ( ( !support.noCloneEvent || !support.noCloneChecked ) &&
				( elem.nodeType === 1 || elem.nodeType === 11 ) && !jQuery.isXMLDoc( elem ) ) {

			// We eschew Sizzle here for performance reasons: http://jsperf.com/getall-vs-sizzle/2
			destElements = getAll( clone );
			srcElements = getAll( elem );

			// Fix all IE cloning issues
			for ( i = 0; ( node = srcElements[ i ] ) != null; ++i ) {

				// Ensure that the destination node is not null; Fixes #9587
				if ( destElements[ i ] ) {
					fixCloneNodeIssues( node, destElements[ i ] );
				}
			}
		}

		// Copy the events from the original to the clone
		if ( dataAndEvents ) {
			if ( deepDataAndEvents ) {
				srcElements = srcElements || getAll( elem );
				destElements = destElements || getAll( clone );

				for ( i = 0; ( node = srcElements[ i ] ) != null; i++ ) {
					cloneCopyEvent( node, destElements[ i ] );
				}
			} else {
				cloneCopyEvent( elem, clone );
			}
		}

		// Preserve script evaluation history
		destElements = getAll( clone, "script" );
		if ( destElements.length > 0 ) {
			setGlobalEval( destElements, !inPage && getAll( elem, "script" ) );
		}

		destElements = srcElements = node = null;

		// Return the cloned set
		return clone;
	},

	cleanData: function( elems, /* internal */ forceAcceptData ) {
		var elem, type, id, data,
			i = 0,
			internalKey = jQuery.expando,
			cache = jQuery.cache,
			attributes = support.attributes,
			special = jQuery.event.special;

		for ( ; ( elem = elems[ i ] ) != null; i++ ) {
			if ( forceAcceptData || acceptData( elem ) ) {

				id = elem[ internalKey ];
				data = id && cache[ id ];

				if ( data ) {
					if ( data.events ) {
						for ( type in data.events ) {
							if ( special[ type ] ) {
								jQuery.event.remove( elem, type );

							// This is a shortcut to avoid jQuery.event.remove's overhead
							} else {
								jQuery.removeEvent( elem, type, data.handle );
							}
						}
					}

					// Remove cache only if it was not already removed by jQuery.event.remove
					if ( cache[ id ] ) {

						delete cache[ id ];

						// Support: IE<9
						// IE does not allow us to delete expando properties from nodes
						// IE creates expando attributes along with the property
						// IE does not have a removeAttribute function on Document nodes
						if ( !attributes && typeof elem.removeAttribute !== "undefined" ) {
							elem.removeAttribute( internalKey );

						// Webkit & Blink performance suffers when deleting properties
						// from DOM nodes, so set to undefined instead
						// https://code.google.com/p/chromium/issues/detail?id=378607
						} else {
							elem[ internalKey ] = undefined;
						}

						deletedIds.push( id );
					}
				}
			}
		}
	}
} );

jQuery.fn.extend( {

	// Keep domManip exposed until 3.0 (gh-2225)
	domManip: domManip,

	detach: function( selector ) {
		return remove( this, selector, true );
	},

	remove: function( selector ) {
		return remove( this, selector );
	},

	text: function( value ) {
		return access( this, function( value ) {
			return value === undefined ?
				jQuery.text( this ) :
				this.empty().append(
					( this[ 0 ] && this[ 0 ].ownerDocument || document ).createTextNode( value )
				);
		}, null, value, arguments.length );
	},

	append: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.appendChild( elem );
			}
		} );
	},

	prepend: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.insertBefore( elem, target.firstChild );
			}
		} );
	},

	before: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this );
			}
		} );
	},

	after: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this.nextSibling );
			}
		} );
	},

	empty: function() {
		var elem,
			i = 0;

		for ( ; ( elem = this[ i ] ) != null; i++ ) {

			// Remove element nodes and prevent memory leaks
			if ( elem.nodeType === 1 ) {
				jQuery.cleanData( getAll( elem, false ) );
			}

			// Remove any remaining nodes
			while ( elem.firstChild ) {
				elem.removeChild( elem.firstChild );
			}

			// If this is a select, ensure that it displays empty (#12336)
			// Support: IE<9
			if ( elem.options && jQuery.nodeName( elem, "select" ) ) {
				elem.options.length = 0;
			}
		}

		return this;
	},

	clone: function( dataAndEvents, deepDataAndEvents ) {
		dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
		deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;

		return this.map( function() {
			return jQuery.clone( this, dataAndEvents, deepDataAndEvents );
		} );
	},

	html: function( value ) {
		return access( this, function( value ) {
			var elem = this[ 0 ] || {},
				i = 0,
				l = this.length;

			if ( value === undefined ) {
				return elem.nodeType === 1 ?
					elem.innerHTML.replace( rinlinejQuery, "" ) :
					undefined;
			}

			// See if we can take a shortcut and just use innerHTML
			if ( typeof value === "string" && !rnoInnerhtml.test( value ) &&
				( support.htmlSerialize || !rnoshimcache.test( value )  ) &&
				( support.leadingWhitespace || !rleadingWhitespace.test( value ) ) &&
				!wrapMap[ ( rtagName.exec( value ) || [ "", "" ] )[ 1 ].toLowerCase() ] ) {

				value = jQuery.htmlPrefilter( value );

				try {
					for ( ; i < l; i++ ) {

						// Remove element nodes and prevent memory leaks
						elem = this[ i ] || {};
						if ( elem.nodeType === 1 ) {
							jQuery.cleanData( getAll( elem, false ) );
							elem.innerHTML = value;
						}
					}

					elem = 0;

				// If using innerHTML throws an exception, use the fallback method
				} catch ( e ) {}
			}

			if ( elem ) {
				this.empty().append( value );
			}
		}, null, value, arguments.length );
	},

	replaceWith: function() {
		var ignored = [];

		// Make the changes, replacing each non-ignored context element with the new content
		return domManip( this, arguments, function( elem ) {
			var parent = this.parentNode;

			if ( jQuery.inArray( this, ignored ) < 0 ) {
				jQuery.cleanData( getAll( this ) );
				if ( parent ) {
					parent.replaceChild( elem, this );
				}
			}

		// Force callback invocation
		}, ignored );
	}
} );

jQuery.each( {
	appendTo: "append",
	prependTo: "prepend",
	insertBefore: "before",
	insertAfter: "after",
	replaceAll: "replaceWith"
}, function( name, original ) {
	jQuery.fn[ name ] = function( selector ) {
		var elems,
			i = 0,
			ret = [],
			insert = jQuery( selector ),
			last = insert.length - 1;

		for ( ; i <= last; i++ ) {
			elems = i === last ? this : this.clone( true );
			jQuery( insert[ i ] )[ original ]( elems );

			// Modern browsers can apply jQuery collections as arrays, but oldIE needs a .get()
			push.apply( ret, elems.get() );
		}

		return this.pushStack( ret );
	};
} );


var iframe,
	elemdisplay = {

		// Support: Firefox
		// We have to pre-define these values for FF (#10227)
		HTML: "block",
		BODY: "block"
	};

/**
 * Retrieve the actual display of a element
 * @param {String} name nodeName of the element
 * @param {Object} doc Document object
 */

// Called only from within defaultDisplay
function actualDisplay( name, doc ) {
	var elem = jQuery( doc.createElement( name ) ).appendTo( doc.body ),

		display = jQuery.css( elem[ 0 ], "display" );

	// We don't have any data stored on the element,
	// so use "detach" method as fast way to get rid of the element
	elem.detach();

	return display;
}

/**
 * Try to determine the default display value of an element
 * @param {String} nodeName
 */
function defaultDisplay( nodeName ) {
	var doc = document,
		display = elemdisplay[ nodeName ];

	if ( !display ) {
		display = actualDisplay( nodeName, doc );

		// If the simple way fails, read from inside an iframe
		if ( display === "none" || !display ) {

			// Use the already-created iframe if possible
			iframe = ( iframe || jQuery( "<iframe frameborder='0' width='0' height='0'/>" ) )
				.appendTo( doc.documentElement );

			// Always write a new HTML skeleton so Webkit and Firefox don't choke on reuse
			doc = ( iframe[ 0 ].contentWindow || iframe[ 0 ].contentDocument ).document;

			// Support: IE
			doc.write();
			doc.close();

			display = actualDisplay( nodeName, doc );
			iframe.detach();
		}

		// Store the correct default display
		elemdisplay[ nodeName ] = display;
	}

	return display;
}
var rmargin = ( /^margin/ );

var rnumnonpx = new RegExp( "^(" + pnum + ")(?!px)[a-z%]+$", "i" );

var swap = function( elem, options, callback, args ) {
	var ret, name,
		old = {};

	// Remember the old values, and insert the new ones
	for ( name in options ) {
		old[ name ] = elem.style[ name ];
		elem.style[ name ] = options[ name ];
	}

	ret = callback.apply( elem, args || [] );

	// Revert the old values
	for ( name in options ) {
		elem.style[ name ] = old[ name ];
	}

	return ret;
};


var documentElement = document.documentElement;



( function() {
	var pixelPositionVal, pixelMarginRightVal, boxSizingReliableVal,
		reliableHiddenOffsetsVal, reliableMarginRightVal, reliableMarginLeftVal,
		container = document.createElement( "div" ),
		div = document.createElement( "div" );

	// Finish early in limited (non-browser) environments
	if ( !div.style ) {
		return;
	}

	div.style.cssText = "float:left;opacity:.5";

	// Support: IE<9
	// Make sure that element opacity exists (as opposed to filter)
	support.opacity = div.style.opacity === "0.5";

	// Verify style float existence
	// (IE uses styleFloat instead of cssFloat)
	support.cssFloat = !!div.style.cssFloat;

	div.style.backgroundClip = "content-box";
	div.cloneNode( true ).style.backgroundClip = "";
	support.clearCloneStyle = div.style.backgroundClip === "content-box";

	container = document.createElement( "div" );
	container.style.cssText = "border:0;width:8px;height:0;top:0;left:-9999px;" +
		"padding:0;margin-top:1px;position:absolute";
	div.innerHTML = "";
	container.appendChild( div );

	// Support: Firefox<29, Android 2.3
	// Vendor-prefix box-sizing
	support.boxSizing = div.style.boxSizing === "" || div.style.MozBoxSizing === "" ||
		div.style.WebkitBoxSizing === "";

	jQuery.extend( support, {
		reliableHiddenOffsets: function() {
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return reliableHiddenOffsetsVal;
		},

		boxSizingReliable: function() {

			// We're checking for pixelPositionVal here instead of boxSizingReliableVal
			// since that compresses better and they're computed together anyway.
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return boxSizingReliableVal;
		},

		pixelMarginRight: function() {

			// Support: Android 4.0-4.3
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return pixelMarginRightVal;
		},

		pixelPosition: function() {
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return pixelPositionVal;
		},

		reliableMarginRight: function() {

			// Support: Android 2.3
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return reliableMarginRightVal;
		},

		reliableMarginLeft: function() {

			// Support: IE <=8 only, Android 4.0 - 4.3 only, Firefox <=3 - 37
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return reliableMarginLeftVal;
		}
	} );

	function computeStyleTests() {
		var contents, divStyle,
			documentElement = document.documentElement;

		// Setup
		documentElement.appendChild( container );

		div.style.cssText =

			// Support: Android 2.3
			// Vendor-prefix box-sizing
			"-webkit-box-sizing:border-box;box-sizing:border-box;" +
			"position:relative;display:block;" +
			"margin:auto;border:1px;padding:1px;" +
			"top:1%;width:50%";

		// Support: IE<9
		// Assume reasonable values in the absence of getComputedStyle
		pixelPositionVal = boxSizingReliableVal = reliableMarginLeftVal = false;
		pixelMarginRightVal = reliableMarginRightVal = true;

		// Check for getComputedStyle so that this code is not run in IE<9.
		if ( window.getComputedStyle ) {
			divStyle = window.getComputedStyle( div );
			pixelPositionVal = ( divStyle || {} ).top !== "1%";
			reliableMarginLeftVal = ( divStyle || {} ).marginLeft === "2px";
			boxSizingReliableVal = ( divStyle || { width: "4px" } ).width === "4px";

			// Support: Android 4.0 - 4.3 only
			// Some styles come back with percentage values, even though they shouldn't
			div.style.marginRight = "50%";
			pixelMarginRightVal = ( divStyle || { marginRight: "4px" } ).marginRight === "4px";

			// Support: Android 2.3 only
			// Div with explicit width and no margin-right incorrectly
			// gets computed margin-right based on width of container (#3333)
			// WebKit Bug 13343 - getComputedStyle returns wrong value for margin-right
			contents = div.appendChild( document.createElement( "div" ) );

			// Reset CSS: box-sizing; display; margin; border; padding
			contents.style.cssText = div.style.cssText =

				// Support: Android 2.3
				// Vendor-prefix box-sizing
				"-webkit-box-sizing:content-box;-moz-box-sizing:content-box;" +
				"box-sizing:content-box;display:block;margin:0;border:0;padding:0";
			contents.style.marginRight = contents.style.width = "0";
			div.style.width = "1px";

			reliableMarginRightVal =
				!parseFloat( ( window.getComputedStyle( contents ) || {} ).marginRight );

			div.removeChild( contents );
		}

		// Support: IE6-8
		// First check that getClientRects works as expected
		// Check if table cells still have offsetWidth/Height when they are set
		// to display:none and there are still other visible table cells in a
		// table row; if so, offsetWidth/Height are not reliable for use when
		// determining if an element has been hidden directly using
		// display:none (it is still safe to use offsets if a parent element is
		// hidden; don safety goggles and see bug #4512 for more information).
		div.style.display = "none";
		reliableHiddenOffsetsVal = div.getClientRects().length === 0;
		if ( reliableHiddenOffsetsVal ) {
			div.style.display = "";
			div.innerHTML = "<table><tr><td></td><td>t</td></tr></table>";
			div.childNodes[ 0 ].style.borderCollapse = "separate";
			contents = div.getElementsByTagName( "td" );
			contents[ 0 ].style.cssText = "margin:0;border:0;padding:0;display:none";
			reliableHiddenOffsetsVal = contents[ 0 ].offsetHeight === 0;
			if ( reliableHiddenOffsetsVal ) {
				contents[ 0 ].style.display = "";
				contents[ 1 ].style.display = "none";
				reliableHiddenOffsetsVal = contents[ 0 ].offsetHeight === 0;
			}
		}

		// Teardown
		documentElement.removeChild( container );
	}

} )();


var getStyles, curCSS,
	rposition = /^(top|right|bottom|left)$/;

if ( window.getComputedStyle ) {
	getStyles = function( elem ) {

		// Support: IE<=11+, Firefox<=30+ (#15098, #14150)
		// IE throws on elements created in popups
		// FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
		var view = elem.ownerDocument.defaultView;

		if ( !view || !view.opener ) {
			view = window;
		}

		return view.getComputedStyle( elem );
	};

	curCSS = function( elem, name, computed ) {
		var width, minWidth, maxWidth, ret,
			style = elem.style;

		computed = computed || getStyles( elem );

		// getPropertyValue is only needed for .css('filter') in IE9, see #12537
		ret = computed ? computed.getPropertyValue( name ) || computed[ name ] : undefined;

		// Support: Opera 12.1x only
		// Fall back to style even without computed
		// computed is undefined for elems on document fragments
		if ( ( ret === "" || ret === undefined ) && !jQuery.contains( elem.ownerDocument, elem ) ) {
			ret = jQuery.style( elem, name );
		}

		if ( computed ) {

			// A tribute to the "awesome hack by Dean Edwards"
			// Chrome < 17 and Safari 5.0 uses "computed value"
			// instead of "used value" for margin-right
			// Safari 5.1.7 (at least) returns percentage for a larger set of values,
			// but width seems to be reliably pixels
			// this is against the CSSOM draft spec:
			// http://dev.w3.org/csswg/cssom/#resolved-values
			if ( !support.pixelMarginRight() && rnumnonpx.test( ret ) && rmargin.test( name ) ) {

				// Remember the original values
				width = style.width;
				minWidth = style.minWidth;
				maxWidth = style.maxWidth;

				// Put in the new values to get a computed value out
				style.minWidth = style.maxWidth = style.width = ret;
				ret = computed.width;

				// Revert the changed values
				style.width = width;
				style.minWidth = minWidth;
				style.maxWidth = maxWidth;
			}
		}

		// Support: IE
		// IE returns zIndex value as an integer.
		return ret === undefined ?
			ret :
			ret + "";
	};
} else if ( documentElement.currentStyle ) {
	getStyles = function( elem ) {
		return elem.currentStyle;
	};

	curCSS = function( elem, name, computed ) {
		var left, rs, rsLeft, ret,
			style = elem.style;

		computed = computed || getStyles( elem );
		ret = computed ? computed[ name ] : undefined;

		// Avoid setting ret to empty string here
		// so we don't default to auto
		if ( ret == null && style && style[ name ] ) {
			ret = style[ name ];
		}

		// From the awesome hack by Dean Edwards
		// http://erik.eae.net/archives/2007/07/27/18.54.15/#comment-102291

		// If we're not dealing with a regular pixel number
		// but a number that has a weird ending, we need to convert it to pixels
		// but not position css attributes, as those are
		// proportional to the parent element instead
		// and we can't measure the parent instead because it
		// might trigger a "stacking dolls" problem
		if ( rnumnonpx.test( ret ) && !rposition.test( name ) ) {

			// Remember the original values
			left = style.left;
			rs = elem.runtimeStyle;
			rsLeft = rs && rs.left;

			// Put in the new values to get a computed value out
			if ( rsLeft ) {
				rs.left = elem.currentStyle.left;
			}
			style.left = name === "fontSize" ? "1em" : ret;
			ret = style.pixelLeft + "px";

			// Revert the changed values
			style.left = left;
			if ( rsLeft ) {
				rs.left = rsLeft;
			}
		}

		// Support: IE
		// IE returns zIndex value as an integer.
		return ret === undefined ?
			ret :
			ret + "" || "auto";
	};
}




function addGetHookIf( conditionFn, hookFn ) {

	// Define the hook, we'll check on the first run if it's really needed.
	return {
		get: function() {
			if ( conditionFn() ) {

				// Hook not needed (or it's not possible to use it due
				// to missing dependency), remove it.
				delete this.get;
				return;
			}

			// Hook needed; redefine it so that the support test is not executed again.
			return ( this.get = hookFn ).apply( this, arguments );
		}
	};
}


var

		ralpha = /alpha\([^)]*\)/i,
	ropacity = /opacity\s*=\s*([^)]*)/i,

	// swappable if display is none or starts with table except
	// "table", "table-cell", or "table-caption"
	// see here for display values:
	// https://developer.mozilla.org/en-US/docs/CSS/display
	rdisplayswap = /^(none|table(?!-c[ea]).+)/,
	rnumsplit = new RegExp( "^(" + pnum + ")(.*)$", "i" ),

	cssShow = { position: "absolute", visibility: "hidden", display: "block" },
	cssNormalTransform = {
		letterSpacing: "0",
		fontWeight: "400"
	},

	cssPrefixes = [ "Webkit", "O", "Moz", "ms" ],
	emptyStyle = document.createElement( "div" ).style;


// return a css property mapped to a potentially vendor prefixed property
function vendorPropName( name ) {

	// shortcut for names that are not vendor prefixed
	if ( name in emptyStyle ) {
		return name;
	}

	// check for vendor prefixed names
	var capName = name.charAt( 0 ).toUpperCase() + name.slice( 1 ),
		i = cssPrefixes.length;

	while ( i-- ) {
		name = cssPrefixes[ i ] + capName;
		if ( name in emptyStyle ) {
			return name;
		}
	}
}

function showHide( elements, show ) {
	var display, elem, hidden,
		values = [],
		index = 0,
		length = elements.length;

	for ( ; index < length; index++ ) {
		elem = elements[ index ];
		if ( !elem.style ) {
			continue;
		}

		values[ index ] = jQuery._data( elem, "olddisplay" );
		display = elem.style.display;
		if ( show ) {

			// Reset the inline display of this element to learn if it is
			// being hidden by cascaded rules or not
			if ( !values[ index ] && display === "none" ) {
				elem.style.display = "";
			}

			// Set elements which have been overridden with display: none
			// in a stylesheet to whatever the default browser style is
			// for such an element
			if ( elem.style.display === "" && isHidden( elem ) ) {
				values[ index ] =
					jQuery._data( elem, "olddisplay", defaultDisplay( elem.nodeName ) );
			}
		} else {
			hidden = isHidden( elem );

			if ( display && display !== "none" || !hidden ) {
				jQuery._data(
					elem,
					"olddisplay",
					hidden ? display : jQuery.css( elem, "display" )
				);
			}
		}
	}

	// Set the display of most of the elements in a second loop
	// to avoid the constant reflow
	for ( index = 0; index < length; index++ ) {
		elem = elements[ index ];
		if ( !elem.style ) {
			continue;
		}
		if ( !show || elem.style.display === "none" || elem.style.display === "" ) {
			elem.style.display = show ? values[ index ] || "" : "none";
		}
	}

	return elements;
}

function setPositiveNumber( elem, value, subtract ) {
	var matches = rnumsplit.exec( value );
	return matches ?

		// Guard against undefined "subtract", e.g., when used as in cssHooks
		Math.max( 0, matches[ 1 ] - ( subtract || 0 ) ) + ( matches[ 2 ] || "px" ) :
		value;
}

function augmentWidthOrHeight( elem, name, extra, isBorderBox, styles ) {
	var i = extra === ( isBorderBox ? "border" : "content" ) ?

		// If we already have the right measurement, avoid augmentation
		4 :

		// Otherwise initialize for horizontal or vertical properties
		name === "width" ? 1 : 0,

		val = 0;

	for ( ; i < 4; i += 2 ) {

		// both box models exclude margin, so add it if we want it
		if ( extra === "margin" ) {
			val += jQuery.css( elem, extra + cssExpand[ i ], true, styles );
		}

		if ( isBorderBox ) {

			// border-box includes padding, so remove it if we want content
			if ( extra === "content" ) {
				val -= jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );
			}

			// at this point, extra isn't border nor margin, so remove border
			if ( extra !== "margin" ) {
				val -= jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}
		} else {

			// at this point, extra isn't content, so add padding
			val += jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );

			// at this point, extra isn't content nor padding, so add border
			if ( extra !== "padding" ) {
				val += jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}
		}
	}

	return val;
}

function getWidthOrHeight( elem, name, extra ) {

	// Start with offset property, which is equivalent to the border-box value
	var valueIsBorderBox = true,
		val = name === "width" ? elem.offsetWidth : elem.offsetHeight,
		styles = getStyles( elem ),
		isBorderBox = support.boxSizing &&
			jQuery.css( elem, "boxSizing", false, styles ) === "border-box";

	// some non-html elements return undefined for offsetWidth, so check for null/undefined
	// svg - https://bugzilla.mozilla.org/show_bug.cgi?id=649285
	// MathML - https://bugzilla.mozilla.org/show_bug.cgi?id=491668
	if ( val <= 0 || val == null ) {

		// Fall back to computed then uncomputed css if necessary
		val = curCSS( elem, name, styles );
		if ( val < 0 || val == null ) {
			val = elem.style[ name ];
		}

		// Computed unit is not pixels. Stop here and return.
		if ( rnumnonpx.test( val ) ) {
			return val;
		}

		// we need the check for style in case a browser which returns unreliable values
		// for getComputedStyle silently falls back to the reliable elem.style
		valueIsBorderBox = isBorderBox &&
			( support.boxSizingReliable() || val === elem.style[ name ] );

		// Normalize "", auto, and prepare for extra
		val = parseFloat( val ) || 0;
	}

	// use the active box-sizing model to add/subtract irrelevant styles
	return ( val +
		augmentWidthOrHeight(
			elem,
			name,
			extra || ( isBorderBox ? "border" : "content" ),
			valueIsBorderBox,
			styles
		)
	) + "px";
}

jQuery.extend( {

	// Add in style property hooks for overriding the default
	// behavior of getting and setting a style property
	cssHooks: {
		opacity: {
			get: function( elem, computed ) {
				if ( computed ) {

					// We should always get a number back from opacity
					var ret = curCSS( elem, "opacity" );
					return ret === "" ? "1" : ret;
				}
			}
		}
	},

	// Don't automatically add "px" to these possibly-unitless properties
	cssNumber: {
		"animationIterationCount": true,
		"columnCount": true,
		"fillOpacity": true,
		"flexGrow": true,
		"flexShrink": true,
		"fontWeight": true,
		"lineHeight": true,
		"opacity": true,
		"order": true,
		"orphans": true,
		"widows": true,
		"zIndex": true,
		"zoom": true
	},

	// Add in properties whose names you wish to fix before
	// setting or getting the value
	cssProps: {

		// normalize float css property
		"float": support.cssFloat ? "cssFloat" : "styleFloat"
	},

	// Get and set the style property on a DOM Node
	style: function( elem, name, value, extra ) {

		// Don't set styles on text and comment nodes
		if ( !elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style ) {
			return;
		}

		// Make sure that we're working with the right name
		var ret, type, hooks,
			origName = jQuery.camelCase( name ),
			style = elem.style;

		name = jQuery.cssProps[ origName ] ||
			( jQuery.cssProps[ origName ] = vendorPropName( origName ) || origName );

		// gets hook for the prefixed version
		// followed by the unprefixed version
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// Check if we're setting a value
		if ( value !== undefined ) {
			type = typeof value;

			// Convert "+=" or "-=" to relative numbers (#7345)
			if ( type === "string" && ( ret = rcssNum.exec( value ) ) && ret[ 1 ] ) {
				value = adjustCSS( elem, name, ret );

				// Fixes bug #9237
				type = "number";
			}

			// Make sure that null and NaN values aren't set. See: #7116
			if ( value == null || value !== value ) {
				return;
			}

			// If a number was passed in, add the unit (except for certain CSS properties)
			if ( type === "number" ) {
				value += ret && ret[ 3 ] || ( jQuery.cssNumber[ origName ] ? "" : "px" );
			}

			// Fixes #8908, it can be done more correctly by specifing setters in cssHooks,
			// but it would mean to define eight
			// (for every problematic property) identical functions
			if ( !support.clearCloneStyle && value === "" && name.indexOf( "background" ) === 0 ) {
				style[ name ] = "inherit";
			}

			// If a hook was provided, use that value, otherwise just set the specified value
			if ( !hooks || !( "set" in hooks ) ||
				( value = hooks.set( elem, value, extra ) ) !== undefined ) {

				// Support: IE
				// Swallow errors from 'invalid' CSS values (#5509)
				try {
					style[ name ] = value;
				} catch ( e ) {}
			}

		} else {

			// If a hook was provided get the non-computed value from there
			if ( hooks && "get" in hooks &&
				( ret = hooks.get( elem, false, extra ) ) !== undefined ) {

				return ret;
			}

			// Otherwise just get the value from the style object
			return style[ name ];
		}
	},

	css: function( elem, name, extra, styles ) {
		var num, val, hooks,
			origName = jQuery.camelCase( name );

		// Make sure that we're working with the right name
		name = jQuery.cssProps[ origName ] ||
			( jQuery.cssProps[ origName ] = vendorPropName( origName ) || origName );

		// gets hook for the prefixed version
		// followed by the unprefixed version
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// If a hook was provided get the computed value from there
		if ( hooks && "get" in hooks ) {
			val = hooks.get( elem, true, extra );
		}

		// Otherwise, if a way to get the computed value exists, use that
		if ( val === undefined ) {
			val = curCSS( elem, name, styles );
		}

		//convert "normal" to computed value
		if ( val === "normal" && name in cssNormalTransform ) {
			val = cssNormalTransform[ name ];
		}

		// Return, converting to number if forced or a qualifier was provided and val looks numeric
		if ( extra === "" || extra ) {
			num = parseFloat( val );
			return extra === true || isFinite( num ) ? num || 0 : val;
		}
		return val;
	}
} );

jQuery.each( [ "height", "width" ], function( i, name ) {
	jQuery.cssHooks[ name ] = {
		get: function( elem, computed, extra ) {
			if ( computed ) {

				// certain elements can have dimension info if we invisibly show them
				// however, it must have a current display style that would benefit from this
				return rdisplayswap.test( jQuery.css( elem, "display" ) ) &&
					elem.offsetWidth === 0 ?
						swap( elem, cssShow, function() {
							return getWidthOrHeight( elem, name, extra );
						} ) :
						getWidthOrHeight( elem, name, extra );
			}
		},

		set: function( elem, value, extra ) {
			var styles = extra && getStyles( elem );
			return setPositiveNumber( elem, value, extra ?
				augmentWidthOrHeight(
					elem,
					name,
					extra,
					support.boxSizing &&
						jQuery.css( elem, "boxSizing", false, styles ) === "border-box",
					styles
				) : 0
			);
		}
	};
} );

if ( !support.opacity ) {
	jQuery.cssHooks.opacity = {
		get: function( elem, computed ) {

			// IE uses filters for opacity
			return ropacity.test( ( computed && elem.currentStyle ?
				elem.currentStyle.filter :
				elem.style.filter ) || "" ) ?
					( 0.01 * parseFloat( RegExp.$1 ) ) + "" :
					computed ? "1" : "";
		},

		set: function( elem, value ) {
			var style = elem.style,
				currentStyle = elem.currentStyle,
				opacity = jQuery.isNumeric( value ) ? "alpha(opacity=" + value * 100 + ")" : "",
				filter = currentStyle && currentStyle.filter || style.filter || "";

			// IE has trouble with opacity if it does not have layout
			// Force it by setting the zoom level
			style.zoom = 1;

			// if setting opacity to 1, and no other filters exist -
			// attempt to remove filter attribute #6652
			// if value === "", then remove inline opacity #12685
			if ( ( value >= 1 || value === "" ) &&
					jQuery.trim( filter.replace( ralpha, "" ) ) === "" &&
					style.removeAttribute ) {

				// Setting style.filter to null, "" & " " still leave "filter:" in the cssText
				// if "filter:" is present at all, clearType is disabled, we want to avoid this
				// style.removeAttribute is IE Only, but so apparently is this code path...
				style.removeAttribute( "filter" );

				// if there is no filter style applied in a css rule
				// or unset inline opacity, we are done
				if ( value === "" || currentStyle && !currentStyle.filter ) {
					return;
				}
			}

			// otherwise, set new filter values
			style.filter = ralpha.test( filter ) ?
				filter.replace( ralpha, opacity ) :
				filter + " " + opacity;
		}
	};
}

jQuery.cssHooks.marginRight = addGetHookIf( support.reliableMarginRight,
	function( elem, computed ) {
		if ( computed ) {
			return swap( elem, { "display": "inline-block" },
				curCSS, [ elem, "marginRight" ] );
		}
	}
);

jQuery.cssHooks.marginLeft = addGetHookIf( support.reliableMarginLeft,
	function( elem, computed ) {
		if ( computed ) {
			return (
				parseFloat( curCSS( elem, "marginLeft" ) ) ||

				// Support: IE<=11+
				// Running getBoundingClientRect on a disconnected node in IE throws an error
				// Support: IE8 only
				// getClientRects() errors on disconnected elems
				( jQuery.contains( elem.ownerDocument, elem ) ?
					elem.getBoundingClientRect().left -
						swap( elem, { marginLeft: 0 }, function() {
							return elem.getBoundingClientRect().left;
						} ) :
					0
				)
			) + "px";
		}
	}
);

// These hooks are used by animate to expand properties
jQuery.each( {
	margin: "",
	padding: "",
	border: "Width"
}, function( prefix, suffix ) {
	jQuery.cssHooks[ prefix + suffix ] = {
		expand: function( value ) {
			var i = 0,
				expanded = {},

				// assumes a single number if not a string
				parts = typeof value === "string" ? value.split( " " ) : [ value ];

			for ( ; i < 4; i++ ) {
				expanded[ prefix + cssExpand[ i ] + suffix ] =
					parts[ i ] || parts[ i - 2 ] || parts[ 0 ];
			}

			return expanded;
		}
	};

	if ( !rmargin.test( prefix ) ) {
		jQuery.cssHooks[ prefix + suffix ].set = setPositiveNumber;
	}
} );

jQuery.fn.extend( {
	css: function( name, value ) {
		return access( this, function( elem, name, value ) {
			var styles, len,
				map = {},
				i = 0;

			if ( jQuery.isArray( name ) ) {
				styles = getStyles( elem );
				len = name.length;

				for ( ; i < len; i++ ) {
					map[ name[ i ] ] = jQuery.css( elem, name[ i ], false, styles );
				}

				return map;
			}

			return value !== undefined ?
				jQuery.style( elem, name, value ) :
				jQuery.css( elem, name );
		}, name, value, arguments.length > 1 );
	},
	show: function() {
		return showHide( this, true );
	},
	hide: function() {
		return showHide( this );
	},
	toggle: function( state ) {
		if ( typeof state === "boolean" ) {
			return state ? this.show() : this.hide();
		}

		return this.each( function() {
			if ( isHidden( this ) ) {
				jQuery( this ).show();
			} else {
				jQuery( this ).hide();
			}
		} );
	}
} );


function Tween( elem, options, prop, end, easing ) {
	return new Tween.prototype.init( elem, options, prop, end, easing );
}
jQuery.Tween = Tween;

Tween.prototype = {
	constructor: Tween,
	init: function( elem, options, prop, end, easing, unit ) {
		this.elem = elem;
		this.prop = prop;
		this.easing = easing || jQuery.easing._default;
		this.options = options;
		this.start = this.now = this.cur();
		this.end = end;
		this.unit = unit || ( jQuery.cssNumber[ prop ] ? "" : "px" );
	},
	cur: function() {
		var hooks = Tween.propHooks[ this.prop ];

		return hooks && hooks.get ?
			hooks.get( this ) :
			Tween.propHooks._default.get( this );
	},
	run: function( percent ) {
		var eased,
			hooks = Tween.propHooks[ this.prop ];

		if ( this.options.duration ) {
			this.pos = eased = jQuery.easing[ this.easing ](
				percent, this.options.duration * percent, 0, 1, this.options.duration
			);
		} else {
			this.pos = eased = percent;
		}
		this.now = ( this.end - this.start ) * eased + this.start;

		if ( this.options.step ) {
			this.options.step.call( this.elem, this.now, this );
		}

		if ( hooks && hooks.set ) {
			hooks.set( this );
		} else {
			Tween.propHooks._default.set( this );
		}
		return this;
	}
};

Tween.prototype.init.prototype = Tween.prototype;

Tween.propHooks = {
	_default: {
		get: function( tween ) {
			var result;

			// Use a property on the element directly when it is not a DOM element,
			// or when there is no matching style property that exists.
			if ( tween.elem.nodeType !== 1 ||
				tween.elem[ tween.prop ] != null && tween.elem.style[ tween.prop ] == null ) {
				return tween.elem[ tween.prop ];
			}

			// passing an empty string as a 3rd parameter to .css will automatically
			// attempt a parseFloat and fallback to a string if the parse fails
			// so, simple values such as "10px" are parsed to Float.
			// complex values such as "rotate(1rad)" are returned as is.
			result = jQuery.css( tween.elem, tween.prop, "" );

			// Empty strings, null, undefined and "auto" are converted to 0.
			return !result || result === "auto" ? 0 : result;
		},
		set: function( tween ) {

			// use step hook for back compat - use cssHook if its there - use .style if its
			// available and use plain properties where available
			if ( jQuery.fx.step[ tween.prop ] ) {
				jQuery.fx.step[ tween.prop ]( tween );
			} else if ( tween.elem.nodeType === 1 &&
				( tween.elem.style[ jQuery.cssProps[ tween.prop ] ] != null ||
					jQuery.cssHooks[ tween.prop ] ) ) {
				jQuery.style( tween.elem, tween.prop, tween.now + tween.unit );
			} else {
				tween.elem[ tween.prop ] = tween.now;
			}
		}
	}
};

// Support: IE <=9
// Panic based approach to setting things on disconnected nodes

Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
	set: function( tween ) {
		if ( tween.elem.nodeType && tween.elem.parentNode ) {
			tween.elem[ tween.prop ] = tween.now;
		}
	}
};

jQuery.easing = {
	linear: function( p ) {
		return p;
	},
	swing: function( p ) {
		return 0.5 - Math.cos( p * Math.PI ) / 2;
	},
	_default: "swing"
};

jQuery.fx = Tween.prototype.init;

// Back Compat <1.8 extension point
jQuery.fx.step = {};




var
	fxNow, timerId,
	rfxtypes = /^(?:toggle|show|hide)$/,
	rrun = /queueHooks$/;

// Animations created synchronously will run synchronously
function createFxNow() {
	window.setTimeout( function() {
		fxNow = undefined;
	} );
	return ( fxNow = jQuery.now() );
}

// Generate parameters to create a standard animation
function genFx( type, includeWidth ) {
	var which,
		attrs = { height: type },
		i = 0;

	// if we include width, step value is 1 to do all cssExpand values,
	// if we don't include width, step value is 2 to skip over Left and Right
	includeWidth = includeWidth ? 1 : 0;
	for ( ; i < 4 ; i += 2 - includeWidth ) {
		which = cssExpand[ i ];
		attrs[ "margin" + which ] = attrs[ "padding" + which ] = type;
	}

	if ( includeWidth ) {
		attrs.opacity = attrs.width = type;
	}

	return attrs;
}

function createTween( value, prop, animation ) {
	var tween,
		collection = ( Animation.tweeners[ prop ] || [] ).concat( Animation.tweeners[ "*" ] ),
		index = 0,
		length = collection.length;
	for ( ; index < length; index++ ) {
		if ( ( tween = collection[ index ].call( animation, prop, value ) ) ) {

			// we're done with this property
			return tween;
		}
	}
}

function defaultPrefilter( elem, props, opts ) {
	/* jshint validthis: true */
	var prop, value, toggle, tween, hooks, oldfire, display, checkDisplay,
		anim = this,
		orig = {},
		style = elem.style,
		hidden = elem.nodeType && isHidden( elem ),
		dataShow = jQuery._data( elem, "fxshow" );

	// handle queue: false promises
	if ( !opts.queue ) {
		hooks = jQuery._queueHooks( elem, "fx" );
		if ( hooks.unqueued == null ) {
			hooks.unqueued = 0;
			oldfire = hooks.empty.fire;
			hooks.empty.fire = function() {
				if ( !hooks.unqueued ) {
					oldfire();
				}
			};
		}
		hooks.unqueued++;

		anim.always( function() {

			// doing this makes sure that the complete handler will be called
			// before this completes
			anim.always( function() {
				hooks.unqueued--;
				if ( !jQuery.queue( elem, "fx" ).length ) {
					hooks.empty.fire();
				}
			} );
		} );
	}

	// height/width overflow pass
	if ( elem.nodeType === 1 && ( "height" in props || "width" in props ) ) {

		// Make sure that nothing sneaks out
		// Record all 3 overflow attributes because IE does not
		// change the overflow attribute when overflowX and
		// overflowY are set to the same value
		opts.overflow = [ style.overflow, style.overflowX, style.overflowY ];

		// Set display property to inline-block for height/width
		// animations on inline elements that are having width/height animated
		display = jQuery.css( elem, "display" );

		// Test default display if display is currently "none"
		checkDisplay = display === "none" ?
			jQuery._data( elem, "olddisplay" ) || defaultDisplay( elem.nodeName ) : display;

		if ( checkDisplay === "inline" && jQuery.css( elem, "float" ) === "none" ) {

			// inline-level elements accept inline-block;
			// block-level elements need to be inline with layout
			if ( !support.inlineBlockNeedsLayout || defaultDisplay( elem.nodeName ) === "inline" ) {
				style.display = "inline-block";
			} else {
				style.zoom = 1;
			}
		}
	}

	if ( opts.overflow ) {
		style.overflow = "hidden";
		if ( !support.shrinkWrapBlocks() ) {
			anim.always( function() {
				style.overflow = opts.overflow[ 0 ];
				style.overflowX = opts.overflow[ 1 ];
				style.overflowY = opts.overflow[ 2 ];
			} );
		}
	}

	// show/hide pass
	for ( prop in props ) {
		value = props[ prop ];
		if ( rfxtypes.exec( value ) ) {
			delete props[ prop ];
			toggle = toggle || value === "toggle";
			if ( value === ( hidden ? "hide" : "show" ) ) {

				// If there is dataShow left over from a stopped hide or show
				// and we are going to proceed with show, we should pretend to be hidden
				if ( value === "show" && dataShow && dataShow[ prop ] !== undefined ) {
					hidden = true;
				} else {
					continue;
				}
			}
			orig[ prop ] = dataShow && dataShow[ prop ] || jQuery.style( elem, prop );

		// Any non-fx value stops us from restoring the original display value
		} else {
			display = undefined;
		}
	}

	if ( !jQuery.isEmptyObject( orig ) ) {
		if ( dataShow ) {
			if ( "hidden" in dataShow ) {
				hidden = dataShow.hidden;
			}
		} else {
			dataShow = jQuery._data( elem, "fxshow", {} );
		}

		// store state if its toggle - enables .stop().toggle() to "reverse"
		if ( toggle ) {
			dataShow.hidden = !hidden;
		}
		if ( hidden ) {
			jQuery( elem ).show();
		} else {
			anim.done( function() {
				jQuery( elem ).hide();
			} );
		}
		anim.done( function() {
			var prop;
			jQuery._removeData( elem, "fxshow" );
			for ( prop in orig ) {
				jQuery.style( elem, prop, orig[ prop ] );
			}
		} );
		for ( prop in orig ) {
			tween = createTween( hidden ? dataShow[ prop ] : 0, prop, anim );

			if ( !( prop in dataShow ) ) {
				dataShow[ prop ] = tween.start;
				if ( hidden ) {
					tween.end = tween.start;
					tween.start = prop === "width" || prop === "height" ? 1 : 0;
				}
			}
		}

	// If this is a noop like .hide().hide(), restore an overwritten display value
	} else if ( ( display === "none" ? defaultDisplay( elem.nodeName ) : display ) === "inline" ) {
		style.display = display;
	}
}

function propFilter( props, specialEasing ) {
	var index, name, easing, value, hooks;

	// camelCase, specialEasing and expand cssHook pass
	for ( index in props ) {
		name = jQuery.camelCase( index );
		easing = specialEasing[ name ];
		value = props[ index ];
		if ( jQuery.isArray( value ) ) {
			easing = value[ 1 ];
			value = props[ index ] = value[ 0 ];
		}

		if ( index !== name ) {
			props[ name ] = value;
			delete props[ index ];
		}

		hooks = jQuery.cssHooks[ name ];
		if ( hooks && "expand" in hooks ) {
			value = hooks.expand( value );
			delete props[ name ];

			// not quite $.extend, this wont overwrite keys already present.
			// also - reusing 'index' from above because we have the correct "name"
			for ( index in value ) {
				if ( !( index in props ) ) {
					props[ index ] = value[ index ];
					specialEasing[ index ] = easing;
				}
			}
		} else {
			specialEasing[ name ] = easing;
		}
	}
}

function Animation( elem, properties, options ) {
	var result,
		stopped,
		index = 0,
		length = Animation.prefilters.length,
		deferred = jQuery.Deferred().always( function() {

			// don't match elem in the :animated selector
			delete tick.elem;
		} ),
		tick = function() {
			if ( stopped ) {
				return false;
			}
			var currentTime = fxNow || createFxNow(),
				remaining = Math.max( 0, animation.startTime + animation.duration - currentTime ),

				// Support: Android 2.3
				// Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (#12497)
				temp = remaining / animation.duration || 0,
				percent = 1 - temp,
				index = 0,
				length = animation.tweens.length;

			for ( ; index < length ; index++ ) {
				animation.tweens[ index ].run( percent );
			}

			deferred.notifyWith( elem, [ animation, percent, remaining ] );

			if ( percent < 1 && length ) {
				return remaining;
			} else {
				deferred.resolveWith( elem, [ animation ] );
				return false;
			}
		},
		animation = deferred.promise( {
			elem: elem,
			props: jQuery.extend( {}, properties ),
			opts: jQuery.extend( true, {
				specialEasing: {},
				easing: jQuery.easing._default
			}, options ),
			originalProperties: properties,
			originalOptions: options,
			startTime: fxNow || createFxNow(),
			duration: options.duration,
			tweens: [],
			createTween: function( prop, end ) {
				var tween = jQuery.Tween( elem, animation.opts, prop, end,
						animation.opts.specialEasing[ prop ] || animation.opts.easing );
				animation.tweens.push( tween );
				return tween;
			},
			stop: function( gotoEnd ) {
				var index = 0,

					// if we are going to the end, we want to run all the tweens
					// otherwise we skip this part
					length = gotoEnd ? animation.tweens.length : 0;
				if ( stopped ) {
					return this;
				}
				stopped = true;
				for ( ; index < length ; index++ ) {
					animation.tweens[ index ].run( 1 );
				}

				// resolve when we played the last frame
				// otherwise, reject
				if ( gotoEnd ) {
					deferred.notifyWith( elem, [ animation, 1, 0 ] );
					deferred.resolveWith( elem, [ animation, gotoEnd ] );
				} else {
					deferred.rejectWith( elem, [ animation, gotoEnd ] );
				}
				return this;
			}
		} ),
		props = animation.props;

	propFilter( props, animation.opts.specialEasing );

	for ( ; index < length ; index++ ) {
		result = Animation.prefilters[ index ].call( animation, elem, props, animation.opts );
		if ( result ) {
			if ( jQuery.isFunction( result.stop ) ) {
				jQuery._queueHooks( animation.elem, animation.opts.queue ).stop =
					jQuery.proxy( result.stop, result );
			}
			return result;
		}
	}

	jQuery.map( props, createTween, animation );

	if ( jQuery.isFunction( animation.opts.start ) ) {
		animation.opts.start.call( elem, animation );
	}

	jQuery.fx.timer(
		jQuery.extend( tick, {
			elem: elem,
			anim: animation,
			queue: animation.opts.queue
		} )
	);

	// attach callbacks from options
	return animation.progress( animation.opts.progress )
		.done( animation.opts.done, animation.opts.complete )
		.fail( animation.opts.fail )
		.always( animation.opts.always );
}

jQuery.Animation = jQuery.extend( Animation, {

	tweeners: {
		"*": [ function( prop, value ) {
			var tween = this.createTween( prop, value );
			adjustCSS( tween.elem, prop, rcssNum.exec( value ), tween );
			return tween;
		} ]
	},

	tweener: function( props, callback ) {
		if ( jQuery.isFunction( props ) ) {
			callback = props;
			props = [ "*" ];
		} else {
			props = props.match( rnotwhite );
		}

		var prop,
			index = 0,
			length = props.length;

		for ( ; index < length ; index++ ) {
			prop = props[ index ];
			Animation.tweeners[ prop ] = Animation.tweeners[ prop ] || [];
			Animation.tweeners[ prop ].unshift( callback );
		}
	},

	prefilters: [ defaultPrefilter ],

	prefilter: function( callback, prepend ) {
		if ( prepend ) {
			Animation.prefilters.unshift( callback );
		} else {
			Animation.prefilters.push( callback );
		}
	}
} );

jQuery.speed = function( speed, easing, fn ) {
	var opt = speed && typeof speed === "object" ? jQuery.extend( {}, speed ) : {
		complete: fn || !fn && easing ||
			jQuery.isFunction( speed ) && speed,
		duration: speed,
		easing: fn && easing || easing && !jQuery.isFunction( easing ) && easing
	};

	opt.duration = jQuery.fx.off ? 0 : typeof opt.duration === "number" ? opt.duration :
		opt.duration in jQuery.fx.speeds ?
			jQuery.fx.speeds[ opt.duration ] : jQuery.fx.speeds._default;

	// normalize opt.queue - true/undefined/null -> "fx"
	if ( opt.queue == null || opt.queue === true ) {
		opt.queue = "fx";
	}

	// Queueing
	opt.old = opt.complete;

	opt.complete = function() {
		if ( jQuery.isFunction( opt.old ) ) {
			opt.old.call( this );
		}

		if ( opt.queue ) {
			jQuery.dequeue( this, opt.queue );
		}
	};

	return opt;
};

jQuery.fn.extend( {
	fadeTo: function( speed, to, easing, callback ) {

		// show any hidden elements after setting opacity to 0
		return this.filter( isHidden ).css( "opacity", 0 ).show()

			// animate to the value specified
			.end().animate( { opacity: to }, speed, easing, callback );
	},
	animate: function( prop, speed, easing, callback ) {
		var empty = jQuery.isEmptyObject( prop ),
			optall = jQuery.speed( speed, easing, callback ),
			doAnimation = function() {

				// Operate on a copy of prop so per-property easing won't be lost
				var anim = Animation( this, jQuery.extend( {}, prop ), optall );

				// Empty animations, or finishing resolves immediately
				if ( empty || jQuery._data( this, "finish" ) ) {
					anim.stop( true );
				}
			};
			doAnimation.finish = doAnimation;

		return empty || optall.queue === false ?
			this.each( doAnimation ) :
			this.queue( optall.queue, doAnimation );
	},
	stop: function( type, clearQueue, gotoEnd ) {
		var stopQueue = function( hooks ) {
			var stop = hooks.stop;
			delete hooks.stop;
			stop( gotoEnd );
		};

		if ( typeof type !== "string" ) {
			gotoEnd = clearQueue;
			clearQueue = type;
			type = undefined;
		}
		if ( clearQueue && type !== false ) {
			this.queue( type || "fx", [] );
		}

		return this.each( function() {
			var dequeue = true,
				index = type != null && type + "queueHooks",
				timers = jQuery.timers,
				data = jQuery._data( this );

			if ( index ) {
				if ( data[ index ] && data[ index ].stop ) {
					stopQueue( data[ index ] );
				}
			} else {
				for ( index in data ) {
					if ( data[ index ] && data[ index ].stop && rrun.test( index ) ) {
						stopQueue( data[ index ] );
					}
				}
			}

			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this &&
					( type == null || timers[ index ].queue === type ) ) {

					timers[ index ].anim.stop( gotoEnd );
					dequeue = false;
					timers.splice( index, 1 );
				}
			}

			// start the next in the queue if the last step wasn't forced
			// timers currently will call their complete callbacks, which will dequeue
			// but only if they were gotoEnd
			if ( dequeue || !gotoEnd ) {
				jQuery.dequeue( this, type );
			}
		} );
	},
	finish: function( type ) {
		if ( type !== false ) {
			type = type || "fx";
		}
		return this.each( function() {
			var index,
				data = jQuery._data( this ),
				queue = data[ type + "queue" ],
				hooks = data[ type + "queueHooks" ],
				timers = jQuery.timers,
				length = queue ? queue.length : 0;

			// enable finishing flag on private data
			data.finish = true;

			// empty the queue first
			jQuery.queue( this, type, [] );

			if ( hooks && hooks.stop ) {
				hooks.stop.call( this, true );
			}

			// look for any active animations, and finish them
			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this && timers[ index ].queue === type ) {
					timers[ index ].anim.stop( true );
					timers.splice( index, 1 );
				}
			}

			// look for any animations in the old queue and finish them
			for ( index = 0; index < length; index++ ) {
				if ( queue[ index ] && queue[ index ].finish ) {
					queue[ index ].finish.call( this );
				}
			}

			// turn off finishing flag
			delete data.finish;
		} );
	}
} );

jQuery.each( [ "toggle", "show", "hide" ], function( i, name ) {
	var cssFn = jQuery.fn[ name ];
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return speed == null || typeof speed === "boolean" ?
			cssFn.apply( this, arguments ) :
			this.animate( genFx( name, true ), speed, easing, callback );
	};
} );

// Generate shortcuts for custom animations
jQuery.each( {
	slideDown: genFx( "show" ),
	slideUp: genFx( "hide" ),
	slideToggle: genFx( "toggle" ),
	fadeIn: { opacity: "show" },
	fadeOut: { opacity: "hide" },
	fadeToggle: { opacity: "toggle" }
}, function( name, props ) {
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return this.animate( props, speed, easing, callback );
	};
} );

jQuery.timers = [];
jQuery.fx.tick = function() {
	var timer,
		timers = jQuery.timers,
		i = 0;

	fxNow = jQuery.now();

	for ( ; i < timers.length; i++ ) {
		timer = timers[ i ];

		// Checks the timer has not already been removed
		if ( !timer() && timers[ i ] === timer ) {
			timers.splice( i--, 1 );
		}
	}

	if ( !timers.length ) {
		jQuery.fx.stop();
	}
	fxNow = undefined;
};

jQuery.fx.timer = function( timer ) {
	jQuery.timers.push( timer );
	if ( timer() ) {
		jQuery.fx.start();
	} else {
		jQuery.timers.pop();
	}
};

jQuery.fx.interval = 13;

jQuery.fx.start = function() {
	if ( !timerId ) {
		timerId = window.setInterval( jQuery.fx.tick, jQuery.fx.interval );
	}
};

jQuery.fx.stop = function() {
	window.clearInterval( timerId );
	timerId = null;
};

jQuery.fx.speeds = {
	slow: 600,
	fast: 200,

	// Default speed
	_default: 400
};


// Based off of the plugin by Clint Helfers, with permission.
// http://web.archive.org/web/20100324014747/http://blindsignals.com/index.php/2009/07/jquery-delay/
jQuery.fn.delay = function( time, type ) {
	time = jQuery.fx ? jQuery.fx.speeds[ time ] || time : time;
	type = type || "fx";

	return this.queue( type, function( next, hooks ) {
		var timeout = window.setTimeout( next, time );
		hooks.stop = function() {
			window.clearTimeout( timeout );
		};
	} );
};


( function() {
	var a,
		input = document.createElement( "input" ),
		div = document.createElement( "div" ),
		select = document.createElement( "select" ),
		opt = select.appendChild( document.createElement( "option" ) );

	// Setup
	div = document.createElement( "div" );
	div.setAttribute( "className", "t" );
	div.innerHTML = "  <link/><table></table><a href='/a'>a</a><input type='checkbox'/>";
	a = div.getElementsByTagName( "a" )[ 0 ];

	// Support: Windows Web Apps (WWA)
	// `type` must use .setAttribute for WWA (#14901)
	input.setAttribute( "type", "checkbox" );
	div.appendChild( input );

	a = div.getElementsByTagName( "a" )[ 0 ];

	// First batch of tests.
	a.style.cssText = "top:1px";

	// Test setAttribute on camelCase class.
	// If it works, we need attrFixes when doing get/setAttribute (ie6/7)
	support.getSetAttribute = div.className !== "t";

	// Get the style information from getAttribute
	// (IE uses .cssText instead)
	support.style = /top/.test( a.getAttribute( "style" ) );

	// Make sure that URLs aren't manipulated
	// (IE normalizes it by default)
	support.hrefNormalized = a.getAttribute( "href" ) === "/a";

	// Check the default checkbox/radio value ("" on WebKit; "on" elsewhere)
	support.checkOn = !!input.value;

	// Make sure that a selected-by-default option has a working selected property.
	// (WebKit defaults to false instead of true, IE too, if it's in an optgroup)
	support.optSelected = opt.selected;

	// Tests for enctype support on a form (#6743)
	support.enctype = !!document.createElement( "form" ).enctype;

	// Make sure that the options inside disabled selects aren't marked as disabled
	// (WebKit marks them as disabled)
	select.disabled = true;
	support.optDisabled = !opt.disabled;

	// Support: IE8 only
	// Check if we can trust getAttribute("value")
	input = document.createElement( "input" );
	input.setAttribute( "value", "" );
	support.input = input.getAttribute( "value" ) === "";

	// Check if an input maintains its value after becoming a radio
	input.value = "t";
	input.setAttribute( "type", "radio" );
	support.radioValue = input.value === "t";
} )();


var rreturn = /\r/g,
	rspaces = /[\x20\t\r\n\f]+/g;

jQuery.fn.extend( {
	val: function( value ) {
		var hooks, ret, isFunction,
			elem = this[ 0 ];

		if ( !arguments.length ) {
			if ( elem ) {
				hooks = jQuery.valHooks[ elem.type ] ||
					jQuery.valHooks[ elem.nodeName.toLowerCase() ];

				if (
					hooks &&
					"get" in hooks &&
					( ret = hooks.get( elem, "value" ) ) !== undefined
				) {
					return ret;
				}

				ret = elem.value;

				return typeof ret === "string" ?

					// handle most common string cases
					ret.replace( rreturn, "" ) :

					// handle cases where value is null/undef or number
					ret == null ? "" : ret;
			}

			return;
		}

		isFunction = jQuery.isFunction( value );

		return this.each( function( i ) {
			var val;

			if ( this.nodeType !== 1 ) {
				return;
			}

			if ( isFunction ) {
				val = value.call( this, i, jQuery( this ).val() );
			} else {
				val = value;
			}

			// Treat null/undefined as ""; convert numbers to string
			if ( val == null ) {
				val = "";
			} else if ( typeof val === "number" ) {
				val += "";
			} else if ( jQuery.isArray( val ) ) {
				val = jQuery.map( val, function( value ) {
					return value == null ? "" : value + "";
				} );
			}

			hooks = jQuery.valHooks[ this.type ] || jQuery.valHooks[ this.nodeName.toLowerCase() ];

			// If set returns undefined, fall back to normal setting
			if ( !hooks || !( "set" in hooks ) || hooks.set( this, val, "value" ) === undefined ) {
				this.value = val;
			}
		} );
	}
} );

jQuery.extend( {
	valHooks: {
		option: {
			get: function( elem ) {
				var val = jQuery.find.attr( elem, "value" );
				return val != null ?
					val :

					// Support: IE10-11+
					// option.text throws exceptions (#14686, #14858)
					// Strip and collapse whitespace
					// https://html.spec.whatwg.org/#strip-and-collapse-whitespace
					jQuery.trim( jQuery.text( elem ) ).replace( rspaces, " " );
			}
		},
		select: {
			get: function( elem ) {
				var value, option,
					options = elem.options,
					index = elem.selectedIndex,
					one = elem.type === "select-one" || index < 0,
					values = one ? null : [],
					max = one ? index + 1 : options.length,
					i = index < 0 ?
						max :
						one ? index : 0;

				// Loop through all the selected options
				for ( ; i < max; i++ ) {
					option = options[ i ];

					// oldIE doesn't update selected after form reset (#2551)
					if ( ( option.selected || i === index ) &&

							// Don't return options that are disabled or in a disabled optgroup
							( support.optDisabled ?
								!option.disabled :
								option.getAttribute( "disabled" ) === null ) &&
							( !option.parentNode.disabled ||
								!jQuery.nodeName( option.parentNode, "optgroup" ) ) ) {

						// Get the specific value for the option
						value = jQuery( option ).val();

						// We don't need an array for one selects
						if ( one ) {
							return value;
						}

						// Multi-Selects return an array
						values.push( value );
					}
				}

				return values;
			},

			set: function( elem, value ) {
				var optionSet, option,
					options = elem.options,
					values = jQuery.makeArray( value ),
					i = options.length;

				while ( i-- ) {
					option = options[ i ];

					if ( jQuery.inArray( jQuery.valHooks.option.get( option ), values ) > -1 ) {

						// Support: IE6
						// When new option element is added to select box we need to
						// force reflow of newly added node in order to workaround delay
						// of initialization properties
						try {
							option.selected = optionSet = true;

						} catch ( _ ) {

							// Will be executed only in IE6
							option.scrollHeight;
						}

					} else {
						option.selected = false;
					}
				}

				// Force browsers to behave consistently when non-matching value is set
				if ( !optionSet ) {
					elem.selectedIndex = -1;
				}

				return options;
			}
		}
	}
} );

// Radios and checkboxes getter/setter
jQuery.each( [ "radio", "checkbox" ], function() {
	jQuery.valHooks[ this ] = {
		set: function( elem, value ) {
			if ( jQuery.isArray( value ) ) {
				return ( elem.checked = jQuery.inArray( jQuery( elem ).val(), value ) > -1 );
			}
		}
	};
	if ( !support.checkOn ) {
		jQuery.valHooks[ this ].get = function( elem ) {
			return elem.getAttribute( "value" ) === null ? "on" : elem.value;
		};
	}
} );




var nodeHook, boolHook,
	attrHandle = jQuery.expr.attrHandle,
	ruseDefault = /^(?:checked|selected)$/i,
	getSetAttribute = support.getSetAttribute,
	getSetInput = support.input;

jQuery.fn.extend( {
	attr: function( name, value ) {
		return access( this, jQuery.attr, name, value, arguments.length > 1 );
	},

	removeAttr: function( name ) {
		return this.each( function() {
			jQuery.removeAttr( this, name );
		} );
	}
} );

jQuery.extend( {
	attr: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set attributes on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		// Fallback to prop when attributes are not supported
		if ( typeof elem.getAttribute === "undefined" ) {
			return jQuery.prop( elem, name, value );
		}

		// All attributes are lowercase
		// Grab necessary hook if one is defined
		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {
			name = name.toLowerCase();
			hooks = jQuery.attrHooks[ name ] ||
				( jQuery.expr.match.bool.test( name ) ? boolHook : nodeHook );
		}

		if ( value !== undefined ) {
			if ( value === null ) {
				jQuery.removeAttr( elem, name );
				return;
			}

			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			elem.setAttribute( name, value + "" );
			return value;
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		ret = jQuery.find.attr( elem, name );

		// Non-existent attributes return null, we normalize to undefined
		return ret == null ? undefined : ret;
	},

	attrHooks: {
		type: {
			set: function( elem, value ) {
				if ( !support.radioValue && value === "radio" &&
					jQuery.nodeName( elem, "input" ) ) {

					// Setting the type on a radio button after the value resets the value in IE8-9
					// Reset value to default in case type is set after value during creation
					var val = elem.value;
					elem.setAttribute( "type", value );
					if ( val ) {
						elem.value = val;
					}
					return value;
				}
			}
		}
	},

	removeAttr: function( elem, value ) {
		var name, propName,
			i = 0,
			attrNames = value && value.match( rnotwhite );

		if ( attrNames && elem.nodeType === 1 ) {
			while ( ( name = attrNames[ i++ ] ) ) {
				propName = jQuery.propFix[ name ] || name;

				// Boolean attributes get special treatment (#10870)
				if ( jQuery.expr.match.bool.test( name ) ) {

					// Set corresponding property to false
					if ( getSetInput && getSetAttribute || !ruseDefault.test( name ) ) {
						elem[ propName ] = false;

					// Support: IE<9
					// Also clear defaultChecked/defaultSelected (if appropriate)
					} else {
						elem[ jQuery.camelCase( "default-" + name ) ] =
							elem[ propName ] = false;
					}

				// See #9699 for explanation of this approach (setting first, then removal)
				} else {
					jQuery.attr( elem, name, "" );
				}

				elem.removeAttribute( getSetAttribute ? name : propName );
			}
		}
	}
} );

// Hooks for boolean attributes
boolHook = {
	set: function( elem, value, name ) {
		if ( value === false ) {

			// Remove boolean attributes when set to false
			jQuery.removeAttr( elem, name );
		} else if ( getSetInput && getSetAttribute || !ruseDefault.test( name ) ) {

			// IE<8 needs the *property* name
			elem.setAttribute( !getSetAttribute && jQuery.propFix[ name ] || name, name );

		} else {

			// Support: IE<9
			// Use defaultChecked and defaultSelected for oldIE
			elem[ jQuery.camelCase( "default-" + name ) ] = elem[ name ] = true;
		}
		return name;
	}
};

jQuery.each( jQuery.expr.match.bool.source.match( /\w+/g ), function( i, name ) {
	var getter = attrHandle[ name ] || jQuery.find.attr;

	if ( getSetInput && getSetAttribute || !ruseDefault.test( name ) ) {
		attrHandle[ name ] = function( elem, name, isXML ) {
			var ret, handle;
			if ( !isXML ) {

				// Avoid an infinite loop by temporarily removing this function from the getter
				handle = attrHandle[ name ];
				attrHandle[ name ] = ret;
				ret = getter( elem, name, isXML ) != null ?
					name.toLowerCase() :
					null;
				attrHandle[ name ] = handle;
			}
			return ret;
		};
	} else {
		attrHandle[ name ] = function( elem, name, isXML ) {
			if ( !isXML ) {
				return elem[ jQuery.camelCase( "default-" + name ) ] ?
					name.toLowerCase() :
					null;
			}
		};
	}
} );

// fix oldIE attroperties
if ( !getSetInput || !getSetAttribute ) {
	jQuery.attrHooks.value = {
		set: function( elem, value, name ) {
			if ( jQuery.nodeName( elem, "input" ) ) {

				// Does not return so that setAttribute is also used
				elem.defaultValue = value;
			} else {

				// Use nodeHook if defined (#1954); otherwise setAttribute is fine
				return nodeHook && nodeHook.set( elem, value, name );
			}
		}
	};
}

// IE6/7 do not support getting/setting some attributes with get/setAttribute
if ( !getSetAttribute ) {

	// Use this for any attribute in IE6/7
	// This fixes almost every IE6/7 issue
	nodeHook = {
		set: function( elem, value, name ) {

			// Set the existing or create a new attribute node
			var ret = elem.getAttributeNode( name );
			if ( !ret ) {
				elem.setAttributeNode(
					( ret = elem.ownerDocument.createAttribute( name ) )
				);
			}

			ret.value = value += "";

			// Break association with cloned elements by also using setAttribute (#9646)
			if ( name === "value" || value === elem.getAttribute( name ) ) {
				return value;
			}
		}
	};

	// Some attributes are constructed with empty-string values when not defined
	attrHandle.id = attrHandle.name = attrHandle.coords =
		function( elem, name, isXML ) {
			var ret;
			if ( !isXML ) {
				return ( ret = elem.getAttributeNode( name ) ) && ret.value !== "" ?
					ret.value :
					null;
			}
		};

	// Fixing value retrieval on a button requires this module
	jQuery.valHooks.button = {
		get: function( elem, name ) {
			var ret = elem.getAttributeNode( name );
			if ( ret && ret.specified ) {
				return ret.value;
			}
		},
		set: nodeHook.set
	};

	// Set contenteditable to false on removals(#10429)
	// Setting to empty string throws an error as an invalid value
	jQuery.attrHooks.contenteditable = {
		set: function( elem, value, name ) {
			nodeHook.set( elem, value === "" ? false : value, name );
		}
	};

	// Set width and height to auto instead of 0 on empty string( Bug #8150 )
	// This is for removals
	jQuery.each( [ "width", "height" ], function( i, name ) {
		jQuery.attrHooks[ name ] = {
			set: function( elem, value ) {
				if ( value === "" ) {
					elem.setAttribute( name, "auto" );
					return value;
				}
			}
		};
	} );
}

if ( !support.style ) {
	jQuery.attrHooks.style = {
		get: function( elem ) {

			// Return undefined in the case of empty string
			// Note: IE uppercases css property names, but if we were to .toLowerCase()
			// .cssText, that would destroy case sensitivity in URL's, like in "background"
			return elem.style.cssText || undefined;
		},
		set: function( elem, value ) {
			return ( elem.style.cssText = value + "" );
		}
	};
}




var rfocusable = /^(?:input|select|textarea|button|object)$/i,
	rclickable = /^(?:a|area)$/i;

jQuery.fn.extend( {
	prop: function( name, value ) {
		return access( this, jQuery.prop, name, value, arguments.length > 1 );
	},

	removeProp: function( name ) {
		name = jQuery.propFix[ name ] || name;
		return this.each( function() {

			// try/catch handles cases where IE balks (such as removing a property on window)
			try {
				this[ name ] = undefined;
				delete this[ name ];
			} catch ( e ) {}
		} );
	}
} );

jQuery.extend( {
	prop: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set properties on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {

			// Fix name and attach hooks
			name = jQuery.propFix[ name ] || name;
			hooks = jQuery.propHooks[ name ];
		}

		if ( value !== undefined ) {
			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			return ( elem[ name ] = value );
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		return elem[ name ];
	},

	propHooks: {
		tabIndex: {
			get: function( elem ) {

				// elem.tabIndex doesn't always return the
				// correct value when it hasn't been explicitly set
				// http://fluidproject.org/blog/2008/01/09/getting-setting-and-removing-tabindex-values-with-javascript/
				// Use proper attribute retrieval(#12072)
				var tabindex = jQuery.find.attr( elem, "tabindex" );

				return tabindex ?
					parseInt( tabindex, 10 ) :
					rfocusable.test( elem.nodeName ) ||
						rclickable.test( elem.nodeName ) && elem.href ?
							0 :
							-1;
			}
		}
	},

	propFix: {
		"for": "htmlFor",
		"class": "className"
	}
} );

// Some attributes require a special call on IE
// http://msdn.microsoft.com/en-us/library/ms536429%28VS.85%29.aspx
if ( !support.hrefNormalized ) {

	// href/src property should get the full normalized URL (#10299/#12915)
	jQuery.each( [ "href", "src" ], function( i, name ) {
		jQuery.propHooks[ name ] = {
			get: function( elem ) {
				return elem.getAttribute( name, 4 );
			}
		};
	} );
}

// Support: Safari, IE9+
// Accessing the selectedIndex property
// forces the browser to respect setting selected
// on the option
// The getter ensures a default option is selected
// when in an optgroup
if ( !support.optSelected ) {
	jQuery.propHooks.selected = {
		get: function( elem ) {
			var parent = elem.parentNode;

			if ( parent ) {
				parent.selectedIndex;

				// Make sure that it also works with optgroups, see #5701
				if ( parent.parentNode ) {
					parent.parentNode.selectedIndex;
				}
			}
			return null;
		},
		set: function( elem ) {
			var parent = elem.parentNode;
			if ( parent ) {
				parent.selectedIndex;

				if ( parent.parentNode ) {
					parent.parentNode.selectedIndex;
				}
			}
		}
	};
}

jQuery.each( [
	"tabIndex",
	"readOnly",
	"maxLength",
	"cellSpacing",
	"cellPadding",
	"rowSpan",
	"colSpan",
	"useMap",
	"frameBorder",
	"contentEditable"
], function() {
	jQuery.propFix[ this.toLowerCase() ] = this;
} );

// IE6/7 call enctype encoding
if ( !support.enctype ) {
	jQuery.propFix.enctype = "encoding";
}




var rclass = /[\t\r\n\f]/g;

function getClass( elem ) {
	return jQuery.attr( elem, "class" ) || "";
}

jQuery.fn.extend( {
	addClass: function( value ) {
		var classes, elem, cur, curValue, clazz, j, finalValue,
			i = 0;

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).addClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		if ( typeof value === "string" && value ) {
			classes = value.match( rnotwhite ) || [];

			while ( ( elem = this[ i++ ] ) ) {
				curValue = getClass( elem );
				cur = elem.nodeType === 1 &&
					( " " + curValue + " " ).replace( rclass, " " );

				if ( cur ) {
					j = 0;
					while ( ( clazz = classes[ j++ ] ) ) {
						if ( cur.indexOf( " " + clazz + " " ) < 0 ) {
							cur += clazz + " ";
						}
					}

					// only assign if different to avoid unneeded rendering.
					finalValue = jQuery.trim( cur );
					if ( curValue !== finalValue ) {
						jQuery.attr( elem, "class", finalValue );
					}
				}
			}
		}

		return this;
	},

	removeClass: function( value ) {
		var classes, elem, cur, curValue, clazz, j, finalValue,
			i = 0;

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).removeClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		if ( !arguments.length ) {
			return this.attr( "class", "" );
		}

		if ( typeof value === "string" && value ) {
			classes = value.match( rnotwhite ) || [];

			while ( ( elem = this[ i++ ] ) ) {
				curValue = getClass( elem );

				// This expression is here for better compressibility (see addClass)
				cur = elem.nodeType === 1 &&
					( " " + curValue + " " ).replace( rclass, " " );

				if ( cur ) {
					j = 0;
					while ( ( clazz = classes[ j++ ] ) ) {

						// Remove *all* instances
						while ( cur.indexOf( " " + clazz + " " ) > -1 ) {
							cur = cur.replace( " " + clazz + " ", " " );
						}
					}

					// Only assign if different to avoid unneeded rendering.
					finalValue = jQuery.trim( cur );
					if ( curValue !== finalValue ) {
						jQuery.attr( elem, "class", finalValue );
					}
				}
			}
		}

		return this;
	},

	toggleClass: function( value, stateVal ) {
		var type = typeof value;

		if ( typeof stateVal === "boolean" && type === "string" ) {
			return stateVal ? this.addClass( value ) : this.removeClass( value );
		}

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( i ) {
				jQuery( this ).toggleClass(
					value.call( this, i, getClass( this ), stateVal ),
					stateVal
				);
			} );
		}

		return this.each( function() {
			var className, i, self, classNames;

			if ( type === "string" ) {

				// Toggle individual class names
				i = 0;
				self = jQuery( this );
				classNames = value.match( rnotwhite ) || [];

				while ( ( className = classNames[ i++ ] ) ) {

					// Check each className given, space separated list
					if ( self.hasClass( className ) ) {
						self.removeClass( className );
					} else {
						self.addClass( className );
					}
				}

			// Toggle whole class name
			} else if ( value === undefined || type === "boolean" ) {
				className = getClass( this );
				if ( className ) {

					// store className if set
					jQuery._data( this, "__className__", className );
				}

				// If the element has a class name or if we're passed "false",
				// then remove the whole classname (if there was one, the above saved it).
				// Otherwise bring back whatever was previously saved (if anything),
				// falling back to the empty string if nothing was stored.
				jQuery.attr( this, "class",
					className || value === false ?
					"" :
					jQuery._data( this, "__className__" ) || ""
				);
			}
		} );
	},

	hasClass: function( selector ) {
		var className, elem,
			i = 0;

		className = " " + selector + " ";
		while ( ( elem = this[ i++ ] ) ) {
			if ( elem.nodeType === 1 &&
				( " " + getClass( elem ) + " " ).replace( rclass, " " )
					.indexOf( className ) > -1
			) {
				return true;
			}
		}

		return false;
	}
} );




// Return jQuery for attributes-only inclusion


jQuery.each( ( "blur focus focusin focusout load resize scroll unload click dblclick " +
	"mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave " +
	"change select submit keydown keypress keyup error contextmenu" ).split( " " ),
	function( i, name ) {

	// Handle event binding
	jQuery.fn[ name ] = function( data, fn ) {
		return arguments.length > 0 ?
			this.on( name, null, data, fn ) :
			this.trigger( name );
	};
} );

jQuery.fn.extend( {
	hover: function( fnOver, fnOut ) {
		return this.mouseenter( fnOver ).mouseleave( fnOut || fnOver );
	}
} );


var location = window.location;

var nonce = jQuery.now();

var rquery = ( /\?/ );



var rvalidtokens = /(,)|(\[|{)|(}|])|"(?:[^"\\\r\n]|\\["\\\/bfnrt]|\\u[\da-fA-F]{4})*"\s*:?|true|false|null|-?(?!0\d)\d+(?:\.\d+|)(?:[eE][+-]?\d+|)/g;

jQuery.parseJSON = function( data ) {

	// Attempt to parse using the native JSON parser first
	if ( window.JSON && window.JSON.parse ) {

		// Support: Android 2.3
		// Workaround failure to string-cast null input
		return window.JSON.parse( data + "" );
	}

	var requireNonComma,
		depth = null,
		str = jQuery.trim( data + "" );

	// Guard against invalid (and possibly dangerous) input by ensuring that nothing remains
	// after removing valid tokens
	return str && !jQuery.trim( str.replace( rvalidtokens, function( token, comma, open, close ) {

		// Force termination if we see a misplaced comma
		if ( requireNonComma && comma ) {
			depth = 0;
		}

		// Perform no more replacements after returning to outermost depth
		if ( depth === 0 ) {
			return token;
		}

		// Commas must not follow "[", "{", or ","
		requireNonComma = open || comma;

		// Determine new depth
		// array/object open ("[" or "{"): depth += true - false (increment)
		// array/object close ("]" or "}"): depth += false - true (decrement)
		// other cases ("," or primitive): depth += true - true (numeric cast)
		depth += !close - !open;

		// Remove this token
		return "";
	} ) ) ?
		( Function( "return " + str ) )() :
		jQuery.error( "Invalid JSON: " + data );
};


// Cross-browser xml parsing
jQuery.parseXML = function( data ) {
	var xml, tmp;
	if ( !data || typeof data !== "string" ) {
		return null;
	}
	try {
		if ( window.DOMParser ) { // Standard
			tmp = new window.DOMParser();
			xml = tmp.parseFromString( data, "text/xml" );
		} else { // IE
			xml = new window.ActiveXObject( "Microsoft.XMLDOM" );
			xml.async = "false";
			xml.loadXML( data );
		}
	} catch ( e ) {
		xml = undefined;
	}
	if ( !xml || !xml.documentElement || xml.getElementsByTagName( "parsererror" ).length ) {
		jQuery.error( "Invalid XML: " + data );
	}
	return xml;
};


var
	rhash = /#.*$/,
	rts = /([?&])_=[^&]*/,

	// IE leaves an \r character at EOL
	rheaders = /^(.*?):[ \t]*([^\r\n]*)\r?$/mg,

	// #7653, #8125, #8152: local protocol detection
	rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
	rnoContent = /^(?:GET|HEAD)$/,
	rprotocol = /^\/\//,
	rurl = /^([\w.+-]+:)(?:\/\/(?:[^\/?#]*@|)([^\/?#:]*)(?::(\d+)|)|)/,

	/* Prefilters
	 * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
	 * 2) These are called:
	 *    - BEFORE asking for a transport
	 *    - AFTER param serialization (s.data is a string if s.processData is true)
	 * 3) key is the dataType
	 * 4) the catchall symbol "*" can be used
	 * 5) execution will start with transport dataType and THEN continue down to "*" if needed
	 */
	prefilters = {},

	/* Transports bindings
	 * 1) key is the dataType
	 * 2) the catchall symbol "*" can be used
	 * 3) selection will start with transport dataType and THEN go to "*" if needed
	 */
	transports = {},

	// Avoid comment-prolog char sequence (#10098); must appease lint and evade compression
	allTypes = "*/".concat( "*" ),

	// Document location
	ajaxLocation = location.href,

	// Segment location into parts
	ajaxLocParts = rurl.exec( ajaxLocation.toLowerCase() ) || [];

// Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
function addToPrefiltersOrTransports( structure ) {

	// dataTypeExpression is optional and defaults to "*"
	return function( dataTypeExpression, func ) {

		if ( typeof dataTypeExpression !== "string" ) {
			func = dataTypeExpression;
			dataTypeExpression = "*";
		}

		var dataType,
			i = 0,
			dataTypes = dataTypeExpression.toLowerCase().match( rnotwhite ) || [];

		if ( jQuery.isFunction( func ) ) {

			// For each dataType in the dataTypeExpression
			while ( ( dataType = dataTypes[ i++ ] ) ) {

				// Prepend if requested
				if ( dataType.charAt( 0 ) === "+" ) {
					dataType = dataType.slice( 1 ) || "*";
					( structure[ dataType ] = structure[ dataType ] || [] ).unshift( func );

				// Otherwise append
				} else {
					( structure[ dataType ] = structure[ dataType ] || [] ).push( func );
				}
			}
		}
	};
}

// Base inspection function for prefilters and transports
function inspectPrefiltersOrTransports( structure, options, originalOptions, jqXHR ) {

	var inspected = {},
		seekingTransport = ( structure === transports );

	function inspect( dataType ) {
		var selected;
		inspected[ dataType ] = true;
		jQuery.each( structure[ dataType ] || [], function( _, prefilterOrFactory ) {
			var dataTypeOrTransport = prefilterOrFactory( options, originalOptions, jqXHR );
			if ( typeof dataTypeOrTransport === "string" &&
				!seekingTransport && !inspected[ dataTypeOrTransport ] ) {

				options.dataTypes.unshift( dataTypeOrTransport );
				inspect( dataTypeOrTransport );
				return false;
			} else if ( seekingTransport ) {
				return !( selected = dataTypeOrTransport );
			}
		} );
		return selected;
	}

	return inspect( options.dataTypes[ 0 ] ) || !inspected[ "*" ] && inspect( "*" );
}

// A special extend for ajax options
// that takes "flat" options (not to be deep extended)
// Fixes #9887
function ajaxExtend( target, src ) {
	var deep, key,
		flatOptions = jQuery.ajaxSettings.flatOptions || {};

	for ( key in src ) {
		if ( src[ key ] !== undefined ) {
			( flatOptions[ key ] ? target : ( deep || ( deep = {} ) ) )[ key ] = src[ key ];
		}
	}
	if ( deep ) {
		jQuery.extend( true, target, deep );
	}

	return target;
}

/* Handles responses to an ajax request:
 * - finds the right dataType (mediates between content-type and expected dataType)
 * - returns the corresponding response
 */
function ajaxHandleResponses( s, jqXHR, responses ) {
	var firstDataType, ct, finalDataType, type,
		contents = s.contents,
		dataTypes = s.dataTypes;

	// Remove auto dataType and get content-type in the process
	while ( dataTypes[ 0 ] === "*" ) {
		dataTypes.shift();
		if ( ct === undefined ) {
			ct = s.mimeType || jqXHR.getResponseHeader( "Content-Type" );
		}
	}

	// Check if we're dealing with a known content-type
	if ( ct ) {
		for ( type in contents ) {
			if ( contents[ type ] && contents[ type ].test( ct ) ) {
				dataTypes.unshift( type );
				break;
			}
		}
	}

	// Check to see if we have a response for the expected dataType
	if ( dataTypes[ 0 ] in responses ) {
		finalDataType = dataTypes[ 0 ];
	} else {

		// Try convertible dataTypes
		for ( type in responses ) {
			if ( !dataTypes[ 0 ] || s.converters[ type + " " + dataTypes[ 0 ] ] ) {
				finalDataType = type;
				break;
			}
			if ( !firstDataType ) {
				firstDataType = type;
			}
		}

		// Or just use first one
		finalDataType = finalDataType || firstDataType;
	}

	// If we found a dataType
	// We add the dataType to the list if needed
	// and return the corresponding response
	if ( finalDataType ) {
		if ( finalDataType !== dataTypes[ 0 ] ) {
			dataTypes.unshift( finalDataType );
		}
		return responses[ finalDataType ];
	}
}

/* Chain conversions given the request and the original response
 * Also sets the responseXXX fields on the jqXHR instance
 */
function ajaxConvert( s, response, jqXHR, isSuccess ) {
	var conv2, current, conv, tmp, prev,
		converters = {},

		// Work with a copy of dataTypes in case we need to modify it for conversion
		dataTypes = s.dataTypes.slice();

	// Create converters map with lowercased keys
	if ( dataTypes[ 1 ] ) {
		for ( conv in s.converters ) {
			converters[ conv.toLowerCase() ] = s.converters[ conv ];
		}
	}

	current = dataTypes.shift();

	// Convert to each sequential dataType
	while ( current ) {

		if ( s.responseFields[ current ] ) {
			jqXHR[ s.responseFields[ current ] ] = response;
		}

		// Apply the dataFilter if provided
		if ( !prev && isSuccess && s.dataFilter ) {
			response = s.dataFilter( response, s.dataType );
		}

		prev = current;
		current = dataTypes.shift();

		if ( current ) {

			// There's only work to do if current dataType is non-auto
			if ( current === "*" ) {

				current = prev;

			// Convert response if prev dataType is non-auto and differs from current
			} else if ( prev !== "*" && prev !== current ) {

				// Seek a direct converter
				conv = converters[ prev + " " + current ] || converters[ "* " + current ];

				// If none found, seek a pair
				if ( !conv ) {
					for ( conv2 in converters ) {

						// If conv2 outputs current
						tmp = conv2.split( " " );
						if ( tmp[ 1 ] === current ) {

							// If prev can be converted to accepted input
							conv = converters[ prev + " " + tmp[ 0 ] ] ||
								converters[ "* " + tmp[ 0 ] ];
							if ( conv ) {

								// Condense equivalence converters
								if ( conv === true ) {
									conv = converters[ conv2 ];

								// Otherwise, insert the intermediate dataType
								} else if ( converters[ conv2 ] !== true ) {
									current = tmp[ 0 ];
									dataTypes.unshift( tmp[ 1 ] );
								}
								break;
							}
						}
					}
				}

				// Apply converter (if not an equivalence)
				if ( conv !== true ) {

					// Unless errors are allowed to bubble, catch and return them
					if ( conv && s[ "throws" ] ) { // jscs:ignore requireDotNotation
						response = conv( response );
					} else {
						try {
							response = conv( response );
						} catch ( e ) {
							return {
								state: "parsererror",
								error: conv ? e : "No conversion from " + prev + " to " + current
							};
						}
					}
				}
			}
		}
	}

	return { state: "success", data: response };
}

jQuery.extend( {

	// Counter for holding the number of active queries
	active: 0,

	// Last-Modified header cache for next request
	lastModified: {},
	etag: {},

	ajaxSettings: {
		url: ajaxLocation,
		type: "GET",
		isLocal: rlocalProtocol.test( ajaxLocParts[ 1 ] ),
		global: true,
		processData: true,
		async: true,
		contentType: "application/x-www-form-urlencoded; charset=UTF-8",
		/*
		timeout: 0,
		data: null,
		dataType: null,
		username: null,
		password: null,
		cache: null,
		throws: false,
		traditional: false,
		headers: {},
		*/

		accepts: {
			"*": allTypes,
			text: "text/plain",
			html: "text/html",
			xml: "application/xml, text/xml",
			json: "application/json, text/javascript"
		},

		contents: {
			xml: /\bxml\b/,
			html: /\bhtml/,
			json: /\bjson\b/
		},

		responseFields: {
			xml: "responseXML",
			text: "responseText",
			json: "responseJSON"
		},

		// Data converters
		// Keys separate source (or catchall "*") and destination types with a single space
		converters: {

			// Convert anything to text
			"* text": String,

			// Text to html (true = no transformation)
			"text html": true,

			// Evaluate text as a json expression
			"text json": jQuery.parseJSON,

			// Parse text as xml
			"text xml": jQuery.parseXML
		},

		// For options that shouldn't be deep extended:
		// you can add your own custom options here if
		// and when you create one that shouldn't be
		// deep extended (see ajaxExtend)
		flatOptions: {
			url: true,
			context: true
		}
	},

	// Creates a full fledged settings object into target
	// with both ajaxSettings and settings fields.
	// If target is omitted, writes into ajaxSettings.
	ajaxSetup: function( target, settings ) {
		return settings ?

			// Building a settings object
			ajaxExtend( ajaxExtend( target, jQuery.ajaxSettings ), settings ) :

			// Extending ajaxSettings
			ajaxExtend( jQuery.ajaxSettings, target );
	},

	ajaxPrefilter: addToPrefiltersOrTransports( prefilters ),
	ajaxTransport: addToPrefiltersOrTransports( transports ),

	// Main method
	ajax: function( url, options ) {

		// If url is an object, simulate pre-1.5 signature
		if ( typeof url === "object" ) {
			options = url;
			url = undefined;
		}

		// Force options to be an object
		options = options || {};

		var

			// Cross-domain detection vars
			parts,

			// Loop variable
			i,

			// URL without anti-cache param
			cacheURL,

			// Response headers as string
			responseHeadersString,

			// timeout handle
			timeoutTimer,

			// To know if global events are to be dispatched
			fireGlobals,

			transport,

			// Response headers
			responseHeaders,

			// Create the final options object
			s = jQuery.ajaxSetup( {}, options ),

			// Callbacks context
			callbackContext = s.context || s,

			// Context for global events is callbackContext if it is a DOM node or jQuery collection
			globalEventContext = s.context &&
				( callbackContext.nodeType || callbackContext.jquery ) ?
					jQuery( callbackContext ) :
					jQuery.event,

			// Deferreds
			deferred = jQuery.Deferred(),
			completeDeferred = jQuery.Callbacks( "once memory" ),

			// Status-dependent callbacks
			statusCode = s.statusCode || {},

			// Headers (they are sent all at once)
			requestHeaders = {},
			requestHeadersNames = {},

			// The jqXHR state
			state = 0,

			// Default abort message
			strAbort = "canceled",

			// Fake xhr
			jqXHR = {
				readyState: 0,

				// Builds headers hashtable if needed
				getResponseHeader: function( key ) {
					var match;
					if ( state === 2 ) {
						if ( !responseHeaders ) {
							responseHeaders = {};
							while ( ( match = rheaders.exec( responseHeadersString ) ) ) {
								responseHeaders[ match[ 1 ].toLowerCase() ] = match[ 2 ];
							}
						}
						match = responseHeaders[ key.toLowerCase() ];
					}
					return match == null ? null : match;
				},

				// Raw string
				getAllResponseHeaders: function() {
					return state === 2 ? responseHeadersString : null;
				},

				// Caches the header
				setRequestHeader: function( name, value ) {
					var lname = name.toLowerCase();
					if ( !state ) {
						name = requestHeadersNames[ lname ] = requestHeadersNames[ lname ] || name;
						requestHeaders[ name ] = value;
					}
					return this;
				},

				// Overrides response content-type header
				overrideMimeType: function( type ) {
					if ( !state ) {
						s.mimeType = type;
					}
					return this;
				},

				// Status-dependent callbacks
				statusCode: function( map ) {
					var code;
					if ( map ) {
						if ( state < 2 ) {
							for ( code in map ) {

								// Lazy-add the new callback in a way that preserves old ones
								statusCode[ code ] = [ statusCode[ code ], map[ code ] ];
							}
						} else {

							// Execute the appropriate callbacks
							jqXHR.always( map[ jqXHR.status ] );
						}
					}
					return this;
				},

				// Cancel the request
				abort: function( statusText ) {
					var finalText = statusText || strAbort;
					if ( transport ) {
						transport.abort( finalText );
					}
					done( 0, finalText );
					return this;
				}
			};

		// Attach deferreds
		deferred.promise( jqXHR ).complete = completeDeferred.add;
		jqXHR.success = jqXHR.done;
		jqXHR.error = jqXHR.fail;

		// Remove hash character (#7531: and string promotion)
		// Add protocol if not provided (#5866: IE7 issue with protocol-less urls)
		// Handle falsy url in the settings object (#10093: consistency with old signature)
		// We also use the url parameter if available
		s.url = ( ( url || s.url || ajaxLocation ) + "" )
			.replace( rhash, "" )
			.replace( rprotocol, ajaxLocParts[ 1 ] + "//" );

		// Alias method option to type as per ticket #12004
		s.type = options.method || options.type || s.method || s.type;

		// Extract dataTypes list
		s.dataTypes = jQuery.trim( s.dataType || "*" ).toLowerCase().match( rnotwhite ) || [ "" ];

		// A cross-domain request is in order when we have a protocol:host:port mismatch
		if ( s.crossDomain == null ) {
			parts = rurl.exec( s.url.toLowerCase() );
			s.crossDomain = !!( parts &&
				( parts[ 1 ] !== ajaxLocParts[ 1 ] || parts[ 2 ] !== ajaxLocParts[ 2 ] ||
					( parts[ 3 ] || ( parts[ 1 ] === "http:" ? "80" : "443" ) ) !==
						( ajaxLocParts[ 3 ] || ( ajaxLocParts[ 1 ] === "http:" ? "80" : "443" ) ) )
			);
		}

		// Convert data if not already a string
		if ( s.data && s.processData && typeof s.data !== "string" ) {
			s.data = jQuery.param( s.data, s.traditional );
		}

		// Apply prefilters
		inspectPrefiltersOrTransports( prefilters, s, options, jqXHR );

		// If request was aborted inside a prefilter, stop there
		if ( state === 2 ) {
			return jqXHR;
		}

		// We can fire global events as of now if asked to
		// Don't fire events if jQuery.event is undefined in an AMD-usage scenario (#15118)
		fireGlobals = jQuery.event && s.global;

		// Watch for a new set of requests
		if ( fireGlobals && jQuery.active++ === 0 ) {
			jQuery.event.trigger( "ajaxStart" );
		}

		// Uppercase the type
		s.type = s.type.toUpperCase();

		// Determine if request has content
		s.hasContent = !rnoContent.test( s.type );

		// Save the URL in case we're toying with the If-Modified-Since
		// and/or If-None-Match header later on
		cacheURL = s.url;

		// More options handling for requests with no content
		if ( !s.hasContent ) {

			// If data is available, append data to url
			if ( s.data ) {
				cacheURL = ( s.url += ( rquery.test( cacheURL ) ? "&" : "?" ) + s.data );

				// #9682: remove data so that it's not used in an eventual retry
				delete s.data;
			}

			// Add anti-cache in url if needed
			if ( s.cache === false ) {
				s.url = rts.test( cacheURL ) ?

					// If there is already a '_' parameter, set its value
					cacheURL.replace( rts, "$1_=" + nonce++ ) :

					// Otherwise add one to the end
					cacheURL + ( rquery.test( cacheURL ) ? "&" : "?" ) + "_=" + nonce++;
			}
		}

		// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
		if ( s.ifModified ) {
			if ( jQuery.lastModified[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-Modified-Since", jQuery.lastModified[ cacheURL ] );
			}
			if ( jQuery.etag[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-None-Match", jQuery.etag[ cacheURL ] );
			}
		}

		// Set the correct header, if data is being sent
		if ( s.data && s.hasContent && s.contentType !== false || options.contentType ) {
			jqXHR.setRequestHeader( "Content-Type", s.contentType );
		}

		// Set the Accepts header for the server, depending on the dataType
		jqXHR.setRequestHeader(
			"Accept",
			s.dataTypes[ 0 ] && s.accepts[ s.dataTypes[ 0 ] ] ?
				s.accepts[ s.dataTypes[ 0 ] ] +
					( s.dataTypes[ 0 ] !== "*" ? ", " + allTypes + "; q=0.01" : "" ) :
				s.accepts[ "*" ]
		);

		// Check for headers option
		for ( i in s.headers ) {
			jqXHR.setRequestHeader( i, s.headers[ i ] );
		}

		// Allow custom headers/mimetypes and early abort
		if ( s.beforeSend &&
			( s.beforeSend.call( callbackContext, jqXHR, s ) === false || state === 2 ) ) {

			// Abort if not done already and return
			return jqXHR.abort();
		}

		// aborting is no longer a cancellation
		strAbort = "abort";

		// Install callbacks on deferreds
		for ( i in { success: 1, error: 1, complete: 1 } ) {
			jqXHR[ i ]( s[ i ] );
		}

		// Get transport
		transport = inspectPrefiltersOrTransports( transports, s, options, jqXHR );

		// If no transport, we auto-abort
		if ( !transport ) {
			done( -1, "No Transport" );
		} else {
			jqXHR.readyState = 1;

			// Send global event
			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxSend", [ jqXHR, s ] );
			}

			// If request was aborted inside ajaxSend, stop there
			if ( state === 2 ) {
				return jqXHR;
			}

			// Timeout
			if ( s.async && s.timeout > 0 ) {
				timeoutTimer = window.setTimeout( function() {
					jqXHR.abort( "timeout" );
				}, s.timeout );
			}

			try {
				state = 1;
				transport.send( requestHeaders, done );
			} catch ( e ) {

				// Propagate exception as error if not done
				if ( state < 2 ) {
					done( -1, e );

				// Simply rethrow otherwise
				} else {
					throw e;
				}
			}
		}

		// Callback for when everything is done
		function done( status, nativeStatusText, responses, headers ) {
			var isSuccess, success, error, response, modified,
				statusText = nativeStatusText;

			// Called once
			if ( state === 2 ) {
				return;
			}

			// State is "done" now
			state = 2;

			// Clear timeout if it exists
			if ( timeoutTimer ) {
				window.clearTimeout( timeoutTimer );
			}

			// Dereference transport for early garbage collection
			// (no matter how long the jqXHR object will be used)
			transport = undefined;

			// Cache response headers
			responseHeadersString = headers || "";

			// Set readyState
			jqXHR.readyState = status > 0 ? 4 : 0;

			// Determine if successful
			isSuccess = status >= 200 && status < 300 || status === 304;

			// Get response data
			if ( responses ) {
				response = ajaxHandleResponses( s, jqXHR, responses );
			}

			// Convert no matter what (that way responseXXX fields are always set)
			response = ajaxConvert( s, response, jqXHR, isSuccess );

			// If successful, handle type chaining
			if ( isSuccess ) {

				// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
				if ( s.ifModified ) {
					modified = jqXHR.getResponseHeader( "Last-Modified" );
					if ( modified ) {
						jQuery.lastModified[ cacheURL ] = modified;
					}
					modified = jqXHR.getResponseHeader( "etag" );
					if ( modified ) {
						jQuery.etag[ cacheURL ] = modified;
					}
				}

				// if no content
				if ( status === 204 || s.type === "HEAD" ) {
					statusText = "nocontent";

				// if not modified
				} else if ( status === 304 ) {
					statusText = "notmodified";

				// If we have data, let's convert it
				} else {
					statusText = response.state;
					success = response.data;
					error = response.error;
					isSuccess = !error;
				}
			} else {

				// We extract error from statusText
				// then normalize statusText and status for non-aborts
				error = statusText;
				if ( status || !statusText ) {
					statusText = "error";
					if ( status < 0 ) {
						status = 0;
					}
				}
			}

			// Set data for the fake xhr object
			jqXHR.status = status;
			jqXHR.statusText = ( nativeStatusText || statusText ) + "";

			// Success/Error
			if ( isSuccess ) {
				deferred.resolveWith( callbackContext, [ success, statusText, jqXHR ] );
			} else {
				deferred.rejectWith( callbackContext, [ jqXHR, statusText, error ] );
			}

			// Status-dependent callbacks
			jqXHR.statusCode( statusCode );
			statusCode = undefined;

			if ( fireGlobals ) {
				globalEventContext.trigger( isSuccess ? "ajaxSuccess" : "ajaxError",
					[ jqXHR, s, isSuccess ? success : error ] );
			}

			// Complete
			completeDeferred.fireWith( callbackContext, [ jqXHR, statusText ] );

			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxComplete", [ jqXHR, s ] );

				// Handle the global AJAX counter
				if ( !( --jQuery.active ) ) {
					jQuery.event.trigger( "ajaxStop" );
				}
			}
		}

		return jqXHR;
	},

	getJSON: function( url, data, callback ) {
		return jQuery.get( url, data, callback, "json" );
	},

	getScript: function( url, callback ) {
		return jQuery.get( url, undefined, callback, "script" );
	}
} );

jQuery.each( [ "get", "post" ], function( i, method ) {
	jQuery[ method ] = function( url, data, callback, type ) {

		// shift arguments if data argument was omitted
		if ( jQuery.isFunction( data ) ) {
			type = type || callback;
			callback = data;
			data = undefined;
		}

		// The url can be an options object (which then must have .url)
		return jQuery.ajax( jQuery.extend( {
			url: url,
			type: method,
			dataType: type,
			data: data,
			success: callback
		}, jQuery.isPlainObject( url ) && url ) );
	};
} );


jQuery._evalUrl = function( url ) {
	return jQuery.ajax( {
		url: url,

		// Make this explicit, since user can override this through ajaxSetup (#11264)
		type: "GET",
		dataType: "script",
		cache: true,
		async: false,
		global: false,
		"throws": true
	} );
};


jQuery.fn.extend( {
	wrapAll: function( html ) {
		if ( jQuery.isFunction( html ) ) {
			return this.each( function( i ) {
				jQuery( this ).wrapAll( html.call( this, i ) );
			} );
		}

		if ( this[ 0 ] ) {

			// The elements to wrap the target around
			var wrap = jQuery( html, this[ 0 ].ownerDocument ).eq( 0 ).clone( true );

			if ( this[ 0 ].parentNode ) {
				wrap.insertBefore( this[ 0 ] );
			}

			wrap.map( function() {
				var elem = this;

				while ( elem.firstChild && elem.firstChild.nodeType === 1 ) {
					elem = elem.firstChild;
				}

				return elem;
			} ).append( this );
		}

		return this;
	},

	wrapInner: function( html ) {
		if ( jQuery.isFunction( html ) ) {
			return this.each( function( i ) {
				jQuery( this ).wrapInner( html.call( this, i ) );
			} );
		}

		return this.each( function() {
			var self = jQuery( this ),
				contents = self.contents();

			if ( contents.length ) {
				contents.wrapAll( html );

			} else {
				self.append( html );
			}
		} );
	},

	wrap: function( html ) {
		var isFunction = jQuery.isFunction( html );

		return this.each( function( i ) {
			jQuery( this ).wrapAll( isFunction ? html.call( this, i ) : html );
		} );
	},

	unwrap: function() {
		return this.parent().each( function() {
			if ( !jQuery.nodeName( this, "body" ) ) {
				jQuery( this ).replaceWith( this.childNodes );
			}
		} ).end();
	}
} );


function getDisplay( elem ) {
	return elem.style && elem.style.display || jQuery.css( elem, "display" );
}

function filterHidden( elem ) {

	// Disconnected elements are considered hidden
	if ( !jQuery.contains( elem.ownerDocument || document, elem ) ) {
		return true;
	}
	while ( elem && elem.nodeType === 1 ) {
		if ( getDisplay( elem ) === "none" || elem.type === "hidden" ) {
			return true;
		}
		elem = elem.parentNode;
	}
	return false;
}

jQuery.expr.filters.hidden = function( elem ) {

	// Support: Opera <= 12.12
	// Opera reports offsetWidths and offsetHeights less than zero on some elements
	return support.reliableHiddenOffsets() ?
		( elem.offsetWidth <= 0 && elem.offsetHeight <= 0 &&
			!elem.getClientRects().length ) :
			filterHidden( elem );
};

jQuery.expr.filters.visible = function( elem ) {
	return !jQuery.expr.filters.hidden( elem );
};




var r20 = /%20/g,
	rbracket = /\[\]$/,
	rCRLF = /\r?\n/g,
	rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
	rsubmittable = /^(?:input|select|textarea|keygen)/i;

function buildParams( prefix, obj, traditional, add ) {
	var name;

	if ( jQuery.isArray( obj ) ) {

		// Serialize array item.
		jQuery.each( obj, function( i, v ) {
			if ( traditional || rbracket.test( prefix ) ) {

				// Treat each array item as a scalar.
				add( prefix, v );

			} else {

				// Item is non-scalar (array or object), encode its numeric index.
				buildParams(
					prefix + "[" + ( typeof v === "object" && v != null ? i : "" ) + "]",
					v,
					traditional,
					add
				);
			}
		} );

	} else if ( !traditional && jQuery.type( obj ) === "object" ) {

		// Serialize object item.
		for ( name in obj ) {
			buildParams( prefix + "[" + name + "]", obj[ name ], traditional, add );
		}

	} else {

		// Serialize scalar item.
		add( prefix, obj );
	}
}

// Serialize an array of form elements or a set of
// key/values into a query string
jQuery.param = function( a, traditional ) {
	var prefix,
		s = [],
		add = function( key, value ) {

			// If value is a function, invoke it and return its value
			value = jQuery.isFunction( value ) ? value() : ( value == null ? "" : value );
			s[ s.length ] = encodeURIComponent( key ) + "=" + encodeURIComponent( value );
		};

	// Set traditional to true for jQuery <= 1.3.2 behavior.
	if ( traditional === undefined ) {
		traditional = jQuery.ajaxSettings && jQuery.ajaxSettings.traditional;
	}

	// If an array was passed in, assume that it is an array of form elements.
	if ( jQuery.isArray( a ) || ( a.jquery && !jQuery.isPlainObject( a ) ) ) {

		// Serialize the form elements
		jQuery.each( a, function() {
			add( this.name, this.value );
		} );

	} else {

		// If traditional, encode the "old" way (the way 1.3.2 or older
		// did it), otherwise encode params recursively.
		for ( prefix in a ) {
			buildParams( prefix, a[ prefix ], traditional, add );
		}
	}

	// Return the resulting serialization
	return s.join( "&" ).replace( r20, "+" );
};

jQuery.fn.extend( {
	serialize: function() {
		return jQuery.param( this.serializeArray() );
	},
	serializeArray: function() {
		return this.map( function() {

			// Can add propHook for "elements" to filter or add form elements
			var elements = jQuery.prop( this, "elements" );
			return elements ? jQuery.makeArray( elements ) : this;
		} )
		.filter( function() {
			var type = this.type;

			// Use .is(":disabled") so that fieldset[disabled] works
			return this.name && !jQuery( this ).is( ":disabled" ) &&
				rsubmittable.test( this.nodeName ) && !rsubmitterTypes.test( type ) &&
				( this.checked || !rcheckableType.test( type ) );
		} )
		.map( function( i, elem ) {
			var val = jQuery( this ).val();

			return val == null ?
				null :
				jQuery.isArray( val ) ?
					jQuery.map( val, function( val ) {
						return { name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
					} ) :
					{ name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
		} ).get();
	}
} );


// Create the request object
// (This is still attached to ajaxSettings for backward compatibility)
jQuery.ajaxSettings.xhr = window.ActiveXObject !== undefined ?

	// Support: IE6-IE8
	function() {

		// XHR cannot access local files, always use ActiveX for that case
		if ( this.isLocal ) {
			return createActiveXHR();
		}

		// Support: IE 9-11
		// IE seems to error on cross-domain PATCH requests when ActiveX XHR
		// is used. In IE 9+ always use the native XHR.
		// Note: this condition won't catch Edge as it doesn't define
		// document.documentMode but it also doesn't support ActiveX so it won't
		// reach this code.
		if ( document.documentMode > 8 ) {
			return createStandardXHR();
		}

		// Support: IE<9
		// oldIE XHR does not support non-RFC2616 methods (#13240)
		// See http://msdn.microsoft.com/en-us/library/ie/ms536648(v=vs.85).aspx
		// and http://www.w3.org/Protocols/rfc2616/rfc2616-sec9.html#sec9
		// Although this check for six methods instead of eight
		// since IE also does not support "trace" and "connect"
		return /^(get|post|head|put|delete|options)$/i.test( this.type ) &&
			createStandardXHR() || createActiveXHR();
	} :

	// For all other browsers, use the standard XMLHttpRequest object
	createStandardXHR;

var xhrId = 0,
	xhrCallbacks = {},
	xhrSupported = jQuery.ajaxSettings.xhr();

// Support: IE<10
// Open requests must be manually aborted on unload (#5280)
// See https://support.microsoft.com/kb/2856746 for more info
if ( window.attachEvent ) {
	window.attachEvent( "onunload", function() {
		for ( var key in xhrCallbacks ) {
			xhrCallbacks[ key ]( undefined, true );
		}
	} );
}

// Determine support properties
support.cors = !!xhrSupported && ( "withCredentials" in xhrSupported );
xhrSupported = support.ajax = !!xhrSupported;

// Create transport if the browser can provide an xhr
if ( xhrSupported ) {

	jQuery.ajaxTransport( function( options ) {

		// Cross domain only allowed if supported through XMLHttpRequest
		if ( !options.crossDomain || support.cors ) {

			var callback;

			return {
				send: function( headers, complete ) {
					var i,
						xhr = options.xhr(),
						id = ++xhrId;

					// Open the socket
					xhr.open(
						options.type,
						options.url,
						options.async,
						options.username,
						options.password
					);

					// Apply custom fields if provided
					if ( options.xhrFields ) {
						for ( i in options.xhrFields ) {
							xhr[ i ] = options.xhrFields[ i ];
						}
					}

					// Override mime type if needed
					if ( options.mimeType && xhr.overrideMimeType ) {
						xhr.overrideMimeType( options.mimeType );
					}

					// X-Requested-With header
					// For cross-domain requests, seeing as conditions for a preflight are
					// akin to a jigsaw puzzle, we simply never set it to be sure.
					// (it can always be set on a per-request basis or even using ajaxSetup)
					// For same-domain requests, won't change header if already provided.
					if ( !options.crossDomain && !headers[ "X-Requested-With" ] ) {
						headers[ "X-Requested-With" ] = "XMLHttpRequest";
					}

					// Set headers
					for ( i in headers ) {

						// Support: IE<9
						// IE's ActiveXObject throws a 'Type Mismatch' exception when setting
						// request header to a null-value.
						//
						// To keep consistent with other XHR implementations, cast the value
						// to string and ignore `undefined`.
						if ( headers[ i ] !== undefined ) {
							xhr.setRequestHeader( i, headers[ i ] + "" );
						}
					}

					// Do send the request
					// This may raise an exception which is actually
					// handled in jQuery.ajax (so no try/catch here)
					xhr.send( ( options.hasContent && options.data ) || null );

					// Listener
					callback = function( _, isAbort ) {
						var status, statusText, responses;

						// Was never called and is aborted or complete
						if ( callback && ( isAbort || xhr.readyState === 4 ) ) {

							// Clean up
							delete xhrCallbacks[ id ];
							callback = undefined;
							xhr.onreadystatechange = jQuery.noop;

							// Abort manually if needed
							if ( isAbort ) {
								if ( xhr.readyState !== 4 ) {
									xhr.abort();
								}
							} else {
								responses = {};
								status = xhr.status;

								// Support: IE<10
								// Accessing binary-data responseText throws an exception
								// (#11426)
								if ( typeof xhr.responseText === "string" ) {
									responses.text = xhr.responseText;
								}

								// Firefox throws an exception when accessing
								// statusText for faulty cross-domain requests
								try {
									statusText = xhr.statusText;
								} catch ( e ) {

									// We normalize with Webkit giving an empty statusText
									statusText = "";
								}

								// Filter status for non standard behaviors

								// If the request is local and we have data: assume a success
								// (success with no data won't get notified, that's the best we
								// can do given current implementations)
								if ( !status && options.isLocal && !options.crossDomain ) {
									status = responses.text ? 200 : 404;

								// IE - #1450: sometimes returns 1223 when it should be 204
								} else if ( status === 1223 ) {
									status = 204;
								}
							}
						}

						// Call complete if needed
						if ( responses ) {
							complete( status, statusText, responses, xhr.getAllResponseHeaders() );
						}
					};

					// Do send the request
					// `xhr.send` may raise an exception, but it will be
					// handled in jQuery.ajax (so no try/catch here)
					if ( !options.async ) {

						// If we're in sync mode we fire the callback
						callback();
					} else if ( xhr.readyState === 4 ) {

						// (IE6 & IE7) if it's in cache and has been
						// retrieved directly we need to fire the callback
						window.setTimeout( callback );
					} else {

						// Register the callback, but delay it in case `xhr.send` throws
						// Add to the list of active xhr callbacks
						xhr.onreadystatechange = xhrCallbacks[ id ] = callback;
					}
				},

				abort: function() {
					if ( callback ) {
						callback( undefined, true );
					}
				}
			};
		}
	} );
}

// Functions to create xhrs
function createStandardXHR() {
	try {
		return new window.XMLHttpRequest();
	} catch ( e ) {}
}

function createActiveXHR() {
	try {
		return new window.ActiveXObject( "Microsoft.XMLHTTP" );
	} catch ( e ) {}
}




// Install script dataType
jQuery.ajaxSetup( {
	accepts: {
		script: "text/javascript, application/javascript, " +
			"application/ecmascript, application/x-ecmascript"
	},
	contents: {
		script: /\b(?:java|ecma)script\b/
	},
	converters: {
		"text script": function( text ) {
			jQuery.globalEval( text );
			return text;
		}
	}
} );

// Handle cache's special case and global
jQuery.ajaxPrefilter( "script", function( s ) {
	if ( s.cache === undefined ) {
		s.cache = false;
	}
	if ( s.crossDomain ) {
		s.type = "GET";
		s.global = false;
	}
} );

// Bind script tag hack transport
jQuery.ajaxTransport( "script", function( s ) {

	// This transport only deals with cross domain requests
	if ( s.crossDomain ) {

		var script,
			head = document.head || jQuery( "head" )[ 0 ] || document.documentElement;

		return {

			send: function( _, callback ) {

				script = document.createElement( "script" );

				script.async = true;

				if ( s.scriptCharset ) {
					script.charset = s.scriptCharset;
				}

				script.src = s.url;

				// Attach handlers for all browsers
				script.onload = script.onreadystatechange = function( _, isAbort ) {

					if ( isAbort || !script.readyState || /loaded|complete/.test( script.readyState ) ) {

						// Handle memory leak in IE
						script.onload = script.onreadystatechange = null;

						// Remove the script
						if ( script.parentNode ) {
							script.parentNode.removeChild( script );
						}

						// Dereference the script
						script = null;

						// Callback if not abort
						if ( !isAbort ) {
							callback( 200, "success" );
						}
					}
				};

				// Circumvent IE6 bugs with base elements (#2709 and #4378) by prepending
				// Use native DOM manipulation to avoid our domManip AJAX trickery
				head.insertBefore( script, head.firstChild );
			},

			abort: function() {
				if ( script ) {
					script.onload( undefined, true );
				}
			}
		};
	}
} );




var oldCallbacks = [],
	rjsonp = /(=)\?(?=&|$)|\?\?/;

// Default jsonp settings
jQuery.ajaxSetup( {
	jsonp: "callback",
	jsonpCallback: function() {
		var callback = oldCallbacks.pop() || ( jQuery.expando + "_" + ( nonce++ ) );
		this[ callback ] = true;
		return callback;
	}
} );

// Detect, normalize options and install callbacks for jsonp requests
jQuery.ajaxPrefilter( "json jsonp", function( s, originalSettings, jqXHR ) {

	var callbackName, overwritten, responseContainer,
		jsonProp = s.jsonp !== false && ( rjsonp.test( s.url ) ?
			"url" :
			typeof s.data === "string" &&
				( s.contentType || "" )
					.indexOf( "application/x-www-form-urlencoded" ) === 0 &&
				rjsonp.test( s.data ) && "data"
		);

	// Handle iff the expected data type is "jsonp" or we have a parameter to set
	if ( jsonProp || s.dataTypes[ 0 ] === "jsonp" ) {

		// Get callback name, remembering preexisting value associated with it
		callbackName = s.jsonpCallback = jQuery.isFunction( s.jsonpCallback ) ?
			s.jsonpCallback() :
			s.jsonpCallback;

		// Insert callback into url or form data
		if ( jsonProp ) {
			s[ jsonProp ] = s[ jsonProp ].replace( rjsonp, "$1" + callbackName );
		} else if ( s.jsonp !== false ) {
			s.url += ( rquery.test( s.url ) ? "&" : "?" ) + s.jsonp + "=" + callbackName;
		}

		// Use data converter to retrieve json after script execution
		s.converters[ "script json" ] = function() {
			if ( !responseContainer ) {
				jQuery.error( callbackName + " was not called" );
			}
			return responseContainer[ 0 ];
		};

		// force json dataType
		s.dataTypes[ 0 ] = "json";

		// Install callback
		overwritten = window[ callbackName ];
		window[ callbackName ] = function() {
			responseContainer = arguments;
		};

		// Clean-up function (fires after converters)
		jqXHR.always( function() {

			// If previous value didn't exist - remove it
			if ( overwritten === undefined ) {
				jQuery( window ).removeProp( callbackName );

			// Otherwise restore preexisting value
			} else {
				window[ callbackName ] = overwritten;
			}

			// Save back as free
			if ( s[ callbackName ] ) {

				// make sure that re-using the options doesn't screw things around
				s.jsonpCallback = originalSettings.jsonpCallback;

				// save the callback name for future use
				oldCallbacks.push( callbackName );
			}

			// Call if it was a function and we have a response
			if ( responseContainer && jQuery.isFunction( overwritten ) ) {
				overwritten( responseContainer[ 0 ] );
			}

			responseContainer = overwritten = undefined;
		} );

		// Delegate to script
		return "script";
	}
} );




// data: string of html
// context (optional): If specified, the fragment will be created in this context,
// defaults to document
// keepScripts (optional): If true, will include scripts passed in the html string
jQuery.parseHTML = function( data, context, keepScripts ) {
	if ( !data || typeof data !== "string" ) {
		return null;
	}
	if ( typeof context === "boolean" ) {
		keepScripts = context;
		context = false;
	}
	context = context || document;

	var parsed = rsingleTag.exec( data ),
		scripts = !keepScripts && [];

	// Single tag
	if ( parsed ) {
		return [ context.createElement( parsed[ 1 ] ) ];
	}

	parsed = buildFragment( [ data ], context, scripts );

	if ( scripts && scripts.length ) {
		jQuery( scripts ).remove();
	}

	return jQuery.merge( [], parsed.childNodes );
};


// Keep a copy of the old load method
var _load = jQuery.fn.load;

/**
 * Load a url into a page
 */
jQuery.fn.load = function( url, params, callback ) {
	if ( typeof url !== "string" && _load ) {
		return _load.apply( this, arguments );
	}

	var selector, type, response,
		self = this,
		off = url.indexOf( " " );

	if ( off > -1 ) {
		selector = jQuery.trim( url.slice( off, url.length ) );
		url = url.slice( 0, off );
	}

	// If it's a function
	if ( jQuery.isFunction( params ) ) {

		// We assume that it's the callback
		callback = params;
		params = undefined;

	// Otherwise, build a param string
	} else if ( params && typeof params === "object" ) {
		type = "POST";
	}

	// If we have elements to modify, make the request
	if ( self.length > 0 ) {
		jQuery.ajax( {
			url: url,

			// If "type" variable is undefined, then "GET" method will be used.
			// Make value of this field explicit since
			// user can override it through ajaxSetup method
			type: type || "GET",
			dataType: "html",
			data: params
		} ).done( function( responseText ) {

			// Save response for use in complete callback
			response = arguments;

			self.html( selector ?

				// If a selector was specified, locate the right elements in a dummy div
				// Exclude scripts to avoid IE 'Permission Denied' errors
				jQuery( "<div>" ).append( jQuery.parseHTML( responseText ) ).find( selector ) :

				// Otherwise use the full result
				responseText );

		// If the request succeeds, this function gets "data", "status", "jqXHR"
		// but they are ignored because response was set above.
		// If it fails, this function gets "jqXHR", "status", "error"
		} ).always( callback && function( jqXHR, status ) {
			self.each( function() {
				callback.apply( this, response || [ jqXHR.responseText, status, jqXHR ] );
			} );
		} );
	}

	return this;
};




// Attach a bunch of functions for handling common AJAX events
jQuery.each( [
	"ajaxStart",
	"ajaxStop",
	"ajaxComplete",
	"ajaxError",
	"ajaxSuccess",
	"ajaxSend"
], function( i, type ) {
	jQuery.fn[ type ] = function( fn ) {
		return this.on( type, fn );
	};
} );




jQuery.expr.filters.animated = function( elem ) {
	return jQuery.grep( jQuery.timers, function( fn ) {
		return elem === fn.elem;
	} ).length;
};





/**
 * Gets a window from an element
 */
function getWindow( elem ) {
	return jQuery.isWindow( elem ) ?
		elem :
		elem.nodeType === 9 ?
			elem.defaultView || elem.parentWindow :
			false;
}

jQuery.offset = {
	setOffset: function( elem, options, i ) {
		var curPosition, curLeft, curCSSTop, curTop, curOffset, curCSSLeft, calculatePosition,
			position = jQuery.css( elem, "position" ),
			curElem = jQuery( elem ),
			props = {};

		// set position first, in-case top/left are set even on static elem
		if ( position === "static" ) {
			elem.style.position = "relative";
		}

		curOffset = curElem.offset();
		curCSSTop = jQuery.css( elem, "top" );
		curCSSLeft = jQuery.css( elem, "left" );
		calculatePosition = ( position === "absolute" || position === "fixed" ) &&
			jQuery.inArray( "auto", [ curCSSTop, curCSSLeft ] ) > -1;

		// need to be able to calculate position if either top or left
		// is auto and position is either absolute or fixed
		if ( calculatePosition ) {
			curPosition = curElem.position();
			curTop = curPosition.top;
			curLeft = curPosition.left;
		} else {
			curTop = parseFloat( curCSSTop ) || 0;
			curLeft = parseFloat( curCSSLeft ) || 0;
		}

		if ( jQuery.isFunction( options ) ) {

			// Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
			options = options.call( elem, i, jQuery.extend( {}, curOffset ) );
		}

		if ( options.top != null ) {
			props.top = ( options.top - curOffset.top ) + curTop;
		}
		if ( options.left != null ) {
			props.left = ( options.left - curOffset.left ) + curLeft;
		}

		if ( "using" in options ) {
			options.using.call( elem, props );
		} else {
			curElem.css( props );
		}
	}
};

jQuery.fn.extend( {
	offset: function( options ) {
		if ( arguments.length ) {
			return options === undefined ?
				this :
				this.each( function( i ) {
					jQuery.offset.setOffset( this, options, i );
				} );
		}

		var docElem, win,
			box = { top: 0, left: 0 },
			elem = this[ 0 ],
			doc = elem && elem.ownerDocument;

		if ( !doc ) {
			return;
		}

		docElem = doc.documentElement;

		// Make sure it's not a disconnected DOM node
		if ( !jQuery.contains( docElem, elem ) ) {
			return box;
		}

		// If we don't have gBCR, just use 0,0 rather than error
		// BlackBerry 5, iOS 3 (original iPhone)
		if ( typeof elem.getBoundingClientRect !== "undefined" ) {
			box = elem.getBoundingClientRect();
		}
		win = getWindow( doc );
		return {
			top: box.top  + ( win.pageYOffset || docElem.scrollTop )  - ( docElem.clientTop  || 0 ),
			left: box.left + ( win.pageXOffset || docElem.scrollLeft ) - ( docElem.clientLeft || 0 )
		};
	},

	position: function() {
		if ( !this[ 0 ] ) {
			return;
		}

		var offsetParent, offset,
			parentOffset = { top: 0, left: 0 },
			elem = this[ 0 ];

		// Fixed elements are offset from window (parentOffset = {top:0, left: 0},
		// because it is its only offset parent
		if ( jQuery.css( elem, "position" ) === "fixed" ) {

			// we assume that getBoundingClientRect is available when computed position is fixed
			offset = elem.getBoundingClientRect();
		} else {

			// Get *real* offsetParent
			offsetParent = this.offsetParent();

			// Get correct offsets
			offset = this.offset();
			if ( !jQuery.nodeName( offsetParent[ 0 ], "html" ) ) {
				parentOffset = offsetParent.offset();
			}

			// Add offsetParent borders
			parentOffset.top  += jQuery.css( offsetParent[ 0 ], "borderTopWidth", true );
			parentOffset.left += jQuery.css( offsetParent[ 0 ], "borderLeftWidth", true );
		}

		// Subtract parent offsets and element margins
		// note: when an element has margin: auto the offsetLeft and marginLeft
		// are the same in Safari causing offset.left to incorrectly be 0
		return {
			top:  offset.top  - parentOffset.top - jQuery.css( elem, "marginTop", true ),
			left: offset.left - parentOffset.left - jQuery.css( elem, "marginLeft", true )
		};
	},

	offsetParent: function() {
		return this.map( function() {
			var offsetParent = this.offsetParent;

			while ( offsetParent && ( !jQuery.nodeName( offsetParent, "html" ) &&
				jQuery.css( offsetParent, "position" ) === "static" ) ) {
				offsetParent = offsetParent.offsetParent;
			}
			return offsetParent || documentElement;
		} );
	}
} );

// Create scrollLeft and scrollTop methods
jQuery.each( { scrollLeft: "pageXOffset", scrollTop: "pageYOffset" }, function( method, prop ) {
	var top = /Y/.test( prop );

	jQuery.fn[ method ] = function( val ) {
		return access( this, function( elem, method, val ) {
			var win = getWindow( elem );

			if ( val === undefined ) {
				return win ? ( prop in win ) ? win[ prop ] :
					win.document.documentElement[ method ] :
					elem[ method ];
			}

			if ( win ) {
				win.scrollTo(
					!top ? val : jQuery( win ).scrollLeft(),
					top ? val : jQuery( win ).scrollTop()
				);

			} else {
				elem[ method ] = val;
			}
		}, method, val, arguments.length, null );
	};
} );

// Support: Safari<7-8+, Chrome<37-44+
// Add the top/left cssHooks using jQuery.fn.position
// Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
// getComputedStyle returns percent when specified for top/left/bottom/right
// rather than make the css module depend on the offset module, we just check for it here
jQuery.each( [ "top", "left" ], function( i, prop ) {
	jQuery.cssHooks[ prop ] = addGetHookIf( support.pixelPosition,
		function( elem, computed ) {
			if ( computed ) {
				computed = curCSS( elem, prop );

				// if curCSS returns percentage, fallback to offset
				return rnumnonpx.test( computed ) ?
					jQuery( elem ).position()[ prop ] + "px" :
					computed;
			}
		}
	);
} );


// Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
jQuery.each( { Height: "height", Width: "width" }, function( name, type ) {
	jQuery.each( { padding: "inner" + name, content: type, "": "outer" + name },
	function( defaultExtra, funcName ) {

		// margin is only for outerHeight, outerWidth
		jQuery.fn[ funcName ] = function( margin, value ) {
			var chainable = arguments.length && ( defaultExtra || typeof margin !== "boolean" ),
				extra = defaultExtra || ( margin === true || value === true ? "margin" : "border" );

			return access( this, function( elem, type, value ) {
				var doc;

				if ( jQuery.isWindow( elem ) ) {

					// As of 5/8/2012 this will yield incorrect results for Mobile Safari, but there
					// isn't a whole lot we can do. See pull request at this URL for discussion:
					// https://github.com/jquery/jquery/pull/764
					return elem.document.documentElement[ "client" + name ];
				}

				// Get document width or height
				if ( elem.nodeType === 9 ) {
					doc = elem.documentElement;

					// Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
					// whichever is greatest
					// unfortunately, this causes bug #3838 in IE6/8 only,
					// but there is currently no good, small way to fix it.
					return Math.max(
						elem.body[ "scroll" + name ], doc[ "scroll" + name ],
						elem.body[ "offset" + name ], doc[ "offset" + name ],
						doc[ "client" + name ]
					);
				}

				return value === undefined ?

					// Get width or height on the element, requesting but not forcing parseFloat
					jQuery.css( elem, type, extra ) :

					// Set width or height on the element
					jQuery.style( elem, type, value, extra );
			}, type, chainable ? margin : undefined, chainable, null );
		};
	} );
} );


jQuery.fn.extend( {

	bind: function( types, data, fn ) {
		return this.on( types, null, data, fn );
	},
	unbind: function( types, fn ) {
		return this.off( types, null, fn );
	},

	delegate: function( selector, types, data, fn ) {
		return this.on( types, selector, data, fn );
	},
	undelegate: function( selector, types, fn ) {

		// ( namespace ) or ( selector, types [, fn] )
		return arguments.length === 1 ?
			this.off( selector, "**" ) :
			this.off( types, selector || "**", fn );
	}
} );

// The number of elements contained in the matched element set
jQuery.fn.size = function() {
	return this.length;
};

jQuery.fn.andSelf = jQuery.fn.addBack;




// Register as a named AMD module, since jQuery can be concatenated with other
// files that may use define, but not via a proper concatenation script that
// understands anonymous AMD modules. A named AMD is safest and most robust
// way to register. Lowercase jquery is used because AMD module names are
// derived from file names, and jQuery is normally delivered in a lowercase
// file name. Do this after creating the global so that if an AMD module wants
// to call noConflict to hide this version of jQuery, it will work.

// Note that for maximum portability, libraries that are not jQuery should
// declare themselves as anonymous modules, and avoid setting a global if an
// AMD loader is present. jQuery is a special case. For more information, see
// https://github.com/jrburke/requirejs/wiki/Updating-existing-libraries#wiki-anon

if ( typeof define === "function" && define.amd ) {
	define( "jquery", [], function() {
		return jQuery;
	} );
}



var

	// Map over jQuery in case of overwrite
	_jQuery = window.jQuery,

	// Map over the $ in case of overwrite
	_$ = window.$;

jQuery.noConflict = function( deep ) {
	if ( window.$ === jQuery ) {
		window.$ = _$;
	}

	if ( deep && window.jQuery === jQuery ) {
		window.jQuery = _jQuery;
	}

	return jQuery;
};

// Expose jQuery and $ identifiers, even in
// AMD (#7102#comment:10, https://github.com/jquery/jquery/pull/557)
// and CommonJS for browser emulators (#13566)
if ( !noGlobal ) {
	window.jQuery = window.$ = jQuery;
}

return jQuery;
}));
/*!
 * jQuery UI Core 1.11.4
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/category/ui-core/
 */

(function( factory ) {
	if ( typeof define === "function" && define.amd ) {

		// AMD. Register as an anonymous module.
		define( [ "jquery" ], factory );
	} else {

		// Browser globals
		factory( jQuery );
	}
}(function( $ ) {

// $.ui might exist from components with no dependencies, e.g., $.ui.position
$.ui = $.ui || {};

$.extend( $.ui, {
	version: "1.11.4",

	keyCode: {
		BACKSPACE: 8,
		COMMA: 188,
		DELETE: 46,
		DOWN: 40,
		END: 35,
		ENTER: 13,
		ESCAPE: 27,
		HOME: 36,
		LEFT: 37,
		PAGE_DOWN: 34,
		PAGE_UP: 33,
		PERIOD: 190,
		RIGHT: 39,
		SPACE: 32,
		TAB: 9,
		UP: 38
	}
});

// plugins
$.fn.extend({
	scrollParent: function( includeHidden ) {
		var position = this.css( "position" ),
			excludeStaticParent = position === "absolute",
			overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/,
			scrollParent = this.parents().filter( function() {
				var parent = $( this );
				if ( excludeStaticParent && parent.css( "position" ) === "static" ) {
					return false;
				}
				return overflowRegex.test( parent.css( "overflow" ) + parent.css( "overflow-y" ) + parent.css( "overflow-x" ) );
			}).eq( 0 );

		return position === "fixed" || !scrollParent.length ? $( this[ 0 ].ownerDocument || document ) : scrollParent;
	},

	uniqueId: (function() {
		var uuid = 0;

		return function() {
			return this.each(function() {
				if ( !this.id ) {
					this.id = "ui-id-" + ( ++uuid );
				}
			});
		};
	})(),

	removeUniqueId: function() {
		return this.each(function() {
			if ( /^ui-id-\d+$/.test( this.id ) ) {
				$( this ).removeAttr( "id" );
			}
		});
	}
});

// selectors
function focusable( element, isTabIndexNotNaN ) {
	var map, mapName, img,
		nodeName = element.nodeName.toLowerCase();
	if ( "area" === nodeName ) {
		map = element.parentNode;
		mapName = map.name;
		if ( !element.href || !mapName || map.nodeName.toLowerCase() !== "map" ) {
			return false;
		}
		img = $( "img[usemap='#" + mapName + "']" )[ 0 ];
		return !!img && visible( img );
	}
	return ( /^(input|select|textarea|button|object)$/.test( nodeName ) ?
		!element.disabled :
		"a" === nodeName ?
			element.href || isTabIndexNotNaN :
			isTabIndexNotNaN) &&
		// the element and all of its ancestors must be visible
		visible( element );
}

function visible( element ) {
	return $.expr.filters.visible( element ) &&
		!$( element ).parents().addBack().filter(function() {
			return $.css( this, "visibility" ) === "hidden";
		}).length;
}

$.extend( $.expr[ ":" ], {
	data: $.expr.createPseudo ?
		$.expr.createPseudo(function( dataName ) {
			return function( elem ) {
				return !!$.data( elem, dataName );
			};
		}) :
		// support: jQuery <1.8
		function( elem, i, match ) {
			return !!$.data( elem, match[ 3 ] );
		},

	focusable: function( element ) {
		return focusable( element, !isNaN( $.attr( element, "tabindex" ) ) );
	},

	tabbable: function( element ) {
		var tabIndex = $.attr( element, "tabindex" ),
			isTabIndexNaN = isNaN( tabIndex );
		return ( isTabIndexNaN || tabIndex >= 0 ) && focusable( element, !isTabIndexNaN );
	}
});

// support: jQuery <1.8
if ( !$( "<a>" ).outerWidth( 1 ).jquery ) {
	$.each( [ "Width", "Height" ], function( i, name ) {
		var side = name === "Width" ? [ "Left", "Right" ] : [ "Top", "Bottom" ],
			type = name.toLowerCase(),
			orig = {
				innerWidth: $.fn.innerWidth,
				innerHeight: $.fn.innerHeight,
				outerWidth: $.fn.outerWidth,
				outerHeight: $.fn.outerHeight
			};

		function reduce( elem, size, border, margin ) {
			$.each( side, function() {
				size -= parseFloat( $.css( elem, "padding" + this ) ) || 0;
				if ( border ) {
					size -= parseFloat( $.css( elem, "border" + this + "Width" ) ) || 0;
				}
				if ( margin ) {
					size -= parseFloat( $.css( elem, "margin" + this ) ) || 0;
				}
			});
			return size;
		}

		$.fn[ "inner" + name ] = function( size ) {
			if ( size === undefined ) {
				return orig[ "inner" + name ].call( this );
			}

			return this.each(function() {
				$( this ).css( type, reduce( this, size ) + "px" );
			});
		};

		$.fn[ "outer" + name] = function( size, margin ) {
			if ( typeof size !== "number" ) {
				return orig[ "outer" + name ].call( this, size );
			}

			return this.each(function() {
				$( this).css( type, reduce( this, size, true, margin ) + "px" );
			});
		};
	});
}

// support: jQuery <1.8
if ( !$.fn.addBack ) {
	$.fn.addBack = function( selector ) {
		return this.add( selector == null ?
			this.prevObject : this.prevObject.filter( selector )
		);
	};
}

// support: jQuery 1.6.1, 1.6.2 (http://bugs.jquery.com/ticket/9413)
if ( $( "<a>" ).data( "a-b", "a" ).removeData( "a-b" ).data( "a-b" ) ) {
	$.fn.removeData = (function( removeData ) {
		return function( key ) {
			if ( arguments.length ) {
				return removeData.call( this, $.camelCase( key ) );
			} else {
				return removeData.call( this );
			}
		};
	})( $.fn.removeData );
}

// deprecated
$.ui.ie = !!/msie [\w.]+/.exec( navigator.userAgent.toLowerCase() );

$.fn.extend({
	focus: (function( orig ) {
		return function( delay, fn ) {
			return typeof delay === "number" ?
				this.each(function() {
					var elem = this;
					setTimeout(function() {
						$( elem ).focus();
						if ( fn ) {
							fn.call( elem );
						}
					}, delay );
				}) :
				orig.apply( this, arguments );
		};
	})( $.fn.focus ),

	disableSelection: (function() {
		var eventType = "onselectstart" in document.createElement( "div" ) ?
			"selectstart" :
			"mousedown";

		return function() {
			return this.bind( eventType + ".ui-disableSelection", function( event ) {
				event.preventDefault();
			});
		};
	})(),

	enableSelection: function() {
		return this.unbind( ".ui-disableSelection" );
	},

	zIndex: function( zIndex ) {
		if ( zIndex !== undefined ) {
			return this.css( "zIndex", zIndex );
		}

		if ( this.length ) {
			var elem = $( this[ 0 ] ), position, value;
			while ( elem.length && elem[ 0 ] !== document ) {
				// Ignore z-index if position is set to a value where z-index is ignored by the browser
				// This makes behavior of this function consistent across browsers
				// WebKit always returns auto if the element is positioned
				position = elem.css( "position" );
				if ( position === "absolute" || position === "relative" || position === "fixed" ) {
					// IE returns 0 when zIndex is not specified
					// other browsers return a string
					// we ignore the case of nested elements with an explicit value of 0
					// <div style="z-index: -10;"><div style="z-index: 0;"></div></div>
					value = parseInt( elem.css( "zIndex" ), 10 );
					if ( !isNaN( value ) && value !== 0 ) {
						return value;
					}
				}
				elem = elem.parent();
			}
		}

		return 0;
	}
});

// $.ui.plugin is deprecated. Use $.widget() extensions instead.
$.ui.plugin = {
	add: function( module, option, set ) {
		var i,
			proto = $.ui[ module ].prototype;
		for ( i in set ) {
			proto.plugins[ i ] = proto.plugins[ i ] || [];
			proto.plugins[ i ].push( [ option, set[ i ] ] );
		}
	},
	call: function( instance, name, args, allowDisconnected ) {
		var i,
			set = instance.plugins[ name ];

		if ( !set ) {
			return;
		}

		if ( !allowDisconnected && ( !instance.element[ 0 ].parentNode || instance.element[ 0 ].parentNode.nodeType === 11 ) ) {
			return;
		}

		for ( i = 0; i < set.length; i++ ) {
			if ( instance.options[ set[ i ][ 0 ] ] ) {
				set[ i ][ 1 ].apply( instance.element, args );
			}
		}
	}
};

}));
(function($, undefined) {

/**
 * Unobtrusive scripting adapter for jQuery
 * https://github.com/rails/jquery-ujs
 *
 * Requires jQuery 1.8.0 or later.
 *
 * Released under the MIT license
 *
 */

  // Cut down on the number of issues from people inadvertently including jquery_ujs twice
  // by detecting and raising an error when it happens.
  'use strict';

  if ( $.rails !== undefined ) {
    $.error('jquery-ujs has already been loaded!');
  }

  // Shorthand to make it a little easier to call public rails functions from within rails.js
  var rails;
  var $document = $(document);

  $.rails = rails = {
    // Link elements bound by jquery-ujs
    linkClickSelector: 'a[data-confirm], a[data-method], a[data-remote]:not([disabled]), a[data-disable-with], a[data-disable]',

    // Button elements bound by jquery-ujs
    buttonClickSelector: 'button[data-remote]:not([form]):not(form button), button[data-confirm]:not([form]):not(form button)',

    // Select elements bound by jquery-ujs
    inputChangeSelector: 'select[data-remote], input[data-remote], textarea[data-remote]',

    // Form elements bound by jquery-ujs
    formSubmitSelector: 'form',

    // Form input elements bound by jquery-ujs
    formInputClickSelector: 'form input[type=submit], form input[type=image], form button[type=submit], form button:not([type]), input[type=submit][form], input[type=image][form], button[type=submit][form], button[form]:not([type])',

    // Form input elements disabled during form submission
    disableSelector: 'input[data-disable-with]:enabled, button[data-disable-with]:enabled, textarea[data-disable-with]:enabled, input[data-disable]:enabled, button[data-disable]:enabled, textarea[data-disable]:enabled',

    // Form input elements re-enabled after form submission
    enableSelector: 'input[data-disable-with]:disabled, button[data-disable-with]:disabled, textarea[data-disable-with]:disabled, input[data-disable]:disabled, button[data-disable]:disabled, textarea[data-disable]:disabled',

    // Form required input elements
    requiredInputSelector: 'input[name][required]:not([disabled]), textarea[name][required]:not([disabled])',

    // Form file input elements
    fileInputSelector: 'input[name][type=file]:not([disabled])',

    // Link onClick disable selector with possible reenable after remote submission
    linkDisableSelector: 'a[data-disable-with], a[data-disable]',

    // Button onClick disable selector with possible reenable after remote submission
    buttonDisableSelector: 'button[data-remote][data-disable-with], button[data-remote][data-disable]',

    // Up-to-date Cross-Site Request Forgery token
    csrfToken: function() {
     return $('meta[name=csrf-token]').attr('content');
    },

    // URL param that must contain the CSRF token
    csrfParam: function() {
     return $('meta[name=csrf-param]').attr('content');
    },

    // Make sure that every Ajax request sends the CSRF token
    CSRFProtection: function(xhr) {
      var token = rails.csrfToken();
      if (token) xhr.setRequestHeader('X-CSRF-Token', token);
    },

    // Make sure that all forms have actual up-to-date tokens (cached forms contain old ones)
    refreshCSRFTokens: function(){
      $('form input[name="' + rails.csrfParam() + '"]').val(rails.csrfToken());
    },

    // Triggers an event on an element and returns false if the event result is false
    fire: function(obj, name, data) {
      var event = $.Event(name);
      obj.trigger(event, data);
      return event.result !== false;
    },

    // Default confirm dialog, may be overridden with custom confirm dialog in $.rails.confirm
    confirm: function(message) {
      return confirm(message);
    },

    // Default ajax function, may be overridden with custom function in $.rails.ajax
    ajax: function(options) {
      return $.ajax(options);
    },

    // Default way to get an element's href. May be overridden at $.rails.href.
    href: function(element) {
      return element[0].href;
    },

    // Checks "data-remote" if true to handle the request through a XHR request.
    isRemote: function(element) {
      return element.data('remote') !== undefined && element.data('remote') !== false;
    },

    // Submits "remote" forms and links with ajax
    handleRemote: function(element) {
      var method, url, data, withCredentials, dataType, options;

      if (rails.fire(element, 'ajax:before')) {
        withCredentials = element.data('with-credentials') || null;
        dataType = element.data('type') || ($.ajaxSettings && $.ajaxSettings.dataType);

        if (element.is('form')) {
          method = element.data('ujs:submit-button-formmethod') || element.attr('method');
          url = element.data('ujs:submit-button-formaction') || element.attr('action');
          data = $(element[0]).serializeArray();
          // memoized value from clicked submit button
          var button = element.data('ujs:submit-button');
          if (button) {
            data.push(button);
            element.data('ujs:submit-button', null);
          }
          element.data('ujs:submit-button-formmethod', null);
          element.data('ujs:submit-button-formaction', null);
        } else if (element.is(rails.inputChangeSelector)) {
          method = element.data('method');
          url = element.data('url');
          data = element.serialize();
          if (element.data('params')) data = data + '&' + element.data('params');
        } else if (element.is(rails.buttonClickSelector)) {
          method = element.data('method') || 'get';
          url = element.data('url');
          data = element.serialize();
          if (element.data('params')) data = data + '&' + element.data('params');
        } else {
          method = element.data('method');
          url = rails.href(element);
          data = element.data('params') || null;
        }

        options = {
          type: method || 'GET', data: data, dataType: dataType,
          // stopping the "ajax:beforeSend" event will cancel the ajax request
          beforeSend: function(xhr, settings) {
            if (settings.dataType === undefined) {
              xhr.setRequestHeader('accept', '*/*;q=0.5, ' + settings.accepts.script);
            }
            if (rails.fire(element, 'ajax:beforeSend', [xhr, settings])) {
              element.trigger('ajax:send', xhr);
            } else {
              return false;
            }
          },
          success: function(data, status, xhr) {
            element.trigger('ajax:success', [data, status, xhr]);
          },
          complete: function(xhr, status) {
            element.trigger('ajax:complete', [xhr, status]);
          },
          error: function(xhr, status, error) {
            element.trigger('ajax:error', [xhr, status, error]);
          },
          crossDomain: rails.isCrossDomain(url)
        };

        // There is no withCredentials for IE6-8 when
        // "Enable native XMLHTTP support" is disabled
        if (withCredentials) {
          options.xhrFields = {
            withCredentials: withCredentials
          };
        }

        // Only pass url to `ajax` options if not blank
        if (url) { options.url = url; }

        return rails.ajax(options);
      } else {
        return false;
      }
    },

    // Determines if the request is a cross domain request.
    isCrossDomain: function(url) {
      var originAnchor = document.createElement('a');
      originAnchor.href = location.href;
      var urlAnchor = document.createElement('a');

      try {
        urlAnchor.href = url;
        // This is a workaround to a IE bug.
        urlAnchor.href = urlAnchor.href;

        // If URL protocol is false or is a string containing a single colon
        // *and* host are false, assume it is not a cross-domain request
        // (should only be the case for IE7 and IE compatibility mode).
        // Otherwise, evaluate protocol and host of the URL against the origin
        // protocol and host.
        return !(((!urlAnchor.protocol || urlAnchor.protocol === ':') && !urlAnchor.host) ||
          (originAnchor.protocol + '//' + originAnchor.host ===
            urlAnchor.protocol + '//' + urlAnchor.host));
      } catch (e) {
        // If there is an error parsing the URL, assume it is crossDomain.
        return true;
      }
    },

    // Handles "data-method" on links such as:
    // <a href="/users/5" data-method="delete" rel="nofollow" data-confirm="Are you sure?">Delete</a>
    handleMethod: function(link) {
      var href = rails.href(link),
        method = link.data('method'),
        target = link.attr('target'),
        csrfToken = rails.csrfToken(),
        csrfParam = rails.csrfParam(),
        form = $('<form method="post" action="' + href + '"></form>'),
        metadataInput = '<input name="_method" value="' + method + '" type="hidden" />';

      if (csrfParam !== undefined && csrfToken !== undefined && !rails.isCrossDomain(href)) {
        metadataInput += '<input name="' + csrfParam + '" value="' + csrfToken + '" type="hidden" />';
      }

      if (target) { form.attr('target', target); }

      form.hide().append(metadataInput).appendTo('body');
      form.submit();
    },

    // Helper function that returns form elements that match the specified CSS selector
    // If form is actually a "form" element this will return associated elements outside the from that have
    // the html form attribute set
    formElements: function(form, selector) {
      return form.is('form') ? $(form[0].elements).filter(selector) : form.find(selector);
    },

    /* Disables form elements:
      - Caches element value in 'ujs:enable-with' data store
      - Replaces element text with value of 'data-disable-with' attribute
      - Sets disabled property to true
    */
    disableFormElements: function(form) {
      rails.formElements(form, rails.disableSelector).each(function() {
        rails.disableFormElement($(this));
      });
    },

    disableFormElement: function(element) {
      var method, replacement;

      method = element.is('button') ? 'html' : 'val';
      replacement = element.data('disable-with');

      if (replacement !== undefined) {
        element.data('ujs:enable-with', element[method]());
        element[method](replacement);
      }

      element.prop('disabled', true);
      element.data('ujs:disabled', true);
    },

    /* Re-enables disabled form elements:
      - Replaces element text with cached value from 'ujs:enable-with' data store (created in `disableFormElements`)
      - Sets disabled property to false
    */
    enableFormElements: function(form) {
      rails.formElements(form, rails.enableSelector).each(function() {
        rails.enableFormElement($(this));
      });
    },

    enableFormElement: function(element) {
      var method = element.is('button') ? 'html' : 'val';
      if (element.data('ujs:enable-with') !== undefined) {
        element[method](element.data('ujs:enable-with'));
        element.removeData('ujs:enable-with'); // clean up cache
      }
      element.prop('disabled', false);
      element.removeData('ujs:disabled');
    },

   /* For 'data-confirm' attribute:
      - Fires `confirm` event
      - Shows the confirmation dialog
      - Fires the `confirm:complete` event

      Returns `true` if no function stops the chain and user chose yes; `false` otherwise.
      Attaching a handler to the element's `confirm` event that returns a `falsy` value cancels the confirmation dialog.
      Attaching a handler to the element's `confirm:complete` event that returns a `falsy` value makes this function
      return false. The `confirm:complete` event is fired whether or not the user answered true or false to the dialog.
   */
    allowAction: function(element) {
      var message = element.data('confirm'),
          answer = false, callback;
      if (!message) { return true; }

      if (rails.fire(element, 'confirm')) {
        try {
          answer = rails.confirm(message);
        } catch (e) {
          (console.error || console.log).call(console, e.stack || e);
        }
        callback = rails.fire(element, 'confirm:complete', [answer]);
      }
      return answer && callback;
    },

    // Helper function which checks for blank inputs in a form that match the specified CSS selector
    blankInputs: function(form, specifiedSelector, nonBlank) {
      var foundInputs = $(),
        input,
        valueToCheck,
        radiosForNameWithNoneSelected,
        radioName,
        selector = specifiedSelector || 'input,textarea',
        requiredInputs = form.find(selector),
        checkedRadioButtonNames = {};

      requiredInputs.each(function() {
        input = $(this);
        if (input.is('input[type=radio]')) {

          // Don't count unchecked required radio as blank if other radio with same name is checked,
          // regardless of whether same-name radio input has required attribute or not. The spec
          // states https://www.w3.org/TR/html5/forms.html#the-required-attribute
          radioName = input.attr('name');

          // Skip if we've already seen the radio with this name.
          if (!checkedRadioButtonNames[radioName]) {

            // If none checked
            if (form.find('input[type=radio]:checked[name="' + radioName + '"]').length === 0) {
              radiosForNameWithNoneSelected = form.find(
                'input[type=radio][name="' + radioName + '"]');
              foundInputs = foundInputs.add(radiosForNameWithNoneSelected);
            }

            // We only need to check each name once.
            checkedRadioButtonNames[radioName] = radioName;
          }
        } else {
          valueToCheck = input.is('input[type=checkbox],input[type=radio]') ? input.is(':checked') : !!input.val();
          if (valueToCheck === nonBlank) {
            foundInputs = foundInputs.add(input);
          }
        }
      });
      return foundInputs.length ? foundInputs : false;
    },

    // Helper function which checks for non-blank inputs in a form that match the specified CSS selector
    nonBlankInputs: function(form, specifiedSelector) {
      return rails.blankInputs(form, specifiedSelector, true); // true specifies nonBlank
    },

    // Helper function, needed to provide consistent behavior in IE
    stopEverything: function(e) {
      $(e.target).trigger('ujs:everythingStopped');
      e.stopImmediatePropagation();
      return false;
    },

    //  Replace element's html with the 'data-disable-with' after storing original html
    //  and prevent clicking on it
    disableElement: function(element) {
      var replacement = element.data('disable-with');

      if (replacement !== undefined) {
        element.data('ujs:enable-with', element.html()); // store enabled state
        element.html(replacement);
      }

      element.bind('click.railsDisable', function(e) { // prevent further clicking
        return rails.stopEverything(e);
      });
      element.data('ujs:disabled', true);
    },

    // Restore element to its original state which was disabled by 'disableElement' above
    enableElement: function(element) {
      if (element.data('ujs:enable-with') !== undefined) {
        element.html(element.data('ujs:enable-with')); // set to old enabled state
        element.removeData('ujs:enable-with'); // clean up cache
      }
      element.unbind('click.railsDisable'); // enable element
      element.removeData('ujs:disabled');
    }
  };

  if (rails.fire($document, 'rails:attachBindings')) {

    $.ajaxPrefilter(function(options, originalOptions, xhr){ if ( !options.crossDomain ) { rails.CSRFProtection(xhr); }});

    // This event works the same as the load event, except that it fires every
    // time the page is loaded.
    //
    // See https://github.com/rails/jquery-ujs/issues/357
    // See https://developer.mozilla.org/en-US/docs/Using_Firefox_1.5_caching
    $(window).on('pageshow.rails', function () {
      $($.rails.enableSelector).each(function () {
        var element = $(this);

        if (element.data('ujs:disabled')) {
          $.rails.enableFormElement(element);
        }
      });

      $($.rails.linkDisableSelector).each(function () {
        var element = $(this);

        if (element.data('ujs:disabled')) {
          $.rails.enableElement(element);
        }
      });
    });

    $document.on('ajax:complete', rails.linkDisableSelector, function() {
        rails.enableElement($(this));
    });

    $document.on('ajax:complete', rails.buttonDisableSelector, function() {
        rails.enableFormElement($(this));
    });

    $document.on('click.rails', rails.linkClickSelector, function(e) {
      var link = $(this), method = link.data('method'), data = link.data('params'), metaClick = e.metaKey || e.ctrlKey;
      if (!rails.allowAction(link)) return rails.stopEverything(e);

      if (!metaClick && link.is(rails.linkDisableSelector)) rails.disableElement(link);

      if (rails.isRemote(link)) {
        if (metaClick && (!method || method === 'GET') && !data) { return true; }

        var handleRemote = rails.handleRemote(link);
        // Response from rails.handleRemote() will either be false or a deferred object promise.
        if (handleRemote === false) {
          rails.enableElement(link);
        } else {
          handleRemote.fail( function() { rails.enableElement(link); } );
        }
        return false;

      } else if (method) {
        rails.handleMethod(link);
        return false;
      }
    });

    $document.on('click.rails', rails.buttonClickSelector, function(e) {
      var button = $(this);

      if (!rails.allowAction(button) || !rails.isRemote(button)) return rails.stopEverything(e);

      if (button.is(rails.buttonDisableSelector)) rails.disableFormElement(button);

      var handleRemote = rails.handleRemote(button);
      // Response from rails.handleRemote() will either be false or a deferred object promise.
      if (handleRemote === false) {
        rails.enableFormElement(button);
      } else {
        handleRemote.fail( function() { rails.enableFormElement(button); } );
      }
      return false;
    });

    $document.on('change.rails', rails.inputChangeSelector, function(e) {
      var link = $(this);
      if (!rails.allowAction(link) || !rails.isRemote(link)) return rails.stopEverything(e);

      rails.handleRemote(link);
      return false;
    });

    $document.on('submit.rails', rails.formSubmitSelector, function(e) {
      var form = $(this),
        remote = rails.isRemote(form),
        blankRequiredInputs,
        nonBlankFileInputs;

      if (!rails.allowAction(form)) return rails.stopEverything(e);

      // Skip other logic when required values are missing or file upload is present
      if (form.attr('novalidate') === undefined) {
        if (form.data('ujs:formnovalidate-button') === undefined) {
          blankRequiredInputs = rails.blankInputs(form, rails.requiredInputSelector, false);
          if (blankRequiredInputs && rails.fire(form, 'ajax:aborted:required', [blankRequiredInputs])) {
            return rails.stopEverything(e);
          }
        } else {
          // Clear the formnovalidate in case the next button click is not on a formnovalidate button
          // Not strictly necessary to do here, since it is also reset on each button click, but just to be certain
          form.data('ujs:formnovalidate-button', undefined);
        }
      }

      if (remote) {
        nonBlankFileInputs = rails.nonBlankInputs(form, rails.fileInputSelector);
        if (nonBlankFileInputs) {
          // Slight timeout so that the submit button gets properly serialized
          // (make it easy for event handler to serialize form without disabled values)
          setTimeout(function(){ rails.disableFormElements(form); }, 13);
          var aborted = rails.fire(form, 'ajax:aborted:file', [nonBlankFileInputs]);

          // Re-enable form elements if event bindings return false (canceling normal form submission)
          if (!aborted) { setTimeout(function(){ rails.enableFormElements(form); }, 13); }

          return aborted;
        }

        rails.handleRemote(form);
        return false;

      } else {
        // Slight timeout so that the submit button gets properly serialized
        setTimeout(function(){ rails.disableFormElements(form); }, 13);
      }
    });

    $document.on('click.rails', rails.formInputClickSelector, function(event) {
      var button = $(this);

      if (!rails.allowAction(button)) return rails.stopEverything(event);

      // Register the pressed submit button
      var name = button.attr('name'),
        data = name ? {name:name, value:button.val()} : null;

      var form = button.closest('form');
      if (form.length === 0) {
        form = $('#' + button.attr('form'));
      }
      form.data('ujs:submit-button', data);

      // Save attributes from button
      form.data('ujs:formnovalidate-button', button.attr('formnovalidate'));
      form.data('ujs:submit-button-formaction', button.attr('formaction'));
      form.data('ujs:submit-button-formmethod', button.attr('formmethod'));
    });

    $document.on('ajax:send.rails', rails.formSubmitSelector, function(event) {
      if (this === event.target) rails.disableFormElements($(this));
    });

    $document.on('ajax:complete.rails', rails.formSubmitSelector, function(event) {
      if (this === event.target) rails.enableFormElements($(this));
    });

    $(function(){
      rails.refreshCSRFTokens();
    });
  }

})( jQuery );
// jQuery Mask Plugin v1.14.0
// github.com/igorescobar/jQuery-Mask-Plugin
(function(b){"function"===typeof define&&define.amd?define(["jquery"],b):"object"===typeof exports?module.exports=b(require("jquery")):b(jQuery||Zepto)})(function(b){var y=function(a,e,d){var c={invalid:[],getCaret:function(){try{var r,b=0,e=a.get(0),d=document.selection,f=e.selectionStart;if(d&&-1===navigator.appVersion.indexOf("MSIE 10"))r=d.createRange(),r.moveStart("character",-c.val().length),b=r.text.length;else if(f||"0"===f)b=f;return b}catch(g){}},setCaret:function(r){try{if(a.is(":focus")){var c,
b=a.get(0);b.setSelectionRange?(b.focus(),b.setSelectionRange(r,r)):(c=b.createTextRange(),c.collapse(!0),c.moveEnd("character",r),c.moveStart("character",r),c.select())}}catch(e){}},events:function(){a.on("keydown.mask",function(c){a.data("mask-keycode",c.keyCode||c.which)}).on(b.jMaskGlobals.useInput?"input.mask":"keyup.mask",c.behaviour).on("paste.mask drop.mask",function(){setTimeout(function(){a.keydown().keyup()},100)}).on("change.mask",function(){a.data("changed",!0)}).on("blur.mask",function(){n===
c.val()||a.data("changed")||a.trigger("change");a.data("changed",!1)}).on("blur.mask",function(){n=c.val()}).on("focus.mask",function(a){!0===d.selectOnFocus&&b(a.target).select()}).on("focusout.mask",function(){d.clearIfNotMatch&&!p.test(c.val())&&c.val("")})},getRegexMask:function(){for(var a=[],c,b,d,f,l=0;l<e.length;l++)(c=g.translation[e.charAt(l)])?(b=c.pattern.toString().replace(/.{1}$|^.{1}/g,""),d=c.optional,(c=c.recursive)?(a.push(e.charAt(l)),f={digit:e.charAt(l),pattern:b}):a.push(d||
c?b+"?":b)):a.push(e.charAt(l).replace(/[-\/\\^$*+?.()|[\]{}]/g,"\\$&"));a=a.join("");f&&(a=a.replace(new RegExp("("+f.digit+"(.*"+f.digit+")?)"),"($1)?").replace(new RegExp(f.digit,"g"),f.pattern));return new RegExp(a)},destroyEvents:function(){a.off("input keydown keyup paste drop blur focusout ".split(" ").join(".mask "))},val:function(c){var b=a.is("input")?"val":"text";if(0<arguments.length){if(a[b]()!==c)a[b](c);b=a}else b=a[b]();return b},getMCharsBeforeCount:function(a,c){for(var b=0,d=0,
f=e.length;d<f&&d<a;d++)g.translation[e.charAt(d)]||(a=c?a+1:a,b++);return b},caretPos:function(a,b,d,h){return g.translation[e.charAt(Math.min(a-1,e.length-1))]?Math.min(a+d-b-h,d):c.caretPos(a+1,b,d,h)},behaviour:function(d){d=d||window.event;c.invalid=[];var e=a.data("mask-keycode");if(-1===b.inArray(e,g.byPassKeys)){var m=c.getCaret(),h=c.val().length,f=c.getMasked(),l=f.length,k=c.getMCharsBeforeCount(l-1)-c.getMCharsBeforeCount(h-1),n=m<h;c.val(f);n&&(8!==e&&46!==e&&(m=c.caretPos(m,h,l,k)),
c.setCaret(m));return c.callbacks(d)}},getMasked:function(a,b){var m=[],h=void 0===b?c.val():b+"",f=0,l=e.length,k=0,n=h.length,q=1,p="push",u=-1,t,w;d.reverse?(p="unshift",q=-1,t=0,f=l-1,k=n-1,w=function(){return-1<f&&-1<k}):(t=l-1,w=function(){return f<l&&k<n});for(;w();){var x=e.charAt(f),v=h.charAt(k),s=g.translation[x];if(s)v.match(s.pattern)?(m[p](v),s.recursive&&(-1===u?u=f:f===t&&(f=u-q),t===u&&(f-=q)),f+=q):s.optional?(f+=q,k-=q):s.fallback?(m[p](s.fallback),f+=q,k-=q):c.invalid.push({p:k,
v:v,e:s.pattern}),k+=q;else{if(!a)m[p](x);v===x&&(k+=q);f+=q}}h=e.charAt(t);l!==n+1||g.translation[h]||m.push(h);return m.join("")},callbacks:function(b){var g=c.val(),m=g!==n,h=[g,b,a,d],f=function(a,b,c){"function"===typeof d[a]&&b&&d[a].apply(this,c)};f("onChange",!0===m,h);f("onKeyPress",!0===m,h);f("onComplete",g.length===e.length,h);f("onInvalid",0<c.invalid.length,[g,b,a,c.invalid,d])}};a=b(a);var g=this,n=c.val(),p;e="function"===typeof e?e(c.val(),void 0,a,d):e;g.mask=e;g.options=d;g.remove=
function(){var b=c.getCaret();c.destroyEvents();c.val(g.getCleanVal());c.setCaret(b-c.getMCharsBeforeCount(b));return a};g.getCleanVal=function(){return c.getMasked(!0)};g.getMaskedVal=function(a){return c.getMasked(!1,a)};g.init=function(e){e=e||!1;d=d||{};g.clearIfNotMatch=b.jMaskGlobals.clearIfNotMatch;g.byPassKeys=b.jMaskGlobals.byPassKeys;g.translation=b.extend({},b.jMaskGlobals.translation,d.translation);g=b.extend(!0,{},g,d);p=c.getRegexMask();!1===e?(d.placeholder&&a.attr("placeholder",d.placeholder),
a.data("mask")&&a.attr("autocomplete","off"),c.destroyEvents(),c.events(),e=c.getCaret(),c.val(c.getMasked()),c.setCaret(e+c.getMCharsBeforeCount(e,!0))):(c.events(),c.val(c.getMasked()))};g.init(!a.is("input"))};b.maskWatchers={};var A=function(){var a=b(this),e={},d=a.attr("data-mask");a.attr("data-mask-reverse")&&(e.reverse=!0);a.attr("data-mask-clearifnotmatch")&&(e.clearIfNotMatch=!0);"true"===a.attr("data-mask-selectonfocus")&&(e.selectOnFocus=!0);if(z(a,d,e))return a.data("mask",new y(this,
d,e))},z=function(a,e,d){d=d||{};var c=b(a).data("mask"),g=JSON.stringify;a=b(a).val()||b(a).text();try{return"function"===typeof e&&(e=e(a)),"object"!==typeof c||g(c.options)!==g(d)||c.mask!==e}catch(n){}};b.fn.mask=function(a,e){e=e||{};var d=this.selector,c=b.jMaskGlobals,g=c.watchInterval,c=e.watchInputs||c.watchInputs,n=function(){if(z(this,a,e))return b(this).data("mask",new y(this,a,e))};b(this).each(n);d&&""!==d&&c&&(clearInterval(b.maskWatchers[d]),b.maskWatchers[d]=setInterval(function(){b(document).find(d).each(n)},
g));return this};b.fn.masked=function(a){return this.data("mask").getMaskedVal(a)};b.fn.unmask=function(){clearInterval(b.maskWatchers[this.selector]);delete b.maskWatchers[this.selector];return this.each(function(){var a=b(this).data("mask");a&&a.remove().removeData("mask")})};b.fn.cleanVal=function(){return this.data("mask").getCleanVal()};b.applyDataMask=function(a){a=a||b.jMaskGlobals.maskElements;(a instanceof b?a:b(a)).filter(b.jMaskGlobals.dataMaskAttr).each(A)};var p={maskElements:"input,td,span,div",
dataMaskAttr:"*[data-mask]",dataMask:!0,watchInterval:300,watchInputs:!0,useInput:function(a){var b=document.createElement("div"),d;a="on"+a;d=a in b;d||(b.setAttribute(a,"return;"),d="function"===typeof b[a]);return d}("input"),watchDataMask:!1,byPassKeys:[9,16,17,18,36,37,38,39,40,91],translation:{0:{pattern:/\d/},9:{pattern:/\d/,optional:!0},"#":{pattern:/\d/,recursive:!0},A:{pattern:/[a-zA-Z0-9]/},S:{pattern:/[a-zA-Z]/}}};b.jMaskGlobals=b.jMaskGlobals||{};p=b.jMaskGlobals=b.extend(!0,{},p,b.jMaskGlobals);
p.dataMask&&b.applyDataMask();setInterval(function(){b.jMaskGlobals.watchDataMask&&b.applyDataMask()},p.watchInterval)});
/*!
 * jQuery Validation Plugin v1.16.0
 *
 * http://jqueryvalidation.org/
 *
 * Copyright (c) 2016 Jörn Zaefferer
 * Released under the MIT license
 */

(function( factory ) {
    if ( typeof define === "function" && define.amd ) {
        define( ["jquery"], factory );
    } else if (typeof module === "object" && module.exports) {
        module.exports = factory( require( "jquery" ) );
    } else {
        factory( jQuery );
    }
}(function( $ ) {

$.extend( $.fn, {

    // http://jqueryvalidation.org/validate/
    validate: function( options ) {

        // If nothing is selected, return nothing; can't chain anyway
        if ( !this.length ) {
            if ( options && options.debug && window.console ) {
                console.warn( "Nothing selected, can't validate, returning nothing." );
            }
            return;
        }

        // Check if a validator for this form was already created
        var validator = $.data( this[ 0 ], "validator" );
        if ( validator ) {
            return validator;
        }

        // Add novalidate tag if HTML5.
        this.attr( "novalidate", "novalidate" );

        validator = new $.validator( options, this[ 0 ] );
        $.data( this[ 0 ], "validator", validator );

        if ( validator.settings.onsubmit ) {

            this.on( "click.validate", ":submit", function( event ) {
                if ( validator.settings.submitHandler ) {
                    validator.submitButton = event.target;
                }

                // Allow suppressing validation by adding a cancel class to the submit button
                if ( $( this ).hasClass( "cancel" ) ) {
                    validator.cancelSubmit = true;
                }

                // Allow suppressing validation by adding the html5 formnovalidate attribute to the submit button
                if ( $( this ).attr( "formnovalidate" ) !== undefined ) {
                    validator.cancelSubmit = true;
                }
            } );

            // Validate the form on submit
            this.on( "submit.validate", function( event ) {
                if ( validator.settings.debug ) {

                    // Prevent form submit to be able to see console output
                    event.preventDefault();
                }
                function handle() {
                    var hidden, result;
                    if ( validator.settings.submitHandler ) {
                        if ( validator.submitButton ) {

                            // Insert a hidden input as a replacement for the missing submit button
                            hidden = $( "<input type='hidden'/>" )
                                .attr( "name", validator.submitButton.name )
                                .val( $( validator.submitButton ).val() )
                                .appendTo( validator.currentForm );
                        }
                        result = validator.settings.submitHandler.call( validator, validator.currentForm, event );
                        if ( validator.submitButton ) {

                            // And clean up afterwards; thanks to no-block-scope, hidden can be referenced
                            hidden.remove();
                        }
                        if ( result !== undefined ) {
                            return result;
                        }
                        return false;
                    }
                    return true;
                }

                // Prevent submit for invalid forms or custom submit handlers
                if ( validator.cancelSubmit ) {
                    validator.cancelSubmit = false;
                    return handle();
                }
                if ( validator.form() ) {
                    if ( validator.pendingRequest ) {
                        validator.formSubmitted = true;
                        return false;
                    }
                    return handle();
                } else {
                    validator.focusInvalid();
                    return false;
                }
            } );
        }

        return validator;
    },

    // http://jqueryvalidation.org/valid/
    valid: function() {
        var valid, validator, errorList;

        if ( $( this[ 0 ] ).is( "form" ) ) {
            valid = this.validate().form();
        } else {
            errorList = [];
            valid = true;
            validator = $( this[ 0 ].form ).validate();
            this.each( function() {
                valid = validator.element( this ) && valid;
                if ( !valid ) {
                    errorList = errorList.concat( validator.errorList );
                }
            } );
            validator.errorList = errorList;
        }
        return valid;
    },

    // http://jqueryvalidation.org/rules/
    rules: function( command, argument ) {
        var element = this[ 0 ],
            settings, staticRules, existingRules, data, param, filtered;

        // If nothing is selected, return empty object; can't chain anyway
        if ( element == null || element.form == null ) {
            return;
        }

        if ( command ) {
            settings = $.data( element.form, "validator" ).settings;
            staticRules = settings.rules;
            existingRules = $.validator.staticRules( element );
            switch ( command ) {
            case "add":
                $.extend( existingRules, $.validator.normalizeRule( argument ) );

                // Remove messages from rules, but allow them to be set separately
                delete existingRules.messages;
                staticRules[ element.name ] = existingRules;
                if ( argument.messages ) {
                    settings.messages[ element.name ] = $.extend( settings.messages[ element.name ], argument.messages );
                }
                break;
            case "remove":
                if ( !argument ) {
                    delete staticRules[ element.name ];
                    return existingRules;
                }
                filtered = {};
                $.each( argument.split( /\s/ ), function( index, method ) {
                    filtered[ method ] = existingRules[ method ];
                    delete existingRules[ method ];
                    if ( method === "required" ) {
                        $( element ).removeAttr( "aria-required" );
                    }
                } );
                return filtered;
            }
        }

        data = $.validator.normalizeRules(
        $.extend(
            {},
            $.validator.classRules( element ),
            $.validator.attributeRules( element ),
            $.validator.dataRules( element ),
            $.validator.staticRules( element )
        ), element );

        // Make sure required is at front
        if ( data.required ) {
            param = data.required;
            delete data.required;
            data = $.extend( { required: param }, data );
            $( element ).attr( "aria-required", "true" );
        }

        // Make sure remote is at back
        if ( data.remote ) {
            param = data.remote;
            delete data.remote;
            data = $.extend( data, { remote: param } );
        }

        return data;
    }
} );

// Custom selectors
$.extend( $.expr.pseudos || $.expr[ ":" ], {        // '|| $.expr[ ":" ]' here enables backwards compatibility to jQuery 1.7. Can be removed when dropping jQ 1.7.x support

    // http://jqueryvalidation.org/blank-selector/
    blank: function( a ) {
        return !$.trim( "" + $( a ).val() );
    },

    // http://jqueryvalidation.org/filled-selector/
    filled: function( a ) {
        var val = $( a ).val();
        return val !== null && !!$.trim( "" + val );
    },

    // http://jqueryvalidation.org/unchecked-selector/
    unchecked: function( a ) {
        return !$( a ).prop( "checked" );
    }
} );

// Constructor for validator
$.validator = function( options, form ) {
    this.settings = $.extend( true, {}, $.validator.defaults, options );
    this.currentForm = form;
    this.init();
};

// http://jqueryvalidation.org/jQuery.validator.format/
$.validator.format = function( source, params ) {
    if ( arguments.length === 1 ) {
        return function() {
            var args = $.makeArray( arguments );
            args.unshift( source );
            return $.validator.format.apply( this, args );
        };
    }
    if ( params === undefined ) {
        return source;
    }
    if ( arguments.length > 2 && params.constructor !== Array  ) {
        params = $.makeArray( arguments ).slice( 1 );
    }
    if ( params.constructor !== Array ) {
        params = [ params ];
    }
    $.each( params, function( i, n ) {
        source = source.replace( new RegExp( "\\{" + i + "\\}", "g" ), function() {
            return n;
        } );
    } );
    return source;
};

$.extend( $.validator, {

    defaults: {
        messages: {},
        groups: {},
        rules: {},
        errorClass: "error",
        pendingClass: "pending",
        validClass: "valid",
        errorElement: "label",
        focusCleanup: false,
        focusInvalid: true,
        errorContainer: $( [] ),
        errorLabelContainer: $( [] ),
        onsubmit: true,
        ignore: ":hidden",
        ignoreTitle: false,
        onfocusin: function( element ) {
            this.lastActive = element;

            // Hide error label and remove error class on focus if enabled
            if ( this.settings.focusCleanup ) {
                if ( this.settings.unhighlight ) {
                    this.settings.unhighlight.call( this, element, this.settings.errorClass, this.settings.validClass );
                }
                this.hideThese( this.errorsFor( element ) );
            }
        },
        onfocusout: function( element ) {
            if ( !this.checkable( element ) && ( element.name in this.submitted || !this.optional( element ) ) ) {
                this.element( element );
            }
        },
        onkeyup: function( element, event ) {

            // Avoid revalidate the field when pressing one of the following keys
            // Shift       => 16
            // Ctrl        => 17
            // Alt         => 18
            // Caps lock   => 20
            // End         => 35
            // Home        => 36
            // Left arrow  => 37
            // Up arrow    => 38
            // Right arrow => 39
            // Down arrow  => 40
            // Insert      => 45
            // Num lock    => 144
            // AltGr key   => 225
            var excludedKeys = [
                16, 17, 18, 20, 35, 36, 37,
                38, 39, 40, 45, 144, 225
            ];

            if ( event.which === 9 && this.elementValue( element ) === "" || $.inArray( event.keyCode, excludedKeys ) !== -1 ) {
                return;
            } else if ( element.name in this.submitted || element.name in this.invalid ) {
                this.element( element );
            }
        },
        onclick: function( element ) {

            // Click on selects, radiobuttons and checkboxes
            if ( element.name in this.submitted ) {
                this.element( element );

            // Or option elements, check parent select in that case
            } else if ( element.parentNode.name in this.submitted ) {
                this.element( element.parentNode );
            }
        },
        highlight: function( element, errorClass, validClass ) {
            if ( element.type === "radio" ) {
                this.findByName( element.name ).addClass( errorClass ).removeClass( validClass );
            } else {
                $( element ).addClass( errorClass ).removeClass( validClass );
            }
        },
        unhighlight: function( element, errorClass, validClass ) {
            if ( element.type === "radio" ) {
                this.findByName( element.name ).removeClass( errorClass ).addClass( validClass );
            } else {
                $( element ).removeClass( errorClass ).addClass( validClass );
            }
        }
    },

    // http://jqueryvalidation.org/jQuery.validator.setDefaults/
    setDefaults: function( settings ) {
        $.extend( $.validator.defaults, settings );
    },

    messages: {
        required: "This field is required.",
        remote: "Please fix this field.",
        email: "Please enter a valid email address.",
        url: "Please enter a valid URL.",
        date: "Please enter a valid date.",
        dateISO: "Please enter a valid date (ISO).",
        number: "Please enter a valid number.",
        digits: "Please enter only digits.",
        equalTo: "Please enter the same value again.",
        maxlength: $.validator.format( "Please enter no more than {0} characters." ),
        minlength: $.validator.format( "Please enter at least {0} characters." ),
        rangelength: $.validator.format( "Please enter a value between {0} and {1} characters long." ),
        range: $.validator.format( "Please enter a value between {0} and {1}." ),
        max: $.validator.format( "Please enter a value less than or equal to {0}." ),
        min: $.validator.format( "Please enter a value greater than or equal to {0}." ),
        step: $.validator.format( "Please enter a multiple of {0}." )
    },

    autoCreateRanges: false,

    prototype: {

        init: function() {
            this.labelContainer = $( this.settings.errorLabelContainer );
            this.errorContext = this.labelContainer.length && this.labelContainer || $( this.currentForm );
            this.containers = $( this.settings.errorContainer ).add( this.settings.errorLabelContainer );
            this.submitted = {};
            this.valueCache = {};
            this.pendingRequest = 0;
            this.pending = {};
            this.invalid = {};
            this.reset();

            var groups = ( this.groups = {} ),
                rules;
            $.each( this.settings.groups, function( key, value ) {
                if ( typeof value === "string" ) {
                    value = value.split( /\s/ );
                }
                $.each( value, function( index, name ) {
                    groups[ name ] = key;
                } );
            } );
            rules = this.settings.rules;
            $.each( rules, function( key, value ) {
                rules[ key ] = $.validator.normalizeRule( value );
            } );

            function delegate( event ) {

                // Set form expando on contenteditable
                if ( !this.form && this.hasAttribute( "contenteditable" ) ) {
                    this.form = $( this ).closest( "form" )[ 0 ];
                }

                var validator = $.data( this.form, "validator" ),
                    eventType = "on" + event.type.replace( /^validate/, "" ),
                    settings = validator.settings;
                if ( settings[ eventType ] && !$( this ).is( settings.ignore ) ) {
                    settings[ eventType ].call( validator, this, event );
                }
            }

            $( this.currentForm )
                .on( "focusin.validate focusout.validate keyup.validate",
                    ":text, [type='password'], [type='file'], select, textarea, [type='number'], [type='search'], " +
                    "[type='tel'], [type='url'], [type='email'], [type='datetime'], [type='date'], [type='month'], " +
                    "[type='week'], [type='time'], [type='datetime-local'], [type='range'], [type='color'], " +
                    "[type='radio'], [type='checkbox'], [contenteditable], [type='button']", delegate )

                // Support: Chrome, oldIE
                // "select" is provided as event.target when clicking a option
                .on( "click.validate", "select, option, [type='radio'], [type='checkbox']", delegate );

            if ( this.settings.invalidHandler ) {
                $( this.currentForm ).on( "invalid-form.validate", this.settings.invalidHandler );
            }

            // Add aria-required to any Static/Data/Class required fields before first validation
            // Screen readers require this attribute to be present before the initial submission http://www.w3.org/TR/WCAG-TECHS/ARIA2.html
            $( this.currentForm ).find( "[required], [data-rule-required], .required" ).attr( "aria-required", "true" );
        },

        // http://jqueryvalidation.org/Validator.form/
        form: function() {
            this.checkForm();
            $.extend( this.submitted, this.errorMap );
            this.invalid = $.extend( {}, this.errorMap );
            if ( !this.valid() ) {
                $( this.currentForm ).triggerHandler( "invalid-form", [ this ] );
            }
            this.showErrors();
            return this.valid();
        },

        checkForm: function() {
            this.prepareForm();
            for ( var i = 0, elements = ( this.currentElements = this.elements() ); elements[ i ]; i++ ) {
                this.check( elements[ i ] );
            }
            return this.valid();
        },

        // http://jqueryvalidation.org/Validator.element/
        element: function( element ) {
            var cleanElement = this.clean( element ),
                checkElement = this.validationTargetFor( cleanElement ),
                v = this,
                result = true,
                rs, group;

            if ( checkElement === undefined ) {
                delete this.invalid[ cleanElement.name ];
            } else {
                this.prepareElement( checkElement );
                this.currentElements = $( checkElement );

                // If this element is grouped, then validate all group elements already
                // containing a value
                group = this.groups[ checkElement.name ];
                if ( group ) {
                    $.each( this.groups, function( name, testgroup ) {
                        if ( testgroup === group && name !== checkElement.name ) {
                            cleanElement = v.validationTargetFor( v.clean( v.findByName( name ) ) );
                            if ( cleanElement && cleanElement.name in v.invalid ) {
                                v.currentElements.push( cleanElement );
                                result = v.check( cleanElement ) && result;
                            }
                        }
                    } );
                }

                rs = this.check( checkElement ) !== false;
                result = result && rs;
                if ( rs ) {
                    this.invalid[ checkElement.name ] = false;
                } else {
                    this.invalid[ checkElement.name ] = true;
                }

                if ( !this.numberOfInvalids() ) {

                    // Hide error containers on last error
                    this.toHide = this.toHide.add( this.containers );
                }
                this.showErrors();

                // Add aria-invalid status for screen readers
                $( element ).attr( "aria-invalid", !rs );
            }

            return result;
        },

        // http://jqueryvalidation.org/Validator.showErrors/
        showErrors: function( errors ) {
            if ( errors ) {
                var validator = this;

                // Add items to error list and map
                $.extend( this.errorMap, errors );
                this.errorList = $.map( this.errorMap, function( message, name ) {
                    return {
                        message: message,
                        element: validator.findByName( name )[ 0 ]
                    };
                } );

                // Remove items from success list
                this.successList = $.grep( this.successList, function( element ) {
                    return !( element.name in errors );
                } );
            }
            if ( this.settings.showErrors ) {
                this.settings.showErrors.call( this, this.errorMap, this.errorList );
            } else {
                this.defaultShowErrors();
            }
        },

        // http://jqueryvalidation.org/Validator.resetForm/
        resetForm: function() {
            if ( $.fn.resetForm ) {
                $( this.currentForm ).resetForm();
            }
            this.invalid = {};
            this.submitted = {};
            this.prepareForm();
            this.hideErrors();
            var elements = this.elements()
                .removeData( "previousValue" )
                .removeAttr( "aria-invalid" );

            this.resetElements( elements );
        },

        resetElements: function( elements ) {
            var i;

            if ( this.settings.unhighlight ) {
                for ( i = 0; elements[ i ]; i++ ) {
                    this.settings.unhighlight.call( this, elements[ i ],
                        this.settings.errorClass, "" );
                    this.findByName( elements[ i ].name ).removeClass( this.settings.validClass );
                }
            } else {
                elements
                    .removeClass( this.settings.errorClass )
                    .removeClass( this.settings.validClass );
            }
        },

        numberOfInvalids: function() {
            return this.objectLength( this.invalid );
        },

        objectLength: function( obj ) {
            /* jshint unused: false */
            var count = 0,
                i;
            for ( i in obj ) {
                if ( obj[ i ] ) {
                    count++;
                }
            }
            return count;
        },

        hideErrors: function() {
            this.hideThese( this.toHide );
        },

        hideThese: function( errors ) {
            errors.not( this.containers ).text( "" );
            this.addWrapper( errors ).hide();
        },

        valid: function() {
            return this.size() === 0;
        },

        size: function() {
            return this.errorList.length;
        },

        focusInvalid: function() {
            if ( this.settings.focusInvalid ) {
                try {
                    $( this.findLastActive() || this.errorList.length && this.errorList[ 0 ].element || [] )
                    .filter( ":visible" )
                    .focus()

                    // Manually trigger focusin event; without it, focusin handler isn't called, findLastActive won't have anything to find
                    .trigger( "focusin" );
                } catch ( e ) {

                    // Ignore IE throwing errors when focusing hidden elements
                }
            }
        },

        findLastActive: function() {
            var lastActive = this.lastActive;
            return lastActive && $.grep( this.errorList, function( n ) {
                return n.element.name === lastActive.name;
            } ).length === 1 && lastActive;
        },

        elements: function() {
            var validator = this,
                rulesCache = {};

            // Select all valid inputs inside the form (no submit or reset buttons)
            return $( this.currentForm )
            .find( "input, select, textarea, [contenteditable]" )
            .not( ":submit, :reset, :image, :disabled" )
            .not( this.settings.ignore )
            .filter( function() {
                var name = this.name || $( this ).attr( "name" ); // For contenteditable
                if ( !name && validator.settings.debug && window.console ) {
                    console.error( "%o has no name assigned", this );
                }

                // Set form expando on contenteditable
                if ( this.hasAttribute( "contenteditable" ) ) {
                    this.form = $( this ).closest( "form" )[ 0 ];
                }

                // Select only the first element for each name, and only those with rules specified
                if ( name in rulesCache || !validator.objectLength( $( this ).rules() ) ) {
                    return false;
                }

                rulesCache[ name ] = true;
                return true;
            } );
        },

        clean: function( selector ) {
            return $( selector )[ 0 ];
        },

        errors: function() {
            var errorClass = this.settings.errorClass.split( " " ).join( "." );
            return $( this.settings.errorElement + "." + errorClass, this.errorContext );
        },

        resetInternals: function() {
            this.successList = [];
            this.errorList = [];
            this.errorMap = {};
            this.toShow = $( [] );
            this.toHide = $( [] );
        },

        reset: function() {
            this.resetInternals();
            this.currentElements = $( [] );
        },

        prepareForm: function() {
            this.reset();
            this.toHide = this.errors().add( this.containers );
        },

        prepareElement: function( element ) {
            this.reset();
            this.toHide = this.errorsFor( element );
        },

        elementValue: function( element ) {
            var $element = $( element ),
                type = element.type,
                val, idx;

            if ( type === "radio" || type === "checkbox" ) {
                return this.findByName( element.name ).filter( ":checked" ).val();
            } else if ( type === "number" && typeof element.validity !== "undefined" ) {
                return element.validity.badInput ? "NaN" : $element.val();
            }

            if ( element.hasAttribute( "contenteditable" ) ) {
                val = $element.text();
            } else {
                val = $element.val();
            }

            if ( type === "file" ) {

                // Modern browser (chrome & safari)
                if ( val.substr( 0, 12 ) === "C:\\fakepath\\" ) {
                    return val.substr( 12 );
                }

                // Legacy browsers
                // Unix-based path
                idx = val.lastIndexOf( "/" );
                if ( idx >= 0 ) {
                    return val.substr( idx + 1 );
                }

                // Windows-based path
                idx = val.lastIndexOf( "\\" );
                if ( idx >= 0 ) {
                    return val.substr( idx + 1 );
                }

                // Just the file name
                return val;
            }

            if ( typeof val === "string" ) {
                return val.replace( /\r/g, "" );
            }
            return val;
        },

        check: function( element ) {
            element = this.validationTargetFor( this.clean( element ) );

            var rules = $( element ).rules(),
                rulesCount = $.map( rules, function( n, i ) {
                    return i;
                } ).length,
                dependencyMismatch = false,
                val = this.elementValue( element ),
                result, method, rule;

            // If a normalizer is defined for this element, then
            // call it to retreive the changed value instead
            // of using the real one.
            // Note that `this` in the normalizer is `element`.
            if ( typeof rules.normalizer === "function" ) {
                val = rules.normalizer.call( element, val );

                if ( typeof val !== "string" ) {
                    throw new TypeError( "The normalizer should return a string value." );
                }

                // Delete the normalizer from rules to avoid treating
                // it as a pre-defined method.
                delete rules.normalizer;
            }

            for ( method in rules ) {
                rule = { method: method, parameters: rules[ method ] };
                try {
                    result = $.validator.methods[ method ].call( this, val, element, rule.parameters );

                    // If a method indicates that the field is optional and therefore valid,
                    // don't mark it as valid when there are no other rules
                    if ( result === "dependency-mismatch" && rulesCount === 1 ) {
                        dependencyMismatch = true;
                        continue;
                    }
                    dependencyMismatch = false;

                    if ( result === "pending" ) {
                        this.toHide = this.toHide.not( this.errorsFor( element ) );
                        return;
                    }

                    if ( !result ) {
                        this.formatAndAdd( element, rule );
                        return false;
                    }
                } catch ( e ) {
                    if ( this.settings.debug && window.console ) {
                        console.log( "Exception occurred when checking element " + element.id + ", check the '" + rule.method + "' method.", e );
                    }
                    if ( e instanceof TypeError ) {
                        e.message += ".  Exception occurred when checking element " + element.id + ", check the '" + rule.method + "' method.";
                    }

                    throw e;
                }
            }
            if ( dependencyMismatch ) {
                return;
            }
            if ( this.objectLength( rules ) ) {
                this.successList.push( element );
            }
            return true;
        },

        // Return the custom message for the given element and validation method
        // specified in the element's HTML5 data attribute
        // return the generic message if present and no method specific message is present
        customDataMessage: function( element, method ) {
            return $( element ).data( "msg" + method.charAt( 0 ).toUpperCase() +
                method.substring( 1 ).toLowerCase() ) || $( element ).data( "msg" );
        },

        // Return the custom message for the given element name and validation method
        customMessage: function( name, method ) {
            var m = this.settings.messages[ name ];
            return m && ( m.constructor === String ? m : m[ method ] );
        },

        // Return the first defined argument, allowing empty strings
        findDefined: function() {
            for ( var i = 0; i < arguments.length; i++ ) {
                if ( arguments[ i ] !== undefined ) {
                    return arguments[ i ];
                }
            }
            return undefined;
        },

        // The second parameter 'rule' used to be a string, and extended to an object literal
        // of the following form:
        // rule = {
        //     method: "method name",
        //     parameters: "the given method parameters"
        // }
        //
        // The old behavior still supported, kept to maintain backward compatibility with
        // old code, and will be removed in the next major release.
        defaultMessage: function( element, rule ) {
            if ( typeof rule === "string" ) {
                rule = { method: rule };
            }

            var message = this.findDefined(
                    this.customMessage( element.name, rule.method ),
                    this.customDataMessage( element, rule.method ),

                    // 'title' is never undefined, so handle empty string as undefined
                    !this.settings.ignoreTitle && element.title || undefined,
                    $.validator.messages[ rule.method ],
                    "<strong>Warning: No message defined for " + element.name + "</strong>"
                ),
                theregex = /\$?\{(\d+)\}/g;
            if ( typeof message === "function" ) {
                message = message.call( this, rule.parameters, element );
            } else if ( theregex.test( message ) ) {
                message = $.validator.format( message.replace( theregex, "{$1}" ), rule.parameters );
            }

            return message;
        },

        formatAndAdd: function( element, rule ) {
            var message = this.defaultMessage( element, rule );

            this.errorList.push( {
                message: message,
                element: element,
                method: rule.method
            } );

            this.errorMap[ element.name ] = message;
            this.submitted[ element.name ] = message;
        },

        addWrapper: function( toToggle ) {
            if ( this.settings.wrapper ) {
                toToggle = toToggle.add( toToggle.parent( this.settings.wrapper ) );
            }
            return toToggle;
        },

        defaultShowErrors: function() {
            var i, elements, error;
            for ( i = 0; this.errorList[ i ]; i++ ) {
                error = this.errorList[ i ];
                if ( this.settings.highlight ) {
                    this.settings.highlight.call( this, error.element, this.settings.errorClass, this.settings.validClass );
                }
                this.showLabel( error.element, error.message );
            }
            if ( this.errorList.length ) {
                this.toShow = this.toShow.add( this.containers );
            }
            if ( this.settings.success ) {
                for ( i = 0; this.successList[ i ]; i++ ) {
                    this.showLabel( this.successList[ i ] );
                }
            }
            if ( this.settings.unhighlight ) {
                for ( i = 0, elements = this.validElements(); elements[ i ]; i++ ) {
                    this.settings.unhighlight.call( this, elements[ i ], this.settings.errorClass, this.settings.validClass );
                }
            }
            this.toHide = this.toHide.not( this.toShow );
            this.hideErrors();
            this.addWrapper( this.toShow ).show();
        },

        validElements: function() {
            return this.currentElements.not( this.invalidElements() );
        },

        invalidElements: function() {
            return $( this.errorList ).map( function() {
                return this.element;
            } );
        },

        showLabel: function( element, message ) {
            var place, group, errorID, v,
                error = this.errorsFor( element ),
                elementID = this.idOrName( element ),
                describedBy = $( element ).attr( "aria-describedby" );

            if ( error.length ) {

                // Refresh error/success class
                error.removeClass( this.settings.validClass ).addClass( this.settings.errorClass );

                // Replace message on existing label
                error.html( message );
            } else {

                // Create error element
                error = $( "<" + this.settings.errorElement + ">" )
                    .attr( "id", elementID + "-error" )
                    .addClass( this.settings.errorClass )
                    .html( message || "" );

                // Maintain reference to the element to be placed into the DOM
                place = error;
                if ( this.settings.wrapper ) {

                    // Make sure the element is visible, even in IE
                    // actually showing the wrapped element is handled elsewhere
                    place = error.hide().show().wrap( "<" + this.settings.wrapper + "/>" ).parent();
                }
                if ( this.labelContainer.length ) {
                    this.labelContainer.append( place );
                } else if ( this.settings.errorPlacement ) {
                    this.settings.errorPlacement.call( this, place, $( element ) );
                } else {
                    place.insertAfter( element );
                }

                // Link error back to the element
                if ( error.is( "label" ) ) {

                    // If the error is a label, then associate using 'for'
                    error.attr( "for", elementID );

                    // If the element is not a child of an associated label, then it's necessary
                    // to explicitly apply aria-describedby
                } else if ( error.parents( "label[for='" + this.escapeCssMeta( elementID ) + "']" ).length === 0 ) {
                    errorID = error.attr( "id" );

                    // Respect existing non-error aria-describedby
                    if ( !describedBy ) {
                        describedBy = errorID;
                    } else if ( !describedBy.match( new RegExp( "\\b" + this.escapeCssMeta( errorID ) + "\\b" ) ) ) {

                        // Add to end of list if not already present
                        describedBy += " " + errorID;
                    }
                    $( element ).attr( "aria-describedby", describedBy );

                    // If this element is grouped, then assign to all elements in the same group
                    group = this.groups[ element.name ];
                    if ( group ) {
                        v = this;
                        $.each( v.groups, function( name, testgroup ) {
                            if ( testgroup === group ) {
                                $( "[name='" + v.escapeCssMeta( name ) + "']", v.currentForm )
                                    .attr( "aria-describedby", error.attr( "id" ) );
                            }
                        } );
                    }
                }
            }
            if ( !message && this.settings.success ) {
                error.text( "" );
                if ( typeof this.settings.success === "string" ) {
                    error.addClass( this.settings.success );
                } else {
                    this.settings.success( error, element );
                }
            }
            this.toShow = this.toShow.add( error );
        },

        errorsFor: function( element ) {
            var name = this.escapeCssMeta( this.idOrName( element ) ),
                describer = $( element ).attr( "aria-describedby" ),
                selector = "label[for='" + name + "'], label[for='" + name + "'] *";

            // 'aria-describedby' should directly reference the error element
            if ( describer ) {
                selector = selector + ", #" + this.escapeCssMeta( describer )
                    .replace( /\s+/g, ", #" );
            }

            return this
                .errors()
                .filter( selector );
        },

        // See https://api.jquery.com/category/selectors/, for CSS
        // meta-characters that should be escaped in order to be used with JQuery
        // as a literal part of a name/id or any selector.
        escapeCssMeta: function( string ) {
            return string.replace( /([\\!"#$%&'()*+,./:;<=>?@\[\]^`{|}~])/g, "\\$1" );
        },

        idOrName: function( element ) {
            return this.groups[ element.name ] || ( this.checkable( element ) ? element.name : element.id || element.name );
        },

        validationTargetFor: function( element ) {

            // If radio/checkbox, validate first element in group instead
            if ( this.checkable( element ) ) {
                element = this.findByName( element.name );
            }

            // Always apply ignore filter
            return $( element ).not( this.settings.ignore )[ 0 ];
        },

        checkable: function( element ) {
            return ( /radio|checkbox/i ).test( element.type );
        },

        findByName: function( name ) {
            return $( this.currentForm ).find( "[name='" + this.escapeCssMeta( name ) + "']" );
        },

        getLength: function( value, element ) {
            switch ( element.nodeName.toLowerCase() ) {
            case "select":
                return $( "option:selected", element ).length;
            case "input":
                if ( this.checkable( element ) ) {
                    return this.findByName( element.name ).filter( ":checked" ).length;
                }
            }
            return value.length;
        },

        depend: function( param, element ) {
            return this.dependTypes[ typeof param ] ? this.dependTypes[ typeof param ]( param, element ) : true;
        },

        dependTypes: {
            "boolean": function( param ) {
                return param;
            },
            "string": function( param, element ) {
                return !!$( param, element.form ).length;
            },
            "function": function( param, element ) {
                return param( element );
            }
        },

        optional: function( element ) {
            var val = this.elementValue( element );
            return !$.validator.methods.required.call( this, val, element ) && "dependency-mismatch";
        },

        startRequest: function( element ) {
            if ( !this.pending[ element.name ] ) {
                this.pendingRequest++;
                $( element ).addClass( this.settings.pendingClass );
                this.pending[ element.name ] = true;
            }
        },

        stopRequest: function( element, valid ) {
            this.pendingRequest--;

            // Sometimes synchronization fails, make sure pendingRequest is never < 0
            if ( this.pendingRequest < 0 ) {
                this.pendingRequest = 0;
            }
            delete this.pending[ element.name ];
            $( element ).removeClass( this.settings.pendingClass );
            if ( valid && this.pendingRequest === 0 && this.formSubmitted && this.form() ) {
                $( this.currentForm ).submit();
                this.formSubmitted = false;
            } else if ( !valid && this.pendingRequest === 0 && this.formSubmitted ) {
                $( this.currentForm ).triggerHandler( "invalid-form", [ this ] );
                this.formSubmitted = false;
            }
        },

        previousValue: function( element, method ) {
            method = typeof method === "string" && method || "remote";

            return $.data( element, "previousValue" ) || $.data( element, "previousValue", {
                old: null,
                valid: true,
                message: this.defaultMessage( element, { method: method } )
            } );
        },

        // Cleans up all forms and elements, removes validator-specific events
        destroy: function() {
            this.resetForm();

            $( this.currentForm )
                .off( ".validate" )
                .removeData( "validator" )
                .find( ".validate-equalTo-blur" )
                    .off( ".validate-equalTo" )
                    .removeClass( "validate-equalTo-blur" );
        }

    },

    classRuleSettings: {
        required: { required: true },
        email: { email: true },
        url: { url: true },
        date: { date: true },
        dateISO: { dateISO: true },
        number: { number: true },
        digits: { digits: true },
        creditcard: { creditcard: true }
    },

    addClassRules: function( className, rules ) {
        if ( className.constructor === String ) {
            this.classRuleSettings[ className ] = rules;
        } else {
            $.extend( this.classRuleSettings, className );
        }
    },

    classRules: function( element ) {
        var rules = {},
            classes = $( element ).attr( "class" );

        if ( classes ) {
            $.each( classes.split( " " ), function() {
                if ( this in $.validator.classRuleSettings ) {
                    $.extend( rules, $.validator.classRuleSettings[ this ] );
                }
            } );
        }
        return rules;
    },

    normalizeAttributeRule: function( rules, type, method, value ) {

        // Convert the value to a number for number inputs, and for text for backwards compability
        // allows type="date" and others to be compared as strings
        if ( /min|max|step/.test( method ) && ( type === null || /number|range|text/.test( type ) ) ) {
            value = Number( value );

            // Support Opera Mini, which returns NaN for undefined minlength
            if ( isNaN( value ) ) {
                value = undefined;
            }
        }

        if ( value || value === 0 ) {
            rules[ method ] = value;
        } else if ( type === method && type !== "range" ) {

            // Exception: the jquery validate 'range' method
            // does not test for the html5 'range' type
            rules[ method ] = true;
        }
    },

    attributeRules: function( element ) {
        var rules = {},
            $element = $( element ),
            type = element.getAttribute( "type" ),
            method, value;

        for ( method in $.validator.methods ) {

            // Support for <input required> in both html5 and older browsers
            if ( method === "required" ) {
                value = element.getAttribute( method );

                // Some browsers return an empty string for the required attribute
                // and non-HTML5 browsers might have required="" markup
                if ( value === "" ) {
                    value = true;
                }

                // Force non-HTML5 browsers to return bool
                value = !!value;
            } else {
                value = $element.attr( method );
            }

            this.normalizeAttributeRule( rules, type, method, value );
        }

        // 'maxlength' may be returned as -1, 2147483647 ( IE ) and 524288 ( safari ) for text inputs
        if ( rules.maxlength && /-1|2147483647|524288/.test( rules.maxlength ) ) {
            delete rules.maxlength;
        }

        return rules;
    },

    dataRules: function( element ) {
        var rules = {},
            $element = $( element ),
            type = element.getAttribute( "type" ),
            method, value;

        for ( method in $.validator.methods ) {
            value = $element.data( "rule" + method.charAt( 0 ).toUpperCase() + method.substring( 1 ).toLowerCase() );
            this.normalizeAttributeRule( rules, type, method, value );
        }
        return rules;
    },

    staticRules: function( element ) {
        var rules = {},
            validator = $.data( element.form, "validator" );

        if ( validator.settings.rules ) {
            rules = $.validator.normalizeRule( validator.settings.rules[ element.name ] ) || {};
        }
        return rules;
    },

    normalizeRules: function( rules, element ) {

        // Handle dependency check
        $.each( rules, function( prop, val ) {

            // Ignore rule when param is explicitly false, eg. required:false
            if ( val === false ) {
                delete rules[ prop ];
                return;
            }
            if ( val.param || val.depends ) {
                var keepRule = true;
                switch ( typeof val.depends ) {
                case "string":
                    keepRule = !!$( val.depends, element.form ).length;
                    break;
                case "function":
                    keepRule = val.depends.call( element, element );
                    break;
                }
                if ( keepRule ) {
                    rules[ prop ] = val.param !== undefined ? val.param : true;
                } else {
                    $.data( element.form, "validator" ).resetElements( $( element ) );
                    delete rules[ prop ];
                }
            }
        } );

        // Evaluate parameters
        $.each( rules, function( rule, parameter ) {
            rules[ rule ] = $.isFunction( parameter ) && rule !== "normalizer" ? parameter( element ) : parameter;
        } );

        // Clean number parameters
        $.each( [ "minlength", "maxlength" ], function() {
            if ( rules[ this ] ) {
                rules[ this ] = Number( rules[ this ] );
            }
        } );
        $.each( [ "rangelength", "range" ], function() {
            var parts;
            if ( rules[ this ] ) {
                if ( $.isArray( rules[ this ] ) ) {
                    rules[ this ] = [ Number( rules[ this ][ 0 ] ), Number( rules[ this ][ 1 ] ) ];
                } else if ( typeof rules[ this ] === "string" ) {
                    parts = rules[ this ].replace( /[\[\]]/g, "" ).split( /[\s,]+/ );
                    rules[ this ] = [ Number( parts[ 0 ] ), Number( parts[ 1 ] ) ];
                }
            }
        } );

        if ( $.validator.autoCreateRanges ) {

            // Auto-create ranges
            if ( rules.min != null && rules.max != null ) {
                rules.range = [ rules.min, rules.max ];
                delete rules.min;
                delete rules.max;
            }
            if ( rules.minlength != null && rules.maxlength != null ) {
                rules.rangelength = [ rules.minlength, rules.maxlength ];
                delete rules.minlength;
                delete rules.maxlength;
            }
        }

        return rules;
    },

    // Converts a simple string to a {string: true} rule, e.g., "required" to {required:true}
    normalizeRule: function( data ) {
        if ( typeof data === "string" ) {
            var transformed = {};
            $.each( data.split( /\s/ ), function() {
                transformed[ this ] = true;
            } );
            data = transformed;
        }
        return data;
    },

    // http://jqueryvalidation.org/jQuery.validator.addMethod/
    addMethod: function( name, method, message ) {
        $.validator.methods[ name ] = method;
        $.validator.messages[ name ] = message !== undefined ? message : $.validator.messages[ name ];
        if ( method.length < 3 ) {
            $.validator.addClassRules( name, $.validator.normalizeRule( name ) );
        }
    },

    // http://jqueryvalidation.org/jQuery.validator.methods/
    methods: {

        // http://jqueryvalidation.org/required-method/
        required: function( value, element, param ) {

            // Check if dependency is met
            if ( !this.depend( param, element ) ) {
                return "dependency-mismatch";
            }
            if ( element.nodeName.toLowerCase() === "select" ) {

                // Could be an array for select-multiple or a string, both are fine this way
                var val = $( element ).val();
                return val && val.length > 0;
            }
            if ( this.checkable( element ) ) {
                return this.getLength( value, element ) > 0;
            }
            return value.length > 0;
        },

        // http://jqueryvalidation.org/email-method/
        email: function( value, element ) {

            // From https://html.spec.whatwg.org/multipage/forms.html#valid-e-mail-address
            // Retrieved 2014-01-14
            // If you have a problem with this implementation, report a bug against the above spec
            // Or use custom methods to implement your own email validation
            return this.optional( element ) || /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test( value );
        },

        // http://jqueryvalidation.org/url-method/
        url: function( value, element ) {

            // Copyright (c) 2010-2013 Diego Perini, MIT licensed
            // https://gist.github.com/dperini/729294
            // see also https://mathiasbynens.be/demo/url-regex
            // modified to allow protocol-relative URLs
            return this.optional( element ) || /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})).?)(?::\d{2,5})?(?:[/?#]\S*)?$/i.test( value );
        },

        // http://jqueryvalidation.org/date-method/
        date: function( value, element ) {
            return this.optional( element ) || !/Invalid|NaN/.test( new Date( value ).toString() );
        },

        // http://jqueryvalidation.org/dateISO-method/
        dateISO: function( value, element ) {
            return this.optional( element ) || /^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])$/.test( value );
        },

        // http://jqueryvalidation.org/number-method/
        number: function( value, element ) {
            return this.optional( element ) || /^(?:-?\d+|-?\d{1,3}(?:,\d{3})+)?(?:\.\d+)?$/.test( value );
        },

        // http://jqueryvalidation.org/digits-method/
        digits: function( value, element ) {
            return this.optional( element ) || /^\d+$/.test( value );
        },

        // http://jqueryvalidation.org/minlength-method/
        minlength: function( value, element, param ) {
            var length = $.isArray( value ) ? value.length : this.getLength( value, element );
            return this.optional( element ) || length >= param;
        },

        // http://jqueryvalidation.org/maxlength-method/
        maxlength: function( value, element, param ) {
            var length = $.isArray( value ) ? value.length : this.getLength( value, element );
            return this.optional( element ) || length <= param;
        },

        // http://jqueryvalidation.org/rangelength-method/
        rangelength: function( value, element, param ) {
            var length = $.isArray( value ) ? value.length : this.getLength( value, element );
            return this.optional( element ) || ( length >= param[ 0 ] && length <= param[ 1 ] );
        },

        // http://jqueryvalidation.org/min-method/
        min: function( value, element, param ) {
            return this.optional( element ) || value >= param;
        },

        // http://jqueryvalidation.org/max-method/
        max: function( value, element, param ) {
            return this.optional( element ) || value <= param;
        },

        // http://jqueryvalidation.org/range-method/
        range: function( value, element, param ) {
            return this.optional( element ) || ( value >= param[ 0 ] && value <= param[ 1 ] );
        },

        // http://jqueryvalidation.org/step-method/
        step: function( value, element, param ) {
            var type = $( element ).attr( "type" ),
                errorMessage = "Step attribute on input type " + type + " is not supported.",
                supportedTypes = [ "text", "number", "range" ],
                re = new RegExp( "\\b" + type + "\\b" ),
                notSupported = type && !re.test( supportedTypes.join() ),
                decimalPlaces = function( num ) {
                    var match = ( "" + num ).match( /(?:\.(\d+))?$/ );
                    if ( !match ) {
                        return 0;
                    }

                    // Number of digits right of decimal point.
                    return match[ 1 ] ? match[ 1 ].length : 0;
                },
                toInt = function( num ) {
                    return Math.round( num * Math.pow( 10, decimals ) );
                },
                valid = true,
                decimals;

            // Works only for text, number and range input types
            // TODO find a way to support input types date, datetime, datetime-local, month, time and week
            if ( notSupported ) {
                throw new Error( errorMessage );
            }

            decimals = decimalPlaces( param );

            // Value can't have too many decimals
            if ( decimalPlaces( value ) > decimals || toInt( value ) % toInt( param ) !== 0 ) {
                valid = false;
            }

            return this.optional( element ) || valid;
        },

        // http://jqueryvalidation.org/equalTo-method/
        equalTo: function( value, element, param ) {

            // Bind to the blur event of the target in order to revalidate whenever the target field is updated
            var target = $( param );
            if ( this.settings.onfocusout && target.not( ".validate-equalTo-blur" ).length ) {
                target.addClass( "validate-equalTo-blur" ).on( "blur.validate-equalTo", function() {
                    $( element ).valid();
                } );
            }
            return value === target.val();
        },

        // http://jqueryvalidation.org/remote-method/
        remote: function( value, element, param, method ) {
            if ( this.optional( element ) ) {
                return "dependency-mismatch";
            }

            method = typeof method === "string" && method || "remote";

            var previous = this.previousValue( element, method ),
                validator, data, optionDataString;

            if ( !this.settings.messages[ element.name ] ) {
                this.settings.messages[ element.name ] = {};
            }
            previous.originalMessage = previous.originalMessage || this.settings.messages[ element.name ][ method ];
            this.settings.messages[ element.name ][ method ] = previous.message;

            param = typeof param === "string" && { url: param } || param;
            optionDataString = $.param( $.extend( { data: value }, param.data ) );
            if ( previous.old === optionDataString ) {
                return previous.valid;
            }

            previous.old = optionDataString;
            validator = this;
            this.startRequest( element );
            data = {};
            data[ element.name ] = value;
            $.ajax( $.extend( true, {
                mode: "abort",
                port: "validate" + element.name,
                dataType: "json",
                data: data,
                context: validator.currentForm,
                success: function( response ) {
                    var valid = response === true || response === "true",
                        errors, message, submitted;

                    validator.settings.messages[ element.name ][ method ] = previous.originalMessage;
                    if ( valid ) {
                        submitted = validator.formSubmitted;
                        validator.resetInternals();
                        validator.toHide = validator.errorsFor( element );
                        validator.formSubmitted = submitted;
                        validator.successList.push( element );
                        validator.invalid[ element.name ] = false;
                        validator.showErrors();
                    } else {
                        errors = {};
                        message = response || validator.defaultMessage( element, { method: method, parameters: value } );
                        errors[ element.name ] = previous.message = message;
                        validator.invalid[ element.name ] = true;
                        validator.showErrors( errors );
                    }
                    previous.valid = valid;
                    validator.stopRequest( element, valid );
                }
            }, param ) );
            return "pending";
        }
    }

} );

// Ajax mode: abort
// usage: $.ajax({ mode: "abort"[, port: "uniqueport"]});
// if mode:"abort" is used, the previous request on that port (port can be undefined) is aborted via XMLHttpRequest.abort()

var pendingRequests = {},
    ajax;

// Use a prefilter if available (1.5+)
if ( $.ajaxPrefilter ) {
    $.ajaxPrefilter( function( settings, _, xhr ) {
        var port = settings.port;
        if ( settings.mode === "abort" ) {
            if ( pendingRequests[ port ] ) {
                pendingRequests[ port ].abort();
            }
            pendingRequests[ port ] = xhr;
        }
    } );
} else {

    // Proxy ajax
    ajax = $.ajax;
    $.ajax = function( settings ) {
        var mode = ( "mode" in settings ? settings : $.ajaxSettings ).mode,
            port = ( "port" in settings ? settings : $.ajaxSettings ).port;
        if ( mode === "abort" ) {
            if ( pendingRequests[ port ] ) {
                pendingRequests[ port ].abort();
            }
            pendingRequests[ port ] = ajax.apply( this, arguments );
            return pendingRequests[ port ];
        }
        return ajax.apply( this, arguments );
    };
}
return $;
}));
/*! tether 1.4.0 */


(function(root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(factory);
  } else if (typeof exports === 'object') {
    module.exports = factory(require, exports, module);
  } else {
    root.Tether = factory();
  }
}(this, function(require, exports, module) {

'use strict';

var _createClass = (function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ('value' in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; })();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError('Cannot call a class as a function'); } }

var TetherBase = undefined;
if (typeof TetherBase === 'undefined') {
  TetherBase = { modules: [] };
}

var zeroElement = null;

// Same as native getBoundingClientRect, except it takes into account parent <frame> offsets
// if the element lies within a nested document (<frame> or <iframe>-like).
function getActualBoundingClientRect(node) {
  var boundingRect = node.getBoundingClientRect();

  // The original object returned by getBoundingClientRect is immutable, so we clone it
  // We can't use extend because the properties are not considered part of the object by hasOwnProperty in IE9
  var rect = {};
  for (var k in boundingRect) {
    rect[k] = boundingRect[k];
  }

  if (node.ownerDocument !== document) {
    var _frameElement = node.ownerDocument.defaultView.frameElement;
    if (_frameElement) {
      var frameRect = getActualBoundingClientRect(_frameElement);
      rect.top += frameRect.top;
      rect.bottom += frameRect.top;
      rect.left += frameRect.left;
      rect.right += frameRect.left;
    }
  }

  return rect;
}

function getScrollParents(el) {
  // In firefox if the el is inside an iframe with display: none; window.getComputedStyle() will return null;
  // https://bugzilla.mozilla.org/show_bug.cgi?id=548397
  var computedStyle = getComputedStyle(el) || {};
  var position = computedStyle.position;
  var parents = [];

  if (position === 'fixed') {
    return [el];
  }

  var parent = el;
  while ((parent = parent.parentNode) && parent && parent.nodeType === 1) {
    var style = undefined;
    try {
      style = getComputedStyle(parent);
    } catch (err) {}

    if (typeof style === 'undefined' || style === null) {
      parents.push(parent);
      return parents;
    }

    var _style = style;
    var overflow = _style.overflow;
    var overflowX = _style.overflowX;
    var overflowY = _style.overflowY;

    if (/(auto|scroll)/.test(overflow + overflowY + overflowX)) {
      if (position !== 'absolute' || ['relative', 'absolute', 'fixed'].indexOf(style.position) >= 0) {
        parents.push(parent);
      }
    }
  }

  parents.push(el.ownerDocument.body);

  // If the node is within a frame, account for the parent window scroll
  if (el.ownerDocument !== document) {
    parents.push(el.ownerDocument.defaultView);
  }

  return parents;
}

var uniqueId = (function () {
  var id = 0;
  return function () {
    return ++id;
  };
})();

var zeroPosCache = {};
var getOrigin = function getOrigin() {
  // getBoundingClientRect is unfortunately too accurate.  It introduces a pixel or two of
  // jitter as the user scrolls that messes with our ability to detect if two positions
  // are equivilant or not.  We place an element at the top left of the page that will
  // get the same jitter, so we can cancel the two out.
  var node = zeroElement;
  if (!node || !document.body.contains(node)) {
    node = document.createElement('div');
    node.setAttribute('data-tether-id', uniqueId());
    extend(node.style, {
      top: 0,
      left: 0,
      position: 'absolute'
    });

    document.body.appendChild(node);

    zeroElement = node;
  }

  var id = node.getAttribute('data-tether-id');
  if (typeof zeroPosCache[id] === 'undefined') {
    zeroPosCache[id] = getActualBoundingClientRect(node);

    // Clear the cache when this position call is done
    defer(function () {
      delete zeroPosCache[id];
    });
  }

  return zeroPosCache[id];
};

function removeUtilElements() {
  if (zeroElement) {
    document.body.removeChild(zeroElement);
  }
  zeroElement = null;
};

function getBounds(el) {
  var doc = undefined;
  if (el === document) {
    doc = document;
    el = document.documentElement;
  } else {
    doc = el.ownerDocument;
  }

  var docEl = doc.documentElement;

  var box = getActualBoundingClientRect(el);

  var origin = getOrigin();

  box.top -= origin.top;
  box.left -= origin.left;

  if (typeof box.width === 'undefined') {
    box.width = document.body.scrollWidth - box.left - box.right;
  }
  if (typeof box.height === 'undefined') {
    box.height = document.body.scrollHeight - box.top - box.bottom;
  }

  box.top = box.top - docEl.clientTop;
  box.left = box.left - docEl.clientLeft;
  box.right = doc.body.clientWidth - box.width - box.left;
  box.bottom = doc.body.clientHeight - box.height - box.top;

  return box;
}

function getOffsetParent(el) {
  return el.offsetParent || document.documentElement;
}

var _scrollBarSize = null;
function getScrollBarSize() {
  if (_scrollBarSize) {
    return _scrollBarSize;
  }
  var inner = document.createElement('div');
  inner.style.width = '100%';
  inner.style.height = '200px';

  var outer = document.createElement('div');
  extend(outer.style, {
    position: 'absolute',
    top: 0,
    left: 0,
    pointerEvents: 'none',
    visibility: 'hidden',
    width: '200px',
    height: '150px',
    overflow: 'hidden'
  });

  outer.appendChild(inner);

  document.body.appendChild(outer);

  var widthContained = inner.offsetWidth;
  outer.style.overflow = 'scroll';
  var widthScroll = inner.offsetWidth;

  if (widthContained === widthScroll) {
    widthScroll = outer.clientWidth;
  }

  document.body.removeChild(outer);

  var width = widthContained - widthScroll;

  _scrollBarSize = { width: width, height: width };
  return _scrollBarSize;
}

function extend() {
  var out = arguments.length <= 0 || arguments[0] === undefined ? {} : arguments[0];

  var args = [];

  Array.prototype.push.apply(args, arguments);

  args.slice(1).forEach(function (obj) {
    if (obj) {
      for (var key in obj) {
        if (({}).hasOwnProperty.call(obj, key)) {
          out[key] = obj[key];
        }
      }
    }
  });

  return out;
}

function removeClass(el, name) {
  if (typeof el.classList !== 'undefined') {
    name.split(' ').forEach(function (cls) {
      if (cls.trim()) {
        el.classList.remove(cls);
      }
    });
  } else {
    var regex = new RegExp('(^| )' + name.split(' ').join('|') + '( |$)', 'gi');
    var className = getClassName(el).replace(regex, ' ');
    setClassName(el, className);
  }
}

function addClass(el, name) {
  if (typeof el.classList !== 'undefined') {
    name.split(' ').forEach(function (cls) {
      if (cls.trim()) {
        el.classList.add(cls);
      }
    });
  } else {
    removeClass(el, name);
    var cls = getClassName(el) + (' ' + name);
    setClassName(el, cls);
  }
}

function hasClass(el, name) {
  if (typeof el.classList !== 'undefined') {
    return el.classList.contains(name);
  }
  var className = getClassName(el);
  return new RegExp('(^| )' + name + '( |$)', 'gi').test(className);
}

function getClassName(el) {
  // Can't use just SVGAnimatedString here since nodes within a Frame in IE have
  // completely separately SVGAnimatedString base classes
  if (el.className instanceof el.ownerDocument.defaultView.SVGAnimatedString) {
    return el.className.baseVal;
  }
  return el.className;
}

function setClassName(el, className) {
  el.setAttribute('class', className);
}

function updateClasses(el, add, all) {
  // Of the set of 'all' classes, we need the 'add' classes, and only the
  // 'add' classes to be set.
  all.forEach(function (cls) {
    if (add.indexOf(cls) === -1 && hasClass(el, cls)) {
      removeClass(el, cls);
    }
  });

  add.forEach(function (cls) {
    if (!hasClass(el, cls)) {
      addClass(el, cls);
    }
  });
}

var deferred = [];

var defer = function defer(fn) {
  deferred.push(fn);
};

var flush = function flush() {
  var fn = undefined;
  while (fn = deferred.pop()) {
    fn();
  }
};

var Evented = (function () {
  function Evented() {
    _classCallCheck(this, Evented);
  }

  _createClass(Evented, [{
    key: 'on',
    value: function on(event, handler, ctx) {
      var once = arguments.length <= 3 || arguments[3] === undefined ? false : arguments[3];

      if (typeof this.bindings === 'undefined') {
        this.bindings = {};
      }
      if (typeof this.bindings[event] === 'undefined') {
        this.bindings[event] = [];
      }
      this.bindings[event].push({ handler: handler, ctx: ctx, once: once });
    }
  }, {
    key: 'once',
    value: function once(event, handler, ctx) {
      this.on(event, handler, ctx, true);
    }
  }, {
    key: 'off',
    value: function off(event, handler) {
      if (typeof this.bindings === 'undefined' || typeof this.bindings[event] === 'undefined') {
        return;
      }

      if (typeof handler === 'undefined') {
        delete this.bindings[event];
      } else {
        var i = 0;
        while (i < this.bindings[event].length) {
          if (this.bindings[event][i].handler === handler) {
            this.bindings[event].splice(i, 1);
          } else {
            ++i;
          }
        }
      }
    }
  }, {
    key: 'trigger',
    value: function trigger(event) {
      if (typeof this.bindings !== 'undefined' && this.bindings[event]) {
        var i = 0;

        for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        while (i < this.bindings[event].length) {
          var _bindings$event$i = this.bindings[event][i];
          var handler = _bindings$event$i.handler;
          var ctx = _bindings$event$i.ctx;
          var once = _bindings$event$i.once;

          var context = ctx;
          if (typeof context === 'undefined') {
            context = this;
          }

          handler.apply(context, args);

          if (once) {
            this.bindings[event].splice(i, 1);
          } else {
            ++i;
          }
        }
      }
    }
  }]);

  return Evented;
})();

TetherBase.Utils = {
  getActualBoundingClientRect: getActualBoundingClientRect,
  getScrollParents: getScrollParents,
  getBounds: getBounds,
  getOffsetParent: getOffsetParent,
  extend: extend,
  addClass: addClass,
  removeClass: removeClass,
  hasClass: hasClass,
  updateClasses: updateClasses,
  defer: defer,
  flush: flush,
  uniqueId: uniqueId,
  Evented: Evented,
  getScrollBarSize: getScrollBarSize,
  removeUtilElements: removeUtilElements
};
/* globals TetherBase, performance */

'use strict';

var _slicedToArray = (function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i['return']) _i['return'](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError('Invalid attempt to destructure non-iterable instance'); } }; })();

var _createClass = (function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ('value' in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; })();

var _get = function get(_x6, _x7, _x8) { var _again = true; _function: while (_again) { var object = _x6, property = _x7, receiver = _x8; _again = false; if (object === null) object = Function.prototype; var desc = Object.getOwnPropertyDescriptor(object, property); if (desc === undefined) { var parent = Object.getPrototypeOf(object); if (parent === null) { return undefined; } else { _x6 = parent; _x7 = property; _x8 = receiver; _again = true; desc = parent = undefined; continue _function; } } else if ('value' in desc) { return desc.value; } else { var getter = desc.get; if (getter === undefined) { return undefined; } return getter.call(receiver); } } };

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError('Cannot call a class as a function'); } }

function _inherits(subClass, superClass) { if (typeof superClass !== 'function' && superClass !== null) { throw new TypeError('Super expression must either be null or a function, not ' + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

if (typeof TetherBase === 'undefined') {
  throw new Error('You must include the utils.js file before tether.js');
}

var _TetherBase$Utils = TetherBase.Utils;
var getScrollParents = _TetherBase$Utils.getScrollParents;
var getBounds = _TetherBase$Utils.getBounds;
var getOffsetParent = _TetherBase$Utils.getOffsetParent;
var extend = _TetherBase$Utils.extend;
var addClass = _TetherBase$Utils.addClass;
var removeClass = _TetherBase$Utils.removeClass;
var updateClasses = _TetherBase$Utils.updateClasses;
var defer = _TetherBase$Utils.defer;
var flush = _TetherBase$Utils.flush;
var getScrollBarSize = _TetherBase$Utils.getScrollBarSize;
var removeUtilElements = _TetherBase$Utils.removeUtilElements;

function within(a, b) {
  var diff = arguments.length <= 2 || arguments[2] === undefined ? 1 : arguments[2];

  return a + diff >= b && b >= a - diff;
}

var transformKey = (function () {
  if (typeof document === 'undefined') {
    return '';
  }
  var el = document.createElement('div');

  var transforms = ['transform', 'WebkitTransform', 'OTransform', 'MozTransform', 'msTransform'];
  for (var i = 0; i < transforms.length; ++i) {
    var key = transforms[i];
    if (el.style[key] !== undefined) {
      return key;
    }
  }
})();

var tethers = [];

var position = function position() {
  tethers.forEach(function (tether) {
    tether.position(false);
  });
  flush();
};

function now() {
  if (typeof performance !== 'undefined' && typeof performance.now !== 'undefined') {
    return performance.now();
  }
  return +new Date();
}

(function () {
  var lastCall = null;
  var lastDuration = null;
  var pendingTimeout = null;

  var tick = function tick() {
    if (typeof lastDuration !== 'undefined' && lastDuration > 16) {
      // We voluntarily throttle ourselves if we can't manage 60fps
      lastDuration = Math.min(lastDuration - 16, 250);

      // Just in case this is the last event, remember to position just once more
      pendingTimeout = setTimeout(tick, 250);
      return;
    }

    if (typeof lastCall !== 'undefined' && now() - lastCall < 10) {
      // Some browsers call events a little too frequently, refuse to run more than is reasonable
      return;
    }

    if (pendingTimeout != null) {
      clearTimeout(pendingTimeout);
      pendingTimeout = null;
    }

    lastCall = now();
    position();
    lastDuration = now() - lastCall;
  };

  if (typeof window !== 'undefined' && typeof window.addEventListener !== 'undefined') {
    ['resize', 'scroll', 'touchmove'].forEach(function (event) {
      window.addEventListener(event, tick);
    });
  }
})();

var MIRROR_LR = {
  center: 'center',
  left: 'right',
  right: 'left'
};

var MIRROR_TB = {
  middle: 'middle',
  top: 'bottom',
  bottom: 'top'
};

var OFFSET_MAP = {
  top: 0,
  left: 0,
  middle: '50%',
  center: '50%',
  bottom: '100%',
  right: '100%'
};

var autoToFixedAttachment = function autoToFixedAttachment(attachment, relativeToAttachment) {
  var left = attachment.left;
  var top = attachment.top;

  if (left === 'auto') {
    left = MIRROR_LR[relativeToAttachment.left];
  }

  if (top === 'auto') {
    top = MIRROR_TB[relativeToAttachment.top];
  }

  return { left: left, top: top };
};

var attachmentToOffset = function attachmentToOffset(attachment) {
  var left = attachment.left;
  var top = attachment.top;

  if (typeof OFFSET_MAP[attachment.left] !== 'undefined') {
    left = OFFSET_MAP[attachment.left];
  }

  if (typeof OFFSET_MAP[attachment.top] !== 'undefined') {
    top = OFFSET_MAP[attachment.top];
  }

  return { left: left, top: top };
};

function addOffset() {
  var out = { top: 0, left: 0 };

  for (var _len = arguments.length, offsets = Array(_len), _key = 0; _key < _len; _key++) {
    offsets[_key] = arguments[_key];
  }

  offsets.forEach(function (_ref) {
    var top = _ref.top;
    var left = _ref.left;

    if (typeof top === 'string') {
      top = parseFloat(top, 10);
    }
    if (typeof left === 'string') {
      left = parseFloat(left, 10);
    }

    out.top += top;
    out.left += left;
  });

  return out;
}

function offsetToPx(offset, size) {
  if (typeof offset.left === 'string' && offset.left.indexOf('%') !== -1) {
    offset.left = parseFloat(offset.left, 10) / 100 * size.width;
  }
  if (typeof offset.top === 'string' && offset.top.indexOf('%') !== -1) {
    offset.top = parseFloat(offset.top, 10) / 100 * size.height;
  }

  return offset;
}

var parseOffset = function parseOffset(value) {
  var _value$split = value.split(' ');

  var _value$split2 = _slicedToArray(_value$split, 2);

  var top = _value$split2[0];
  var left = _value$split2[1];

  return { top: top, left: left };
};
var parseAttachment = parseOffset;

var TetherClass = (function (_Evented) {
  _inherits(TetherClass, _Evented);

  function TetherClass(options) {
    var _this = this;

    _classCallCheck(this, TetherClass);

    _get(Object.getPrototypeOf(TetherClass.prototype), 'constructor', this).call(this);
    this.position = this.position.bind(this);

    tethers.push(this);

    this.history = [];

    this.setOptions(options, false);

    TetherBase.modules.forEach(function (module) {
      if (typeof module.initialize !== 'undefined') {
        module.initialize.call(_this);
      }
    });

    this.position();
  }

  _createClass(TetherClass, [{
    key: 'getClass',
    value: function getClass() {
      var key = arguments.length <= 0 || arguments[0] === undefined ? '' : arguments[0];
      var classes = this.options.classes;

      if (typeof classes !== 'undefined' && classes[key]) {
        return this.options.classes[key];
      } else if (this.options.classPrefix) {
        return this.options.classPrefix + '-' + key;
      } else {
        return key;
      }
    }
  }, {
    key: 'setOptions',
    value: function setOptions(options) {
      var _this2 = this;

      var pos = arguments.length <= 1 || arguments[1] === undefined ? true : arguments[1];

      var defaults = {
        offset: '0 0',
        targetOffset: '0 0',
        targetAttachment: 'auto auto',
        classPrefix: 'tether'
      };

      this.options = extend(defaults, options);

      var _options = this.options;
      var element = _options.element;
      var target = _options.target;
      var targetModifier = _options.targetModifier;

      this.element = element;
      this.target = target;
      this.targetModifier = targetModifier;

      if (this.target === 'viewport') {
        this.target = document.body;
        this.targetModifier = 'visible';
      } else if (this.target === 'scroll-handle') {
        this.target = document.body;
        this.targetModifier = 'scroll-handle';
      }

      ['element', 'target'].forEach(function (key) {
        if (typeof _this2[key] === 'undefined') {
          throw new Error('Tether Error: Both element and target must be defined');
        }

        if (typeof _this2[key].jquery !== 'undefined') {
          _this2[key] = _this2[key][0];
        } else if (typeof _this2[key] === 'string') {
          _this2[key] = document.querySelector(_this2[key]);
        }
      });

      addClass(this.element, this.getClass('element'));
      if (!(this.options.addTargetClasses === false)) {
        addClass(this.target, this.getClass('target'));
      }

      if (!this.options.attachment) {
        throw new Error('Tether Error: You must provide an attachment');
      }

      this.targetAttachment = parseAttachment(this.options.targetAttachment);
      this.attachment = parseAttachment(this.options.attachment);
      this.offset = parseOffset(this.options.offset);
      this.targetOffset = parseOffset(this.options.targetOffset);

      if (typeof this.scrollParents !== 'undefined') {
        this.disable();
      }

      if (this.targetModifier === 'scroll-handle') {
        this.scrollParents = [this.target];
      } else {
        this.scrollParents = getScrollParents(this.target);
      }

      if (!(this.options.enabled === false)) {
        this.enable(pos);
      }
    }
  }, {
    key: 'getTargetBounds',
    value: function getTargetBounds() {
      if (typeof this.targetModifier !== 'undefined') {
        if (this.targetModifier === 'visible') {
          if (this.target === document.body) {
            return { top: pageYOffset, left: pageXOffset, height: innerHeight, width: innerWidth };
          } else {
            var bounds = getBounds(this.target);

            var out = {
              height: bounds.height,
              width: bounds.width,
              top: bounds.top,
              left: bounds.left
            };

            out.height = Math.min(out.height, bounds.height - (pageYOffset - bounds.top));
            out.height = Math.min(out.height, bounds.height - (bounds.top + bounds.height - (pageYOffset + innerHeight)));
            out.height = Math.min(innerHeight, out.height);
            out.height -= 2;

            out.width = Math.min(out.width, bounds.width - (pageXOffset - bounds.left));
            out.width = Math.min(out.width, bounds.width - (bounds.left + bounds.width - (pageXOffset + innerWidth)));
            out.width = Math.min(innerWidth, out.width);
            out.width -= 2;

            if (out.top < pageYOffset) {
              out.top = pageYOffset;
            }
            if (out.left < pageXOffset) {
              out.left = pageXOffset;
            }

            return out;
          }
        } else if (this.targetModifier === 'scroll-handle') {
          var bounds = undefined;
          var target = this.target;
          if (target === document.body) {
            target = document.documentElement;

            bounds = {
              left: pageXOffset,
              top: pageYOffset,
              height: innerHeight,
              width: innerWidth
            };
          } else {
            bounds = getBounds(target);
          }

          var style = getComputedStyle(target);

          var hasBottomScroll = target.scrollWidth > target.clientWidth || [style.overflow, style.overflowX].indexOf('scroll') >= 0 || this.target !== document.body;

          var scrollBottom = 0;
          if (hasBottomScroll) {
            scrollBottom = 15;
          }

          var height = bounds.height - parseFloat(style.borderTopWidth) - parseFloat(style.borderBottomWidth) - scrollBottom;

          var out = {
            width: 15,
            height: height * 0.975 * (height / target.scrollHeight),
            left: bounds.left + bounds.width - parseFloat(style.borderLeftWidth) - 15
          };

          var fitAdj = 0;
          if (height < 408 && this.target === document.body) {
            fitAdj = -0.00011 * Math.pow(height, 2) - 0.00727 * height + 22.58;
          }

          if (this.target !== document.body) {
            out.height = Math.max(out.height, 24);
          }

          var scrollPercentage = this.target.scrollTop / (target.scrollHeight - height);
          out.top = scrollPercentage * (height - out.height - fitAdj) + bounds.top + parseFloat(style.borderTopWidth);

          if (this.target === document.body) {
            out.height = Math.max(out.height, 24);
          }

          return out;
        }
      } else {
        return getBounds(this.target);
      }
    }
  }, {
    key: 'clearCache',
    value: function clearCache() {
      this._cache = {};
    }
  }, {
    key: 'cache',
    value: function cache(k, getter) {
      // More than one module will often need the same DOM info, so
      // we keep a cache which is cleared on each position call
      if (typeof this._cache === 'undefined') {
        this._cache = {};
      }

      if (typeof this._cache[k] === 'undefined') {
        this._cache[k] = getter.call(this);
      }

      return this._cache[k];
    }
  }, {
    key: 'enable',
    value: function enable() {
      var _this3 = this;

      var pos = arguments.length <= 0 || arguments[0] === undefined ? true : arguments[0];

      if (!(this.options.addTargetClasses === false)) {
        addClass(this.target, this.getClass('enabled'));
      }
      addClass(this.element, this.getClass('enabled'));
      this.enabled = true;

      this.scrollParents.forEach(function (parent) {
        if (parent !== _this3.target.ownerDocument) {
          parent.addEventListener('scroll', _this3.position);
        }
      });

      if (pos) {
        this.position();
      }
    }
  }, {
    key: 'disable',
    value: function disable() {
      var _this4 = this;

      removeClass(this.target, this.getClass('enabled'));
      removeClass(this.element, this.getClass('enabled'));
      this.enabled = false;

      if (typeof this.scrollParents !== 'undefined') {
        this.scrollParents.forEach(function (parent) {
          parent.removeEventListener('scroll', _this4.position);
        });
      }
    }
  }, {
    key: 'destroy',
    value: function destroy() {
      var _this5 = this;

      this.disable();

      tethers.forEach(function (tether, i) {
        if (tether === _this5) {
          tethers.splice(i, 1);
        }
      });

      // Remove any elements we were using for convenience from the DOM
      if (tethers.length === 0) {
        removeUtilElements();
      }
    }
  }, {
    key: 'updateAttachClasses',
    value: function updateAttachClasses(elementAttach, targetAttach) {
      var _this6 = this;

      elementAttach = elementAttach || this.attachment;
      targetAttach = targetAttach || this.targetAttachment;
      var sides = ['left', 'top', 'bottom', 'right', 'middle', 'center'];

      if (typeof this._addAttachClasses !== 'undefined' && this._addAttachClasses.length) {
        // updateAttachClasses can be called more than once in a position call, so
        // we need to clean up after ourselves such that when the last defer gets
        // ran it doesn't add any extra classes from previous calls.
        this._addAttachClasses.splice(0, this._addAttachClasses.length);
      }

      if (typeof this._addAttachClasses === 'undefined') {
        this._addAttachClasses = [];
      }
      var add = this._addAttachClasses;

      if (elementAttach.top) {
        add.push(this.getClass('element-attached') + '-' + elementAttach.top);
      }
      if (elementAttach.left) {
        add.push(this.getClass('element-attached') + '-' + elementAttach.left);
      }
      if (targetAttach.top) {
        add.push(this.getClass('target-attached') + '-' + targetAttach.top);
      }
      if (targetAttach.left) {
        add.push(this.getClass('target-attached') + '-' + targetAttach.left);
      }

      var all = [];
      sides.forEach(function (side) {
        all.push(_this6.getClass('element-attached') + '-' + side);
        all.push(_this6.getClass('target-attached') + '-' + side);
      });

      defer(function () {
        if (!(typeof _this6._addAttachClasses !== 'undefined')) {
          return;
        }

        updateClasses(_this6.element, _this6._addAttachClasses, all);
        if (!(_this6.options.addTargetClasses === false)) {
          updateClasses(_this6.target, _this6._addAttachClasses, all);
        }

        delete _this6._addAttachClasses;
      });
    }
  }, {
    key: 'position',
    value: function position() {
      var _this7 = this;

      var flushChanges = arguments.length <= 0 || arguments[0] === undefined ? true : arguments[0];

      // flushChanges commits the changes immediately, leave true unless you are positioning multiple
      // tethers (in which case call Tether.Utils.flush yourself when you're done)

      if (!this.enabled) {
        return;
      }

      this.clearCache();

      // Turn 'auto' attachments into the appropriate corner or edge
      var targetAttachment = autoToFixedAttachment(this.targetAttachment, this.attachment);

      this.updateAttachClasses(this.attachment, targetAttachment);

      var elementPos = this.cache('element-bounds', function () {
        return getBounds(_this7.element);
      });

      var width = elementPos.width;
      var height = elementPos.height;

      if (width === 0 && height === 0 && typeof this.lastSize !== 'undefined') {
        var _lastSize = this.lastSize;

        // We cache the height and width to make it possible to position elements that are
        // getting hidden.
        width = _lastSize.width;
        height = _lastSize.height;
      } else {
        this.lastSize = { width: width, height: height };
      }

      var targetPos = this.cache('target-bounds', function () {
        return _this7.getTargetBounds();
      });
      var targetSize = targetPos;

      // Get an actual px offset from the attachment
      var offset = offsetToPx(attachmentToOffset(this.attachment), { width: width, height: height });
      var targetOffset = offsetToPx(attachmentToOffset(targetAttachment), targetSize);

      var manualOffset = offsetToPx(this.offset, { width: width, height: height });
      var manualTargetOffset = offsetToPx(this.targetOffset, targetSize);

      // Add the manually provided offset
      offset = addOffset(offset, manualOffset);
      targetOffset = addOffset(targetOffset, manualTargetOffset);

      // It's now our goal to make (element position + offset) == (target position + target offset)
      var left = targetPos.left + targetOffset.left - offset.left;
      var top = targetPos.top + targetOffset.top - offset.top;

      for (var i = 0; i < TetherBase.modules.length; ++i) {
        var _module2 = TetherBase.modules[i];
        var ret = _module2.position.call(this, {
          left: left,
          top: top,
          targetAttachment: targetAttachment,
          targetPos: targetPos,
          elementPos: elementPos,
          offset: offset,
          targetOffset: targetOffset,
          manualOffset: manualOffset,
          manualTargetOffset: manualTargetOffset,
          scrollbarSize: scrollbarSize,
          attachment: this.attachment
        });

        if (ret === false) {
          return false;
        } else if (typeof ret === 'undefined' || typeof ret !== 'object') {
          continue;
        } else {
          top = ret.top;
          left = ret.left;
        }
      }

      // We describe the position three different ways to give the optimizer
      // a chance to decide the best possible way to position the element
      // with the fewest repaints.
      var next = {
        // It's position relative to the page (absolute positioning when
        // the element is a child of the body)
        page: {
          top: top,
          left: left
        },

        // It's position relative to the viewport (fixed positioning)
        viewport: {
          top: top - pageYOffset,
          bottom: pageYOffset - top - height + innerHeight,
          left: left - pageXOffset,
          right: pageXOffset - left - width + innerWidth
        }
      };

      var doc = this.target.ownerDocument;
      var win = doc.defaultView;

      var scrollbarSize = undefined;
      if (win.innerHeight > doc.documentElement.clientHeight) {
        scrollbarSize = this.cache('scrollbar-size', getScrollBarSize);
        next.viewport.bottom -= scrollbarSize.height;
      }

      if (win.innerWidth > doc.documentElement.clientWidth) {
        scrollbarSize = this.cache('scrollbar-size', getScrollBarSize);
        next.viewport.right -= scrollbarSize.width;
      }

      if (['', 'static'].indexOf(doc.body.style.position) === -1 || ['', 'static'].indexOf(doc.body.parentElement.style.position) === -1) {
        // Absolute positioning in the body will be relative to the page, not the 'initial containing block'
        next.page.bottom = doc.body.scrollHeight - top - height;
        next.page.right = doc.body.scrollWidth - left - width;
      }

      if (typeof this.options.optimizations !== 'undefined' && this.options.optimizations.moveElement !== false && !(typeof this.targetModifier !== 'undefined')) {
        (function () {
          var offsetParent = _this7.cache('target-offsetparent', function () {
            return getOffsetParent(_this7.target);
          });
          var offsetPosition = _this7.cache('target-offsetparent-bounds', function () {
            return getBounds(offsetParent);
          });
          var offsetParentStyle = getComputedStyle(offsetParent);
          var offsetParentSize = offsetPosition;

          var offsetBorder = {};
          ['Top', 'Left', 'Bottom', 'Right'].forEach(function (side) {
            offsetBorder[side.toLowerCase()] = parseFloat(offsetParentStyle['border' + side + 'Width']);
          });

          offsetPosition.right = doc.body.scrollWidth - offsetPosition.left - offsetParentSize.width + offsetBorder.right;
          offsetPosition.bottom = doc.body.scrollHeight - offsetPosition.top - offsetParentSize.height + offsetBorder.bottom;

          if (next.page.top >= offsetPosition.top + offsetBorder.top && next.page.bottom >= offsetPosition.bottom) {
            if (next.page.left >= offsetPosition.left + offsetBorder.left && next.page.right >= offsetPosition.right) {
              // We're within the visible part of the target's scroll parent
              var scrollTop = offsetParent.scrollTop;
              var scrollLeft = offsetParent.scrollLeft;

              // It's position relative to the target's offset parent (absolute positioning when
              // the element is moved to be a child of the target's offset parent).
              next.offset = {
                top: next.page.top - offsetPosition.top + scrollTop - offsetBorder.top,
                left: next.page.left - offsetPosition.left + scrollLeft - offsetBorder.left
              };
            }
          }
        })();
      }

      // We could also travel up the DOM and try each containing context, rather than only
      // looking at the body, but we're gonna get diminishing returns.

      this.move(next);

      this.history.unshift(next);

      if (this.history.length > 3) {
        this.history.pop();
      }

      if (flushChanges) {
        flush();
      }

      return true;
    }

    // THE ISSUE
  }, {
    key: 'move',
    value: function move(pos) {
      var _this8 = this;

      if (!(typeof this.element.parentNode !== 'undefined')) {
        return;
      }

      var same = {};

      for (var type in pos) {
        same[type] = {};

        for (var key in pos[type]) {
          var found = false;

          for (var i = 0; i < this.history.length; ++i) {
            var point = this.history[i];
            if (typeof point[type] !== 'undefined' && !within(point[type][key], pos[type][key])) {
              found = true;
              break;
            }
          }

          if (!found) {
            same[type][key] = true;
          }
        }
      }

      var css = { top: '', left: '', right: '', bottom: '' };

      var transcribe = function transcribe(_same, _pos) {
        var hasOptimizations = typeof _this8.options.optimizations !== 'undefined';
        var gpu = hasOptimizations ? _this8.options.optimizations.gpu : null;
        if (gpu !== false) {
          var yPos = undefined,
              xPos = undefined;
          if (_same.top) {
            css.top = 0;
            yPos = _pos.top;
          } else {
            css.bottom = 0;
            yPos = -_pos.bottom;
          }

          if (_same.left) {
            css.left = 0;
            xPos = _pos.left;
          } else {
            css.right = 0;
            xPos = -_pos.right;
          }

          if (window.matchMedia) {
            // HubSpot/tether#207
            var retina = window.matchMedia('only screen and (min-resolution: 1.3dppx)').matches || window.matchMedia('only screen and (-webkit-min-device-pixel-ratio: 1.3)').matches;
            if (!retina) {
              xPos = Math.round(xPos);
              yPos = Math.round(yPos);
            }
          }

          css[transformKey] = 'translateX(' + xPos + 'px) translateY(' + yPos + 'px)';

          if (transformKey !== 'msTransform') {
            // The Z transform will keep this in the GPU (faster, and prevents artifacts),
            // but IE9 doesn't support 3d transforms and will choke.
            css[transformKey] += " translateZ(0)";
          }
        } else {
          if (_same.top) {
            css.top = _pos.top + 'px';
          } else {
            css.bottom = _pos.bottom + 'px';
          }

          if (_same.left) {
            css.left = _pos.left + 'px';
          } else {
            css.right = _pos.right + 'px';
          }
        }
      };

      var moved = false;
      if ((same.page.top || same.page.bottom) && (same.page.left || same.page.right)) {
        css.position = 'absolute';
        transcribe(same.page, pos.page);
      } else if ((same.viewport.top || same.viewport.bottom) && (same.viewport.left || same.viewport.right)) {
        css.position = 'fixed';
        transcribe(same.viewport, pos.viewport);
      } else if (typeof same.offset !== 'undefined' && same.offset.top && same.offset.left) {
        (function () {
          css.position = 'absolute';
          var offsetParent = _this8.cache('target-offsetparent', function () {
            return getOffsetParent(_this8.target);
          });

          if (getOffsetParent(_this8.element) !== offsetParent) {
            defer(function () {
              _this8.element.parentNode.removeChild(_this8.element);
              offsetParent.appendChild(_this8.element);
            });
          }

          transcribe(same.offset, pos.offset);
          moved = true;
        })();
      } else {
        css.position = 'absolute';
        transcribe({ top: true, left: true }, pos.page);
      }

      if (!moved) {
        if (this.options.bodyElement) {
          this.options.bodyElement.appendChild(this.element);
        } else {
          var offsetParentIsBody = true;
          var currentNode = this.element.parentNode;
          while (currentNode && currentNode.nodeType === 1 && currentNode.tagName !== 'BODY') {
            if (getComputedStyle(currentNode).position !== 'static') {
              offsetParentIsBody = false;
              break;
            }

            currentNode = currentNode.parentNode;
          }

          if (!offsetParentIsBody) {
            this.element.parentNode.removeChild(this.element);
            this.element.ownerDocument.body.appendChild(this.element);
          }
        }
      }

      // Any css change will trigger a repaint, so let's avoid one if nothing changed
      var writeCSS = {};
      var write = false;
      for (var key in css) {
        var val = css[key];
        var elVal = this.element.style[key];

        if (elVal !== val) {
          write = true;
          writeCSS[key] = val;
        }
      }

      if (write) {
        defer(function () {
          extend(_this8.element.style, writeCSS);
          _this8.trigger('repositioned');
        });
      }
    }
  }]);

  return TetherClass;
})(Evented);

TetherClass.modules = [];

TetherBase.position = position;

var Tether = extend(TetherClass, TetherBase);
/* globals TetherBase */

'use strict';

var _slicedToArray = (function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i['return']) _i['return'](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError('Invalid attempt to destructure non-iterable instance'); } }; })();

var _TetherBase$Utils = TetherBase.Utils;
var getBounds = _TetherBase$Utils.getBounds;
var extend = _TetherBase$Utils.extend;
var updateClasses = _TetherBase$Utils.updateClasses;
var defer = _TetherBase$Utils.defer;

var BOUNDS_FORMAT = ['left', 'top', 'right', 'bottom'];

function getBoundingRect(tether, to) {
  if (to === 'scrollParent') {
    to = tether.scrollParents[0];
  } else if (to === 'window') {
    to = [pageXOffset, pageYOffset, innerWidth + pageXOffset, innerHeight + pageYOffset];
  }

  if (to === document) {
    to = to.documentElement;
  }

  if (typeof to.nodeType !== 'undefined') {
    (function () {
      var node = to;
      var size = getBounds(to);
      var pos = size;
      var style = getComputedStyle(to);

      to = [pos.left, pos.top, size.width + pos.left, size.height + pos.top];

      // Account any parent Frames scroll offset
      if (node.ownerDocument !== document) {
        var win = node.ownerDocument.defaultView;
        to[0] += win.pageXOffset;
        to[1] += win.pageYOffset;
        to[2] += win.pageXOffset;
        to[3] += win.pageYOffset;
      }

      BOUNDS_FORMAT.forEach(function (side, i) {
        side = side[0].toUpperCase() + side.substr(1);
        if (side === 'Top' || side === 'Left') {
          to[i] += parseFloat(style['border' + side + 'Width']);
        } else {
          to[i] -= parseFloat(style['border' + side + 'Width']);
        }
      });
    })();
  }

  return to;
}

TetherBase.modules.push({
  position: function position(_ref) {
    var _this = this;

    var top = _ref.top;
    var left = _ref.left;
    var targetAttachment = _ref.targetAttachment;

    if (!this.options.constraints) {
      return true;
    }

    var _cache = this.cache('element-bounds', function () {
      return getBounds(_this.element);
    });

    var height = _cache.height;
    var width = _cache.width;

    if (width === 0 && height === 0 && typeof this.lastSize !== 'undefined') {
      var _lastSize = this.lastSize;

      // Handle the item getting hidden as a result of our positioning without glitching
      // the classes in and out
      width = _lastSize.width;
      height = _lastSize.height;
    }

    var targetSize = this.cache('target-bounds', function () {
      return _this.getTargetBounds();
    });

    var targetHeight = targetSize.height;
    var targetWidth = targetSize.width;

    var allClasses = [this.getClass('pinned'), this.getClass('out-of-bounds')];

    this.options.constraints.forEach(function (constraint) {
      var outOfBoundsClass = constraint.outOfBoundsClass;
      var pinnedClass = constraint.pinnedClass;

      if (outOfBoundsClass) {
        allClasses.push(outOfBoundsClass);
      }
      if (pinnedClass) {
        allClasses.push(pinnedClass);
      }
    });

    allClasses.forEach(function (cls) {
      ['left', 'top', 'right', 'bottom'].forEach(function (side) {
        allClasses.push(cls + '-' + side);
      });
    });

    var addClasses = [];

    var tAttachment = extend({}, targetAttachment);
    var eAttachment = extend({}, this.attachment);

    this.options.constraints.forEach(function (constraint) {
      var to = constraint.to;
      var attachment = constraint.attachment;
      var pin = constraint.pin;

      if (typeof attachment === 'undefined') {
        attachment = '';
      }

      var changeAttachX = undefined,
          changeAttachY = undefined;
      if (attachment.indexOf(' ') >= 0) {
        var _attachment$split = attachment.split(' ');

        var _attachment$split2 = _slicedToArray(_attachment$split, 2);

        changeAttachY = _attachment$split2[0];
        changeAttachX = _attachment$split2[1];
      } else {
        changeAttachX = changeAttachY = attachment;
      }

      var bounds = getBoundingRect(_this, to);

      if (changeAttachY === 'target' || changeAttachY === 'both') {
        if (top < bounds[1] && tAttachment.top === 'top') {
          top += targetHeight;
          tAttachment.top = 'bottom';
        }

        if (top + height > bounds[3] && tAttachment.top === 'bottom') {
          top -= targetHeight;
          tAttachment.top = 'top';
        }
      }

      if (changeAttachY === 'together') {
        if (tAttachment.top === 'top') {
          if (eAttachment.top === 'bottom' && top < bounds[1]) {
            top += targetHeight;
            tAttachment.top = 'bottom';

            top += height;
            eAttachment.top = 'top';
          } else if (eAttachment.top === 'top' && top + height > bounds[3] && top - (height - targetHeight) >= bounds[1]) {
            top -= height - targetHeight;
            tAttachment.top = 'bottom';

            eAttachment.top = 'bottom';
          }
        }

        if (tAttachment.top === 'bottom') {
          if (eAttachment.top === 'top' && top + height > bounds[3]) {
            top -= targetHeight;
            tAttachment.top = 'top';

            top -= height;
            eAttachment.top = 'bottom';
          } else if (eAttachment.top === 'bottom' && top < bounds[1] && top + (height * 2 - targetHeight) <= bounds[3]) {
            top += height - targetHeight;
            tAttachment.top = 'top';

            eAttachment.top = 'top';
          }
        }

        if (tAttachment.top === 'middle') {
          if (top + height > bounds[3] && eAttachment.top === 'top') {
            top -= height;
            eAttachment.top = 'bottom';
          } else if (top < bounds[1] && eAttachment.top === 'bottom') {
            top += height;
            eAttachment.top = 'top';
          }
        }
      }

      if (changeAttachX === 'target' || changeAttachX === 'both') {
        if (left < bounds[0] && tAttachment.left === 'left') {
          left += targetWidth;
          tAttachment.left = 'right';
        }

        if (left + width > bounds[2] && tAttachment.left === 'right') {
          left -= targetWidth;
          tAttachment.left = 'left';
        }
      }

      if (changeAttachX === 'together') {
        if (left < bounds[0] && tAttachment.left === 'left') {
          if (eAttachment.left === 'right') {
            left += targetWidth;
            tAttachment.left = 'right';

            left += width;
            eAttachment.left = 'left';
          } else if (eAttachment.left === 'left') {
            left += targetWidth;
            tAttachment.left = 'right';

            left -= width;
            eAttachment.left = 'right';
          }
        } else if (left + width > bounds[2] && tAttachment.left === 'right') {
          if (eAttachment.left === 'left') {
            left -= targetWidth;
            tAttachment.left = 'left';

            left -= width;
            eAttachment.left = 'right';
          } else if (eAttachment.left === 'right') {
            left -= targetWidth;
            tAttachment.left = 'left';

            left += width;
            eAttachment.left = 'left';
          }
        } else if (tAttachment.left === 'center') {
          if (left + width > bounds[2] && eAttachment.left === 'left') {
            left -= width;
            eAttachment.left = 'right';
          } else if (left < bounds[0] && eAttachment.left === 'right') {
            left += width;
            eAttachment.left = 'left';
          }
        }
      }

      if (changeAttachY === 'element' || changeAttachY === 'both') {
        if (top < bounds[1] && eAttachment.top === 'bottom') {
          top += height;
          eAttachment.top = 'top';
        }

        if (top + height > bounds[3] && eAttachment.top === 'top') {
          top -= height;
          eAttachment.top = 'bottom';
        }
      }

      if (changeAttachX === 'element' || changeAttachX === 'both') {
        if (left < bounds[0]) {
          if (eAttachment.left === 'right') {
            left += width;
            eAttachment.left = 'left';
          } else if (eAttachment.left === 'center') {
            left += width / 2;
            eAttachment.left = 'left';
          }
        }

        if (left + width > bounds[2]) {
          if (eAttachment.left === 'left') {
            left -= width;
            eAttachment.left = 'right';
          } else if (eAttachment.left === 'center') {
            left -= width / 2;
            eAttachment.left = 'right';
          }
        }
      }

      if (typeof pin === 'string') {
        pin = pin.split(',').map(function (p) {
          return p.trim();
        });
      } else if (pin === true) {
        pin = ['top', 'left', 'right', 'bottom'];
      }

      pin = pin || [];

      var pinned = [];
      var oob = [];

      if (top < bounds[1]) {
        if (pin.indexOf('top') >= 0) {
          top = bounds[1];
          pinned.push('top');
        } else {
          oob.push('top');
        }
      }

      if (top + height > bounds[3]) {
        if (pin.indexOf('bottom') >= 0) {
          top = bounds[3] - height;
          pinned.push('bottom');
        } else {
          oob.push('bottom');
        }
      }

      if (left < bounds[0]) {
        if (pin.indexOf('left') >= 0) {
          left = bounds[0];
          pinned.push('left');
        } else {
          oob.push('left');
        }
      }

      if (left + width > bounds[2]) {
        if (pin.indexOf('right') >= 0) {
          left = bounds[2] - width;
          pinned.push('right');
        } else {
          oob.push('right');
        }
      }

      if (pinned.length) {
        (function () {
          var pinnedClass = undefined;
          if (typeof _this.options.pinnedClass !== 'undefined') {
            pinnedClass = _this.options.pinnedClass;
          } else {
            pinnedClass = _this.getClass('pinned');
          }

          addClasses.push(pinnedClass);
          pinned.forEach(function (side) {
            addClasses.push(pinnedClass + '-' + side);
          });
        })();
      }

      if (oob.length) {
        (function () {
          var oobClass = undefined;
          if (typeof _this.options.outOfBoundsClass !== 'undefined') {
            oobClass = _this.options.outOfBoundsClass;
          } else {
            oobClass = _this.getClass('out-of-bounds');
          }

          addClasses.push(oobClass);
          oob.forEach(function (side) {
            addClasses.push(oobClass + '-' + side);
          });
        })();
      }

      if (pinned.indexOf('left') >= 0 || pinned.indexOf('right') >= 0) {
        eAttachment.left = tAttachment.left = false;
      }
      if (pinned.indexOf('top') >= 0 || pinned.indexOf('bottom') >= 0) {
        eAttachment.top = tAttachment.top = false;
      }

      if (tAttachment.top !== targetAttachment.top || tAttachment.left !== targetAttachment.left || eAttachment.top !== _this.attachment.top || eAttachment.left !== _this.attachment.left) {
        _this.updateAttachClasses(eAttachment, tAttachment);
        _this.trigger('update', {
          attachment: eAttachment,
          targetAttachment: tAttachment
        });
      }
    });

    defer(function () {
      if (!(_this.options.addTargetClasses === false)) {
        updateClasses(_this.target, addClasses, allClasses);
      }
      updateClasses(_this.element, addClasses, allClasses);
    });

    return { top: top, left: left };
  }
});
/* globals TetherBase */

'use strict';

var _TetherBase$Utils = TetherBase.Utils;
var getBounds = _TetherBase$Utils.getBounds;
var updateClasses = _TetherBase$Utils.updateClasses;
var defer = _TetherBase$Utils.defer;

TetherBase.modules.push({
  position: function position(_ref) {
    var _this = this;

    var top = _ref.top;
    var left = _ref.left;

    var _cache = this.cache('element-bounds', function () {
      return getBounds(_this.element);
    });

    var height = _cache.height;
    var width = _cache.width;

    var targetPos = this.getTargetBounds();

    var bottom = top + height;
    var right = left + width;

    var abutted = [];
    if (top <= targetPos.bottom && bottom >= targetPos.top) {
      ['left', 'right'].forEach(function (side) {
        var targetPosSide = targetPos[side];
        if (targetPosSide === left || targetPosSide === right) {
          abutted.push(side);
        }
      });
    }

    if (left <= targetPos.right && right >= targetPos.left) {
      ['top', 'bottom'].forEach(function (side) {
        var targetPosSide = targetPos[side];
        if (targetPosSide === top || targetPosSide === bottom) {
          abutted.push(side);
        }
      });
    }

    var allClasses = [];
    var addClasses = [];

    var sides = ['left', 'top', 'right', 'bottom'];
    allClasses.push(this.getClass('abutted'));
    sides.forEach(function (side) {
      allClasses.push(_this.getClass('abutted') + '-' + side);
    });

    if (abutted.length) {
      addClasses.push(this.getClass('abutted'));
    }

    abutted.forEach(function (side) {
      addClasses.push(_this.getClass('abutted') + '-' + side);
    });

    defer(function () {
      if (!(_this.options.addTargetClasses === false)) {
        updateClasses(_this.target, addClasses, allClasses);
      }
      updateClasses(_this.element, addClasses, allClasses);
    });

    return true;
  }
});
/* globals TetherBase */

'use strict';

var _slicedToArray = (function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i['return']) _i['return'](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError('Invalid attempt to destructure non-iterable instance'); } }; })();

TetherBase.modules.push({
  position: function position(_ref) {
    var top = _ref.top;
    var left = _ref.left;

    if (!this.options.shift) {
      return;
    }

    var shift = this.options.shift;
    if (typeof this.options.shift === 'function') {
      shift = this.options.shift.call(this, { top: top, left: left });
    }

    var shiftTop = undefined,
        shiftLeft = undefined;
    if (typeof shift === 'string') {
      shift = shift.split(' ');
      shift[1] = shift[1] || shift[0];

      var _shift = shift;

      var _shift2 = _slicedToArray(_shift, 2);

      shiftTop = _shift2[0];
      shiftLeft = _shift2[1];

      shiftTop = parseFloat(shiftTop, 10);
      shiftLeft = parseFloat(shiftLeft, 10);
    } else {
      shiftTop = shift.top;
      shiftLeft = shift.left;
    }

    top += shiftTop;
    left += shiftLeft;

    return { top: top, left: left };
  }
});
return Tether;

}));
/*!
 * Bootstrap v4.0.0-alpha.6 (https://getbootstrap.com)
 * Copyright 2011-2017 The Bootstrap Authors (https://github.com/twbs/bootstrap/graphs/contributors)
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 */


if (typeof jQuery === 'undefined') {
  throw new Error('Bootstrap\'s JavaScript requires jQuery. jQuery must be included before Bootstrap\'s JavaScript.')
}

+function ($) {
  var version = $.fn.jquery.split(' ')[0].split('.')
  if ((version[0] < 2 && version[1] < 9) || (version[0] == 1 && version[1] == 9 && version[2] < 1) || (version[0] >= 4)) {
    throw new Error('Bootstrap\'s JavaScript requires at least jQuery v1.9.1 but less than v4.0.0')
  }
}(jQuery);


+function () {

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): util.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Util = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Private TransitionEnd Helpers
   * ------------------------------------------------------------------------
   */

  var transition = false;

  var MAX_UID = 1000000;

  var TransitionEndEvent = {
    WebkitTransition: 'webkitTransitionEnd',
    MozTransition: 'transitionend',
    OTransition: 'oTransitionEnd otransitionend',
    transition: 'transitionend'
  };

  // shoutout AngusCroll (https://goo.gl/pxwQGp)
  function toType(obj) {
    return {}.toString.call(obj).match(/\s([a-zA-Z]+)/)[1].toLowerCase();
  }

  function isElement(obj) {
    return (obj[0] || obj).nodeType;
  }

  function getSpecialTransitionEndEvent() {
    return {
      bindType: transition.end,
      delegateType: transition.end,
      handle: function handle(event) {
        if ($(event.target).is(this)) {
          return event.handleObj.handler.apply(this, arguments); // eslint-disable-line prefer-rest-params
        }
        return undefined;
      }
    };
  }

  function transitionEndTest() {
    if (window.QUnit) {
      return false;
    }

    var el = document.createElement('bootstrap');

    for (var name in TransitionEndEvent) {
      if (el.style[name] !== undefined) {
        return {
          end: TransitionEndEvent[name]
        };
      }
    }

    return false;
  }

  function transitionEndEmulator(duration) {
    var _this = this;

    var called = false;

    $(this).one(Util.TRANSITION_END, function () {
      called = true;
    });

    setTimeout(function () {
      if (!called) {
        Util.triggerTransitionEnd(_this);
      }
    }, duration);

    return this;
  }

  function setTransitionEndSupport() {
    transition = transitionEndTest();

    $.fn.emulateTransitionEnd = transitionEndEmulator;

    if (Util.supportsTransitionEnd()) {
      $.event.special[Util.TRANSITION_END] = getSpecialTransitionEndEvent();
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Public Util Api
   * --------------------------------------------------------------------------
   */

  var Util = {

    TRANSITION_END: 'bsTransitionEnd',

    getUID: function getUID(prefix) {
      do {
        // eslint-disable-next-line no-bitwise
        prefix += ~~(Math.random() * MAX_UID); // "~~" acts like a faster Math.floor() here
      } while (document.getElementById(prefix));
      return prefix;
    },
    getSelectorFromElement: function getSelectorFromElement(element) {
      var selector = element.getAttribute('data-target');

      if (!selector) {
        selector = element.getAttribute('href') || '';
        selector = /^#[a-z]/i.test(selector) ? selector : null;
      }

      return selector;
    },
    reflow: function reflow(element) {
      return element.offsetHeight;
    },
    triggerTransitionEnd: function triggerTransitionEnd(element) {
      $(element).trigger(transition.end);
    },
    supportsTransitionEnd: function supportsTransitionEnd() {
      return Boolean(transition);
    },
    typeCheckConfig: function typeCheckConfig(componentName, config, configTypes) {
      for (var property in configTypes) {
        if (configTypes.hasOwnProperty(property)) {
          var expectedTypes = configTypes[property];
          var value = config[property];
          var valueType = value && isElement(value) ? 'element' : toType(value);

          if (!new RegExp(expectedTypes).test(valueType)) {
            throw new Error(componentName.toUpperCase() + ': ' + ('Option "' + property + '" provided type "' + valueType + '" ') + ('but expected type "' + expectedTypes + '".'));
          }
        }
      }
    }
  };

  setTransitionEndSupport();

  return Util;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): alert.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Alert = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'alert';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.alert';
  var EVENT_KEY = '.' + DATA_KEY;
  var DATA_API_KEY = '.data-api';
  var JQUERY_NO_CONFLICT = $.fn[NAME];
  var TRANSITION_DURATION = 150;

  var Selector = {
    DISMISS: '[data-dismiss="alert"]'
  };

  var Event = {
    CLOSE: 'close' + EVENT_KEY,
    CLOSED: 'closed' + EVENT_KEY,
    CLICK_DATA_API: 'click' + EVENT_KEY + DATA_API_KEY
  };

  var ClassName = {
    ALERT: 'alert',
    FADE: 'fade',
    SHOW: 'show'
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Alert = function () {
    function Alert(element) {
      _classCallCheck(this, Alert);

      this._element = element;
    }

    // getters

    // public

    Alert.prototype.close = function close(element) {
      element = element || this._element;

      var rootElement = this._getRootElement(element);
      var customEvent = this._triggerCloseEvent(rootElement);

      if (customEvent.isDefaultPrevented()) {
        return;
      }

      this._removeElement(rootElement);
    };

    Alert.prototype.dispose = function dispose() {
      $.removeData(this._element, DATA_KEY);
      this._element = null;
    };

    // private

    Alert.prototype._getRootElement = function _getRootElement(element) {
      var selector = Util.getSelectorFromElement(element);
      var parent = false;

      if (selector) {
        parent = $(selector)[0];
      }

      if (!parent) {
        parent = $(element).closest('.' + ClassName.ALERT)[0];
      }

      return parent;
    };

    Alert.prototype._triggerCloseEvent = function _triggerCloseEvent(element) {
      var closeEvent = $.Event(Event.CLOSE);

      $(element).trigger(closeEvent);
      return closeEvent;
    };

    Alert.prototype._removeElement = function _removeElement(element) {
      var _this2 = this;

      $(element).removeClass(ClassName.SHOW);

      if (!Util.supportsTransitionEnd() || !$(element).hasClass(ClassName.FADE)) {
        this._destroyElement(element);
        return;
      }

      $(element).one(Util.TRANSITION_END, function (event) {
        return _this2._destroyElement(element, event);
      }).emulateTransitionEnd(TRANSITION_DURATION);
    };

    Alert.prototype._destroyElement = function _destroyElement(element) {
      $(element).detach().trigger(Event.CLOSED).remove();
    };

    // static

    Alert._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var $element = $(this);
        var data = $element.data(DATA_KEY);

        if (!data) {
          data = new Alert(this);
          $element.data(DATA_KEY, data);
        }

        if (config === 'close') {
          data[config](this);
        }
      });
    };

    Alert._handleDismiss = function _handleDismiss(alertInstance) {
      return function (event) {
        if (event) {
          event.preventDefault();
        }

        alertInstance.close(this);
      };
    };

    _createClass(Alert, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }]);

    return Alert;
  }();

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  $(document).on(Event.CLICK_DATA_API, Selector.DISMISS, Alert._handleDismiss(new Alert()));

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Alert._jQueryInterface;
  $.fn[NAME].Constructor = Alert;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Alert._jQueryInterface;
  };

  return Alert;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): button.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Button = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'button';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.button';
  var EVENT_KEY = '.' + DATA_KEY;
  var DATA_API_KEY = '.data-api';
  var JQUERY_NO_CONFLICT = $.fn[NAME];

  var ClassName = {
    ACTIVE: 'active',
    BUTTON: 'btn',
    FOCUS: 'focus'
  };

  var Selector = {
    DATA_TOGGLE_CARROT: '[data-toggle^="button"]',
    DATA_TOGGLE: '[data-toggle="buttons"]',
    INPUT: 'input',
    ACTIVE: '.active',
    BUTTON: '.btn'
  };

  var Event = {
    CLICK_DATA_API: 'click' + EVENT_KEY + DATA_API_KEY,
    FOCUS_BLUR_DATA_API: 'focus' + EVENT_KEY + DATA_API_KEY + ' ' + ('blur' + EVENT_KEY + DATA_API_KEY)
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Button = function () {
    function Button(element) {
      _classCallCheck(this, Button);

      this._element = element;
    }

    // getters

    // public

    Button.prototype.toggle = function toggle() {
      var triggerChangeEvent = true;
      var rootElement = $(this._element).closest(Selector.DATA_TOGGLE)[0];

      if (rootElement) {
        var input = $(this._element).find(Selector.INPUT)[0];

        if (input) {
          if (input.type === 'radio') {
            if (input.checked && $(this._element).hasClass(ClassName.ACTIVE)) {
              triggerChangeEvent = false;
            } else {
              var activeElement = $(rootElement).find(Selector.ACTIVE)[0];

              if (activeElement) {
                $(activeElement).removeClass(ClassName.ACTIVE);
              }
            }
          }

          if (triggerChangeEvent) {
            input.checked = !$(this._element).hasClass(ClassName.ACTIVE);
            $(input).trigger('change');
          }

          input.focus();
        }
      }

      this._element.setAttribute('aria-pressed', !$(this._element).hasClass(ClassName.ACTIVE));

      if (triggerChangeEvent) {
        $(this._element).toggleClass(ClassName.ACTIVE);
      }
    };

    Button.prototype.dispose = function dispose() {
      $.removeData(this._element, DATA_KEY);
      this._element = null;
    };

    // static

    Button._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var data = $(this).data(DATA_KEY);

        if (!data) {
          data = new Button(this);
          $(this).data(DATA_KEY, data);
        }

        if (config === 'toggle') {
          data[config]();
        }
      });
    };

    _createClass(Button, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }]);

    return Button;
  }();

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  $(document).on(Event.CLICK_DATA_API, Selector.DATA_TOGGLE_CARROT, function (event) {
    event.preventDefault();

    var button = event.target;

    if (!$(button).hasClass(ClassName.BUTTON)) {
      button = $(button).closest(Selector.BUTTON);
    }

    Button._jQueryInterface.call($(button), 'toggle');
  }).on(Event.FOCUS_BLUR_DATA_API, Selector.DATA_TOGGLE_CARROT, function (event) {
    var button = $(event.target).closest(Selector.BUTTON)[0];
    $(button).toggleClass(ClassName.FOCUS, /^focus(in)?$/.test(event.type));
  });

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Button._jQueryInterface;
  $.fn[NAME].Constructor = Button;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Button._jQueryInterface;
  };

  return Button;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): carousel.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Carousel = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'carousel';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.carousel';
  var EVENT_KEY = '.' + DATA_KEY;
  var DATA_API_KEY = '.data-api';
  var JQUERY_NO_CONFLICT = $.fn[NAME];
  var TRANSITION_DURATION = 600;
  var ARROW_LEFT_KEYCODE = 37; // KeyboardEvent.which value for left arrow key
  var ARROW_RIGHT_KEYCODE = 39; // KeyboardEvent.which value for right arrow key

  var Default = {
    interval: 5000,
    keyboard: true,
    slide: false,
    pause: 'hover',
    wrap: true
  };

  var DefaultType = {
    interval: '(number|boolean)',
    keyboard: 'boolean',
    slide: '(boolean|string)',
    pause: '(string|boolean)',
    wrap: 'boolean'
  };

  var Direction = {
    NEXT: 'next',
    PREV: 'prev',
    LEFT: 'left',
    RIGHT: 'right'
  };

  var Event = {
    SLIDE: 'slide' + EVENT_KEY,
    SLID: 'slid' + EVENT_KEY,
    KEYDOWN: 'keydown' + EVENT_KEY,
    MOUSEENTER: 'mouseenter' + EVENT_KEY,
    MOUSELEAVE: 'mouseleave' + EVENT_KEY,
    LOAD_DATA_API: 'load' + EVENT_KEY + DATA_API_KEY,
    CLICK_DATA_API: 'click' + EVENT_KEY + DATA_API_KEY
  };

  var ClassName = {
    CAROUSEL: 'carousel',
    ACTIVE: 'active',
    SLIDE: 'slide',
    RIGHT: 'carousel-item-right',
    LEFT: 'carousel-item-left',
    NEXT: 'carousel-item-next',
    PREV: 'carousel-item-prev',
    ITEM: 'carousel-item'
  };

  var Selector = {
    ACTIVE: '.active',
    ACTIVE_ITEM: '.active.carousel-item',
    ITEM: '.carousel-item',
    NEXT_PREV: '.carousel-item-next, .carousel-item-prev',
    INDICATORS: '.carousel-indicators',
    DATA_SLIDE: '[data-slide], [data-slide-to]',
    DATA_RIDE: '[data-ride="carousel"]'
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Carousel = function () {
    function Carousel(element, config) {
      _classCallCheck(this, Carousel);

      this._items = null;
      this._interval = null;
      this._activeElement = null;

      this._isPaused = false;
      this._isSliding = false;

      this._config = this._getConfig(config);
      this._element = $(element)[0];
      this._indicatorsElement = $(this._element).find(Selector.INDICATORS)[0];

      this._addEventListeners();
    }

    // getters

    // public

    Carousel.prototype.next = function next() {
      if (this._isSliding) {
        throw new Error('Carousel is sliding');
      }
      this._slide(Direction.NEXT);
    };

    Carousel.prototype.nextWhenVisible = function nextWhenVisible() {
      // Don't call next when the page isn't visible
      if (!document.hidden) {
        this.next();
      }
    };

    Carousel.prototype.prev = function prev() {
      if (this._isSliding) {
        throw new Error('Carousel is sliding');
      }
      this._slide(Direction.PREVIOUS);
    };

    Carousel.prototype.pause = function pause(event) {
      if (!event) {
        this._isPaused = true;
      }

      if ($(this._element).find(Selector.NEXT_PREV)[0] && Util.supportsTransitionEnd()) {
        Util.triggerTransitionEnd(this._element);
        this.cycle(true);
      }

      clearInterval(this._interval);
      this._interval = null;
    };

    Carousel.prototype.cycle = function cycle(event) {
      if (!event) {
        this._isPaused = false;
      }

      if (this._interval) {
        clearInterval(this._interval);
        this._interval = null;
      }

      if (this._config.interval && !this._isPaused) {
        this._interval = setInterval((document.visibilityState ? this.nextWhenVisible : this.next).bind(this), this._config.interval);
      }
    };

    Carousel.prototype.to = function to(index) {
      var _this3 = this;

      this._activeElement = $(this._element).find(Selector.ACTIVE_ITEM)[0];

      var activeIndex = this._getItemIndex(this._activeElement);

      if (index > this._items.length - 1 || index < 0) {
        return;
      }

      if (this._isSliding) {
        $(this._element).one(Event.SLID, function () {
          return _this3.to(index);
        });
        return;
      }

      if (activeIndex === index) {
        this.pause();
        this.cycle();
        return;
      }

      var direction = index > activeIndex ? Direction.NEXT : Direction.PREVIOUS;

      this._slide(direction, this._items[index]);
    };

    Carousel.prototype.dispose = function dispose() {
      $(this._element).off(EVENT_KEY);
      $.removeData(this._element, DATA_KEY);

      this._items = null;
      this._config = null;
      this._element = null;
      this._interval = null;
      this._isPaused = null;
      this._isSliding = null;
      this._activeElement = null;
      this._indicatorsElement = null;
    };

    // private

    Carousel.prototype._getConfig = function _getConfig(config) {
      config = $.extend({}, Default, config);
      Util.typeCheckConfig(NAME, config, DefaultType);
      return config;
    };

    Carousel.prototype._addEventListeners = function _addEventListeners() {
      var _this4 = this;

      if (this._config.keyboard) {
        $(this._element).on(Event.KEYDOWN, function (event) {
          return _this4._keydown(event);
        });
      }

      if (this._config.pause === 'hover' && !('ontouchstart' in document.documentElement)) {
        $(this._element).on(Event.MOUSEENTER, function (event) {
          return _this4.pause(event);
        }).on(Event.MOUSELEAVE, function (event) {
          return _this4.cycle(event);
        });
      }
    };

    Carousel.prototype._keydown = function _keydown(event) {
      if (/input|textarea/i.test(event.target.tagName)) {
        return;
      }

      switch (event.which) {
        case ARROW_LEFT_KEYCODE:
          event.preventDefault();
          this.prev();
          break;
        case ARROW_RIGHT_KEYCODE:
          event.preventDefault();
          this.next();
          break;
        default:
          return;
      }
    };

    Carousel.prototype._getItemIndex = function _getItemIndex(element) {
      this._items = $.makeArray($(element).parent().find(Selector.ITEM));
      return this._items.indexOf(element);
    };

    Carousel.prototype._getItemByDirection = function _getItemByDirection(direction, activeElement) {
      var isNextDirection = direction === Direction.NEXT;
      var isPrevDirection = direction === Direction.PREVIOUS;
      var activeIndex = this._getItemIndex(activeElement);
      var lastItemIndex = this._items.length - 1;
      var isGoingToWrap = isPrevDirection && activeIndex === 0 || isNextDirection && activeIndex === lastItemIndex;

      if (isGoingToWrap && !this._config.wrap) {
        return activeElement;
      }

      var delta = direction === Direction.PREVIOUS ? -1 : 1;
      var itemIndex = (activeIndex + delta) % this._items.length;

      return itemIndex === -1 ? this._items[this._items.length - 1] : this._items[itemIndex];
    };

    Carousel.prototype._triggerSlideEvent = function _triggerSlideEvent(relatedTarget, eventDirectionName) {
      var slideEvent = $.Event(Event.SLIDE, {
        relatedTarget: relatedTarget,
        direction: eventDirectionName
      });

      $(this._element).trigger(slideEvent);

      return slideEvent;
    };

    Carousel.prototype._setActiveIndicatorElement = function _setActiveIndicatorElement(element) {
      if (this._indicatorsElement) {
        $(this._indicatorsElement).find(Selector.ACTIVE).removeClass(ClassName.ACTIVE);

        var nextIndicator = this._indicatorsElement.children[this._getItemIndex(element)];

        if (nextIndicator) {
          $(nextIndicator).addClass(ClassName.ACTIVE);
        }
      }
    };

    Carousel.prototype._slide = function _slide(direction, element) {
      var _this5 = this;

      var activeElement = $(this._element).find(Selector.ACTIVE_ITEM)[0];
      var nextElement = element || activeElement && this._getItemByDirection(direction, activeElement);

      var isCycling = Boolean(this._interval);

      var directionalClassName = void 0;
      var orderClassName = void 0;
      var eventDirectionName = void 0;

      if (direction === Direction.NEXT) {
        directionalClassName = ClassName.LEFT;
        orderClassName = ClassName.NEXT;
        eventDirectionName = Direction.LEFT;
      } else {
        directionalClassName = ClassName.RIGHT;
        orderClassName = ClassName.PREV;
        eventDirectionName = Direction.RIGHT;
      }

      if (nextElement && $(nextElement).hasClass(ClassName.ACTIVE)) {
        this._isSliding = false;
        return;
      }

      var slideEvent = this._triggerSlideEvent(nextElement, eventDirectionName);
      if (slideEvent.isDefaultPrevented()) {
        return;
      }

      if (!activeElement || !nextElement) {
        // some weirdness is happening, so we bail
        return;
      }

      this._isSliding = true;

      if (isCycling) {
        this.pause();
      }

      this._setActiveIndicatorElement(nextElement);

      var slidEvent = $.Event(Event.SLID, {
        relatedTarget: nextElement,
        direction: eventDirectionName
      });

      if (Util.supportsTransitionEnd() && $(this._element).hasClass(ClassName.SLIDE)) {

        $(nextElement).addClass(orderClassName);

        Util.reflow(nextElement);

        $(activeElement).addClass(directionalClassName);
        $(nextElement).addClass(directionalClassName);

        $(activeElement).one(Util.TRANSITION_END, function () {
          $(nextElement).removeClass(directionalClassName + ' ' + orderClassName).addClass(ClassName.ACTIVE);

          $(activeElement).removeClass(ClassName.ACTIVE + ' ' + orderClassName + ' ' + directionalClassName);

          _this5._isSliding = false;

          setTimeout(function () {
            return $(_this5._element).trigger(slidEvent);
          }, 0);
        }).emulateTransitionEnd(TRANSITION_DURATION);
      } else {
        $(activeElement).removeClass(ClassName.ACTIVE);
        $(nextElement).addClass(ClassName.ACTIVE);

        this._isSliding = false;
        $(this._element).trigger(slidEvent);
      }

      if (isCycling) {
        this.cycle();
      }
    };

    // static

    Carousel._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var data = $(this).data(DATA_KEY);
        var _config = $.extend({}, Default, $(this).data());

        if ((typeof config === 'undefined' ? 'undefined' : _typeof(config)) === 'object') {
          $.extend(_config, config);
        }

        var action = typeof config === 'string' ? config : _config.slide;

        if (!data) {
          data = new Carousel(this, _config);
          $(this).data(DATA_KEY, data);
        }

        if (typeof config === 'number') {
          data.to(config);
        } else if (typeof action === 'string') {
          if (data[action] === undefined) {
            throw new Error('No method named "' + action + '"');
          }
          data[action]();
        } else if (_config.interval) {
          data.pause();
          data.cycle();
        }
      });
    };

    Carousel._dataApiClickHandler = function _dataApiClickHandler(event) {
      var selector = Util.getSelectorFromElement(this);

      if (!selector) {
        return;
      }

      var target = $(selector)[0];

      if (!target || !$(target).hasClass(ClassName.CAROUSEL)) {
        return;
      }

      var config = $.extend({}, $(target).data(), $(this).data());
      var slideIndex = this.getAttribute('data-slide-to');

      if (slideIndex) {
        config.interval = false;
      }

      Carousel._jQueryInterface.call($(target), config);

      if (slideIndex) {
        $(target).data(DATA_KEY).to(slideIndex);
      }

      event.preventDefault();
    };

    _createClass(Carousel, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }, {
      key: 'Default',
      get: function get() {
        return Default;
      }
    }]);

    return Carousel;
  }();

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  $(document).on(Event.CLICK_DATA_API, Selector.DATA_SLIDE, Carousel._dataApiClickHandler);

  $(window).on(Event.LOAD_DATA_API, function () {
    $(Selector.DATA_RIDE).each(function () {
      var $carousel = $(this);
      Carousel._jQueryInterface.call($carousel, $carousel.data());
    });
  });

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Carousel._jQueryInterface;
  $.fn[NAME].Constructor = Carousel;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Carousel._jQueryInterface;
  };

  return Carousel;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): collapse.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Collapse = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'collapse';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.collapse';
  var EVENT_KEY = '.' + DATA_KEY;
  var DATA_API_KEY = '.data-api';
  var JQUERY_NO_CONFLICT = $.fn[NAME];
  var TRANSITION_DURATION = 600;

  var Default = {
    toggle: true,
    parent: ''
  };

  var DefaultType = {
    toggle: 'boolean',
    parent: 'string'
  };

  var Event = {
    SHOW: 'show' + EVENT_KEY,
    SHOWN: 'shown' + EVENT_KEY,
    HIDE: 'hide' + EVENT_KEY,
    HIDDEN: 'hidden' + EVENT_KEY,
    CLICK_DATA_API: 'click' + EVENT_KEY + DATA_API_KEY
  };

  var ClassName = {
    SHOW: 'show',
    COLLAPSE: 'collapse',
    COLLAPSING: 'collapsing',
    COLLAPSED: 'collapsed'
  };

  var Dimension = {
    WIDTH: 'width',
    HEIGHT: 'height'
  };

  var Selector = {
    ACTIVES: '.card > .show, .card > .collapsing',
    DATA_TOGGLE: '[data-toggle="collapse"]'
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Collapse = function () {
    function Collapse(element, config) {
      _classCallCheck(this, Collapse);

      this._isTransitioning = false;
      this._element = element;
      this._config = this._getConfig(config);
      this._triggerArray = $.makeArray($('[data-toggle="collapse"][href="#' + element.id + '"],' + ('[data-toggle="collapse"][data-target="#' + element.id + '"]')));

      this._parent = this._config.parent ? this._getParent() : null;

      if (!this._config.parent) {
        this._addAriaAndCollapsedClass(this._element, this._triggerArray);
      }

      if (this._config.toggle) {
        this.toggle();
      }
    }

    // getters

    // public

    Collapse.prototype.toggle = function toggle() {
      if ($(this._element).hasClass(ClassName.SHOW)) {
        this.hide();
      } else {
        this.show();
      }
    };

    Collapse.prototype.show = function show() {
      var _this6 = this;

      if (this._isTransitioning) {
        throw new Error('Collapse is transitioning');
      }

      if ($(this._element).hasClass(ClassName.SHOW)) {
        return;
      }

      var actives = void 0;
      var activesData = void 0;

      if (this._parent) {
        actives = $.makeArray($(this._parent).find(Selector.ACTIVES));
        if (!actives.length) {
          actives = null;
        }
      }

      if (actives) {
        activesData = $(actives).data(DATA_KEY);
        if (activesData && activesData._isTransitioning) {
          return;
        }
      }

      var startEvent = $.Event(Event.SHOW);
      $(this._element).trigger(startEvent);
      if (startEvent.isDefaultPrevented()) {
        return;
      }

      if (actives) {
        Collapse._jQueryInterface.call($(actives), 'hide');
        if (!activesData) {
          $(actives).data(DATA_KEY, null);
        }
      }

      var dimension = this._getDimension();

      $(this._element).removeClass(ClassName.COLLAPSE).addClass(ClassName.COLLAPSING);

      this._element.style[dimension] = 0;
      this._element.setAttribute('aria-expanded', true);

      if (this._triggerArray.length) {
        $(this._triggerArray).removeClass(ClassName.COLLAPSED).attr('aria-expanded', true);
      }

      this.setTransitioning(true);

      var complete = function complete() {
        $(_this6._element).removeClass(ClassName.COLLAPSING).addClass(ClassName.COLLAPSE).addClass(ClassName.SHOW);

        _this6._element.style[dimension] = '';

        _this6.setTransitioning(false);

        $(_this6._element).trigger(Event.SHOWN);
      };

      if (!Util.supportsTransitionEnd()) {
        complete();
        return;
      }

      var capitalizedDimension = dimension[0].toUpperCase() + dimension.slice(1);
      var scrollSize = 'scroll' + capitalizedDimension;

      $(this._element).one(Util.TRANSITION_END, complete).emulateTransitionEnd(TRANSITION_DURATION);

      this._element.style[dimension] = this._element[scrollSize] + 'px';
    };

    Collapse.prototype.hide = function hide() {
      var _this7 = this;

      if (this._isTransitioning) {
        throw new Error('Collapse is transitioning');
      }

      if (!$(this._element).hasClass(ClassName.SHOW)) {
        return;
      }

      var startEvent = $.Event(Event.HIDE);
      $(this._element).trigger(startEvent);
      if (startEvent.isDefaultPrevented()) {
        return;
      }

      var dimension = this._getDimension();
      var offsetDimension = dimension === Dimension.WIDTH ? 'offsetWidth' : 'offsetHeight';

      this._element.style[dimension] = this._element[offsetDimension] + 'px';

      Util.reflow(this._element);

      $(this._element).addClass(ClassName.COLLAPSING).removeClass(ClassName.COLLAPSE).removeClass(ClassName.SHOW);

      this._element.setAttribute('aria-expanded', false);

      if (this._triggerArray.length) {
        $(this._triggerArray).addClass(ClassName.COLLAPSED).attr('aria-expanded', false);
      }

      this.setTransitioning(true);

      var complete = function complete() {
        _this7.setTransitioning(false);
        $(_this7._element).removeClass(ClassName.COLLAPSING).addClass(ClassName.COLLAPSE).trigger(Event.HIDDEN);
      };

      this._element.style[dimension] = '';

      if (!Util.supportsTransitionEnd()) {
        complete();
        return;
      }

      $(this._element).one(Util.TRANSITION_END, complete).emulateTransitionEnd(TRANSITION_DURATION);
    };

    Collapse.prototype.setTransitioning = function setTransitioning(isTransitioning) {
      this._isTransitioning = isTransitioning;
    };

    Collapse.prototype.dispose = function dispose() {
      $.removeData(this._element, DATA_KEY);

      this._config = null;
      this._parent = null;
      this._element = null;
      this._triggerArray = null;
      this._isTransitioning = null;
    };

    // private

    Collapse.prototype._getConfig = function _getConfig(config) {
      config = $.extend({}, Default, config);
      config.toggle = Boolean(config.toggle); // coerce string values
      Util.typeCheckConfig(NAME, config, DefaultType);
      return config;
    };

    Collapse.prototype._getDimension = function _getDimension() {
      var hasWidth = $(this._element).hasClass(Dimension.WIDTH);
      return hasWidth ? Dimension.WIDTH : Dimension.HEIGHT;
    };

    Collapse.prototype._getParent = function _getParent() {
      var _this8 = this;

      var parent = $(this._config.parent)[0];
      var selector = '[data-toggle="collapse"][data-parent="' + this._config.parent + '"]';

      $(parent).find(selector).each(function (i, element) {
        _this8._addAriaAndCollapsedClass(Collapse._getTargetFromElement(element), [element]);
      });

      return parent;
    };

    Collapse.prototype._addAriaAndCollapsedClass = function _addAriaAndCollapsedClass(element, triggerArray) {
      if (element) {
        var isOpen = $(element).hasClass(ClassName.SHOW);
        element.setAttribute('aria-expanded', isOpen);

        if (triggerArray.length) {
          $(triggerArray).toggleClass(ClassName.COLLAPSED, !isOpen).attr('aria-expanded', isOpen);
        }
      }
    };

    // static

    Collapse._getTargetFromElement = function _getTargetFromElement(element) {
      var selector = Util.getSelectorFromElement(element);
      return selector ? $(selector)[0] : null;
    };

    Collapse._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var $this = $(this);
        var data = $this.data(DATA_KEY);
        var _config = $.extend({}, Default, $this.data(), (typeof config === 'undefined' ? 'undefined' : _typeof(config)) === 'object' && config);

        if (!data && _config.toggle && /show|hide/.test(config)) {
          _config.toggle = false;
        }

        if (!data) {
          data = new Collapse(this, _config);
          $this.data(DATA_KEY, data);
        }

        if (typeof config === 'string') {
          if (data[config] === undefined) {
            throw new Error('No method named "' + config + '"');
          }
          data[config]();
        }
      });
    };

    _createClass(Collapse, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }, {
      key: 'Default',
      get: function get() {
        return Default;
      }
    }]);

    return Collapse;
  }();

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  $(document).on(Event.CLICK_DATA_API, Selector.DATA_TOGGLE, function (event) {
    event.preventDefault();

    var target = Collapse._getTargetFromElement(this);
    var data = $(target).data(DATA_KEY);
    var config = data ? 'toggle' : $(this).data();

    Collapse._jQueryInterface.call($(target), config);
  });

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Collapse._jQueryInterface;
  $.fn[NAME].Constructor = Collapse;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Collapse._jQueryInterface;
  };

  return Collapse;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): dropdown.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Dropdown = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'dropdown';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.dropdown';
  var EVENT_KEY = '.' + DATA_KEY;
  var DATA_API_KEY = '.data-api';
  var JQUERY_NO_CONFLICT = $.fn[NAME];
  var ESCAPE_KEYCODE = 27; // KeyboardEvent.which value for Escape (Esc) key
  var ARROW_UP_KEYCODE = 38; // KeyboardEvent.which value for up arrow key
  var ARROW_DOWN_KEYCODE = 40; // KeyboardEvent.which value for down arrow key
  var RIGHT_MOUSE_BUTTON_WHICH = 3; // MouseEvent.which value for the right button (assuming a right-handed mouse)

  var Event = {
    HIDE: 'hide' + EVENT_KEY,
    HIDDEN: 'hidden' + EVENT_KEY,
    SHOW: 'show' + EVENT_KEY,
    SHOWN: 'shown' + EVENT_KEY,
    CLICK: 'click' + EVENT_KEY,
    CLICK_DATA_API: 'click' + EVENT_KEY + DATA_API_KEY,
    FOCUSIN_DATA_API: 'focusin' + EVENT_KEY + DATA_API_KEY,
    KEYDOWN_DATA_API: 'keydown' + EVENT_KEY + DATA_API_KEY
  };

  var ClassName = {
    BACKDROP: 'dropdown-backdrop',
    DISABLED: 'disabled',
    SHOW: 'show'
  };

  var Selector = {
    BACKDROP: '.dropdown-backdrop',
    DATA_TOGGLE: '[data-toggle="dropdown"]',
    FORM_CHILD: '.dropdown form',
    ROLE_MENU: '[role="menu"]',
    ROLE_LISTBOX: '[role="listbox"]',
    NAVBAR_NAV: '.navbar-nav',
    VISIBLE_ITEMS: '[role="menu"] li:not(.disabled) a, ' + '[role="listbox"] li:not(.disabled) a'
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Dropdown = function () {
    function Dropdown(element) {
      _classCallCheck(this, Dropdown);

      this._element = element;

      this._addEventListeners();
    }

    // getters

    // public

    Dropdown.prototype.toggle = function toggle() {
      if (this.disabled || $(this).hasClass(ClassName.DISABLED)) {
        return false;
      }

      var parent = Dropdown._getParentFromElement(this);
      var isActive = $(parent).hasClass(ClassName.SHOW);

      Dropdown._clearMenus();

      if (isActive) {
        return false;
      }

      if ('ontouchstart' in document.documentElement && !$(parent).closest(Selector.NAVBAR_NAV).length) {

        // if mobile we use a backdrop because click events don't delegate
        var dropdown = document.createElement('div');
        dropdown.className = ClassName.BACKDROP;
        $(dropdown).insertBefore(this);
        $(dropdown).on('click', Dropdown._clearMenus);
      }

      var relatedTarget = {
        relatedTarget: this
      };
      var showEvent = $.Event(Event.SHOW, relatedTarget);

      $(parent).trigger(showEvent);

      if (showEvent.isDefaultPrevented()) {
        return false;
      }

      this.focus();
      this.setAttribute('aria-expanded', true);

      $(parent).toggleClass(ClassName.SHOW);
      $(parent).trigger($.Event(Event.SHOWN, relatedTarget));

      return false;
    };

    Dropdown.prototype.dispose = function dispose() {
      $.removeData(this._element, DATA_KEY);
      $(this._element).off(EVENT_KEY);
      this._element = null;
    };

    // private

    Dropdown.prototype._addEventListeners = function _addEventListeners() {
      $(this._element).on(Event.CLICK, this.toggle);
    };

    // static

    Dropdown._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var data = $(this).data(DATA_KEY);

        if (!data) {
          data = new Dropdown(this);
          $(this).data(DATA_KEY, data);
        }

        if (typeof config === 'string') {
          if (data[config] === undefined) {
            throw new Error('No method named "' + config + '"');
          }
          data[config].call(this);
        }
      });
    };

    Dropdown._clearMenus = function _clearMenus(event) {
      if (event && event.which === RIGHT_MOUSE_BUTTON_WHICH) {
        return;
      }

      var backdrop = $(Selector.BACKDROP)[0];
      if (backdrop) {
        backdrop.parentNode.removeChild(backdrop);
      }

      var toggles = $.makeArray($(Selector.DATA_TOGGLE));

      for (var i = 0; i < toggles.length; i++) {
        var parent = Dropdown._getParentFromElement(toggles[i]);
        var relatedTarget = {
          relatedTarget: toggles[i]
        };

        if (!$(parent).hasClass(ClassName.SHOW)) {
          continue;
        }

        if (event && (event.type === 'click' && /input|textarea/i.test(event.target.tagName) || event.type === 'focusin') && $.contains(parent, event.target)) {
          continue;
        }

        var hideEvent = $.Event(Event.HIDE, relatedTarget);
        $(parent).trigger(hideEvent);
        if (hideEvent.isDefaultPrevented()) {
          continue;
        }

        toggles[i].setAttribute('aria-expanded', 'false');

        $(parent).removeClass(ClassName.SHOW).trigger($.Event(Event.HIDDEN, relatedTarget));
      }
    };

    Dropdown._getParentFromElement = function _getParentFromElement(element) {
      var parent = void 0;
      var selector = Util.getSelectorFromElement(element);

      if (selector) {
        parent = $(selector)[0];
      }

      return parent || element.parentNode;
    };

    Dropdown._dataApiKeydownHandler = function _dataApiKeydownHandler(event) {
      if (!/(38|40|27|32)/.test(event.which) || /input|textarea/i.test(event.target.tagName)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (this.disabled || $(this).hasClass(ClassName.DISABLED)) {
        return;
      }

      var parent = Dropdown._getParentFromElement(this);
      var isActive = $(parent).hasClass(ClassName.SHOW);

      if (!isActive && event.which !== ESCAPE_KEYCODE || isActive && event.which === ESCAPE_KEYCODE) {

        if (event.which === ESCAPE_KEYCODE) {
          var toggle = $(parent).find(Selector.DATA_TOGGLE)[0];
          $(toggle).trigger('focus');
        }

        $(this).trigger('click');
        return;
      }

      var items = $(parent).find(Selector.VISIBLE_ITEMS).get();

      if (!items.length) {
        return;
      }

      var index = items.indexOf(event.target);

      if (event.which === ARROW_UP_KEYCODE && index > 0) {
        // up
        index--;
      }

      if (event.which === ARROW_DOWN_KEYCODE && index < items.length - 1) {
        // down
        index++;
      }

      if (index < 0) {
        index = 0;
      }

      items[index].focus();
    };

    _createClass(Dropdown, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }]);

    return Dropdown;
  }();

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  $(document).on(Event.KEYDOWN_DATA_API, Selector.DATA_TOGGLE, Dropdown._dataApiKeydownHandler).on(Event.KEYDOWN_DATA_API, Selector.ROLE_MENU, Dropdown._dataApiKeydownHandler).on(Event.KEYDOWN_DATA_API, Selector.ROLE_LISTBOX, Dropdown._dataApiKeydownHandler).on(Event.CLICK_DATA_API + ' ' + Event.FOCUSIN_DATA_API, Dropdown._clearMenus).on(Event.CLICK_DATA_API, Selector.DATA_TOGGLE, Dropdown.prototype.toggle).on(Event.CLICK_DATA_API, Selector.FORM_CHILD, function (e) {
    e.stopPropagation();
  });

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Dropdown._jQueryInterface;
  $.fn[NAME].Constructor = Dropdown;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Dropdown._jQueryInterface;
  };

  return Dropdown;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): modal.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Modal = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'modal';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.modal';
  var EVENT_KEY = '.' + DATA_KEY;
  var DATA_API_KEY = '.data-api';
  var JQUERY_NO_CONFLICT = $.fn[NAME];
  var TRANSITION_DURATION = 300;
  var BACKDROP_TRANSITION_DURATION = 150;
  var ESCAPE_KEYCODE = 27; // KeyboardEvent.which value for Escape (Esc) key

  var Default = {
    backdrop: true,
    keyboard: true,
    focus: true,
    show: true
  };

  var DefaultType = {
    backdrop: '(boolean|string)',
    keyboard: 'boolean',
    focus: 'boolean',
    show: 'boolean'
  };

  var Event = {
    HIDE: 'hide' + EVENT_KEY,
    HIDDEN: 'hidden' + EVENT_KEY,
    SHOW: 'show' + EVENT_KEY,
    SHOWN: 'shown' + EVENT_KEY,
    FOCUSIN: 'focusin' + EVENT_KEY,
    RESIZE: 'resize' + EVENT_KEY,
    CLICK_DISMISS: 'click.dismiss' + EVENT_KEY,
    KEYDOWN_DISMISS: 'keydown.dismiss' + EVENT_KEY,
    MOUSEUP_DISMISS: 'mouseup.dismiss' + EVENT_KEY,
    MOUSEDOWN_DISMISS: 'mousedown.dismiss' + EVENT_KEY,
    CLICK_DATA_API: 'click' + EVENT_KEY + DATA_API_KEY
  };

  var ClassName = {
    SCROLLBAR_MEASURER: 'modal-scrollbar-measure',
    BACKDROP: 'modal-backdrop',
    OPEN: 'modal-open',
    FADE: 'fade',
    SHOW: 'show'
  };

  var Selector = {
    DIALOG: '.modal-dialog',
    DATA_TOGGLE: '[data-toggle="modal"]',
    DATA_DISMISS: '[data-dismiss="modal"]',
    FIXED_CONTENT: '.fixed-top, .fixed-bottom, .is-fixed, .sticky-top'
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Modal = function () {
    function Modal(element, config) {
      _classCallCheck(this, Modal);

      this._config = this._getConfig(config);
      this._element = element;
      this._dialog = $(element).find(Selector.DIALOG)[0];
      this._backdrop = null;
      this._isShown = false;
      this._isBodyOverflowing = false;
      this._ignoreBackdropClick = false;
      this._isTransitioning = false;
      this._originalBodyPadding = 0;
      this._scrollbarWidth = 0;
    }

    // getters

    // public

    Modal.prototype.toggle = function toggle(relatedTarget) {
      return this._isShown ? this.hide() : this.show(relatedTarget);
    };

    Modal.prototype.show = function show(relatedTarget) {
      var _this9 = this;

      if (this._isTransitioning) {
        throw new Error('Modal is transitioning');
      }

      if (Util.supportsTransitionEnd() && $(this._element).hasClass(ClassName.FADE)) {
        this._isTransitioning = true;
      }
      var showEvent = $.Event(Event.SHOW, {
        relatedTarget: relatedTarget
      });

      $(this._element).trigger(showEvent);

      if (this._isShown || showEvent.isDefaultPrevented()) {
        return;
      }

      this._isShown = true;

      this._checkScrollbar();
      this._setScrollbar();

      $(document.body).addClass(ClassName.OPEN);

      this._setEscapeEvent();
      this._setResizeEvent();

      $(this._element).on(Event.CLICK_DISMISS, Selector.DATA_DISMISS, function (event) {
        return _this9.hide(event);
      });

      $(this._dialog).on(Event.MOUSEDOWN_DISMISS, function () {
        $(_this9._element).one(Event.MOUSEUP_DISMISS, function (event) {
          if ($(event.target).is(_this9._element)) {
            _this9._ignoreBackdropClick = true;
          }
        });
      });

      this._showBackdrop(function () {
        return _this9._showElement(relatedTarget);
      });
    };

    Modal.prototype.hide = function hide(event) {
      var _this10 = this;

      if (event) {
        event.preventDefault();
      }

      if (this._isTransitioning) {
        throw new Error('Modal is transitioning');
      }

      var transition = Util.supportsTransitionEnd() && $(this._element).hasClass(ClassName.FADE);
      if (transition) {
        this._isTransitioning = true;
      }

      var hideEvent = $.Event(Event.HIDE);
      $(this._element).trigger(hideEvent);

      if (!this._isShown || hideEvent.isDefaultPrevented()) {
        return;
      }

      this._isShown = false;

      this._setEscapeEvent();
      this._setResizeEvent();

      $(document).off(Event.FOCUSIN);

      $(this._element).removeClass(ClassName.SHOW);

      $(this._element).off(Event.CLICK_DISMISS);
      $(this._dialog).off(Event.MOUSEDOWN_DISMISS);

      if (transition) {
        $(this._element).one(Util.TRANSITION_END, function (event) {
          return _this10._hideModal(event);
        }).emulateTransitionEnd(TRANSITION_DURATION);
      } else {
        this._hideModal();
      }
    };

    Modal.prototype.dispose = function dispose() {
      $.removeData(this._element, DATA_KEY);

      $(window, document, this._element, this._backdrop).off(EVENT_KEY);

      this._config = null;
      this._element = null;
      this._dialog = null;
      this._backdrop = null;
      this._isShown = null;
      this._isBodyOverflowing = null;
      this._ignoreBackdropClick = null;
      this._originalBodyPadding = null;
      this._scrollbarWidth = null;
    };

    // private

    Modal.prototype._getConfig = function _getConfig(config) {
      config = $.extend({}, Default, config);
      Util.typeCheckConfig(NAME, config, DefaultType);
      return config;
    };

    Modal.prototype._showElement = function _showElement(relatedTarget) {
      var _this11 = this;

      var transition = Util.supportsTransitionEnd() && $(this._element).hasClass(ClassName.FADE);

      if (!this._element.parentNode || this._element.parentNode.nodeType !== Node.ELEMENT_NODE) {
        // don't move modals dom position
        document.body.appendChild(this._element);
      }

      this._element.style.display = 'block';
      this._element.removeAttribute('aria-hidden');
      this._element.scrollTop = 0;

      if (transition) {
        Util.reflow(this._element);
      }

      $(this._element).addClass(ClassName.SHOW);

      if (this._config.focus) {
        this._enforceFocus();
      }

      var shownEvent = $.Event(Event.SHOWN, {
        relatedTarget: relatedTarget
      });

      var transitionComplete = function transitionComplete() {
        if (_this11._config.focus) {
          _this11._element.focus();
        }
        _this11._isTransitioning = false;
        $(_this11._element).trigger(shownEvent);
      };

      if (transition) {
        $(this._dialog).one(Util.TRANSITION_END, transitionComplete).emulateTransitionEnd(TRANSITION_DURATION);
      } else {
        transitionComplete();
      }
    };

    Modal.prototype._enforceFocus = function _enforceFocus() {
      var _this12 = this;

      $(document).off(Event.FOCUSIN) // guard against infinite focus loop
      .on(Event.FOCUSIN, function (event) {
        if (document !== event.target && _this12._element !== event.target && !$(_this12._element).has(event.target).length) {
          _this12._element.focus();
        }
      });
    };

    Modal.prototype._setEscapeEvent = function _setEscapeEvent() {
      var _this13 = this;

      if (this._isShown && this._config.keyboard) {
        $(this._element).on(Event.KEYDOWN_DISMISS, function (event) {
          if (event.which === ESCAPE_KEYCODE) {
            _this13.hide();
          }
        });
      } else if (!this._isShown) {
        $(this._element).off(Event.KEYDOWN_DISMISS);
      }
    };

    Modal.prototype._setResizeEvent = function _setResizeEvent() {
      var _this14 = this;

      if (this._isShown) {
        $(window).on(Event.RESIZE, function (event) {
          return _this14._handleUpdate(event);
        });
      } else {
        $(window).off(Event.RESIZE);
      }
    };

    Modal.prototype._hideModal = function _hideModal() {
      var _this15 = this;

      this._element.style.display = 'none';
      this._element.setAttribute('aria-hidden', 'true');
      this._isTransitioning = false;
      this._showBackdrop(function () {
        $(document.body).removeClass(ClassName.OPEN);
        _this15._resetAdjustments();
        _this15._resetScrollbar();
        $(_this15._element).trigger(Event.HIDDEN);
      });
    };

    Modal.prototype._removeBackdrop = function _removeBackdrop() {
      if (this._backdrop) {
        $(this._backdrop).remove();
        this._backdrop = null;
      }
    };

    Modal.prototype._showBackdrop = function _showBackdrop(callback) {
      var _this16 = this;

      var animate = $(this._element).hasClass(ClassName.FADE) ? ClassName.FADE : '';

      if (this._isShown && this._config.backdrop) {
        var doAnimate = Util.supportsTransitionEnd() && animate;

        this._backdrop = document.createElement('div');
        this._backdrop.className = ClassName.BACKDROP;

        if (animate) {
          $(this._backdrop).addClass(animate);
        }

        $(this._backdrop).appendTo(document.body);

        $(this._element).on(Event.CLICK_DISMISS, function (event) {
          if (_this16._ignoreBackdropClick) {
            _this16._ignoreBackdropClick = false;
            return;
          }
          if (event.target !== event.currentTarget) {
            return;
          }
          if (_this16._config.backdrop === 'static') {
            _this16._element.focus();
          } else {
            _this16.hide();
          }
        });

        if (doAnimate) {
          Util.reflow(this._backdrop);
        }

        $(this._backdrop).addClass(ClassName.SHOW);

        if (!callback) {
          return;
        }

        if (!doAnimate) {
          callback();
          return;
        }

        $(this._backdrop).one(Util.TRANSITION_END, callback).emulateTransitionEnd(BACKDROP_TRANSITION_DURATION);
      } else if (!this._isShown && this._backdrop) {
        $(this._backdrop).removeClass(ClassName.SHOW);

        var callbackRemove = function callbackRemove() {
          _this16._removeBackdrop();
          if (callback) {
            callback();
          }
        };

        if (Util.supportsTransitionEnd() && $(this._element).hasClass(ClassName.FADE)) {
          $(this._backdrop).one(Util.TRANSITION_END, callbackRemove).emulateTransitionEnd(BACKDROP_TRANSITION_DURATION);
        } else {
          callbackRemove();
        }
      } else if (callback) {
        callback();
      }
    };

    // ----------------------------------------------------------------------
    // the following methods are used to handle overflowing modals
    // todo (fat): these should probably be refactored out of modal.js
    // ----------------------------------------------------------------------

    Modal.prototype._handleUpdate = function _handleUpdate() {
      this._adjustDialog();
    };

    Modal.prototype._adjustDialog = function _adjustDialog() {
      var isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;

      if (!this._isBodyOverflowing && isModalOverflowing) {
        this._element.style.paddingLeft = this._scrollbarWidth + 'px';
      }

      if (this._isBodyOverflowing && !isModalOverflowing) {
        this._element.style.paddingRight = this._scrollbarWidth + 'px';
      }
    };

    Modal.prototype._resetAdjustments = function _resetAdjustments() {
      this._element.style.paddingLeft = '';
      this._element.style.paddingRight = '';
    };

    Modal.prototype._checkScrollbar = function _checkScrollbar() {
      this._isBodyOverflowing = document.body.clientWidth < window.innerWidth;
      this._scrollbarWidth = this._getScrollbarWidth();
    };

    Modal.prototype._setScrollbar = function _setScrollbar() {
      var bodyPadding = parseInt($(Selector.FIXED_CONTENT).css('padding-right') || 0, 10);

      this._originalBodyPadding = document.body.style.paddingRight || '';

      if (this._isBodyOverflowing) {
        document.body.style.paddingRight = bodyPadding + this._scrollbarWidth + 'px';
      }
    };

    Modal.prototype._resetScrollbar = function _resetScrollbar() {
      document.body.style.paddingRight = this._originalBodyPadding;
    };

    Modal.prototype._getScrollbarWidth = function _getScrollbarWidth() {
      // thx d.walsh
      var scrollDiv = document.createElement('div');
      scrollDiv.className = ClassName.SCROLLBAR_MEASURER;
      document.body.appendChild(scrollDiv);
      var scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
      document.body.removeChild(scrollDiv);
      return scrollbarWidth;
    };

    // static

    Modal._jQueryInterface = function _jQueryInterface(config, relatedTarget) {
      return this.each(function () {
        var data = $(this).data(DATA_KEY);
        var _config = $.extend({}, Modal.Default, $(this).data(), (typeof config === 'undefined' ? 'undefined' : _typeof(config)) === 'object' && config);

        if (!data) {
          data = new Modal(this, _config);
          $(this).data(DATA_KEY, data);
        }

        if (typeof config === 'string') {
          if (data[config] === undefined) {
            throw new Error('No method named "' + config + '"');
          }
          data[config](relatedTarget);
        } else if (_config.show) {
          data.show(relatedTarget);
        }
      });
    };

    _createClass(Modal, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }, {
      key: 'Default',
      get: function get() {
        return Default;
      }
    }]);

    return Modal;
  }();

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  $(document).on(Event.CLICK_DATA_API, Selector.DATA_TOGGLE, function (event) {
    var _this17 = this;

    var target = void 0;
    var selector = Util.getSelectorFromElement(this);

    if (selector) {
      target = $(selector)[0];
    }

    var config = $(target).data(DATA_KEY) ? 'toggle' : $.extend({}, $(target).data(), $(this).data());

    if (this.tagName === 'A' || this.tagName === 'AREA') {
      event.preventDefault();
    }

    var $target = $(target).one(Event.SHOW, function (showEvent) {
      if (showEvent.isDefaultPrevented()) {
        // only register focus restorer if modal will actually get shown
        return;
      }

      $target.one(Event.HIDDEN, function () {
        if ($(_this17).is(':visible')) {
          _this17.focus();
        }
      });
    });

    Modal._jQueryInterface.call($(target), config, this);
  });

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Modal._jQueryInterface;
  $.fn[NAME].Constructor = Modal;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Modal._jQueryInterface;
  };

  return Modal;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): scrollspy.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var ScrollSpy = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'scrollspy';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.scrollspy';
  var EVENT_KEY = '.' + DATA_KEY;
  var DATA_API_KEY = '.data-api';
  var JQUERY_NO_CONFLICT = $.fn[NAME];

  var Default = {
    offset: 10,
    method: 'auto',
    target: ''
  };

  var DefaultType = {
    offset: 'number',
    method: 'string',
    target: '(string|element)'
  };

  var Event = {
    ACTIVATE: 'activate' + EVENT_KEY,
    SCROLL: 'scroll' + EVENT_KEY,
    LOAD_DATA_API: 'load' + EVENT_KEY + DATA_API_KEY
  };

  var ClassName = {
    DROPDOWN_ITEM: 'dropdown-item',
    DROPDOWN_MENU: 'dropdown-menu',
    NAV_LINK: 'nav-link',
    NAV: 'nav',
    ACTIVE: 'active'
  };

  var Selector = {
    DATA_SPY: '[data-spy="scroll"]',
    ACTIVE: '.active',
    LIST_ITEM: '.list-item',
    LI: 'li',
    LI_DROPDOWN: 'li.dropdown',
    NAV_LINKS: '.nav-link',
    DROPDOWN: '.dropdown',
    DROPDOWN_ITEMS: '.dropdown-item',
    DROPDOWN_TOGGLE: '.dropdown-toggle'
  };

  var OffsetMethod = {
    OFFSET: 'offset',
    POSITION: 'position'
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var ScrollSpy = function () {
    function ScrollSpy(element, config) {
      var _this18 = this;

      _classCallCheck(this, ScrollSpy);

      this._element = element;
      this._scrollElement = element.tagName === 'BODY' ? window : element;
      this._config = this._getConfig(config);
      this._selector = this._config.target + ' ' + Selector.NAV_LINKS + ',' + (this._config.target + ' ' + Selector.DROPDOWN_ITEMS);
      this._offsets = [];
      this._targets = [];
      this._activeTarget = null;
      this._scrollHeight = 0;

      $(this._scrollElement).on(Event.SCROLL, function (event) {
        return _this18._process(event);
      });

      this.refresh();
      this._process();
    }

    // getters

    // public

    ScrollSpy.prototype.refresh = function refresh() {
      var _this19 = this;

      var autoMethod = this._scrollElement !== this._scrollElement.window ? OffsetMethod.POSITION : OffsetMethod.OFFSET;

      var offsetMethod = this._config.method === 'auto' ? autoMethod : this._config.method;

      var offsetBase = offsetMethod === OffsetMethod.POSITION ? this._getScrollTop() : 0;

      this._offsets = [];
      this._targets = [];

      this._scrollHeight = this._getScrollHeight();

      var targets = $.makeArray($(this._selector));

      targets.map(function (element) {
        var target = void 0;
        var targetSelector = Util.getSelectorFromElement(element);

        if (targetSelector) {
          target = $(targetSelector)[0];
        }

        if (target && (target.offsetWidth || target.offsetHeight)) {
          // todo (fat): remove sketch reliance on jQuery position/offset
          return [$(target)[offsetMethod]().top + offsetBase, targetSelector];
        }
        return null;
      }).filter(function (item) {
        return item;
      }).sort(function (a, b) {
        return a[0] - b[0];
      }).forEach(function (item) {
        _this19._offsets.push(item[0]);
        _this19._targets.push(item[1]);
      });
    };

    ScrollSpy.prototype.dispose = function dispose() {
      $.removeData(this._element, DATA_KEY);
      $(this._scrollElement).off(EVENT_KEY);

      this._element = null;
      this._scrollElement = null;
      this._config = null;
      this._selector = null;
      this._offsets = null;
      this._targets = null;
      this._activeTarget = null;
      this._scrollHeight = null;
    };

    // private

    ScrollSpy.prototype._getConfig = function _getConfig(config) {
      config = $.extend({}, Default, config);

      if (typeof config.target !== 'string') {
        var id = $(config.target).attr('id');
        if (!id) {
          id = Util.getUID(NAME);
          $(config.target).attr('id', id);
        }
        config.target = '#' + id;
      }

      Util.typeCheckConfig(NAME, config, DefaultType);

      return config;
    };

    ScrollSpy.prototype._getScrollTop = function _getScrollTop() {
      return this._scrollElement === window ? this._scrollElement.pageYOffset : this._scrollElement.scrollTop;
    };

    ScrollSpy.prototype._getScrollHeight = function _getScrollHeight() {
      return this._scrollElement.scrollHeight || Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    };

    ScrollSpy.prototype._getOffsetHeight = function _getOffsetHeight() {
      return this._scrollElement === window ? window.innerHeight : this._scrollElement.offsetHeight;
    };

    ScrollSpy.prototype._process = function _process() {
      var scrollTop = this._getScrollTop() + this._config.offset;
      var scrollHeight = this._getScrollHeight();
      var maxScroll = this._config.offset + scrollHeight - this._getOffsetHeight();

      if (this._scrollHeight !== scrollHeight) {
        this.refresh();
      }

      if (scrollTop >= maxScroll) {
        var target = this._targets[this._targets.length - 1];

        if (this._activeTarget !== target) {
          this._activate(target);
        }
        return;
      }

      if (this._activeTarget && scrollTop < this._offsets[0] && this._offsets[0] > 0) {
        this._activeTarget = null;
        this._clear();
        return;
      }

      for (var i = this._offsets.length; i--;) {
        var isActiveTarget = this._activeTarget !== this._targets[i] && scrollTop >= this._offsets[i] && (this._offsets[i + 1] === undefined || scrollTop < this._offsets[i + 1]);

        if (isActiveTarget) {
          this._activate(this._targets[i]);
        }
      }
    };

    ScrollSpy.prototype._activate = function _activate(target) {
      this._activeTarget = target;

      this._clear();

      var queries = this._selector.split(',');
      queries = queries.map(function (selector) {
        return selector + '[data-target="' + target + '"],' + (selector + '[href="' + target + '"]');
      });

      var $link = $(queries.join(','));

      if ($link.hasClass(ClassName.DROPDOWN_ITEM)) {
        $link.closest(Selector.DROPDOWN).find(Selector.DROPDOWN_TOGGLE).addClass(ClassName.ACTIVE);
        $link.addClass(ClassName.ACTIVE);
      } else {
        // todo (fat) this is kinda sus...
        // recursively add actives to tested nav-links
        $link.parents(Selector.LI).find('> ' + Selector.NAV_LINKS).addClass(ClassName.ACTIVE);
      }

      $(this._scrollElement).trigger(Event.ACTIVATE, {
        relatedTarget: target
      });
    };

    ScrollSpy.prototype._clear = function _clear() {
      $(this._selector).filter(Selector.ACTIVE).removeClass(ClassName.ACTIVE);
    };

    // static

    ScrollSpy._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var data = $(this).data(DATA_KEY);
        var _config = (typeof config === 'undefined' ? 'undefined' : _typeof(config)) === 'object' && config;

        if (!data) {
          data = new ScrollSpy(this, _config);
          $(this).data(DATA_KEY, data);
        }

        if (typeof config === 'string') {
          if (data[config] === undefined) {
            throw new Error('No method named "' + config + '"');
          }
          data[config]();
        }
      });
    };

    _createClass(ScrollSpy, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }, {
      key: 'Default',
      get: function get() {
        return Default;
      }
    }]);

    return ScrollSpy;
  }();

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  $(window).on(Event.LOAD_DATA_API, function () {
    var scrollSpys = $.makeArray($(Selector.DATA_SPY));

    for (var i = scrollSpys.length; i--;) {
      var $spy = $(scrollSpys[i]);
      ScrollSpy._jQueryInterface.call($spy, $spy.data());
    }
  });

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = ScrollSpy._jQueryInterface;
  $.fn[NAME].Constructor = ScrollSpy;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return ScrollSpy._jQueryInterface;
  };

  return ScrollSpy;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): tab.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Tab = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'tab';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.tab';
  var EVENT_KEY = '.' + DATA_KEY;
  var DATA_API_KEY = '.data-api';
  var JQUERY_NO_CONFLICT = $.fn[NAME];
  var TRANSITION_DURATION = 150;

  var Event = {
    HIDE: 'hide' + EVENT_KEY,
    HIDDEN: 'hidden' + EVENT_KEY,
    SHOW: 'show' + EVENT_KEY,
    SHOWN: 'shown' + EVENT_KEY,
    CLICK_DATA_API: 'click' + EVENT_KEY + DATA_API_KEY
  };

  var ClassName = {
    DROPDOWN_MENU: 'dropdown-menu',
    ACTIVE: 'active',
    DISABLED: 'disabled',
    FADE: 'fade',
    SHOW: 'show'
  };

  var Selector = {
    A: 'a',
    LI: 'li',
    DROPDOWN: '.dropdown',
    LIST: 'ul:not(.dropdown-menu), ol:not(.dropdown-menu), nav:not(.dropdown-menu)',
    FADE_CHILD: '> .nav-item .fade, > .fade',
    ACTIVE: '.active',
    ACTIVE_CHILD: '> .nav-item > .active, > .active',
    DATA_TOGGLE: '[data-toggle="tab"], [data-toggle="pill"]',
    DROPDOWN_TOGGLE: '.dropdown-toggle',
    DROPDOWN_ACTIVE_CHILD: '> .dropdown-menu .active'
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Tab = function () {
    function Tab(element) {
      _classCallCheck(this, Tab);

      this._element = element;
    }

    // getters

    // public

    Tab.prototype.show = function show() {
      var _this20 = this;

      if (this._element.parentNode && this._element.parentNode.nodeType === Node.ELEMENT_NODE && $(this._element).hasClass(ClassName.ACTIVE) || $(this._element).hasClass(ClassName.DISABLED)) {
        return;
      }

      var target = void 0;
      var previous = void 0;
      var listElement = $(this._element).closest(Selector.LIST)[0];
      var selector = Util.getSelectorFromElement(this._element);

      if (listElement) {
        previous = $.makeArray($(listElement).find(Selector.ACTIVE));
        previous = previous[previous.length - 1];
      }

      var hideEvent = $.Event(Event.HIDE, {
        relatedTarget: this._element
      });

      var showEvent = $.Event(Event.SHOW, {
        relatedTarget: previous
      });

      if (previous) {
        $(previous).trigger(hideEvent);
      }

      $(this._element).trigger(showEvent);

      if (showEvent.isDefaultPrevented() || hideEvent.isDefaultPrevented()) {
        return;
      }

      if (selector) {
        target = $(selector)[0];
      }

      this._activate(this._element, listElement);

      var complete = function complete() {
        var hiddenEvent = $.Event(Event.HIDDEN, {
          relatedTarget: _this20._element
        });

        var shownEvent = $.Event(Event.SHOWN, {
          relatedTarget: previous
        });

        $(previous).trigger(hiddenEvent);
        $(_this20._element).trigger(shownEvent);
      };

      if (target) {
        this._activate(target, target.parentNode, complete);
      } else {
        complete();
      }
    };

    Tab.prototype.dispose = function dispose() {
      $.removeClass(this._element, DATA_KEY);
      this._element = null;
    };

    // private

    Tab.prototype._activate = function _activate(element, container, callback) {
      var _this21 = this;

      var active = $(container).find(Selector.ACTIVE_CHILD)[0];
      var isTransitioning = callback && Util.supportsTransitionEnd() && (active && $(active).hasClass(ClassName.FADE) || Boolean($(container).find(Selector.FADE_CHILD)[0]));

      var complete = function complete() {
        return _this21._transitionComplete(element, active, isTransitioning, callback);
      };

      if (active && isTransitioning) {
        $(active).one(Util.TRANSITION_END, complete).emulateTransitionEnd(TRANSITION_DURATION);
      } else {
        complete();
      }

      if (active) {
        $(active).removeClass(ClassName.SHOW);
      }
    };

    Tab.prototype._transitionComplete = function _transitionComplete(element, active, isTransitioning, callback) {
      if (active) {
        $(active).removeClass(ClassName.ACTIVE);

        var dropdownChild = $(active.parentNode).find(Selector.DROPDOWN_ACTIVE_CHILD)[0];

        if (dropdownChild) {
          $(dropdownChild).removeClass(ClassName.ACTIVE);
        }

        active.setAttribute('aria-expanded', false);
      }

      $(element).addClass(ClassName.ACTIVE);
      element.setAttribute('aria-expanded', true);

      if (isTransitioning) {
        Util.reflow(element);
        $(element).addClass(ClassName.SHOW);
      } else {
        $(element).removeClass(ClassName.FADE);
      }

      if (element.parentNode && $(element.parentNode).hasClass(ClassName.DROPDOWN_MENU)) {

        var dropdownElement = $(element).closest(Selector.DROPDOWN)[0];
        if (dropdownElement) {
          $(dropdownElement).find(Selector.DROPDOWN_TOGGLE).addClass(ClassName.ACTIVE);
        }

        element.setAttribute('aria-expanded', true);
      }

      if (callback) {
        callback();
      }
    };

    // static

    Tab._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var $this = $(this);
        var data = $this.data(DATA_KEY);

        if (!data) {
          data = new Tab(this);
          $this.data(DATA_KEY, data);
        }

        if (typeof config === 'string') {
          if (data[config] === undefined) {
            throw new Error('No method named "' + config + '"');
          }
          data[config]();
        }
      });
    };

    _createClass(Tab, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }]);

    return Tab;
  }();

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  $(document).on(Event.CLICK_DATA_API, Selector.DATA_TOGGLE, function (event) {
    event.preventDefault();
    Tab._jQueryInterface.call($(this), 'show');
  });

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Tab._jQueryInterface;
  $.fn[NAME].Constructor = Tab;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Tab._jQueryInterface;
  };

  return Tab;
}(jQuery);

/* global Tether */

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): tooltip.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Tooltip = function ($) {

  /**
   * Check for Tether dependency
   * Tether - http://tether.io/
   */
  if (typeof Tether === 'undefined') {
    throw new Error('Bootstrap tooltips require Tether (http://tether.io/)');
  }

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'tooltip';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.tooltip';
  var EVENT_KEY = '.' + DATA_KEY;
  var JQUERY_NO_CONFLICT = $.fn[NAME];
  var TRANSITION_DURATION = 150;
  var CLASS_PREFIX = 'bs-tether';

  var Default = {
    animation: true,
    template: '<div class="tooltip" role="tooltip">' + '<div class="tooltip-inner"></div></div>',
    trigger: 'hover focus',
    title: '',
    delay: 0,
    html: false,
    selector: false,
    placement: 'top',
    offset: '0 0',
    constraints: [],
    container: false
  };

  var DefaultType = {
    animation: 'boolean',
    template: 'string',
    title: '(string|element|function)',
    trigger: 'string',
    delay: '(number|object)',
    html: 'boolean',
    selector: '(string|boolean)',
    placement: '(string|function)',
    offset: 'string',
    constraints: 'array',
    container: '(string|element|boolean)'
  };

  var AttachmentMap = {
    TOP: 'bottom center',
    RIGHT: 'middle left',
    BOTTOM: 'top center',
    LEFT: 'middle right'
  };

  var HoverState = {
    SHOW: 'show',
    OUT: 'out'
  };

  var Event = {
    HIDE: 'hide' + EVENT_KEY,
    HIDDEN: 'hidden' + EVENT_KEY,
    SHOW: 'show' + EVENT_KEY,
    SHOWN: 'shown' + EVENT_KEY,
    INSERTED: 'inserted' + EVENT_KEY,
    CLICK: 'click' + EVENT_KEY,
    FOCUSIN: 'focusin' + EVENT_KEY,
    FOCUSOUT: 'focusout' + EVENT_KEY,
    MOUSEENTER: 'mouseenter' + EVENT_KEY,
    MOUSELEAVE: 'mouseleave' + EVENT_KEY
  };

  var ClassName = {
    FADE: 'fade',
    SHOW: 'show'
  };

  var Selector = {
    TOOLTIP: '.tooltip',
    TOOLTIP_INNER: '.tooltip-inner'
  };

  var TetherClass = {
    element: false,
    enabled: false
  };

  var Trigger = {
    HOVER: 'hover',
    FOCUS: 'focus',
    CLICK: 'click',
    MANUAL: 'manual'
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Tooltip = function () {
    function Tooltip(element, config) {
      _classCallCheck(this, Tooltip);

      // private
      this._isEnabled = true;
      this._timeout = 0;
      this._hoverState = '';
      this._activeTrigger = {};
      this._isTransitioning = false;
      this._tether = null;

      // protected
      this.element = element;
      this.config = this._getConfig(config);
      this.tip = null;

      this._setListeners();
    }

    // getters

    // public

    Tooltip.prototype.enable = function enable() {
      this._isEnabled = true;
    };

    Tooltip.prototype.disable = function disable() {
      this._isEnabled = false;
    };

    Tooltip.prototype.toggleEnabled = function toggleEnabled() {
      this._isEnabled = !this._isEnabled;
    };

    Tooltip.prototype.toggle = function toggle(event) {
      if (event) {
        var dataKey = this.constructor.DATA_KEY;
        var context = $(event.currentTarget).data(dataKey);

        if (!context) {
          context = new this.constructor(event.currentTarget, this._getDelegateConfig());
          $(event.currentTarget).data(dataKey, context);
        }

        context._activeTrigger.click = !context._activeTrigger.click;

        if (context._isWithActiveTrigger()) {
          context._enter(null, context);
        } else {
          context._leave(null, context);
        }
      } else {

        if ($(this.getTipElement()).hasClass(ClassName.SHOW)) {
          this._leave(null, this);
          return;
        }

        this._enter(null, this);
      }
    };

    Tooltip.prototype.dispose = function dispose() {
      clearTimeout(this._timeout);

      this.cleanupTether();

      $.removeData(this.element, this.constructor.DATA_KEY);

      $(this.element).off(this.constructor.EVENT_KEY);
      $(this.element).closest('.modal').off('hide.bs.modal');

      if (this.tip) {
        $(this.tip).remove();
      }

      this._isEnabled = null;
      this._timeout = null;
      this._hoverState = null;
      this._activeTrigger = null;
      this._tether = null;

      this.element = null;
      this.config = null;
      this.tip = null;
    };

    Tooltip.prototype.show = function show() {
      var _this22 = this;

      if ($(this.element).css('display') === 'none') {
        throw new Error('Please use show on visible elements');
      }

      var showEvent = $.Event(this.constructor.Event.SHOW);
      if (this.isWithContent() && this._isEnabled) {
        if (this._isTransitioning) {
          throw new Error('Tooltip is transitioning');
        }
        $(this.element).trigger(showEvent);

        var isInTheDom = $.contains(this.element.ownerDocument.documentElement, this.element);

        if (showEvent.isDefaultPrevented() || !isInTheDom) {
          return;
        }

        var tip = this.getTipElement();
        var tipId = Util.getUID(this.constructor.NAME);

        tip.setAttribute('id', tipId);
        this.element.setAttribute('aria-describedby', tipId);

        this.setContent();

        if (this.config.animation) {
          $(tip).addClass(ClassName.FADE);
        }

        var placement = typeof this.config.placement === 'function' ? this.config.placement.call(this, tip, this.element) : this.config.placement;

        var attachment = this._getAttachment(placement);

        var container = this.config.container === false ? document.body : $(this.config.container);

        $(tip).data(this.constructor.DATA_KEY, this).appendTo(container);

        $(this.element).trigger(this.constructor.Event.INSERTED);

        this._tether = new Tether({
          attachment: attachment,
          element: tip,
          target: this.element,
          classes: TetherClass,
          classPrefix: CLASS_PREFIX,
          offset: this.config.offset,
          constraints: this.config.constraints,
          addTargetClasses: false
        });

        Util.reflow(tip);
        this._tether.position();

        $(tip).addClass(ClassName.SHOW);

        var complete = function complete() {
          var prevHoverState = _this22._hoverState;
          _this22._hoverState = null;
          _this22._isTransitioning = false;

          $(_this22.element).trigger(_this22.constructor.Event.SHOWN);

          if (prevHoverState === HoverState.OUT) {
            _this22._leave(null, _this22);
          }
        };

        if (Util.supportsTransitionEnd() && $(this.tip).hasClass(ClassName.FADE)) {
          this._isTransitioning = true;
          $(this.tip).one(Util.TRANSITION_END, complete).emulateTransitionEnd(Tooltip._TRANSITION_DURATION);
          return;
        }

        complete();
      }
    };

    Tooltip.prototype.hide = function hide(callback) {
      var _this23 = this;

      var tip = this.getTipElement();
      var hideEvent = $.Event(this.constructor.Event.HIDE);
      if (this._isTransitioning) {
        throw new Error('Tooltip is transitioning');
      }
      var complete = function complete() {
        if (_this23._hoverState !== HoverState.SHOW && tip.parentNode) {
          tip.parentNode.removeChild(tip);
        }

        _this23.element.removeAttribute('aria-describedby');
        $(_this23.element).trigger(_this23.constructor.Event.HIDDEN);
        _this23._isTransitioning = false;
        _this23.cleanupTether();

        if (callback) {
          callback();
        }
      };

      $(this.element).trigger(hideEvent);

      if (hideEvent.isDefaultPrevented()) {
        return;
      }

      $(tip).removeClass(ClassName.SHOW);

      this._activeTrigger[Trigger.CLICK] = false;
      this._activeTrigger[Trigger.FOCUS] = false;
      this._activeTrigger[Trigger.HOVER] = false;

      if (Util.supportsTransitionEnd() && $(this.tip).hasClass(ClassName.FADE)) {
        this._isTransitioning = true;
        $(tip).one(Util.TRANSITION_END, complete).emulateTransitionEnd(TRANSITION_DURATION);
      } else {
        complete();
      }

      this._hoverState = '';
    };

    // protected

    Tooltip.prototype.isWithContent = function isWithContent() {
      return Boolean(this.getTitle());
    };

    Tooltip.prototype.getTipElement = function getTipElement() {
      return this.tip = this.tip || $(this.config.template)[0];
    };

    Tooltip.prototype.setContent = function setContent() {
      var $tip = $(this.getTipElement());

      this.setElementContent($tip.find(Selector.TOOLTIP_INNER), this.getTitle());

      $tip.removeClass(ClassName.FADE + ' ' + ClassName.SHOW);

      this.cleanupTether();
    };

    Tooltip.prototype.setElementContent = function setElementContent($element, content) {
      var html = this.config.html;
      if ((typeof content === 'undefined' ? 'undefined' : _typeof(content)) === 'object' && (content.nodeType || content.jquery)) {
        // content is a DOM node or a jQuery
        if (html) {
          if (!$(content).parent().is($element)) {
            $element.empty().append(content);
          }
        } else {
          $element.text($(content).text());
        }
      } else {
        $element[html ? 'html' : 'text'](content);
      }
    };

    Tooltip.prototype.getTitle = function getTitle() {
      var title = this.element.getAttribute('data-original-title');

      if (!title) {
        title = typeof this.config.title === 'function' ? this.config.title.call(this.element) : this.config.title;
      }

      return title;
    };

    Tooltip.prototype.cleanupTether = function cleanupTether() {
      if (this._tether) {
        this._tether.destroy();
      }
    };

    // private

    Tooltip.prototype._getAttachment = function _getAttachment(placement) {
      return AttachmentMap[placement.toUpperCase()];
    };

    Tooltip.prototype._setListeners = function _setListeners() {
      var _this24 = this;

      var triggers = this.config.trigger.split(' ');

      triggers.forEach(function (trigger) {
        if (trigger === 'click') {
          $(_this24.element).on(_this24.constructor.Event.CLICK, _this24.config.selector, function (event) {
            return _this24.toggle(event);
          });
        } else if (trigger !== Trigger.MANUAL) {
          var eventIn = trigger === Trigger.HOVER ? _this24.constructor.Event.MOUSEENTER : _this24.constructor.Event.FOCUSIN;
          var eventOut = trigger === Trigger.HOVER ? _this24.constructor.Event.MOUSELEAVE : _this24.constructor.Event.FOCUSOUT;

          $(_this24.element).on(eventIn, _this24.config.selector, function (event) {
            return _this24._enter(event);
          }).on(eventOut, _this24.config.selector, function (event) {
            return _this24._leave(event);
          });
        }

        $(_this24.element).closest('.modal').on('hide.bs.modal', function () {
          return _this24.hide();
        });
      });

      if (this.config.selector) {
        this.config = $.extend({}, this.config, {
          trigger: 'manual',
          selector: ''
        });
      } else {
        this._fixTitle();
      }
    };

    Tooltip.prototype._fixTitle = function _fixTitle() {
      var titleType = _typeof(this.element.getAttribute('data-original-title'));
      if (this.element.getAttribute('title') || titleType !== 'string') {
        this.element.setAttribute('data-original-title', this.element.getAttribute('title') || '');
        this.element.setAttribute('title', '');
      }
    };

    Tooltip.prototype._enter = function _enter(event, context) {
      var dataKey = this.constructor.DATA_KEY;

      context = context || $(event.currentTarget).data(dataKey);

      if (!context) {
        context = new this.constructor(event.currentTarget, this._getDelegateConfig());
        $(event.currentTarget).data(dataKey, context);
      }

      if (event) {
        context._activeTrigger[event.type === 'focusin' ? Trigger.FOCUS : Trigger.HOVER] = true;
      }

      if ($(context.getTipElement()).hasClass(ClassName.SHOW) || context._hoverState === HoverState.SHOW) {
        context._hoverState = HoverState.SHOW;
        return;
      }

      clearTimeout(context._timeout);

      context._hoverState = HoverState.SHOW;

      if (!context.config.delay || !context.config.delay.show) {
        context.show();
        return;
      }

      context._timeout = setTimeout(function () {
        if (context._hoverState === HoverState.SHOW) {
          context.show();
        }
      }, context.config.delay.show);
    };

    Tooltip.prototype._leave = function _leave(event, context) {
      var dataKey = this.constructor.DATA_KEY;

      context = context || $(event.currentTarget).data(dataKey);

      if (!context) {
        context = new this.constructor(event.currentTarget, this._getDelegateConfig());
        $(event.currentTarget).data(dataKey, context);
      }

      if (event) {
        context._activeTrigger[event.type === 'focusout' ? Trigger.FOCUS : Trigger.HOVER] = false;
      }

      if (context._isWithActiveTrigger()) {
        return;
      }

      clearTimeout(context._timeout);

      context._hoverState = HoverState.OUT;

      if (!context.config.delay || !context.config.delay.hide) {
        context.hide();
        return;
      }

      context._timeout = setTimeout(function () {
        if (context._hoverState === HoverState.OUT) {
          context.hide();
        }
      }, context.config.delay.hide);
    };

    Tooltip.prototype._isWithActiveTrigger = function _isWithActiveTrigger() {
      for (var trigger in this._activeTrigger) {
        if (this._activeTrigger[trigger]) {
          return true;
        }
      }

      return false;
    };

    Tooltip.prototype._getConfig = function _getConfig(config) {
      config = $.extend({}, this.constructor.Default, $(this.element).data(), config);

      if (config.delay && typeof config.delay === 'number') {
        config.delay = {
          show: config.delay,
          hide: config.delay
        };
      }

      Util.typeCheckConfig(NAME, config, this.constructor.DefaultType);

      return config;
    };

    Tooltip.prototype._getDelegateConfig = function _getDelegateConfig() {
      var config = {};

      if (this.config) {
        for (var key in this.config) {
          if (this.constructor.Default[key] !== this.config[key]) {
            config[key] = this.config[key];
          }
        }
      }

      return config;
    };

    // static

    Tooltip._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var data = $(this).data(DATA_KEY);
        var _config = (typeof config === 'undefined' ? 'undefined' : _typeof(config)) === 'object' && config;

        if (!data && /dispose|hide/.test(config)) {
          return;
        }

        if (!data) {
          data = new Tooltip(this, _config);
          $(this).data(DATA_KEY, data);
        }

        if (typeof config === 'string') {
          if (data[config] === undefined) {
            throw new Error('No method named "' + config + '"');
          }
          data[config]();
        }
      });
    };

    _createClass(Tooltip, null, [{
      key: 'VERSION',
      get: function get() {
        return VERSION;
      }
    }, {
      key: 'Default',
      get: function get() {
        return Default;
      }
    }, {
      key: 'NAME',
      get: function get() {
        return NAME;
      }
    }, {
      key: 'DATA_KEY',
      get: function get() {
        return DATA_KEY;
      }
    }, {
      key: 'Event',
      get: function get() {
        return Event;
      }
    }, {
      key: 'EVENT_KEY',
      get: function get() {
        return EVENT_KEY;
      }
    }, {
      key: 'DefaultType',
      get: function get() {
        return DefaultType;
      }
    }]);

    return Tooltip;
  }();

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Tooltip._jQueryInterface;
  $.fn[NAME].Constructor = Tooltip;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Tooltip._jQueryInterface;
  };

  return Tooltip;
}(jQuery);

/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.0.0-alpha.6): popover.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

var Popover = function ($) {

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  var NAME = 'popover';
  var VERSION = '4.0.0-alpha.6';
  var DATA_KEY = 'bs.popover';
  var EVENT_KEY = '.' + DATA_KEY;
  var JQUERY_NO_CONFLICT = $.fn[NAME];

  var Default = $.extend({}, Tooltip.Default, {
    placement: 'right',
    trigger: 'click',
    content: '',
    template: '<div class="popover" role="tooltip">' + '<h3 class="popover-title"></h3>' + '<div class="popover-content"></div></div>'
  });

  var DefaultType = $.extend({}, Tooltip.DefaultType, {
    content: '(string|element|function)'
  });

  var ClassName = {
    FADE: 'fade',
    SHOW: 'show'
  };

  var Selector = {
    TITLE: '.popover-title',
    CONTENT: '.popover-content'
  };

  var Event = {
    HIDE: 'hide' + EVENT_KEY,
    HIDDEN: 'hidden' + EVENT_KEY,
    SHOW: 'show' + EVENT_KEY,
    SHOWN: 'shown' + EVENT_KEY,
    INSERTED: 'inserted' + EVENT_KEY,
    CLICK: 'click' + EVENT_KEY,
    FOCUSIN: 'focusin' + EVENT_KEY,
    FOCUSOUT: 'focusout' + EVENT_KEY,
    MOUSEENTER: 'mouseenter' + EVENT_KEY,
    MOUSELEAVE: 'mouseleave' + EVENT_KEY
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  var Popover = function (_Tooltip) {
    _inherits(Popover, _Tooltip);

    function Popover() {
      _classCallCheck(this, Popover);

      return _possibleConstructorReturn(this, _Tooltip.apply(this, arguments));
    }

    // overrides

    Popover.prototype.isWithContent = function isWithContent() {
      return this.getTitle() || this._getContent();
    };

    Popover.prototype.getTipElement = function getTipElement() {
      return this.tip = this.tip || $(this.config.template)[0];
    };

    Popover.prototype.setContent = function setContent() {
      var $tip = $(this.getTipElement());

      // we use append for html objects to maintain js events
      this.setElementContent($tip.find(Selector.TITLE), this.getTitle());
      this.setElementContent($tip.find(Selector.CONTENT), this._getContent());

      $tip.removeClass(ClassName.FADE + ' ' + ClassName.SHOW);

      this.cleanupTether();
    };

    // private

    Popover.prototype._getContent = function _getContent() {
      return this.element.getAttribute('data-content') || (typeof this.config.content === 'function' ? this.config.content.call(this.element) : this.config.content);
    };

    // static

    Popover._jQueryInterface = function _jQueryInterface(config) {
      return this.each(function () {
        var data = $(this).data(DATA_KEY);
        var _config = (typeof config === 'undefined' ? 'undefined' : _typeof(config)) === 'object' ? config : null;

        if (!data && /destroy|hide/.test(config)) {
          return;
        }

        if (!data) {
          data = new Popover(this, _config);
          $(this).data(DATA_KEY, data);
        }

        if (typeof config === 'string') {
          if (data[config] === undefined) {
            throw new Error('No method named "' + config + '"');
          }
          data[config]();
        }
      });
    };

    _createClass(Popover, null, [{
      key: 'VERSION',


      // getters

      get: function get() {
        return VERSION;
      }
    }, {
      key: 'Default',
      get: function get() {
        return Default;
      }
    }, {
      key: 'NAME',
      get: function get() {
        return NAME;
      }
    }, {
      key: 'DATA_KEY',
      get: function get() {
        return DATA_KEY;
      }
    }, {
      key: 'Event',
      get: function get() {
        return Event;
      }
    }, {
      key: 'EVENT_KEY',
      get: function get() {
        return EVENT_KEY;
      }
    }, {
      key: 'DefaultType',
      get: function get() {
        return DefaultType;
      }
    }]);

    return Popover;
  }(Tooltip);

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   */

  $.fn[NAME] = Popover._jQueryInterface;
  $.fn[NAME].Constructor = Popover;
  $.fn[NAME].noConflict = function () {
    $.fn[NAME] = JQUERY_NO_CONFLICT;
    return Popover._jQueryInterface;
  };

  return Popover;
}(jQuery);

}();
(function($) {
  window.NestedFormEvents = function() {
    this.addFields = $.proxy(this.addFields, this);
    this.removeFields = $.proxy(this.removeFields, this);
  };

  NestedFormEvents.prototype = {
    addFields: function(e) {
      // Setup
      var link      = e.currentTarget;
      var assoc     = $(link).data('association');                // Name of child
      var blueprint = $('#' + $(link).data('blueprint-id'));
      var content   = blueprint.data('blueprint');                // Fields template

      // Make the context correct by replacing <parents> with the generated ID
      // of each of the parent objects
      var context = ($(link).closest('.fields').closestChild('input, textarea, select').eq(0).attr('name') || '').replace(new RegExp('\[[a-z_]+\]$'), '');

      // context will be something like this for a brand new form:
      // project[tasks_attributes][1255929127459][assignments_attributes][1255929128105]
      // or for an edit form:
      // project[tasks_attributes][0][assignments_attributes][1]
      if (context) {
        var parentNames = context.match(/[a-z_]+_attributes(?=\]\[(new_)?\d+\])/g) || [];
        var parentIds   = context.match(/[0-9]+/g) || [];

        for(var i = 0; i < parentNames.length; i++) {
          if(parentIds[i]) {
            content = content.replace(
              new RegExp('(_' + parentNames[i] + ')_.+?_', 'g'),
              '$1_' + parentIds[i] + '_');

            content = content.replace(
              new RegExp('(\\[' + parentNames[i] + '\\])\\[.+?\\]', 'g'),
              '$1[' + parentIds[i] + ']');
          }
        }
      }

      // Make a unique ID for the new child
      var regexp  = new RegExp('new_' + assoc, 'g');
      var new_id  = this.newId();
      content     = $.trim(content.replace(regexp, new_id));

      var field = this.insertFields(content, assoc, link);
      // bubble up event upto document (through form)
      field
        .trigger({ type: 'nested:fieldAdded', field: field })
        .trigger({ type: 'nested:fieldAdded:' + assoc, field: field });
      return false;
    },
    newId: function() {
      return new Date().getTime();
    },
    insertFields: function(content, assoc, link) {
      var target = $(link).data('target');
      if (target) {
        return $(content).appendTo($(target));
      } else {
        return $(content).insertBefore(link);
      }
    },
    removeFields: function(e) {
      var $link = $(e.currentTarget),
          assoc = $link.data('association'); // Name of child to be removed
      
      var hiddenField = $link.prev('input[type=hidden]');
      hiddenField.val('1');
      
      var field = $link.closest('.fields');
      field.hide();
      
      field
        .trigger({ type: 'nested:fieldRemoved', field: field })
        .trigger({ type: 'nested:fieldRemoved:' + assoc, field: field });
      return false;
    }
  };

  window.nestedFormEvents = new NestedFormEvents();
  $(document)
    .delegate('form a.add_nested_fields',    'click', nestedFormEvents.addFields)
    .delegate('form a.remove_nested_fields', 'click', nestedFormEvents.removeFields);
})(jQuery);

// http://plugins.jquery.com/project/closestChild
/*
 * Copyright 2011, Tobias Lindig
 *
 * Dual licensed under the MIT (http://www.opensource.org/licenses/mit-license.php)
 * and GPL (http://www.opensource.org/licenses/gpl-license.php) licenses.
 *
 */
(function($) {
        $.fn.closestChild = function(selector) {
                // breadth first search for the first matched node
                if (selector && selector != '') {
                        var queue = [];
                        queue.push(this);
                        while(queue.length > 0) {
                                var node = queue.shift();
                                var children = node.children();
                                for(var i = 0; i < children.length; ++i) {
                                        var child = $(children[i]);
                                        if (child.is(selector)) {
                                                return child; //well, we found one
                                        }
                                        queue.push(child);
                                }
                        }
                }
                return $();//nothing found
        };
})(jQuery);
//  This file is part of the jQuery formatCurrency Plugin.
//
//    The jQuery formatCurrency Plugin is free software: you can redistribute it
//    and/or modify it under the terms of the GNU Lesser General Public License as published 
//    by the Free Software Foundation, either version 3 of the License, or
//    (at your option) any later version.

//    The jQuery formatCurrency Plugin is distributed in the hope that it will
//    be useful, but WITHOUT ANY WARRANTY; without even the implied warranty 
//    of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
//    GNU Lesser General Public License for more details.
//
//    You should have received a copy of the GNU Lesser General Public License along with 
//    the jQuery formatCurrency Plugin.  If not, see <http://www.gnu.org/licenses/>.

;(function ($) {
	$.formatCurrency = {};
	$.formatCurrencyLive = {};
	$.toNumber = {};
	$.asNumber = {};

	var fcDefaults = {
		colorize: false,
		region: '',
		roundToDecimalPlace: 2, // roundToDecimalPlace: -2; for not allowing decimals; roundToDecimalPlace: -1; for no rounding; 0 to round to the dollar; 1 for one digit cents; 2 for two digit cents; 3 for three digit cents; ...
		minimumDecimalPlaces: 9,
		eventOnDecimalsEntered: false,
		suppressCurrencySymbol: false,
		removeTrailingZerosOnDecimal: false,
		parseAsFloat: false
	};
	
	var fcLiveDefaults = {
		decPointCharCodes: [], // by index: 0 - key code for key press event; 1 & 2 key codes for keyup event for the decimal point keys on the keyboard and keypad, respectively
		formatOnBlur: true,
		filterKeys: true,
		formatOnType: true
	};

	var toNumberDefaults = {
		region: ''
	};

	var asNumberDefaults = {
		region: '',
		parse: true,
		parseType: 'Float'
	};

	$.formatCurrency.regions = [];

	// default Region is en
	$.formatCurrency.regions[''] = {
		symbol: '$',
		positiveFormat: '%s%n',
		negativeFormat: '(%s%n)',
		decimalSymbol: '.',
		digitGroupSymbol: ',',
		groupDigits: true
	};

	$.formatCurrencyLive.setDefaults = function(settings) {
		$.extend(fcLiveDefaults, settings);
	};

	$.fn.formatCurrencyLive = function (settings) {
		return this.each(function () {
			var $this = $(this);

			settings = buildSettingsObjGraph(
				settings,
				$.extend(
					{},
					fcDefaults,
					fcLiveDefaults,
					($this.data('formatCurrency') ? $this.data('formatCurrency') : {})
					)
				);

			// setup the decimal point charCode setting according to the region
			if (settings.roundToDecimalPlace === -2)
				settings.decPointCharCodes = [-1, -1, -1];
			else {
				if (settings.decimalSymbol == ',')
					settings.decPointCharCodes = [44, 188, 110];
				else
					settings.decPointCharCodes = [46, 190, 110];
			}

			$this.data('formatCurrency', settings);

			if (settings.filterKeys)
				$this.off('keypress.formatCurrency')
			.on('keypress.formatCurrency', function (ev) {
				if (!keyAllowed(ev, settings.decPointCharCodes)) ev.preventDefault();
			});

			if (settings.formatOnType) {
				var settingsFmtOnType = $.extend({}, settings, { roundToDecimalPlace: -1, removeTrailingZerosOnDecimal: false });
				$this.off('keyup.formatCurrency')
				.on('keyup.formatCurrency', function (ev) {
					if (keyAllowed(ev, settings.decPointCharCodes)) $(this).formatCurrency(settingsFmtOnType);
				});
			}

			if (settings.formatOnBlur)
				$this.on('blur.formatCurrency', function (ev) {
					$(this).formatCurrency(settings);
				});

			$(this).formatCurrency(settings);
		});
	};

	$.formatCurrency.setDefaults = function(settings) {
		$.extend(fcDefaults, settings);
	};

	$.formatCurrency.setAllDefaults = function(settings) {
		for (var prop in settings) {
			if(fcDefaults.hasOwnProperty(prop)) fcDefaults[prop] = settings[prop];
			if(fcLiveDefaults.hasOwnProperty(prop)) fcLiveDefaults[prop] = settings[prop];
			if(toNumberDefaults.hasOwnProperty(prop)) toNumberDefaults[prop] = settings[prop];
			if(asNumberDefaults.hasOwnProperty(prop)) asNumberDefaults[prop] = settings[prop];
		}
	};

	$.fn.formatCurrency = function (destination, settings) {

		if (arguments.length == 1 && typeof destination !== "string") {
			settings = destination;
			destination = false;
		}

		// if this element has settings associated with it by the live Formatter and no settings
		// was specified, use the stored settings. Else, build the settings object as normal
		settings = ($(this).data('formatCurrency') && !settings ?
			$(this).data('formatCurrency') : 
			settings
			);

		return this.each(function () {
			var $this = $(this);

			// get number
			var num = '0';
			num = $this[$this.is('input, select, textarea') ? 'val' : 'html']();

			var money = $.getFormattedCurrency(num, settings, true);
			settings = money[4];

			$this.data('fcMetadata', {
				'hasDecimals': money[1],
				'originalDecimals': money[2],
				'isPositive': money[3]
			});

			if(!money) return;

			// setup destination
			var $destination = $([]);
			if (!destination) {
				$destination = $this;
			} else {
				$destination = $(destination);
			}
			// set destination
			$destination[$destination.is('input, select, textarea') ? 'val' : 'html'](money[0]);

			if (money[1] &&
				settings.eventOnDecimalsEntered &&
				money[2].length > 0) {
				$destination.trigger('decimalsEntered', money[2]);
		}

			// colorize
			if (settings.colorize) $destination.css('color', money[3] ? 'black' : 'red');
		});
	};

	$.getFormattedCurrency = function (expr, settings, returnMetadata) {
		settings = buildSettingsObjGraph(settings, fcDefaults);

		if (settings.parseAsFloat) {
			var tryFloat = parseFloat(expr);
			expr = isNaN(tryFloat) ? expr : tryFloat;
		}

		expr = (typeof(expr) !== "string" ? expr.toString().replace('\.', settings.decimalSymbol) : expr);

		//identify '(123)' as a negative number
		if (expr.search('\\(') >= 0)
			expr = '-' + expr;

		if (expr === '' || (expr === '-' && settings.roundToDecimalPlace === -1))
			return (returnMetadata ? [ expr, false, "", true, settings ] : "");

		expr = expr.replace(settings.symbol, ''); // Remove currency symbol for arithmetic
		expr = expr.replace(settings.regexGroupDigit, ''); // Remove group digit for arithmetic

		if (settings.decimalSymbol != '.')
			expr = expr.replace(settings.decimalSymbol, '.');  // reset to US decimal for arithmetic				

		// if the number is valid use it, otherwise clean it
		if (isNaN(expr)) {
			// clean number
			expr = expr.replace(settings.regexArithmetic, '');

			if (expr === '' || (expr === '-' && settings.roundToDecimalPlace === -1))
				expr = '0';

			if (isNaN(expr))
				expr = '0';
		}

		var isPositive = (expr == Math.abs(expr));
		if (!isPositive && settings.disableNegative === true) {
			expr = 0;
			isPositive = true;
		}

		// evalutate number input
		var numParts = String(expr).split('.');
		var hasDecimals = (numParts.length > 1 && settings.roundToDecimalPlace > -2);
		var decimals = (hasDecimals ? numParts[1].toString() : '0');
		var originalDecimals = hasDecimals || decimals != '0' ? decimals : '';

		// format number
		expr = Math.abs(numParts[0]);
		expr = isNaN(expr) ? 0 : expr;
		if (settings.roundToDecimalPlace >= 0) {
			decimals = parseFloat('1.' + decimals); // prepend "0."; (IE does NOT round 0.50.toFixed(0) up, but (1+0.50).toFixed(0)-1
			decimals = decimals.toFixed(settings.roundToDecimalPlace); // round
			if (decimals.substring(0, 1) == '2') {
				expr = Number(expr) + 1;
			}
			decimals = decimals.substring(2); // remove "0."
		}
		expr = String(expr);

		if (settings.groupDigits) {
			for (var i = 0; i < Math.floor((expr.length - (1 + i)) / 3); i++) {
				expr = expr.substring(0, expr.length - (4 * i + 3)) + settings.digitGroupSymbol + expr.substring(expr.length - (4 * i + 3));
			}
		}
		if ((hasDecimals && settings.roundToDecimalPlace == -1) || settings.roundToDecimalPlace > 0) {
			if (settings.removeTrailingZerosOnDecimal) {
				decimals = decimals.replace(/0+$/, '');
			} else {
				if (decimals.length < settings.minimumDecimalPlaces) decimals = String(decimals + Array(settings.
					minimumDecimalPlaces+1).join('0')).slice(0,settings.minimumDecimalPlaces);
			}
			expr += (decimals.length > 0 ? settings.decimalSymbol + decimals : "");
		}

		// format symbol/negative
		var format = isPositive ? settings.positiveFormat : settings.negativeFormat;
		var money = format;
		money = money.replace(/%s/g, settings.symbol);
		money = money.replace(/%n/g, expr);

		return (returnMetadata ? [ money, hasDecimals, originalDecimals, isPositive, settings ] : money);
	};

	// Remove all non numbers from text	
	$.toNumber.setDefaults = function(settings) {
		$.extend(toNumberDefaults, settings);
	};

	$.fn.toNumber = function (settings) {
		return this.each(function () {
			var method = $(this).is('input, select, textarea') ? 'val' : 'html';
			$(this)[method]($.toNumber($(this)[method](), settings));
		});
	};

	$.toNumber = function(expr, settings) {
		settings = buildSettingsObjGraph(settings, toNumberDefaults);

		return expr.replace('(', '(-').replace(settings.regex, '');
	};

	// returns the value from the first element as a number
	$.asNumber.setDefaults = function(settings) {
		$.extend(asNumberDefaults, settings);
	};

	$.fn.asNumber = function (settings) {
		var method = $(this).is('input, select, textarea') ? 'val' : 'html';
		var num = $(this)[method]();

		return $.asNumber(num, settings)
	};

	$.asNumber = function(expr, settings) {
		settings = buildSettingsObjGraph(settings, asNumberDefaults);

		expr = expr ? expr : "";
		expr = expr.replace('(', '(-');
		expr = expr.replace(settings.regex, '');
		if (!settings.parse) return expr;

		if (expr.length == 0) expr = '0';

		if (settings.decimalSymbol != '.') expr = expr.replace(settings.decimalSymbol, '.');  // reset to US decimal for arithmetic

		return window['parse' + settings.parseType](expr);
	};

	function buildSettingsObjGraph(settings, defaults) {
		if (!settings) settings = {};
		var bareSettings = settings;

		// build settings graph starting from the defaults and merging region settings if needed
		settings = $.extend(
			{},
			defaults,
			$.formatCurrency.regions[''],
			settings
			);

		if (settings.region !== '') $.extend(settings, getRegionOrCulture(settings.region), bareSettings);

		if (settings.suppressCurrencySymbol) {
			settings.symbol = '';
			settings.positiveFormat = $.trim(settings.positiveFormat.replace('%s', ''));
			settings.negativeFormat = $.trim(settings.negativeFormat.replace('%s', '').replace(' %n', '%n'));
		}

		if (settings.roundToDecimalPlace >= -1) {
			settings.minimumDecimalPlaces = (settings.roundToDecimalPlace == -1 || settings.minimumDecimalPlaces > settings.roundToDecimalPlace ? settings.roundToDecimalPlace : settings.minimumDecimalPlaces);
		} else {
			settings.minimumDecimalPlaces = 0;
		}

		// validate parseType if it exists (for the 'asNumber' settings object graph)
		if (settings.hasOwnProperty('parseType'))
			settings.parseType = validateParseType(settings.parseType);

		// Generate regex for formatting
		settings.regexArithmetic = new RegExp("[^\\d\\.\\-]", "g");
		if (settings.symbol === '')
			settings.regex = new RegExp("[^\\d" + settings.decimalSymbol + "-]", "g");
		else {
			var symbol = settings.symbol.replace('$', '\\$').replace('.', '\\.');
			settings.regex = new RegExp(symbol + "|[^\\d" + settings.decimalSymbol + "-]", "g");
		}

		settings.regexGroupDigit = new RegExp("\\" + settings.digitGroupSymbol, "g");

		return settings;
	}

	function keyAllowed(ev, decPointCodes) {
		if (ev.which >= 48 && ev.which <= 57)
			return true;
		else if (ev.type == 'keypress' && ev.which == decPointCodes[0])
			return (ev.target.value.indexOf(String.fromCharCode(decPointCodes[0])) == -1);
		else if (ev.type == 'keypress') {
			if (ev.ctrlKey && ev.key.toUpperCase() == "A") return true;
			return (ev.which < 32 || (ev.which >= 33 && ev.which <= 40) || (decPointCodes[0] != -1 && ev.which == 46));
		} else if (ev.type == 'keyup') {
			if (ev.ctrlKey && ev.key.toUpperCase() == "A") return false;

			if(ev.which < 32 || (ev.which >= 33 && ev.which <= 40)) return false;

			if(decPointCodes[0] != -1) {
				switch (ev.which) {
					case 78: break; // N (Opera 9.63+ maps the "." from the number key section to the "N" key too!) (See: http://unixpapa.com/js/key.html search for ". Del")
					case decPointCodes[2]: break; // . number block (Opera 9.63+ maps the "." from the number block to the "N" key (78) !!!)
					case decPointCodes[1]: break; // .
					default: return true;
				}
			}

			return false;
		} else
		return true;
	}

	function getRegionOrCulture(region) {
		var regionInfo = $.formatCurrency.regions[region];
		if (regionInfo) {
			return regionInfo;
		}
		else {
			if (/(\w+)-(\w+)/g.test(region)) {
				var culture = region.replace(/(\w+)-(\w+)/g, "$1");
				return $.formatCurrency.regions[culture];
			}
		}
		// fallback to extend(null) (i.e. nothing)
		return null;
	}

	function validateParseType(parseType) {
		switch (parseType.toLowerCase()) {
			case 'int':
			return 'Int';
			case 'float':
			return 'Float';
			default:
			throw 'invalid parseType';
		}
	}

})(jQuery);
/*! Simple JavaScript Inheritance
 * By John Resig http://ejohn.org/
 * MIT Licensed.
 */

!function(){"use strict";var a=!1;window.JQClass=function(){},JQClass.classes={},JQClass.extend=function b(c){function d(){!a&&this._init&&this._init.apply(this,arguments)}var e=this.prototype;a=!0;var f=new this;a=!1;for(var g in c)if("function"==typeof c[g]&&"function"==typeof e[g])f[g]=function(a,b){return function(){var c=this._super;this._super=function(b){return e[a].apply(this,b||[])};var d=b.apply(this,arguments);return this._super=c,d}}(g,c[g]);else if("object"==typeof c[g]&&"object"==typeof e[g]&&"defaultOptions"===g){var h,i=e[g],j=c[g],k={};for(h in i)k[h]=i[h];for(h in j)k[h]=j[h];f[g]=k}else f[g]=c[g];return d.prototype=f,d.prototype.constructor=d,d.extend=b,d}}(),/*! Abstract base class for collection plugins v1.0.2.
	Written by Keith Wood (wood.keith{at}optusnet.com.au) December 2013.
	Licensed under the MIT license (http://keith-wood.name/licence.html). */
function($){"use strict";function camelCase(a){return a.replace(/-([a-z])/g,function(a,b){return b.toUpperCase()})}JQClass.classes.JQPlugin=JQClass.extend({name:"plugin",defaultOptions:{},regionalOptions:{},deepMerge:!0,_getMarker:function(){return"is-"+this.name},_init:function(){$.extend(this.defaultOptions,this.regionalOptions&&this.regionalOptions[""]||{});var a=camelCase(this.name);$[a]=this,$.fn[a]=function(b){var c=Array.prototype.slice.call(arguments,1),d=this,e=this;return this.each(function(){if("string"==typeof b){if("_"===b[0]||!$[a][b])throw"Unknown method: "+b;var f=$[a][b].apply($[a],[this].concat(c));if(f!==d&&void 0!==f)return e=f,!1}else $[a]._attach(this,b)}),e}},setDefaults:function(a){$.extend(this.defaultOptions,a||{})},_attach:function(a,b){if(a=$(a),!a.hasClass(this._getMarker())){a.addClass(this._getMarker()),b=$.extend(this.deepMerge,{},this.defaultOptions,this._getMetadata(a),b||{});var c=$.extend({name:this.name,elem:a,options:b},this._instSettings(a,b));a.data(this.name,c),this._postAttach(a,c),this.option(a,b)}},_instSettings:function(a,b){return{}},_postAttach:function(a,b){},_getMetadata:function(elem){try{var data=elem.data(this.name.toLowerCase())||"";data=data.replace(/(\\?)'/g,function(a,b){return b?"'":'"'}).replace(/([a-zA-Z0-9]+):/g,function(a,b,c){var d=data.substring(0,c).match(/"/g);return d&&d.length%2!==0?b+":":'"'+b+'":'}).replace(/\\:/g,":"),data=$.parseJSON("{"+data+"}");for(var key in data)if(data.hasOwnProperty(key)){var value=data[key];"string"==typeof value&&value.match(/^new Date\(([-0-9,\s]*)\)$/)&&(data[key]=eval(value))}return data}catch(a){return{}}},_getInst:function(a){return $(a).data(this.name)||{}},option:function(a,b,c){a=$(a);var d=a.data(this.name),e=b||{};return!b||"string"==typeof b&&"undefined"==typeof c?(e=(d||{}).options,e&&b?e[b]:e):void(a.hasClass(this._getMarker())&&("string"==typeof b&&(e={},e[b]=c),this._optionsChanged(a,d,e),$.extend(d.options,e)))},_optionsChanged:function(a,b,c){},destroy:function(a){a=$(a),a.hasClass(this._getMarker())&&(this._preDestroy(a,this._getInst(a)),a.removeData(this.name).removeClass(this._getMarker()))},_preDestroy:function(a,b){}}),$.JQPlugin={createPlugin:function(a,b){"object"==typeof a&&(b=a,a="JQPlugin"),a=camelCase(a);var c=camelCase(b.name);JQClass.classes[c]=JQClass.classes[a].extend(b),new JQClass.classes[c]}}}(jQuery);

/*! http://keith-wood.name/countdown.html
	Countdown for jQuery v2.1.0.
	Written by Keith Wood (wood.keith{at}optusnet.com.au) January 2008.
	Available under the MIT (http://keith-wood.name/licence.html) license. 
	Please attribute the author if you use it. */

!function(a){"use strict";var b="countdown",c=0,d=1,e=2,f=3,g=4,h=5,i=6;a.JQPlugin.createPlugin({name:b,defaultOptions:{until:null,since:null,timezone:null,serverSync:null,format:"dHMS",layout:"",compact:!1,padZeroes:!1,significant:0,description:"",expiryUrl:"",expiryText:"",alwaysExpire:!1,onExpiry:null,onTick:null,tickInterval:1},regionalOptions:{"":{labels:["Years","Months","Weeks","Days","Hours","Minutes","Seconds"],labels1:["Year","Month","Week","Day","Hour","Minute","Second"],compactLabels:["y","m","w","d"],whichLabels:null,digits:["0","1","2","3","4","5","6","7","8","9"],timeSeparator:":",isRTL:!1}},_rtlClass:b+"-rtl",_sectionClass:b+"-section",_amountClass:b+"-amount",_periodClass:b+"-period",_rowClass:b+"-row",_holdingClass:b+"-holding",_showClass:b+"-show",_descrClass:b+"-descr",_timerElems:[],_init:function(){function b(a){var h=a<1e12?e?window.performance.now()+window.performance.timing.navigationStart:d():a||d();h-g>=1e3&&(c._updateElems(),g=h),f(b)}var c=this;this._super(),this._serverSyncs=[];var d="function"==typeof Date.now?Date.now:function(){return(new Date).getTime()},e=window.performance&&"function"==typeof window.performance.now,f=window.requestAnimationFrame||window.webkitRequestAnimationFrame||window.mozRequestAnimationFrame||window.oRequestAnimationFrame||window.msRequestAnimationFrame||null,g=0;!f||a.noRequestAnimationFrame?(a.noRequestAnimationFrame=null,a.countdown._timer=setInterval(function(){c._updateElems()},1e3)):(g=window.animationStartTime||window.webkitAnimationStartTime||window.mozAnimationStartTime||window.oAnimationStartTime||window.msAnimationStartTime||d(),f(b))},UTCDate:function(a,b,c,d,e,f,g,h){"object"==typeof b&&b instanceof Date&&(h=b.getMilliseconds(),g=b.getSeconds(),f=b.getMinutes(),e=b.getHours(),d=b.getDate(),c=b.getMonth(),b=b.getFullYear());var i=new Date;return i.setUTCFullYear(b),i.setUTCDate(1),i.setUTCMonth(c||0),i.setUTCDate(d||1),i.setUTCHours(e||0),i.setUTCMinutes((f||0)-(Math.abs(a)<30?60*a:a)),i.setUTCSeconds(g||0),i.setUTCMilliseconds(h||0),i},periodsToSeconds:function(a){return 31557600*a[0]+2629800*a[1]+604800*a[2]+86400*a[3]+3600*a[4]+60*a[5]+a[6]},resync:function(){var b=this;a("."+this._getMarker()).each(function(){var c=a.data(this,b.name);if(c.options.serverSync){for(var d=null,e=0;e<b._serverSyncs.length;e++)if(b._serverSyncs[e][0]===c.options.serverSync){d=b._serverSyncs[e];break}if(b._eqNull(d[2])){var f=a.isFunction(c.options.serverSync)?c.options.serverSync.apply(this,[]):null;d[2]=(f?(new Date).getTime()-f.getTime():0)-d[1]}c._since&&c._since.setMilliseconds(c._since.getMilliseconds()+d[2]),c._until.setMilliseconds(c._until.getMilliseconds()+d[2])}});for(var c=0;c<b._serverSyncs.length;c++)b._eqNull(b._serverSyncs[c][2])||(b._serverSyncs[c][1]+=b._serverSyncs[c][2],delete b._serverSyncs[c][2])},_instSettings:function(a,b){return{_periods:[0,0,0,0,0,0,0]}},_addElem:function(a){this._hasElem(a)||this._timerElems.push(a)},_hasElem:function(b){return a.inArray(b,this._timerElems)>-1},_removeElem:function(b){this._timerElems=a.map(this._timerElems,function(a){return a===b?null:a})},_updateElems:function(){for(var a=this._timerElems.length-1;a>=0;a--)this._updateCountdown(this._timerElems[a])},_optionsChanged:function(b,c,d){d.layout&&(d.layout=d.layout.replace(/&lt;/g,"<").replace(/&gt;/g,">")),this._resetExtraLabels(c.options,d);var e=c.options.timezone!==d.timezone;a.extend(c.options,d),this._adjustSettings(b,c,!this._eqNull(d.until)||!this._eqNull(d.since)||e);var f=new Date;(c._since&&c._since<f||c._until&&c._until>f)&&this._addElem(b[0]),this._updateCountdown(b,c)},_updateCountdown:function(b,c){if(b=b.jquery?b:a(b),c=c||this._getInst(b)){if(b.html(this._generateHTML(c)).toggleClass(this._rtlClass,c.options.isRTL),"pause"!==c._hold&&a.isFunction(c.options.onTick)){var d="lap"!==c._hold?c._periods:this._calculatePeriods(c,c._show,c.options.significant,new Date);1!==c.options.tickInterval&&this.periodsToSeconds(d)%c.options.tickInterval!==0||c.options.onTick.apply(b[0],[d])}var e="pause"!==c._hold&&(c._since?c._now.getTime()<c._since.getTime():c._now.getTime()>=c._until.getTime());if(e&&!c._expiring){if(c._expiring=!0,this._hasElem(b[0])||c.options.alwaysExpire){if(this._removeElem(b[0]),a.isFunction(c.options.onExpiry)&&c.options.onExpiry.apply(b[0],[]),c.options.expiryText){var f=c.options.layout;c.options.layout=c.options.expiryText,this._updateCountdown(b[0],c),c.options.layout=f}c.options.expiryUrl&&(window.location=c.options.expiryUrl)}c._expiring=!1}else"pause"===c._hold&&this._removeElem(b[0])}},_resetExtraLabels:function(a,b){var c=null;for(c in b)c.match(/[Ll]abels[02-9]|compactLabels1/)&&(a[c]=b[c]);for(c in a)c.match(/[Ll]abels[02-9]|compactLabels1/)&&"undefined"==typeof b[c]&&(a[c]=null)},_eqNull:function(a){return"undefined"==typeof a||null===a},_adjustSettings:function(b,c,d){for(var e=null,f=0;f<this._serverSyncs.length;f++)if(this._serverSyncs[f][0]===c.options.serverSync){e=this._serverSyncs[f][1];break}var g=null,h=null;if(this._eqNull(e)){var i=a.isFunction(c.options.serverSync)?c.options.serverSync.apply(b[0],[]):null;g=new Date,h=i?g.getTime()-i.getTime():0,this._serverSyncs.push([c.options.serverSync,h])}else g=new Date,h=c.options.serverSync?e:0;var j=c.options.timezone;j=this._eqNull(j)?-g.getTimezoneOffset():j,(d||!d&&this._eqNull(c._until)&&this._eqNull(c._since))&&(c._since=c.options.since,this._eqNull(c._since)||(c._since=this.UTCDate(j,this._determineTime(c._since,null)),c._since&&h&&c._since.setMilliseconds(c._since.getMilliseconds()+h)),c._until=this.UTCDate(j,this._determineTime(c.options.until,g)),h&&c._until.setMilliseconds(c._until.getMilliseconds()+h)),c._show=this._determineShow(c)},_preDestroy:function(a,b){this._removeElem(a[0]),a.empty()},pause:function(a){this._hold(a,"pause")},lap:function(a){this._hold(a,"lap")},resume:function(a){this._hold(a,null)},toggle:function(b){var c=a.data(b,this.name)||{};this[c._hold?"resume":"pause"](b)},toggleLap:function(b){var c=a.data(b,this.name)||{};this[c._hold?"resume":"lap"](b)},_hold:function(b,c){var d=a.data(b,this.name);if(d){if("pause"===d._hold&&!c){d._periods=d._savePeriods;var e=d._since?"-":"+";d[d._since?"_since":"_until"]=this._determineTime(e+d._periods[0]+"y"+e+d._periods[1]+"o"+e+d._periods[2]+"w"+e+d._periods[3]+"d"+e+d._periods[4]+"h"+e+d._periods[5]+"m"+e+d._periods[6]+"s"),this._addElem(b)}d._hold=c,d._savePeriods="pause"===c?d._periods:null,a.data(b,this.name,d),this._updateCountdown(b,d)}},getTimes:function(b){var c=a.data(b,this.name);return c?"pause"===c._hold?c._savePeriods:c._hold?this._calculatePeriods(c,c._show,c.options.significant,new Date):c._periods:null},_determineTime:function(a,b){var c=this,d=function(a){var b=new Date;return b.setTime(b.getTime()+1e3*a),b},e=function(a){a=a.toLowerCase();for(var b=new Date,d=b.getFullYear(),e=b.getMonth(),f=b.getDate(),g=b.getHours(),h=b.getMinutes(),i=b.getSeconds(),j=/([+-]?[0-9]+)\s*(s|m|h|d|w|o|y)?/g,k=j.exec(a);k;){switch(k[2]||"s"){case"s":i+=parseInt(k[1],10);break;case"m":h+=parseInt(k[1],10);break;case"h":g+=parseInt(k[1],10);break;case"d":f+=parseInt(k[1],10);break;case"w":f+=7*parseInt(k[1],10);break;case"o":e+=parseInt(k[1],10),f=Math.min(f,c._getDaysInMonth(d,e));break;case"y":d+=parseInt(k[1],10),f=Math.min(f,c._getDaysInMonth(d,e))}k=j.exec(a)}return new Date(d,e,f,g,h,i,0)},f=this._eqNull(a)?b:"string"==typeof a?e(a):"number"==typeof a?d(a):a;return f&&f.setMilliseconds(0),f},_getDaysInMonth:function(a,b){return 32-new Date(a,b,32).getDate()},_normalLabels:function(a){return a},_generateHTML:function(b){var j=this;b._periods=b._hold?b._periods:this._calculatePeriods(b,b._show,b.options.significant,new Date);var k=!1,l=0,m=b.options.significant,n=a.extend({},b._show),o=null;for(o=c;o<=i;o++)k=k||"?"===b._show[o]&&b._periods[o]>0,n[o]="?"!==b._show[o]||k?b._show[o]:null,l+=n[o]?1:0,m-=b._periods[o]>0?1:0;var p=[!1,!1,!1,!1,!1,!1,!1];for(o=i;o>=c;o--)b._show[o]&&(b._periods[o]?p[o]=!0:(p[o]=m>0,m--));var q=b.options.compact?b.options.compactLabels:b.options.labels,r=b.options.whichLabels||this._normalLabels,s=function(a){var c=b.options["compactLabels"+r(b._periods[a])];return n[a]?j._translateDigits(b,b._periods[a])+(c?c[a]:q[a])+" ":""},t=b.options.padZeroes?2:1,u=function(a){var c=b.options["labels"+r(b._periods[a])];return!b.options.significant&&n[a]||b.options.significant&&p[a]?'<span class="'+j._sectionClass+'"><span class="'+j._amountClass+'">'+j._minDigits(b,b._periods[a],t)+'</span><span class="'+j._periodClass+'">'+(c?c[a]:q[a])+"</span></span>":""};return b.options.layout?this._buildLayout(b,n,b.options.layout,b.options.compact,b.options.significant,p):(b.options.compact?'<span class="'+this._rowClass+" "+this._amountClass+(b._hold?" "+this._holdingClass:"")+'">'+s(c)+s(d)+s(e)+s(f)+(n[g]?this._minDigits(b,b._periods[g],2):"")+(n[h]?(n[g]?b.options.timeSeparator:"")+this._minDigits(b,b._periods[h],2):"")+(n[i]?(n[g]||n[h]?b.options.timeSeparator:"")+this._minDigits(b,b._periods[i],2):""):'<span class="'+this._rowClass+" "+this._showClass+(b.options.significant||l)+(b._hold?" "+this._holdingClass:"")+'">'+u(c)+u(d)+u(e)+u(f)+u(g)+u(h)+u(i))+"</span>"+(b.options.description?'<span class="'+this._rowClass+" "+this._descrClass+'">'+b.options.description+"</span>":"")},_buildLayout:function(b,j,k,l,m,n){for(var o=b.options[l?"compactLabels":"labels"],p=b.options.whichLabels||this._normalLabels,q=function(a){return(b.options[(l?"compactLabels":"labels")+p(b._periods[a])]||o)[a]},r=function(a,c){return b.options.digits[Math.floor(a/c)%10]},s={desc:b.options.description,sep:b.options.timeSeparator,yl:q(c),yn:this._minDigits(b,b._periods[c],1),ynn:this._minDigits(b,b._periods[c],2),ynnn:this._minDigits(b,b._periods[c],3),y1:r(b._periods[c],1),y10:r(b._periods[c],10),y100:r(b._periods[c],100),y1000:r(b._periods[c],1e3),ol:q(d),on:this._minDigits(b,b._periods[d],1),onn:this._minDigits(b,b._periods[d],2),onnn:this._minDigits(b,b._periods[d],3),o1:r(b._periods[d],1),o10:r(b._periods[d],10),o100:r(b._periods[d],100),o1000:r(b._periods[d],1e3),wl:q(e),wn:this._minDigits(b,b._periods[e],1),wnn:this._minDigits(b,b._periods[e],2),wnnn:this._minDigits(b,b._periods[e],3),w1:r(b._periods[e],1),w10:r(b._periods[e],10),w100:r(b._periods[e],100),w1000:r(b._periods[e],1e3),dl:q(f),dn:this._minDigits(b,b._periods[f],1),dnn:this._minDigits(b,b._periods[f],2),dnnn:this._minDigits(b,b._periods[f],3),d1:r(b._periods[f],1),d10:r(b._periods[f],10),d100:r(b._periods[f],100),d1000:r(b._periods[f],1e3),hl:q(g),hn:this._minDigits(b,b._periods[g],1),hnn:this._minDigits(b,b._periods[g],2),hnnn:this._minDigits(b,b._periods[g],3),h1:r(b._periods[g],1),h10:r(b._periods[g],10),h100:r(b._periods[g],100),h1000:r(b._periods[g],1e3),ml:q(h),mn:this._minDigits(b,b._periods[h],1),mnn:this._minDigits(b,b._periods[h],2),mnnn:this._minDigits(b,b._periods[h],3),m1:r(b._periods[h],1),m10:r(b._periods[h],10),m100:r(b._periods[h],100),m1000:r(b._periods[h],1e3),sl:q(i),sn:this._minDigits(b,b._periods[i],1),snn:this._minDigits(b,b._periods[i],2),snnn:this._minDigits(b,b._periods[i],3),s1:r(b._periods[i],1),s10:r(b._periods[i],10),s100:r(b._periods[i],100),s1000:r(b._periods[i],1e3)},t=k,u=c;u<=i;u++){var v="yowdhms".charAt(u),w=new RegExp("\\{"+v+"<\\}([\\s\\S]*)\\{"+v+">\\}","g");t=t.replace(w,!m&&j[u]||m&&n[u]?"$1":"")}return a.each(s,function(a,b){var c=new RegExp("\\{"+a+"\\}","g");t=t.replace(c,b)}),t},_minDigits:function(a,b,c){return b=""+b,b.length>=c?this._translateDigits(a,b):(b="0000000000"+b,this._translateDigits(a,b.substr(b.length-c)))},_translateDigits:function(a,b){return(""+b).replace(/[0-9]/g,function(b){return a.options.digits[b]})},_determineShow:function(a){var b=a.options.format,j=[];return j[c]=b.match("y")?"?":b.match("Y")?"!":null,j[d]=b.match("o")?"?":b.match("O")?"!":null,j[e]=b.match("w")?"?":b.match("W")?"!":null,j[f]=b.match("d")?"?":b.match("D")?"!":null,j[g]=b.match("h")?"?":b.match("H")?"!":null,j[h]=b.match("m")?"?":b.match("M")?"!":null,j[i]=b.match("s")?"?":b.match("S")?"!":null,j},_calculatePeriods:function(a,b,j,k){a._now=k,a._now.setMilliseconds(0);var l=new Date(a._now.getTime());a._since?k.getTime()<a._since.getTime()?a._now=k=l:k=a._since:(l.setTime(a._until.getTime()),k.getTime()>a._until.getTime()&&(a._now=k=l));var m=[0,0,0,0,0,0,0];if(b[c]||b[d]){var n=this._getDaysInMonth(k.getFullYear(),k.getMonth()),o=this._getDaysInMonth(l.getFullYear(),l.getMonth()),p=l.getDate()===k.getDate()||l.getDate()>=Math.min(n,o)&&k.getDate()>=Math.min(n,o),q=function(a){return 60*(60*a.getHours()+a.getMinutes())+a.getSeconds()},r=Math.max(0,12*(l.getFullYear()-k.getFullYear())+l.getMonth()-k.getMonth()+(l.getDate()<k.getDate()&&!p||p&&q(l)<q(k)?-1:0));m[c]=b[c]?Math.floor(r/12):0,m[d]=b[d]?r-12*m[c]:0,k=new Date(k.getTime());var s=k.getDate()===n,t=this._getDaysInMonth(k.getFullYear()+m[c],k.getMonth()+m[d]);k.getDate()>t&&k.setDate(t),k.setFullYear(k.getFullYear()+m[c]),k.setMonth(k.getMonth()+m[d]),s&&k.setDate(t)}var u=Math.floor((l.getTime()-k.getTime())/1e3),v=null,w=function(a,c){m[a]=b[a]?Math.floor(u/c):0,u-=m[a]*c};if(w(e,604800),w(f,86400),w(g,3600),w(h,60),w(i,1),u>0&&!a._since){var x=[1,12,4.3482,7,24,60,60],y=i,z=1;for(v=i;v>=c;v--)b[v]&&(m[y]>=z&&(m[y]=0,u=1),u>0&&(m[v]++,u=0,y=v,z=1)),z*=x[v]}if(j)for(v=c;v<=i;v++)j&&m[v]?j--:j||(m[v]=0);return m}})}(jQuery);

window.bioEp={bgEl:{},popupEl:{},closeBtnEl:{},shown:!1,overflowDefault:"visible",transformDefault:"",width:400,height:220,html:"",css:"",fonts:[],delay:5,showOnDelay:!1,cookieExp:30,showOncePerSession:!1,onPopup:null,cookieManager:{create:function(a,b,d,c){var e="";c?e="; expires=0":d&&(c=new Date,c.setTime(c.getTime()+864E5*d),e="; expires="+c.toGMTString());document.cookie=a+"="+b+e+"; path=/"},get:function(a){a+="=";for(var b=document.cookie.split(";"),d=0;d<b.length;d++){for(var c=b[d];" "==
c.charAt(0);)c=c.substring(1,c.length);if(0===c.indexOf(a))return c.substring(a.length,c.length)}return null},erase:function(a){this.create(a,"",-1)}},checkCookie:function(){if(0>=this.cookieExp){if(this.showOncePerSession&&"true"==this.cookieManager.get("bioep_shown_session"))return!0;this.cookieManager.erase("bioep_shown");return!1}return"true"==this.cookieManager.get("bioep_shown")?!0:!1},addCSS:function(){for(var a=0;a<this.fonts.length;a++){var b=document.createElement("link");b.href=this.fonts[a];
b.type="text/css";b.rel="stylesheet";document.head.appendChild(b)}a=document.createTextNode("#bio_ep_bg {display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: #000; opacity: 0.3; z-index: 10001;}#bio_ep {display: none; position: fixed; width: "+this.width+"px; height: "+this.height+"px; font-family: 'Titillium Web', sans-serif; font-size: 16px; left: 50%; top: 50%; transform: translateX(-50%) translateY(-50%); -webkit-transform: translateX(-50%) translateY(-50%); -ms-transform: translateX(-50%) translateY(-50%); background-color: #fff; box-shadow: 0px 1px 4px 0 rgba(0,0,0,0.5); z-index: 10002;}#bio_ep_close {position: absolute; left: 100%; margin: -8px 0 0 -12px; width: 20px; height: 20px; color: #fff; font-size: 12px; font-weight: bold; text-align: center; border-radius: 50%; background-color: #5c5c5c; cursor: pointer;}"+
this.css);b=document.createElement("style");b.id='bioep-style';b.type="text/css";b.appendChild(a);document.head.insertBefore(b,document.getElementById("bioep-style"))},addPopup:function(){this.bgEl=document.createElement("div");this.bgEl.id="bio_ep_bg";document.body.appendChild(this.bgEl);document.getElementById("bio_ep")?this.popupEl=document.getElementById("bio_ep"):(this.popupEl=document.createElement("div"),this.popupEl.id="bio_ep",this.popupEl.innerHTML=this.html,document.body.appendChild(this.popupEl));document.getElementById("bio_ep_close")?
this.closeBtnEl=document.getElementById("bio_ep_close"):(this.closeBtnEl=document.createElement("div"),this.closeBtnEl.id="bio_ep_close",this.closeBtnEl.appendChild(document.createTextNode("X")),this.popupEl.insertBefore(this.closeBtnEl,this.popupEl.firstChild))},showPopup:function(){if(!this.shown&&(this.bgEl.style.display="block",this.popupEl.style.display="block",this.scalePopup(),this.overflowDefault=document.body.style.overflow,document.body.style.overflow="hidden",this.shown=!0,this.cookieManager.create("bioep_shown",
"true",this.cookieExp,!1),this.cookieManager.create("bioep_shown_session","true",0,!0),"function"===typeof this.onPopup))this.onPopup()},hidePopup:function(){this.bgEl.style.display="none";this.popupEl.style.display="none";document.body.style.overflow=this.overflowDefault},scalePopup:function(){var a=bioEp.popupEl.offsetWidth,b=bioEp.popupEl.offsetHeight,d=window.innerWidth,c=window.innerHeight,e=0,f=0,g=a/b;a>d-40&&(e=d-40,f=e/g,f>c-40&&(f=c-40,e=f*g));0===f&&b>c-40&&(e=(c-40)*g);a=e/a;if(0>=a||
1<a)a=1;""===this.transformDefault&&(this.transformDefault=window.getComputedStyle(this.popupEl,null).getPropertyValue("transform"));this.popupEl.style.transform=this.transformDefault+" scale("+a+")"},addEvent:function(a,b,d){a.addEventListener?a.addEventListener(b,d,!1):a.attachEvent&&a.attachEvent("on"+b,d)},loadEvents:function(){this.addEvent(document,"mouseout",function(a){a=a?a:window.event;"input"!=a.target.tagName.toLowerCase()&&(a.clientX>=Math.max(document.documentElement.clientWidth,window.innerWidth||
0)-50||50<=a.clientY||a.relatedTarget||a.toElement||bioEp.showPopup())}.bind(this));this.addEvent(this.closeBtnEl,"click",function(){bioEp.hidePopup()});this.addEvent(window,"resize",function(){bioEp.scalePopup()})},setOptions:function(a){this.width="undefined"===typeof a.width?this.width:a.width;this.height="undefined"===typeof a.height?this.height:a.height;this.html="undefined"===typeof a.html?this.html:a.html;this.css="undefined"===typeof a.css?this.css:a.css;this.fonts="undefined"===typeof a.fonts?
this.fonts:a.fonts;this.delay="undefined"===typeof a.delay?this.delay:a.delay;this.showOnDelay="undefined"===typeof a.showOnDelay?this.showOnDelay:a.showOnDelay;this.cookieExp="undefined"===typeof a.cookieExp?this.cookieExp:a.cookieExp;this.showOncePerSession="undefined"===typeof a.showOncePerSession?this.showOncePerSession:a.showOncePerSession;this.onPopup="undefined"===typeof a.onPopup?this.onPopup:a.onPopup},domReady:function(a){"interactive"===document.readyState||"complete"===document.readyState?
a():this.addEvent(document,"DOMContentLoaded",a)},init:function(a){"undefined"!==typeof a&&this.setOptions(a);this.addCSS();this.domReady(function(){bioEp.checkCookie()||(bioEp.addPopup(),setTimeout(function(){bioEp.loadEvents();bioEp.showOnDelay&&bioEp.showPopup()},1E3*bioEp.delay))})}};
/*!
 * JavaScript Cookie v2.2.0
 * https://github.com/js-cookie/js-cookie
 *
 * Copyright 2006, 2015 Klaus Hartl & Fagner Brack
 * Released under the MIT license
 */

;(function (factory) {
	var registeredInModuleLoader = false;
	if (typeof define === 'function' && define.amd) {
		define(factory);
		registeredInModuleLoader = true;
	}
	if (typeof exports === 'object') {
		module.exports = factory();
		registeredInModuleLoader = true;
	}
	if (!registeredInModuleLoader) {
		var OldCookies = window.Cookies;
		var api = window.Cookies = factory();
		api.noConflict = function () {
			window.Cookies = OldCookies;
			return api;
		};
	}
}(function () {
	function extend () {
		var i = 0;
		var result = {};
		for (; i < arguments.length; i++) {
			var attributes = arguments[ i ];
			for (var key in attributes) {
				result[key] = attributes[key];
			}
		}
		return result;
	}

	function init (converter) {
		function api (key, value, attributes) {
			var result;
			if (typeof document === 'undefined') {
				return;
			}

			// Write

			if (arguments.length > 1) {
				attributes = extend({
					path: '/'
				}, api.defaults, attributes);

				if (typeof attributes.expires === 'number') {
					var expires = new Date();
					expires.setMilliseconds(expires.getMilliseconds() + attributes.expires * 864e+5);
					attributes.expires = expires;
				}

				// We're using "expires" because "max-age" is not supported by IE
				attributes.expires = attributes.expires ? attributes.expires.toUTCString() : '';

				try {
					result = JSON.stringify(value);
					if (/^[\{\[]/.test(result)) {
						value = result;
					}
				} catch (e) {}

				if (!converter.write) {
					value = encodeURIComponent(String(value))
						.replace(/%(23|24|26|2B|3A|3C|3E|3D|2F|3F|40|5B|5D|5E|60|7B|7D|7C)/g, decodeURIComponent);
				} else {
					value = converter.write(value, key);
				}

				key = encodeURIComponent(String(key));
				key = key.replace(/%(23|24|26|2B|5E|60|7C)/g, decodeURIComponent);
				key = key.replace(/[\(\)]/g, escape);

				var stringifiedAttributes = '';

				for (var attributeName in attributes) {
					if (!attributes[attributeName]) {
						continue;
					}
					stringifiedAttributes += '; ' + attributeName;
					if (attributes[attributeName] === true) {
						continue;
					}
					stringifiedAttributes += '=' + attributes[attributeName];
				}
				return (document.cookie = key + '=' + value + stringifiedAttributes);
			}

			// Read

			if (!key) {
				result = {};
			}

			// To prevent the for loop in the first place assign an empty array
			// in case there are no cookies at all. Also prevents odd result when
			// calling "get()"
			var cookies = document.cookie ? document.cookie.split('; ') : [];
			var rdecode = /(%[0-9A-Z]{2})+/g;
			var i = 0;

			for (; i < cookies.length; i++) {
				var parts = cookies[i].split('=');
				var cookie = parts.slice(1).join('=');

				if (!this.json && cookie.charAt(0) === '"') {
					cookie = cookie.slice(1, -1);
				}

				try {
					var name = parts[0].replace(rdecode, decodeURIComponent);
					cookie = converter.read ?
						converter.read(cookie, name) : converter(cookie, name) ||
						cookie.replace(rdecode, decodeURIComponent);

					if (this.json) {
						try {
							cookie = JSON.parse(cookie);
						} catch (e) {}
					}

					if (key === name) {
						result = cookie;
						break;
					}

					if (!key) {
						result[name] = cookie;
					}
				} catch (e) {}
			}

			return result;
		}

		api.set = api;
		api.get = function (key) {
			return api.call(api, key);
		};
		api.getJSON = function () {
			return api.apply({
				json: true
			}, [].slice.call(arguments));
		};
		api.defaults = {};

		api.remove = function (key, attributes) {
			api(key, '', extend(attributes, {
				expires: -1
			}));
		};

		api.withConverter = init;

		return api;
	}

	return init(function () {});
}));
(function() {
  window.App || (window.App = {});

  App.UpdateCard = (function() {
    function UpdateCard() {}

    UpdateCard.prototype.setup = function(stripe_elements_style) {
      var api_key, card, disableSubmitButton, elements, enableSubmitButton, formSubmitHandler, locale, moovTokenHandler, stripe, stripeTokenHandler, supported_locales;
      $.urlParam = function(name) {
        var results;
        if (name.startsWith('additional[')) {
          name = name.replace('[', '\\[').replace(']', '\\]').replace('-', '\\-');
        }
        results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(decodeURIComponent(window.location.href));
        if (results === null) {
          return null;
        } else {
          return decodeURIComponent(results[1]) || 0;
        }
      };
      if ($('#card-errors').children().length > 0) {
        $('.rv-card-details').hide();
        $('.card-form').removeClass('hidden-xs-up');
      }
      if ($("meta[name=stripe-publishable-key]").attr('content')) {
        api_key = $('meta[name="stripe-publishable-key"]').attr('content');
        stripe = Stripe(api_key);
        locale = $("meta[http-equiv=content-language]").attr('content');
        supported_locales = ['ar', 'da', 'de', 'en', 'es', 'fi', 'fr', 'he', 'it', 'ja', 'no', 'nl', 'pl', 'sv', 'zh'];
        locale = supported_locales.indexOf($.urlParam('lang')) > -1 ? $.urlParam('lang') : supported_locales.indexOf(locale) > -1 ? locale : 'en';
        elements = stripe.elements({
          locale: locale
        });
        card = elements.create('card', {
          style: stripe_elements_style
        });
        card.mount('#update-card-element');
        card.addEventListener('card', function(e) {
          var displayError;
          displayError = $('#card-errors');
          if (e.error) {
            return displayError.text(e.message);
          } else {
            return displayError.text('');
          }
        });
      }
      $('#update-card-submit').on('click', function(e) {
        if ($('#update-card-submit').attr('disabled') === void 0) {
          e.preventDefault();
          disableSubmitButton();
          if (App.moovCardLink) {
            if (App.moovCardLink.onSuccess === void 0) {
              App.moovCardLink.onSuccess = function(data) {
                return moovTokenHandler(data);
              };
              App.moovCardLink.onError = function(err) {
                enableSubmitButton();
                return $('#card-errors').text(err);
              };
            }
            return App.moovCardLink.submit();
          } else {
            return stripe.createToken(card).then(function(result) {
              if (result.error) {
                enableSubmitButton();
                return $('#card-errors').text(result.error.message);
              } else {
                return stripeTokenHandler(result.token);
              }
            });
          }
        }
      });
      stripeTokenHandler = function(token) {
        var card_details, cc_form;
        card_details = $('#card-details').data();
        if (card_details === void 0) {
          $('.stripe-card-token-field').val(token.id);
          cc_form = $('#update-card-element').closest("form");
          if (cc_form.length === 1) {
            return cc_form.submit();
          }
        } else {
          return formSubmitHandler(token.id);
        }
      };
      moovTokenHandler = function(token) {
        return formSubmitHandler(token.cardID);
      };
      formSubmitHandler = function(token) {
        var card_details, click, target;
        card_details = $('#card-details').data();
        target = card_details.target;
        click = card_details.click;
        $('.update-card-dd > .loading').show();
        return $.ajax(card_details.url, {
          type: 'POST',
          xhrFields: {
            withCredentials: true
          },
          dataType: 'json',
          data: {
            token: token,
            id: card_details.id,
            organization_uid: $('meta[name=organization_revv_uid]').attr('content')
          },
          success: function(response) {
            $('.rv-panel-section').find('.card-form').addClass('hidden-xs-up');
            card_details = $('.rv-panel-section').find('.rv-card-details');
            card_details.show();
            card_details.find('.last4-details').text(response.last4);
            card_details.find('.expiry-details').text(response.expiry);
            $('.saved-payment-details').text(response.brand + ' (' + response.last4 + ')');
            $('.rv-card-info').text(response.brand + ' (' + response.last4 + ')');
            $('#card-details').data('id', response.id);
            if (click !== void 0) {
              $(click).click();
            }
            if ($(target).length !== 0) {
              $('.update-card-dd > .loading').hide();
              $('.nav-tabs a[href="' + target + '"]').tab('show');
              $('#card-errors').empty();
              enableSubmitButton();
            } else {
              location.reload();
            }
            if (card) {
              return card.clear();
            }
          },
          error: function(response) {
            var result;
            $('.update-card-dd > .loading').hide();
            enableSubmitButton();
            result = response.responseJSON;
            return $('#card-errors').text(result.error);
          }
        });
      };
      disableSubmitButton = function() {
        $('#update-card-submit').attr('disabled', true);
        $('.update-card-engine .update-text').hide();
        return $('.update-card-engine .updating-text').removeClass('hidden-xs-up');
      };
      enableSubmitButton = function() {
        $('#update-card-submit').attr('disabled', false);
        $('.update-card-engine .update-text').show();
        return $('.update-card-engine .updating-text').addClass('hidden-xs-up');
      };
      $('#card-edit').on('click', function(e) {
        e.preventDefault();
        $(this).closest('.rv-panel-section').find('.rv-card-details').hide();
        return $(this).closest('.rv-panel-section').find('.card-form').removeClass('hidden-xs-up');
      });
      return $('.card-form').on('click', 'a', function(e) {
        e.preventDefault();
        $(this).closest('.rv-panel-section').find('.card-form').addClass('hidden-xs-up');
        return $(this).closest('.rv-panel-section').find('.rv-card-details').show();
      });
    };

    return UpdateCard;

  })();

}).call(this);
(function() {
  window.App || (window.App = {});

  App.AuthenticityToken = (function() {
    function AuthenticityToken() {}

    AuthenticityToken.prototype.setup = function() {
      if ($('meta[name=csrf-token]').attr('content')) {
        $('[name="authenticity_token"]').val($('meta[name=csrf-token]').attr('content'));
      }
      if ($('meta[name=revv-token]').attr('content')) {
        return $('[name="revv_authenticity_token"]').val($('meta[name=revv-token]').attr('content'));
      }
    };

    return AuthenticityToken;

  })();

}).call(this);
(function() {
  window.App || (window.App = {});

  App.DataName = (function() {
    function DataName() {}

    DataName.prototype.setup = function() {
      var already_checked;
      if (!String.prototype.startsWith) {
        String.prototype.startsWith = function(searchString, position) {
          position = position || 0;
          return this.indexOf(searchString, position) === position;
        };
      }
      $.urlParam = function(name) {
        var results;
        if (name.startsWith('additional[')) {
          name = name.replace('[', '\\[').replace(']', '\\]').replace('-', '\\-');
        }
        results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(decodeURIComponent(window.location.href));
        if (results === null) {
          return null;
        } else {
          return decodeURIComponent(results[1]) || 0;
        }
      };
      $.updateURLParameter = function(url, param, paramVal) {
        var TheAnchor, TheParams, additionalURL, baseURL, i, newAdditionalURL, rows_txt, temp, tempArray, tmpAnchor;
        TheAnchor = null;
        newAdditionalURL = '';
        tempArray = url.split('?');
        baseURL = tempArray[0];
        additionalURL = tempArray[1];
        temp = '';
        if (additionalURL) {
          tmpAnchor = additionalURL.split('#');
          TheParams = tmpAnchor[0];
          TheAnchor = tmpAnchor[1];
          if (TheAnchor) {
            additionalURL = TheParams;
          }
          tempArray = additionalURL.split('&');
          i = 0;
          while (i < tempArray.length) {
            if (tempArray[i].split('=')[0] !== param) {
              newAdditionalURL += temp + tempArray[i];
              temp = '&';
            }
            i++;
          }
        } else {
          tmpAnchor = baseURL.split('#');
          TheParams = tmpAnchor[0];
          TheAnchor = tmpAnchor[1];
          if (TheParams) {
            baseURL = TheParams;
          }
        }
        if (TheAnchor) {
          paramVal += '#' + TheAnchor;
        }
        rows_txt = temp + '' + param + '=' + paramVal;
        return baseURL + '?' + newAdditionalURL + rows_txt;
      };
      already_checked = false;
      return $('[data-name]').each(function() {
        var _this, name;
        _this = this;
        if ($(this).data('url-ignore')) {
          return;
        }
        name = $(this).data('name');
        if (!Array.isArray(name)) {
          name = [name];
        }
        return name.forEach(function(current, index, array) {
          var current_amount, new_amount, removeTrailingZerosOnDecimal, value, values;
          value = $.urlParam(current);
          if (value) {
            if (current === 'email') {
              if (value.indexOf('@') === -1) {
                return;
              }
            }
            if (current.toLowerCase() === 'state') {
              value = value.toUpperCase();
            }
            if (current === 'recurring' && value && !already_checked) {
              already_checked = true;
            }
            if ((current === 'money_pledge' || current === 'money_bomb') && value && !already_checked) {
              already_checked = true;
            }
            if ((current === 'money_pledge' || current === 'money_bomb' || current === 'recurring') && already_checked) {
              return;
            }
            if ($(_this).is('input[type="checkbox"]') && value === 'true') {
              $(_this).click();
              $(_this).prop('checked', value);
              $(_this).prop('disabled', false);
              $(_this).parent('label').addClass('active');
              if ($(_this).hasClass('recurring-interval-check')) {
                $('input[id$=_donor_selected_recurring_interval]').val($(_this).data('interval'));
              }
            } else if ($(_this).is('.multiple-choice-checkbox input[type="checkbox"]')) {
              values = value.split(',');
              index = $(_this).data('index') || 0;
              if ($.inArray(String(index + 1), values) >= 0) {
                $(_this).prop('checked', '1');
                $(_this).parent('label').addClass('active');
              }
              return;
            } else if ($(_this).is('.radio-buttons input[type="radio"]')) {
              index = String(($(_this).data('index') || 0) + 1);
              if (value === index) {
                $(_this).prop('checked', '1');
                $(_this).parent('label').addClass('active');
              }
              return;
            } else if ($(_this).is('input') || $(_this).is('select') || $(_this).is('textarea') || $(_this).is('hidden')) {
              if (!(current === 'bundler_id' && ($(_this).data('slug') != null))) {
                $(_this).val(value);
              }
            } else {
              if ($(_this).is('.amount-option') || $(_this).is('.conduit-amount-option')) {
                current_amount = $(_this).data('amount');
                new_amount = parseFloat(value);
                if (new_amount) {
                  removeTrailingZerosOnDecimal = parseFloat(new_amount.toFixed(0)) === parseFloat(new_amount.toFixed(2));
                  $(_this).text(new_amount).formatCurrency({
                    roundToDecimalPlace: 2,
                    removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
                  });
                  $(_this).prop('data-amount', new_amount).attr('data-amount', new_amount).data('amount', new_amount);
                }
              } else {
                $(_this).text(value);
              }
            }
            if (current === 'other_amount' && value === 'false') {
              $(_this).parents('.field-group').hide();
            }
            if ($(_this).is('.text-field-toggle-value')) {
              $(_this).parents('.text-field-toggle').find('.text-field-toggle-checkbox').click();
            }
            if (!(current === 'bundler_id' && ($(_this).data('slug') != null))) {
              $(_this).attr('value', value);
            }
            $(_this).attr('autofocus', false);
            if ($('.donation-page-params').data('version') !== 'v2' && !$(_this).is('div') && !$('#payment-request-button')) {
              $(_this).parents('form').validate().element(_this);
            }
            return $(_this).trigger('data.name.set', value);
          } else if (current === 'recurring_no' && $.urlParam('recurring') === 'false') {
            $(_this).prop('checked', '1');
            return $(_this).parent('label').addClass('active');
          }
        });
      });
    };

    return DataName;

  })();

}).call(this);
/*! picturefill - v3.0.2 - 2016-02-12
 * https://scottjehl.github.io/picturefill/
 * Copyright (c) 2016 https://github.com/scottjehl/picturefill/blob/master/Authors.txt; Licensed MIT
 */

!function(a){var b=navigator.userAgent;a.HTMLPictureElement&&/ecko/.test(b)&&b.match(/rv\:(\d+)/)&&RegExp.$1<45&&addEventListener("resize",function(){var b,c=document.createElement("source"),d=function(a){var b,d,e=a.parentNode;"PICTURE"===e.nodeName.toUpperCase()?(b=c.cloneNode(),e.insertBefore(b,e.firstElementChild),setTimeout(function(){e.removeChild(b)})):(!a._pfLastSize||a.offsetWidth>a._pfLastSize)&&(a._pfLastSize=a.offsetWidth,d=a.sizes,a.sizes+=",100vw",setTimeout(function(){a.sizes=d}))},e=function(){var a,b=document.querySelectorAll("picture > img, img[srcset][sizes]");for(a=0;a<b.length;a++)d(b[a])},f=function(){clearTimeout(b),b=setTimeout(e,99)},g=a.matchMedia&&matchMedia("(orientation: landscape)"),h=function(){f(),g&&g.addListener&&g.addListener(f)};return c.srcset="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==",/^[c|i]|d$/.test(document.readyState||"")?h():document.addEventListener("DOMContentLoaded",h),f}())}(window),function(a,b,c){"use strict";function d(a){return" "===a||"	"===a||"\n"===a||"\f"===a||"\r"===a}function e(b,c){var d=new a.Image;return d.onerror=function(){A[b]=!1,ba()},d.onload=function(){A[b]=1===d.width,ba()},d.src=c,"pending"}function f(){M=!1,P=a.devicePixelRatio,N={},O={},s.DPR=P||1,Q.width=Math.max(a.innerWidth||0,z.clientWidth),Q.height=Math.max(a.innerHeight||0,z.clientHeight),Q.vw=Q.width/100,Q.vh=Q.height/100,r=[Q.height,Q.width,P].join("-"),Q.em=s.getEmValue(),Q.rem=Q.em}function g(a,b,c,d){var e,f,g,h;return"saveData"===B.algorithm?a>2.7?h=c+1:(f=b-c,e=Math.pow(a-.6,1.5),g=f*e,d&&(g+=.1*e),h=a+g):h=c>1?Math.sqrt(a*b):a,h>c}function h(a){var b,c=s.getSet(a),d=!1;"pending"!==c&&(d=r,c&&(b=s.setRes(c),s.applySetCandidate(b,a))),a[s.ns].evaled=d}function i(a,b){return a.res-b.res}function j(a,b,c){var d;return!c&&b&&(c=a[s.ns].sets,c=c&&c[c.length-1]),d=k(b,c),d&&(b=s.makeUrl(b),a[s.ns].curSrc=b,a[s.ns].curCan=d,d.res||aa(d,d.set.sizes)),d}function k(a,b){var c,d,e;if(a&&b)for(e=s.parseSet(b),a=s.makeUrl(a),c=0;c<e.length;c++)if(a===s.makeUrl(e[c].url)){d=e[c];break}return d}function l(a,b){var c,d,e,f,g=a.getElementsByTagName("source");for(c=0,d=g.length;d>c;c++)e=g[c],e[s.ns]=!0,f=e.getAttribute("srcset"),f&&b.push({srcset:f,media:e.getAttribute("media"),type:e.getAttribute("type"),sizes:e.getAttribute("sizes")})}function m(a,b){function c(b){var c,d=b.exec(a.substring(m));return d?(c=d[0],m+=c.length,c):void 0}function e(){var a,c,d,e,f,i,j,k,l,m=!1,o={};for(e=0;e<h.length;e++)f=h[e],i=f[f.length-1],j=f.substring(0,f.length-1),k=parseInt(j,10),l=parseFloat(j),X.test(j)&&"w"===i?((a||c)&&(m=!0),0===k?m=!0:a=k):Y.test(j)&&"x"===i?((a||c||d)&&(m=!0),0>l?m=!0:c=l):X.test(j)&&"h"===i?((d||c)&&(m=!0),0===k?m=!0:d=k):m=!0;m||(o.url=g,a&&(o.w=a),c&&(o.d=c),d&&(o.h=d),d||c||a||(o.d=1),1===o.d&&(b.has1x=!0),o.set=b,n.push(o))}function f(){for(c(T),i="",j="in descriptor";;){if(k=a.charAt(m),"in descriptor"===j)if(d(k))i&&(h.push(i),i="",j="after descriptor");else{if(","===k)return m+=1,i&&h.push(i),void e();if("("===k)i+=k,j="in parens";else{if(""===k)return i&&h.push(i),void e();i+=k}}else if("in parens"===j)if(")"===k)i+=k,j="in descriptor";else{if(""===k)return h.push(i),void e();i+=k}else if("after descriptor"===j)if(d(k));else{if(""===k)return void e();j="in descriptor",m-=1}m+=1}}for(var g,h,i,j,k,l=a.length,m=0,n=[];;){if(c(U),m>=l)return n;g=c(V),h=[],","===g.slice(-1)?(g=g.replace(W,""),e()):f()}}function n(a){function b(a){function b(){f&&(g.push(f),f="")}function c(){g[0]&&(h.push(g),g=[])}for(var e,f="",g=[],h=[],i=0,j=0,k=!1;;){if(e=a.charAt(j),""===e)return b(),c(),h;if(k){if("*"===e&&"/"===a[j+1]){k=!1,j+=2,b();continue}j+=1}else{if(d(e)){if(a.charAt(j-1)&&d(a.charAt(j-1))||!f){j+=1;continue}if(0===i){b(),j+=1;continue}e=" "}else if("("===e)i+=1;else if(")"===e)i-=1;else{if(","===e){b(),c(),j+=1;continue}if("/"===e&&"*"===a.charAt(j+1)){k=!0,j+=2;continue}}f+=e,j+=1}}}function c(a){return k.test(a)&&parseFloat(a)>=0?!0:l.test(a)?!0:"0"===a||"-0"===a||"+0"===a?!0:!1}var e,f,g,h,i,j,k=/^(?:[+-]?[0-9]+|[0-9]*\.[0-9]+)(?:[eE][+-]?[0-9]+)?(?:ch|cm|em|ex|in|mm|pc|pt|px|rem|vh|vmin|vmax|vw)$/i,l=/^calc\((?:[0-9a-z \.\+\-\*\/\(\)]+)\)$/i;for(f=b(a),g=f.length,e=0;g>e;e++)if(h=f[e],i=h[h.length-1],c(i)){if(j=i,h.pop(),0===h.length)return j;if(h=h.join(" "),s.matchesMedia(h))return j}return"100vw"}b.createElement("picture");var o,p,q,r,s={},t=!1,u=function(){},v=b.createElement("img"),w=v.getAttribute,x=v.setAttribute,y=v.removeAttribute,z=b.documentElement,A={},B={algorithm:""},C="data-pfsrc",D=C+"set",E=navigator.userAgent,F=/rident/.test(E)||/ecko/.test(E)&&E.match(/rv\:(\d+)/)&&RegExp.$1>35,G="currentSrc",H=/\s+\+?\d+(e\d+)?w/,I=/(\([^)]+\))?\s*(.+)/,J=a.picturefillCFG,K="position:absolute;left:0;visibility:hidden;display:block;padding:0;border:none;font-size:1em;width:1em;overflow:hidden;clip:rect(0px, 0px, 0px, 0px)",L="font-size:100%!important;",M=!0,N={},O={},P=a.devicePixelRatio,Q={px:1,"in":96},R=b.createElement("a"),S=!1,T=/^[ \t\n\r\u000c]+/,U=/^[, \t\n\r\u000c]+/,V=/^[^ \t\n\r\u000c]+/,W=/[,]+$/,X=/^\d+$/,Y=/^-?(?:[0-9]+|[0-9]*\.[0-9]+)(?:[eE][+-]?[0-9]+)?$/,Z=function(a,b,c,d){a.addEventListener?a.addEventListener(b,c,d||!1):a.attachEvent&&a.attachEvent("on"+b,c)},$=function(a){var b={};return function(c){return c in b||(b[c]=a(c)),b[c]}},_=function(){var a=/^([\d\.]+)(em|vw|px)$/,b=function(){for(var a=arguments,b=0,c=a[0];++b in a;)c=c.replace(a[b],a[++b]);return c},c=$(function(a){return"return "+b((a||"").toLowerCase(),/\band\b/g,"&&",/,/g,"||",/min-([a-z-\s]+):/g,"e.$1>=",/max-([a-z-\s]+):/g,"e.$1<=",/calc([^)]+)/g,"($1)",/(\d+[\.]*[\d]*)([a-z]+)/g,"($1 * e.$2)",/^(?!(e.[a-z]|[0-9\.&=|><\+\-\*\(\)\/])).*/gi,"")+";"});return function(b,d){var e;if(!(b in N))if(N[b]=!1,d&&(e=b.match(a)))N[b]=e[1]*Q[e[2]];else try{N[b]=new Function("e",c(b))(Q)}catch(f){}return N[b]}}(),aa=function(a,b){return a.w?(a.cWidth=s.calcListLength(b||"100vw"),a.res=a.w/a.cWidth):a.res=a.d,a},ba=function(a){if(t){var c,d,e,f=a||{};if(f.elements&&1===f.elements.nodeType&&("IMG"===f.elements.nodeName.toUpperCase()?f.elements=[f.elements]:(f.context=f.elements,f.elements=null)),c=f.elements||s.qsa(f.context||b,f.reevaluate||f.reselect?s.sel:s.selShort),e=c.length){for(s.setupRun(f),S=!0,d=0;e>d;d++)s.fillImg(c[d],f);s.teardownRun(f)}}};o=a.console&&console.warn?function(a){console.warn(a)}:u,G in v||(G="src"),A["image/jpeg"]=!0,A["image/gif"]=!0,A["image/png"]=!0,A["image/svg+xml"]=b.implementation.hasFeature("http://www.w3.org/TR/SVG11/feature#Image","1.1"),s.ns=("pf"+(new Date).getTime()).substr(0,9),s.supSrcset="srcset"in v,s.supSizes="sizes"in v,s.supPicture=!!a.HTMLPictureElement,s.supSrcset&&s.supPicture&&!s.supSizes&&!function(a){v.srcset="data:,a",a.src="data:,a",s.supSrcset=v.complete===a.complete,s.supPicture=s.supSrcset&&s.supPicture}(b.createElement("img")),s.supSrcset&&!s.supSizes?!function(){var a="data:image/gif;base64,R0lGODlhAgABAPAAAP///wAAACH5BAAAAAAALAAAAAACAAEAAAICBAoAOw==",c="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==",d=b.createElement("img"),e=function(){var a=d.width;2===a&&(s.supSizes=!0),q=s.supSrcset&&!s.supSizes,t=!0,setTimeout(ba)};d.onload=e,d.onerror=e,d.setAttribute("sizes","9px"),d.srcset=c+" 1w,"+a+" 9w",d.src=c}():t=!0,s.selShort="picture>img,img[srcset]",s.sel=s.selShort,s.cfg=B,s.DPR=P||1,s.u=Q,s.types=A,s.setSize=u,s.makeUrl=$(function(a){return R.href=a,R.href}),s.qsa=function(a,b){return"querySelector"in a?a.querySelectorAll(b):[]},s.matchesMedia=function(){return a.matchMedia&&(matchMedia("(min-width: 0.1em)")||{}).matches?s.matchesMedia=function(a){return!a||matchMedia(a).matches}:s.matchesMedia=s.mMQ,s.matchesMedia.apply(this,arguments)},s.mMQ=function(a){return a?_(a):!0},s.calcLength=function(a){var b=_(a,!0)||!1;return 0>b&&(b=!1),b},s.supportsType=function(a){return a?A[a]:!0},s.parseSize=$(function(a){var b=(a||"").match(I);return{media:b&&b[1],length:b&&b[2]}}),s.parseSet=function(a){return a.cands||(a.cands=m(a.srcset,a)),a.cands},s.getEmValue=function(){var a;if(!p&&(a=b.body)){var c=b.createElement("div"),d=z.style.cssText,e=a.style.cssText;c.style.cssText=K,z.style.cssText=L,a.style.cssText=L,a.appendChild(c),p=c.offsetWidth,a.removeChild(c),p=parseFloat(p,10),z.style.cssText=d,a.style.cssText=e}return p||16},s.calcListLength=function(a){if(!(a in O)||B.uT){var b=s.calcLength(n(a));O[a]=b?b:Q.width}return O[a]},s.setRes=function(a){var b;if(a){b=s.parseSet(a);for(var c=0,d=b.length;d>c;c++)aa(b[c],a.sizes)}return b},s.setRes.res=aa,s.applySetCandidate=function(a,b){if(a.length){var c,d,e,f,h,k,l,m,n,o=b[s.ns],p=s.DPR;if(k=o.curSrc||b[G],l=o.curCan||j(b,k,a[0].set),l&&l.set===a[0].set&&(n=F&&!b.complete&&l.res-.1>p,n||(l.cached=!0,l.res>=p&&(h=l))),!h)for(a.sort(i),f=a.length,h=a[f-1],d=0;f>d;d++)if(c=a[d],c.res>=p){e=d-1,h=a[e]&&(n||k!==s.makeUrl(c.url))&&g(a[e].res,c.res,p,a[e].cached)?a[e]:c;break}h&&(m=s.makeUrl(h.url),o.curSrc=m,o.curCan=h,m!==k&&s.setSrc(b,h),s.setSize(b))}},s.setSrc=function(a,b){var c;a.src=b.url,"image/svg+xml"===b.set.type&&(c=a.style.width,a.style.width=a.offsetWidth+1+"px",a.offsetWidth+1&&(a.style.width=c))},s.getSet=function(a){var b,c,d,e=!1,f=a[s.ns].sets;for(b=0;b<f.length&&!e;b++)if(c=f[b],c.srcset&&s.matchesMedia(c.media)&&(d=s.supportsType(c.type))){"pending"===d&&(c=d),e=c;break}return e},s.parseSets=function(a,b,d){var e,f,g,h,i=b&&"PICTURE"===b.nodeName.toUpperCase(),j=a[s.ns];(j.src===c||d.src)&&(j.src=w.call(a,"src"),j.src?x.call(a,C,j.src):y.call(a,C)),(j.srcset===c||d.srcset||!s.supSrcset||a.srcset)&&(e=w.call(a,"srcset"),j.srcset=e,h=!0),j.sets=[],i&&(j.pic=!0,l(b,j.sets)),j.srcset?(f={srcset:j.srcset,sizes:w.call(a,"sizes")},j.sets.push(f),g=(q||j.src)&&H.test(j.srcset||""),g||!j.src||k(j.src,f)||f.has1x||(f.srcset+=", "+j.src,f.cands.push({url:j.src,d:1,set:f}))):j.src&&j.sets.push({srcset:j.src,sizes:null}),j.curCan=null,j.curSrc=c,j.supported=!(i||f&&!s.supSrcset||g&&!s.supSizes),h&&s.supSrcset&&!j.supported&&(e?(x.call(a,D,e),a.srcset=""):y.call(a,D)),j.supported&&!j.srcset&&(!j.src&&a.src||a.src!==s.makeUrl(j.src))&&(null===j.src?a.removeAttribute("src"):a.src=j.src),j.parsed=!0},s.fillImg=function(a,b){var c,d=b.reselect||b.reevaluate;a[s.ns]||(a[s.ns]={}),c=a[s.ns],(d||c.evaled!==r)&&((!c.parsed||b.reevaluate)&&s.parseSets(a,a.parentNode,b),c.supported?c.evaled=r:h(a))},s.setupRun=function(){(!S||M||P!==a.devicePixelRatio)&&f()},s.supPicture?(ba=u,s.fillImg=u):!function(){var c,d=a.attachEvent?/d$|^c/:/d$|^c|^i/,e=function(){var a=b.readyState||"";f=setTimeout(e,"loading"===a?200:999),b.body&&(s.fillImgs(),c=c||d.test(a),c&&clearTimeout(f))},f=setTimeout(e,b.body?9:99),g=function(a,b){var c,d,e=function(){var f=new Date-d;b>f?c=setTimeout(e,b-f):(c=null,a())};return function(){d=new Date,c||(c=setTimeout(e,b))}},h=z.clientHeight,i=function(){M=Math.max(a.innerWidth||0,z.clientWidth)!==Q.width||z.clientHeight!==h,h=z.clientHeight,M&&s.fillImgs()};Z(a,"resize",g(i,99)),Z(b,"readystatechange",e)}(),s.picturefill=ba,s.fillImgs=ba,s.teardownRun=u,ba._=s,a.picturefillCFG={pf:s,push:function(a){var b=a.shift();"function"==typeof s[b]?s[b].apply(s,a):(B[b]=a[0],S&&s.fillImgs({reselect:!0}))}};for(;J&&J.length;)a.picturefillCFG.push(J.shift());a.picturefill=ba,"object"==typeof module&&"object"==typeof module.exports?module.exports=ba:"function"==typeof define&&define.amd&&define("picturefill",function(){return ba}),s.supPicture||(A["image/webp"]=e("image/webp","data:image/webp;base64,UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAABBxAR/Q9ERP8DAABWUDggGAAAADABAJ0BKgEAAQADADQlpAADcAD++/1QAA=="))}(window,document);
!function(e,t){"object"==typeof exports&&"object"==typeof module?module.exports=t():"function"==typeof define&&define.amd?define("party",[],t):"object"==typeof exports?exports.party=t():e.party=t()}(self,(function(){return(()=>{"use strict";var e={"./src/components/circle.ts":(e,t)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Circle=void 0;var r=function(){function e(e,t,r){void 0===r&&(r=0),this.x=e,this.y=t,this.radius=r}return e.zero=new e(0,0),e}();t.Circle=r},"./src/components/color.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Color=void 0;var i=r("./src/systems/math.ts"),n=function(){function e(e,t,r){this.values=new Float32Array(3),this.rgb=[e,t,r]}return Object.defineProperty(e.prototype,"r",{get:function(){return this.values[0]},set:function(e){this.values[0]=Math.floor(e)},enumerable:!1,configurable:!0}),Object.defineProperty(e.prototype,"g",{get:function(){return this.values[1]},set:function(e){this.values[1]=Math.floor(e)},enumerable:!1,configurable:!0}),Object.defineProperty(e.prototype,"b",{get:function(){return this.values[2]},set:function(e){this.values[2]=Math.floor(e)},enumerable:!1,configurable:!0}),Object.defineProperty(e.prototype,"rgb",{get:function(){return[this.r,this.g,this.b]},set:function(e){this.r=e[0],this.g=e[1],this.b=e[2]},enumerable:!1,configurable:!0}),e.prototype.mix=function(t,r){return void 0===r&&(r=.5),new e(i.lerp(this.r,t.r,r),i.lerp(this.g,t.g,r),i.lerp(this.b,t.b,r))},e.prototype.toHex=function(){var e=function(e){return e.toString(16).padStart(2,"0")};return"#"+e(this.r)+e(this.g)+e(this.b)},e.prototype.toString=function(){return"rgb("+this.values.join(", ")+")"},e.fromHex=function(t){return t.startsWith("#")&&(t=t.substr(1)),new e(parseInt(t.substr(0,2),16),parseInt(t.substr(2,2),16),parseInt(t.substr(4,2),16))},e.fromHsl=function(t,r,i){if(t/=360,i/=100,0===(r/=100))return new e(i,i,i);var n=function(e,t,r){return r<0&&(r+=1),r>1&&(r-=1),r<1/6?e+6*(t-e)*r:r<.5?t:r<2/3?e+(t-e)*(2/3-r)*6:e},o=function(e){return Math.min(255,256*e)},s=i<.5?i*(1+r):i+r-i*r,a=2*i-s;return new e(o(n(a,s,t+1/3)),o(n(a,s,t)),o(n(a,s,t-1/3)))},e.white=new e(255,255,255),e.black=new e(0,0,0),e}();t.Color=n},"./src/components/gradient.ts":function(e,t,r){var i,n=this&&this.__extends||(i=function(e,t){return(i=Object.setPrototypeOf||{__proto__:[]}instanceof Array&&function(e,t){e.__proto__=t}||function(e,t){for(var r in t)Object.prototype.hasOwnProperty.call(t,r)&&(e[r]=t[r])})(e,t)},function(e,t){if("function"!=typeof t&&null!==t)throw new TypeError("Class extends value "+String(t)+" is not a constructor or null");function r(){this.constructor=e}i(e,t),e.prototype=null===t?Object.create(t):(r.prototype=t.prototype,new r)}),o=this&&this.__spreadArray||function(e,t){for(var r=0,i=t.length,n=e.length;r<i;r++,n++)e[n]=t[r];return e};Object.defineProperty(t,"__esModule",{value:!0}),t.Gradient=void 0;var s=function(e){function t(){return null!==e&&e.apply(this,arguments)||this}return n(t,e),t.prototype.interpolate=function(e,t,r){return e.mix(t,r)},t.solid=function(e){return new t({value:e,time:.5})},t.simple=function(){for(var e=[],r=0;r<arguments.length;r++)e[r]=arguments[r];var i=1/(e.length-1);return new(t.bind.apply(t,o([void 0],e.map((function(e,t){return{value:e,time:t*i}})))))},t}(r("./src/components/spline.ts").Spline);t.Gradient=s},"./src/components/index.ts":function(e,t,r){var i=this&&this.__createBinding||(Object.create?function(e,t,r,i){void 0===i&&(i=r),Object.defineProperty(e,i,{enumerable:!0,get:function(){return t[r]}})}:function(e,t,r,i){void 0===i&&(i=r),e[i]=t[r]}),n=this&&this.__exportStar||function(e,t){for(var r in e)"default"===r||Object.prototype.hasOwnProperty.call(t,r)||i(t,e,r)};Object.defineProperty(t,"__esModule",{value:!0}),n(r("./src/components/circle.ts"),t),n(r("./src/components/color.ts"),t),n(r("./src/components/gradient.ts"),t),n(r("./src/components/numericSpline.ts"),t),n(r("./src/components/rect.ts"),t),n(r("./src/components/vector.ts"),t)},"./src/components/numericSpline.ts":function(e,t,r){var i,n=this&&this.__extends||(i=function(e,t){return(i=Object.setPrototypeOf||{__proto__:[]}instanceof Array&&function(e,t){e.__proto__=t}||function(e,t){for(var r in t)Object.prototype.hasOwnProperty.call(t,r)&&(e[r]=t[r])})(e,t)},function(e,t){if("function"!=typeof t&&null!==t)throw new TypeError("Class extends value "+String(t)+" is not a constructor or null");function r(){this.constructor=e}i(e,t),e.prototype=null===t?Object.create(t):(r.prototype=t.prototype,new r)});Object.defineProperty(t,"__esModule",{value:!0}),t.NumericSpline=void 0;var o=r("./src/systems/math.ts"),s=function(e){function t(){return null!==e&&e.apply(this,arguments)||this}return n(t,e),t.prototype.interpolate=function(e,t,r){return o.slerp(e,t,r)},t}(r("./src/components/spline.ts").Spline);t.NumericSpline=s},"./src/components/rect.ts":(e,t)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Rect=void 0;var r=function(){function e(e,t,r,i){void 0===r&&(r=0),void 0===i&&(i=0),this.x=e,this.y=t,this.width=r,this.height=i}return e.fromScreen=function(){return new e(window.scrollX,window.scrollY,window.innerWidth,window.innerHeight)},e.fromElement=function(t){var r=t.getBoundingClientRect();return new e(window.scrollX+r.x,window.scrollY+r.y,r.width,r.height)},e.zero=new e(0,0),e}();t.Rect=r},"./src/components/spline.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Spline=void 0;var i=r("./src/systems/math.ts"),n=function(){function e(){for(var e=[],t=0;t<arguments.length;t++)e[t]=arguments[t];if(0===e.length)throw new Error("Splines require at least one key.");if(Array.isArray(e[0]))throw new Error("You are trying to pass an array to the spline constructor, which is not supported. Try to spread the array into the constructor instead.");this.keys=e}return e.prototype.evaluate=function(e){if(0===this.keys.length)throw new Error("Attempt to evaluate a spline with no keys.");if(1===this.keys.length)return this.keys[0].value;var t=this.keys.sort((function(e,t){return e.time-t.time})),r=t.findIndex((function(t){return t.time>e}));if(0===r)return t[0].value;if(-1===r)return t[t.length-1].value;var n=t[r-1],o=t[r],s=i.invlerp(n.time,o.time,e);return this.interpolate(n.value,o.value,s)},e}();t.Spline=n},"./src/components/vector.ts":function(e,t,r){var i=this&&this.__spreadArray||function(e,t){for(var r=0,i=t.length,n=e.length;r<i;r++,n++)e[n]=t[r];return e};Object.defineProperty(t,"__esModule",{value:!0}),t.Vector=void 0;var n=r("./src/systems/math.ts"),o=function(){function e(e,t,r){void 0===e&&(e=0),void 0===t&&(t=0),void 0===r&&(r=0),this.values=new Float32Array(3),this.xyz=[e,t,r]}return Object.defineProperty(e.prototype,"x",{get:function(){return this.values[0]},set:function(e){this.values[0]=e},enumerable:!1,configurable:!0}),Object.defineProperty(e.prototype,"y",{get:function(){return this.values[1]},set:function(e){this.values[1]=e},enumerable:!1,configurable:!0}),Object.defineProperty(e.prototype,"z",{get:function(){return this.values[2]},set:function(e){this.values[2]=e},enumerable:!1,configurable:!0}),Object.defineProperty(e.prototype,"xyz",{get:function(){return[this.x,this.y,this.z]},set:function(e){this.values[0]=e[0],this.values[1]=e[1],this.values[2]=e[2]},enumerable:!1,configurable:!0}),e.prototype.magnitude=function(){return Math.sqrt(this.sqrMagnitude())},e.prototype.sqrMagnitude=function(){return this.x*this.x+this.y*this.y+this.z*this.z},e.prototype.add=function(t){return new e(this.x+t.x,this.y+t.y,this.z+t.z)},e.prototype.subtract=function(t){return new e(this.x-t.x,this.y-t.y,this.z-t.z)},e.prototype.scale=function(t){return"number"==typeof t?new e(this.x*t,this.y*t,this.z*t):new e(this.x*t.x,this.y*t.y,this.z*t.z)},e.prototype.normalized=function(){var t=this.magnitude();return 0!==t?this.scale(1/t):new(e.bind.apply(e,i([void 0],this.xyz)))},e.prototype.angle=function(e){return n.rad2deg*Math.acos((this.x*e.x+this.y*e.y+this.z*e.z)/(this.magnitude()*e.magnitude()))},e.prototype.cross=function(t){return new e(this.y*t.z-this.z*t.y,this.z*t.x-this.x*t.z,this.x*t.y-this.y*t.x)},e.prototype.dot=function(e){return this.magnitude()*e.magnitude()*Math.cos(n.deg2rad*this.angle(e))},e.prototype.toString=function(){return"Vector("+this.values.join(", ")+")"},e.from2dAngle=function(t){return new e(Math.cos(t*n.deg2rad),Math.sin(t*n.deg2rad))},e.zero=new e(0,0,0),e.one=new e(1,1,1),e.right=new e(1,0,0),e.up=new e(0,1,0),e.forward=new e(0,0,1),e}();t.Vector=o},"./src/containers.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.particleContainer=t.debugContainer=t.rootContainer=void 0;var i=r("./src/settings.ts"),n=r("./src/util/index.ts");function o(e){return e&&e.isConnected}function s(e,t,r){var i=document.createElement("div");return i.id="party-js-"+e,Object.assign(i.style,t),r.appendChild(i)}t.rootContainer=new n.Lazy((function(){return s("container",{position:"fixed",left:"0",top:"0",height:"100vh",width:"100vw",pointerEvents:"none",userSelect:"none",zIndex:i.settings.zIndex.toString()},document.body)}),o),t.debugContainer=new n.Lazy((function(){return s("debug",{position:"absolute",top:"0",left:"0",margin:"0.5em",padding:"0.5em 1em",border:"2px solid rgb(0, 0, 0, 0.2)",background:"rgb(0, 0, 0, 0.1)",color:"#555",fontFamily:"monospace"},t.rootContainer.current)}),o),t.particleContainer=new n.Lazy((function(){return s("particles",{width:"100%",height:"100%",overflow:"hidden",perspective:"1200px"},t.rootContainer.current)}),o)},"./src/debug.ts":function(e,t,r){var i=this&&this.__spreadArray||function(e,t){for(var r=0,i=t.length,n=e.length;r<i;r++,n++)e[n]=t[r];return e};Object.defineProperty(t,"__esModule",{value:!0}),t.Debug=void 0;var n=r("./src/containers.ts"),o=r("./src/settings.ts"),s=function(){function e(e){this.scene=e,this.refreshRate=8,this.refreshTimer=1/this.refreshRate}return e.prototype.tick=function(e){var t=n.debugContainer.current,r=o.settings.debug?"block":"none";t.style.display!==r&&(t.style.display=r),o.settings.debug&&(this.refreshTimer+=e,this.refreshTimer>1/this.refreshRate&&(this.refreshTimer=0,t.innerHTML=this.getDebugInformation(e).join("<br>")))},e.prototype.getDebugInformation=function(e){var t=this.scene.emitters.length,r=this.scene.emitters.reduce((function(e,t){return e+t.particles.length}),0),n=["<b>party.js Debug</b>","--------------","FPS: "+Math.round(1/e),"Emitters: "+t,"Particles: "+r],o=this.scene.emitters.map((function(e){return["⭯: "+(e.currentLoop+1)+"/"+(e.options.loops>=0?e.options.loops:"∞"),"Σp: "+e.particles.length,e.isExpired?"<i>expired</i>":"Σt: "+e.durationTimer.toFixed(3)+"s"].join(", ")}));return n.push.apply(n,i(["--------------"],o)),n},e}();t.Debug=s},"./src/index.ts":function(e,t,r){var i=this&&this.__createBinding||(Object.create?function(e,t,r,i){void 0===i&&(i=r),Object.defineProperty(e,i,{enumerable:!0,get:function(){return t[r]}})}:function(e,t,r,i){void 0===i&&(i=r),e[i]=t[r]}),n=this&&this.__exportStar||function(e,t){for(var r in e)"default"===r||Object.prototype.hasOwnProperty.call(t,r)||i(t,e,r)};Object.defineProperty(t,"__esModule",{value:!0}),t.default=t.forceInit=t.util=t.math=t.random=t.sources=t.variation=t.Emitter=t.Particle=t.settings=t.scene=void 0;var o=r("./src/scene.ts"),s=r("./src/util/index.ts");n(r("./src/components/index.ts"),t),n(r("./src/templates/index.ts"),t),n(r("./src/systems/shapes.ts"),t),n(r("./src/systems/modules.ts"),t),t.scene=new s.Lazy((function(){if("undefined"==typeof document||"undefined"==typeof window)throw new Error("It seems like you are trying to run party.js in a non-browser-like environment, which is not supported.");return new o.Scene}));var a=r("./src/settings.ts");Object.defineProperty(t,"settings",{enumerable:!0,get:function(){return a.settings}});var c=r("./src/particles/particle.ts");Object.defineProperty(t,"Particle",{enumerable:!0,get:function(){return c.Particle}});var u=r("./src/particles/emitter.ts");Object.defineProperty(t,"Emitter",{enumerable:!0,get:function(){return u.Emitter}}),t.variation=r("./src/systems/variation.ts"),t.sources=r("./src/systems/sources.ts"),t.random=r("./src/systems/random.ts"),t.math=r("./src/systems/math.ts"),t.util=r("./src/util/index.ts"),t.forceInit=function(){t.scene.current},t.default=r("./src/index.ts")},"./src/particles/emitter.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Emitter=void 0;var i=r("./src/components/vector.ts"),n=r("./src/settings.ts"),o=r("./src/systems/variation.ts"),s=r("./src/util/config.ts"),a=r("./src/particles/options/index.ts"),c=r("./src/particles/particle.ts"),u=function(){function e(e){this.particles=[],this.currentLoop=0,this.durationTimer=0,this.emissionTimer=0,this.attemptedBurstIndices=[],this.options=s.overrideDefaults(a.getDefaultEmitterOptions(),null==e?void 0:e.emitterOptions),this.emission=s.overrideDefaults(a.getDefaultEmissionOptions(),null==e?void 0:e.emissionOptions),this.renderer=s.overrideDefaults(a.getDefaultRendererOptions(),null==e?void 0:e.rendererOptions)}return Object.defineProperty(e.prototype,"isExpired",{get:function(){return this.options.loops>=0&&this.currentLoop>=this.options.loops},enumerable:!1,configurable:!0}),Object.defineProperty(e.prototype,"canRemove",{get:function(){return 0===this.particles.length},enumerable:!1,configurable:!0}),e.prototype.clearParticles=function(){return this.particles.splice(0).length},e.prototype.tick=function(e){if(!this.isExpired&&(this.durationTimer+=e,this.durationTimer>=this.options.duration&&(this.currentLoop++,this.durationTimer=0,this.attemptedBurstIndices=[]),!this.isExpired)){for(var t=0,r=0,i=this.emission.bursts;r<i.length;r++){var n=i[r];if(n.time<=this.durationTimer&&!this.attemptedBurstIndices.includes(t)){for(var s=o.evaluateVariation(n.count),a=0;a<s;a++)this.emitParticle();this.attemptedBurstIndices.push(t)}t++}this.emissionTimer+=e;for(var c=1/this.emission.rate;this.emissionTimer>c;)this.emissionTimer-=c,this.emitParticle()}var u=function(t){var r=l.particles[t];l.tickParticle(r,e),l.options.despawningRules.some((function(e){return e(r)}))&&l.particles.splice(t,1)},l=this;for(a=this.particles.length-1;a>=0;a--)u(a)},e.prototype.tickParticle=function(e,t){e.lifetime-=t,this.options.useGravity&&(e.velocity=e.velocity.add(i.Vector.up.scale(n.settings.gravity*t))),e.location=e.location.add(e.velocity.scale(t));for(var r=0,o=this.options.modules;r<o.length;r++){(0,o[r])(e)}},e.prototype.emitParticle=function(){var e=new c.Particle({location:this.emission.sourceSampler(),lifetime:o.evaluateVariation(this.emission.initialLifetime),velocity:i.Vector.from2dAngle(o.evaluateVariation(this.emission.angle)).scale(o.evaluateVariation(this.emission.initialSpeed)),size:o.evaluateVariation(this.emission.initialSize),rotation:o.evaluateVariation(this.emission.initialRotation),color:o.evaluateVariation(this.emission.initialColor)});return this.particles.push(e),this.particles.length>this.options.maxParticles&&this.particles.shift(),e},e}();t.Emitter=u},"./src/particles/options/emissionOptions.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.getDefaultEmissionOptions=void 0;var i=r("./src/components/index.ts"),n=r("./src/systems/sources.ts");t.getDefaultEmissionOptions=function(){return{rate:10,angle:0,bursts:[],sourceSampler:n.rectSource(i.Rect.zero),initialLifetime:5,initialSpeed:5,initialSize:1,initialRotation:i.Vector.zero,initialColor:i.Color.white}}},"./src/particles/options/emitterOptions.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.getDefaultEmitterOptions=void 0;var i=r("./src/util/rules.ts");t.getDefaultEmitterOptions=function(){return{duration:5,loops:1,useGravity:!0,maxParticles:300,despawningRules:[i.despawningRules.lifetime,i.despawningRules.bounds],modules:[]}}},"./src/particles/options/index.ts":function(e,t,r){var i=this&&this.__createBinding||(Object.create?function(e,t,r,i){void 0===i&&(i=r),Object.defineProperty(e,i,{enumerable:!0,get:function(){return t[r]}})}:function(e,t,r,i){void 0===i&&(i=r),e[i]=t[r]}),n=this&&this.__exportStar||function(e,t){for(var r in e)"default"===r||Object.prototype.hasOwnProperty.call(t,r)||i(t,e,r)};Object.defineProperty(t,"__esModule",{value:!0}),n(r("./src/particles/options/emitterOptions.ts"),t),n(r("./src/particles/options/emissionOptions.ts"),t),n(r("./src/particles/options/renderOptions.ts"),t)},"./src/particles/options/renderOptions.ts":(e,t)=>{function r(e,t){var r=e.toHex();switch(t.nodeName.toLowerCase()){case"div":t.style.background=r;break;case"svg":t.style.fill=t.style.color=r;break;default:t.style.color=r}}function i(e,t){t.style.opacity=e.toString()}function n(e,t){t.style.filter="brightness("+(.5+Math.abs(e))+")"}function o(e,t){t.style.transform="translateX("+(e.location.x-window.scrollX).toFixed(3)+"px) translateY("+(e.location.y-window.scrollY).toFixed(3)+"px) translateZ("+e.location.z.toFixed(3)+"px) rotateX("+e.rotation.x.toFixed(3)+"deg) rotateY("+e.rotation.y.toFixed(3)+"deg) rotateZ("+e.rotation.z.toFixed(3)+"deg) scale("+e.size.toFixed(3)+")"}Object.defineProperty(t,"__esModule",{value:!0}),t.getDefaultRendererOptions=void 0,t.getDefaultRendererOptions=function(){return{shapeFactory:"square",applyColor:r,applyOpacity:i,applyLighting:n,applyTransform:o}}},"./src/particles/particle.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Particle=void 0;var i=r("./src/components/index.ts"),n=r("./src/util/config.ts"),o=function(e){var t=n.overrideDefaults({lifetime:0,size:1,location:i.Vector.zero,rotation:i.Vector.zero,velocity:i.Vector.zero,color:i.Color.white,opacity:1},e);this.id=Symbol(),this.size=this.initialSize=t.size,this.lifetime=this.initialLifetime=t.lifetime,this.rotation=this.initialRotation=t.rotation,this.location=t.location,this.velocity=t.velocity,this.color=t.color,this.opacity=t.opacity};t.Particle=o},"./src/particles/renderer.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Renderer=void 0;var i=r("./src/index.ts"),n=r("./src/components/vector.ts"),o=r("./src/containers.ts"),s=r("./src/systems/shapes.ts"),a=r("./src/util/index.ts"),c=function(){function e(){this.elements=new Map,this.light=new n.Vector(0,0,1),this.enabled=!0,this.enabled=!i.settings.respectReducedMotion||!window.matchMedia("(prefers-reduced-motion)").matches}return e.prototype.begin=function(){this.renderedParticles=[]},e.prototype.end=function(){for(var e=this.elements.keys(),t=e.next();!t.done;){var r=t.value;this.renderedParticles.includes(r)||(this.elements.get(r).remove(),this.elements.delete(r)),t=e.next()}return this.renderedParticles.length},e.prototype.renderParticle=function(e,t){if(this.enabled){var r=t.renderer,i=this.elements.has(e.id)?this.elements.get(e.id):this.createParticleElement(e,r);if(r.applyColor&&r.applyColor(e.color,i),r.applyOpacity&&r.applyOpacity(e.opacity,i),r.applyLighting){var n=a.rotationToNormal(e.rotation).dot(this.light);r.applyLighting(n,i)}r.applyTransform&&r.applyTransform(e,i),this.renderedParticles.push(e.id)}},e.prototype.createParticleElement=function(e,t){var r=s.resolveShapeFactory(t.shapeFactory).cloneNode(!0);return r.style.position="absolute",this.elements.set(e.id,o.particleContainer.current.appendChild(r)),r},e}();t.Renderer=c},"./src/scene.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Scene=void 0;var i=r("./src/debug.ts"),n=r("./src/particles/emitter.ts"),o=r("./src/particles/renderer.ts"),s=function(){function e(){this.emitters=[],this.debug=new i.Debug(this),this.renderer=new o.Renderer,this.scheduledTickId=void 0,this.lastTickTimestamp=performance.now(),this.tick=this.tick.bind(this),this.scheduleTick()}return e.prototype.createEmitter=function(e){var t=new n.Emitter(e);return this.emitters.push(t),t},e.prototype.clearEmitters=function(){return this.emitters.splice(0).length},e.prototype.clearParticles=function(){return this.emitters.reduce((function(e,t){return e+t.clearParticles()}),0)},e.prototype.scheduleTick=function(){this.scheduledTickId=window.requestAnimationFrame(this.tick)},e.prototype.cancelTick=function(){window.cancelAnimationFrame(this.scheduledTickId)},e.prototype.tick=function(e){var t=(e-this.lastTickTimestamp)/1e3;try{for(var r=0;r<this.emitters.length;r++){(o=this.emitters[r]).tick(t),o.isExpired&&o.canRemove&&this.emitters.splice(r--,1)}}catch(e){console.error("An error occurred while updating the scene's emitters:\n\""+e+'"')}try{this.renderer.begin();for(var i=0,n=this.emitters;i<n.length;i++)for(var o=n[i],s=0,a=o.particles;s<a.length;s++){var c=a[s];this.renderer.renderParticle(c,o)}this.renderer.end()}catch(e){console.error("An error occurred while rendering the scene's particles:\n\""+e+'"')}this.debug.tick(t),this.lastTickTimestamp=e,this.scheduleTick()},e}();t.Scene=s},"./src/settings.ts":(e,t)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.settings=void 0,t.settings={debug:!1,gravity:800,zIndex:99999,respectReducedMotion:!0}},"./src/systems/math.ts":(e,t)=>{function r(e,t,r){return(1-r)*e+r*t}Object.defineProperty(t,"__esModule",{value:!0}),t.approximately=t.clamp=t.invlerp=t.slerp=t.lerp=t.epsilon=t.rad2deg=t.deg2rad=void 0,t.deg2rad=Math.PI/180,t.rad2deg=180/Math.PI,t.epsilon=1e-6,t.lerp=r,t.slerp=function(e,t,i){return r(e,t,(1-Math.cos(i*Math.PI))/2)},t.invlerp=function(e,t,r){return(r-e)/(t-e)},t.clamp=function(e,t,r){return Math.min(r,Math.max(t,e))},t.approximately=function(e,r){return Math.abs(e-r)<t.epsilon}},"./src/systems/modules.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.ModuleBuilder=void 0;var i=r("./src/components/index.ts"),n=function(){function e(){this.factor="lifetime",this.isRelative=!1}return e.prototype.drive=function(e){return this.driverKey=e,this},e.prototype.through=function(e){return this.factor=e,this},e.prototype.by=function(e){return this.driverValue=e,this},e.prototype.relative=function(e){return void 0===e&&(e=!0),this.isRelative=e,this},e.prototype.build=function(){var e=this;if(void 0===this.driverKey)throw new Error("No driving key was provided in the module builder. Did you forget a '.drive()' call?");if(void 0===this.driverValue)throw new Error("No driving value was provided in the module builder. Did you forget a '.through()' call?");return function(t){o(t,e.driverKey,function(e,t,r){if("object"==typeof e&&"evaluate"in e)return e.evaluate(t);if("function"==typeof e)return e(t,r);return e}(e.driverValue,function(e,t){switch(e){case"lifetime":return t.initialLifetime-t.lifetime;case"relativeLifetime":return(t.initialLifetime-t.lifetime)/t.initialLifetime;case"size":return t.size;default:throw new Error("Invalid driving factor '"+e+"'.")}}(e.factor,t),t),e.isRelative)}},e}();function o(e,t,r,n){if(void 0===n&&(n=!1),n){var s=e["initial"+t[0].toUpperCase()+t.substr(1)];if(void 0===s)throw new Error("Unable to use relative chaining with key '"+t+"'; no initial value exists.");if(r instanceof i.Vector)o(e,t,s.add(r));else{if("number"!=typeof r)throw new Error("Unable to use relative chaining with particle key '"+t+"'; no relative operation for '"+r+"' could be inferred.");o(e,t,s*r)}}else e[t]=r}t.ModuleBuilder=n},"./src/systems/random.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.randomInsideCircle=t.randomInsideRect=t.randomUnitVector=t.pick=t.randomRange=void 0;var i=r("./src/components/index.ts"),n=r("./src/systems/math.ts");function o(e,t){return void 0===e&&(e=0),void 0===t&&(t=1),n.lerp(e,t,Math.random())}t.randomRange=o,t.pick=function(e){return 0===e.length?void 0:e[Math.floor(Math.random()*e.length)]},t.randomUnitVector=function(){var e=o(0,2*Math.PI),t=o(-1,1);return new i.Vector(Math.sqrt(1-t*t)*Math.cos(e),Math.sqrt(1-t*t)*Math.sin(e),t)},t.randomInsideRect=function(e){return new i.Vector(e.x+o(0,e.width),e.y+o(0,e.height))},t.randomInsideCircle=function(e){var t=o(0,2*Math.PI),r=o(0,e.radius);return new i.Vector(e.x+Math.cos(t)*r,e.y+Math.sin(t)*r)}},"./src/systems/shapes.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.resolveShapeFactory=t.resolvableShapes=void 0;var i=r("./src/systems/variation.ts");t.resolvableShapes={square:'<div style="height: 10px; width: 10px;"></div>',rectangle:'<div style="height: 6px; width: 10px;"></div>',circle:'<svg viewBox="0 0 2 2" width="10" height="10"><circle cx="1" cy="1" r="1" fill="currentColor"/></svg>',roundedSquare:'<div style="height: 10px; width: 10px; border-radius: 3px;"></div>',roundedRectangle:'<div style="height: 6px; width: 10px; border-radius: 3px;"></div>',star:'<svg viewBox="0 0 512 512" width="15" height="15"><polygon fill="currentColor" points="512,197.816 325.961,185.585 255.898,9.569 185.835,185.585 0,197.816 142.534,318.842 95.762,502.431 255.898,401.21 416.035,502.431 369.263,318.842"/></svg>'},t.resolveShapeFactory=function(e){var r=i.evaluateVariation(e);if("string"==typeof r){var n=t.resolvableShapes[r];if(!n)throw new Error("Failed to resolve shape key '"+r+"'. Did you forget to add it to the 'resolvableShapes' lookup?");var o=document.createElement("div");return o.innerHTML=n,o.firstElementChild}return r}},"./src/systems/sources.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.circleSource=t.rectSource=t.mouseSource=t.elementSource=t.dynamicSource=void 0;var i=r("./src/components/index.ts"),n=r("./src/systems/random.ts");function o(e){return function(){return n.randomInsideRect(i.Rect.fromElement(e))}}function s(e){return function(){return new i.Vector(window.scrollX+e.clientX,window.scrollY+e.clientY)}}function a(e){return function(){return n.randomInsideRect(e)}}function c(e){return function(){return n.randomInsideCircle(e)}}t.dynamicSource=function(e){if(e instanceof HTMLElement)return o(e);if(e instanceof i.Circle)return c(e);if(e instanceof i.Rect)return a(e);if(e instanceof MouseEvent)return s(e);throw new Error("Cannot infer the source type of '"+e+"'.")},t.elementSource=o,t.mouseSource=s,t.rectSource=a,t.circleSource=c},"./src/systems/variation.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.gradientSample=t.splineSample=t.skewRelative=t.skew=t.range=t.evaluateVariation=void 0;var i=r("./src/systems/random.ts");function n(e){return function(){return e.evaluate(Math.random())}}t.evaluateVariation=function(e){return Array.isArray(e)?i.pick(e):"function"==typeof e?e():e},t.range=function(e,t){return function(){return i.randomRange(e,t)}},t.skew=function(e,t){return function(){return e+i.randomRange(-t,t)}},t.skewRelative=function(e,t){return function(){return e*(1+i.randomRange(-t,t))}},t.splineSample=n,t.gradientSample=function(e){return n(e)}},"./src/templates/confetti.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.confetti=void 0;var i=r("./src/index.ts"),n=r("./src/components/index.ts"),o=r("./src/systems/modules.ts"),s=r("./src/systems/random.ts"),a=r("./src/systems/sources.ts"),c=r("./src/systems/variation.ts"),u=r("./src/util/index.ts");t.confetti=function(e,t){var r=u.overrideDefaults({count:c.range(20,40),spread:c.range(35,45),speed:c.range(300,600),size:c.skew(1,.2),rotation:function(){return s.randomUnitVector().scale(180)},color:function(){return n.Color.fromHsl(s.randomRange(0,360),100,70)},modules:[(new o.ModuleBuilder).drive("size").by((function(e){return Math.min(1,3*e)})).relative().build(),(new o.ModuleBuilder).drive("rotation").by((function(e){return new n.Vector(140,200,260).scale(e)})).relative().build()],shapes:["square","circle"]},t);return i.scene.current.createEmitter({emitterOptions:{loops:1,duration:8,modules:r.modules},emissionOptions:{rate:0,bursts:[{time:0,count:r.count}],sourceSampler:a.dynamicSource(e),angle:c.skew(-90,c.evaluateVariation(r.spread)),initialLifetime:8,initialSpeed:r.speed,initialSize:r.size,initialRotation:r.rotation,initialColor:r.color},rendererOptions:{shapeFactory:r.shapes}})}},"./src/templates/index.ts":function(e,t,r){var i=this&&this.__createBinding||(Object.create?function(e,t,r,i){void 0===i&&(i=r),Object.defineProperty(e,i,{enumerable:!0,get:function(){return t[r]}})}:function(e,t,r,i){void 0===i&&(i=r),e[i]=t[r]}),n=this&&this.__exportStar||function(e,t){for(var r in e)"default"===r||Object.prototype.hasOwnProperty.call(t,r)||i(t,e,r)};Object.defineProperty(t,"__esModule",{value:!0}),n(r("./src/templates/confetti.ts"),t),n(r("./src/templates/sparkles.ts"),t)},"./src/templates/sparkles.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.sparkles=void 0;var i=r("./src/index.ts"),n=r("./src/components/index.ts"),o=r("./src/systems/modules.ts"),s=r("./src/systems/random.ts"),a=r("./src/systems/sources.ts"),c=r("./src/systems/variation.ts"),u=r("./src/util/index.ts");t.sparkles=function(e,t){var r=u.overrideDefaults({lifetime:c.range(1,2),count:c.range(10,20),speed:c.range(100,200),size:c.range(.8,1.8),rotation:function(){return new n.Vector(0,0,s.randomRange(0,360))},color:function(){return n.Color.fromHsl(50,100,s.randomRange(55,85))},modules:[(new o.ModuleBuilder).drive("rotation").by((function(e){return new n.Vector(0,0,200).scale(e)})).relative().build(),(new o.ModuleBuilder).drive("size").by(new n.NumericSpline({time:0,value:0},{time:.3,value:1},{time:.7,value:1},{time:1,value:0})).through("relativeLifetime").relative().build(),(new o.ModuleBuilder).drive("opacity").by(new n.NumericSpline({time:0,value:1},{time:.5,value:1},{time:1,value:0})).through("relativeLifetime").build()],shapes:"star"},t);return i.scene.current.createEmitter({emitterOptions:{loops:1,duration:3,useGravity:!1,modules:r.modules},emissionOptions:{rate:0,bursts:[{time:0,count:r.count}],sourceSampler:a.dynamicSource(e),angle:c.range(0,360),initialLifetime:r.lifetime,initialSpeed:r.speed,initialSize:r.size,initialRotation:r.rotation,initialColor:r.color},rendererOptions:{applyLighting:void 0,shapeFactory:r.shapes}})}},"./src/util/config.ts":(e,t)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.overrideDefaults=void 0,t.overrideDefaults=function(e,t){return Object.assign({},e,t)}},"./src/util/index.ts":function(e,t,r){var i=this&&this.__createBinding||(Object.create?function(e,t,r,i){void 0===i&&(i=r),Object.defineProperty(e,i,{enumerable:!0,get:function(){return t[r]}})}:function(e,t,r,i){void 0===i&&(i=r),e[i]=t[r]}),n=this&&this.__exportStar||function(e,t){for(var r in e)"default"===r||Object.prototype.hasOwnProperty.call(t,r)||i(t,e,r)};Object.defineProperty(t,"__esModule",{value:!0}),n(r("./src/util/config.ts"),t),n(r("./src/util/rotation.ts"),t),n(r("./src/util/rules.ts"),t),n(r("./src/util/lazy.ts"),t)},"./src/util/lazy.ts":(e,t)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.Lazy=void 0;var r=function(){function e(t,r){void 0===r&&(r=e.defaultExists),this.factory=t,this.exists=r}return Object.defineProperty(e.prototype,"current",{get:function(){return this.exists(this.value)||(this.value=this.factory()),this.value},enumerable:!1,configurable:!0}),e.defaultExists=function(e){return void 0!==e},e}();t.Lazy=r},"./src/util/rotation.ts":(e,t,r)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.rotationToNormal=void 0;var i=r("./src/components/index.ts"),n=r("./src/systems/math.ts");t.rotationToNormal=function(e){var t=e.x*n.deg2rad,r=e.y*n.deg2rad,o=new i.Vector(Math.cos(r),0,Math.sin(r)),s=new i.Vector(0,Math.cos(t),Math.sin(t));return o.cross(s)}},"./src/util/rules.ts":(e,t)=>{Object.defineProperty(t,"__esModule",{value:!0}),t.despawningRules=void 0,t.despawningRules={lifetime:function(e){return e.lifetime<=0},bounds:function(e){var t=document.documentElement.scrollHeight;return e.location.y>t}}}},t={};var r=function r(i){var n=t[i];if(void 0!==n)return n.exports;var o=t[i]={exports:{}};return e[i].call(o.exports,o,o.exports,r),o.exports}("./src/index.ts");return r=r.default})()}));

/*!
 * UAParser.js v0.7.21
 * Lightweight JavaScript-based User-Agent string parser
 * https://github.com/faisalman/ua-parser-js
 *
 * Copyright © 2012-2019 Faisal Salman <f@faisalman.com>
 * Licensed under MIT License
 */

(function(window,undefined){"use strict";var LIBVERSION="0.7.21",EMPTY="",UNKNOWN="?",FUNC_TYPE="function",UNDEF_TYPE="undefined",OBJ_TYPE="object",STR_TYPE="string",MAJOR="major",MODEL="model",NAME="name",TYPE="type",VENDOR="vendor",VERSION="version",ARCHITECTURE="architecture",CONSOLE="console",MOBILE="mobile",TABLET="tablet",SMARTTV="smarttv",WEARABLE="wearable",EMBEDDED="embedded";var util={extend:function(regexes,extensions){var mergedRegexes={};for(var i in regexes){if(extensions[i]&&extensions[i].length%2===0){mergedRegexes[i]=extensions[i].concat(regexes[i])}else{mergedRegexes[i]=regexes[i]}}return mergedRegexes},has:function(str1,str2){if(typeof str1==="string"){return str2.toLowerCase().indexOf(str1.toLowerCase())!==-1}else{return false}},lowerize:function(str){return str.toLowerCase()},major:function(version){return typeof version===STR_TYPE?version.replace(/[^\d\.]/g,"").split(".")[0]:undefined},trim:function(str){return str.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g,"")}};var mapper={rgx:function(ua,arrays){var i=0,j,k,p,q,matches,match;while(i<arrays.length&&!matches){var regex=arrays[i],props=arrays[i+1];j=k=0;while(j<regex.length&&!matches){matches=regex[j++].exec(ua);if(!!matches){for(p=0;p<props.length;p++){match=matches[++k];q=props[p];if(typeof q===OBJ_TYPE&&q.length>0){if(q.length==2){if(typeof q[1]==FUNC_TYPE){this[q[0]]=q[1].call(this,match)}else{this[q[0]]=q[1]}}else if(q.length==3){if(typeof q[1]===FUNC_TYPE&&!(q[1].exec&&q[1].test)){this[q[0]]=match?q[1].call(this,match,q[2]):undefined}else{this[q[0]]=match?match.replace(q[1],q[2]):undefined}}else if(q.length==4){this[q[0]]=match?q[3].call(this,match.replace(q[1],q[2])):undefined}}else{this[q]=match?match:undefined}}}}i+=2}},str:function(str,map){for(var i in map){if(typeof map[i]===OBJ_TYPE&&map[i].length>0){for(var j=0;j<map[i].length;j++){if(util.has(map[i][j],str)){return i===UNKNOWN?undefined:i}}}else if(util.has(map[i],str)){return i===UNKNOWN?undefined:i}}return str}};var maps={browser:{oldsafari:{version:{"1.0":"/8",1.2:"/1",1.3:"/3","2.0":"/412","2.0.2":"/416","2.0.3":"/417","2.0.4":"/419","?":"/"}}},device:{amazon:{model:{"Fire Phone":["SD","KF"]}},sprint:{model:{"Evo Shift 4G":"7373KT"},vendor:{HTC:"APA",Sprint:"Sprint"}}},os:{windows:{version:{ME:"4.90","NT 3.11":"NT3.51","NT 4.0":"NT4.0",2000:"NT 5.0",XP:["NT 5.1","NT 5.2"],Vista:"NT 6.0",7:"NT 6.1",8:"NT 6.2",8.1:"NT 6.3",10:["NT 6.4","NT 10.0"],RT:"ARM"}}}};var regexes={browser:[[/(opera\smini)\/([\w\.-]+)/i,/(opera\s[mobiletab]+).+version\/([\w\.-]+)/i,/(opera).+version\/([\w\.]+)/i,/(opera)[\/\s]+([\w\.]+)/i],[NAME,VERSION],[/(opios)[\/\s]+([\w\.]+)/i],[[NAME,"Opera Mini"],VERSION],[/\s(opr)\/([\w\.]+)/i],[[NAME,"Opera"],VERSION],[/(kindle)\/([\w\.]+)/i,/(lunascape|maxthon|netfront|jasmine|blazer)[\/\s]?([\w\.]*)/i,/(avant\s|iemobile|slim)(?:browser)?[\/\s]?([\w\.]*)/i,/(bidubrowser|baidubrowser)[\/\s]?([\w\.]+)/i,/(?:ms|\()(ie)\s([\w\.]+)/i,/(rekonq)\/([\w\.]*)/i,/(chromium|flock|rockmelt|midori|epiphany|silk|skyfire|ovibrowser|bolt|iron|vivaldi|iridium|phantomjs|bowser|quark|qupzilla|falkon)\/([\w\.-]+)/i],[NAME,VERSION],[/(konqueror)\/([\w\.]+)/i],[[NAME,"Konqueror"],VERSION],[/(trident).+rv[:\s]([\w\.]+).+like\sgecko/i],[[NAME,"IE"],VERSION],[/(edge|edgios|edga|edg)\/((\d+)?[\w\.]+)/i],[[NAME,"Edge"],VERSION],[/(yabrowser)\/([\w\.]+)/i],[[NAME,"Yandex"],VERSION],[/(Avast)\/([\w\.]+)/i],[[NAME,"Avast Secure Browser"],VERSION],[/(AVG)\/([\w\.]+)/i],[[NAME,"AVG Secure Browser"],VERSION],[/(puffin)\/([\w\.]+)/i],[[NAME,"Puffin"],VERSION],[/(focus)\/([\w\.]+)/i],[[NAME,"Firefox Focus"],VERSION],[/(opt)\/([\w\.]+)/i],[[NAME,"Opera Touch"],VERSION],[/((?:[\s\/])uc?\s?browser|(?:juc.+)ucweb)[\/\s]?([\w\.]+)/i],[[NAME,"UCBrowser"],VERSION],[/(comodo_dragon)\/([\w\.]+)/i],[[NAME,/_/g," "],VERSION],[/(windowswechat qbcore)\/([\w\.]+)/i],[[NAME,"WeChat(Win) Desktop"],VERSION],[/(micromessenger)\/([\w\.]+)/i],[[NAME,"WeChat"],VERSION],[/(brave)\/([\w\.]+)/i],[[NAME,"Brave"],VERSION],[/(qqbrowserlite)\/([\w\.]+)/i],[NAME,VERSION],[/(QQ)\/([\d\.]+)/i],[NAME,VERSION],[/m?(qqbrowser)[\/\s]?([\w\.]+)/i],[NAME,VERSION],[/(baiduboxapp)[\/\s]?([\w\.]+)/i],[NAME,VERSION],[/(2345Explorer)[\/\s]?([\w\.]+)/i],[NAME,VERSION],[/(MetaSr)[\/\s]?([\w\.]+)/i],[NAME],[/(LBBROWSER)/i],[NAME],[/xiaomi\/miuibrowser\/([\w\.]+)/i],[VERSION,[NAME,"MIUI Browser"]],[/;fbav\/([\w\.]+);/i],[VERSION,[NAME,"Facebook"]],[/safari\s(line)\/([\w\.]+)/i,/android.+(line)\/([\w\.]+)\/iab/i],[NAME,VERSION],[/headlesschrome(?:\/([\w\.]+)|\s)/i],[VERSION,[NAME,"Chrome Headless"]],[/\swv\).+(chrome)\/([\w\.]+)/i],[[NAME,/(.+)/,"$1 WebView"],VERSION],[/((?:oculus|samsung)browser)\/([\w\.]+)/i],[[NAME,/(.+(?:g|us))(.+)/,"$1 $2"],VERSION],[/android.+version\/([\w\.]+)\s+(?:mobile\s?safari|safari)*/i],[VERSION,[NAME,"Android Browser"]],[/(sailfishbrowser)\/([\w\.]+)/i],[[NAME,"Sailfish Browser"],VERSION],[/(chrome|omniweb|arora|[tizenoka]{5}\s?browser)\/v?([\w\.]+)/i],[NAME,VERSION],[/(dolfin)\/([\w\.]+)/i],[[NAME,"Dolphin"],VERSION],[/(qihu|qhbrowser|qihoobrowser|360browser)/i],[[NAME,"360 Browser"]],[/((?:android.+)crmo|crios)\/([\w\.]+)/i],[[NAME,"Chrome"],VERSION],[/(coast)\/([\w\.]+)/i],[[NAME,"Opera Coast"],VERSION],[/fxios\/([\w\.-]+)/i],[VERSION,[NAME,"Firefox"]],[/version\/([\w\.]+).+?mobile\/\w+\s(safari)/i],[VERSION,[NAME,"Mobile Safari"]],[/version\/([\w\.]+).+?(mobile\s?safari|safari)/i],[VERSION,NAME],[/webkit.+?(gsa)\/([\w\.]+).+?(mobile\s?safari|safari)(\/[\w\.]+)/i],[[NAME,"GSA"],VERSION],[/webkit.+?(mobile\s?safari|safari)(\/[\w\.]+)/i],[NAME,[VERSION,mapper.str,maps.browser.oldsafari.version]],[/(webkit|khtml)\/([\w\.]+)/i],[NAME,VERSION],[/(navigator|netscape)\/([\w\.-]+)/i],[[NAME,"Netscape"],VERSION],[/(swiftfox)/i,/(icedragon|iceweasel|camino|chimera|fennec|maemo\sbrowser|minimo|conkeror)[\/\s]?([\w\.\+]+)/i,/(firefox|seamonkey|k-meleon|icecat|iceape|firebird|phoenix|palemoon|basilisk|waterfox)\/([\w\.-]+)$/i,/(mozilla)\/([\w\.]+).+rv\:.+gecko\/\d+/i,/(polaris|lynx|dillo|icab|doris|amaya|w3m|netsurf|sleipnir)[\/\s]?([\w\.]+)/i,/(links)\s\(([\w\.]+)/i,/(gobrowser)\/?([\w\.]*)/i,/(ice\s?browser)\/v?([\w\._]+)/i,/(mosaic)[\/\s]([\w\.]+)/i],[NAME,VERSION]],cpu:[[/(?:(amd|x(?:(?:86|64)[_-])?|wow|win)64)[;\)]/i],[[ARCHITECTURE,"amd64"]],[/(ia32(?=;))/i],[[ARCHITECTURE,util.lowerize]],[/((?:i[346]|x)86)[;\)]/i],[[ARCHITECTURE,"ia32"]],[/windows\s(ce|mobile);\sppc;/i],[[ARCHITECTURE,"arm"]],[/((?:ppc|powerpc)(?:64)?)(?:\smac|;|\))/i],[[ARCHITECTURE,/ower/,"",util.lowerize]],[/(sun4\w)[;\)]/i],[[ARCHITECTURE,"sparc"]],[/((?:avr32|ia64(?=;))|68k(?=\))|arm(?:64|(?=v\d+[;l]))|(?=atmel\s)avr|(?:irix|mips|sparc)(?:64)?(?=;)|pa-risc)/i],[[ARCHITECTURE,util.lowerize]]],device:[[/\((ipad|playbook);[\w\s\),;-]+(rim|apple)/i],[MODEL,VENDOR,[TYPE,TABLET]],[/applecoremedia\/[\w\.]+ \((ipad)/],[MODEL,[VENDOR,"Apple"],[TYPE,TABLET]],[/(apple\s{0,1}tv)/i],[[MODEL,"Apple TV"],[VENDOR,"Apple"],[TYPE,SMARTTV]],[/(archos)\s(gamepad2?)/i,/(hp).+(touchpad)/i,/(hp).+(tablet)/i,/(kindle)\/([\w\.]+)/i,/\s(nook)[\w\s]+build\/(\w+)/i,/(dell)\s(strea[kpr\s\d]*[\dko])/i],[VENDOR,MODEL,[TYPE,TABLET]],[/(kf[A-z]+)\sbuild\/.+silk\//i],[MODEL,[VENDOR,"Amazon"],[TYPE,TABLET]],[/(sd|kf)[0349hijorstuw]+\sbuild\/.+silk\//i],[[MODEL,mapper.str,maps.device.amazon.model],[VENDOR,"Amazon"],[TYPE,MOBILE]],[/android.+aft([bms])\sbuild/i],[MODEL,[VENDOR,"Amazon"],[TYPE,SMARTTV]],[/\((ip[honed|\s\w*]+);.+(apple)/i],[MODEL,VENDOR,[TYPE,MOBILE]],[/\((ip[honed|\s\w*]+);/i],[MODEL,[VENDOR,"Apple"],[TYPE,MOBILE]],[/(blackberry)[\s-]?(\w+)/i,/(blackberry|benq|palm(?=\-)|sonyericsson|acer|asus|dell|meizu|motorola|polytron)[\s_-]?([\w-]*)/i,/(hp)\s([\w\s]+\w)/i,/(asus)-?(\w+)/i],[VENDOR,MODEL,[TYPE,MOBILE]],[/\(bb10;\s(\w+)/i],[MODEL,[VENDOR,"BlackBerry"],[TYPE,MOBILE]],[/android.+(transfo[prime\s]{4,10}\s\w+|eeepc|slider\s\w+|nexus 7|padfone|p00c)/i],[MODEL,[VENDOR,"Asus"],[TYPE,TABLET]],[/(sony)\s(tablet\s[ps])\sbuild\//i,/(sony)?(?:sgp.+)\sbuild\//i],[[VENDOR,"Sony"],[MODEL,"Xperia Tablet"],[TYPE,TABLET]],[/android.+\s([c-g]\d{4}|so[-l]\w+)(?=\sbuild\/|\).+chrome\/(?![1-6]{0,1}\d\.))/i],[MODEL,[VENDOR,"Sony"],[TYPE,MOBILE]],[/\s(ouya)\s/i,/(nintendo)\s([wids3u]+)/i],[VENDOR,MODEL,[TYPE,CONSOLE]],[/android.+;\s(shield)\sbuild/i],[MODEL,[VENDOR,"Nvidia"],[TYPE,CONSOLE]],[/(playstation\s[34portablevi]+)/i],[MODEL,[VENDOR,"Sony"],[TYPE,CONSOLE]],[/(sprint\s(\w+))/i],[[VENDOR,mapper.str,maps.device.sprint.vendor],[MODEL,mapper.str,maps.device.sprint.model],[TYPE,MOBILE]],[/(htc)[;_\s-]+([\w\s]+(?=\)|\sbuild)|\w+)/i,/(zte)-(\w*)/i,/(alcatel|geeksphone|nexian|panasonic|(?=;\s)sony)[_\s-]?([\w-]*)/i],[VENDOR,[MODEL,/_/g," "],[TYPE,MOBILE]],[/(nexus\s9)/i],[MODEL,[VENDOR,"HTC"],[TYPE,TABLET]],[/d\/huawei([\w\s-]+)[;\)]/i,/(nexus\s6p|vog-l29|ane-lx1|eml-l29)/i],[MODEL,[VENDOR,"Huawei"],[TYPE,MOBILE]],[/android.+(bah2?-a?[lw]\d{2})/i],[MODEL,[VENDOR,"Huawei"],[TYPE,TABLET]],[/(microsoft);\s(lumia[\s\w]+)/i],[VENDOR,MODEL,[TYPE,MOBILE]],[/[\s\(;](xbox(?:\sone)?)[\s\);]/i],[MODEL,[VENDOR,"Microsoft"],[TYPE,CONSOLE]],[/(kin\.[onetw]{3})/i],[[MODEL,/\./g," "],[VENDOR,"Microsoft"],[TYPE,MOBILE]],[/\s(milestone|droid(?:[2-4x]|\s(?:bionic|x2|pro|razr))?:?(\s4g)?)[\w\s]+build\//i,/mot[\s-]?(\w*)/i,/(XT\d{3,4}) build\//i,/(nexus\s6)/i],[MODEL,[VENDOR,"Motorola"],[TYPE,MOBILE]],[/android.+\s(mz60\d|xoom[\s2]{0,2})\sbuild\//i],[MODEL,[VENDOR,"Motorola"],[TYPE,TABLET]],[/hbbtv\/\d+\.\d+\.\d+\s+\([\w\s]*;\s*(\w[^;]*);([^;]*)/i],[[VENDOR,util.trim],[MODEL,util.trim],[TYPE,SMARTTV]],[/hbbtv.+maple;(\d+)/i],[[MODEL,/^/,"SmartTV"],[VENDOR,"Samsung"],[TYPE,SMARTTV]],[/\(dtv[\);].+(aquos)/i],[MODEL,[VENDOR,"Sharp"],[TYPE,SMARTTV]],[/android.+((sch-i[89]0\d|shw-m380s|gt-p\d{4}|gt-n\d+|sgh-t8[56]9|nexus 10))/i,/((SM-T\w+))/i],[[VENDOR,"Samsung"],MODEL,[TYPE,TABLET]],[/smart-tv.+(samsung)/i],[VENDOR,[TYPE,SMARTTV],MODEL],[/((s[cgp]h-\w+|gt-\w+|galaxy\snexus|sm-\w[\w\d]+))/i,/(sam[sung]*)[\s-]*(\w+-?[\w-]*)/i,/sec-((sgh\w+))/i],[[VENDOR,"Samsung"],MODEL,[TYPE,MOBILE]],[/sie-(\w*)/i],[MODEL,[VENDOR,"Siemens"],[TYPE,MOBILE]],[/(maemo|nokia).*(n900|lumia\s\d+)/i,/(nokia)[\s_-]?([\w-]*)/i],[[VENDOR,"Nokia"],MODEL,[TYPE,MOBILE]],[/android[x\d\.\s;]+\s([ab][1-7]\-?[0178a]\d\d?)/i],[MODEL,[VENDOR,"Acer"],[TYPE,TABLET]],[/android.+([vl]k\-?\d{3})\s+build/i],[MODEL,[VENDOR,"LG"],[TYPE,TABLET]],[/android\s3\.[\s\w;-]{10}(lg?)-([06cv9]{3,4})/i],[[VENDOR,"LG"],MODEL,[TYPE,TABLET]],[/(lg) netcast\.tv/i],[VENDOR,MODEL,[TYPE,SMARTTV]],[/(nexus\s[45])/i,/lg[e;\s\/-]+(\w*)/i,/android.+lg(\-?[\d\w]+)\s+build/i],[MODEL,[VENDOR,"LG"],[TYPE,MOBILE]],[/(lenovo)\s?(s(?:5000|6000)(?:[\w-]+)|tab(?:[\s\w]+))/i],[VENDOR,MODEL,[TYPE,TABLET]],[/android.+(ideatab[a-z0-9\-\s]+)/i],[MODEL,[VENDOR,"Lenovo"],[TYPE,TABLET]],[/(lenovo)[_\s-]?([\w-]+)/i],[VENDOR,MODEL,[TYPE,MOBILE]],[/linux;.+((jolla));/i],[VENDOR,MODEL,[TYPE,MOBILE]],[/((pebble))app\/[\d\.]+\s/i],[VENDOR,MODEL,[TYPE,WEARABLE]],[/android.+;\s(oppo)\s?([\w\s]+)\sbuild/i],[VENDOR,MODEL,[TYPE,MOBILE]],[/crkey/i],[[MODEL,"Chromecast"],[VENDOR,"Google"],[TYPE,SMARTTV]],[/android.+;\s(glass)\s\d/i],[MODEL,[VENDOR,"Google"],[TYPE,WEARABLE]],[/android.+;\s(pixel c)[\s)]/i],[MODEL,[VENDOR,"Google"],[TYPE,TABLET]],[/android.+;\s(pixel( [23])?( xl)?)[\s)]/i],[MODEL,[VENDOR,"Google"],[TYPE,MOBILE]],[/android.+;\s(\w+)\s+build\/hm\1/i,/android.+(hm[\s\-_]*note?[\s_]*(?:\d\w)?)\s+build/i,/android.+(mi[\s\-_]*(?:a\d|one|one[\s_]plus|note lte)?[\s_]*(?:\d?\w?)[\s_]*(?:plus)?)\s+build/i,/android.+(redmi[\s\-_]*(?:note)?(?:[\s_]*[\w\s]+))\s+build/i],[[MODEL,/_/g," "],[VENDOR,"Xiaomi"],[TYPE,MOBILE]],[/android.+(mi[\s\-_]*(?:pad)(?:[\s_]*[\w\s]+))\s+build/i],[[MODEL,/_/g," "],[VENDOR,"Xiaomi"],[TYPE,TABLET]],[/android.+;\s(m[1-5]\snote)\sbuild/i],[MODEL,[VENDOR,"Meizu"],[TYPE,MOBILE]],[/(mz)-([\w-]{2,})/i],[[VENDOR,"Meizu"],MODEL,[TYPE,MOBILE]],[/android.+a000(1)\s+build/i,/android.+oneplus\s(a\d{4})[\s)]/i],[MODEL,[VENDOR,"OnePlus"],[TYPE,MOBILE]],[/android.+[;\/]\s*(RCT[\d\w]+)\s+build/i],[MODEL,[VENDOR,"RCA"],[TYPE,TABLET]],[/android.+[;\/\s]+(Venue[\d\s]{2,7})\s+build/i],[MODEL,[VENDOR,"Dell"],[TYPE,TABLET]],[/android.+[;\/]\s*(Q[T|M][\d\w]+)\s+build/i],[MODEL,[VENDOR,"Verizon"],[TYPE,TABLET]],[/android.+[;\/]\s+(Barnes[&\s]+Noble\s+|BN[RT])(V?.*)\s+build/i],[[VENDOR,"Barnes & Noble"],MODEL,[TYPE,TABLET]],[/android.+[;\/]\s+(TM\d{3}.*\b)\s+build/i],[MODEL,[VENDOR,"NuVision"],[TYPE,TABLET]],[/android.+;\s(k88)\sbuild/i],[MODEL,[VENDOR,"ZTE"],[TYPE,TABLET]],[/android.+[;\/]\s*(gen\d{3})\s+build.*49h/i],[MODEL,[VENDOR,"Swiss"],[TYPE,MOBILE]],[/android.+[;\/]\s*(zur\d{3})\s+build/i],[MODEL,[VENDOR,"Swiss"],[TYPE,TABLET]],[/android.+[;\/]\s*((Zeki)?TB.*\b)\s+build/i],[MODEL,[VENDOR,"Zeki"],[TYPE,TABLET]],[/(android).+[;\/]\s+([YR]\d{2})\s+build/i,/android.+[;\/]\s+(Dragon[\-\s]+Touch\s+|DT)(\w{5})\sbuild/i],[[VENDOR,"Dragon Touch"],MODEL,[TYPE,TABLET]],[/android.+[;\/]\s*(NS-?\w{0,9})\sbuild/i],[MODEL,[VENDOR,"Insignia"],[TYPE,TABLET]],[/android.+[;\/]\s*((NX|Next)-?\w{0,9})\s+build/i],[MODEL,[VENDOR,"NextBook"],[TYPE,TABLET]],[/android.+[;\/]\s*(Xtreme\_)?(V(1[045]|2[015]|30|40|60|7[05]|90))\s+build/i],[[VENDOR,"Voice"],MODEL,[TYPE,MOBILE]],[/android.+[;\/]\s*(LVTEL\-)?(V1[12])\s+build/i],[[VENDOR,"LvTel"],MODEL,[TYPE,MOBILE]],[/android.+;\s(PH-1)\s/i],[MODEL,[VENDOR,"Essential"],[TYPE,MOBILE]],[/android.+[;\/]\s*(V(100MD|700NA|7011|917G).*\b)\s+build/i],[MODEL,[VENDOR,"Envizen"],[TYPE,TABLET]],[/android.+[;\/]\s*(Le[\s\-]+Pan)[\s\-]+(\w{1,9})\s+build/i],[VENDOR,MODEL,[TYPE,TABLET]],[/android.+[;\/]\s*(Trio[\s\-]*.*)\s+build/i],[MODEL,[VENDOR,"MachSpeed"],[TYPE,TABLET]],[/android.+[;\/]\s*(Trinity)[\-\s]*(T\d{3})\s+build/i],[VENDOR,MODEL,[TYPE,TABLET]],[/android.+[;\/]\s*TU_(1491)\s+build/i],[MODEL,[VENDOR,"Rotor"],[TYPE,TABLET]],[/android.+(KS(.+))\s+build/i],[MODEL,[VENDOR,"Amazon"],[TYPE,TABLET]],[/android.+(Gigaset)[\s\-]+(Q\w{1,9})\s+build/i],[VENDOR,MODEL,[TYPE,TABLET]],[/\s(tablet|tab)[;\/]/i,/\s(mobile)(?:[;\/]|\ssafari)/i],[[TYPE,util.lowerize],VENDOR,MODEL],[/[\s\/\(](smart-?tv)[;\)]/i],[[TYPE,SMARTTV]],[/(android[\w\.\s\-]{0,9});.+build/i],[MODEL,[VENDOR,"Generic"]]],engine:[[/windows.+\sedge\/([\w\.]+)/i],[VERSION,[NAME,"EdgeHTML"]],[/webkit\/537\.36.+chrome\/(?!27)([\w\.]+)/i],[VERSION,[NAME,"Blink"]],[/(presto)\/([\w\.]+)/i,/(webkit|trident|netfront|netsurf|amaya|lynx|w3m|goanna)\/([\w\.]+)/i,/(khtml|tasman|links)[\/\s]\(?([\w\.]+)/i,/(icab)[\/\s]([23]\.[\d\.]+)/i],[NAME,VERSION],[/rv\:([\w\.]{1,9}).+(gecko)/i],[VERSION,NAME]],os:[[/microsoft\s(windows)\s(vista|xp)/i],[NAME,VERSION],[/(windows)\snt\s6\.2;\s(arm)/i,/(windows\sphone(?:\sos)*)[\s\/]?([\d\.\s\w]*)/i,/(windows\smobile|windows)[\s\/]?([ntce\d\.\s]+\w)/i],[NAME,[VERSION,mapper.str,maps.os.windows.version]],[/(win(?=3|9|n)|win\s9x\s)([nt\d\.]+)/i],[[NAME,"Windows"],[VERSION,mapper.str,maps.os.windows.version]],[/\((bb)(10);/i],[[NAME,"BlackBerry"],VERSION],[/(blackberry)\w*\/?([\w\.]*)/i,/(tizen|kaios)[\/\s]([\w\.]+)/i,/(android|webos|palm\sos|qnx|bada|rim\stablet\sos|meego|sailfish|contiki)[\/\s-]?([\w\.]*)/i],[NAME,VERSION],[/(symbian\s?os|symbos|s60(?=;))[\/\s-]?([\w\.]*)/i],[[NAME,"Symbian"],VERSION],[/\((series40);/i],[NAME],[/mozilla.+\(mobile;.+gecko.+firefox/i],[[NAME,"Firefox OS"],VERSION],[/(nintendo|playstation)\s([wids34portablevu]+)/i,/(mint)[\/\s\(]?(\w*)/i,/(mageia|vectorlinux)[;\s]/i,/(joli|[kxln]?ubuntu|debian|suse|opensuse|gentoo|(?=\s)arch|slackware|fedora|mandriva|centos|pclinuxos|redhat|zenwalk|linpus)[\/\s-]?(?!chrom)([\w\.-]*)/i,/(hurd|linux)\s?([\w\.]*)/i,/(gnu)\s?([\w\.]*)/i],[NAME,VERSION],[/(cros)\s[\w]+\s([\w\.]+\w)/i],[[NAME,"Chromium OS"],VERSION],[/(sunos)\s?([\w\.\d]*)/i],[[NAME,"Solaris"],VERSION],[/\s([frentopc-]{0,4}bsd|dragonfly)\s?([\w\.]*)/i],[NAME,VERSION],[/(haiku)\s(\w+)/i],[NAME,VERSION],[/cfnetwork\/.+darwin/i,/ip[honead]{2,4}(?:.*os\s([\w]+)\slike\smac|;\sopera)/i],[[VERSION,/_/g,"."],[NAME,"iOS"]],[/(mac\sos\sx)\s?([\w\s\.]*)/i,/(macintosh|mac(?=_powerpc)\s)/i],[[NAME,"Mac OS"],[VERSION,/_/g,"."]],[/((?:open)?solaris)[\/\s-]?([\w\.]*)/i,/(aix)\s((\d)(?=\.|\)|\s)[\w\.])*/i,/(plan\s9|minix|beos|os\/2|amigaos|morphos|risc\sos|openvms|fuchsia)/i,/(unix)\s?([\w\.]*)/i],[NAME,VERSION]]};var UAParser=function(uastring,extensions){if(typeof uastring==="object"){extensions=uastring;uastring=undefined}if(!(this instanceof UAParser)){return new UAParser(uastring,extensions).getResult()}var ua=uastring||(window&&window.navigator&&window.navigator.userAgent?window.navigator.userAgent:EMPTY);var rgxmap=extensions?util.extend(regexes,extensions):regexes;this.getBrowser=function(){var browser={name:undefined,version:undefined};mapper.rgx.call(browser,ua,rgxmap.browser);browser.major=util.major(browser.version);return browser};this.getCPU=function(){var cpu={architecture:undefined};mapper.rgx.call(cpu,ua,rgxmap.cpu);return cpu};this.getDevice=function(){var device={vendor:undefined,model:undefined,type:undefined};mapper.rgx.call(device,ua,rgxmap.device);return device};this.getEngine=function(){var engine={name:undefined,version:undefined};mapper.rgx.call(engine,ua,rgxmap.engine);return engine};this.getOS=function(){var os={name:undefined,version:undefined};mapper.rgx.call(os,ua,rgxmap.os);return os};this.getResult=function(){return{ua:this.getUA(),browser:this.getBrowser(),engine:this.getEngine(),os:this.getOS(),device:this.getDevice(),cpu:this.getCPU()}};this.getUA=function(){return ua};this.setUA=function(uastring){ua=uastring;return this};return this};UAParser.VERSION=LIBVERSION;UAParser.BROWSER={NAME:NAME,MAJOR:MAJOR,VERSION:VERSION};UAParser.CPU={ARCHITECTURE:ARCHITECTURE};UAParser.DEVICE={MODEL:MODEL,VENDOR:VENDOR,TYPE:TYPE,CONSOLE:CONSOLE,MOBILE:MOBILE,SMARTTV:SMARTTV,TABLET:TABLET,WEARABLE:WEARABLE,EMBEDDED:EMBEDDED};UAParser.ENGINE={NAME:NAME,VERSION:VERSION};UAParser.OS={NAME:NAME,VERSION:VERSION};if(typeof exports!==UNDEF_TYPE){if(typeof module!==UNDEF_TYPE&&module.exports){exports=module.exports=UAParser}exports.UAParser=UAParser}else{if(typeof define==="function"&&define.amd){define(function(){return UAParser})}else if(window){window.UAParser=UAParser}}var $=window&&(window.jQuery||window.Zepto);if($&&!$.ua){var parser=new UAParser;$.ua=parser.getResult();$.ua.get=function(){return parser.getUA()};$.ua.set=function(uastring){parser.setUA(uastring);var result=parser.getResult();for(var prop in result){$.ua[prop]=result[prop]}}}})(typeof window==="object"?window:this);
(function() {
  window.App || (window.App = {});

  App.init = function() {
    var stripe_elements_style;
    new App.UserAgentChecker().setup();
    new App.Bootstrap().setup();
    new App.Common().setup();
    new App.Ransack().setup();
    new App.Prototype().setup();
    new App.FloatLabel().setup();
    new App.StripeIdToken().setup();
    window.App.KeyboardShortcuts = new App.KeyboardShortcuts();
    window.App.KeyboardShortcuts.setup();
    if ($('script[data-name=honeybadger]').length !== 0) {
      new App.HoneybadgerSetup().setup();
    }
    if ($('body.landing-page').length === 0 && $('body.upsell-page').length === 0 && $('.panel-type-upsell').length === 0) {
      new App.BestInPlace().setup();
      new App.MiniColors().setup();
      new App.FileUploader().setup();
      new App.ClipboardBuilder().setup();
      new App.PageBuilder().setup();
      new App.EventPageBuilder().setup();
      new App.Refund().setup();
    }
    if (($('body.landing-page').length !== 0 || $('body.upsell-page').length !== 0) && !$('.panel-type-upsell').length) {
      new App.DataName().setup();
      new App.LandingPage().setup();
      new App.Countdown().setup();
      new App.DonationStream().setup();
      new App.ElectionDayLookUp().setup();
      new App.AmountButtonAnimation().setup();
      new App.AuthenticityToken().setup();
      new App.AddressAutocomplete().setup();
      new App.ExitIntentPopup().setup();
      new App.SendGridEmailVerification().setup();
      new App.Confetti().setup();
      new App.Modal().setup();
    }
    if ($('body.landing-page').length !== 0 && $('form[data-without-card=true]').length !== 0) {
      new App.LandingPage.Profile().setup();
    }
    if ($('body.user_onboarding').length !== 0) {
      new App.UserOnboarding().setup();
    }
    if ($('body.collections').length !== 0) {
      new App.ProductsCollection().setup();
    }
    if ($('body.upsell-page').length) {
      new App.Countdown().setup();
      new App.ExitIntentPopup().setup();
      if ($('.upsell-page-mobile-phone-upsell').length) {
        new App.MobilePhoneUpsell().setup();
      }
    }
    if ($('.report-builder').length !== 0 || $('.integration-builder').length !== 0) {
      new App.ReportBuilder().setup();
    }
    if ($(".edit_integrations_zapier").length !== 0) {
      new App.Integrations.Zapier().setup();
    }
    if ($(".edit_integrations_salesforce_marketing_cloud").length !== 0 || $(".new_integrations_salesforce_marketing_cloud").length !== 0) {
      new App.Integrations.SalesforceMarketingCloud().setup();
    }
    if ($('body.reclaim_accounts').length) {
      new App.ReclaimAccounts().setup();
    }
    if ($("#update-card-element").length !== 0) {
      stripe_elements_style = {
        base: {
          iconColor: '#697A8B',
          color: '#354B70',
          lineHeight: '24px',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '15px',
          fontWeight: '600',
          '::placeholder': {
            color: '#697A8B'
          }
        },
        invalid: {
          color: '#ff4d55',
          iconColor: '#ff4d55'
        }
      };
      new App.UpdateCard().setup(stripe_elements_style);
    } else if ($(".update-moov-card").length !== 0) {
      new App.UpdateCard().setup({});
    }
    if ($("#payment-request-button").length !== 0 && window.PaymentRequest !== void 0) {
      if ($('meta[name=page-payment-request-button-enabled-id]').length !== 0) {
        new App.LandingPage.PaymentRequest().create();
      }
      new App.LandingPage.PaymentRequest().setup();
    } else {
      $('.payment-request-details').remove();
    }
    if (($('.rv-type-conduit-page').length || $('.upsell-page-conduit-upsell').length) && !$('.panel-type-upsell').length) {
      new App.LandingPage.ConduitPage().setup();
    }
    if ($('.rv-type-membership-page').length || $('.rv-type-subscribable-item-page').length || $('.upsell-page-membership-upsell').length || $('.upsell-page-subscribable-item-upsell').length) {
      new App.LandingPage.SubscribablePage().setup();
    }
    if ($('body.advanced.index').length) {
      new App.AdvancedIndex().setup();
    }
    if (($('.rv-type-merchandise-page').length || $('.upsell-page-merchandise-upsell').length || $('.rv-type-checkout-page').length) && !$('.panel-type-upsell').length) {
      new App.LandingPage.MerchandisePage().setup();
    }
    if ($('body.initiatives').length) {
      new App.InitiativesBuilder().setup();
    }
    if ($('.style-guide').length) {
      new App.StyleGuide().setup();
    }
    if ($('body.conduit_candidates.edit').length) {
      new App.ConduitBuilder().setup();
    }
    if ($('.panel-type-upsell').length) {
      new App.LandingPage.PanelUpsellPage().setup();
    }
    if ($('.contact-card-upsell').length) {
      new App.LandingPage.ContactCardUpsellPage().setup();
    }
    if ($('.reporting').length) {
      new App.Reporting().setup();
    }
    if ($('.conduit-inclusion-reporting').length) {
      new App.ConduitInclusionReporting().setup();
    }
    if ($('.origin-report').length) {
      new App.Reporting().setup();
    }
    if ($('.merchandise-campaign-form').length) {
      new App.LandingPage.InitiativeMerchandiseCampaign().setup();
    }
    if ($('.stripe-file-upload').length) {
      new App.StripeFileUpload().setup();
    }
    if ($('.stripe-bank-account-token').length) {
      new App.StripeBankAccountToken().setup();
    }
    if ($('body.onboarding').length) {
      new App.Onboarding().setup();
    }
    if ($('.upsell-group-settings').length) {
      new App.UpsellGroup().setup();
    }
    if ($('.leads-reporting').length) {
      new App.LeadsReporting().setup();
    }
    if ($('.account_closure_settings').length) {
      new App.AccountClosureSettings().setup();
    }
    if ($('.virtual-terminal').length) {
      return new App.VirtualTerminal().setup();
    }
  };

  App.initVideoEmbed = function() {
    var i, j, ref, results, vidDefer;
    vidDefer = document.getElementsByTagName('iframe');
    results = [];
    for (i = j = 0, ref = vidDefer.length; 0 <= ref ? j < ref : j > ref; i = 0 <= ref ? ++j : --j) {
      if (vidDefer[i].getAttribute('data-src')) {
        vidDefer[i].setAttribute('src', vidDefer[i].getAttribute('data-src'));
        results.push(vidDefer[i].removeAttribute('data-src'));
      } else {
        results.push(void 0);
      }
    }
    return results;
  };

  $(document).ready(function() {
    App.init();
    return App.initVideoEmbed();
  });

}).call(this);
(function() {
  window.gm_authFailure = function() {
    var i, input, len, old_placeholder, ref, results;
    if (typeof google !== "undefined" && google !== null) {
      ref = document.getElementsByClassName("autocomplete");
      results = [];
      for (i = 0, len = ref.length; i < len; i++) {
        input = ref[i];
        input.disabled = false;
        input.placeholder = '';
        $(input).removeClass('gm-err-autocomplete');
        $(input).attr('style', '');
        old_placeholder = $(input).siblings('label').text();
        input.placeholder = old_placeholder;
        results.push(google.maps.event.clearInstanceListeners(input));
      }
      return results;
    }
  };

  App.AddressAutocomplete = (function() {
    function AddressAutocomplete() {}

    AddressAutocomplete.prototype.setup = function() {
      var componentFormat, componentMapping, fillInAddress, google_address_autocomplete_delay, initAutocomplete;
      componentFormat = {
        street_number: "short_name",
        route: "long_name",
        locality: "long_name",
        administrative_area_level_1: "short_name",
        postal_code: "short_name"
      };
      componentMapping = {
        street_number: "address",
        route: "address",
        locality: "city",
        administrative_area_level_1: "state",
        postal_code: "zip"
      };
      initAutocomplete = function(input) {
        var _addEventListener, addEventListenerWrapper, autocomplete;
        _addEventListener = input.addEventListener ? input.addEventListener : input.attachEvent;
        $(input).data('suggestion_selected', false);
        addEventListenerWrapper = function(type, listener) {
          var orig_listener;
          if (type === 'keydown') {
            orig_listener = listener;
            listener = function(event) {
              var simulated_downarrow;
              if ((event.which === 13 || event.which === 9) && !$(input).data('suggestion_selected')) {
                $(input).data('suggestion_selected', true);
                simulated_downarrow = $.Event('keydown', {
                  keyCode: 40,
                  which: 40
                });
                orig_listener.apply(input, [simulated_downarrow]);
                if (event.which === 13) {
                  event.preventDefault();
                  $(input).parents('.row').find('.zip').focus();
                }
              } else {
                $(input).data('suggestion_selected', false);
              }
              orig_listener.apply(input, [event]);
            };
          }
          _addEventListener.apply(input, [type, listener]);
        };
        input.addEventListener = addEventListenerWrapper;
        input.attachEvent = addEventListenerWrapper;
        autocomplete = new google.maps.places.Autocomplete(input, {
          types: ["geocode"],
          componentRestrictions: {
            country: ["us"]
          }
        });
        return autocomplete.addListener("place_changed", function() {
          return fillInAddress(autocomplete, input);
        });
      };
      fillInAddress = function(autocomplete, input) {
        var _, _input, addressType, address_component, component, componentId, field, format, i, len, object_name, place, prefix, ref, results, value;
        place = autocomplete.getPlace();
        object_name = input.dataset.objectName;
        prefix = input.dataset.prefix;
        for (_ in componentMapping) {
          component = componentMapping[_];
          _input = document.getElementById(object_name + "_" + prefix + component);
          if (_input) {
            _input.value = "";
            _input.disabled = false;
          }
        }
        ref = place.address_components;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          address_component = ref[i];
          addressType = address_component.types[0];
          component = componentMapping[addressType];
          if (component) {
            componentId = object_name + "_" + prefix + component;
            format = componentFormat[addressType];
            if (format) {
              value = address_component[format];
              field = document.getElementById(componentId);
              if (field) {
                if (!field.value) {
                  field.value = value;
                } else {
                  field.value += " " + value;
                }
              }
            }
          }
          if (App.submitButtonValidator !== void 0) {
            results.push(App.submitButtonValidator());
          } else {
            results.push(void 0);
          }
        }
        return results;
      };
      if (typeof google !== "undefined" && google !== null) {
        google_address_autocomplete_delay = function(input_class) {
          var activeAutocomplete, autocomplete_limit;
          activeAutocomplete = false;
          autocomplete_limit = 5;
          return $(input_class).on('keyup', function() {
            var i, input, len, ref;
            if (this.value.length >= autocomplete_limit && activeAutocomplete === false) {
              ref = document.getElementsByClassName("autocomplete");
              for (i = 0, len = ref.length; i < len; i++) {
                input = ref[i];
                initAutocomplete(input);
              }
              return activeAutocomplete = true;
            } else {
              if (this.value.length < autocomplete_limit && activeAutocomplete === true) {
                google.maps.event.clearInstanceListeners(this);
                $('.pac-container').remove();
                return activeAutocomplete = false;
              }
            }
          });
        };
        return google_address_autocomplete_delay('.form-control.autocomplete');
      }
    };

    return AddressAutocomplete;

  })();

}).call(this);
(function() {
  App.AdvancedIndex = (function() {
    function AdvancedIndex() {}

    AdvancedIndex.prototype.setup = function() {
      var poll, url;
      url = document.location.pathname + '/stats';
      poll = function() {
        $.ajax({
          'url': url,
          'success': function(data) {
            $('.active-subscriptions-count').html(data.active_subscriptions);
            $('.paused-subscriptions-count').html(data.available_paused_subscriptions);
            setTimeout(poll, 1000);
          }
        });
      };
      return poll();
    };

    return AdvancedIndex;

  })();

}).call(this);
(function() {
  App.AmountButtonAnimation = (function() {
    function AmountButtonAnimation() {}

    AmountButtonAnimation.prototype.setup = function() {
      return $(document).ready(function() {
        return $('.rv-amount-button-animation, .rv-accept-button-animation').each(function() {
          var animation_classes, button_override, color, delay, delay_class, duration, duration_class, element, element_id, position, position_name, repeat, repeat_after, repeat_class, style;
          button_override = parseInt($.urlParam('animatedButton'));
          if (button_override) {
            position = $.urlParam('animatedButton');
            if ($('.rv-amount-button-animation[data-position="' + button_override + '"]').length === 0) {
              if (!$(this).is($('.rv-amount-button-animation').last())) {
                return;
              }
              $('[data-name="' + 'amt' + position + '"]').addClass('animated-override-button');
            } else if (button_override !== $(this).data('position')) {
              return;
            }
          } else {
            position = $(this).data('position');
          }
          color = $(this).data('color');
          style = $(this).data('style');
          duration = $(this).data('duration');
          delay = $(this).data('delay');
          repeat = $(this).data('repeat');
          repeat_after = $(this).data('repeat-after');
          element_id = $(this).data('element-id');
          if (repeat === 1 && repeat_after === 0) {
            repeat_class = ' animate__infinite';
          } else {
            repeat_class = '';
          }
          position_name = 'amt' + position;
          if (delay === 0 || !delay) {
            delay_class = '';
          } else {
            delay_class = ' animate__delay-' + delay + 's';
          }
          if (duration) {
            duration_class = ' animate__' + duration;
          } else {
            duration_class = '';
          }
          if ($(this).hasClass('rv-accept-button-animation')) {
            element = $(element_id);
          } else {
            element = $('[data-name="' + position_name + '"]');
          }
          animation_classes = 'animate__animated animate__' + style + duration_class + delay_class + repeat_class;
          element.addClass('color-button ' + animation_classes);
          if (repeat === 1 && repeat_after !== 0) {
            return element.on('animationend', function() {
              element.removeClass(animation_classes);
              return setTimeout((function() {
                return element.addClass(animation_classes);
              }), repeat_after);
            });
          }
        });
      });
    };

    return AmountButtonAnimation;

  })();

}).call(this);
(function() {
  App.Bootstrap = (function() {
    function Bootstrap() {}

    Bootstrap.prototype.setup = function() {
      var formatIsoDate, setFieldValues;
      $('[data-toggle="tooltip"]').tooltip();
      $('[data-toggle="popover"]').popover();
      $('[data-toggle="popover"][data-target]').on('click', function() {
        return $($(this).data('target')).toggle();
      });
      if ($.fn.datepicker) {
        $('.input-daterange').datepicker({
          'format': 'yyyy-mm-dd',
          'orientation': 'bottom auto'
        });
      }
      $('.datetimepicker-input').each(function() {
        var date_passed, one_month_from_now, options, today, tomorrow;
        options = {
          value: $(this).attr('value'),
          format: 'Y/m/d h:i A',
          step: 30,
          scrollMonth: false,
          scrollInput: false
        };
        if ($('.datetimepicker-input-past').length) {
          options['maxDate'] = 0;
        } else {
          options['minDate'] = 0;
        }
        if ($('.datetimepicker-input-no-time').length) {
          options['timepicker'] = false;
          options['format'] = 'Y/m/d';
        }
        if ($(this).hasClass('delayed-start')) {
          date_passed = Date.parse("2020/11/1") < new Date;
          if (!date_passed) {
            options['minDate'] = "2020/11/01";
            options['startDate'] = "2020/11/01";
          } else {
            tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            one_month_from_now = new Date();
            one_month_from_now.setDate(one_month_from_now.getDate() + 30);
            options['minDate'] = tomorrow;
            options['maxDate'] = one_month_from_now;
            today = new Date();
            today.setDate(today.getDate());
            options['defaultDate'] = today;
          }
        }
        return $(this).datetimepicker(options);
      });
      formatIsoDate = function(date_value) {
        var last_space_separator_index;
        if (date_value === void 0) {
          return date_value;
        }
        last_space_separator_index = date_value.lastIndexOf(' ');
        return date_value.substring(0, last_space_separator_index) + date_value.substring(last_space_separator_index + 1);
      };
      $('.input-daterange input').each(function() {
        var dp, endDate, formattedEndDate, formattedStartDate, parent, startDate, truncated_value;
        dp = $(this);
        parent = $(this).parent();
        startDate = parent.data('start-date');
        endDate = parent.data('end-date');
        formattedStartDate = formatIsoDate(startDate);
        formattedEndDate = formatIsoDate(endDate);
        if (startDate) {
          dp.datepicker('setStartDate', new Date(formattedStartDate));
        }
        if (endDate) {
          dp.datepicker('setEndDate', new Date(formattedEndDate));
        }
        truncated_value = $(this).val().split('T')[0];
        return $(this).val(truncated_value);
      });
      $('.modal').on('shown.bs.modal', function() {
        return $(this).find('input').focus();
      });
      $('.rv-modal').on('show.bs.modal', function(event) {
        var modal, trigger;
        trigger = $(event.relatedTarget);
        modal = $(this);
        return $.each(trigger.data(), function(key, value) {
          var data_input_key, element, input_element;
          if (!['toggle', 'target'].includes(key)) {
            key = key.dasherize();
            element = $('.' + key);
            if (element.length) {
              return element.text(value);
            } else {
              data_input_key = '[data-input=' + key + ']';
              input_element = $(data_input_key);
              if (input_element) {
                return input_element.val(value);
              }
            }
          }
        });
      });
      $('.rv-error-count').each(function() {
        var anchor, error_count, url;
        url = this.href;
        anchor = url.substring(url.indexOf("#") + 1);
        error_count = $('#' + anchor).find('.has-danger').size();
        if (error_count > 0) {
          return $(this).append(' <span class="badge badge-pill badge-danger" title="' + error_count + ' error(s)">!</span>');
        } else {
          return $(this).find('.error-count-badge').remove();
        }
      });
      $('[data-toggle=toggle]').on('click', function(event) {
        return $($(this).data('target')).each(function() {
          return $(this).toggle('show');
        });
      });
      $('[data-toggle=class_collapse]').on('click', function(event) {
        return $($(this).data('target')).each(function() {
          return $(this).toggleClass('show');
        });
      });
      $('[data-toggle=stop_collapse]').on('click', function(event) {
        $($(this).data('target')).each(function() {
          return $(this).toggleClass('show');
        });
        event.stopPropagation();
        return event.preventDefault();
      });
      $(document).keydown(function(event) {
        if ((event.ctrlKey || event.metaKey) && event.which === 70) {
          $('.searchable-collapse').addClass('show');
          return $('.searchable-collapse-summary').removeClass('show');
        }
      });
      $(document).on('change', '[data-toggle="updater"][data-target]', function() {
        var data, target_selector, value;
        target_selector = $(this).data('target');
        value = this.value;
        data = $(this).data('update-data');
        return $(this).parents('.update-group').find(target_selector).each(function() {
          return $(this).attr('value', data[value]);
        });
      });
      $('[data-toggle=link]').on('click', function(event) {
        var trigger;
        trigger = $(event.target);
        if (!trigger.hasClass('dropdown-item')) {
          if (window.App.KeyboardShortcuts.isMetaOrControlDown()) {
            return window.open($(this).data('path'));
          } else {
            return location.href = $(this).data('path');
          }
        }
      });
      $('[type=checkbox][data-toggle=confirm]').on('click', function(event) {
        var $checkbox, $checkbox_hidden;
        $checkbox = $(this);
        if ($checkbox.is(':checked')) {
          $checkbox_hidden = $(this).prev();
          if (!confirm($checkbox.data('message'))) {
            $checkbox.prop('checked', false);
            return $checkbox_hidden.prop('value', '0');
          }
        }
      });
      $('.rv-table-row').on('click', '[data-toggle=dropdown]', function(event) {
        event.stopPropagation();
        return $(this).dropdown('toggle');
      });
      setFieldValues = function(target, data) {
        var nested_fields;
        nested_fields = target.parents('.nested-fields').first();
        nested_fields.find('[name$="[input_type]"]').val(data.input_type || 'text_field').trigger('change');
        nested_fields.find('[name$="[label]"]').val(data.label);
        if (data.label) {
          nested_fields.find('[name$="[label]"]').trigger('click');
        }
        nested_fields.find('[name$="[placeholder]"]').val(data.placeholder);
        if (data.placeholder) {
          nested_fields.find('[name$="[placeholder]"]').trigger('click');
        }
        nested_fields.find('[name$="[default_value]"]').val(data.default_value);
        if (data.default_value) {
          nested_fields.find('[name$="[default_value]"]').trigger('click');
        }
        nested_fields.find('[name$="[position]"]').val(data.position || 'before');
        nested_fields.find('[name$="[target]"]').val(data.target || 'donation');
        nested_fields.find('[name$="[required]"]').prop('checked', data.required || false);
        return nested_fields.find('[name$="[other_field_enabled]"]').prop('checked', data.other_field_enabled || false);
      };
      $('body').on('change', '[data-toggle="custom-select"]', function() {
        var $target, _this, url;
        _this = this;
        $target = $(this).parents($(this).data('target'));
        if (this.value) {
          url = $(this).data('url');
          return $.ajax({
            'url': url + '/' + this.value + '.json',
            'success': function(data) {
              return setFieldValues($target, data);
            }
          });
        }
      });
      $('body').on('click', 'input[type="checkbox"][data-toggle="collapse"]', function(event) {
        event.stopPropagation();
        return $($(this).data('target')).toggle('show');
      });
      $('[data-toggle="disable-toggle"]').on('click', function(event) {
        event.stopPropagation();
        $($(this).data('target')).show().prop("disabled", false);
        return $(this).hide();
      });
      $('.alert-legal').on('closed.bs.alert', function(e) {
        return $.ajax('/profile/accept_privacy_policy', {
          type: 'POST',
          dataType: 'json'
        });
      });
      $('select[data-toggle="select-toggler"]').on('change', function() {
        var data, target, val;
        data = $(this).data();
        val = $(this).val();
        if (data.asTargets || data.asTargets === 'true') {
          target = $(val + '.select-toggle-item');
          $(target).addClass('active').trigger('show.toggle.item');
          return $(target).siblings('.select-toggle-item').removeClass('active').trigger('hide.toggle.item');
        } else {
          target = data.target;
          if (val === data.nilValue || val === '0' || val === 0 || val === null || val === void 0 || val === '') {
            return $(target).removeClass('active').trigger('hide.toggle.item');
          } else {
            return $(target).addClass('active').trigger('show.toggle.item');
          }
        }
      });
      $('.rv-panel').on('change', '.widget-type-select', function(e) {
        var sel, settings, target, val;
        val = $(this).val();
        sel = "." + val + "-fields";
        settings = $(this).closest(".widget-setting").find("[class*='-fields']");
        settings.each(function() {
          return $(this).removeClass('active');
        });
        target = $(this).closest(".widget-setting").find("." + val + "-fields")[0];
        $(target).addClass('active');
        if ($('.datetimepicker-input').length) {
          return $('.datetimepicker-input').each(function() {
            return $(this).datetimepicker({
              minDate: 0,
              value: $(this).attr('value'),
              format: 'Y/m/d h:i A',
              step: 30
            });
          });
        }
      });
      $('#widgets-page-settings, #widgets-upsell-settings').on('change', function() {
        if ($('.page-search-and-select').length) {
          new App.PageBuilder().attach_select2($('.page-search-and-select'), 'Choose a Page');
          $('.page-search-and-select').on('change', function(e) {
            return new App.PageBuilder().page_custom_fields(this);
          });
          return $('.poll_results_custom_field').on('change', function(e) {
            var selected_custom_field;
            selected_custom_field = $(this).find(':selected').text();
            if (selected_custom_field.length) {
              return $('.poll_results_label').val(selected_custom_field);
            }
          });
        }
      });
      $('#widgets-page-settings, #widgets-upsell-settings').on('change', '.animation-setting', function(e) {
        var animation_classes, delay, delay_class, duration, duration_class, preview_button, repeat, style, style_class, widget;
        widget = $(this).parents('.widget-setting .active');
        preview_button = widget.find('.animate-button-preview');
        style = widget.find('.animation-style').val();
        duration = widget.find('.animation-duration').val();
        delay = widget.find('.animation-delay').val();
        repeat = widget.find('.animation-repeat').is(':checked');
        style_class = ' animate__' + style;
        if (duration) {
          duration_class = ' animate__' + duration;
        } else {
          duration_class = '';
        }
        if (delay === 0) {
          delay_class = '';
        } else {
          delay_class = ' animate__delay-' + delay + 's';
        }
        animation_classes = 'animate__animated ' + style_class + duration_class + delay_class;
        preview_button.addClass(animation_classes);
        return preview_button.one('webkitAnimationEnd oanimationend msAnimationEnd animationend', function(e) {
          return preview_button.removeClass(animation_classes);
        });
      });
      $('#widgets-page-settings, #widgets-upsell-settings').on('change', '.minicolors', function(e) {
        var preview_button, val, widget;
        widget = $(this).parents('.widget-setting');
        preview_button = widget.find('.animate-button-preview');
        val = $(this).val();
        if (val) {
          return preview_button.css('background-color', val);
        }
      });
      $('.dropdown-item[data-toggle=collapse]').on('click.bs.dropdown', function(e) {
        var target;
        $(this).find('.collapse-hidden').toggle();
        $(this).find('.collapse-shown').toggle();
        e.stopPropagation();
        e.preventDefault();
        target = $(this).attr('href');
        return $(target).collapse('toggle');
      });
      (function() {
        $('select[data-toggle="select-toggler"]').trigger('change');
        return $('.widget-type-select').trigger('change');
      })();
      $('input[type="checkbox"]').on('click', function() {
        return $(this).parents('label').toggleClass('active');
      });
      $('body').on('click', 'input[type="radio"]', function() {
        $('[name="' + $(this).attr('name') + '"]').parent('label').removeClass('active');
        return $(this).parent('label').toggleClass('active');
      });
      $('[data-toggle=reveal]').on('click', function(event) {
        $($(this).data('target')).removeClass('hidden-xs-up');
        return $(this).hide();
      });
      $('[data-toggle-hide]').on('click', function(event) {
        $($(this).data('toggle-hide')).addClass('hidden-xs-up');
        return $(this).hide();
      });
      if ($('[data-toggle=scrollable]').length > 0) {
        $(window).on('resize scroll', function() {
          return $('[data-toggle=scrollable]').each(function() {
            if ($(this).isInViewport()) {
              return $(this).trigger('scrollable.visible');
            }
          });
        });
      }
      $('.collapse.js-icon-toggle').on('show.bs.collapse', function(e) {
        var closedIconSelector, iconParent, openIconSelector;
        iconParent = $("a[href='#" + e.target.id + "']");
        closedIconSelector = $(e.target).data('closed-icon-selector');
        openIconSelector = $(e.target).data('open-icon-selector');
        iconParent.find(closedIconSelector).hide();
        return iconParent.find(openIconSelector).show();
      });
      $('.collapse.js-icon-toggle').on('hide.bs.collapse', function(e) {
        var closedIconSelector, iconParent, openIconSelector;
        iconParent = $("a[href='#" + e.target.id + "']");
        closedIconSelector = $(e.target).data('closed-icon-selector');
        openIconSelector = $(e.target).data('open-icon-selector');
        iconParent.find(openIconSelector).hide();
        return iconParent.find(closedIconSelector).show();
      });
      (function() {
        var target;
        target = window.location.hash;
        if (target.length === 0) {
          target = $('body').data('current-tab');
        }
        if ($('[href="' + target + '"]').length) {
          $('[href="' + target + '"]').click();
          return new App.Bootstrap().set_current_tab(target);
        }
      })();
      return $('[role=tab]').on('click', function() {
        var target;
        target = $(this).data('target') || $(this).attr('href');
        return new App.Bootstrap().set_current_tab(target);
      });
    };

    Bootstrap.prototype.set_current_tab = function(target) {
      var tab_field;
      tab_field = $('.current-tab');
      if (!tab_field.length) {
        tab_field = $('<input type="hidden" name="current_tab" class="current-tab">').appendTo('form');
      }
      tab_field.val(target);
      tab_field.trigger('change.current.tab');
      if ($('body.landing-page').length === 0 && $('body.upsell-page').length === 0) {
        return window.location.hash = target;
      }
    };

    return Bootstrap;

  })();

}).call(this);
(function() {
  App.Common = (function() {
    function Common() {}

    Common.prototype.setup = function() {
      var additionalOptions, breadcrumbs;
      App.roundDown = function(value) {
        var float_value;
        float_value = parseFloat(value);
        return parseFloat((parseInt(float_value * 100) / 100).toFixed(2));
      };
      App.toIntCents = function(value) {
        return parseInt((value * 100.0).toFixed(0));
      };
      App.toFloatDollars = function(value) {
        return parseFloat((value / 100.0).toFixed(2));
      };
      App.countDecimals = function(value) {
        if (Math.floor(value) !== value) {
          return value.toString().split(".")[1].length || 0;
        } else {
          return 0;
        }
      };
      App.removeTrailingZerosOnCents = function(value_cents) {
        var value_dollars;
        value_dollars = App.toFloatDollars(value_cents);
        return parseFloat(value_dollars.toFixed(0)) === parseFloat(value_dollars.toFixed(2));
      };
      App.formatCentsToCurrency = function(value_cents) {
        return $("<p>" + App.toFloatDollars(value_cents) + "</p>").formatCurrency({
          roundToDecimalPlace: 2,
          removeTrailingZerosOnDecimal: App.removeTrailingZerosOnCents(value_cents)
        }).text();
      };
      if (!String.prototype.startsWith) {
        String.prototype.startsWith = function(searchString, position) {
          position = position || 0;
          return this.indexOf(searchString, position) === position;
        };
      }
      if (!String.prototype.includes) {
        String.prototype.includes = function(search, start) {
          if (typeof start !== 'number') {
            start = 0;
          }
          if (start + search.length > this.length) {
            return false;
          } else {
            return this.indexOf(search, start) !== -1;
          }
        };
      }
      App.sortable_helper_fixer = function(e, ui) {
        ui.children().each(function() {
          return $(this).width($(this).width());
        });
        return ui;
      };
      App.sortable_helper = function(selector_class, axis) {
        return $(selector_class).sortable({
          helper: App.sortable_helper_fixer,
          axis: axis,
          items: '.sortable-item',
          cursor: 'move',
          start: function(e, ui) {
            $(ui.item).click();
            return ui.placeholder.height(ui.item.height());
          },
          update: function(e, ui) {
            $(this).find('.sortable-item').each(function(i) {
              var $item, target;
              $item = $(this);
              target = $item.data('sortable-target');
              if (target) {
                return $(target).find('.sortable-position').val(i + 1);
              } else {
                return $item.find('.sortable-position').val(i + 1);
              }
            });
            if ($(this).parents('form.auto-submit').length) {
              return $(this).parents('form.auto-submit').trigger('submit.rails');
            }
          }
        });
      };
      if ($.fn.sortable) {
        App.sortable_helper('.all-sortable', false);
        App.sortable_helper('.horizontal-sortable', 'x');
        App.sortable_helper('.vertical-sortable', 'y');
        $('.vertical-position-sortable').sortable({
          helper: App.sortable_helper_fixer,
          axis: 'y',
          items: '.sortable-item',
          cursor: 'move',
          update: function(e, ui) {
            var url;
            url = $(this).data('url');
            if (url) {
              return $.ajax({
                url: url,
                data: $(this).sortable('serialize'),
                xhrFields: {
                  withCredentials: true
                },
                type: 'PATCH'
              });
            }
          }
        });
      }
      if ($('.rv-error-text:visible').text().includes("custom amount upsell must be after double and recurring upsells")) {
        breadcrumbs = $('.sortable-item');
        breadcrumbs.each(function(index, el) {
          var outtaPlace, position, previous;
          position = $(el).data('position');
          if (position !== index + 1) {
            outtaPlace = $(el).remove();
            previous = $('.horizontal-sortable').find("[data-position='" + (position - 1).toString() + "']");
            previous.after(outtaPlace);
            return false;
          }
        });
      }
      $('body').on('change', '.static-column-selector', function() {
        var $parent, $select;
        $select = $(this);
        $parent = $select.parents('.rv-report-format-section');
        if ($select.find(':selected').length !== 0 && ($select.find(':selected').prop('value') === '' || $select.find(':selected').prop('value') === 'static')) {
          $parent.find('.static-column-toggle').prop('value', 'true');
          return $parent.find('.static-column-value').removeClass('static-column-value--hide');
        } else {
          $parent.find('.static-column-toggle').prop('value', 'false');
          return $parent.find('.static-column-value').addClass('static-column-value--hide').find('input').prop('value', null);
        }
      });
      $('.auto-fill').on('click', '.active[data-toggle="autofill"]', function() {
        var items, number_fields, parent, target, value;
        parent = $(this).data('parent');
        target = $(this).data('target');
        value = $(this).data('value');
        items = $(this).closest(parent).find(target);
        if (isNaN(parseFloat(value))) {
          number_fields = items.filter('input[type=number]');
          number_fields.attr('type', 'text');
          number_fields.data('originalType', 'number');
        }
        items.filter(':not(select)').text(value).prop('value', value);
        $(this).parents(parent).find('select').append('<option value="' + value + '">' + value + '</option>').val(value);
        $(this).closest(parent).find('.field-group').addClass('employment-form--hide');
        return items.each(function() {
          if (this.required) {
            return $(this).parents('form[data-toggle=validate]').first().validate().element($(this));
          }
        });
      });
      $('.auto-fill').on('click', '[data-toggle="autofill"]:not(.active)', function() {
        var items, parent, target, value;
        parent = $(this).data('parent');
        target = $(this).data('target');
        value = $(this).data('value');
        items = $(this).closest(parent).find(target);
        items.each(function() {
          if ($(this).data('originalType') === 'number') {
            return $(this).attr('type', 'number');
          }
        });
        items.filter("[id*='_zip']").addClass('zip-mask');
        items.filter(':not(select)').text('').prop('value', '');
        $(this).parents(parent).find('option').filter(':contains("' + value + '")').remove();
        $(this).closest(parent).find('.field-group').removeClass('employment-form--hide');
        return items.each(function() {
          if (this.required) {
            return $(this).parents('form[data-toggle=validate]').first().validate().element($(this));
          }
        });
      });
      $(".phone-mask").mask('1 (000) 000-0000');
      $(".phone-mask-no-country-code").mask('(000) 000-0000');
      $(".zip-mask").mask('00000');
      $('.canada-zip-mask').mask('S0S 0S0');
      $(".tax-id-mask").mask('00-0000000');
      $(".dob-mask").mask('00 / 00 / 0000');
      $(".ssn-last4-mask").mask('***-**-0000');
      $(".ssn-mask").mask('000-00-0000');
      $(".sin-mask").mask('000-000-000');
      $(".routing-number-mask").mask('000000000');
      $(".fec-registration-mask").mask('C00000000');
      $(".micro-deposit-mask").bind('keydown', function(e) {
        var current_value, last_value;
        if (e.key >= 0 && e.key <= 9) {
          current_value = $(this).val();
          last_value = current_value.charAt(current_value.length - 1);
          $(this).val('$0.' + last_value + e.key);
          return false;
        } else {
          return false;
        }
      });
      if ($('.pages.edit').length !== 0) {
        $('a[data-toggle="wizard"]').on('click', function(e) {
          e.preventDefault();
          return $.each($(this).data('target').split(' '), function() {
            return $('a.nav-link[href="' + this + '"]').tab('show');
          });
        });
      }
      if ($('.cocoon-positionable').length !== 0) {
        $('.page-builder').on('cocoon:before-insert', '.cocoon-positionable', function(event, newItem) {
          var val;
          if ($(this).find(".cocoon-item-position").last().val()) {
            val = parseInt($(this).find(".cocoon-item-position").last().val());
            return $(newItem).find(".cocoon-item-position").attr('value', val + 1);
          } else {
            return $(newItem).find(".cocoon-item-position").attr('value', 1);
          }
        });
      }
      (function() {
        $(document).on('click', '.dropdown-menu-modern .dropdown-menu', function(e) {
          e.stopPropagation();
        });
        $('body').on('mouseenter mouseleave', '.dropdown-toggle, .nav-menu-item-link', function(e) {
          if ($('.dropdown-menu-modern.show').length === 0 && !$(this).hasClass('nav-dropdown-modern')) {
            $('.dropdown-toggle').removeClass('active');
            $('.nav-menu-item-link').removeClass('active');
            return $(this).addClass('active');
          }
        });
        $('body').on('click', '.dropdown-toggle', function(e) {
          if (!$(this).hasClass('nav-dropdown-modern')) {
            $('.dropdown-toggle').removeClass('active');
            return $(this).addClass('active');
          }
        });
        $('body').on('click', '.rv-dropdown-toggle', function(e) {
          if (!$(this).hasClass('nav-dropdown-modern')) {
            $('.rv-dropdown-toggle').removeClass('active');
            $('.js-dropdown-menu.active').removeClass('active').hide();
            $(this).parent().find('.js-dropdown-menu').show().addClass('active');
            $(this).parent().addClass('show');
            return $(this).addClass('active');
          }
        });
        $('body').on('mouseenter mouseleave', '.list-inline-item > .nav-link-modern', function(e) {
          if (!$(this).hasClass('nav-dropdown-modern')) {
            $('.list-inline-item > .nav-link-modern').removeClass('hover');
            if (!$(this).hasClass('active')) {
              return $(this).addClass('hover');
            }
          }
        });
        $('meta[name=tos-required-modal]').each(function() {
          return $('#terms-of-service-modal').modal('show');
        });
        if ($('#deprecation-redirect-warning-modal').length > 0) {
          return $('#deprecation-redirect-warning-modal').modal('show');
        }
      })();
      jQuery.fn.filterByText = function(textbox, selectSingleMatch, minimumTextValue) {
        return this.each(function() {
          var optionsAndOptGroups, select;
          select = this;
          optionsAndOptGroups = [];
          if (typeof selectSingleMatch === 'undefined') {
            selectSingleMatch = false;
          }
          if (typeof minimumTextValue === 'undefined') {
            minimumTextValue = 0;
          }
          $(select).children('option, optgroup').each(function() {
            optionsAndOptGroups.push($(this));
          });
          $(select).data('optionsAndOptGroups', optionsAndOptGroups);
          $(textbox).bind('keyup', function(e) {
            var regex, search;
            if ($(textbox).val().length > minimumTextValue || e.which === 46 || e.which === 8) {
              optionsAndOptGroups = $(select).empty().data('optionsAndOptGroups');
              search = $.trim($(this).val());
              regex = new RegExp(search, 'gi');
              $.each(optionsAndOptGroups, function() {
                var optionGroupClone;
                if ($(this).is('option')) {
                  if ($(this).text().match(regex) !== null) {
                    $(select).append($(this));
                  }
                } else {
                  optionGroupClone = $(this).clone();
                  $.each(optionGroupClone.children('option'), function() {
                    if ($(this).text().match(regex) === null) {
                      $(this).remove();
                    }
                  });
                  if (optionGroupClone.children().length) {
                    $(select).append(optionGroupClone);
                  }
                }
              });
            }
          });
          if (selectSingleMatch === true && $(select).children().length === 1) {
            $(select).children().get(0).selected = true;
          }
        });
      };
      $('select[data-filter=true]').each(function() {
        if ($(this).data('target').length && $($(this).data('target'))) {
          return $(this).filterByText($(this).data('target'));
        }
      });
      $('div[data-filter=true]').each(function() {
        var select;
        if ($(this).data('target').length && $($(this).data('target'))) {
          select = this;
          return $($(this).data('target')).bind('change keyup', function() {
            var regex, search;
            search = $.trim($(this).val());
            if (search.length) {
              regex = new RegExp(search, 'gi');
              return $(select).find('.option').each(function(i) {
                if ($(this).text().trim().match(regex) === null) {
                  return $(this).attr('style', 'display:none !important');
                } else {
                  return $(this).attr('style', '');
                }
              });
            } else {
              return $(select).find('.option').attr('style', '');
            }
          });
        }
      });
      if ($('body.landing-page').length === 0 && $('meta[name=sso-api-domain]').attr('content') && $('#authy-token').length === 0 && $('#authy-cellphone').length === 0) {
        additionalOptions = {};
        if (App.useSameSiteNone()) {
          additionalOptions['sameSite'] = 'none';
        }
        Cookies.set('origin_url', window.location.href, $.extend({
          secure: true
        }, additionalOptions));
        $.ajax({
          url: $('meta[name=sso-api-domain]').attr('content'),
          dataType: 'JSON',
          xhrFields: {
            withCredentials: true
          },
          type: 'GET',
          success: function(response, status, xhr) {
            var inFiveMinutes, sso_tries;
            inFiveMinutes = new Date(new Date().getTime() + 5 * 60 * 1000);
            if (Cookies.get('sso_tries') === void 0) {
              Cookies.set('sso_tries', 1, $.extend({
                expires: inFiveMinutes,
                secure: true
              }, additionalOptions));
            }
            sso_tries = parseInt(Cookies.get('sso_tries'));
            if (response && response.data && response.data.attributes && response.data.attributes.login_url && sso_tries <= 3) {
              sso_tries += 1;
              Cookies.set('sso_tries', sso_tries, $.extend({
                expires: inFiveMinutes,
                secure: true
              }, additionalOptions));
              window.location.href = response.data.attributes.login_url;
            }
            if (response && response.data && response.data.attributes && response.data.attributes.rvid) {
              return Cookies.set('rvid', response.data.attributes.rvid, $.extend({
                expires: 1825,
                secure: true
              }, additionalOptions));
            }
          }
        });
      }
      $('.btn-export, #export_report_button').click(function(e) {
        var button, icon, text;
        e.preventDefault();
        button = $(this);
        if ($('.alert.alert-dismissible.alert-info.rv-alert').length > 0) {
          button.addClass('disabled');
          return;
        }
        if (button.hasClass('disabled')) {

        } else {
          button.addClass('disabled');
          icon = button.find('.far');
          text = button.find('.rv-fw-regular');
          icon.addClass('fa-spinner fa-spin');
          return button.closest('form').submit();
        }
      });
      return $('.btn-outline-info').click(function(e) {
        var button, icon, text;
        button = $(this);
        if (button.hasClass('disabled')) {

        } else {
          button.addClass('disabled');
          icon = button.find('.far');
          return text = button.find('.rv-fw-regular');
        }
      });
    };

    return Common;

  })();

}).call(this);
(function() {
  App.Confetti = (function() {
    function Confetti() {}

    Confetti.prototype.setup = function() {
      return $(document).ready(function() {
        return $('.rv-confetti-animation').each(function() {
          var animation_style, color, custom_image, emoji, image, options, shape, trigger_element, use_emoji;
          color = $(this).data('color');
          shape = $(this).data('shape');
          animation_style = $(this).data('animation-style');
          trigger_element = $(this).data('trigger-element');
          use_emoji = $(this).data('use-emoji');
          emoji = $(this).data('emoji');
          custom_image = $(this).data('custom-image');
          options = {
            count: party.variation.range(20, 40)
          };
          if (use_emoji === 1 && emoji) {
            image = document.createElement('img');
            if (custom_image) {
              image.src = custom_image;
            } else {
              image.src = emoji;
            }
            image.style.width = '30px';
            options.shapes = image;
          } else if (shape) {
            options.shapes = shape;
          }
          if (color) {
            options.color = party.Color.fromHex(color);
          }
          $('.amount-option, .conduit-amount-option').on('click', function(e) {
            if ($('.landing-page-amounts-options').length > 0) {
              return $('.landing-page-amounts-options .btn').not(this).removeClass('already-animated');
            } else if ($('.conduit-amounts').length > 0) {
              return $('.conduit-amounts .btn').not(this).removeClass('already-animated');
            }
          });
          return $(trigger_element).on('click', function(e) {
            if ($(this).is("[class*='amount-']")) {
              if ($(this).hasClass('already-animated')) {
                return;
              }
            }
            if ($(this).is("[class*='btn-recurring']") || $(this).is("[class*='btn-cover-fees']")) {
              if (!$(this).find(':checkbox').is(':checked')) {
                $(this).removeClass('already-animated');
                return;
              }
              if ($(this).hasClass('already-animated')) {
                return;
              }
            }
            if ($(this).attr('style')) {
              return;
            }
            if (animation_style === 'sparkles') {
              party.sparkles(this, options);
            } else {
              party.confetti(this, options);
            }
            return $(this).addClass('already-animated');
          });
        });
      });
    };

    return Confetti;

  })();

}).call(this);
(function() {
  App.Countdown = (function() {
    var reset_to_next_day;

    function Countdown() {}

    Countdown.prototype.setup = function() {
      $('.rv-shotclock').each(function() {
        var seconds;
        seconds = $(this).data('seconds');
        return $(this).countdown({
          until: +seconds,
          compact: true,
          padZeroes: true,
          format: 'MS',
          layout: "<div class='custom-countdown-container'> <span class='custom-countdown-number value-minutes-{d10}{d1}{h10}{h1}{m10}{m1}'>{m10}</span> <span class='custom-countdown-number value-minutes-{d10}{d1}{h10}{h1}{m10}{m1}'>{m1}</span> <div class='custom-countdown-label'>minutes</div> </div> <div class='custom-countdown-container'> <span class='custom-countdown-separator'>:</span> </div> <div class='custom-countdown-container'> <span class='custom-countdown-number value-seconds-{d10}{d1}{h10}{h1}{m10}{m1}{s10}{s1}'>{s10}</span> <span class='custom-countdown-number value-seconds-{d10}{d1}{h10}{h1}{m10}{m1}{s10}{s1}'>{s1}</span> <div class='custom-countdown-label'>seconds</div> </div>",
          expiryText: '<div class="timer-expired">The timer has expired, but you can still donate below!</div>',
          alwaysExpire: true
        });
      });
      return $('.rv-countdown').each(function() {
        var d, end_ticks, reset_next_day;
        if ($(this).hasClass('countdown-to-midnight')) {
          d = new Date();
          end_ticks = d.setHours(24, 0, 0, 0);
        } else {
          end_ticks = $(this).data('end') * 1000;
        }
        reset_next_day = $(this).data('reset-next-day');
        return $(this).countdown({
          until: new Date(end_ticks),
          padZeroes: true,
          format: 'dhMS',
          layout: "{d<}<div class='custom-countdown-container'> <span class='custom-countdown-number value-days-{d10}{d1}'>{d10}</span> <span class='custom-countdown-number value-days-{d10}{d1}'>{d1}</span> <div class='custom-countdown-label'>days</div> </div> <div class='custom-countdown-container'> <span class='custom-countdown-separator'>:</span> </div>{d>} <div class='custom-countdown-container'> <span class='custom-countdown-number value-hours-{d10}{d1}{h10}{h1}'>{h10}</span> <span class='custom-countdown-number value-hours-{d10}{d1}{h10}{h1}'>{h1}</span> <div class='custom-countdown-label'>hours</div> </div> <div class='custom-countdown-container'> <span class='custom-countdown-separator'>:</span> </div> <div class='custom-countdown-container'> <span class='custom-countdown-number value-minutes-{d10}{d1}{h10}{h1}{m10}{m1}'>{m10}</span> <span class='custom-countdown-number value-minutes-{d10}{d1}{h10}{h1}{m10}{m1}'>{m1}</span> <div class='custom-countdown-label'>minutes</div> </div> <div class='custom-countdown-container'> <span class='custom-countdown-separator'>:</span> </div> <div class='custom-countdown-container'> <span class='custom-countdown-number value-seconds-{d10}{d1}{h10}{h1}{m10}{m1}{s10}{s1}'>{s10}</span> <span class='custom-countdown-number value-seconds-{d10}{d1}{h10}{h1}{m10}{m1}{s10}{s1}'>{s1}</span> <div class='custom-countdown-label'>seconds</div> </div>",
          expiryText: reset_next_day ? '' : '<div class="timer-expired">The countdown has ended, but you can still donate below!</div>',
          alwaysExpire: true,
          onExpiry: function() {
            if (reset_next_day) {
              return reset_to_next_day();
            }
          }
        });
      });
    };

    reset_to_next_day = function() {
      $('.rv-countdown').countdown('destroy');
      return $('.rv-countdown').countdown({
        until: '+24h +0s',
        padZeroes: true,
        format: 'dhMS',
        expiryText: '',
        alwaysExpire: true,
        onExpiry: function() {
          return reset_to_next_day();
        }
      });
    };

    return Countdown;

  })();

}).call(this);
(function() {
  App.DonationStream = (function() {
    function DonationStream() {}

    DonationStream.prototype.setup = function() {
      var dynamic_polling_value;
      App.displayResults = function(array_results, donation_stream, animation_duration, custom_copy) {
        var display_results, i, r, results_container;
        display_results = '';
        for (i in array_results) {
          r = array_results[i];
          display_results += '<span class="donation"><span class="amount-value">' + App.formatCentsToCurrency(r.amount * 100) + '</span> just donated by ' + r.name + '</span>';
        }
        if (custom_copy) {
          display_results += '<span class="donation badge badge-primary">' + custom_copy + '</span>';
        }
        results_container = $('<div class="donations"></div>');
        results_container.html(display_results);
        donation_stream.find('.display-results').html(results_container);
        return donation_stream.find('.display-results .donations').attr('style', "animation-duration: " + animation_duration + 's; -webkit-animation-duration: ' + animation_duration + 's;');
      };
      App.fetchAndDisplayResults = function(donation_stream, animation_duration) {
        var csrf_token, custom_copy, test_data;
        csrf_token = $('meta[name=csrf-token]').attr('content') || donation_stream.data('csrf-token');
        custom_copy = donation_stream.data('custom-copy');
        if (window.location.href.slice(-8) === '/preview') {
          test_data = [
            {
              amount: 100,
              name: 'TEST John D.'
            }, {
              amount: 199.50,
              name: 'TEST Paul K.'
            }, {
              amount: 400,
              name: 'TEST Clarence B.'
            }, {
              amount: 1000,
              name: 'TEST Richard J.'
            }
          ];
          return App.displayResults(test_data, donation_stream, animation_duration, custom_copy);
        } else {
          return $.ajax({
            url: donation_stream.data('donation-api-url') + '/' + donation_stream.data('donation-stream-uid'),
            headers: {
              'X-CSRF-Token': csrf_token
            },
            data: {
              stream_id: donation_stream.data('donation-stream-public-uid')
            },
            dataType: 'JSON',
            cache: false,
            xhrFields: {
              withCredentials: true
            },
            type: 'GET',
            success: function(response) {
              if (response && response.results && response.results.length > 0) {
                return App.displayResults(response.results, donation_stream, animation_duration, custom_copy);
              }
            }
          });
        }
      };
      dynamic_polling_value = {
        15: 4000,
        30: 2000,
        45: 2000,
        50: 2000,
        60: 1000
      };
      return $('.rv-donation-stream').each(function() {
        var animation_duration, direct, donation_stream;
        donation_stream = $(this);
        animation_duration = donation_stream.data('animation-duration');
        direct = donation_stream.data('direct');
        App.fetchAndDisplayResults(donation_stream, animation_duration);
        if (direct === 1 || direct === '1') {
          return setInterval(function() {
            return App.fetchAndDisplayResults(donation_stream, animation_duration);
          }, animation_duration * dynamic_polling_value[animation_duration]);
        }
      });
    };

    return DonationStream;

  })();

}).call(this);
(function() {
  App.ElectionDayLookUp = (function() {
    function ElectionDayLookUp() {}

    ElectionDayLookUp.prototype.setup = function() {
      var add_to_calendar_copy, copy_element, election_day_location_copy, enter_your_voting_address_copy, get_directions_copy, here_is_your_election_day_location_copy, not_my_address_copy, try_a_new_address_copy, voting_times_copy, your_voting_address_copy;
      copy_element = $('.rv-election-day-look-up .copy');
      election_day_location_copy = copy_element.data('election-day-location');
      add_to_calendar_copy = copy_element.data('add-to-calendar');
      your_voting_address_copy = copy_element.data('your-voting-address');
      get_directions_copy = copy_element.data('get-directions');
      here_is_your_election_day_location_copy = copy_element.data('here-is-your-election-day-location');
      enter_your_voting_address_copy = copy_element.data('enter-your-voting-address');
      not_my_address_copy = copy_element.data('not-my-address');
      voting_times_copy = copy_element.data('voting-times');
      try_a_new_address_copy = copy_element.data('try-a-new-address');
      $('.reset-form').on('click', function(e) {
        e.preventDefault();
        $('.reset-row').remove();
        return $('.profile-form input, .profile-form select').val('');
      });
      App.formatAddress = function(address, classes) {
        var address_content, address_element;
        if (classes == null) {
          classes = '';
        }
        address_element = $('<div>', {
          "class": classes
        });
        address_content = '';
        if (address.locationName) {
          address_content = "<div><strong>" + address.locationName + "</strong></div>";
        }
        address_content += "<div>" + address.line1 + "</div>";
        address_content += "<div>" + address.city + ", " + address.state;
        if (address.zip) {
          address_content += " " + address.zip;
        }
        address_content += "</div>";
        address_element.html(address_content);
        return address_element;
      };
      App.formatStringAddress = function(polling_location) {
        var address, formatted_address;
        address = polling_location;
        formatted_address = '';
        if (address.locationName) {
          formatted_address += address.locationName + ', ';
        }
        formatted_address += address.line1 + ', ' + address.city + ', ' + address.state;
        if (address.zip) {
          formatted_address += ' ' + address.zip;
        }
        return formatted_address;
      };
      App.staticMap = function(polling_location, google_maps_key, latitude, longitude) {
        var location, markers, static_map;
        location = App.formatStringAddress(polling_location);
        static_map = $('<div>', {
          "class": 'static-map'
        });
        markers = "color:red|" + latitude + "," + longitude;
        static_map.append($('<img>', {
          src: "https://maps.googleapis.com/maps/api/staticmap?center=" + location + "&size=325x325&maptype=roadmap&key=" + google_maps_key + "&markers=" + markers
        }));
        return static_map;
      };
      App.calendarButton = function(polling_location, election, formatted_polling_hours, calendar_url) {
        var address_element, calendar_button, calendar_link, calendar_types, dates, details, i, j, link, location, row, space, target, text;
        if (!election || !polling_location) {
          return '';
        }
        text = election_day_location_copy;
        if (formatted_polling_hours === '') {
          dates = election.electionDay.replace(/-/g, '') + '/' + '20201104';
        } else {
          dates = formatted_polling_hours;
        }
        location = App.formatStringAddress(polling_location.address);
        details = polling_location.pollingHours + "\n" + location;
        calendar_button = $('<div class="add-to-calendar">');
        address_element = $('<div>');
        address_element.html($('<b>', {
          html: add_to_calendar_copy
        }));
        calendar_button.append(address_element);
        row = $('<div>', {
          "class": 'calendar-links'
        });
        calendar_types = ['Google', 'Apple', 'Outlook'];
        for (i = j = 0; j < 3; i = ++j) {
          if (calendar_types[i] === 'Google') {
            link = encodeURI("https://calendar.google.com/calendar/r/eventedit?action=TEMPLATE&text=" + text + "&details=" + details + "&location=" + location + "&dates=" + dates.google_calendar_format);
            target = '_blank';
          } else {
            link = calendar_url;
            target = '';
          }
          calendar_link = $('<a>', {
            "class": 'btn btn-primary',
            target: target,
            href: link,
            id: calendar_types[i].toLowerCase() + '-calendar'
          });
          if (i === 2) {
            space = "";
          } else {
            space = " ";
          }
          calendar_link.html(calendar_types[i]);
          row.append(calendar_link);
          row.append(space);
        }
        calendar_button.append(row);
        return calendar_button;
      };
      App.directionsButton = function(voter_address, polling_location) {
        var directions_button, link, url;
        url = "https://www.google.com/maps/dir/" + App.formatStringAddress(voter_address) + "/" + App.formatStringAddress(polling_location);
        directions_button = $('<div class="get-directions">');
        link = $('<a>', {
          target: '_blank',
          href: encodeURI(url),
          "class": 'btn btn-block btn-primary'
        });
        link.html(get_directions_copy);
        directions_button.append(link);
        return directions_button;
      };
      App.notMyAddress = function() {
        return $('#not-my-address').on('click', function(e) {
          var newUrl;
          e.preventDefault();
          newUrl = $.updateURLParameter(window.location.href, 'resetaddress', true);
          return window.location = newUrl;
        });
      };
      return $('.rv-election-day-look-up').each(function() {
        var add_to_calendar, automatic, csrf_token, directions, element, form, not_found_copy, reset_address, valid;
        element = $(this);
        csrf_token = element.data('csrf-token');
        add_to_calendar = element.data('display-add-to-calendar');
        directions = element.data('display-directions');
        not_found_copy = element.data('not-found-copy');
        automatic = element.data('automatic');
        reset_address = window.location.href.indexOf('resetaddress=true') !== -1;
        $('.lookup-button').on('click', function(e) {
          var address, form, valid;
          e.preventDefault();
          form = $('#election-day-look-up-form');
          valid = App.elementValidatorFor(form);
          if (valid) {
            address = $('#election-day-look-up-form input#address').val() + ', ' + $('#election-day-look-up-form input#city').val() + ', ' + $('#election-day-look-up-form select#state').val() + ' ' + $('#election-day-look-up-form input#zip').val();
            $('.loading-text').html('Please Wait...');
            $('.loading').show();
            return $.ajax({
              url: element.data('election-day-look-up-api-url'),
              headers: {
                'X-Revv-Apikey': element.data('election-day-look-up-api-key'),
                'X-CSRF-Token': csrf_token
              },
              data: {
                address: address,
                election_id: element.data('election-id')
              },
              dataType: 'JSON',
              xhrFields: {
                withCredentials: true
              },
              type: 'POST',
              success: function(response) {
                var admin_name, admin_url, calendar_url, election, formatted_polling_hours, polling_address, polling_location, result, sos_link, voter_address;
                admin_name = response.adminName;
                admin_url = response.adminUrl;
                if (response.pollingLocations && response.pollingLocations.length > 0) {
                  result = $('<div>', {
                    "class": 'voter-results'
                  });
                  result.append($('<p><b>' + here_is_your_election_day_location_copy + '</b></p>'));
                  if (response.normalizedInput) {
                    voter_address = $('<div>', {
                      "class": 'election-day-address'
                    });
                    voter_address.append($('<div><strong>' + your_voting_address_copy + '</strong></div>'));
                    voter_address.append(App.formatAddress(response.normalizedInput, ''));
                    voter_address.append($('<a>', {
                      href: '#',
                      id: 'not-my-address',
                      html: not_my_address_copy
                    }));
                    result.append(voter_address);
                  }
                  polling_location = response.pollingLocations[0];
                  polling_address = $('<div>', {
                    "class": 'election-day-address polling-address'
                  });
                  polling_address.append("<div><i class='fas fa-2x icon-fa-map-marker'></i></div>");
                  polling_address.append(App.formatAddress(polling_location['address'], 'address-block'));
                  result.append(polling_address);
                  if (polling_location.pollingHours) {
                    result.append('<div class="voting-times"><i class="fas icon-fa-clock"/> <b>' + voting_times_copy + ':</b> ' + polling_location.pollingHours + '</div>');
                  }
                  result.append(App.staticMap(polling_location['address'], element.data('google-maps-key'), polling_location['latitude'], polling_location['longitude']));
                  if (add_to_calendar === 1) {
                    calendar_url = response.icsCalendarUrl;
                    election = response.election;
                    formatted_polling_hours = response.formattedPollingHours;
                    result.append(App.calendarButton(polling_location, election, formatted_polling_hours, calendar_url));
                  }
                  if (directions === 1) {
                    result.append(App.directionsButton(response.normalizedInput, polling_location['address']));
                  }
                } else {
                  result = $('<div>', {
                    "class": 'voter-results'
                  });
                  result.append(not_found_copy);
                  if (admin_url && admin_name) {
                    sos_link = $("<div class='admin-link'>Please check with the <a target='_blank' href='" + admin_url + "'>" + admin_name + "</a></div>");
                    result.append(sos_link);
                  }
                  result.append($('<a>', {
                    href: '#',
                    id: 'not-my-address',
                    "class": 'btn btn-block btn-primary',
                    html: try_a_new_address_copy
                  }));
                }
                $('.loading').hide();
                $('.loading-text').html('Processing');
                $('.display-form').hide();
                result.insertAfter($('.display-form'));
                return App.notMyAddress();
              },
              error: function(response, error) {
                var result;
                $('.loading').hide();
                $('.loading-text').html('Processing');
                result = $('<div>', {
                  "class": 'voter-results'
                });
                result.append(not_found_copy);
                result.append($('<a>', {
                  href: '#',
                  id: 'not-my-address',
                  "class": 'btn btn-block btn-primary',
                  html: try_a_new_address_copy
                }));
                $('.display-form').hide();
                result.insertAfter($('.display-form'));
                return App.notMyAddress();
              }
            });
          }
        });
        if (automatic === 1 && !reset_address) {
          form = $('#election-day-look-up-form');
          valid = App.elementValidatorFor(form);
          if (valid) {
            $('.lookup-button').click();
          }
        }
        if (reset_address) {
          $('.profile-form input, .profile-form select').val('');
          return $('.reset-row').remove();
        }
      });
    };

    return ElectionDayLookUp;

  })();

}).call(this);
(function() {
  App.ExitIntentPopup = (function() {
    var datalayer_push;

    function ExitIntentPopup() {}

    ExitIntentPopup.prototype.setup = function() {
      var cookie_expiration, html_content, popup_body_copy, popup_body_text, popup_button_copy, popup_class, popup_header_copy, popup_image, x_class;
      if ($('#exit-intent-popup').length) {
        popup_header_copy = $('#exit-intent-popup > .popup_header_copy').html();
        popup_body_copy = $('#exit-intent-popup > .popup_body_copy').html();
        popup_image = $('#exit-intent-popup > .popup_image').html();
        popup_body_text = $('#exit-intent-popup > .popup_body_text').html();
        popup_button_copy = $('#exit-intent-popup > .popup_button_copy').html();
        cookie_expiration = $('#exit-intent-popup').data('cookie-expiration') || 0;
        x_class = 'none';
        popup_class = '';
        if (!popup_header_copy && !popup_button_copy && !popup_body_text) {
          x_class = 'block';
          popup_class = ' image-only';
        }
        html_content = "<div class='rv-panel" + popup_class + "'>";
        if (popup_header_copy) {
          html_content += "<div class='header-section rv-panel-section'>";
          html_content += "<div class='rv-header'>" + popup_header_copy;
          html_content += "<button aria-label='Close' type='button' class='close'><span>&times;</span></button>";
          html_content += "</div>";
          html_content += "</div>";
        }
        if (popup_image) {
          html_content += "<div class='popup-image rv-panel-section'>" + popup_image + "</div>";
        }
        html_content += "<div class='rv-panel-section'>" + popup_body_copy + "</div>";
        if (popup_button_copy) {
          html_content += "<div class='rv-panel-section'><div class='row justify-content-center'>";
          html_content += "<a class='btn btn-primary' href='#' id='exitintent'>" + popup_button_copy + "</a>";
          html_content += "</div></div>";
        }
        html_content += "</div>";
        bioEp.init({
          html: html_content,
          css: '#bio_ep {width: 400px; height: auto;}' + '#bio_ep_close {padding-top: 2px; padding-left: 0px; padding-right: 0px; display: ' + x_class + ';}',
          delay: 1,
          cookieExp: cookie_expiration,
          onPopup: datalayer_push
        });
        return $('#bio_ep_close, #bio_ep_bg, #bio_ep .btn.btn-primary, #bio_ep button.close').click(function(e) {
          var newUrl, url;
          e.preventDefault();
          bioEp.hidePopup();
          url = window.location.href;
          if (url.indexOf('exitintent=true') === -1) {
            newUrl = $.updateURLParameter(window.location.href, 'exitintent', true);
            window.history.replaceState('', '', newUrl);
            return $('input[id$=source_url]').val(newUrl);
          }
        });
      }
    };

    datalayer_push = function() {
      var category, form_id;
      category = App.dataLayerCategory();
      if ($('.upsell-page').length && (!$('.upsell-page-panel-upsell').length && !$('.upsell-page-conduit-panel-upsell').length)) {
        form_id = $('.upsell-page form').attr('id');
        category = form_id.replace('new_', '').replace(/_/g, ' ');
      }
      if ($('.upsell-page-panel-upsell').length) {
        category = 'panel upsell';
      }
      if ($('.upsell-page-conduit-panel-upsell').length) {
        category = 'conduit panel upsell';
      }
      return new App.DataLayer().push({
        'event': app_platform + "Event",
        'category': category,
        'form': null,
        'action': 'widget',
        'label': 'exit-intent'
      });
    };

    return ExitIntentPopup;

  })();

}).call(this);
(function() {
  App.FloatLabel = (function() {
    function FloatLabel() {}

    FloatLabel.prototype.create_placeholder = function($element) {
      var $element_id, $placeholder, element_required, placeholder_text;
      $element.wrap('<div class="float-label">');
      $element_id = $element.attr('id');
      element_required = $element.attr('required') || '';
      placeholder_text = $element.attr('placeholder') || $element.attr('title');
      $placeholder = $('<label>').attr('for', $element_id).text(placeholder_text).addClass('placeholder').addClass(element_required).addClass($element.prop("tagName").toLowerCase());
      $placeholder.insertBefore($element);
      if ($element.val().length) {
        $placeholder.addClass('active');
      }
      $element.on('click focus', function() {
        $element.attr('placeholder', '');
        return $placeholder.addClass('active');
      });
      $element.on('blur', function() {
        var currentValue;
        $element.attr('placeholder', placeholder_text);
        currentValue = $element.val();
        if (currentValue === '') {
          return $placeholder.removeClass('active');
        }
      });
      $element.on('data.name.set', function(e, item) {
        if ($element.val()) {
          return $placeholder.addClass('active');
        }
      });
      return $placeholder;
    };

    FloatLabel.prototype.setup = function() {
      $(document).on('click', 'input[type=text], input[type=email], input[type=number], input[type=password], textarea', function() {
        var $element, $placeholder;
        $element = $(this);
        if ($element.parent('.float-label').length === 0 && !$element.hasClass('no-float-label') && !$element.hasClass('tox-textarea')) {
          $placeholder = new App.FloatLabel().create_placeholder($element);
          return $element.click().focus();
        }
      });
      return $('input[type=text], input[type=email], input[type=number], input[type=password], textarea').each(function() {
        var $element, $placeholder;
        $element = $(this);
        if (!$element.hasClass('no-float-label') && !$element.hasClass('tox-textarea')) {
          return $placeholder = new App.FloatLabel().create_placeholder($element);
        }
      });
    };

    return FloatLabel;

  })();

}).call(this);
(function() {
  App.HoneybadgerSetup = (function() {
    function HoneybadgerSetup() {}

    HoneybadgerSetup.prototype.setup = function() {
      return App.honeybadgerOnLoad = function() {
        var apiKey, environment;
        apiKey = $('script[data-name=honeybadger]').data('key');
        environment = $('script[data-name=honeybadger]').data('environment');
        return Honeybadger.configure({
          apiKey: apiKey,
          environment: environment
        });
      };
    };

    return HoneybadgerSetup;

  })();

}).call(this);
(function() {
  App.KeyboardShortcuts = (function() {
    var _keys;

    function KeyboardShortcuts() {}

    _keys = {};

    KeyboardShortcuts.prototype.setup = function() {
      $(document).keydown(function(e) {
        return window.App.KeyboardShortcuts.keyDown(e);
      });
      return $(document).keyup(function(e) {
        return window.App.KeyboardShortcuts.keyUp(e);
      });
    };

    KeyboardShortcuts.prototype.keyDown = function(e) {
      var element;
      if (e.which === 91 || e.which === 17) {
        _keys[e.key] = true;
        _keys[e.which] = true;
      }
      if (e.which === 13 && (element = e.currentTarget.activeElement)) {
        if ($(element).hasClass('submit-on-enter')) {
          return $(element).closest('form').submit();
        }
      }
    };

    KeyboardShortcuts.prototype.keyUp = function(e) {
      if (e.which === 91 || e.which === 17) {
        _keys[e.key] = false;
        return _keys[e.which] = false;
      }
    };

    KeyboardShortcuts.prototype.keys = function() {
      return _keys;
    };

    KeyboardShortcuts.prototype.isKeyDown = function(key) {
      if (_keys[key]) {
        return true;
      } else {
        return false;
      }
    };

    KeyboardShortcuts.prototype.isMetaOrControlDown = function() {
      return _keys[91] || _keys[17] || false;
    };

    return KeyboardShortcuts;

  })();

}).call(this);
(function() {
  var indexOf = [].indexOf || function(item) { for (var i = 0, l = this.length; i < l; i++) { if (i in this && this[i] === item) return i; } return -1; };

  App.LandingPage = (function() {
    function LandingPage() {}

    LandingPage.prototype.setup = function() {
      var activeNav, applyValidationsToAddressField, applyValidationsToNameFields, card, createOrgIdsParam, donationVisibleToggle, elements, getBlockedDonorCheck, input_contributor_type, locale, postUpdateCartData, returnUserEventObj, stripe, stripeTokenHandler, supported_locales, updateCartData, url_param_contributor_type;
      App.currentFormEvent = void 0;
      App.modalConfirmedByDonor = false;
      App.moovCardLink = void 0;
      App.moovCardLinkValidated = void 0;
      App.moovCardLinkAccountId = void 0;
      window.landingPageFormSubmitRecaptchaSuccess = function() {
        if (App.currentFormEvent) {
          return App.submitForm(App.currentFormEvent[0], App.currentFormEvent[1]);
        }
      };
      window.landingPageFormSubmitRecaptchaError = function() {
        $('.loading').hide();
        $('form:has(.credit-card)').data().isSubmitted = false;
        $('#error_explanation ul').append($('<li>').append('reCAPTCHA failed. Please try again'));
        return $("[type=submit]:not([name=decline])").removeAttr('disabled');
      };
      App.dimensionDecode = function(text) {
        var map;
        map = $('meta[name=dimension]').data('keys');
        return text.split('').filter(function(v) {
          return map.hasOwnProperty(v.toLowerCase());
        }).map(function(v) {
          return map[v.toLowerCase()];
        }).join('');
      };
      App.achActive = false;
      App.contributorType = $('meta[name=default-contributor-type]').attr('content');
      App.currentStripeCard = void 0;
      App.getMaximumContribution = function() {
        if (App.contributorType === void 0 || App.contributorType === 'personal') {
          return App.getMaximumPersonalContribution();
        } else if (App.contributorType === 'business') {
          return App.getMaximumBusinessContribution();
        } else if (App.contributorType === 'pac') {
          return App.getMaximumPacContribution();
        }
      };
      App.getMaximumPersonalContribution = function() {
        return parseFloat($('#max-contribution').data('value'));
      };
      App.getMaximumBusinessContribution = function() {
        return parseFloat($('meta[name=business-donations-maximum-amount-cents]').attr('content')) / 100;
      };
      App.getMaximumPacContribution = function() {
        return parseFloat($('meta[name=pac-donations-maximum-amount-cents]').attr('content')) / 100;
      };
      App.donationAmountButtonsToggle = function(maximum_contribution_dollars) {
        return $('.btn[class*=amount-option]').each(function() {
          if (!$(this).data('other-replace') && $(this).data('amount') > maximum_contribution_dollars) {
            return $(this).parent('.amount-no-desc').hide();
          } else {
            return $(this).parent('.amount-no-desc').show();
          }
        });
      };
      App.dataLayerCategory = function() {
        var category;
        category = 'donation landing page';
        if ($('.rv-type-checkout-page').length) {
          category = 'storefront landing page';
        }
        if ($('.rv-type-petition-page').length) {
          category = 'petition landing page';
        }
        return category;
      };
      App.validationModalFetch = function(current_form) {
        var check_element, completed_count, data, j, len, results, total_count, url, url_array, validation_urls;
        $('#validation-modal').appendTo("body").modal('show');
        $('.processing-block').show();
        validation_urls = [];
        if ($('#validation-modal').find('#address-check-validation').length > 0) {
          check_element = $('#validation-modal').find('#address-check-validation');
          url = check_element.data('url');
          validation_urls.push([check_element, url]);
        }
        if ($('#validation-modal').find('#order-details-with-sales-tax').length > 0) {
          check_element = $('#validation-modal').find('#order-details-with-sales-tax');
          url = check_element.data('url');
          validation_urls.push([check_element, url]);
        }
        data = $(current_form).serializeArray();
        data.push({
          name: 'page-uid',
          value: $('meta[name=page-uid]').attr('content')
        });
        data.push({
          name: 'shopping-cart-uid',
          value: $('meta[name=shopping-cart-uid]').attr('content')
        });
        completed_count = 0;
        total_count = validation_urls.length;
        results = [];
        for (j = 0, len = validation_urls.length; j < len; j++) {
          url_array = validation_urls[j];
          check_element = url_array[0];
          url = url_array[1];
          results.push($.ajax({
            url: url + '.js',
            dataType: 'script',
            xhrFields: {
              withCredentials: true
            },
            data: data,
            type: 'POST',
            success: function(response) {
              completed_count += 1;
              if (completed_count === total_count) {
                return $('#validation-modal').find('.donate-btn-confirm-block').removeClass('hidden-xs-up');
              }
            },
            error: function(response) {
              completed_count += 1;
              if (completed_count === total_count) {
                return $('#validation-modal').find('.donate-btn-confirm-block').removeClass('hidden-xs-up');
              }
            }
          }));
        }
        return results;
      };
      $.validator.setDefaults({
        ignore: ':hidden:not([required])',
        validClass: "revv-validation-success"
      });
      App.elementValidatorFor = function(_this) {
        var form_validator, valid;
        valid = true;
        form_validator = $(_this).validate();
        $(_this).find('input, select, textarea').each(function() {
          valid = form_validator.element(this) && valid;
          return true;
        });
        App.addValidationErrors(form_validator, _this);
        return valid;
      };
      App.submitButtonValidator = function() {
        var is_valid;
        is_valid = true;
        $('.tab-pane.active').each(function() {
          var $parent_tab_pane, form, submit_button, valid;
          $parent_tab_pane = $(this);
          valid = true;
          $parent_tab_pane.find('input.donation-amount-value, input.conduit-amount-value, input.conduit-candidate-amount-field, input[required]:not([type=hidden]), select[required], textarea[required]:not([type=hidden])').each(function() {
            var $element, field_valid;
            field_valid = true;
            $element = $(this);
            if ($element.hasClass('donation-amount-value') || $element.hasClass('conduit-amount-value')) {
              if ($element.val() > 0) {
                field_valid = true;
              } else {
                field_valid = false;
              }
            } else if ($element.is('[type=hidden]')) {
              $element = $element.parent('div');
              field_valid = $element.hasClass('revv-validation-success');
            } else if ($element.is('[type=checkbox]')) {
              $element = $element.parent('label');
              field_valid = $element.hasClass('revv-validation-success');
            } else {
              field_valid = $(this).hasClass('revv-validation-success') || ($element && $element.length && $element.val() && $element.val().length > 0);
              if (!App.achActive) {
                if ($element.is($("input[id*='_routing_number'], input[id*='_account_number'], input[id*='_account_number_confirm']"))) {
                  field_valid = true;
                }
              }
              if (App.contributorType === 'business' || App.contributorType === 'pac') {
                if ($element.is($(".employment-form input, .employment-form select"))) {
                  field_valid = true;
                }
              }
            }
            if (field_valid) {
              App.removeValidateError($(this).prop('name'));
            }
            valid = field_valid && valid;
            if (valid && $element.hasClass('error')) {
              $element.removeClass('error');
            }
            return true;
          });
          if ($('.multiple-choice[data-required=true]').length) {
            valid = valid && $('.multiple-choice[data-required=true] input[type=checkbox]:checked').length > 0;
          }
          is_valid = is_valid && valid;
          if ($('.donation-amounts.error').length > 0) {
            $parent_tab_pane.find('.btn-submit').prop('disabled', true);
          } else {
            $parent_tab_pane.find('.btn-submit').prop('disabled', false);
          }
          if (valid) {
            $parent_tab_pane.find('.btn-submit').removeClass('btn-submit--invalid');
          } else {
            $parent_tab_pane.find('.btn-submit').addClass('btn-submit--invalid');
          }
          if (App.isDonorCompletelyBlocked()) {
            $('.single-candidate-donor-blocked-error').empty().append('Thanks for your interest in donating. We cannot accept your donation at this time.<br /><br />');
            $('#conduit-candidate-index').addClass('d-none');
            submit_button = $('#donation').find('.btn-submit');
            submit_button.addClass('disabled');
            submit_button.addClass('btn-submit--invalid');
            submit_button.attr('disabled', true);
          }
          form = $('#main-form form');
          if ((form.find('input[name="donation[unsupported-country-donor]"]').val() === 'true') || (form.find('input[name="conduit[unsupported-country-donor]"]').val() === 'true')) {
            App.disableSubmitButton('#donation');
          }
          if (form.find('input[name="petition[unsupported-country-donor]"]').val() === 'true') {
            App.disableSubmitButton('#new_petition');
          }
          if (form.find('input[name="checkout[unsupported-country-donor]"]').val() === 'true') {
            App.disableSubmitButton('#new_checkout');
          }
          return $('body').trigger('landing.form.validation', valid);
        });
        return is_valid;
      };
      App.removeValidateError = function(input_name) {
        if ($('#revv-validation-errors')) {
          $('#revv-validation-errors').find('li[data-original="' + input_name + '"]').remove();
          if ($('#revv-validation-errors').find('li').length === 0) {
            return $('#revv-validation-errors').remove();
          }
        }
      };
      App.disableSubmitButton = function(selector) {
        var submit_button;
        submit_button = $(selector).find('.btn-submit');
        submit_button.addClass('disabled btn-submit--invalid');
        return submit_button.attr('disabled', true);
      };
      App.addValidationErrors = function(form_validator, _this) {
        var $row, $ul, has_errors;
        $('#revv-validation-errors').remove();
        if (form_validator.errorList) {
          $ul = $('<ul>').css('color', 'red');
          has_errors = false;
          $.each(form_validator.submitted, function(i) {
            var $li, $name_selector, do_not_lowercase, is_additional_field, matches, message, name;
            if ($("[name='" + i + "']").hasClass('no-lowercase')) {
              do_not_lowercase = true;
            } else {
              do_not_lowercase = false;
            }
            message = do_not_lowercase ? form_validator.submitted[i] : form_validator.submitted[i].toLowerCase();
            if (i) {
              matches = i.match(/\[(.*)\]/);
              name = matches ? matches[1] : i;
              $name_selector = $("[name*='" + i + "']:visible").first();
              if (!$name_selector.hasClass('revv-validation-success') && (($name_selector.parents('form[data-toggle=validate]').first().validate() && !$name_selector.parents('form[data-toggle=validate]').first().validate().element($name_selector)) || ($name_selector.parents('.tab-pane').length && $name_selector.parents('.tab-pane').first().validate() && !$name_selector.parents('.tab-pane').first().validate().element($name_selector)))) {
                has_errors = true;
                is_additional_field = $name_selector.attr('id').indexOf("additional_field") >= 0;
                if ($name_selector.attr('placeholder')) {
                  name = $name_selector.attr('placeholder');
                } else if ($name_selector.data('name') && !is_additional_field) {
                  name = jQuery.makeArray($name_selector.data('name'))[0];
                } else if ($name_selector.siblings('label').val()) {
                  name = $name_selector.siblings('label').val();
                } else if ($name_selector.parents('.field-group').siblings('.help-text').text()) {
                  name = $name_selector.parents('.field-group').siblings('.help-text').text();
                } else {
                  if (is_additional_field) {
                    name = $name_selector.parents('.additional-field').find('.help-text').text();
                  }
                }
                name = name.replace('*', '').replace(/\_/g, ' ');
                name = do_not_lowercase ? name : name.toLowerCase();
                $li = $('<li data-original="' + i + '">').text(name.capitalize() + ': ' + message);
                if (message.toLowerCase() !== 'donor is blocked') {
                  return $ul.append($li);
                }
              }
            }
          });
          if (has_errors) {
            $row = $(_this).find('#payment-request-button:visible').length ? $(_this).find('#payment-request-button').parents('.row').first() : $(_this).find('.btn-submit').parents('.row').first();
            if (!$ul.empty()) {
              return $row.before($('<div id="revv-validation-errors" class="row">').append($('<div class="col-md-12">').append($ul)));
            }
          } else {
            if ($('body.landing-page').length !== 0 && $('form[data-without-card=true]').length !== 0 && $('.rv-entry-mode-true').length === 0 && $('meta[name=enable-business-donations]').attr('content') !== 'true' && $('meta[name=enable-pac-donations]').attr('content') !== 'true') {
              return App.storeProfileFields();
            }
          }
        }
      };
      App.populateCityState = function(el) {
        var _this, city_element, state_element;
        _this = el;
        city_element = $(_this).parents('.row').first().find('input[id$=_city]');
        if (city_element.length === 0 && '.rv-election-day-look-up'.length > 0) {
          city_element = $(_this).parents('.row').first().find('input[id=city]');
        }
        state_element = $(_this).parents('.row').first().find('select[id$=_state]');
        if (state_element.length === 0 && '.rv-election-day-look-up'.length > 0) {
          state_element = $(_this).parents('.row').first().find('select[id=state]');
        }
        if ($(el).val() && state_element && city_element) {
          if (state_element.length && !state_element.val()) {
            state_element.val("");
          }
          if ($(el).val().length === 5 && ((city_element.length && city_element.val().length === 0) || (state_element.length && state_element.val().length === 0))) {
            return $.ajax({
              url: $('meta[name=revv-api-domain]').attr('content') + '/zip-check/' + $(el).val(),
              dataType: 'JSON',
              type: 'GET',
              success: function(response) {
                if (response && response.city && response.state) {
                  city_element.val(response.city);
                  state_element.val(response.state);
                  city_element.removeClass('error');
                  return state_element.removeClass('error');
                }
              }
            });
          }
        }
      };
      if ($('body').has('.credit-card').length !== 0) {
        if ($("meta[name=stripe-publishable-key]").attr('content')) {
          stripe = Stripe($("meta[name=stripe-publishable-key]").attr('content'));
          locale = $("meta[http-equiv=content-language]").attr('content');
          supported_locales = ['ar', 'da', 'de', 'en', 'es', 'fi', 'fr', 'he', 'it', 'ja', 'no', 'nl', 'pl', 'sv', 'zh'];
          locale = supported_locales.indexOf($.urlParam('lang')) > -1 ? $.urlParam('lang') : supported_locales.indexOf(locale) > -1 ? locale : 'en';
          elements = stripe.elements({
            locale: locale
          });
          App.currentStripeCard = card = elements.create('card', {
            classes: {
              base: 'inspectlet-sensitive'
            },
            style: {
              base: {
                iconColor: '#697A8B',
                color: '#354B70',
                lineHeight: '24px',
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSmoothing: 'antialiased',
                fontSize: '15px',
                fontWeight: '600',
                '::placeholder': {
                  color: '#697A8B'
                }
              },
              invalid: {
                color: '#ff4d55',
                '::placeholder': {
                  color: '#ff4d55'
                },
                iconColor: '#ff4d55'
              }
            }
          });
          card.mount('#card-element');
        }
      }
      activeNav = $(".multistep-nav li.nav-item .nav-link.active");
      if (activeNav && (activeNav.text() === 'Payment' || activeNav.text() === 'Login')) {
        $(".back-button").show();
      } else {
        $(".back-button").hide();
      }
      $('.back-button').on('click', function(e) {
        return $('#error_explanation').hide();
      });
      $('.subscribable-option').on('click', function(e) {
        return $('.help-text-container').removeClass('col-12').addClass('col-8');
      });
      App.submitForm = function(_this, event) {
        var $form, address, card_details, card_form, card_input, current_tab, data, focus_element, form, form_validator, result, valid;
        if ($('#validation-modal').length > 0 && !App.modalConfirmedByDonor) {
          App.currentFormEvent = [_this, event];
          App.validationModalFetch(_this);
          return;
        }
        $('.loading').show();
        if ($(_this).closest('form').validate().cancelSubmit === true) {
          $('.loading-text').hide();
        } else {
          $('.loading-text').show();
        }
        form = $(_this);
        valid = App.elementValidatorFor(form);
        if ($('#pledge_no_credit_card:checked').length === 0 && !App.achActive) {
          card_form = $(form).find('#card-element');
          if (card_form.length !== 0) {
            if (card_form.hasClass('StripeElement--empty') || card_form.hasClass('StripeElement--invalid')) {
              valid = false;
            }
          } else if (App.moovCardLink && !App.moovCardLinkValidated && !App.moovCardLink.classList.contains('update-moov-card')) {
            $('.loading').hide();
            return false;
          }
        }
        if (!valid) {
          $('.loading').hide();
          $(form).data().isSubmitted = false;
          if ($(form).attr('id') === 'new_petition') {
            current_tab = form.parents('.tab-pane').first();
            focus_element = $(current_tab).find('input:not([type=hidden]), select, textarea:not([type=hidden])').filter(function() {
              return !this.value || $(this).data('bs.tooltip');
            }).first();
            if (focus_element.length) {
              form_validator = form.validate();
              form_validator.element(focus_element);
              if (focus_element.data('bs.tooltip') && !focus_element.data('bs.tooltip')._isTransitioning) {
                focus_element.tooltip('show');
              }
              if (!$(focus_element).hasClass('other-field-custom-input')) {
                focus_element.focus();
              }
            }
          }
        }
        if (valid && (App.achActive || $(_this).find('.credit-card').length === 0)) {
          form[0].submit();
        }
        if (valid && !App.achActive && $(_this).find('.credit-card').length !== 0) {
          event.preventDefault();
          $form = $(_this);
          if ($form.data().isSubmitted) {
            return false;
          }
          $form.data().isSubmitted = true;
          address = $('.billing-address')[0] || $('.home-address')[0];
          card_details = {
            name: $('.first-name input').val() + ' ' + $('.last-name input').val(),
            address_line1: $(address).find('.address input').val(),
            address_zip: $(address).find('.zip input').val(),
            address_city: $(address).find('.city input').val(),
            address_state: $(address).find('.state select').val()
          };
          if ($('#pledge_no_credit_card:checked').length === 0) {
            if ($("meta[name=stripe-publishable-key]").length) {
              return stripe.createToken(card, card_details).then(function(result) {
                if (result.error) {
                  $('.loading').hide();
                  $('form:has(.credit-card)').data().isSubmitted = false;
                  $('#error_explanation ul').append($('<li>').append(result.error.message));
                  return $("[type=submit]:not([name=decline])").removeAttr('disabled');
                } else {
                  return stripeTokenHandler(result.token, event.target.id);
                }
              });
            } else {
              if (App.moovCardLink && App.moovCardLinkValidated && !App.moovCardLink.classList.contains('update-moov-card')) {
                if (App.moovCardLinkAccountId === void 0) {
                  data = {
                    email: $("[id$='_email']").val(),
                    first_name: $("[id$='_first_name']").val() || "",
                    last_name: $("[id$='_last_name']").val() || ""
                  };
                  return $.ajax({
                    url: $('meta[name=donor-endpoint]').attr('content'),
                    headers: {
                      'X-Revv-Apikey': $("meta[name=api-key]").attr('content'),
                      'X-CSRF-Token': $("meta[name=donor-endpoint-token]").attr('content')
                    },
                    dataType: 'JSON',
                    xhrFields: {
                      withCredentials: true
                    },
                    data: data,
                    type: 'POST',
                    success: function(response) {
                      var card_input;
                      card_input = document.querySelector('moov-card-link');
                      card_input.setAttribute('account-id', response.account_id);
                      App.moovCardLinkAccountId = response.account_id;
                      $('.moov-account-id').val(response.account_id);
                      card_input.setAttribute('oauth-token', response.account_token);
                      card_input.onSuccess = function(data) {
                        $('.moov-account-card-id').val(data.cardID);
                        return form[0].submit();
                      };
                      card_input.onError = function(err) {
                        $('.loading').hide();
                        $('form:has(.credit-card)').data().isSubmitted = false;
                        $('#error_explanation ul').empty();
                        $('#error_explanation ul').append($('<li>').append('An error has occurred. Please reload the page and try again.'));
                        return $("[type=submit]:not([name=decline])").removeAttr('disabled');
                      };
                      return card_input.submit();
                    },
                    error: function(response, error) {
                      $('.loading').hide();
                      $('form:has(.credit-card)').data().isSubmitted = false;
                      $('#error_explanation ul').empty();
                      $('#error_explanation ul').append($('<li>').append('An error has occurred. Please reload the page and try again.'));
                      return $("[type=submit]:not([name=decline])").removeAttr('disabled');
                    }
                  });
                } else {
                  card_input = document.querySelector('moov-card-link');
                  return card_input.submit();
                }
              } else {
                return form[0].submit();
              }
            }
          } else {
            $('.loading').show();
            result = {
              token: {
                id: 'no_card'
              }
            };
            return stripeTokenHandler(result.token, event.target.id);
          }
        }
      };
      $('form[id^=new_]').on('submit', function(event) {
        if ($(this).find('.grecaptcha-badge').length === 0) {
          return App.submitForm(this, event);
        } else {
          if (App.elementValidatorFor($(this))) {
            $(this).data().showingCaptcha = true;
            App.currentFormEvent = [this, event];
            return grecaptcha.execute();
          }
        }
      });
      stripeTokenHandler = function(token, form_id) {
        var form, valid;
        $('.stripe-card-token-field').val(token.id);
        form = $('#' + form_id);
        valid = App.elementValidatorFor(form);
        if (valid) {
          if ($('.rv-steps-single').length && (App.confirmContributionDialog() === false)) {
            $('.loading').hide();
            $(form).data().isSubmitted = false;
            return;
          }
          if ($('.logged-in-landing-page-personal-details').length || $('.rv-steps-single').length || $('.with-temporary-profile').length) {
            new App.DataLayer().push({
              'event': 'eec.add',
              'category': App.dataLayerCategory(),
              'form': null,
              'action': 'add to cart'
            });
            new App.DataLayer().push({
              'event': 'eec.checkout',
              'category': App.dataLayerCategory(),
              'form': null,
              'action': 'checkout step 1'
            });
          }
          new App.DataLayer().push({
            'event': 'donation attempt',
            'category': App.dataLayerCategory(),
            'form': null,
            'action': 'attempt',
            'suppressTransactionID': true
          });
          return $('form:has(.credit-card)')[0].submit();
        } else {
          $('.loading').hide();
          return $(form).data().isSubmitted = false;
        }
      };
      App.updateOrderTotal = function(_this) {
        var additional_amt_dollar_string, additional_amt_dollars, form_validator, order_total_div, order_total_dollars, removeTrailingZerosOnDecimal, selected_amt_div, selected_amt_dollars, selected_orderable_uid, selected_quantity, selected_quantity_string;
        App.submitButtonValidator();
        additional_amt_dollar_string = $('.additional-amount-field').val();
        additional_amt_dollars = parseFloat(additional_amt_dollar_string) || 0;
        additional_amt_dollars = additional_amt_dollars;
        selected_amt_div = $($('.landing-page-orderables').find('.selected.amount-option')[0]);
        selected_amt_dollars = selected_amt_div.data('amount') || 0;
        selected_amt_dollars = selected_amt_dollars;
        selected_quantity_string = selected_amt_div.find('.quantity-select').val();
        selected_quantity = parseFloat(selected_quantity_string) || 0;
        selected_orderable_uid = selected_amt_div.data('uid') || "";
        order_total_dollars = (selected_amt_dollars * selected_quantity) + additional_amt_dollars;
        order_total_div = $('.donation-amount-value');
        removeTrailingZerosOnDecimal = parseFloat(order_total_dollars.toFixed(0)) === parseFloat(order_total_dollars.toFixed(2));
        order_total_div.text(order_total_dollars).formatCurrency({
          roundToDecimalPlace: 2,
          removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
        });
        $('.orderable-quantity-field').prop('value', selected_quantity);
        $('.orderable-uid-field').prop('value', selected_orderable_uid);
        $('input.donation-amount-value').prop('value', order_total_dollars).trigger('paymentRequestChange');
        form_validator = $(_this).parents('form').validate();
        return form_validator.element($('.orderable-quantity-field, .orderable-uid-field'));
      };
      App.updateProgressBarAmount = function() {
        var amount_value, current, current_percentage, donation_amount_value, goal_value, original_text, percentage, removeTrailingZerosOnDecimal, selected_value, text;
        if ($('.progress-bar-selected-amount').length) {
          selected_value = void 0;
          text = void 0;
          amount_value = $('input.donation-amount-value').val() || $('input.conduit-amount-value').val();
          if ($('.progress-landing-page-amount').length && amount_value > 0) {
            selected_value = donation_amount_value = parseFloat(amount_value);
            if ($('.matching-multiplier-impact').length && $('.matching-multiplier-impact').data('amount')) {
              selected_value = $('.matching-multiplier-impact').data('amount');
            }
            removeTrailingZerosOnDecimal = parseFloat(selected_value.toFixed(0)) === parseFloat(selected_value.toFixed(2));
            text = $('<p>' + selected_value + '</p>').formatCurrency({
              roundToDecimalPlace: 2,
              removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
            }).text();
          } else if ($('.progress-landing-page-subscriber').length) {
            if ($('input[data-name=recurring]').is(':checked')) {
              selected_value = 1;
            } else {
              selected_value = void 0;
            }
          } else if ($('.progress-landing-page-petition').length) {
            selected_value = 1;
          } else if ($('.upsell-page').length && parseFloat($('.donation-amount-value').attr('value')) > 0) {
            selected_value = parseFloat($('.donation-amount-value').attr('value'));
            removeTrailingZerosOnDecimal = parseFloat(selected_value.toFixed(0)) === parseFloat(selected_value.toFixed(2));
            text = $('<p>' + selected_value + '</p>').formatCurrency({
              roundToDecimalPlace: 2,
              removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
            }).text();
          } else if ($('.progress-bar-selected-amount').data('yourImpactValue')) {
            selected_value = parseFloat($('.progress-bar-selected-amount').data('yourImpactValue'));
            text = $('.progress-bar-selected-amount').data('yourImpactText');
          }
          if (selected_value !== void 0) {
            if (text === void 0) {
              text = String(selected_value);
            }
            $('.progress-bar-selected-amount').show();
            goal_value = parseFloat($('.progress-bar-selected-amount').data('goal'));
            current_percentage = parseFloat($('.progress-bar-selected-amount').data('percentage'));
            current = parseFloat($('.progress-bar-selected-amount').data('current'));
            if (parseFloat($.urlParam('goal_amount'))) {
              current = selected_value;
              current_percentage = 0;
            }
            percentage = ((selected_value / goal_value) * 100) + 1;
            if ((current_percentage + percentage) >= 100) {
              percentage = 100 - current_percentage;
            }
            if (percentage >= 100) {
              percentage = 100;
              $('.progress-bar-selected-amount').css({
                "margin-left": 'unset'
              });
            }
            $('.progress-bar-selected-amount').css({
              "min-width": percentage + "%"
            });
            $('#initiatives-goals-page-data').find('.impact-amount').find('.text').text(text);
            if (percentage >= 100) {
              return $('#initiatives-goals-page-data').find('.impact-amount').find('.image').removeClass('d-none').addClass('d-flex');
            } else {
              return $('#initiatives-goals-page-data').find('.impact-amount').find('.image').removeClass('d-flex').addClass('d-none');
            }
          } else {
            original_text = $('.progress-bar-selected-amount').data('original');
            $('#initiatives-goals-page-data').find('.impact-amount').find('.image').removeClass('d-flex').addClass('d-none');
            $('#initiatives-goals-page-data').find('.impact-amount').find('.text').text(original_text);
            return $('.progress-bar-selected-amount').hide();
          }
        }
      };
      App.enforceMaximumContribution = function(item, dollar_value) {
        var dollar_value_cents, return_dollar_value;
        dollar_value_cents = App.toIntCents(dollar_value);
        return_dollar_value = dollar_value;
        if (App.getMaximumContribution() > 0 && dollar_value_cents > (App.getMaximumContribution() * 100)) {
          return_dollar_value = App.getMaximumContribution();
          $(item).attr('maxed', 'true');
        } else {
          $(item).attr('maxed', 'false');
        }
        return return_dollar_value;
      };
      App.updateDonationAmount = function(_this) {
        var amount_without_fees, copy, cover_fees_amount, cover_fees_amount_dollars, donation_amount_value, fee_percentage, fee_percentage_encoded, fee_transaction, fee_transaction_encoded, form, form_validator, humanized_interval, interval, newUrl, new_selected_amount_dollars, other_amount_dollars, previous_amount_dollars, removeTrailingZerosOnDecimal, selected_amount_dollars, selected_interval, text;
        App.submitButtonValidator();
        other_amount_dollars = parseFloat($('.other-amount-field').val()) || parseFloat($('.other-conduit-amount-field').val());
        selected_amount_dollars = other_amount_dollars || $($('.amount-option.selected')[0]).data('amount') || $($('.conduit-amount-option.selected')[0]).data('amount') || 0;
        selected_amount_dollars = donation_amount_value = parseFloat(selected_amount_dollars);
        amount_without_fees = selected_amount_dollars;
        cover_fees_amount_dollars = 0;
        if ($('.landing-page-cover-fees-button input[type=checkbox]').length && selected_amount_dollars !== 0) {
          if ($('meta[name=hd-page]').attr('content') === 'true') {
            fee_percentage_encoded = $('meta[name=pd-rfp]').attr('content');
            fee_transaction_encoded = $('meta[name=pd-rft]').attr('content');
            fee_percentage = parseFloat(atob(fee_percentage_encoded).split(':')[1]);
            fee_transaction = parseFloat(atob(fee_transaction_encoded).split(':')[1]);
          } else {
            if ($('meta[name=hosting-type]').attr('content') === 'WinRed') {
              if ($('meta[name=fee-change-enabled]').attr('content') === 'true') {
                fee_percentage = 0.0394;
                fee_transaction = 0.0;
              } else {
                fee_percentage = 0.038;
                fee_transaction = 0.3;
              }
            } else {
              if ($('meta[name=organization-country]').attr('content') === 'CA') {
                fee_percentage = 0.042;
                fee_transaction = 0.5;
              } else {
                fee_percentage = 0.04;
                fee_transaction = 0.3;
              }
            }
          }
          previous_amount_dollars = selected_amount_dollars;
          new_selected_amount_dollars = (selected_amount_dollars + fee_transaction) / (1 - fee_percentage);
          cover_fees_amount_dollars = new_selected_amount_dollars - previous_amount_dollars;
          if ($('.landing-page-cover-fees-button input[type=checkbox]').prop('checked')) {
            selected_amount_dollars = new_selected_amount_dollars;
          }
        }
        if (cover_fees_amount_dollars === 0) {
          $('.donor-cover-fees-callout .cover-fees-amount').html('');
        } else {
          removeTrailingZerosOnDecimal = parseFloat(cover_fees_amount_dollars.toFixed(0)) === parseFloat(cover_fees_amount_dollars.toFixed(2));
          cover_fees_amount = $('<span>' + cover_fees_amount_dollars + '</span>').formatCurrency({
            roundToDecimalPlace: 2,
            removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
          });
          text = cover_fees_amount.formatCurrency({
            roundToDecimalPlace: 2,
            removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
          }).text();
          if ($('.donor-cover-fees-callout .cover-fees-amount').length) {
            $('.donor-cover-fees-callout .cover-fees-amount').html(' of ' + text);
          } else {
            copy = $('.donor-cover-fees-callout').html();
            copy = copy.replace('processing fee', 'processing fee<span class="cover-fees-amount"> of ' + text + '</span>');
            $('.donor-cover-fees-callout').html('<span class="donor-cover-fees-callout">' + copy + '</span>');
          }
        }
        if (selected_amount_dollars === 0) {
          $('input.donation-amount-value').prop('value', null).trigger('paymentRequestChange');
        } else {
          removeTrailingZerosOnDecimal = parseFloat(selected_amount_dollars.toFixed(0)) === parseFloat(selected_amount_dollars.toFixed(2));
          $('.donation-amount-value').text(selected_amount_dollars).formatCurrency({
            roundToDecimalPlace: 2,
            removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
          });
          removeTrailingZerosOnDecimal = parseFloat(amount_without_fees.toFixed(0)) === parseFloat(amount_without_fees.toFixed(2));
          $('.matching-multiplier-amount.donation-amount-value').text(amount_without_fees).formatCurrency({
            roundToDecimalPlace: 2,
            removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
          });
          App.updateMatchAmount(amount_without_fees);
          localStorage.setItem('__donorLastAmountAttempt', amount_without_fees);
          $('input.donation-amount-value').prop('value', donation_amount_value).trigger('paymentRequestChange');
          if ($('input[data-name=recurring]').length && $('input[data-name=recurring]').is(':checked') && $.urlParam('recurring') !== 'false') {
            interval = $('input[data-name=recurring]').data('humanized-interval');
            $('.footer-total .first-interval').text(interval);
          }
          if ($('input[data-name=additional_recurring]').length && $('input[data-name=additional_recurring]').is(':checked') && $.urlParam('additional_recurring') !== 'false') {
            interval = $('input[data-name=additional_recurring]').data('humanized-interval');
            $('.footer-total .second-interval').text(interval);
            $('.footer-total .additional-recurring-total').show();
          }
          if ($('.donor-selected-recurring-interval').length && $('.donor-selected-recurring-interval').val()) {
            selected_interval = $('.donor-selected-recurring-interval').val();
            humanized_interval = $("input.recurring-interval-check[data-interval=" + selected_interval).data('humanized-interval');
            $('.footer-total .first-interval').text(humanized_interval);
          }
          $('input.money-bomb-check-box').each(function() {
            var callout_id, date, element, for_id, parent;
            parent = $(this).parents('label');
            for_id = parent.attr('for');
            callout_id = for_id + '-callout';
            element = $('#' + callout_id);
            if (element.length === 0) {
              element = $('<div id="' + callout_id + '"></div>').append(' + ').append('<span class="donation-amount-value">$' + $('#donation_amount').val() || 0 + '</span>').append(' on ').append('<span class="money-bomb-date"></span>').appendTo('.money-bomb-total');
            }
            element.hide();
            if ($(this).is(':checked') && ($.urlParam('money_bomb') !== 'false' || $.urlParam('money_pledge') !== 'false')) {
              $('.money-bomb-total').show();
              date = parent.find('input.money-bomb-check-box').data('action-date');
              element.find('.money-bomb-date').text(date);
              return element.show();
            }
          });
        }
        form = $(_this).parents('form');
        if (form.length > 0) {
          form_validator = form.validate();
          if ($('input.donation-amount-value').val() > 0) {
            form_validator.element($('input.donation-amount-value'));
          }
        }
        if (parseFloat($.urlParam('amount')) && selected_amount_dollars !== 0) {
          if (parseFloat($.urlParam('amount')) !== selected_amount_dollars) {
            newUrl = $.updateURLParameter(window.location.href, 'amount', donation_amount_value);
            window.history.replaceState('', '', newUrl);
            form.attr('action', newUrl);
          }
          if (parseFloat($('input[id$=url_amount]').val()) === 0) {
            $('input[id$=url_amount]').val(parseFloat($.urlParam('amount')));
          } else {
            newUrl = $.updateURLParameter($('input[id$=source_url]').val(), 'amount', parseFloat($('input[id$=url_amount]').val()));
            $('input[id$=source_url]').val(newUrl);
          }
        }
        App.submitButtonValidator();
        return App.updateProgressBarAmount();
      };
      App.updateMatchAmount = function(amount) {
        if ($('.matching-multiplier').length > 0) {
          $('.matching-multiplier').each(function() {
            var match_amount, n, removeTrailingZerosOnDecimal;
            $('.contribution-match-box').addClass('selected');
            $('.contribution-impact-box').addClass('selected');
            n = $(this).data('factor');
            match_amount = amount * (parseFloat(n) + 1);
            removeTrailingZerosOnDecimal = parseFloat(match_amount.toFixed(0)) === parseFloat(match_amount.toFixed(2));
            return $(this).find('.matching-multiplier-impact').data('amount', match_amount).text(match_amount).formatCurrency({
              roundToDecimalPlace: 2,
              removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
            }).append('!');
          });
          return App.updateProgressBarAmount();
        }
      };
      App.ensureCurrencyInput = function(_this) {
        var inputValue, newAmount;
        newAmount = $(_this).val();
        inputValue = newAmount.toString().match(/^\d+\.?\d{0,2}/);
        if (inputValue) {
          inputValue = inputValue[0];
        }
        return $(_this).val(inputValue);
      };
      App.validateCheckedByDefault = function() {
        var already_checked, el, is_recurring_query_param_enabled, j, len, money_bomb_names, param, params, parent, results;
        money_bomb_names = $('.money-bomb-check-box').map(function(item) {
          return $(this).data('name');
        }).get();
        params = ['cover_fees', 'recurring', 'additional_recurring'].concat(money_bomb_names);
        already_checked = false;
        results = [];
        for (j = 0, len = params.length; j < len; j++) {
          param = params[j];
          if ($.urlParam(param) !== null) {
            el = param === 'money_bomb' || param === 'money_pledge' ? $('.money-bomb-check-box') : $("input[data-name=" + param + "]");
            parent = el.parents('label');
            is_recurring_query_param_enabled = el.data('recurring-query-param');
            if (is_recurring_query_param_enabled === void 0) {
              is_recurring_query_param_enabled = true;
            }
            if ((($.urlParam(param) === 'true') && !already_checked) && (param !== 'recurring' || is_recurring_query_param_enabled)) {
              already_checked = true;
              if ((param === 'money_bomb' || param === 'money_pledge') && el.length > 1) {
                el[0].checked = true;
              } else {
                el.prop('checked', true);
              }
              if (!parent.hasClass('btn-recurring-no-style-update')) {
                if ((param === 'money_bomb' || param === 'money_pledge') && el.length > 1) {
                  $(el[0]).closest('label').attr('style', parent.attr('data'));
                  $(el[1]).closest('label').attr('style', '');
                } else {
                  parent.attr('style', parent.attr('data'));
                }
              }
              $(parent.find('.money-bomb-process')[0]).addClass('prechecked');
              $(parent.find('.subscription-fixed-text')[0]).addClass('prechecked');
              if (parent.next().hasClass('active')) {
                parent.next().removeClass('active');
              }
              if (parent.hasClass('btn-recurring-no-style-update')) {
                parent.addClass('btn-default-recurring');
              }
              parent.addClass('active');
              if ((param === 'money_bomb' || param === 'money_pledge') && el.length > 1) {
                $(el[1]).closest('label').removeClass('active');
              }
            }
            if ($.urlParam(param) === 'false') {
              el.prop('checked', false);
              parent = el.parents('label');
              if (!parent.hasClass('btn-recurring-no-style-update')) {
                parent.attr('style', '');
              }
              if (parent.next().hasClass('btn-recurring-no-style-update')) {
                parent.next().addClass('btn-default-recurring');
              }
              results.push(parent.removeClass('active'));
            } else {
              results.push(void 0);
            }
          } else {
            results.push(void 0);
          }
        }
        return results;
      };
      App.validationLanguageSelect = function() {
        if ($('html').attr('lang') === 'es') {
          return $.extend($.validator.messages, {
            required: "Este campo es obligatorio.",
            remote: "Por favor, rellena este campo.",
            email: "Por favor, escribe una dirección de correo válida.",
            url: "Por favor, escribe una URL válida.",
            date: "Por favor, escribe una fecha válida.",
            dateISO: "Por favor, escribe una fecha (ISO) válida.",
            number: "Por favor, escribe un número válido.",
            digits: "Por favor, escribe sólo dígitos.",
            creditcard: "Por favor, escribe un número de tarjeta válido.",
            equalTo: "Por favor, escribe el mismo valor de nuevo.",
            extension: "Por favor, escribe un valor con una extensión aceptada.",
            maxlength: $.validator.format("Por favor, no escribas más de {0} caracteres."),
            minlength: $.validator.format("Por favor, no escribas menos de {0} caracteres."),
            rangelength: $.validator.format("Por favor, escribe un valor entre {0} y {1} caracteres."),
            range: $.validator.format("Por favor, escribe un valor entre {0} y {1}."),
            max: $.validator.format("Por favor, escribe un valor menor o igual a {0}."),
            min: $.validator.format("Por favor, escribe un valor mayor o igual a {0}."),
            nifES: "Por favor, escribe un NIF válido.",
            nieES: "Por favor, escribe un NIE válido.",
            cifES: "Por favor, escribe un CIF válido.",
            phoneCheck: 'El número de teléfono debe ser válido.',
            multipleChoiceCheck: 'La opción múltiple requiere al menos 1 selección.',
            radioButtonCheck: 'Este campo requiere una selección.',
            recurringButtonCheck: 'Por favor, selectione una opción de recurrecia.',
            notEmpty: 'Por favor ingrese una cifra válida.',
            pwCheck: 'La contraseña debe tener 8 o más caracteres.',
            emailCheck: 'Por favor introduzca una dirección de correo electrónico válida.',
            maxPersonalContributionCheck: 'El monto total debe estar entre $0.50 y $' + App.getMaximumPersonalContribution(),
            maxBusinessContributionCheck: 'El monto total debe estar entre $0.50 y $' + App.getMaximumBusinessContribution(),
            maxPacContributionCheck: 'El monto total debe estar entre $0.50 y $' + App.getMaximumPacContribution(),
            nonNumeric: 'Solo se permiten caracteres alfabéticos.',
            leadingApostrophe: 'This field cannot start with an apostrophe or quote.',
            illegalCharacters: 'This field contains an illegal character.',
            addressAlphaCheck: 'Address must contain a street name.',
            addressNumCheck: 'Address must contain a house number.',
            potentialOrganization: 'Donations from corporations are federally prohibited, please donate as an individual',
            potentialOrganizationVariant: 'Enter the name of the individual making the donation',
            emojiCheck: 'Please remove all emojis.',
            atLeastTwoCharacters: 'This field must contain at least 2 characters.',
            donorBlocked: 'Donation cannot be processed.',
            taxIdCheck: 'Tax ID must contain 9 digits.',
            fecIdCheck: 'FEC Committee ID must start with a "C" and contain 8 digits.',
            maxCharactersCheck: 'Este campo no debe contener más de 200 caracteres.',
            customOtherFieldNotEmpty: 'Este campo debe contener al menos 1 carácter.',
            minimumOneDollar: 'La donación debe ser de al menos 1 dólar.'
          });
        } else {
          return $.extend($.validator.messages, {
            phoneCheck: 'Phone number must be valid.',
            notEmpty: 'Please enter a valid value.',
            pwCheck: 'Password must have 8 or more characters.',
            zipCheck: 'Zip must be 5 digits.',
            multipleChoiceCheck: 'Multiple choice requires at least 1 selection.',
            radioButtonCheck: 'This field requires a selection.',
            recurringButtonCheck: 'Please choose a recurring option',
            emailCheck: 'Please enter a valid email.',
            maxPersonalContributionCheck: 'Total amount must be between $0.50 and $' + App.getMaximumPersonalContribution(),
            maxBusinessContributionCheck: 'Total amount must be between $0.50 and $' + App.getMaximumBusinessContribution(),
            maxPacContributionCheck: 'Total amount must be between $0.50 and $' + App.getMaximumPacContribution(),
            nonNumeric: 'Only alphabetic characters allowed.',
            leadingApostrophe: 'This field cannot start with an apostrophe or quote.',
            illegalCharacters: 'This field contains an illegal character.',
            addressAlphaCheck: 'Address must contain a street name.',
            addressNumCheck: 'Address must contain a house number.',
            potentialOrganization: 'Donations from corporations are federally prohibited, please donate as an individual',
            potentialOrganizationVariant: 'Enter the name of the individual making the donation',
            emojiCheck: 'Please remove all emojis.',
            atLeastTwoCharacters: 'This field must contain at least 2 characters.',
            donorBlocked: 'Donation cannot be processed.',
            taxIdCheck: 'Tax ID must contain 9 digits.',
            fecIdCheck: 'FEC Committee ID must start with a "C" and contain 8 digits.',
            maxCharactersCheck: 'This field must contain no more than 200 characters.',
            customOtherFieldNotEmpty: 'This field must contain at least 1 character.',
            minimumOneDollar: 'Donation must be minimum 1 dollar.'
          });
        }
      };
      App.confirmContributionDialog = function() {
        var amount_value, confirm_string, confirmation_dialog_contribution, cumulative_amount, date_field, dates, float_amount, interval, j, len, money_bomb_string, recurring_string, ref;
        confirmation_dialog_contribution = parseFloat($('#confirmation_dialog_contribution').data('value'));
        if (confirmation_dialog_contribution === (0/0)) {
          return true;
        }
        amount_value = $('input.donation-amount-value').val() || $('input.conduit-amount-value').val();
        if (confirmation_dialog_contribution === void 0 || amount_value === void 0) {
          return true;
        }
        confirm_string = "Are you sure you want to make a donation of $" + amount_value + "?";
        recurring_string = "";
        money_bomb_string = "";
        float_amount = parseFloat(amount_value);
        cumulative_amount = float_amount;
        if ($(".first-interval:visible").length > 0 && $(".first-interval:visible")[0].innerText.indexOf('now') < 0) {
          interval = $(".first-interval:visible")[0].innerText;
          recurring_string = " This will be a " + interval.toLowerCase() + " recurring donation.";
          if (interval === 'Weekly') {
            cumulative_amount += float_amount * 2;
          }
        }
        if ($(".money-bomb-date:visible").length > 0) {
          dates = [];
          ref = $(".money-bomb-date:visible");
          for (j = 0, len = ref.length; j < len; j++) {
            date_field = ref[j];
            dates.push(date_field.innerText);
          }
          cumulative_amount += float_amount;
          money_bomb_string = " You will also be charged on " + dates.join(" and ") + ".";
        }
        confirm_string += recurring_string;
        confirm_string += money_bomb_string;
        if (cumulative_amount >= confirmation_dialog_contribution) {
          return confirm(confirm_string);
        } else {
          return true;
        }
      };
      App.isDonorBlocked = function() {
        var cc_div, cc_index_div, current_donor_blocked, donor_is_blocked, form, j, len, ref;
        cc_index_div = $('#conduit-candidate-index');
        donor_is_blocked = false;
        if (cc_index_div.length === 0) {
          form = $('#main-form form');
          if (form.find('input[name="donation[state-level-donor-blocked]"]').val() === 'true') {
            donor_is_blocked = true;
          }
        } else {
          ref = cc_index_div.find('.conduit-candidate');
          for (j = 0, len = ref.length; j < len; j++) {
            cc_div = ref[j];
            current_donor_blocked = !!$(cc_div).data('donor-blocked');
            if (current_donor_blocked) {
              donor_is_blocked = true;
              break;
            }
          }
        }
        return donor_is_blocked;
      };
      App.isDonorCompletelyBlocked = function() {
        var cc_div, cc_index_div, current_donor_blocked, donor_is_completely_blocked, form, j, len, ref;
        cc_index_div = $('#conduit-candidate-index');
        donor_is_completely_blocked = true;
        if (cc_index_div.length === 0) {
          form = $('#main-form form');
          if (form.find('input[name="donation[state-level-donor-blocked]"]').val() !== 'true') {
            donor_is_completely_blocked = false;
          }
        } else {
          ref = cc_index_div.find('.conduit-candidate');
          for (j = 0, len = ref.length; j < len; j++) {
            cc_div = ref[j];
            current_donor_blocked = !!$(cc_div).data('donor-blocked');
            if (!current_donor_blocked) {
              donor_is_completely_blocked = false;
              break;
            }
          }
        }
        return donor_is_completely_blocked;
      };
      App.moreThanOneCreditCardMerchantPresent = function() {
        var cc_div, cc_index_div, j, len, more_than_one_merchant_present;
        cc_index_div = $('#conduit-candidate-index').find('.conduit-candidate');
        more_than_one_merchant_present = false;
        if (cc_index_div.length === 1) {
          return more_than_one_merchant_present;
        }
        for (j = 0, len = cc_index_div.length; j < len; j++) {
          cc_div = cc_index_div[j];
          if ($(cc_div).hasClass('merchant_org')) {
            more_than_one_merchant_present = true;
            break;
          }
        }
        return more_than_one_merchant_present;
      };
      $('.landing-page-orderables').find('.amount-option:not(.selected)').each(function() {
        return $(this).find('.quantity-select').css('visibility', 'hidden');
      });
      $('.amount-option').on('click', function() {
        var amount_selected, description;
        if (!$(this).hasClass('other-amount-button')) {
          $('.other-amount-field').val('');
          $('.other-amount-field').removeClass('selected').removeClass('error');
          amount_selected = $(this).data('amount');
          new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': App.dataLayerCategory(),
            'form': null,
            'action': 'selected',
            'label': 'donation_amount',
            'amount': amount_selected,
            'userAction': 't',
            'suppressTransactionID': true
          });
        } else {
          $('.other-amount-field').focus();
        }
        $('.other-amount-button').data('amount', '').removeClass('selected').addClass('hidden-xs-up');
        $('.other-amount-field-group').show();
        $('.amount-option').removeClass('selected');
        $(this).addClass('selected');
        if ($(this).data('description')) {
          description = $(this).data('description');
          $('.donation-amount-description').text(description).prop('value', description);
        }
        if ($('.landing-page-orderables')[0]) {
          $(this).find('.quantity-select').css('visibility', 'visible');
          App.updateOrderTotal(this);
          return $('.landing-page-orderables').find('.amount-option:not(.selected)').each(function() {
            return $(this).find('.quantity-select').prop('value', 1).css('visibility', 'hidden');
          });
        } else {
          return App.updateDonationAmount(this);
        }
      });
      $('.amount-option').on('change', '.quantity-select', function() {
        return App.updateOrderTotal(this);
      });
      $('.additional-amount-field').on('keyup', function() {
        App.ensureCurrencyInput(this);
        return App.updateOrderTotal(this);
      });
      $('.other-amount-field, .other-conduit-amount-field').on('keyup', function() {
        var form_validator, max_other_amount_dollars;
        max_other_amount_dollars = App.enforceMaximumContribution($(this), $(this).val());
        $(this).val(max_other_amount_dollars);
        App.ensureCurrencyInput(this);
        $(this).addClass('selected');
        $('.amount-option').removeClass('selected');
        form_validator = $(this).parents('form').validate();
        if ($('input.donation-amount-value').val() > 0) {
          form_validator.element($('input.donation-amount-value'));
        }
        return App.updateDonationAmount(this);
      });
      $('.other-field-custom-input').focus(function() {
        $(this).removeClass('error');
        $(this).parents('.radio-buttons').find('.radio-button-checkbox').removeClass('active');
        $(this).parents('.radio-buttons').find('.radio-button-checkbox').find(':radio').prop('checked', false);
        $(this).parents('.radio-button-checkbox, .multiple-choice-checkbox').addClass('active');
        return $(this).parents('.custom-field-other-option').find(':checkbox, :radio').prop('checked', true);
      });
      $('.radio-button-checkbox input[type="radio"]').on('change', function() {
        var other_field_custom_input, radio_buttons;
        radio_buttons = $(this).parents('.radio-buttons');
        if (!$(this).hasClass('radio-button-other-field')) {
          radio_buttons.find('.custom-field-other-option').find(':radio').prop('checked', false);
          radio_buttons.find('.custom-field-other-option').removeClass('error active');
          other_field_custom_input = radio_buttons.find('.other-field-custom-input');
          other_field_custom_input.val('');
          return $(other_field_custom_input).closest('.float-label').find('label').removeClass('active');
        } else {
          radio_buttons.find('.radio-button-checkbox').removeClass('active');
          radio_buttons.find('.radio-button-checkbox').find(':radio').prop('checked', false);
          $(this).prop('checked', true);
          $(this).parents('.radio-button-checkbox').addClass('active');
          return $(this).parents('.custom-field-other-option').find('.other-field-custom-input').focus();
        }
      });
      $('.custom-field-other-option input[type="checkbox"]').on('change', function() {
        var other_field_custom_input;
        if ($(this).prop('checked')) {
          return $(this).parents('.custom-field-other-option').find('.other-field-custom-input').focus();
        } else {
          other_field_custom_input = $(this).parents('.custom-field-other-option').find('.other-field-custom-input');
          other_field_custom_input.val('');
          other_field_custom_input.closest('.float-label').find('label').removeClass('active');
          return other_field_custom_input.valid();
        }
      });
      $('.other-amount-field').on('blur', function(e) {
        var amount, parentNode, removeTrailingZerosOnDecimal;
        amount = $(this).val();
        if (parseFloat(amount) > 0) {
          if ($(this).parents('.amount-no-desc').length > 0) {
            parentNode = $(this).parents('.amount-no-desc');
          } else {
            parentNode = $(this).parents('.amount-desc');
          }
          removeTrailingZerosOnDecimal = parseFloat(amount).toFixed(0) === parseFloat(amount).toFixed(2);
          parentNode.find('.amount-option').data('amount', amount).text(amount).formatCurrency({
            roundToDecimalPlace: 2,
            removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
          }).addClass('selected').removeClass('hidden-xs-up');
          parentNode.find('.other-amount-field-group').hide();
          return new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': App.dataLayerCategory(),
            'form': null,
            'action': 'entered',
            'label': 'donation_amount',
            'amount': amount,
            'userAction': 't',
            'suppressTransactionID': true
          });
        } else {
          return e.preventDefault();
        }
      });
      if ($('meta[name=enable-business-donations]').attr('content') === 'false') {
        $('.business-donations-fields').hide();
        $('.contributor-type-option[data-value=business]').remove();
      }
      if ($('meta[name=enable-business-donations-type-of-business]').attr('content') === 'false') {
        $('.type-of-business').hide();
      }
      if ($('meta[name=enable-business-donations-tax-id]').attr('content') === 'false') {
        $('.business-tax-id').hide();
      }
      if ($('meta[name=enable-pac-donations]').attr('content') === 'false') {
        $('.pac-donations-fields').hide();
        $('.contributor-type-option[data-value=pac]').remove();
      }
      if ($('meta[name=enable-pac-donations-fec-id]').attr('content') === 'false') {
        $('.pac-fec-id').hide();
      }
      $('.type-of-business-selector').on('change', function(e) {
        if ($(this).val() === 'Other') {
          $("input[id$='_type_of_business']").last().attr('type', 'text');
          $("input[id$='_type_of_business']").last().val('');
          $("input[id$='_type_of_business']").last().focus();
          return $("select[id$='_type_of_business']").last().next().show();
        } else {
          $("input[id$='_type_of_business']").last().attr('type', 'hidden');
          $("input[id$='_type_of_business']").val($(this).val());
          return $("select[id$='_type_of_business']").last().next().hide();
        }
      });
      App.updateContributorType = function(_this, pageload) {
        var address_field, contributor_type, disabled_param, email_field, email_required, first_name_field, j, last_name_field, len, maximum_contribution_dollars, newUrl, new_address, new_email, new_first_name, new_last_name, ref, selected_donation_fields, selected_donation_fields_class, unselected_donation_fields;
        contributor_type = _this.data('value');
        $('input.contributor-type').val(contributor_type);
        App.contributorType = contributor_type;
        if ($('.landing-page-contributor-type').hasClass('logged-in-landing-page-contributor-type')) {
          if (contributor_type === 'business' || contributor_type === 'pac') {
            newUrl = $.updateURLParameter(window.location.href, 'contributor_type', contributor_type);
            window.location = newUrl;
            return;
          }
        }
        if (contributor_type === 'business' || contributor_type === 'pac') {
          ref = ['recurring', 'money_pledge', 'money_bomb'];
          for (j = 0, len = ref.length; j < len; j++) {
            disabled_param = ref[j];
            if ($.urlParam(disabled_param) === 'true') {
              newUrl = $.updateURLParameter(window.location.href, disabled_param, 'false');
              window.history.replaceState('', '', newUrl);
            }
          }
          $('.login-mobile').hide();
        }
        if (contributor_type === 'personal') {
          $('.login-mobile').show();
          $('.payment-request-details').show();
        }
        if (contributor_type === 'personal' && !$('.landing-page-contributor-type').hasClass('logged-in-landing-page-contributor-type')) {
          if ($.urlParam('contributor_type') === 'business' || $.urlParam('contributor_type') === 'pac') {
            newUrl = $.updateURLParameter(window.location.href, 'contributor_type', 'personal');
            window.location = newUrl;
            return;
          }
        }
        if ($.urlParam('contributor_type') !== null) {
          newUrl = $.updateURLParameter(window.location.href, 'contributor_type', contributor_type);
          window.history.replaceState('', '', newUrl);
        }
        first_name_field = $(".profile-form [id$='_first_name']");
        last_name_field = $(".profile-form [id$='_last_name']");
        email_field = $("[id$='_email']");
        address_field = $(".profile-form [id$='_address']");
        maximum_contribution_dollars = App.getMaximumContribution();
        $('select.merchandise-campaign-variant-option-select').val('');
        $('.other-amount-field, .other-conduit-amount-field').attr('max', maximum_contribution_dollars);
        if (!$('.rv-type-checkout-page').length && !pageload && (!$.urlParam('amount') || $.urlParam('amount') && parseFloat($.urlParam('amount')) > App.getMaximumContribution())) {
          $('.other-amount-field, .other-conduit-amount-field').val('');
          $('.other-amount-button').data('amount', '').removeClass('selected').addClass('hidden-xs-up');
          $('.other-amount-field-group').show();
          $("input[id$='donation_amount']").val('');
          $(".selected.btn[class*=amount-option]").removeClass('selected');
          $('.footer-total .donation-amount-value').text('$0');
        }
        $('.contributor-type-option').removeClass('selected');
        _this.addClass('selected');
        $('.landing-page-personal-details .help-text').hide();
        $('.landing-page-personal-details .help-text.' + contributor_type).show();
        App.donationAmountButtonsToggle(maximum_contribution_dollars);
        if (!$('.rv-type-checkout-page').length) {
          $('.shipping-address-block').addClass('d-none').removeClass('d-block');
          $('.shipping-disclaimer').addClass('d-none').removeClass('d-flex');
          $('.merchandise-campaign-callout > .merchandise-campaign--valid').addClass('d-none').removeClass('d-block');
          $('.merchandise-campaign-callout > .merchandise-campaign--invalid').addClass('d-none').removeClass('d-block');
          $('.qualifies-for-merchandise').val(false);
        }
        App.submitButtonValidator();
        $('[id$=_first_name], [id$=_last_name]').each(function() {
          if ($('form').data('validator')) {
            if (contributor_type !== 'personal' && $(this).rules().potentialOrganization === true) {
              $(this).rules('remove', 'potentialOrganization');
              return $(this).rules('add', {
                potentialOrganizationVariant: true
              });
            } else if ($(this).rules().potentialOrganizationVariant === true) {
              $(this).rules('remove', 'potentialOrganizationVariant');
              return $(this).rules('add', {
                potentialOrganization: true
              });
            }
          }
        });
        if ($('form').data('validator')) {
          $('.donation-amount-value').each(function() {
            $(this).rules('remove', 'maxPersonalContributionCheck');
            $(this).rules('remove', 'maxBusinessContributionCheck');
            $(this).rules('remove', 'maxPacContributionCheck');
            if (App.contributorType === 'business') {
              return $(this).rules('add', {
                maxBusinessContributionCheck: [parseFloat(maximum_contribution_dollars)]
              });
            } else if (App.contributorType === 'pac') {
              return $(this).rules('add', {
                maxPacContributionCheck: [parseFloat(maximum_contribution_dollars)]
              });
            } else {
              return $(this).rules('add', {
                maxPersonalContributionCheck: [parseFloat(maximum_contribution_dollars)]
              });
            }
          });
        }
        if (contributor_type === 'business' || contributor_type === 'pac') {
          if ($('input[data-name=recurring]').is(':checked')) {
            $('input[data-name=recurring]').click();
            $('input[data-name=recurring]').prop('checked', false);
          }
          if ($('input.money-bomb-check-box').is(':checked')) {
            $('input.money-bomb-check-box').click();
          }
          $('input[data-name=additional_recurring]').prop('checked', false);
          if ($('.btn-group-recurring input[type=radio][data-required=true]').find("input:checked").length) {
            $('.btn-group-recurring label').first().removeClass('active');
            $('.btn-group-recurring label').last().addClass('active');
          }
          if ($.urlParam('recurring') === 'true') {
            newUrl = $.updateURLParameter(window.location.href, 'recurring', 'false');
            window.history.replaceState('', '', newUrl);
          }
          $('input[id$=_donor_selected_recurring_interval]').attr('value', '');
          $('.footer-total .first-interval').text('now');
        }
        new_first_name = first_name_field.data(contributor_type + '-first-name');
        first_name_field.attr('placeholder', new_first_name);
        first_name_field.prev('label.placeholder').text(new_first_name);
        new_last_name = last_name_field.data(contributor_type + '-last-name');
        last_name_field.attr('placeholder', new_last_name);
        last_name_field.prev('label.placeholder').text(new_last_name);
        email_required = email_field.data('email-required');
        if (email_required === true) {
          new_email = email_field.data(contributor_type + '-email');
        } else {
          new_email = email_field.data('optional-' + contributor_type + '-email');
        }
        new_address = address_field.data(contributor_type + '-address');
        address_field.attr('placeholder', new_address);
        address_field.prev('label.placeholder').text(new_address);
        email_field.attr('placeholder', new_email);
        email_field.prev('label.placeholder').text(new_email);
        first_name_field.on('blur', function() {
          return first_name_field.attr('placeholder', new_first_name);
        });
        last_name_field.on('blur', function() {
          return last_name_field.attr('placeholder', new_last_name);
        });
        email_field.on('blur', function() {
          return email_field.attr('placeholder', new_email);
        });
        address_field.on('blur', function() {
          return address_field.attr('placeholder', new_address);
        });
        selected_donation_fields_class = '.' + contributor_type + '-donations-fields';
        selected_donation_fields = $(selected_donation_fields_class);
        selected_donation_fields.removeClass('inactive');
        selected_donation_fields.show();
        selected_donation_fields.find('input, select').attr('required', true);
        unselected_donation_fields = $('.donation-fields:not(' + selected_donation_fields_class + ')');
        unselected_donation_fields.addClass('inactive');
        unselected_donation_fields.hide();
        unselected_donation_fields.find('input, select').attr('required', false);
        if (contributor_type === 'pac' || contributor_type === 'business') {
          $('.landing-page-password-details').hide();
          $('.employment-details').hide();
          $('.employment-form input, .employment-form select').attr('required', false);
          $('.profile-form .full-name').removeClass('d-flex flex-row split');
          last_name_field.removeClass('divider');
          $('.profile-form .last-name').removeClass('float-label-right');
          return $('.money-pledge.row').hide();
        } else {
          $('.landing-page-password-details').show();
          $('.employment-details').show();
          $('.employment-form input, .employment-form select').attr('required', true);
          if (!$('.profile-form .full-name').hasClass('d-flex flex-row split')) {
            $('.profile-form .full-name').addClass('d-flex flex-row split');
          }
          if (last_name_field.hasClass('divider')) {
            last_name_field.addClass('divider');
            if (!$('.profile-form .last-name').hasClass('float-label-right')) {
              $('.profile-form .last-name').addClass('float-label-right');
            }
          }
          return $('.money-pledge.row').show();
        }
      };
      $('.contributor-type-option').on('click', function() {
        return App.updateContributorType($(this), false);
      });
      url_param_contributor_type = $.urlParam('contributor_type');
      if (url_param_contributor_type !== null && ((url_param_contributor_type === 'business' && $('meta[name=enable-business-donations]').attr('content') === 'true') || (url_param_contributor_type === 'pac' && $('meta[name=enable-pac-donations]').attr('content') === 'true'))) {
        if (!$('.rv-type-live-stream-page').length) {
          App.updateContributorType($('.contributor-type-option[data-value=' + url_param_contributor_type + ']'), true);
        }
      } else if ($('.contributor-type-option').length) {
        input_contributor_type = $('input.contributor-type').val();
        App.updateContributorType($('.contributor-type-option[data-value=' + input_contributor_type + ']'), true);
      }
      if ($('.contributor-type-option').length === 1) {
        $('.landing-page-contributor-type').hide();
      }
      if ($('.contributor-type-option[data-value=pac]').length || $('.contributor-type-option[data-value=business]').length) {
        $('.footer-login').hide();
      }
      $('.landing-page-cover-fees-button').on('change data.name.set', 'input[type=checkbox]', function(e) {
        var newUrl, parent;
        if ($.urlParam('cover_fees') && $.urlParam('cover_fees').length) {
          newUrl = $.updateURLParameter(window.location.href, 'cover_fees', this.checked);
          window.history.replaceState('', '', newUrl);
          $(this).parents('form').attr('action', newUrl);
        }
        parent = $(e.target).parents('label');
        if (parent.attr('style').length === 0) {
          parent.attr('style', parent.data('data'));
        } else {
          parent.data('style', parent.attr('style'));
          parent.attr('style', '');
        }
        return App.updateDonationAmount(this);
      });
      $('body').on('keyup', 'input[id$=_zip], .rv-election-day-look-up input[id=zip], input[id=petition_postal_code]', function(e) {
        return App.populateCityState(this);
      });
      $('#donate-btn-confirm').on('click', function(e) {
        $('#validation-modal').modal('hide');
        App.modalConfirmedByDonor = true;
        return App.submitForm(App.currentFormEvent[0], App.currentFormEvent[1]);
      });
      $('body').on('click', '.address-check-option', function(e) {
        $('.address-check-option').removeClass('active');
        $(this).addClass('active');
        $(this).find('[type=radio]').attr('checked', 'checked').prop("checked", true);
        return $('body').trigger('apollo.address.check');
      });
      $('body').on('apollo.address.check', function(e) {
        var data, home_address, same_as_billing, selected_address, shipping_address;
        selected_address = $('.address-check-option.active');
        selected_address.find('[type=radio]').attr('checked', 'checked').prop("checked", true);
        if (selected_address) {
          data = selected_address.data('data');
          shipping_address = $('.shipping-address-form');
          home_address = $('.home-address');
          same_as_billing = $('[id$=_shipping_same_as_billing]');
          if (shipping_address) {
            if (same_as_billing[0].checked) {
              same_as_billing.click();
            }
            shipping_address.find('[id$=_first_name]').val(data.first_name || $('.suggested-shipping-address').data('first-name'));
            shipping_address.find('[id$=_last_name]').val(data.last_name || $('.suggested-shipping-address').data('last-name'));
            shipping_address.find('[id$=_address]').val(data.address);
            shipping_address.find('[id$=_address_line2]').val(data.address2);
            shipping_address.find('[id$=_zip]').val(data.postal_code);
            shipping_address.find('[id$=_city]').val(data.city);
            return shipping_address.find('[id$=_state]').val(data.state);
          } else if (home_address) {
            home_address.find('[id$=_address]').val(data.address);
            home_address.find('[id$=_address_line2]').val(data.address2);
            home_address.find('[id$=_zip]').val(data.postal_code);
            home_address.find('[id$=_city]').val(data.city);
            return home_address.find('[id$=_state]').val(data.state);
          }
        }
      });
      $('#validation-modal').on('hidden.bs.modal', function(e) {
        App.modalConfirmedByDonor = false;
        $('#address-check-validation').find('.address-selection').empty();
        return $('#order-details-with-sales-tax').find('.donation-amount-block').addClass('hide');
      });
      (function() {
        var amount, amount_button, amount_buttons, amount_count, amtposition, dimension, el, index, j, k, len, len1, new_amount, oc, parentElement, parentNode, removeTrailingZerosOnDecimal, selected_amount, split_key, value, visible_button, visible_buttons;
        App.validationLanguageSelect();
        if ($.urlParam('amounts') && $.urlParam('amounts').length !== 0) {
          $.urlParam('amounts').split(',').forEach(function(new_amount, index) {
            var amount_field, removeTrailingZerosOnDecimal, selector;
            selector = '.amount-' + String(index + 1);
            amount_field = $(selector);
            if (amount_field.length !== 0) {
              amount_field.data('amount', new_amount);
              amount_field.text(new_amount);
              removeTrailingZerosOnDecimal = parseFloat(new_amount.toFixed(0)) === parseFloat(new_amount.toFixed(2));
              return amount_field.formatCurrency({
                roundToDecimalPlace: 2,
                removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
              });
            }
          });
        }
        if ($.urlParam('dimension') && $.urlParam('dimension').length !== 0 && $('meta[name=dimension]').length !== 0) {
          dimension = $.urlParam('dimension').toLowerCase();
          split_key = void 0;
          $.each($('meta[name=dimension]').data('keys'), function(key, val) {
            if (val === ',') {
              split_key = key;
              return false;
            }
          });
          dimension.split(split_key).forEach(function(option, index) {
            var value;
            value = App.dimensionDecode(option);
            if (value.length) {
              return $('[data-name=amt' + (index + 1) + ']').attr('data-amount', value).text('$' + value);
            }
          });
        }
        if ($.urlParam('amount') && $.urlParam('amount').length !== 0) {
          if ((selected_amount = $('[data-amount="' + $.urlParam('amount') + '"]')).length !== 0) {
            $(selected_amount[0]).click().addClass('selected');
          } else {
            value = $.urlParam('amount');
            new_amount = parseFloat(value);
            removeTrailingZerosOnDecimal = parseFloat(new_amount.toFixed(0)) === parseFloat(new_amount.toFixed(2));
            $('.other-amount-field').val(new_amount).addClass('selected');
            if ($('.other-amount-field').parents('.amount-no-desc').length > 0) {
              parentNode = $('.other-amount-field').parents('.amount-no-desc');
            } else {
              parentNode = $('.other-amount-field').parents('.amount-desc');
            }
            parentNode.find('.amount-option').data('amount', new_amount).text(new_amount).formatCurrency({
              roundToDecimalPlace: 2,
              removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
            }).addClass('selected').removeClass('hidden-xs-up');
            parentNode.find('.other-amount-field-group').hide();
            $('input.donation-amount-value').prop('value', new_amount).trigger('paymentRequestChange');
            $('div.donation-amount-value').text(new_amount).formatCurrency({
              roundToDecimalPlace: 2,
              removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
            });
            App.updateMatchAmount(new_amount);
          }
          App.updateDonationAmount($('.donation-amount-value'));
        }
        if ($.urlParam('zip') && $.urlParam('zip').length === 5) {
          el = $('input[id$=_zip]:not(input[id$=shipping_zip])');
          App.populateCityState(el);
        }
        if ($.urlParam('shipping_zip') && $.urlParam('shipping_zip').length === 5) {
          el = $('input[id$=shipping_zip]');
          App.populateCityState(el);
        }
        if ($('body').find('.rv-steps-multi').length) {
          $(window).load(function() {
            if ($('input[id$=_amount]').length > 0 && $('input[id$=_amount]').val().length > 0) {
              return $('.tab-pane.active.multi').find('.btn-submit').removeClass('btn-submit--invalid');
            }
          });
        }
        if ($('[id$=_one_click]').val() === "true" && $('.rv-type-conduit-page').length === 0) {
          $('[id$=_one_click]').parent('form').find('[type=submit]').click();
        }
        $('body').on('click', 'a[data-target="#login"]', function(e) {
          $('html, body').animate({
            scrollTop: 0
          }, 100);
          return $('#user_login_email').focus();
        });
        if ($.urlParam('amtposition') && $.urlParam('amtposition').length !== 0 && (($('.conduit-amount-option') && $('.conduit-amount-option').length !== 0) || ($('.amount-option') && $('.amount-option').length !== 0))) {
          amtposition = parseInt($.urlParam('amtposition'));
          oc = $.urlParam('oc');
          amount = $.urlParam('amount');
          if (!isNaN(amtposition) && amtposition > 0) {
            if ($('.conduit-amount-option') && $('.conduit-amount-option').length > 0) {
              amount_buttons = document.querySelectorAll('.conduit-amount-option');
            } else {
              amount_buttons = document.querySelectorAll('.amount-option');
            }
            if (amount_buttons && amount_buttons.length > 0) {
              visible_buttons = [];
              for (j = 0, len = amount_buttons.length; j < len; j++) {
                amount_button = amount_buttons[j];
                parentElement = amount_button.parentNode;
                if (amount_button.style.display !== 'none' && parentElement && parentElement.style.display !== 'none' && !parentElement.classList.contains('d-none')) {
                  visible_buttons.push(amount_button);
                }
              }
              if (visible_buttons.length > 0) {
                amount_count = visible_buttons.length;
                if (amount_count >= amtposition && oc !== 'true' && (!amount || amount.length === 0)) {
                  for (index = k = 0, len1 = visible_buttons.length; k < len1; index = ++k) {
                    visible_button = visible_buttons[index];
                    if (index + 1 === amtposition) {
                      visible_button.click();
                      visible_button.classList.add('selected');
                    } else {
                      visible_button.classList.remove('selected');
                    }
                  }
                }
              }
            }
          }
        }
        App.moovCardLink = document.querySelector('moov-card-link');
        if (App.moovCardLink) {
          App.moovCardLink.cardOnFile = true;
          App.moovCardLinkValidated = false;
          App.moovCardLink.onValidated = function(err) {
            return App.moovCardLinkValidated = true;
          };
          return App.moovCardLink.onEnterKeyPress = function(err) {
            return $('form[id^=new_]').submit();
          };
        }
      })();
      $('.address-fill').on('click', '[data-toggle="address-fill"]:not(.active)', function(e) {
        var target_div;
        target_div = $(this).data('target');
        return $(target_div + " input, " + target_div + " select").each(function() {
          return $(this).val('');
        });
      });
      App.addressProcessAutofill = function(_this) {
        var camel_field, field, j, len, origin_address, origin_form_type, origin_value, ref, target_address_type, target_input;
        target_address_type = $(_this).data('target').substring(1, $(_this).data('target').length).split('-')[0];
        origin_form_type = $(_this).data('origin');
        if (target_address_type) {
          ref = ['first_name', 'last_name', 'address', 'address_line2', 'city', 'state', 'zip'];
          for (j = 0, len = ref.length; j < len; j++) {
            field = ref[j];
            if (origin_form_type) {
              origin_value = $("#" + origin_form_type + "_" + field).val();
            } else if (origin_address = $('.suggested-shipping-address')) {
              camel_field = $.camelCase(field.replace('_', '-'));
              origin_value = origin_address.data(camel_field);
            }
            if (field === 'state') {
              target_input = $("select[id*='" + target_address_type + "_" + field + "']");
            } else {
              target_input = $("input[id*='" + target_address_type + "_" + field + "']:not([type=checkbox])");
            }
            target_input.val(origin_value);
            target_input.parents('form[data-toggle=validate]').first().validate().element(target_input);
          }
          return $(_this).trigger('addressFilled');
        }
      };
      $('.address-fill').find('[data-toggle="address-fill"].active').each(function() {
        return App.addressProcessAutofill(this);
      });
      $('.address-fill').on('click', '[data-toggle="address-fill"].active', function(e) {
        return App.addressProcessAutofill(this);
      });
      App.hideCustomShippingAddress = function(_this) {
        var $shipping_form;
        $shipping_form = $(_this).parents('.shipping-address-block').find('.shipping-address-form');
        if ($(_this).data('shipping') === void 0) {
          $(_this).data('shipping', $shipping_form.html());
        }
        $shipping_form.empty();
        $shipping_form.addClass('d-none');
        return $(_this).addClass('active');
      };
      App.showCustomShippingAddress = function(_this) {
        var $shipping_form;
        $shipping_form = $(_this).parents('.shipping-address-block').find('.shipping-address-form');
        $shipping_form.html($(_this).data('shipping'));
        $shipping_form.removeClass('d-none');
        return $(_this).removeClass('active');
      };
      $('.address-fill').find('[data-toggle="custom-shipping-address"].active').each(function() {
        return App.hideCustomShippingAddress(this);
      });
      $('.address-fill').on('click', 'input[type="checkbox"]', function(e) {
        if ($(e.target).is('input[type="checkbox"]')) {
          if ($(e.target).is(':checked')) {
            return App.hideCustomShippingAddress($(this).parents('[data-toggle="custom-shipping-address"]'));
          } else {
            return App.showCustomShippingAddress($(this).parents('[data-toggle="custom-shipping-address"]'));
          }
        }
      });
      $('a[data-toggle="wizard"]').on('click', function(e) {
        var current_tab, donor_is_blocked, focus_element, form_validator, modal_confirmation, recurring_button, valid;
        e.preventDefault();
        current_tab = $(this).parents('.tab-pane').first();
        if ($(this).data('validate')) {
          valid = App.elementValidatorFor(current_tab);
          if (valid) {
            if ($('.rv-steps-multi').length && $(this).data('target') === '#personal') {
              modal_confirmation = App.confirmContributionDialog();
              new App.DataLayer().push({
                'event': 'eec.add',
                'category': App.dataLayerCategory(),
                'form': null,
                'action': 'add to cart'
              });
            } else {
              modal_confirmation = true;
              new App.DataLayer().push({
                'event': 'eec.checkout',
                'category': App.dataLayerCategory(),
                'form': null,
                'action': 'checkout step 1'
              });
            }
            if (modal_confirmation) {
              $.each($(this).data('target').split(' '), function() {
                return $('.nav-tabs a[href="' + this + '"]').tab('show');
              });
            }
            donor_is_blocked = App.isDonorBlocked();
            if ($('.rv-steps-multi').length && $(this).data('target') === '#payment') {
              if (donor_is_blocked) {
                $('.nav-tabs a[href="#donation"]').tab('show');
              }
            }
            if ($('.rv-steps-multi').length && $(this).data('target') === '#personal') {
              if (donor_is_blocked) {
                $('.nav-tabs a[href="#payment"]').tab('show');
              }
            }
          }
        } else {
          $.each($(this).data('target').split(' '), function() {
            return $('.nav-tabs a[href="' + this + '"]').tab('show');
          });
        }
        activeNav = $(".multistep-nav li.nav-item .nav-link.active");
        if (activeNav && activeNav.text() !== 'MainDonation') {
          $(".back-button").show();
        } else {
          $(".back-button").hide();
        }
        if ($(this).data('hide')) {
          $($(this).data('hide')).hide();
        }
        if ($(this).data('show')) {
          $($(this).data('show')).show();
        }
        if ($($(this).data('target') + '[data-scroll-to-element]').length && !$(this).hasClass('btn-submit--invalid')) {
          $('html, body').animate({
            scrollTop: $($(this).data('target') + '[data-scroll-to-element]').offset().top - 50
          }, 20);
        }
        if ($('.btn-group-recurring').data('bs.tooltip')) {
          recurring_button = $('.btn-group-recurring').find("input");
          if (recurring_button) {
            form_validator = recurring_button.parents('form').validate();
            form_validator.element(recurring_button);
            $('html, body').animate({
              scrollTop: $(recurring_button).offset().top - 100
            }, 500);
            return recurring_button.focus();
          }
        } else {
          focus_element = $(current_tab).find('input, select, textarea').filter(function() {
            return !this.value || $(this).data('bs.tooltip');
          }).first();
          if ($('.btn-group-recurring').data('bs.tooltip')) {
            focus_element = $('.btn-group-recurring').find("input");
          }
          if (focus_element.length) {
            form_validator = $(this).parents('form').validate();
            form_validator.element(focus_element);
            if (focus_element.data('bs.tooltip') && !focus_element.data('bs.tooltip')._isTransitioning) {
              focus_element.tooltip('show');
            }
            if (!$(focus_element).hasClass('other-field-custom-input')) {
              return focus_element.focus();
            }
          } else {
            return $($(this).data('target')).find('input').first().focus();
          }
        }
      });
      $(document).on('blur', 'input, textarea', function() {
        var form_validator;
        form_validator = $(this).parents('form').validate();
        if (form_validator) {
          return form_validator.element($(this));
        }
      });
      $(document).on('focusin', 'input:visible:last, select:visible:last', function() {
        var form_validator;
        form_validator = $(this).parents('form').validate();
        if (form_validator) {
          return form_validator.element($(this));
        }
      });
      $.extend(true, $.validator, {
        prototype: {
          defaultShowErrors: function() {
            var _this;
            _this = this;
            $.each(this.errorList, function(index, value) {
              var $element, action, options;
              action = null;
              $element = $(value.element);
              if ($element.is('[type=hidden]')) {
                $element = $element.parent('div');
                action = 'show';
              }
              if ($element.is('[type=checkbox]') || $element.is('[type=radio]')) {
                if ($element.is('[data-name=recurring')) {
                  $element = $element.parent().parent();
                } else {
                  $element = $element.parent('label');
                }
                action = 'show';
              }
              if ($element.is('[name*="[additional_fields]"]')) {
                action = 'show';
              }
              if ($element.is('.multiple-choice-option')) {
                return;
              }
              if ($element.data('bs.tooltip') !== void 0) {
                $element.data('bs.tooltip').config.title = value.message;
              } else {
                options = {
                  placement: 'top',
                  title: value.message,
                  trigger: 'focus'
                };
                $element.tooltip(options);
              }
              if (($element.data('bs.tooltip') && !$element.data('bs.tooltip')._isTransitioning) || action !== 'show') {
                return $element.removeClass(_this.settings.validClass).addClass(_this.settings.errorClass).tooltip(action);
              }
            });
            $.each(_this.validElements(), function(index, value) {
              var $element;
              $element = $(value);
              App.removeValidateError($(value).prop('name'));
              if ($element.is('[type=hidden]')) {
                $element = $element.parent('div');
              }
              if ($element.data('bs.tooltip') && !$element.data('bs.tooltip')._isTransitioning) {
                return $element.removeClass(_this.settings.errorClass).addClass(_this.settings.validClass).tooltip('dispose');
              }
            });
            return App.submitButtonValidator();
          }
        }
      });
      $('[data-toggle="validate"]').each(function() {
        return $(this).validate({
          submitHandler: function(form) {
            if ($('#validation-modal').length > 0 && !App.modalConfirmedByDonor) {
              return;
            }
            if ($(form).has('.credit-card').length === 0 && !$(form).data().isSubmitted && !$(form).data().showingCaptcha) {
              if (App.confirmContributionDialog()) {
                $("[type=submit]:not([name=decline])").attr('disabled', true);
                if ($('.logged-in-landing-page-personal-details').length || $('.with-temporary-profile').length) {
                  new App.DataLayer().push({
                    'event': 'eec.add',
                    'category': App.dataLayerCategory(),
                    'form': null,
                    'action': 'add to cart'
                  });
                  new App.DataLayer().push({
                    'event': 'eec.checkout',
                    'category': App.dataLayerCategory(),
                    'form': null,
                    'action': 'checkout step 1'
                  });
                }
                if ($(form).attr('id') === 'new_conduit' || $(form).attr('id') === 'new_donation') {
                  new App.DataLayer().push({
                    'event': 'donation attempt',
                    'category': App.dataLayerCategory(),
                    'form': null,
                    'action': 'attempt'
                  });
                }
                return form.submit();
              } else {
                $('.loading').hide();
                $(form).data().isSubmitted = false;
              }
            }
          }
        });
      });
      $('[name=decline][type=submit]').on('click', function() {
        $(this).closest('form').validate().cancelSubmit = true;
        return $(this).closest('form').validate().settings.ignore = "*";
      });
      $.validator.addMethod('notEmpty', (function(value, element) {
        return this.optional(element) || value.trim() !== '';
      }));
      $.validator.addMethod('pwCheck', (function(value, element) {
        return value.length === 0 || value.length >= 8;
      }));
      $.validator.addMethod('zipCheck', (function(value, element) {
        return value === 'retired' || value.length === 5;
      }));
      $.validator.addMethod('phoneCheck', (function(value, element) {
        value = value.replace(/\s+/g, "");
        return this.optional(element) || value.length > 9 && value.match(/^(\+?1-?)?(\([2-9]\d{2}\)|[2-9]\d{2})-?[2-9]\d{2}-?\d{4}$/);
      }));
      $.validator.addMethod('multipleChoiceCheck', (function(value, element) {
        var checked;
        if (($(element).parents('.multiple-choice').length)) {
          checked = $(element).parents('.multiple-choice').find("input[type=checkbox]:checked").length > 0;
          return checked;
        } else {
          return true;
        }
      }));
      $.validator.addMethod('radioButtonCheck', (function(value, element) {
        var checked;
        if ($(element).hasClass('radio-button-other-field')) {
          return true;
        }
        if (($(element).parents('.radio-buttons').length)) {
          checked = $(element).parents('.radio-buttons').find("input[type=radio]:checked").length;
          return checked;
        } else {
          return true;
        }
      }));
      $.validator.addMethod('recurringButtonCheck', (function(value, element) {
        var checked;
        if (($(element).parents('.btn-group-recurring').find("input:checked").length)) {
          checked = $(element).parents('.btn-group-recurring').find("input:checked").length;
          return checked;
        } else {
          $(element)[0].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'start'
          });
          return false;
        }
      }));
      $.validator.addMethod('emailCheck', (function(value, element) {
        return value.match(/(.+)@(.+)\.(.+)/) !== null;
      }));
      $.validator.addMethod('maxPersonalContributionCheck', (function(value, element, params) {
        return value >= 0.5 && value <= params[0];
      }));
      $.validator.addMethod('maxBusinessContributionCheck', (function(value, element, params) {
        return value >= 0.5 && value <= params[0];
      }));
      $.validator.addMethod('maxPacContributionCheck', (function(value, element, params) {
        return value >= 0.5 && value <= params[0];
      }));
      $.validator.addMethod('leadingApostrophe', (function(value, element, params) {
        return this.optional(element) || !(value.startsWith("'") || value.startsWith('"'));
      }));
      $.validator.addMethod('illegalCharacters', (function(value, element, params) {
        return value === 'retired' || this.optional(element) || value.match(/[\!\~\@\$\%\^\*\\\/\|\[\]\{\}\(\)£Â]+/) === null;
      }));
      $.validator.addMethod('nonNumeric', (function(value, element, params) {
        return this.optional(element) || value.match(/[\d\@\#\$\%\^\*]+/) === null;
      }));
      $.validator.addMethod('addressAlphaCheck', (function(value, element, params) {
        return value === 'retired' || this.optional(element) || value.replace(/[\d-]*/, '').trim().length > 0;
      }));
      $.validator.addMethod('addressNumCheck', (function(value, element, params) {
        return value === 'retired' || this.optional(element) || value.replace(/\D+/, '').trim().length > 0;
      }));
      $.validator.addMethod('potentialOrganization', (function(value, element, params) {
        return this.optional(element) || (value.trim().toLowerCase().match(/co\./) === null && value.trim().toLowerCase().match(/ltd\.*/) === null && value.toLowerCase().match(/inc\./) === null && value.toLowerCase().match(/\binc(orporated)*\b/) === null && value.toLowerCase().match(/company/) === null && value.toLowerCase().match(/\bco(rp)*(oration)*\b/) === null && value.toLowerCase().match(/ltd/) === null && value.toLowerCase().match(/strategies/) === null && value.toLowerCase().match(/business/) === null && value.toLowerCase().match(/p\.c\./) === null);
      }));
      $.validator.addMethod('potentialOrganizationVariant', (function(value, element, params) {
        return this.optional(element) || (value.trim().toLowerCase().match(/co\./) === null && value.trim().toLowerCase().match(/ltd\.*/) === null && value.toLowerCase().match(/inc\./) === null && value.toLowerCase().match(/\binc(orporated)*\b/) === null && value.toLowerCase().match(/company/) === null && value.toLowerCase().match(/\bco(rp)*(oration)*\b/) === null && value.toLowerCase().match(/ltd/) === null && value.toLowerCase().match(/strategies/) === null && value.toLowerCase().match(/business/) === null && value.toLowerCase().match(/p\.c\./) === null);
      }));
      $.validator.addMethod('emojiCheck', (function(value, element, params) {
        return this.optional(element) || value.trim().toLowerCase().match(/(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g) === null;
      }));
      $.validator.addMethod('atLeastTwoCharacters', (function(value, element, params) {
        return this.optional(element) || value.replace(/[\d-]*/, '').trim().length >= 2;
      }));
      $.validator.addMethod('maxCharactersCheck', (function(value, element, params) {
        if ($(element).hasClass('other-field-custom-input') && $(element).parents('.custom-field-other-option').find(':checkbox, :radio').prop('checked')) {
          return value.trim().length <= params;
        } else {
          return this.optional(element) || value.trim().length <= params;
        }
      }));
      $.validator.addMethod('donorBlocked', (function(value, element, params) {
        var candidateDiv, donorBlocked, input_field_is_valid;
        candidateDiv = $(element).closest('div.conduit-candidate');
        donorBlocked = !!candidateDiv.data('donor-blocked');
        input_field_is_valid = donorBlocked === false || (donorBlocked === true && parseInt(value) === 0);
        if (input_field_is_valid) {
          $(candidateDiv).css('background-color', 'white');
          $(candidateDiv).css('color', '#354B70');
          $(candidateDiv).find('input').css('color', '#354B70');
          $(candidateDiv).find('.blocked-donor-message').empty();
        } else {
          $('#conduit-candidate-index').removeClass('d-none');
          $('#main-form form').find('.conduit-edit-button').remove();
          $(candidateDiv).css('color', 'red');
          $(candidateDiv).find('input').css('color', 'red');
          $(candidateDiv).find('.blocked-donor-message').empty().append('<br /><br />Thanks for your interest in donating. We cannot accept your donation at this time. Change to 0 to continue.');
        }
        return input_field_is_valid;
      }));
      $.validator.addMethod('minimumOneDollar', (function(value, element, params) {
        var val;
        val = parseFloat(value);
        return val === 0 || val >= 1.0 || !App.moreThanOneCreditCardMerchantPresent();
      }));
      $.validator.addMethod('taxIdCheck', (function(value, element) {
        return value.length === 0 || value.replace(/\D+/, '').trim().length >= 9;
      }));
      $.validator.addMethod('fecIdCheck', (function(value, element) {
        return value.length === 0 || value.length >= 9;
      }));
      applyValidationsToNameFields = function() {
        return $('[id$=_first_name], [id$=_last_name]').each(function() {
          var validations;
          validations = {
            leadingApostrophe: true,
            illegalCharacters: true,
            nonNumeric: true,
            atLeastTwoCharacters: true
          };
          if ($('meta[name=organization-name]').attr('content').includes('WinRed')) {
            if (App.contributorType === void 0 || App.contributorType === 'personal') {
              validations.potentialOrganization = true;
            } else {
              validations.potentialOrganizationVariant = true;
            }
          }
          return $(this).rules('add', validations);
        });
      };
      applyValidationsToAddressField = function() {
        return $("input[type='text'][id$=_address]").each(function() {
          return $(this).rules('add', {
            leadingApostrophe: true,
            illegalCharacters: true,
            addressAlphaCheck: true,
            addressNumCheck: true
          });
        });
      };
      $.validator.addMethod('customOtherFieldNotEmpty', (function(value, element) {
        if ($(element).parents('.radio-buttons').find(':radio:not(.radio-button-other-field):checked').length > 0) {
          return true;
        }
        if (!$(element).parents('.custom-field-other-option').find(':checkbox, :radio').prop('checked')) {
          return true;
        }
        return value.trim() !== '';
      }));
      $('[type=text]:not(.other-field-custom-input)').each(function() {
        return $(this).rules('add', {
          notEmpty: true,
          emojiCheck: true
        });
      });
      $('.zip-mask').each(function() {
        return $(this).rules('add', {
          zipCheck: true
        });
      });
      $('[type=password]').each(function() {
        return $(this).rules('add', {
          pwCheck: true
        });
      });
      $('[type=email]:not(#donation_email, #conduit_email)').each(function() {
        return $(this).rules('add', {
          emailCheck: true
        });
      });
      $('#donation_email, #conduit_email').each(function() {
        if ($(this).data('required')) {
          return $(this).rules('add', {
            emailCheck: true
          });
        }
      });
      $('.donation-amount-value').each(function() {
        if (App.contributorType === 'pac') {
          return $(this).rules('add', {
            maxPacContributionCheck: [parseFloat($('meta[name=pac-donations-maximum-amount-cents]').attr('content')) / 100]
          });
        } else if (App.contributorType === 'business') {
          return $(this).rules('add', {
            maxBusinessContributionCheck: [parseFloat($('meta[name=business-donations-maximum-amount-cents]').attr('content')) / 100]
          });
        } else {
          return $(this).rules('add', {
            maxPersonalContributionCheck: [parseFloat($('#max-contribution').data('value'))]
          });
        }
      });
      applyValidationsToNameFields();
      applyValidationsToAddressField();
      $('[id$=_city], [id$=_employer_name], [id$=_occupation]').each(function() {
        return $(this).rules('add', {
          illegalCharacters: true
        });
      });
      $('[name$="[phone_number]"], [name$="[mobile_number]"]').each(function() {
        return $(this).rules('add', {
          phoneCheck: true
        });
      });
      $('.multiple-choice[data-required=true] input[type=checkbox]').each(function() {
        return $(this).rules('add', {
          multipleChoiceCheck: true
        });
      });
      $('.radio-buttons[data-required=true] input[type=radio]').each(function() {
        return $(this).rules('add', {
          radioButtonCheck: true
        });
      });
      $('.btn-group-recurring input[type=radio][data-required=true]').each(function() {
        return $(this).rules('add', {
          recurringButtonCheck: true
        });
      });
      $('.conduit-candidate-amount-field').each(function() {
        return $(this).rules('add', {
          donorBlocked: true,
          minimumOneDollar: true
        });
      });
      $('[id$=_tax_id').each(function() {
        return $(this).rules('add', {
          taxIdCheck: true
        });
      });
      $('[id$=_fec_id').each(function() {
        return $(this).rules('add', {
          fecIdCheck: true
        });
      });
      $('.other-field-custom-input').each(function() {
        return $(this).rules('add', {
          maxCharactersCheck: 200
        });
      });
      $('.other-field-custom-input').each(function() {
        return $(this).rules('add', {
          customOtherFieldNotEmpty: true
        });
      });
      donationVisibleToggle = function() {
        if ($('#donation, .rv-type-petition-page').is(':visible')) {
          $('.landing-page-cta').removeClass('landing-page-cta--hide');
        } else {
          if (!'.rv-type-live-stream-page'.length > 0) {
            $('.landing-page-cta').addClass('landing-page-cta--hide');
          }
        }
        return App.submitButtonValidator();
      };
      (function() {
        if ($('.rv-steps-multi').length) {
          donationVisibleToggle();
        }
        if ($('.rv-type-live-stream-page').length) {
          return window.addEventListener('message', function(event) {
            var data, height, width;
            if (event.isTrusted) {
              data = event.data;
              if (data.type === 'resize-iframe') {
                height = data.payload.height;
                width = data.payload.width;
                if (height <= 10000) {
                  $('#live-stream-page-iframe').attr('height', height);
                  return $('#live-stream-page-iframe').attr('width', width);
                }
              }
            }
          });
        }
      })();
      $('.rv-steps-multi').on('shown.bs.tab', '[data-toggle="tab"]', function() {
        return donationVisibleToggle();
      });
      $('.rv-steps-multi').on('change.current.tab', '.current-tab', function() {
        return donationVisibleToggle();
      });
      App.checkInputFieldForMinimumDonation = function(_this) {
        var amount_input_element, cc_div, input_donation_amount, minimumNotMet, minimum_error_div;
        minimumNotMet = false;
        if (!App.moreThanOneCreditCardMerchantPresent()) {
          return minimumNotMet;
        }
        amount_input_element = $(_this);
        input_donation_amount = parseFloat(amount_input_element.val());
        cc_div = amount_input_element.closest('.conduit-candidate');
        minimum_error_div = cc_div.find('.minimum-donation-required-message');
        if (input_donation_amount > 0 && input_donation_amount < 1.0) {
          minimumNotMet = true;
          minimum_error_div.empty();
          amount_input_element.addClass('minimum_not_met');
          minimum_error_div.append('Minimum donation of $1 required.');
        } else {
          amount_input_element.removeClass('minimum_not_met');
          minimum_error_div.empty();
        }
        return minimumNotMet;
      };
      App.checkAndHandleErrorForMinimumDonation = function() {
        return setTimeout((function() {
          var openCandidateDrawer;
          openCandidateDrawer = false;
          $('.conduit-candidate-amount-field').each(function() {
            var minimumNotMet;
            minimumNotMet = App.checkInputFieldForMinimumDonation(this);
            if (minimumNotMet) {
              return openCandidateDrawer = true;
            }
          });
          if (openCandidateDrawer) {
            $('#conduit-candidate-index').removeClass('d-none');
            return $('#main-form form').find('.conduit-edit-button').remove();
          }
        }), 50);
      };
      $('.conduit-candidate-amount-field').on('change', function(event) {
        var inputElement;
        inputElement = $(event.currentTarget);
        return App.checkInputFieldForMinimumDonation(inputElement);
      });
      $('.conduit-amount-option').on('click', function(event) {
        return App.checkAndHandleErrorForMinimumDonation();
      });
      $('.other-conduit-amount-field').on('change', function(event) {
        return App.checkAndHandleErrorForMinimumDonation();
      });
      $('.recurring-interval-check').each(function() {
        if ($(this).is(':checked')) {
          return $("input[id$='_donor_selected_recurring_interval']").attr('value', $(this).data('interval'));
        }
      });
      returnUserEventObj = function() {
        var proto_dl_user_event;
        proto_dl_user_event = {
          'event': app_platform + "Event",
          'category': App.dataLayerCategory(),
          'form': null,
          'userAction': 't',
          'suppressTransactionID': true
        };
        return proto_dl_user_event;
      };
      $('.btn-recurring-interval .recurring-interval-check, .btn-recurring-no-style-update').on('click', function() {
        var checkbox, dl_user_event, humanized_interval, label;
        checkbox = $(this);
        label = checkbox.parent();
        humanized_interval = checkbox.data('humanized-interval');
        dl_user_event = returnUserEventObj();
        dl_user_event.label = 'recurring button toggle';
        dl_user_event.recurringInterval = humanized_interval;
        if (checkbox.is(':checked') || (checkbox.find('[type=radio]').length && checkbox.find('[type=radio]').attr('value') === 'true')) {
          checkbox.parent().tooltip('hide');
          $('.recurring-interval-check').prop('checked', false);
          $('.btn-recurring-interval').removeClass('active');
          label.addClass('active');
          checkbox.prop('checked', true);
          $('input[id$=_donor_selected_recurring_interval]').attr('value', checkbox.data('interval'));
          $('.footer-total .first-interval').text(humanized_interval);
          dl_user_event.action = 'on';
          return new App.DataLayer().push(dl_user_event);
        } else {
          $('input[id$=_donor_selected_recurring_interval]').attr('value', '');
          $('.footer-total .first-interval').text('now');
          dl_user_event.action = 'off';
          checkbox.parent().tooltip('hide');
          return new App.DataLayer().push(dl_user_event);
        }
      });
      $('input[data-name=recurring]').on('click', function(e) {
        var dl_user_event, interval, parent;
        if ($(e.target).is('input')) {
          parent = $(e.target).parents('label');
          $(parent.find('.subscription-fixed-text')[0]).removeClass('prechecked');
          if (!parent.hasClass('btn-recurring-no-style-update')) {
            if (parent.attr('style').length === 0) {
              parent.attr('style', parent.data('data'));
            } else {
              parent.data('style', parent.attr('style'));
              parent.attr('style', '');
            }
          }
          interval = $('input[data-name=recurring]').data('humanized-interval');
          dl_user_event = returnUserEventObj();
          dl_user_event.recurringInterval = interval;
          dl_user_event.label = 'recurring button toggle';
          if ($(e.target).is(':checked')) {
            $('.footer-total .first-interval').text(interval);
            dl_user_event.action = 'on';
            return new App.DataLayer().push(dl_user_event);
          } else {
            $('.footer-total .first-interval').text('now');
            dl_user_event.action = 'off';
            return new App.DataLayer().push(dl_user_event);
          }
        }
      });
      $('input[data-name=additional_recurring]').on('click', function(e) {
        var dl_user_event, interval, parent;
        if ($(e.target).is('input')) {
          parent = $(e.target).parents('label');
          if (parent.attr('style').length === 0) {
            parent.attr('style', parent.data('data'));
          } else {
            parent.data('style', parent.attr('style'));
            parent.attr('style', '');
          }
          interval = $('input[data-name=additional_recurring]').data('humanized-interval');
          dl_user_event = returnUserEventObj();
          dl_user_event.label = 'recurring2 button toggle';
          dl_user_event.recurringInterval = interval;
          if ($(e.target).is(':checked')) {
            $('.additional-recurring-total').show();
            $('.footer-total .second-interval').text(interval);
            dl_user_event.action = 'on';
            return new App.DataLayer().push(dl_user_event);
          } else {
            $('.additional-recurring-total').hide();
            $('.footer-total .second-interval').text('');
            dl_user_event.action = 'off';
            return new App.DataLayer().push(dl_user_event);
          }
        }
      });
      $('input.money-bomb-check-box').on('click', function(e) {
        var callout_id, date, dl_user_event, element, for_id, moneyPledgeID, parent;
        if ($(e.target).is('input')) {
          parent = $(e.target).parents('label');
          $(parent.find('.money-bomb-process')[0]).removeClass('prechecked');
          if (parent.attr('style').length === 0) {
            parent.attr('style', parent.data('data'));
          } else {
            parent.data('style', parent.attr('style'));
            parent.attr('style', '');
          }
          for_id = parent.attr('for');
          callout_id = for_id + '-callout';
          element = $('#' + callout_id);
          if (element.length === 0) {
            element = $('<div id="' + callout_id + '"></div>').append(' + ').append('<span class="donation-amount-value">$' + $('#donation_amount').val() || 0 + '</span>').append(' on ').append('<span class="money-bomb-date"></span>').appendTo('.money-bomb-total');
          }
          element.hide();
          moneyPledgeID = $('.flex-column input[type=hidden]', parent).val();
          dl_user_event = returnUserEventObj();
          dl_user_event.moneyPledgeID = moneyPledgeID;
          dl_user_event.label = 'pledge button toggle';
          if (parent.hasClass('additional-money-bomb-label')) {
            dl_user_event.label = 'pledge2 button toggle';
          }
          if ($(e.target).is(':checked')) {
            $('.money-bomb-total').show();
            date = parent.find('input.money-bomb-check-box').data('action-date');
            element.find('.money-bomb-date').text(date);
            element.show();
            dl_user_event.action = 'on';
            return new App.DataLayer().push(dl_user_event);
          } else {
            dl_user_event.action = 'off';
            return new App.DataLayer().push(dl_user_event);
          }
        }
      });
      $('.btn-shipping').on('click', function() {
        return setTimeout((function() {
          applyValidationsToNameFields();
          return applyValidationsToAddressField();
        }), 100);
      });
      updateCartData = function(is_user) {
        var amt, val;
        val = $('input.donation-amount-value').val() || $('input.conduit-amount-value').val();
        amt = val || 0;
        return {
          email: $("[id$='_email']").val(),
          subscribed: $("[id$='_subscribed']").prop('checked') || false,
          organization_uid: $("[name='organization-uid']").attr('content'),
          amount_cents: parseFloat(amt) * 100,
          rvid: Cookies.get('rvid'),
          quantity: $("[id$='_quantity']").val() || 1,
          source_url: $("[id$='_source_url']").val(),
          page_uid: $("[name='page-uid']").attr('content'),
          is_user: is_user,
          first_name: $("[id$='_first_name']").val() || "",
          last_name: $("[id$='_last_name']").val() || "",
          user_agent: navigator.userAgent,
          referrer: $("[id$='_referer']").val(),
          source_code: $("[id$='_source_code']").val(),
          bundler_id: $("[id$='_bundler_id']").val(),
          mobile: $("[id$='_mobile_number']").val()
        };
      };
      postUpdateCartData = function(is_user) {
        if ($('meta[name=update-cart-domain]').attr('content') !== void 0 && $('meta[name=update-cart-domain]').attr('content').length > 0) {
          return $.ajax({
            url: $('meta[name=update-cart-domain]').attr('content'),
            dataType: 'JSON',
            xhrFields: {
              withCredentials: true
            },
            data: updateCartData(is_user),
            type: 'POST',
            success: function(response) {},
            error: function(response, error) {}
          });
        }
      };
      getBlockedDonorCheck = function(email, org_ids) {
        return $.ajax({
          url: $('meta[name=blocked-donor-check-domain]').attr('content') + '?email=' + encodeURIComponent(email) + '&org_ids=' + org_ids,
          headers: {
            'X-Revv-Apikey': $('meta[name=blocked-donor-lookup-api-key]').data('key'),
            'X-CSRF-Token': $('meta[name=csrf-token]').attr('content') || $(this).data('csrf-token')
          },
          dataType: 'JSON',
          xhrFields: {
            withCredentials: true
          },
          type: 'GET',
          success: function(response) {
            var blocked_revv_uids, candidates_on_page, cc_div, cc_div_org_revv_uid, cc_index_div, donor_is_blocked, form, is_multiple_candidate_page, j, len, org_id, state_level_single_organization_page;
            if (response) {
              blocked_revv_uids = response.blocked;
              form = $('#main-form form');
              cc_index_div = $('#conduit-candidate-index');
              candidates_on_page = cc_index_div.find('.conduit-candidate');
              is_multiple_candidate_page = candidates_on_page.length > 1;
              state_level_single_organization_page = candidates_on_page.length === 0;
              donor_is_blocked = false;
              if (state_level_single_organization_page) {
                org_id = $('meta[name=organization-uid]').attr('content');
                if (indexOf.call(blocked_revv_uids, org_id) >= 0) {
                  donor_is_blocked = true;
                  form.append('<input type="hidden" name="donation[state-level-donor-blocked]" value="true"></input>');
                }
              } else {
                for (j = 0, len = candidates_on_page.length; j < len; j++) {
                  cc_div = candidates_on_page[j];
                  cc_div_org_revv_uid = $(cc_div).data('org-revv-uid');
                  if (cc_div_org_revv_uid && blocked_revv_uids.includes(cc_div_org_revv_uid)) {
                    donor_is_blocked = true;
                    $(cc_div).data('donor-blocked', true);
                    if (is_multiple_candidate_page) {
                      $(cc_div).css('color', 'red');
                      $(cc_div).find('input').css('color', 'red');
                      $(cc_div).find('.blocked-donor-message').append('<br /><br />Thanks for your interest in donating. We cannot accept your donation at this time. Change to 0 to continue.');
                    }
                  } else {
                    $(cc_div).data('donor-blocked', false);
                    $(cc_div).css('color', '#354B70');
                    $(cc_div).find('input').css('color', '#354B70');
                    $(cc_div).find('.blocked-donor-message').empty();
                  }
                }
              }
              if (donor_is_blocked) {
                form.find('.conduit-edit-button').remove();
                if (is_multiple_candidate_page) {
                  return cc_index_div.removeClass('d-none');
                } else {
                  return $('.single-candidate-donor-blocked-error').empty().append('Thanks for your interest in donating. We cannot accept your donation at this time.<br /><br />');
                }
              }
            }
          },
          error: function(response, error) {}
        });
      };
      createOrgIdsParam = function() {
        var candidates_on_page, cc_div, cc_index_div, form, j, len, org_ids, state_level_single_organization_page;
        form = $('#main-form form');
        cc_index_div = $('#conduit-candidate-index');
        candidates_on_page = cc_index_div.find('.conduit-candidate');
        state_level_single_organization_page = candidates_on_page.length === 0;
        if (state_level_single_organization_page) {
          return $('meta[name=organization-uid]').attr('content');
        } else {
          org_ids = [];
          for (j = 0, len = candidates_on_page.length; j < len; j++) {
            cc_div = candidates_on_page[j];
            org_ids.push($(cc_div).data('org-revv-uid'));
          }
          return org_ids.toString();
        }
      };
      $("input[name*=mobile_number]").on("keyup", function() {
        var optin_checkbox, validator;
        validator = $(this).closest("form").validate();
        optin_checkbox = $("input[name*=sms_enabled]");
        if (!optin_checkbox.is(':checked')) {
          if ($(this).val().length > 0 && validator.element($(this))) {
            return optin_checkbox.prop("disabled", false);
          } else {
            optin_checkbox.prop("checked", false).prop("disabled", true);
            return optin_checkbox.closest("label").removeClass("active");
          }
        }
      });
      return (function() {
        var additionalOptions, css_override, custom_amount_upsell_amount, datalayerOnloadObj, existingProfile, segment, updateBooleanUrlParam;
        if ($('.payment-selector').length !== 0) {
          if ($('.payment-selector').find('#ach.active').length > 0) {
            App.achActive = true;
          }
          $('.payment-selector').on('click', '.nav-link', function() {
            var target;
            target = $(this).data('target');
            if (target === '#ach') {
              App.achActive = true;
              $("input[id*='_routing_number'], input[id*='_account_number'], input[id*='_account_number_confirm']").each(function() {
                $(this).val('');
                return $(this).attr('required', 'required');
              });
              card.clear();
            } else {
              App.achActive = false;
              $("input[id*='_routing_number'], input[id*='_account_number'], input[id*='_account_number_confirm']").each(function() {
                $(this).val('');
                return $(this).attr('required', false);
              });
              $("input[id*='_ach_explicit_acceptance']").prop("checked", false);
              App.submitButtonValidator();
            }
            $('.payment-selector').find('.payment-option:not(' + target + ')').removeClass('active');
            $('.payment-selector').find(target).addClass('active');
            $('.payment-selector').find('.nav-link').not(this).removeClass('active');
            return $(this).addClass('active');
          });
        }
        $('input[data-name=m_optin]').on('click', function() {
          if ($.urlParam('m_optin') && $.urlParam('m_optin').length) {
            updateBooleanUrlParam('m_optin', this);
            return $(this).parents('form').attr('action', newUrl);
          }
        });
        $('input[data-name=recurring]').on('click', function() {
          updateBooleanUrlParam('recurring', this);
          return App.updateProgressBarAmount();
        });
        $('input[data-name=additional_recurring]').on('click', function() {
          updateBooleanUrlParam('additional_recurring', this);
          return App.updateProgressBarAmount();
        });
        $('input.money-bomb-check-box').on('click', function() {
          var newUrl;
          newUrl = window.location.href.replace('money_bomb', 'money_pledge');
          window.history.replaceState('', '', newUrl);
          if (!this.classList.contains('additional-money-bomb-check-box')) {
            return updateBooleanUrlParam('money_pledge', this);
          }
        });
        updateBooleanUrlParam = function(param, el) {
          var newUrl;
          newUrl = $.updateURLParameter(window.location.href, param, el.checked);
          window.history.replaceState('', '', newUrl);
          return $(el).parents('form').attr('action', newUrl);
        };
        if ($('body.landing-page:not(.thanks)').length) {
          datalayerOnloadObj = {
            'event': app_platform + "Event",
            'category': App.dataLayerCategory(),
            'action': 'user session start',
            'label': 'landing page settings',
            'campaignSegment': void 0,
            'customCSS': 'f',
            'suppressTransactionID': true
          };
          segment = $('.campaign-segment').val();
          if (segment === "none" || segment === "CURRENT_CAMPAIGN_SEGMENT") {
            segment = void 0;
          }
          if (segment) {
            datalayerOnloadObj.campaignSegment = segment;
          }
          if ($('.rv-entry-mode-true').length !== 0) {
            localStorage.removeItem('__donorProfile');
            existingProfile = void 0;
          } else {
            existingProfile = localStorage.getItem('__donorProfile');
          }
          datalayerOnloadObj.userCategory = 'anonymous';
          if (existingProfile && JSON.parse(existingProfile)['firstname']) {
            datalayerOnloadObj.userCategory = 'temporary profile';
          }
          if ($('.logged-in-landing-page-personal-details').length) {
            datalayerOnloadObj.userCategory = 'logged in';
          }
          css_override = $('.rv-custom-css-true').length;
          if (css_override) {
            datalayerOnloadObj.customCSS = 't';
          }
          new App.DataLayer().push(datalayerOnloadObj);
        }
        $('body').on('click', '#landing-page-logout', function(e) {
          return localStorage.removeItem('__donorProfile');
        });
        if ($('.upsell-page-custom-amount-upsell').length > 0 && $('.matching-multiplier').length > 0) {
          custom_amount_upsell_amount = $('.card-donation-amount').val();
          $('.matching-multiplier-amount.donation-amount-value').text(custom_amount_upsell_amount).formatCurrency({
            roundToDecimalPlace: 2,
            removeTrailingZerosOnDecimal: true
          });
          App.updateMatchAmount(custom_amount_upsell_amount);
        }
        if ($('form[data-without-card=true]').length === 0 && $('.upsell-panel').length === 0) {
          $('input.donation-amount-value, input.conduit-amount-value').on('paymentRequestChange', function(e) {
            var val;
            val = $('input.donation-amount-value').val() || $('input.conduit-amount-value').val();
            if (val) {
              return postUpdateCartData(true);
            }
          });
          $("[id$='_subscribed']").on('change', function(e) {
            var val;
            val = $('input.donation-amount-value').val() || $('input.conduit-amount-value').val();
            if (val) {
              return postUpdateCartData(true);
            }
          });
        }
        if ($('form[data-without-card=true]').length > 0 && $('meta[name=sso-api-domain]').attr('content')) {
          additionalOptions = {};
          if (App.useSameSiteNone()) {
            additionalOptions['sameSite'] = 'none';
          }
          Cookies.set('origin_url', window.location.href, $.extend({
            secure: true
          }, additionalOptions));
          $.ajax({
            url: $('meta[name=sso-api-domain]').attr('content'),
            dataType: 'JSON',
            xhrFields: {
              withCredentials: true
            },
            type: 'GET',
            success: function(response, status, xhr) {
              var sso_tries;
              if (Cookies.get('sso_tries') === void 0) {
                Cookies.set('sso_tries', 1, $.extend({
                  secure: true
                }, additionalOptions));
              }
              sso_tries = parseInt(Cookies.get('sso_tries'));
              if (response && response.data && response.data.attributes && response.data.attributes.login_url && sso_tries <= 3) {
                sso_tries += 1;
                Cookies.set('sso_tries', sso_tries, $.extend({
                  secure: true
                }, additionalOptions));
                window.location.href = response.data.attributes.login_url;
              }
              if (response && response.data && response.data.attributes && response.data.attributes.rvid) {
                return Cookies.set('rvid', response.data.attributes.rvid, $.extend({
                  expires: 1825,
                  secure: true
                }, additionalOptions));
              }
            }
          });
          $('.abandoned-cart-trigger').on('blur', function(e) {
            var val;
            val = $('input.donation-amount-value').val() || $('input.conduit-amount-value').val();
            if ($('.abandoned-cart-trigger').valid() && val) {
              return postUpdateCartData(false);
            }
          });
          $('.email-donor-check-trigger').on('blur', function(e) {
            var blocked_donor_check_required;
            blocked_donor_check_required = $('meta[name=blocked-donor-check-required]').attr('content');
            if ($('.email-donor-check-trigger').valid() && blocked_donor_check_required === 'true') {
              return getBlockedDonorCheck($('.email-donor-check-trigger').val(), createOrgIdsParam());
            }
          });
        }
        $('.home-address').on('change', 'input[id$=_zip]', function(e) {
          var $form, form_validator;
          $form = $(this).parents('form');
          form_validator = $form.validate();
          if (App.currentStripeCard && form_validator.element(this)) {
            if ($('meta[name="organization-country"]').attr('content') !== 'CA') {
              App.currentStripeCard.update({
                value: {
                  postalCode: $(this).val()
                }
              });
            }
          }
          if (App.moovCardLink && form_validator.element(this)) {
            return App.moovCardLink.billingAddress = {
              postalCode: $(this).val()
            };
          }
        });
        App.validateCheckedByDefault();
        App.updateDonationAmount($('.donation-amount-value'));
        return document.addEventListener('initiatives-statistics-loaded', function() {
          return App.updateDonationAmount($('.donation-amount-value'));
        });
      })();
    };

    return LandingPage;

  })();

}).call(this);
(function() {
  App.LeadsReporting = (function() {
    function LeadsReporting() {}

    LeadsReporting.prototype.setup = function() {
      return $('.sources-select-dropdown').select2({
        theme: "bootstrap",
        containerCssClass: 'sources-select-container'
      });
    };

    return LeadsReporting;

  })();

}).call(this);
(function() {
  App.MobilePhoneUpsell = (function() {
    function MobilePhoneUpsell() {}

    MobilePhoneUpsell.prototype.setup = function() {
      return $("#mobile-edit").on("click", function() {
        $(this).closest(".upsell-page-form").find(".mobile-phone-upsell-field").removeClass("hidden-xs-up");
        return $(this).closest(".rv-mobile-phone-details").hide();
      });
    };

    return MobilePhoneUpsell;

  })();

}).call(this);
(function() {
  App.Modal = (function() {
    function Modal() {}

    Modal.prototype.setup = function() {
      return $(document).ready(function() {
        var revv_location, revv_location_text;
        if (Cookies.get('cookies-accepted') !== 'true') {
          revv_location_text = $('meta[name=revv-location]').attr('content');
          if (revv_location_text) {
            revv_location = JSON.parse(revv_location_text);
          }
          if ((revv_location && revv_location.region === 'Quebec') || $.urlParam('quebec') === 'true') {
            $('#cookies-modal').modal('show');
          }
          return $('#accept-cookies').click(function(e) {
            return Cookies.set('cookies-accepted', 'true', {
              expires: 365
            });
          });
        }
      });
    };

    return Modal;

  })();

}).call(this);
(function() {
  App.Onboarding = (function() {
    function Onboarding() {}

    Onboarding.prototype.setup = function() {
      window.onboardingFormSubmitRecaptchaSuccess = function() {
        return App.currentOnboardingFormEvent[0].submit();
      };
      window.onboardingFormSubmitRecaptchaError = function() {
        return console.log('reCAPTCHA errored');
      };
      if ($('body').find('form.recaptcha').length === 1) {
        $('form').on('submit', function(event) {
          $(this).data().showingCaptcha = true;
          App.currentOnboardingFormEvent = [this, event];
          grecaptcha.execute();
          return event.preventDefault();
        });
      }
      $('#account_creation_organization_type').on('change', function(e) {
        if ($('input.win_red_candidate').val()) {
          if ($(this).find("option:selected").text() === 'State Level') {
            $('#account_creation_organization_state').parent().removeClass('hidden-xs-up');
            $('.fec-registration-id-group').addClass('hidden-xs-up');
            return $('.legal-entity-group').removeClass('hidden-xs-up');
          } else {
            $('#account_creation_organization_state').parent().addClass('hidden-xs-up');
            $('.fec-registration-id-group').removeClass('hidden-xs-up');
            return $('.legal-entity-group').addClass('hidden-xs-up');
          }
        } else {
          if ($(this).find("option:selected").text().indexOf('Political') > -1) {
            $('.fec-registration-id-group').removeClass('hidden-xs-up');
            $('.fec-registration-id-group').find('input').prop('required', true);
          } else {
            $('.fec-registration-id-group').addClass('hidden-xs-up');
            $('.fec-registration-id-group').find('input').prop('required', false);
          }
          if (($(this).find("option:selected").val() === 'for-profit') || ($(this).find("option:selected").val() === 'nonprofit')) {
            $('.country-group').removeClass('hidden-xs-up');
            return $('.country-group').find('select').prop('required', true);
          } else {
            $('.country-group select').val('');
            $('.country-group').addClass('hidden-xs-up');
            $('.country-group').find('select').prop('required', false);
            $('.classification-group select').val('');
            $('.classification-group').addClass('hidden-xs-up');
            return $('.classification-group').find('select').prop('required', false);
          }
        }
      });
      $('#account_creation_organization_country').on('change', function(e) {
        if ($(this).find("option:selected").val() === 'CA') {
          $('.country-group').removeClass('hidden-xs-up');
          $('.classification-group').removeClass('hidden-xs-up');
          return $('.classification-group').find('select').prop('required', true);
        } else {
          $('.classification-group select').val('');
          $('.classification-group').addClass('hidden-xs-up');
          return $('.classification-group').find('select').prop('required', false);
        }
      });
      return $('#account_creation_organization_country').trigger('change');
    };

    return Onboarding;

  })();

}).call(this);
(function() {
  App.Prototype = (function() {
    function Prototype() {}

    Prototype.prototype.setup = function() {
      String.prototype.dasherize = function() {
        return this.replace(/[A-Z]/g, function(char, index) {
          return (index !== 0 ? '-' : '') + char.toLowerCase();
        });
      };
      String.prototype.underscorize = function() {
        return this.replace(/[A-Z]/g, function(char, index) {
          return (index !== 0 ? '_' : '') + char.toLowerCase();
        });
      };
      String.prototype.capitalize = function() {
        return this.charAt(0).toUpperCase() + this.slice(1);
      };
      String.prototype.titleize = function() {
        var string_array;
        string_array = this.split(' ');
        string_array = string_array.map(function(str) {
          return str.capitalize();
        });
        return string_array.join(' ');
      };
      $.fn.clear_form_errors = function() {
        this.find('.form-group').removeClass('has-error');
        this.find('span.help-block').remove();
        return this.find('.errors').remove();
      };
      $.fn.clear_form_fields = function() {
        return this.find(':input', '#myform').not(':button, :submit, :reset, :hidden').val('').removeAttr('checked').removeAttr('selected');
      };
      $.fn.render_form_errors = function(errors) {
        var form;
        form = this;
        this.clear_form_errors();
        $(form).find('.form-group').first().prepend('<ul class="errors"></ul>');
        return $.each(errors, function(field, messages) {
          var input;
          input = form.find('input, select, textarea').filter(function() {
            var name;
            name = $(this).attr('name');
            if (name) {
              return name.match(new RegExp('\\[' + field + '\\(?'));
            }
          });
          if (input.length !== 0) {
            input.closest('.form-group').addClass('has-error');
            return input.parent().append('<span class="help-block">' + messages.join('<br />').capitalize() + '</span>');
          } else {
            return $(form).find('.errors').append("<li>" + field.capitalize() + ' ' + messages.join('<br />') + '</li>');
          }
        });
      };
      $.fn.capitalize = function() {
        return $.each(this, function() {
          var caps;
          caps = this.value;
          caps = caps.charAt(0).toUpperCase() + caps.slice(1);
          return this.value = caps;
        });
      };
      $.fn.isInViewport = function() {
        var elementBottom, elementTop, viewportBottom, viewportTop;
        elementTop = $(this).offset().top;
        elementBottom = elementTop + $(this).outerHeight();
        viewportTop = $(window).scrollTop();
        viewportBottom = viewportTop + $(window).height();
        return elementBottom > viewportTop && elementTop < viewportBottom;
      };
      return $('[data-remote=true]').on("ajax:success", function(e, data, status, xhr) {
        if (data['redirect_url']) {
          return window.location.href = data['redirect_url'];
        }
      }).on("ajax:error", function(e, data, status, xhr) {
        return $(this).render_form_errors(data.responseJSON);
      });
    };

    return Prototype;

  })();

}).call(this);
(function() {
  App.Ransack = (function() {
    function Ransack() {}

    Ransack.prototype.setup = function() {
      return $('[data-toggle="ransack"][data-target]').on('change', function() {
        return $($(this).data('target')).attr('name', 'q[' + this.value + ']');
      });
    };

    return Ransack;

  })();

}).call(this);
(function() {
  App.ReclaimAccounts = (function() {
    function ReclaimAccounts() {}

    ReclaimAccounts.prototype.setup = function() {
      var tab_name;
      tab_name = "#donations";
      return $(".skip-btn").on("click", function(e) {
        e.preventDefault();
        window.location.hash = tab_name;
        return $(".nav-pills").find("a[href='" + tab_name + "']").tab("show");
      });
    };

    return ReclaimAccounts;

  })();

}).call(this);
(function() {
  App.SendGridEmailVerification = (function() {
    function SendGridEmailVerification() {}

    SendGridEmailVerification.prototype.setup = function() {
      var api_key, csrf_token, url;
      url = $('meta[name=sendgrid-email]').attr('content');
      csrf_token = $('meta[name=csrf-token]').attr('content');
      api_key = $('meta[name=api-key]').attr('content');
      if (!url || !csrf_token || !api_key) {
        return;
      }
      return $('input[data-name=email]').on('blur', function(e) {
        var email, verification_email, verification_tries;
        email = $(this).val();
        if (!email) {
          return;
        }
        verification_email = Cookies.get('verification_email');
        if (verification_email === email) {
          return;
        }
        verification_tries = parseInt(Cookies.get('verification_tries'));
        if (verification_tries >= 3) {
          $('.email-validation-message').remove();
          return;
        }
        return $.ajax({
          url: url,
          headers: {
            'X-Revv-Apikey': api_key,
            'X-CSRF-Token': csrf_token
          },
          data: {
            email: email
          },
          dataType: 'JSON',
          xhrFields: {
            withCredentials: true
          },
          type: 'POST',
          success: function(response) {
            var additionalOptions, inFiveMinutes, message, wrapper;
            $('.email-validation-message').remove();
            if (response) {
              additionalOptions = {};
              if (App.useSameSiteNone()) {
                additionalOptions['sameSite'] = 'none';
              }
              inFiveMinutes = new Date(new Date().getTime() + 5 * 60 * 1000);
              if (Cookies.get('verification_tries') === void 0) {
                Cookies.set('verification_tries', 1, $.extend({
                  expires: inFiveMinutes,
                  secure: true
                }, additionalOptions));
              } else {
                verification_tries += 1;
                Cookies.set('verification_tries', verification_tries, $.extend({
                  expires: inFiveMinutes,
                  secure: true
                }, additionalOptions));
              }
              Cookies.set('verification_email', email, $.extend({
                expires: inFiveMinutes,
                secure: true
              }, additionalOptions));
              if (response.verdict === 'Valid' && response.suggestion === void 0) {
                $('.email-validation-message').remove();
                return;
              }
              if (response.suggestion) {
                message = '<a href="#" class="email-suggestion"><div class="copy">Did you mean ' + response.suggestion + '?</div><div class="badge badge-danger">Click to replace</div></a>';
              }
              if (response.verdict !== 'Valid' && response.suggestion === void 0) {
                message = '<div class="email-suggestion"><span class="badge badge-danger">Invalid email - are you sure it\'s correct?</span></div>';
              }
              if (!message) {
                return;
              }
              wrapper = '<div class="email-validation-message form-control">' + message + '</div>';
              if ($('.email-validation-message').length) {
                $('.email-validation-message').replaceWith(wrapper);
              } else {
                $('.email').append(wrapper);
              }
              return $('a.email-suggestion').on('click', function(e) {
                e.preventDefault();
                $('input[data-name=email]').val(response.suggestion);
                return $('.email-validation-message').remove();
              });
            }
          }
        });
      });
    };

    return SendGridEmailVerification;

  })();

}).call(this);
(function() {
  App.StyleGuide = (function() {
    function StyleGuide() {}

    StyleGuide.prototype.setup = function() {
      var active_link, active_tab, target;
      target = window.location.hash;
      if (target === '' || target.includes('-')) {
        active_link = $('.style-guide-sidebar .style-guide-sidebar__nav-link')[0];
        $(active_link).addClass('style-guide-sidebar__nav-item--active');
        window.location.hash = active_link.hash;
      } else {
        $('[href="' + target + '"]').addClass('style-guide-sidebar__nav-item--active');
      }
      $('.style-guide-sidebar .style-guide-sidebar__nav-link').click(function(e) {
        $('.style-guide-sidebar__nav-item--active').removeClass('style-guide-sidebar__nav-item--active');
        return $(e.target).addClass('style-guide-sidebar__nav-item--active');
      });
      active_tab = $('.nav-tabs a.active').removeClass('active');
      $(active_tab).parent().addClass('revv-nav__nav-item--active');
      $('.nav-tabs a').click(function(e) {
        e.preventDefault();
        $('.revv-nav__nav-item--active').removeClass('revv-nav__nav-item--active');
        $(this).tab('show');
        $(this).removeClass('active');
        return $(this).parent().addClass('revv-nav__nav-item--active');
      });
      $('.revv-drawer').parent().each(function(i, el) {
        var id;
        id = '#' + el.id;
        return $(id).collapse();
      });
      $('.js-drawer-open').click(function(e) {
        var id;
        id = $(this).data('drawer');
        return $(id).collapse('show');
      });
      return $('.js-drawer-close').click(function(e) {
        var id;
        id = $(this).data('drawer');
        return $(id).collapse('hide');
      });
    };

    return StyleGuide;

  })();

}).call(this);
(function() {
  App.Typeahead = (function() {
    function Typeahead() {}

    Typeahead.prototype.setup = function() {
      var campaigns, url;
      url = $('.typeahead').data('url');
      campaigns = new Bloodhound({
        datumTokenizer: Bloodhound.tokenizers.whitespace,
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        remote: {
          url: url
        }
      });
      $('.campaign-typeahead.typeahead').typeahead({
        highlight: true,
        autoselect: true,
        minLength: 3,
        classNames: {
          menu: 'tt-menu card',
          suggestion: 'tt-suggestion p-3'
        }
      }, {
        source: campaigns,
        displayKey: 'name'
      });
      $('.campaign-typeahead.typeahead').on('typeahead:select', function(event, suggestion, dataset) {
        return $('.campaign-revv-uid').val(suggestion.revv_uid);
      });
      $('.typeahead-badge').on('click', function(event) {
        $(this).removeClass('d-flex').addClass('d-none');
        return $('.typeahead-input').removeClass('d-none');
      });
      return $('.typeahead-input input').on('blur', function(event) {
        if ($(this).val().trim() === '') {
          return $('.campaign-revv-uid').val('');
        }
      });
    };

    return Typeahead;

  })();

}).call(this);
(function() {
  App.UserAgentChecker = (function() {
    function UserAgentChecker() {}

    UserAgentChecker.prototype.setup = function() {
      App.userAgent = '';
      App.useSameSiteNone = function() {
        App.userAgent = UAParser();
        if (App.userAgent === '') {
          return true;
        } else {
          return !App.missing_same_site_none_support();
        }
      };
      App.missing_same_site_none_support = function() {
        return App.has_webkit_ss_bug() || App.drops_unrecognized_ss_cookies();
      };
      App.has_webkit_ss_bug = function() {
        return App.is_ios_version('12') || (App.is_mac_osx_version('10', '14') && App.is_safari());
      };
      App.drops_unrecognized_ss_cookies = function() {
        return App.is_buggy_chrome() || App.is_buggy_uc();
      };
      App.is_ios_version = function(major) {
        var splits;
        splits = App.userAgent.os.version ? App.userAgent.os.version.split('.') : [];
        return App.userAgent.os.name === 'iOS' && splits[0] === major;
      };
      App.is_mac_osx_version = function(major, minor) {
        var splits;
        splits = App.userAgent.os.version ? App.userAgent.os.version.split('.') : [];
        return App.userAgent.os.name === 'Mac OS' && splits[0] === major && splits[1] === minor;
      };
      App.is_safari = function() {
        return App.userAgent.ua.match(new RegExp(/Safari/));
      };
      App.is_buggy_chrome = function() {
        return App.is_chromium_based() && App.is_chromium_version_between(51, 67);
      };
      App.is_buggy_uc = function() {
        return App.is_uc_browser() && !App.is_uc_version_at_least(12, 13, 2);
      };
      App.is_chromium_based = function() {
        return App.userAgent.ua.match(new RegExp(/Chrom(e|ium)/));
      };
      App.is_chromium_version_between = function(min, max) {
        var match, version;
        match = App.userAgent.ua.match(new RegExp(/Chrom[^\/]+\/(\d+)[\.\d]*/));
        if (!match) {
          return false;
        }
        version = match[1].to_i;
        return min >= version && version <= max;
      };
      App.is_uc_browser = function() {
        return App.userAgent.family === 'UC Browser';
      };
      return App.is_uc_version_at_least = function(major, minor, build) {
        var splits, version_build, version_major, version_minor;
        splits = App.userAgent.os.version.split('.');
        version_major = splits[0];
        version_minor = splits[0];
        version_build = splits[0];
        if (parseInt(version_major) === major) {
          if (parseInt(version_minor) === minor) {
            return parseInt(version_build) >= build;
          } else {
            return parseInt(version_minor) > minor;
          }
        } else {
          return parseInt(version_major) > major;
        }
      };
    };

    return UserAgentChecker;

  })();

}).call(this);
(function() {
  App.UserOnboarding = (function() {
    function UserOnboarding() {}

    UserOnboarding.prototype.setup = function() {
      var testAlphabeticCharactersOnly;
      testAlphabeticCharactersOnly = function(event) {
        var pattern, value;
        value = String.fromCharCode(event.which);
        pattern = new RegExp(/[a-zåäö\- ]/i);
        return pattern.test(value);
      };
      return $('#user_registration_form_first_name, #user_registration_form_last_name').bind('keypress', testAlphabeticCharactersOnly);
    };

    return UserOnboarding;

  })();

}).call(this);
(function() {
  App.VirtualTerminal = (function() {
    function VirtualTerminal() {}

    VirtualTerminal.prototype.setup = function() {
      var card, elements, locale, stripe, supported_locales;
      if ($("meta[name=stripe-publishable-key]").length) {
        stripe = Stripe($("meta[name=stripe-publishable-key]").attr('content'));
        locale = $("meta[http-equiv=content-language]").attr('content');
        supported_locales = ['en'];
        locale = 'en';
        elements = stripe.elements({
          locale: locale
        });
        App.currentStripeCard = card = elements.create('card', {
          classes: {
            base: 'inspectlet-sensitive'
          },
          style: {
            base: {
              iconColor: '#697A8B',
              color: '#354B70',
              lineHeight: '24px',
              fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
              fontSmoothing: 'antialiased',
              fontSize: '15px',
              fontWeight: '600',
              '::placeholder': {
                color: '#697A8B'
              }
            },
            invalid: {
              color: '#ff4d55',
              '::placeholder': {
                color: '#ff4d55'
              },
              iconColor: '#ff4d55'
            }
          }
        });
        card.mount('#card-element');
      }
      return $('#new_create_charge_form').on('submit', function(event) {
        var address, card_details, data, form;
        form = $(this);
        event.preventDefault();
        if ($("meta[name=stripe-publishable-key]").length) {
          address = $('.billing-address')[0] || $('.home-address')[0];
          card_details = {
            name: $("[id$='_first_name']").val() + ' ' + $("[id$='_last_name']").val(),
            address_line1: $("[id$='_address']").val(),
            address_zip: $("[id$='_postal_code']").val(),
            address_city: $("[id$='_city']").val(),
            address_state: $("[id$='_state']").val()
          };
          return stripe.createToken(App.currentStripeCard, card_details).then(function(result) {
            if (result.error) {
              console.log(result.error);
              return alert(result.error);
            } else {
              $("[id$='_stripe_card_token']").val(result.token.id);
              return form[0].submit();
            }
          });
        } else if ($('meta[name=donor-endpoint]').length) {
          console.log('executed moov virtual terminal');
          data = {
            email: $("[id$='_email']").val(),
            first_name: $("[id$='_first_name']").val() || "",
            last_name: $("[id$='_last_name']").val() || ""
          };
          return $.ajax({
            url: $('meta[name=donor-endpoint]').attr('content'),
            headers: {
              'X-Revv-Apikey': $("meta[name=api-key]").attr('content'),
              'X-CSRF-Token': $("meta[name=donor-endpoint-token]").attr('content')
            },
            dataType: 'JSON',
            xhrFields: {
              withCredentials: true
            },
            data: data,
            type: 'POST',
            success: function(response) {
              var card_input;
              card_input = document.querySelector('moov-card-link');
              card_input.setAttribute('account-id', response.account_id);
              $("[id$='_moov_account_id']").val(response.account_id);
              card_input.setAttribute('oauth-token', response.account_token);
              card_input.onSuccess = function(data) {
                $("[id$='_moov_account_card_id']").val(data.cardID);
                return form[0].submit();
              };
              card_input.onError = function(err) {
                console.log(err);
                return alert(err);
              };
              return card_input.submit();
            },
            error: function(response, error) {
              console.log(response, error);
              return alert(response);
            }
          });
        }
      });
    };

    return VirtualTerminal;

  })();

}).call(this);
(function() {
  App.LandingPage.ConduitPage = (function() {
    function ConduitPage() {}

    ConduitPage.prototype.setup = function() {
      App.selectedConduitAmount = 0;
      App.moduloAmount = function(n, m) {
        var d_0, d_1, v_0;
        v_0 = Math.floor(n / m);
        d_0 = n % m;
        d_1 = m - d_0;
        return {
          v_0: v_0 + 1,
          d_0: d_0,
          v_1: v_0,
          d_1: d_1
        };
      };
      App.splitEvenlyAmount = function(split_evenly, modulo_amount_cents, total_amount_cents, percentage) {
        var return_amount_cents;
        return_amount_cents = modulo_amount_cents;
        if (!split_evenly) {
          return_amount_cents = parseInt(percentage * total_amount_cents);
        }
        return App.toFloatDollars(return_amount_cents);
      };
      App.checkMaximumContribution = function(item, dollar_value) {
        var dollar_value_cents, maximum_contribution_cents, return_dollar_value;
        if (App.contributorType === void 0 || App.contributorType === 'personal') {
          maximum_contribution_cents = $(item).data('maximum-contribution-cents');
        } else {
          maximum_contribution_cents = App.getMaximumContribution() * 100;
        }
        dollar_value_cents = App.toIntCents(dollar_value);
        return_dollar_value = dollar_value;
        if (maximum_contribution_cents > 0 && dollar_value_cents > maximum_contribution_cents) {
          return_dollar_value = App.toFloatDollars(maximum_contribution_cents);
          $(item).attr('maxed', 'true');
        } else {
          $(item).attr('maxed', 'false');
        }
        return return_dollar_value;
      };
      App.updateConduitAmount = function(reset_other_amount_field) {
        var candidate_count, distributed_amount, distributed_amount_cents, entered_value_cents, non_maxed, redistributed_amount, remaining_amount_cents, result, split_evenly;
        distributed_amount = 0.0;
        entered_value_cents = App.toIntCents(App.selectedConduitAmount);
        candidate_count = $('.conduit-candidate-amount-field').length;
        split_evenly = $('.conduit-candidates').data('split-evenly');
        result = App.moduloAmount(entered_value_cents, candidate_count);
        $.each($('.conduit-candidate-amount-field').slice(0, result.d_0), function(index, item) {
          var dollar_value;
          dollar_value = App.splitEvenlyAmount(split_evenly, result.v_0, entered_value_cents, $(item).data('default-percentage'));
          dollar_value = App.checkMaximumContribution($(item), dollar_value);
          distributed_amount += dollar_value;
          return $(item).val(dollar_value).prop('value', dollar_value);
        });
        $.each($('.conduit-candidate-amount-field').slice(result.d_0, candidate_count), function(index, item) {
          var dollar_value;
          dollar_value = App.splitEvenlyAmount(split_evenly, result.v_1, entered_value_cents, $(item).data('default-percentage'));
          dollar_value = App.checkMaximumContribution($(item), dollar_value);
          distributed_amount += dollar_value;
          return $(item).val(dollar_value).prop('value', dollar_value);
        });
        if (App.toIntCents(distributed_amount) !== entered_value_cents) {
          non_maxed = $('.conduit-candidate-amount-field[maxed="false"]');
          distributed_amount_cents = App.toIntCents(distributed_amount);
          remaining_amount_cents = entered_value_cents - distributed_amount_cents;
          result = App.moduloAmount(remaining_amount_cents, non_maxed.length);
          $.each(non_maxed.slice(0, result.d_0), function(index, item) {
            var current_value_cents, dollar_value, dollar_value_cents;
            current_value_cents = App.toIntCents($(item).val());
            dollar_value_cents = current_value_cents + result.v_0;
            dollar_value = App.checkMaximumContribution($(item), App.toFloatDollars(dollar_value_cents));
            return $(item).val(dollar_value).prop('value', dollar_value);
          });
          $.each(non_maxed.slice(result.d_0, candidate_count), function(index, item) {
            var current_value_cents, dollar_value, dollar_value_cents;
            current_value_cents = App.toIntCents($(item).val());
            dollar_value_cents = current_value_cents + result.v_1;
            dollar_value = App.checkMaximumContribution($(item), App.toFloatDollars(dollar_value_cents));
            return $(item).val(dollar_value).prop('value', dollar_value);
          });
          redistributed_amount = 0;
          $.each($('.conduit-candidate-amount-field'), function(index, item) {
            var current_value_cents;
            current_value_cents = App.toIntCents($(item).val());
            return redistributed_amount += App.toFloatDollars(current_value_cents);
          });
          if (redistributed_amount < App.selectedConduitAmount) {
            App.selectedConduitAmount = redistributed_amount;
          }
        }
        App.updateTotalConduitAmount(reset_other_amount_field);
        App.updateDonationAmount();
        return App.submitButtonValidator();
      };
      App.updateTotalConduitAmount = function(reset_other_amount_field) {
        var conduit_direct_amount, data_amount, element_to_update, entered_value, removeTrailingZerosOnDecimal, result, selected_amount, total_amount;
        entered_value = App.toIntCents(App.selectedConduitAmount);
        conduit_direct_amount = App.toIntCents($('.conduit-direct-amount-field').val()) || 0;
        total_amount = entered_value + conduit_direct_amount;
        removeTrailingZerosOnDecimal = parseFloat((total_amount / 100).toFixed(0)) === parseFloat((total_amount / 100).toFixed(2));
        $('.donation-amount-value').text(App.toFloatDollars(total_amount)).formatCurrency({
          roundToDecimalPlace: 2,
          removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
        });
        if ($('.matching-multiplier').length) {
          App.updateMatchAmount(App.toFloatDollars(total_amount));
        }
        if (total_amount > 0) {
          $('.conduit-amount-value').val(App.toFloatDollars(total_amount)).trigger('paymentRequestChange');
        }
        $('.conduit-amount-option').removeClass('selected');
        data_amount = App.toFloatDollars(entered_value);
        if (App.countDecimals(data_amount) === 1) {
          data_amount = data_amount.toString() + '0';
        }
        element_to_update = $('.other-conduit-amount-field').length ? '.other-conduit-amount-field' : '#donation-amount-total';
        if ((selected_amount = $('[data-amount="' + data_amount + '"]')).length !== 0) {
          $(selected_amount[0]).addClass('selected');
          if (reset_other_amount_field) {
            $('.other-conduit-amount-field').val('');
            result = String(App.toFloatDollars(entered_value));
            return $('#donation-amount-total').text('$ ' + result);
          }
        } else {
          if (entered_value > 0) {
            result = String(App.toFloatDollars(entered_value));
            if ($(element_to_update).val().indexOf(".") === result.length) {
              result += '.';
            }
            if ($(element_to_update).val().indexOf(".0") === result.length - 1) {
              result += '0';
            }
            $('.other-conduit-amount-field').val(result);
            return $('#donation-amount-total').text('$ ' + result);
          }
        }
      };
      (function() {
        var conduit_amount_cents, conduit_direct_amount_cents, full_amount_cents, preselected_amount;
        if ($('#donation_amount') && $('#donation_amount').val() && $('#donation_amount').val().length) {
          full_amount_cents = App.toIntCents($('#donation_amount').val());
          conduit_direct_amount_cents = App.toIntCents($('.conduit-direct-amount-field').val()) || 0;
          conduit_amount_cents = full_amount_cents - conduit_direct_amount_cents;
          App.selectedConduitAmount = App.toFloatDollars(conduit_amount_cents);
          App.updateTotalConduitAmount(true);
        } else {
          if ($.urlParam('amount')) {
            App.selectedConduitAmount = parseFloat($.urlParam('amount'));
            if (isNaN(App.selectedConduitAmount)) {
              App.selectedConduitAmount = "";
            }
          } else if ($($('.amount-option.selected')[0]).data('amount') || $($('.conduit-amount-option.selected')[0]).data('amount') || 0) {
            preselected_amount = $($('.amount-option.selected')[0]).data('amount') || $($('.conduit-amount-option.selected')[0]).data('amount') || 0;
            full_amount_cents = App.toIntCents(preselected_amount);
            App.selectedConduitAmount = App.toFloatDollars(full_amount_cents);
          }
          App.updateConduitAmount(true);
          App.updateTotalConduitAmount(true);
        }
        if ($('[id$=_one_click]').val() === "true") {
          return $('[id$=_one_click]').parent('form').find('[type=submit]').click();
        }
      })();
      $('.conduit-amounts').on('keyup', '.other-conduit-amount-field', function(item) {
        App.selectedConduitAmount = App.toFloatDollars(App.toIntCents($(this).val()));
        if (App.selectedConduitAmount > 0) {
          return App.updateConduitAmount(false);
        } else {
          $('.conduit-amount-value').val(null).trigger('paymentRequestChange');
          App.updateConduitAmount(true);
          App.updateDonationAmount();
          return App.submitButtonValidator();
        }
      });
      $('.conduit-amounts').on('focusout', '.other-conduit-amount-field', function(item) {
        var entered_value;
        entered_value = App.toIntCents(App.selectedConduitAmount);
        if ($('.other-conduit-amount-field').val().length > 0) {
          $('.other-conduit-amount-field').val(App.toFloatDollars(entered_value));
          return new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': App.dataLayerCategory(),
            'form': null,
            'action': 'entered',
            'label': 'donation_amount',
            'amount': App.selectedConduitAmount,
            'userAction': 't',
            'suppressTransactionID': true
          });
        }
      });
      $('.conduit-direct-amount-field').on('keyup', function(item) {
        App.ensureCurrencyInput(this);
        return App.updateTotalConduitAmount(true);
      });
      $('.conduit-amount-option').on('click', function() {
        var amount_selected;
        $('.conduit-amount-option').removeClass('selected');
        $(this).addClass('selected');
        amount_selected = $(this).data('amount');
        $('.other-conduit-amount-field').val('');
        App.selectedConduitAmount = amount_selected;
        App.updateConduitAmount(true);
        return new App.DataLayer().push({
          'event': app_platform + "Event",
          'category': App.dataLayerCategory(),
          'form': null,
          'action': 'selected',
          'label': 'donation_amount',
          'amount': amount_selected,
          'userAction': 't',
          'suppressTransactionID': true
        });
      });
      $('.conduit-candidate-amount-field').on('focus', function(item) {
        return $(this).data('val', $(this).val());
      });
      $('.conduit-candidate-amount-field').on('keyup', function(item) {
        var current, entered_value, new_entered_value, prev, recurring, remove_old_value;
        prev = $(this).data('val') || 0;
        current = $(this).val() || 0;
        $(this).data('val', current);
        entered_value = App.toIntCents(App.selectedConduitAmount) || 0;
        remove_old_value = entered_value - App.toIntCents(prev);
        new_entered_value = remove_old_value + App.toIntCents(current);
        App.selectedConduitAmount = App.toFloatDollars(new_entered_value);
        App.updateTotalConduitAmount(true);
        recurring = $('input[data-name=recurring]');
        if (recurring.is(':checked')) {
          return $('.footer-total .first-interval').text(recurring.data('humanized-interval'));
        } else {
          return $('.footer-total .first-interval').text('now');
        }
      });
      return $('.conduit-edit-button').on('click', function(item) {
        $('#conduit-candidate-index').toggleClass('d-none');
        $(this).toggleClass('d-none');
        return item.preventDefault();
      });
    };

    return ConduitPage;

  })();

}).call(this);
(function() {
  App.LandingPage.ContactCardUpsellPage = (function() {
    function ContactCardUpsellPage() {}

    ContactCardUpsellPage.prototype.setup = function() {
      return $(document).ready(function() {
        var contact_card, data;
        $('#accept-upsell-btn').hide();
        if (screen.width > 576 && window.location.href.indexOf('preview') === -1) {
          $('.loading .loading-text').hide();
          $('.loading').show();
          $('#decline-upsell-btn').click();
          return;
        }
        $('.upsell-panel').css('visibility', 'visible');
        contact_card = $('.contact-card-upsell');
        data = {
          firstName: $(contact_card).data('contact-card-first-name'),
          lastName: $(contact_card).data('contact-card-last-name'),
          organizationName: $(contact_card).data('organization-name'),
          organizationWebsite: $(contact_card).data('organization-website'),
          primaryPhone: $(contact_card).data('primary-phone-number'),
          secondaryPhone: $(contact_card).data('secondary-phone-number'),
          primaryEmail: $(contact_card).data('primary-email'),
          secondaryEmail: $(contact_card).data('secondary-email'),
          contactCardImageData: $(contact_card).data('contact-card-image-data')
        };
        App.DownloadToFile = function(content, filename, contentType) {
          var a, file;
          a = document.createElement('a');
          file = new Blob([content], {
            type: contentType
          });
          a.href = URL.createObjectURL(file);
          a.download = filename;
          a.click();
          return URL.revokeObjectURL(a.href);
        };
        App.GenerateID = function() {
          var id;
          id = Math.random().toString(36).substr(2, 9);
          return id;
        };
        App.GenerateVCardContent = function(data) {
          var vCard;
          vCard = 'BEGIN:VCARD\nVERSION:3.0\n';
          if (data.firstName && data.lastName) {
            vCard += "FN:" + data.firstName + " " + data.lastName + "\n";
          }
          if (data.organizationName) {
            vCard += "ORG:" + data.organizationName + "\n";
          }
          if (data.organizationWebsite) {
            vCard += "URL:" + data.organizationWebsite + "\n";
          }
          if (data.firstName && data.lastName) {
            vCard += "N:" + data.lastName + ";" + data.firstName + ";;;\n";
          }
          if (data.primaryPhone) {
            vCard += "TEL;TYPE=CELL:" + data.primaryPhone + "\n";
          }
          if (data.secondaryPhone) {
            vCard += "TEL;TYPE=WORK:" + data.secondaryPhone + "\n";
          }
          if (data.primaryEmail) {
            vCard += "EMAIL;TYPE=WORK:" + data.primaryEmail + "\n";
          }
          if (data.secondaryEmail) {
            vCard += "EMAIL;TYPE=OTHER:" + data.secondaryEmail + "\n";
          }
          if (data.contactCardImageData) {
            vCard += "PHOTO;ENCODING=b:" + data.contactCardImageData + "\n";
          }
          vCard += "END:VCARD";
          return vCard;
        };
        return $('#download-contact-card-accept-upsell-btn').on('click', function(e) {
          var error, vCardContent;
          e.preventDefault();
          try {
            vCardContent = App.GenerateVCardContent(data);
            App.DownloadToFile(vCardContent, 'contact-' + App.GenerateID() + '.vcf', 'text/vcard');
            if (window.location.href.indexOf('preview') === -1) {
              return setTimeout((function() {
                return $("form#new_contact_card_upsell").first().submit();
              }), 500);
            }
          } catch (error1) {
            error = error1;
            console.error("Error generating or downloading vCard:", error);
            return alert("An error occurred while downloading the contact card. Please try again.");
          }
        });
      });
    };

    return ContactCardUpsellPage;

  })();

}).call(this);
(function() {
  App.DataLayer = (function() {
    var format_phone, gen_attempt_id, get_button_animation_widget_settings, get_merchandise, get_pii, get_products, set_anonymous_user;

    function DataLayer() {}

    DataLayer.prototype.push = function(obj) {
      var action, amt_array, amt_option, candidate, category, charge_attempt_id, charge_revv_uid, class_list, conduit_candidates, conduit_donation_split, currentMB, donorProfile, double_upsell_revenue, ecommerce_event, event, first_recurring, first_recurring_interval, form, fraction, hasMerchandise, i, is_conduit_panel_upsell_page, is_video_page, j, k, label, len, len1, o, page_type, page_upsell_id, price, product, products, proto_obj, recurring, ref, ref1, ref2, revenue, second_recurring, second_recurring_interval, segment, shipping_cost, upsell_donation_amount;
      event = obj.event;
      category = obj.category;
      form = obj.form;
      action = obj.action;
      label = obj.label ? obj.label : $('title').text();
      proto_obj = {};
      proto_obj.event = event;
      proto_obj.category = category;
      proto_obj.label = label;
      class_list = $('[class*=rv-type]').attr('class');
      page_type = class_list.match(/rv-type-(.*?)-page/)[1];
      proto_obj.action = action;
      if (form) {
        proto_obj.upsellOrderNumber = upsell_order_number + 1;
      } else {
        form = $('form');
      }
      if (event.includes('.')) {
        ecommerce_event = event.split('.')[1];
      }
      if (ecommerce_event) {
        proto_obj['creative'] = void 0;
        proto_obj['ecommerce'] = {};
        proto_obj['ecommerce'][ecommerce_event] = {
          'actionField': {}
        };
      }
      if ($('.upsell-page-tips').length) {
        return;
      }
      is_video_page = $('.rv-video-page').length || $('.rv-live-stream-page').length ? 't' : 'f';
      proto_obj['videoPage'] = is_video_page;
      if (event === 'eec.add' || event === 'eec.checkout' || event === 'eec.purchase') {
        if ($('.rv-type-petition-page').length) {
          return;
        }
        is_conduit_panel_upsell_page = form.parents('.upsell-page-conduit-panel-upsell').length ? true : false;
        hasMerchandise = form.hasClass('merchandise-campaign-form') && $('.merchandise-campaign--valid.d-block', form).length ? true : false;
        if ($('.rv-type-merchandise-page').length || $('.rv-type-product-page').length) {
          hasMerchandise = true;
        }
        if (category.includes('landing page')) {
          if (!$("input." + page_type + "_charge_attempt_id", form).length) {
            charge_attempt_id = gen_attempt_id();
            form.append("<input class='" + page_type + "_charge_attempt_id' type='hidden' name='" + page_type + "[charge_attempt_id]'/>");
            $("input[name='" + page_type + "[charge_attempt_id]']", form).val(charge_attempt_id);
          }
          proto_obj.chargeAttemptID = $("input[name='" + page_type + "[charge_attempt_id]']", form).val();
          segment = $('.campaign-segment').val();
          if (segment === "none" || segment === "CURRENT_CAMPAIGN_SEGMENT") {
            segment = void 0;
          }
          proto_obj['campaignSegment'] = segment;
          proto_obj.userCategory = 'anonymous';
          set_anonymous_user(true);
          if ($('.with-temporary-profile').length) {
            donorProfile = localStorage.getItem('__donorProfile');
            if (donorProfile) {
              donorProfile = JSON.parse(donorProfile);
              if (donorProfile.firstname) {
                proto_obj.userCategory = 'temporary profile';
                set_anonymous_user(false);
              }
            }
          }
          if ($('.logged-in-landing-page-personal-details').length) {
            proto_obj.userCategory = 'logged in';
            set_anonymous_user(false);
          }
          proto_obj.moneyPledge = void 0;
          proto_obj.moneyPledgeID = void 0;
          if ($('input[type=checkbox]#conduit_money_bomb_opt_in', form).length) {
            proto_obj.moneyPledge = $('input[type=checkbox]#conduit_money_bomb_opt_in', form).is(':checked') ? 't' : 'f';
            proto_obj.moneyPledgeID = $('input#conduit_money_bomb_initiative_revv_uid', form).val();
          }
          if ($('.additional-money-bomb-label', form).length) {
            currentMB = 2;
            $('.additional-money-bomb-label', form).each(function() {
              var mpKey, mpuidKey, t;
              t = $(this);
              mpKey = "moneyPledge" + currentMB;
              mpuidKey = mpKey + "ID";
              proto_obj[mpKey] = $('.money-bomb-check-box', t).is(':checked') ? 't' : 'f';
              proto_obj[mpuidKey] = $('.additional-money-bomb-revv-uid', t).val();
              return currentMB++;
            });
          }
          first_recurring = void 0;
          second_recurring = void 0;
          if ($('input[data-name=recurring]', form).length) {
            first_recurring = $('input[data-name=recurring]', form).is(':checked') ? 't' : 'f';
            first_recurring_interval = $('input[data-name=recurring]').data('interval') || void 0;
          }
          if ($('input.donor-selected-recurring-interval', form).length) {
            first_recurring = 'f';
            first_recurring_interval = void 0;
            if ($('input.donor-selected-recurring-interval', form).val()) {
              first_recurring = 't';
              first_recurring_interval = $('input.donor-selected-recurring-interval', form).val();
            }
          }
          if ($('input[data-name=additional_recurring]', form).length) {
            second_recurring = $('input[data-name=additional_recurring]', form).is(':checked') ? 't' : 'f';
          }
          second_recurring_interval = $('input[data-name=additional_recurring]').data('interval') || void 0;
          proto_obj['recurring'] = first_recurring;
          proto_obj['recurringInterval'] = first_recurring_interval;
          proto_obj['recurring2'] = second_recurring;
          proto_obj['recurring2Interval'] = second_recurring_interval;
          recurring = first_recurring === 't' || second_recurring === 't' ? 't' : 'f';
          proto_obj['merchandise'] = hasMerchandise ? 't' : 'f';
          products = get_products(recurring, hasMerchandise);
          proto_obj['splitDonation'] = 'f';
          if (products.length > 1) {
            if (hasMerchandise && products.length !== 2) {
              proto_obj['splitDonation'] = 't';
            }
            if (!hasMerchandise && products.length > 1) {
              proto_obj['splitDonation'] = 't';
            }
          }
        }
      }
      if (event === 'eec.add') {
        proto_obj['action'] = 'add to cart';
        proto_obj.ecommerce[ecommerce_event].actionField.list = 'Shopping cart';
        proto_obj.ecommerce[ecommerce_event].products = products;
      }
      if (event === 'eec.checkout') {
        proto_obj['action'] = 'checkout step 1';
        proto_obj.ecommerce[ecommerce_event].actionField.step = 1;
        proto_obj.ecommerce[ecommerce_event].products = products;
        proto_obj = get_pii(proto_obj);
      }
      if (event === 'eec.purchase') {
        charge_revv_uid = $('.charge_revv_uid', form).val();
        proto_obj.ecommerce[ecommerce_event].actionField.id = charge_revv_uid;
        proto_obj.ecommerce[ecommerce_event].actionField.tax = void 0;
        proto_obj.ecommerce[ecommerce_event].actionField.shipping = void 0;
        proto_obj.ecommerce[ecommerce_event].actionField.coupon = void 0;
        proto_obj.ecommerce[ecommerce_event].actionField.affiliation = affiliation;
        proto_obj.ecommerce.currencyCode = 'USD';
        proto_obj.paymentMethod = payment_method;
        proto_obj.merchandise = 'f';
        proto_obj.userCategory = userCategory;
        if (category === 'panel double upsell' || category === 'panel donation upsell' || category === 'panel pledge upsell') {
          proto_obj.splitDonation = 'f';
          if (candidates.length > 1) {
            proto_obj.splitDonation = 't';
          }
        }
        if (hasMerchandise) {
          shipping_cost = parseInt($('.merchandise_campaign_variant_shipping', form).val()) / 100;
          proto_obj.ecommerce[ecommerce_event].actionField.shipping = +shipping_cost.toFixed(2);
          proto_obj.merchandise = 't';
        }
        if (category === 'panel double upsell') {
          double_upsell_revenue = +parseFloat($('.card-donation-amount-to-double', form).data('amount')).toFixed(2) * 2;
          proto_obj.ecommerce[ecommerce_event].actionField.revenue = +double_upsell_revenue.toFixed(2);
          proto_obj.ecommerce[ecommerce_event].products = [];
          if (isWinRed) {
            for (i = j = 0, len = candidates.length; j < len; i = ++j) {
              product = candidates[i];
              o = {};
              price = +parseFloat(product.amount).toFixed(2) * 2;
              o.price = price;
              o.id = product.candidate_id;
              o.category = product.candidate_name;
              o.variant = void 0;
              o.brand = void 0;
              o.quantity = 1;
              o.name = app_platform + " Donation";
              if (hasMerchandise) {
                o.name = app_platform + " Merchandise Donation";
              }
              proto_obj.ecommerce[ecommerce_event].products.push(o);
            }
          } else {
            o = {};
            o.price = double_upsell_revenue;
            o.id = $('meta[name=organization-uid]').attr('content');
            o.category = $('meta[name=organization-name]').attr('content');
            o.variant = void 0;
            o.brand = void 0;
            o.quantity = 1;
            o.name = app_platform + " Donation";
            if (hasMerchandise) {
              o.name = app_platform + " Merchandise Donation";
            }
            proto_obj.ecommerce[ecommerce_event].products.push(o);
          }
          if (hasMerchandise) {
            (ref = proto_obj.ecommerce[ecommerce_event].products).push.apply(ref, get_merchandise(form));
          }
        }
        if (category === 'panel donation upsell') {
          upsell_donation_amount = parseInt($('input[id=donation_upsell_amount]', form).val());
          proto_obj.ecommerce[ecommerce_event].actionField.revenue = +upsell_donation_amount.toFixed(2);
          proto_obj.ecommerce[ecommerce_event].products = [];
          if (isWinRed) {
            for (i = k = 0, len1 = candidates.length; k < len1; i = ++k) {
              product = candidates[i];
              o = {};
              fraction = parseInt(product.amount) / parseInt(original_amount);
              conduit_donation_split = +parseFloat(upsell_donation_amount * fraction).toFixed(2);
              o.price = conduit_donation_split;
              o.id = product.candidate_id;
              o.category = product.candidate_name;
              o.name = app_platform + " Donation";
              o.variant = void 0;
              o.brand = void 0;
              o.quantity = 1;
              if (hasMerchandise) {
                o.name = app_platform + " Merchandise Donation";
              }
              proto_obj.ecommerce[ecommerce_event].products.push(o);
            }
          } else {
            o = {};
            o.price = upsell_donation_amount;
            o.id = $('meta[name=organization-uid]').attr('content');
            o.category = $('meta[name=organization-name]').attr('content');
            o.variant = void 0;
            o.brand = void 0;
            o.quantity = 1;
            o.name = app_platform + " Donation";
            if (hasMerchandise) {
              o.name = app_platform + " Merchandise Donation";
            }
            proto_obj.ecommerce[ecommerce_event].products.push(o);
          }
          if (hasMerchandise) {
            (ref1 = proto_obj.ecommerce[ecommerce_event].products).push.apply(ref1, get_merchandise(form));
          }
        }
        if (category === 'panel conduit upsell') {
          conduit_candidates = [];
          $('.conduit-candidates .conduit-candidate', form).each(function() {
            var cc_amount, cc_id, cc_name, cc_obj, t;
            t = $(this);
            cc_obj = {};
            cc_id = $('.conduit-candidate-id-hidden', t).val();
            cc_name = $('.conduit-candidate-name-hidden', t).val();
            cc_amount = +parseFloat($('.conduit-candidate-amount-field', t).val()).toFixed(2);
            cc_obj.id = cc_id;
            cc_obj.name = app_platform + " Donation";
            if (hasMerchandise) {
              cc_obj.name = app_platform + " Merchandise Donation";
            }
            cc_obj.category = cc_name;
            cc_obj.variant = void 0;
            cc_obj.brand = void 0;
            cc_obj.quantity = 1;
            cc_obj.price = cc_amount;
            return conduit_candidates.push(cc_obj);
          });
          revenue = +parseFloat($('input[id=conduit_panel_upsell_amount]', form).val()).toFixed(2);
          proto_obj.ecommerce[ecommerce_event].actionField.revenue = revenue;
          proto_obj.splitDonation = 'f';
          if (conduit_candidates.length > 1) {
            proto_obj.splitDonation = 't';
          }
          if (is_conduit_panel_upsell_page) {
            proto_obj.category = 'conduit panel conduit upsell';
          }
          proto_obj.ecommerce[ecommerce_event].products = conduit_candidates;
          if (hasMerchandise) {
            (ref2 = proto_obj.ecommerce[ecommerce_event].products).push.apply(ref2, get_merchandise(form));
          }
        }
      }
      if (action !== 'widget') {
        if (!ecommerce_event) {
          if (!obj.suppressTransactionID) {
            proto_obj.transactionID = charge_revv_uid;
          }
          if (event === (app_platform + "Event")) {
            if (category === 'panel recurring upsell') {
              proto_obj.recurringInterval = interval;
              proto_obj.paymentMethod = payment_method;
            }
            if (category === 'panel pledge upsell') {
              proto_obj.moneyPledge = 't';
              proto_obj.moneyPledgeID = $('#money_bomb_id', form).val();
            }
            if (obj.userAction) {
              proto_obj.userAction = 't';
              if (obj.recurringInterval) {
                proto_obj.recurringInterval = obj.recurringInterval;
              }
              if (obj.moneyPledgeID) {
                proto_obj.moneyPledgeID = obj.moneyPledgeID;
              }
              if (label === 'donation_amount') {
                amt_array = [];
                amt_option = isWinRed ? '.conduit-amount-option' : '.amount-option';
                $(amt_option).each(function() {
                  var t;
                  t = $(this);
                  return amt_array.push(t.data("amount"));
                });
                proto_obj.amount = obj.amount;
                proto_obj.amount_array = amt_array;
              }
            }
            if (label === 'landing page settings') {
              proto_obj.campaignSegment = obj.campaignSegment;
              proto_obj.customCSS = obj.customCSS;
              proto_obj.userCategory = obj.userCategory;
              proto_obj.buttonAnimationSettings = get_button_animation_widget_settings();
              if (page_type === 'petition') {
                proto_obj.category = 'petition landing page';
              }
            }
            if (typeof candidates !== 'undefined' && candidates.length > 0) {
              candidate = candidates[0];
              proto_obj.candidateID = candidate.candidate_id;
              proto_obj.candidateName = candidate.candidate_name;
            }
            if (category === 'panel mobile phone upsell') {
              proto_obj.areaCode = format_phone($('input[name="mobile_phone_upsell[mobile_number]"]', form).val());
            }
          }
          if (event === 'donation attempt') {
            proto_obj.chargeAttemptID = $("form input[name='" + page_type + "[charge_attempt_id]']").val();
          }
        }
        if (form && !category.includes('landing page')) {
          proto_obj.panelUpsellID = $('input[name=upsell_card_revv_uid]', form).val();
          proto_obj.label = $('input[name=upsell_card_name]', form).val();
          if (!is_conduit_panel_upsell_page) {
            page_upsell_id = new URL(location.href).pathname.split('/')[4];
            proto_obj.pageUpsellID = page_upsell_id;
          }
        }
      }
      window.dataLayer = window.dataLayer || [];
      dataLayer.push(proto_obj);
      return proto_obj;
    };

    get_pii = function(pii_obj) {
      var city, is_retired, state;
      city = $('.city input').val() ? $('.city input').val() : $('.hidden-address-information').data('city');
      state = $('.state select').val() ? $('.state select').val() : $('.hidden-address-information').data('state');
      pii_obj.city = city;
      pii_obj.state = state;
      if ($('.btn-employment input[type=checkbox]').length) {
        is_retired = $('.btn-employment input[type=checkbox]').is(':checked');
      } else {
        is_retired = $('.hidden-employment-information').data('retired');
      }
      pii_obj.retired = is_retired ? 'Yes' : 'No';
      if (!is_retired) {
        pii_obj.employer = $('.employment-form input[data-name=employer_name]').val() ? $('.employment-form input[data-name=employer_name]').val() : $('.hidden-employment-information').data('employer');
        pii_obj.occupation = $('.employment-form input[data-name=occupation]').val() ? $('.employment-form input[data-name=occupation]').val() : $('.hidden-employment-information').data('occupation');
      }
      return pii_obj;
    };

    get_products = function(recurring, merchandise) {
      var d_obj, merch_obj, page_type, products, variant;
      products = [];
      if ($('.rv-type-donation-page').length) {
        d_obj = {
          brand: void 0,
          variant: void 0,
          quantity: 1
        };
        d_obj.id = $('meta[name=organization-uid]').attr('content');
        d_obj.name = recurring === 't' ? app_platform + " First Recurring Donation" : app_platform + " Donation";
        d_obj.category = $('meta[name=organization-name]').attr('content');
        d_obj.dimension11 = +parseFloat($('.donation-amount-value').val()).toFixed(2);
        if (merchandise) {
          d_obj.name = app_platform + " Merchandise Donation";
        }
        if (merchandise && recurring === 't') {
          d_obj.name = app_platform + " First Subscribed Merchandise Donation";
        }
        products.push(d_obj);
      }
      if ($('.rv-type-merchandise-page').length || $('.rv-type-checkout-page').length) {
        page_type = $('.rv-type-merchandise-page').length ? 'Merchandise' : 'Checkout';
        merch_obj = {
          'name': app_platform + " " + page_type + " Donation"
        };
        variant = $("[data-variant]").data('variant');
        merch_obj.variant = $('.product-name').text();
        merch_obj.sku = variant.sku;
        merch_obj.brand = void 0;
        merch_obj.category = $('#product-options-data').data('product-name');
        merch_obj.price = variant.price;
        merch_obj.id = variant.revv_uid;
        merch_obj.dimension56 = variant.revv_uid;
        merch_obj.quantity = $('.checkout-quantity-select').val();
        if ($('.rv-type-checkout-page').length) {
          merch_obj.category = $('[data-product]').data('product').name;
        }
        products.push(merch_obj);
      }
      if ($('.rv-type-product-page').length) {
        merch_obj = {
          'name': app_platform + " Merchandise Donation"
        };
        merch_obj.sku = $('.amount-option.selected').data('sku');
        merch_obj.brand = void 0;
        merch_obj.category = $('.amount-option.selected').data('product-name');
        merch_obj.price = parseFloat($('.amount-option.selected').data('amount')).toFixed(2);
        merch_obj.id = $('.orderable-uid-field').val();
        merch_obj.dimension56 = $('.orderable-uid-field').val();
        merch_obj.quantity = $('.orderable-quantity-field').val();
        products.push(merch_obj);
      }
      if ($('.rv-type-conduit-page').length) {
        $('.conduit-candidate').each(function() {
          var cc_amount, cc_id, cc_name, cc_obj, t;
          t = $(this);
          cc_obj = {};
          cc_id = $('.conduit-candidate-id-hidden', t).val();
          cc_name = $('.conduit-candidate-name-hidden', t).val();
          cc_amount = +parseFloat($('.conduit-candidate-amount-field', t).val()).toFixed(2);
          cc_obj.id = cc_id;
          cc_obj.name = recurring === 't' ? app_platform + " First Recurring Donation" : app_platform + " Donation";
          if (merchandise) {
            cc_obj.name = app_platform + " Merchandise Donation";
          }
          if (merchandise && recurring === 't') {
            cc_obj.name = app_platform + " First Subscribed Merchandise Donation";
          }
          cc_obj.category = cc_name;
          cc_obj.variant = void 0;
          cc_obj.brand = void 0;
          cc_obj.quantity = 1;
          cc_obj.dimension11 = cc_amount;
          return products.push(cc_obj);
        });
        if (merchandise) {
          $('.merchandise-campaign--product-details').each(function() {
            var price, t;
            t = $(this);
            price = +parseFloat($('.merchandise_campaign_product_price').val() / 100).toFixed(2);
            merch_obj = {
              'name': app_platform + " Merchandise Donation",
              'quantity': 1
            };
            if (recurring === 't') {
              merch_obj.name = app_platform + " First Subscribed Merchandise Donation";
            }
            merch_obj.variant = $('.merchandise_campaign_variant_name').val();
            merch_obj.sku = $('.merchandise_campaign_variant_sku').val();
            merch_obj.brand = void 0;
            merch_obj.category = $('.merchandise_campaign_product_name').val();
            merch_obj.price = price;
            merch_obj.id = $('.merchandise_campaign_variant_sku').val();
            merch_obj.dimension56 = $('.merchandise_campaign_product_revv_uid').val();
            return products.push(merch_obj);
          });
        }
      }
      return products;
    };

    gen_attempt_id = function() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r, v;
        r = Math.random() * 16 | 0;
        if (c === 'x') {
          v = r;
        } else {
          v = r & 0x3 | 0x8;
        }
        return v.toString(16);
      });
    };

    get_merchandise = function(form) {
      var merch_arr;
      merch_arr = [];
      $('.merchandise-campaign--product-details', form).each(function() {
        var merch_obj, price, t;
        t = $(this);
        price = +parseFloat($('.merchandise_campaign_product_price', t).val() / 100).toFixed(2);
        merch_obj = {
          'name': app_platform + " Merchandise Donation",
          'quantity': 1
        };
        merch_obj.variant = $('.merchandise_campaign_variant_name', t).val();
        merch_obj.brand = void 0;
        merch_obj.sku = $('.merchandise_campaign_variant_sku', t).val();
        merch_obj.category = $('.merchandise_campaign_product_name', t).val();
        merch_obj.price = price;
        merch_obj.dimension56 = $('.merchandise_campaign_product_revv_uid', t).val();
        merch_obj.id = $('.merchandise_campaign_variant_sku', t).val();
        return merch_arr.push(merch_obj);
      });
      return merch_arr;
    };

    format_phone = function(phone) {
      var m, s2;
      s2 = ("" + phone).replace(/\D/g, '');
      m = s2.match(/^1(\d{3})(\d{3})(\d{4})$/);
      if (!m) {
        return null;
      } else {
        return m[1];
      }
    };

    get_button_animation_widget_settings = function() {
      var widget_settings;
      if ($('.rv-amount-button-animation').length === 0) {
        return void 0;
      }
      widget_settings = [];
      $('.rv-amount-button-animation').each(function() {
        return widget_settings.push({
          color: $(this).data('color'),
          style: $(this).data('style'),
          position: $(this).data('position'),
          duration: $(this).data('duration'),
          delay: $(this).data('delay'),
          amount: $('.btn[data-name="amt' + $(this).data('position') + '"]').data('amount')
        });
      });
      return widget_settings;
    };

    set_anonymous_user = function(bool) {
      if (bool) {
        return localStorage.setItem('anonymous_user', true);
      } else {
        return localStorage.removeItem('anonymous_user');
      }
    };

    return DataLayer;

  })();

}).call(this);
(function() {
  App.LandingPage.InitiativeMerchandiseCampaign = (function() {
    function InitiativeMerchandiseCampaign() {}

    InitiativeMerchandiseCampaign.prototype.setup = function() {
      App.parentForm = function(target) {
        var $parent_form, $target;
        $target = $(target);
        $parent_form = $target.is('form') ? $target : $target.parents('form');
        if ($parent_form.length === 0) {
          $parent_form = $target.find('.merchandise-campaign-form');
        }
        return $parent_form;
      };
      App.checkAmount = function(target) {
        var $parent_form, current_amount, merch_invalid_callout, merchandise_minimum, merchandise_recurring_donation_required, met_required_minimum_for_merch, minimum_amount, minimum_required_to_donate, not_qualified_for_merch_message, old_value, product_html, recurring_checkbox_checked, recurring_checkboxes, recurring_is_required_but_not_enabled, recurring_radio_button_checked, recurring_requirement_for_merchandise_met, show_merchandise_fields, valid_source_candidate_amount;
        if (target === void 0) {
          return;
        }
        $parent_form = App.parentForm(target);
        current_amount = parseFloat($parent_form.find('input.donation-amount-value').val() || $parent_form.find('input.conduit-amount-value').val() || $parent_form.find('input.card-donation-amount').val());
        product_html = '';
        merchandise_minimum = void 0;
        minimum_amount = void 0;
        merchandise_recurring_donation_required = void 0;
        recurring_requirement_for_merchandise_met = false;
        minimum_required_to_donate = void 0;
        if ($parent_form.find('.merchandise-campaign--product-details').length === 0) {
          return;
        }
        recurring_checkboxes = $('.landing-page-recurring-button input[type="checkbox"]');
        recurring_checkbox_checked = false;
        recurring_checkboxes.each(function() {
          if ($(this).prop('checked')) {
            recurring_checkbox_checked = true;
            return false;
          }
        });
        recurring_radio_button_checked = $('.landing-page-recurring-button input[type="radio"][data-name="recurring"]').prop('checked');
        $parent_form.find('.merchandise-campaign--product-details').data('products').forEach(function(item) {
          if (!merchandise_recurring_donation_required) {
            merchandise_recurring_donation_required = item.recurring_donation_required;
          }
          minimum_amount = parseFloat(item.minimum_amount);
          if ((minimum_required_to_donate === void 0) || minimum_amount < minimum_required_to_donate) {
            minimum_required_to_donate = minimum_amount;
          }
          if (current_amount > 0 && current_amount >= minimum_amount) {
            merchandise_minimum = minimum_amount;
            product_html = item.html;
          }
          if (!merchandise_recurring_donation_required || (merchandise_recurring_donation_required && recurring_checkbox_checked) || (merchandise_recurring_donation_required && recurring_radio_button_checked)) {
            return recurring_requirement_for_merchandise_met = true;
          }
        });
        if (recurring_requirement_for_merchandise_met) {
          $parent_form.find('.merchandise-campaign--product-details').html(product_html);
        }
        met_required_minimum_for_merch = (merchandise_minimum != null) && merchandise_minimum !== 0 && (current_amount != null) && current_amount !== 0 && current_amount >= merchandise_minimum;
        show_merchandise_fields = met_required_minimum_for_merch && recurring_requirement_for_merchandise_met;
        valid_source_candidate_amount = true;
        if (show_merchandise_fields && $parent_form.find('.conduit-candidate-amount-field').length > 0 && $parent_form.attr('id') !== 'new_conduit_panel_upsell') {
          $parent_form.find('.conduit-candidate-amount-field').each(function() {
            var candidate_amount_cents, candidate_default_percentage, candidate_merchandise_minimum_cents;
            candidate_amount_cents = App.toIntCents(parseFloat(this.value || 0));
            candidate_default_percentage = parseFloat($(this).data('default-percentage'));
            candidate_merchandise_minimum_cents = parseInt((merchandise_minimum * 100) * candidate_default_percentage);
            if (candidate_amount_cents < candidate_merchandise_minimum_cents) {
              return show_merchandise_fields = false;
            }
          });
        }
        if (show_merchandise_fields && $parent_form.find('.conduit-candidate-amount-field').length > 0 && $parent_form.attr('id') === 'new_conduit_panel_upsell') {
          $parent_form.find('.conduit-candidate-amount-field').each(function() {
            var candidate_amount_cents, candidate_default_percentage, candidate_merchandise_minimum_cents;
            if ($(this).data('source-candidate')) {
              candidate_amount_cents = App.toIntCents(parseFloat(this.value || 0));
              candidate_default_percentage = parseFloat($(this).data('default-percentage'));
              candidate_merchandise_minimum_cents = parseInt((merchandise_minimum * 100) * candidate_default_percentage);
              if (candidate_amount_cents < candidate_merchandise_minimum_cents) {
                return valid_source_candidate_amount = false;
              }
            }
          });
        }
        old_value = $parent_form.find('.merchandise-campaign-callout').data('shipping-required');
        $parent_form.find('.merchandise-campaign-callout').data('shipping-required', show_merchandise_fields);
        if (show_merchandise_fields) {
          if (valid_source_candidate_amount) {
            $parent_form.find('.qualifies-for-merchandise').val(true);
            if ($parent_form.attr('id') !== 'new_conduit_panel_upsell') {
              $parent_form.find('.merchandise_campaign_variant_revv_uid').attr('required', 'required');
            }
            $parent_form.find('.shipping-address-block').addClass('d-block').removeClass('d-none');
            $parent_form.find('.shipping-address-block').find('input[type=text], input[type=number], select').not($('[id$=_address_line2]')).prop('required', 'required');
            $parent_form.find('.merchandise-campaign-callout > .merchandise-campaign--invalid').addClass('d-none').removeClass('d-block');
            $parent_form.find('.merchandise-campaign-callout > .merchandise-campaign--valid').addClass('d-block').removeClass('d-none');
            $parent_form.find('.merchandise-campaign-callout .additional-field-group').addClass('d-block').removeClass('d-none');
            $parent_form.find('.upsell-merch-campaign-submit-buttons').addClass('d-block').removeClass('d-none');
            $parent_form.find('.upsell-page-paragraph').addClass('d-block').removeClass('d-none');
            $parent_form.find('.loading-row').removeClass('d-flex').addClass('d-none');
            $parent_form.find('.shipping-disclaimer').removeClass('d-none').addClass('d-flex');
          } else {
            $parent_form.find('.qualifies-for-merchandise').val(false);
            $parent_form.find('.merchandise-campaign-callout > .merchandise-campaign--valid').addClass('d-none').removeClass('d-block');
            $parent_form.find('.merchandise-campaign-callout > .merchandise-campaign--invalid').addClass('d-block').removeClass('d-none');
            $parent_form.find('.merchandise-campaign--product-details').empty();
            $parent_form.find('.upsell-merch-campaign-submit-buttons').addClass('d-none').removeClass('d-block');
            $parent_form.find('.upsell-page-paragraph').addClass('d-block').removeClass('d-none');
          }
        } else {
          $parent_form.find('.qualifies-for-merchandise').val(false);
          $parent_form.find('.merchandise_campaign_variant_revv_uid').val(null);
          $parent_form.find('.merchandise_campaign_variant_revv_uid').removeAttr('required');
          $parent_form.find('.shipping-address-block').addClass('d-none').removeClass('d-block');
          $parent_form.find('.shipping-address-block').find('input[type=text], input[type=number], select').removeProp('required');
          if (current_amount > 0) {
            not_qualified_for_merch_message = 'This donation does not qualify for the gift offer!';
            recurring_is_required_but_not_enabled = merchandise_recurring_donation_required && !recurring_checkbox_checked && !recurring_radio_button_checked;
            if (!met_required_minimum_for_merch && recurring_is_required_but_not_enabled) {
              not_qualified_for_merch_message += " Enable recurring to qualify & donate a minimum of $" + minimum_required_to_donate + ".";
            } else if (!met_required_minimum_for_merch) {
              not_qualified_for_merch_message += " Donate a minimum of $" + minimum_required_to_donate + ".";
            } else if (recurring_is_required_but_not_enabled) {
              not_qualified_for_merch_message += " Enable recurring to qualify.";
            }
            $parent_form.find('.merchandise-campaign-callout > .merchandise-campaign--valid').addClass('d-none').removeClass('d-block');
            merch_invalid_callout = $parent_form.find('.merchandise-campaign-callout > .merchandise-campaign--invalid');
            merch_invalid_callout.html(not_qualified_for_merch_message);
            merch_invalid_callout.addClass('d-block').removeClass('d-none');
            $parent_form.find('.upsell-merch-campaign-submit-buttons').addClass('d-block').removeClass('d-none');
          }
          $parent_form.find('.merchandise-campaign-callout .additional-field-group').addClass('d-none').removeClass('d-block');
          $parent_form.find('.upsell-page-paragraph').addClass('d-none').removeClass('d-block');
          $parent_form.find('.loading-row').removeClass('d-flex').addClass('d-none');
        }
        if ($parent_form.attr('id') === 'new_double_upsell') {
          $parent_form.find('.amounts-row').removeClass('d-flex').addClass('d-none');
        }
        if (old_value !== show_merchandise_fields) {
          return $parent_form.find('.merchandise-campaign-callout').trigger('merchandiseShippingRequiredChanged');
        }
      };
      $('body').on('change', 'select.merchandise-campaign-variant-option-select', function(e) {
        var $parent_form, $target, i, key, ref, result, success, value, values, variant, variants, vi;
        $target = $(e.target);
        $parent_form = $target.parents('form');
        variants = $parent_form.find('.merchandise-campaign-variant-options').data('variants');
        values = $parent_form.find('select.merchandise-campaign-variant-option-select').map(function() {
          return [[$(this).data('option-name'), this.value]];
        }).get();
        result = null;
        for (i in variants) {
          variant = variants[i];
          success = true;
          for (vi in values) {
            ref = values[vi], key = ref[0], value = ref[1];
            if (variant[key] !== value) {
              success = false;
              break;
            }
          }
          if (success) {
            result = variant;
            break;
          }
        }
        if (result) {
          $parent_form.find('.merchandise_campaign_variant_revv_uid').val(variant.variant_revv_uid);
          $parent_form.find('.merchandise_campaign_variant_name').val(variant.variant_name);
          $parent_form.find('.merchandise_campaign_variant_sku').val(variant.variant_sku);
          $parent_form.find('.merchandise_campaign_product_name').val(variant.product_name);
          return $parent_form.find('.merchandise_campaign_variant_shipping').val(variant.variant_shipping);
        } else {
          $parent_form.find('.merchandise_campaign_variant_revv_uid').val(null);
          $parent_form.find('.merchandise_campaign_variant_name').val(null);
          $parent_form.find('.merchandise_campaign_variant_sku').val(null);
          $parent_form.find('.merchandise_campaign_product_name').val(null);
          return $parent_form.find('.merchandise_campaign_variant_shipping').val(null);
        }
      });
      $('.merchandise-campaign-form').each(function() {
        return App.checkAmount(this);
      });
      $('body').on('donation.upsell.amount.selected', function(e) {
        return App.checkAmount(e.target);
      });
      $('body').on('donation.amount.changed', function(e) {
        return App.checkAmount(e.target);
      });
      $('.landing-page-recurring-button input[type="checkbox"]').on('change', (function(_this) {
        return function(e) {
          return App.checkAmount(e.target);
        };
      })(this));
      $('.landing-page-recurring-button input[type="radio"]').on('change', (function(_this) {
        return function(e) {
          return App.checkAmount(e.target);
        };
      })(this));
      $('input[type=submit]').on('click', function(e) {
        var $parent_form;
        $parent_form = App.parentForm(e.target);
        $parent_form.find('input[type=submit]').removeAttr('clicked');
        return $(this).attr('clicked', 'true');
      });
      $('body').on('donation.upsell.submitted', function(e) {
        var $parent_form;
        $parent_form = App.parentForm(e.target);
        return $parent_form.find('input[type=radio], select').attr('disabled', true);
      });
      $('.merchandise-campaign-form').on('submit', function(e) {
        var $loading_row, $parent_form, merchandise_campaign_radio_button_checked, merchandise_campaign_variant_exist, merchandise_campaign_variant_not_options, merchandise_campaign_variant_with_options, merchandise_campaign_variant_with_options_select, options;
        $parent_form = App.parentForm(e.target);
        $loading_row = $parent_form.find('.loading-row');
        merchandise_campaign_variant_exist = function() {
          return $parent_form.find('.merchandise_campaign_variant_revv_uid').val() && $parent_form.find('.merchandise_campaign_variant_revv_uid').val().length;
        };
        merchandise_campaign_variant_with_options_select = function() {
          return $parent_form.find('select.merchandise-campaign-variant-option-select').length > 0 && merchandise_campaign_variant_exist();
        };
        merchandise_campaign_variant_with_options = function() {
          return $parent_form.find('.merchandise-campaign-variant-options').length === 0 && merchandise_campaign_variant_exist();
        };
        merchandise_campaign_radio_button_checked = function() {
          return $parent_form.find('.merchandise-campaign-radio-button-checkbox > input:checked') && $parent_form.find('.merchandise-campaign-radio-button-checkbox > input:checked').length;
        };
        merchandise_campaign_variant_not_options = function() {
          return $parent_form.find('.merchandise-campaign-variant-options').length === 0;
        };
        if ($parent_form.find('.merchandise-campaign-variant-options').length === 0) {
          $parent_form.find('.merchandise-campaign-callout .error').remove();
          $parent_form.find('.upsell-merch-campaign-submit-buttons').addClass('d-none').removeClass('d-block');
          return $loading_row.removeClass('d-none').addClass('d-flex');
        } else if ($parent_form.find('input.btn-decline[clicked=true]').length === 1) {
          $parent_form.find('.merchandise-campaign--product-details').empty();
          $parent_form.find('.upsell-page-paragraph').addClass('d-none').removeClass('d-block');
          $parent_form.find('.upsell-merch-campaign-submit-buttons').addClass('d-none').removeClass('d-block');
          return $loading_row.removeClass('d-none').addClass('d-flex');
        } else if ($parent_form.find('input[type=submit][clicked=true]').length === 0) {
          e.stopPropagation();
          return e.preventDefault();
        } else {
          if (merchandise_campaign_variant_with_options_select() || merchandise_campaign_variant_with_options() || merchandise_campaign_radio_button_checked() || merchandise_campaign_variant_not_options()) {
            $parent_form.find('.upsell-merch-campaign-submit-buttons').addClass('d-none').removeClass('d-block');
            $parent_form.find('.merchandise-campaign--product-details').removeClass('d-flex').addClass('d-none');
            $parent_form.find('.merchandise-campaign-callout .error').remove();
            return $loading_row.removeClass('d-none').addClass('d-flex');
          } else {
            options = {
              placement: 'top',
              title: 'Please complete your gift!',
              trigger: 'focus'
            };
            $parent_form.find('.merchandise-campaign-callout').data('toggle', 'tooltip').tooltip(options);
            if ($parent_form.find('.merchandise-campaign-callout .error').length === 0) {
              $parent_form.find('.merchandise-campaign-callout').append('<div class="error" style="color: red;">Please complete your gift!</div>');
            }
            e.stopPropagation();
            return e.preventDefault();
          }
        }
      });
      return $('input.donation-amount-value, input.conduit-amount-value').on('paymentRequestChange', function(e) {
        return App.checkAmount(e.target);
      });
    };

    return InitiativeMerchandiseCampaign;

  })();

}).call(this);
(function() {
  App.LandingPage.MerchandisePage = (function() {
    function MerchandisePage() {}

    MerchandisePage.prototype.setup = function() {
      App.shippingData = null;
      App.fetchingShippingData = false;
      App.salesTaxInCents = 0;
      App.displayAmount = function(price_cents, shipping_cost_cents) {
        var additional_amount_cents, donor_cover_fees_cents, html, processing_cents, shipping_and_handling_cost_cents, total_cents;
        total_cents = shipping_cost_cents + price_cents;
        html = 'Item(s): ' + App.formatCentsToCurrency(price_cents);
        shipping_and_handling_cost_cents = shipping_cost_cents;
        if ($('.landing-page-cover-fees-button input[type=checkbox]').length && total_cents !== 0) {
          if ($('.landing-page-cover-fees-button input[type=checkbox]').prop('checked')) {
            donor_cover_fees_cents = (total_cents + 30) / (1 - 0.04);
            processing_cents = donor_cover_fees_cents - total_cents;
            total_cents = donor_cover_fees_cents;
            shipping_and_handling_cost_cents += processing_cents;
          }
        }
        if (shipping_cost_cents === 0 && shipping_and_handling_cost_cents > 0) {
          html += "<br/>Fees: " + App.formatCentsToCurrency(shipping_and_handling_cost_cents);
        } else if (shipping_and_handling_cost_cents > 0) {
          html += "<br/>Shipping, handling, & fees: " + App.formatCentsToCurrency(shipping_and_handling_cost_cents);
        }
        if (parseFloat($('input.checkout-additional-amount-value').val()) > 0) {
          additional_amount_cents = App.toIntCents($('input.checkout-additional-amount-value').val());
          total_cents += additional_amount_cents;
          html += "<br/>Additional donation: " + App.formatCentsToCurrency(additional_amount_cents);
        }
        if (App.salesTaxInCents > 0) {
          total_cents += App.salesTaxInCents;
          html += "<br/>Tax: " + App.formatCentsToCurrency(App.salesTaxInCents);
        }
        html += "<br/>Total: " + App.formatCentsToCurrency(total_cents);
        $('.donation-amount-value').html(html);
        $('.donation-amount-value').attr('value', App.toFloatDollars(total_cents));
        return $('#donation_amount').val(App.toFloatDollars(total_cents)).trigger('paymentRequestChange');
      };
      App.updateVariantAmount = function() {
        var additional_shipping_cents, arrayLength, bulk_order_page, i, options_data, override_price_cents, price_cents, rate, selected_shipping_rate, shipping_cost_cents, shipping_rates, total_shipping_cost_cents;
        options_data = $('#product-options-data').data();
        if ($('#product-options-data').length === 0) {
          return;
        }
        override_price_cents = null;
        if (options_data['overridePrice'] > 0) {
          override_price_cents = App.toIntCents(options_data['overridePrice']);
        }
        price_cents = 0;
        additional_shipping_cents = 0;
        bulk_order_page = ($('meta[name=shipping-rates-domain]').attr('content') || '').length > 0;
        $('.product-row, .merch-product-option').each(function(i) {
          var additional_shipping_cost_cents, cost_cents, price, quantity, variant_data;
          variant_data = $(this).data()['variant'];
          price = override_price_cents || App.toIntCents(variant_data['price']);
          additional_shipping_cost_cents = App.toIntCents(variant_data['shipping_price']) || 0.0;
          quantity = parseInt($(this).find('.checkout-quantity-select').val() || $(this).find('.merch-product-quantity').val());
          cost_cents = price * quantity;
          price_cents += cost_cents;
          additional_shipping_cents += additional_shipping_cost_cents * quantity;
          return $(this).find('.product-cost').html(App.toFloatDollars(cost_cents)).formatCurrency({
            roundToDecimalPlace: 2,
            removeTrailingZerosOnDecimal: App.removeTrailingZerosOnCents(cost_cents)
          }).text();
        });
        if (price_cents) {
          shipping_rates = $('#product-options-data').data('shipping-rates');
          selected_shipping_rate = null;
          arrayLength = shipping_rates.length;
          i = 0;
          while (i < arrayLength) {
            rate = shipping_rates[i];
            if (rate.min_total_cents <= price_cents && price_cents <= rate.max_total_cents) {
              selected_shipping_rate = rate;
              break;
            }
            i++;
          }
          shipping_cost_cents = bulk_order_page ? App.shippingData ? App.shippingData.priceCents || App.shippingData.price_cents : 0.0 : selected_shipping_rate ? selected_shipping_rate.shipping_cost_cents : 0.0;
          if (shipping_cost_cents > 0 || additional_shipping_cents > 0) {
            total_shipping_cost_cents = shipping_cost_cents + additional_shipping_cents;
            return App.displayAmount(price_cents, total_shipping_cost_cents);
          } else {
            return App.displayAmount(price_cents, 0);
          }
        } else {
          $('.donation-amount-value').empty();
          return $('.donation-amount-value').attr('value', 0);
        }
      };
      $('.checkout-quantity-select, .merch-product-quantity').on('change', function() {
        App.updateVariantAmount();
        return App.updateProgressBarAmount();
      });
      $('.product-remove > a').on('click', function(e) {
        var product_row_html;
        e.preventDefault();
        product_row_html = $(this).parents('.product-row').parent().html();
        $(this).parents('.product-row').remove();
        App.updateVariantAmount();
        return App.updateProgressBarAmount();
      });
      $('.product-row, .merch-product-option').on('click', function() {
        var variant_data, variant_revv_uid;
        variant_data = $(this).data()['variant'];
        variant_revv_uid = variant_data['revv_uid'];
        $('.carousel-item').removeClass('active').addClass('invisible');
        $('.carousel-item').filter(function(index) {
          var product_carousel_options;
          product_carousel_options = $(this).data();
          if (product_carousel_options['variant']) {
            return variant_revv_uid === product_carousel_options['variant'];
          } else {
            return false;
          }
        }).addClass('active').removeClass('invisible');
        if ($('.carousel-item.active').length === 0) {
          return $('.carousel-item').first().addClass('active').removeClass('invisible');
        }
      });
      $('.checkout-amount-option').on('click', function() {
        var amount_selected, select_new_amount;
        select_new_amount = true;
        if (!$(this).hasClass('other-checkout-amount-button') && $(this).hasClass('selected')) {
          select_new_amount = false;
          new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': App.dataLayerCategory(),
            'form': null,
            'label': 'donation_amount',
            'amount': 0,
            'userAction': 't',
            'suppressTransactionID': true
          });
        } else if (!$(this).hasClass('other-checkout-amount-button')) {
          $('.other-checkout-amount-field').val('');
          $('.other-checkout-amount-field').removeClass('selected').removeClass('error');
          amount_selected = App.toFloatDollars(parseInt($(this).data('amount').fractional));
          new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': App.dataLayerCategory(),
            'form': null,
            'action': 'selected',
            'label': 'donation_amount',
            'amount': amount_selected,
            'userAction': 't',
            'suppressTransactionID': true
          });
        } else {
          $('.other-checkout-amount-field').focus();
        }
        $('.other-checkout-amount-button').data('amount', '').removeClass('selected').addClass('hidden-xs-up');
        $('.other-checkout-amount-field-group').show();
        $('.checkout-amount-option').removeClass('selected');
        if (select_new_amount) {
          $(this).addClass('selected');
        }
        $('input.checkout-additional-amount-value').val(amount_selected);
        App.updateVariantAmount();
        return App.updateProgressBarAmount();
      });
      $('.other-checkout-amount-field').on('keyup', function() {
        var max_other_amount_dollars;
        max_other_amount_dollars = App.enforceMaximumContribution($(this), $(this).val());
        $(this).val(max_other_amount_dollars);
        $('input.checkout-additional-amount-value').val($(this).val());
        $('.checkout-amount-option').removeClass('selected');
        $(this).addClass('selected');
        App.updateVariantAmount();
        return App.updateProgressBarAmount();
      });
      $('.other-checkout-amount-field').on('blur', function(e) {
        var amount, parentNode, removeTrailingZerosOnDecimal;
        amount = $(this).val();
        if (parseFloat(amount) > 0) {
          if ($(this).parents('.amount-no-desc').length > 0) {
            parentNode = $(this).parents('.amount-no-desc');
          } else {
            parentNode = $(this).parents('.amount-desc');
          }
          removeTrailingZerosOnDecimal = parseFloat(amount).toFixed(0) === parseFloat(amount).toFixed(2);
          parentNode.find('.checkout-amount-option').data('amount', amount).text(amount).formatCurrency({
            roundToDecimalPlace: 2,
            removeTrailingZerosOnDecimal: removeTrailingZerosOnDecimal
          }).addClass('selected').removeClass('hidden-xs-up');
          parentNode.find('.other-checkout-amount-field-group').hide();
          return new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': App.dataLayerCategory(),
            'form': null,
            'action': 'entered',
            'label': 'donation_amount',
            'amount': amount,
            'userAction': 't',
            'suppressTransactionID': true
          });
        } else {
          return e.preventDefault();
        }
      });
      App.checkShippingRate = function() {
        var data, empty, shipping_inputs, url;
        if ($('meta[name=shipping-rates-domain]').attr('content') && $('.donation-amount-value').val() > 0) {
          url = $('meta[name=shipping-rates-domain]').attr('content');
          shipping_inputs = $('.shipping-address-form').find('input, select');
          empty = shipping_inputs.filter(function() {
            return $(this).val() === '';
          });
          $('#shipping-methods').hide();
          $('#shipping-methods').find('.processing-block').hide();
          $('#shipping-methods').find('.shipping-method-rates').empty();
          if (shipping_inputs && empty.length === 0 && !App.fetchingShippingData) {
            App.fetchingShippingData = true;
            $('#shipping-methods').show();
            $('#shipping-methods').find('.processing-block').show();
            data = $('.shipping-address-form').parents('form').serializeArray();
            data.push({
              name: 'page-uid',
              value: $('meta[name=page-uid]').attr('content')
            });
            return $.ajax({
              url: url + '.js',
              dataType: 'script',
              xhrFields: {
                withCredentials: true
              },
              data: data,
              type: 'POST',
              success: function(response) {
                App.fetchingShippingData = false;
                return $('#shipping-methods').find('.processing-block').hide();
              },
              error: function(response) {
                App.fetchingShippingData = false;
                return $('#shipping-methods').find('.processing-block').hide();
              }
            });
          }
        }
      };
      (function() {
        if ($('#merchandise_shipping_method').length && $('#merchandise_shipping_method').val().length) {
          App.shippingData = JSON.parse($('#merchandise_shipping_method').val());
        }
        $('.carousel-indicators').hide();
        if ($('.donation-amount-value').val()) {
          App.updateVariantAmount();
          App.updateProgressBarAmount();
        }
        if (($('meta[name=shipping-rates-domain]').attr('content') || '').length > 0) {
          $('.shipping-address-form').on('change', 'input, select', function() {
            return App.checkShippingRate();
          });
          $('body').on('change', '.checkout-quantity-select', function() {
            return App.checkShippingRate();
          });
          $('body').on('addressFilled', '.btn-shipping', function() {
            return App.checkShippingRate();
          });
          $('#shipping-methods').on('click', '.shipping-method-data', function() {
            App.shippingData = $(this).data();
            App.updateVariantAmount();
            return App.updateProgressBarAmount();
          });
        }
        return $('.landing-page-cover-fees-button').on('change data.name.set', 'input[type=checkbox]', function(e) {
          App.updateVariantAmount();
          return App.updateProgressBarAmount();
        });
      })();
      App.updateVariantAmount();
      return App.updateProgressBarAmount();
    };

    return MerchandisePage;

  })();

}).call(this);
(function() {
  App.LandingPage.PanelUpsellPage = (function() {
    function PanelUpsellPage() {}

    PanelUpsellPage.prototype.setup = function() {
      var postCandidateViewData, splitAmountEvenly;
      App.moduloAmount = function(n, m) {
        var d_0, d_1, v_0;
        v_0 = Math.floor(n / m);
        d_0 = n % m;
        d_1 = m - d_0;
        return {
          v_0: v_0 + 1,
          d_0: d_0,
          v_1: v_0,
          d_1: d_1
        };
      };
      App.splitEvenlyAmount = function(split_evenly, modulo_amount_cents, total_amount_cents, percentage) {
        var return_amount_cents;
        return_amount_cents = modulo_amount_cents;
        if (!split_evenly) {
          return_amount_cents = parseInt(percentage * total_amount_cents);
        }
        return App.toFloatDollars(return_amount_cents);
      };
      App.checkMaximumContribution = function(item, dollar_value) {
        var dollar_value_cents, maximum_contribution_cents, return_dollar_value;
        maximum_contribution_cents = $(item).data('maximum-contribution-cents');
        dollar_value_cents = App.toIntCents(dollar_value);
        return_dollar_value = dollar_value;
        if (maximum_contribution_cents > 0 && dollar_value_cents > maximum_contribution_cents) {
          return_dollar_value = App.toFloatDollars(maximum_contribution_cents);
          $(item).attr('maxed', 'true');
        } else {
          $(item).attr('maxed', 'false');
        }
        return return_dollar_value;
      };
      postCandidateViewData = function(candidate_item) {
        var view_data;
        if ($('[data-toggle=scrollable]').length > 0) {
          view_data = {
            upsell_state_uid: $('#card-view-target').data('upsell-state-uid'),
            scrollable_uid: $(candidate_item).find('.scrollable-item-id').val()
          };
          return $.ajax({
            url: $('#card-view-target').data('url'),
            dataType: 'JSON',
            xhrFields: {
              withCredentials: true
            },
            data: view_data,
            type: 'POST',
            success: function(response) {},
            error: function(response, error) {}
          });
        }
      };
      splitAmountEvenly = function(parent, entered_amount) {
        var $candidate_count, $conduit_candidate_amount_fields, $entered_value_cents, distributed_amount, result, split_evenly;
        split_evenly = parent.find('.conduit-candidates').data('split-evenly');
        distributed_amount = 0.0;
        $conduit_candidate_amount_fields = parent.find('.conduit-candidate-amount-field');
        $candidate_count = $conduit_candidate_amount_fields.length;
        $entered_value_cents = entered_amount;
        result = App.moduloAmount($entered_value_cents, $candidate_count);
        $.each($conduit_candidate_amount_fields.slice(0, result.d_0), function(index, item) {
          var dollar_value;
          dollar_value = App.splitEvenlyAmount(split_evenly, result.v_0, $entered_value_cents, $(item).data('default-percentage'));
          dollar_value = App.checkMaximumContribution($(item), dollar_value);
          distributed_amount += dollar_value;
          return $(item).val(dollar_value).prop('value', dollar_value);
        });
        return $.each($conduit_candidate_amount_fields.slice(result.d_0, $candidate_count), function(index, item) {
          var dollar_value;
          dollar_value = App.splitEvenlyAmount(split_evenly, result.v_1, $entered_value_cents, $(item).data('default-percentage'));
          dollar_value = App.checkMaximumContribution($(item), dollar_value);
          distributed_amount += dollar_value;
          return $(item).val(dollar_value).prop('value', dollar_value);
        });
      };
      $('body').on('click', '.other-amount-button', function(item) {
        var $conduit_candidate_callout, $current_row, $other_row, $parent;
        $parent = $(this).parents('.upsell-card').first();
        $current_row = $parent.find('.amounts-row');
        $other_row = $parent.find('.other-amount-row');
        $conduit_candidate_callout = $parent.find('.conduit-candidate-callout');
        $conduit_candidate_callout.removeClass('d-flex').addClass('d-none');
        $current_row.removeClass('d-flex').addClass('d-none');
        $other_row.addClass('d-flex').removeClass('d-none');
        $other_row.find('input.other-amount-field').focus();
        return $other_row.find('input.other-amount-field').attr('required', 'required');
      });
      $('body').on('keyup', '.other-amount-field', function(item) {
        var $amount_field, $parent, $submit, entered_amount, max_dollars, maximum_allowed;
        $parent = $(this).parents('.upsell-card').first();
        $submit = $parent.find('.other-amount-button.submit-amount-option');
        $amount_field = $parent.find('.card-donation-amount');
        maximum_allowed = App.toIntCents($(this).attr("max"));
        entered_amount = App.toIntCents($(this).val());
        splitAmountEvenly($parent, entered_amount);
        if (entered_amount > maximum_allowed) {
          max_dollars = App.toFloatDollars(maximum_allowed);
          $(this).val(max_dollars);
          return $amount_field.val(max_dollars);
        } else {
          return $amount_field.val($(this).val());
        }
      });
      $('body').on('click', '.suggested-amount', function(item) {
        var $amount_field, $current_row, $error_row, $loading_row, $other_amount, $other_row, $parent, amount;
        item.preventDefault();
        amount = parseFloat($(this).data('amount'));
        if (amount > 0) {
          $parent = $(this).parents('.upsell-card').first();
          $other_amount = $parent.find('.other-amount-field');
          $amount_field = $parent.find('.card-donation-amount');
          $amount_field.val(amount);
          $current_row = $parent.find('.amounts-row');
          $other_row = $parent.find('.other-amount-row');
          $loading_row = $parent.find('.loading-row');
          $error_row = $parent.find('.error-row');
          $error_row.removeClass('d-flex').addClass('d-none');
          $current_row.removeClass('d-flex').addClass('d-none');
          $other_row.removeClass('d-flex').addClass('d-none');
          $loading_row.removeClass('d-none').addClass('d-flex');
          return $(this).parents('form').submit();
        }
      });
      $('body').on('click', '.submit-amount-button', function(item) {
        var $amount_field, $conduit_candidate_amount_fields, $conduit_candidates, $error_row, $loading_row, $other_row, $parent, $submit_amount_row, redistributed_amount;
        if ($(this).data('disabled') === 'disabled') {
          return;
        }
        item.preventDefault();
        $(this).addClass('selected');
        $parent = $(this).parents('.upsell-card').first();
        $amount_field = $parent.find('.card-donation-amount');
        $error_row = $parent.find('.error-row');
        $loading_row = $parent.find('.loading-row');
        $submit_amount_row = $parent.find('.submit-amount-row');
        $conduit_candidates = $parent.find('.conduit-candidates');
        $conduit_candidate_amount_fields = $parent.find('.conduit-candidate-amount-field');
        $other_row = $parent.find('.other-amount-row');
        redistributed_amount = 0;
        $.each($conduit_candidate_amount_fields, function(index, item) {
          var current_value_cents;
          current_value_cents = App.toIntCents($(item).val());
          return redistributed_amount += App.toFloatDollars(current_value_cents);
        });
        $amount_field.val(redistributed_amount);
        if (redistributed_amount === 0) {
          return;
        }
        $loading_row.removeClass('d-none').addClass('d-flex');
        $error_row.removeClass('d-flex').addClass('d-none');
        $submit_amount_row.removeClass('d-flex').addClass('d-none');
        $conduit_candidates.removeClass('d-flex').addClass('d-none');
        $other_row.removeClass('d-flex').addClass('d-none');
        $(this).trigger('donation.upsell.amount.selected');
        return $(this).parents('form').submit();
      });
      $('body').on('click', '.submit-amount-option', function(item) {
        var $amount_field, $conduit_candidate_callout, $current_row, $decline_row, $error_row, $loading_row, $other_amount, $other_row, $parent;
        if ($(this).data('disabled') === 'disabled') {
          return;
        }
        item.preventDefault();
        $(this).addClass('selected');
        $parent = $(this).parents('.upsell-card').first();
        $other_amount = $parent.find('.other-amount-field');
        $amount_field = $parent.find('.card-donation-amount');
        $error_row = $parent.find('.error-row');
        $conduit_candidate_callout = $parent.find('.conduit-candidate-callout');
        $amount_field.val($(this).data('amount') || $other_amount.val());
        if (($amount_field[0] && $amount_field.val().length > 0) || $(this).data('immediate-submit') === true) {
          $current_row = $parent.find('.amounts-row');
          $other_row = $parent.find('.other-amount-row');
          $loading_row = $parent.find('.loading-row');
          $decline_row = $parent.find('.decline-row');
          $error_row.removeClass('d-flex').addClass('d-none');
          $current_row.removeClass('d-flex').addClass('d-none');
          $other_row.removeClass('d-flex').addClass('d-none');
          if ($decline_row) {
            $decline_row.removeClass('d-flex').addClass('d-none');
          }
          $conduit_candidate_callout.removeClass('d-flex').addClass('d-none');
          $loading_row.removeClass('d-none').addClass('d-flex');
          splitAmountEvenly($parent, App.toIntCents($amount_field.val()));
          $(this).trigger('donation.upsell.amount.selected');
          return $(this).parents('form').submit();
        }
      });
      $('.panel-type-upsell').find('form').on('ajax:success', function(e, data, status, xhr) {
        var $completed_row, $form, $formId, $loading_row, $parent;
        $parent = $(this).parents('.upsell-card').first();
        $form = $('form', $parent);
        $formId = $form.attr('id');
        $loading_row = $parent.find('.loading-row');
        $completed_row = $parent.find('.completed-row');
        $loading_row.removeClass('d-flex').addClass('d-none');
        $completed_row.removeClass('d-none').addClass('d-flex');
        $parent.append('<input type="hidden" class="charge_revv_uid" value="' + data.charge_revv_uid + '"/>');
        $parent.trigger('donation.upsell.submitted');
        if ($formId === 'new_double_upsell') {
          new App.DataLayer().push({
            'event': 'eec.purchase',
            'category': 'panel double upsell',
            'form': $form,
            'action': 'purchase'
          });
        }
        if ($formId === 'new_donation_upsell') {
          new App.DataLayer().push({
            'event': 'eec.purchase',
            'category': 'panel donation upsell',
            'form': $form,
            'action': 'purchase'
          });
        }
        if ($formId === 'new_conduit_panel_upsell') {
          new App.DataLayer().push({
            'event': 'eec.purchase',
            'category': 'panel conduit upsell',
            'form': $form,
            'action': 'purchase'
          });
        }
        if ($formId === 'new_money_bomb_upsell') {
          new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': 'panel pledge upsell',
            'form': $form,
            'action': 'purchase'
          });
        }
        if ($formId === 'new_password_upsell') {
          new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': 'panel password upsell',
            'form': $form,
            'action': 'lead'
          });
        }
        if ($formId === 'new_mobile_phone_upsell') {
          new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': 'panel mobile phone upsell',
            'form': $form,
            'action': 'lead'
          });
        }
        if ($formId === 'new_recurring_upsell') {
          return new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': 'panel recurring upsell',
            'form': $form,
            'action': 'purchase'
          });
        }
      }).on('ajax:error', function(e, xhr, status, error) {
        var $amounts_row, $current_row, $error_row, $loading_row, $parent, data;
        data = xhr.responseText;
        $parent = $(this).parents('.upsell-card').first();
        $loading_row = $parent.find('.loading-row');
        $error_row = $parent.find('.error-row');
        $loading_row.removeClass('d-flex').addClass('d-none');
        $error_row.removeClass('d-none').addClass('d-flex');
        $error_row.find('.error-callout').html(data);
        $current_row = $parent.find('.amounts-row');
        $current_row.removeClass('d-none').addClass('d-flex');
        $amounts_row = $parent.find('.amounts-row');
        return $amounts_row.removeClass('d-flex').addClass('d-none');
      });
      $('body').on('scrollable.visible', function(item) {
        var _this;
        _this = item.target;
        if ($(_this).parents('.upsell-card').data('view') !== 'registered') {
          $(_this).parents('.upsell-card').data('view', 'registered');
          return postCandidateViewData($(_this).parents('.upsell-card'));
        }
      });
      (function() {
        return $('[data-toggle=scrollable]').each(function() {
          if ($(this).isInViewport() && $(this).parents('.upsell-card').data('view') !== 'registered') {
            $(this).parents('.upsell-card').data('view', 'registered');
            return postCandidateViewData($(this).parents('.upsell-card'));
          }
        });
      })();
      return $('.conduit-edit-button').on('click', function(item) {
        var $amounts_row, $candidate_count, $conduit_candidate_callout, $conduit_candidate_index, $conduit_candidates, $other_row, $parent, $submit_amount_button, $submit_amount_row;
        $parent = $(this).parents('.upsell-card').first();
        $submit_amount_row = $parent.find('.submit-amount-row');
        $amounts_row = $parent.find('.amounts-row');
        $other_row = $parent.find('.other-amount-row');
        $submit_amount_button = $parent.find('submit-amount-button');
        $conduit_candidate_callout = $parent.find('.conduit-candidate-callout');
        $conduit_candidate_index = $parent.find('#conduit-candidate-index');
        $conduit_candidates = $parent.find('.conduit-candidates');
        $candidate_count = $parent.find('.conduit-candidate-amount-field').length;
        $amounts_row.removeClass('d-flex').addClass('d-none');
        $other_row.removeClass('d-flex').addClass('d-none');
        $submit_amount_row.removeClass('d-none').addClass('d-flex');
        $conduit_candidate_callout.removeClass('d-flex').addClass('d-none');
        if ($candidate_count > 2) {
          $conduit_candidates.addClass('vertical-scroll-candidates');
        }
        $conduit_candidate_index.toggleClass('d-none');
        $(this).toggleClass('d-none');
        return item.preventDefault();
      });
    };

    return PanelUpsellPage;

  })();

}).call(this);
(function() {
  App.LandingPage.PaymentRequest = (function() {
    function PaymentRequest() {}

    PaymentRequest.prototype.setup = function() {
      var business_donations, button_enabled, organization_name, pac_donations, paymentRequestClass;
      organization_name = $('meta[name=organization-name]').attr('content');
      button_enabled = $('meta[name=page-payment-request-button-enabled]').attr('content');
      business_donations = $('meta[name=enable-business-donations]').attr('content');
      pac_donations = $('meta[name=enable-pac-donations]').attr('content');
      App.paymentRequestEnabled = false;
      if (business_donations === 'true' || pac_donations === 'true') {
        return;
      }
      if (button_enabled !== 'true') {
        return;
      }
      if (organization_name.indexOf("WinRed") >= 0) {
        organization_name = 'Donation via ' + organization_name;
      }
      if ($("meta[name=stripe-publishable-key]").length === 0 && $('meta[name=page-payment-request-button-enabled-id]').attr('content').length === 0) {
        return;
      }
      App.paymentRequestClass = paymentRequestClass = $("meta[name=stripe-publishable-key]").length !== 0 ? Stripe($("meta[name=stripe-publishable-key]").attr('content')) : new Revv($('meta[name=page-payment-request-button-enabled-id]').attr('content'));
      App.currentPrButton = void 0;
      App.requestShipping = function() {
        return $('.merchandise-campaign-callout').data('shipping-required') || $('.rv-type-checkout-page').length !== 0;
      };
      App.shippingOptions = function() {
        if (App.requestShipping()) {
          return [
            {
              id: 'merchandise-shipping',
              label: 'Shipping',
              detail: $('.merchandise-campaign-callout').data('shipping-disclaimer') || $('.shipping-disclaimer').text(),
              amount: 0
            }
          ];
        } else {
          return [];
        }
      };
      App.setupPaymentRequest = function() {
        var amount, elements, initial_amount, paymentRequest, prButton;
        App.paymentRequestObject = void 0;
        App.paymentRequestEnabled = false;
        $('.stripe-card-token-field').val('');
        amount = $('#donation_amount').val() || $('#conduit_amount').val() || parseFloat($.urlParam('amount'));
        if (isNaN(amount)) {
          amount = 0;
        }
        initial_amount = parseInt(parseFloat(amount * 100).toFixed(0));
        paymentRequest = paymentRequestClass.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: organization_name,
            amount: initial_amount
          },
          requestShipping: App.requestShipping(),
          shippingOptions: App.shippingOptions(),
          requestPayerName: true,
          requestPayerEmail: true,
          requestPayerPhone: true
        });
        elements = paymentRequestClass.elements();
        prButton = elements.create('paymentRequestButton', {
          paymentRequest: paymentRequest,
          style: {
            paymentRequestButton: {
              type: 'donate',
              theme: 'dark',
              height: '52px'
            }
          }
        });
        prButton.on('click', function(ev) {
          var form, form_validator, valid;
          $('.stripe-card-token-field').val('');
          if (paymentRequest._isShowing) {
            ev.preventDefault();
            return;
          }
          form = $('form[id^=new_]')[0];
          valid = true;
          form_validator = $(form).validate();
          $(form).find('input:visible, select:visible, textarea:visible, input[type=hidden]').each(function() {
            valid = form_validator.element(this) && valid;
            return true;
          });
          App.addValidationErrors(form_validator, form);
          if (valid) {
            if (App.paymentRequestClass.manualDisplay) {
              App.paymentRequestClass.show();
            }
            return true;
          } else {
            return ev.preventDefault();
          }
        });
        App.currentPrButton = prButton;
        paymentRequest.canMakePayment().then(function(result) {
          console.log('canMakePayment.then', result);
          if (result && $('#payment-request-button').length !== 0) {
            App.paymentRequestEnabled = true;
            if (prButton.mount !== void 0) {
              prButton.mount('#payment-request-button');
            } else {
              prButton.addClass('apple-pay-button');
            }
            $('#payment-request-button-or').removeClass('d-none');
            $('#single-payment-request-continue-button').removeClass('d-none');
            $('.payment-request-details').css('display', 'block');
            $('.payment-request-details').find('.employment-details.d-none').removeClass('d-none');
            if ($('body .rv-steps-multi').length !== 0) {
              $('.btn-submit[data-toggle=wizard][data-target="#personal"]').text('Continue with credit card');
              if ($('body.with-temporary-profile').length === 0) {
                $(':not(.payment-request-details) > .additional-field-group:not(.merchandise-campaign--additional-field-group):not(:visible)').remove();
              }
            }
            if ($('body .rv-steps-single').length !== 0 && $('body.with-temporary-profile').length === 0) {
              $('.landing-page-details').hide();
            }
            if ($('body .rv-steps-single').length !== 0 && $('body.with-temporary-profile').length !== 0) {
              $('#single-payment-request-continue-button').hide();
            }
            if ($('.rv-error-text').first().children("li").length > 0) {
              $('.employment-details:not(:visible)').find('input[type=text], input[type=number], select').removeProp('required').attr('disabled', false);
            } else {
              $('.employment-details:not(:visible)').find('input[type=text], input[type=number], select').removeProp('required').attr('disabled', true);
            }
            $('.phone-line').find('input[type=text], input[type=number], select').attr('disabled', true);
            $('#donation_amount').on('paymentRequestChange', function() {
              var new_amount;
              new_amount = parseInt(parseFloat($(this).val() * 100).toFixed(0));
              if (new_amount > 49) {
                paymentRequest.update({
                  total: {
                    label: organization_name,
                    amount: new_amount
                  }
                });
                return $('.payment-request-details').css('display', 'block');
              } else {
                return $('.payment-request-details').css('display', 'none');
              }
            });
            if (App.updateVariantAmount) {
              return App.updateVariantAmount();
            }
          } else {
            return $('.payment-request-details').remove();
          }
        });
        paymentRequest.on('shippingaddresschange', function(ev) {
          var success;
          success = false;
          if (ev.shippingAddress) {
            if (ev.shippingAddress.city.trim().length > 0 && ev.shippingAddress.postalCode.trim().length > 0 && ev.shippingAddress.region.trim().length > 0) {
              success = true;
            }
          }
          if (success) {
            return ev.updateWith({
              status: 'success'
            });
          } else {
            return ev.updateWith({
              status: 'invalid_shipping_address'
            });
          }
        });
        paymentRequest.on('token', function(ev) {
          var $fix, $fix_ul, $row, $ul, address, billing_address, billing_city, billing_state, billing_zip, callout, card, city, errors, fail_message, firstName, form, form_validator, lastName, postalCode, state, valid;
          $('.stripe-card-token-field').val(ev.token.id);
          form = $('form[id^=new_]')[0];
          firstName = void 0;
          lastName = void 0;
          valid = true;
          fail_message = 'fail';
          errors = [];
          if (ev.shippingAddress && App.requestShipping()) {
            address = ev.shippingAddress.addressLine[0] || '';
            if (ev.shippingAddress.addressLine[1]) {
              address += ' ' + ev.shippingAddress.addressLine[1];
            }
            city = ev.shippingAddress.city || '';
            state = ev.shippingAddress.region || '';
            postalCode = ev.shippingAddress.postalCode || '';
            firstName = ev.shippingAddress.recipient.split(' ')[0] || '';
            lastName = ev.shippingAddress.recipient.split(' ')[1] || '';
            $('.shipping-address-block').find('.btn-shipping.btn-checkbox.active').find('[id$=_shipping_same_as_billing]').click();
            $('.shipping-address-block').find('[data-name=shipping_address]').val(address);
            $('.shipping-address-block').find('[data-name=shipping_city]').val(city);
            $('.shipping-address-block').find('[data-name=shipping_state]').val(state);
            if ($('.shipping-address-block').find('[data-name=shipping_state]').val() !== state) {
              $('.shipping-address-block').find('[data-name=shipping_state]').append('<option value="' + state + '">' + state + "</option");
              $('.shipping-address-block').find('[data-name=shipping_state]').val(state);
            }
            $('.shipping-address-block').find('[data-name=shipping_zip]').val(postalCode);
            if ($('.shipping-address-block').find('[data-name=shipping_zip]').val() !== postalCode && postalCode.length > 5) {
              $('.shipping-address-block').find('[data-name=shipping_zip]').val(postalCode.substring(0, 5));
            }
            $('.shipping-address-block').find('[data-name=shipping_first_name]').val(firstName);
            $('.shipping-address-block').find('[data-name=shipping_last_name]').val(lastName);
            if (address.trim().length === 0) {
              valid = false;
              errors.push('Shipping address');
            }
            if (city.trim().length === 0) {
              valid = false;
              errors.push('Shipping city');
            }
            if (state.trim().length === 0) {
              valid = false;
              errors.push('Shipping state');
            }
            if (postalCode.trim().length === 0) {
              valid = false;
              errors.push('Shipping zip');
            }
            if (firstName.trim().length === 0 || lastName.trim().length === 0) {
              valid = false;
              errors.push('Shipping name');
            }
          }
          if (ev.payerName.trim().length > 0) {
            firstName = ev.payerName.split(' ')[0];
            lastName = ev.payerName.split(' ')[1];
            if (firstName) {
              valid = valid && firstName.trim().length > 0;
              $(form).find('[id$=_first_name]:not([id$=shipping_first_name])').val(firstName);
            }
            if (lastName) {
              valid = valid && firstName.trim().length > 0;
              $(form).find('[id$=last_name]:not([id$=shipping_last_name])').val(lastName);
            }
          } else {
            valid = false;
            fail_message = 'invalid_payer_name';
          }
          if (ev.payerEmail.trim().length > 0) {
            $(form).find('[id$=_email]').val(ev.payerEmail);
          } else {
            if (valid) {
              valid = false;
              fail_message = 'invalid_payer_email';
            }
          }
          if (ev.payerPhone.trim().length > 0) {
            $(form).find('[id$=phone_number]').val(ev.payerPhone);
          } else {
            if (valid) {
              valid = false;
              fail_message = 'invalid_payer_phone';
            }
          }
          if (ev.token.card) {
            card = ev.token.card;
            billing_address = card.address_line1 || '';
            billing_city = card.address_city || '';
            billing_state = card.address_state || '';
            billing_zip = card.address_zip || '';
            $('.home-address').find('[data-name=address]').val(billing_address);
            $('.home-address').find('[data-name=city]').val(billing_city);
            $('.home-address').find('[data-name=state]').val(billing_state);
            if ($('.home-address').find('[data-name=state]').val() !== billing_state) {
              $('.home-address').find('[data-name=state]').append('<option value="' + billing_state + '">' + billing_state + "</option");
              $('.home-address').find('[data-name=state]').val(billing_state);
            }
            $('.home-address').find('[data-name=zip]').val(billing_zip);
            if ($('.home-address').find('[data-name=zip]').val() !== billing_zip && billing_zip.length > 5) {
              $('.home-address').find('[data-name=zip]').val(billing_zip.substring(0, 5));
            }
            if (billing_address.trim().length === 0) {
              valid = false;
              errors.push('Billing address');
            }
            if (billing_city.trim().length === 0) {
              valid = false;
              errors.push('Billing city');
            }
            if (billing_state.trim().length === 0) {
              valid = false;
              errors.push('Billing state');
            }
            if (billing_zip.trim().length === 0) {
              valid = false;
              errors.push('Billing zip');
            }
            if (card.name.trim().length === 0) {
              valid = false;
              errors.push('Billing name');
            }
          } else {
            valid = false;
          }
          form_validator = $(form).validate();
          $(form).find('input:visible, select:visible, textarea:visible, input[type=hidden]').each(function() {
            valid = form_validator.element(this) && valid;
            return true;
          });
          if (valid) {
            $('.loading').show();
            $('[id$=_from_payment_request_button]').val(true);
            $('[id$=_payment_request_type]').val(paymentRequest._activeBackingLibraryName);
            form.submit();
            if (ev.paymentResponse) {
              ev.paymentResponse.complete('success');
            } else {
              ev.complete('success');
            }
          } else {
            callout = paymentRequest._activeBackingLibraryName === 'APPLE_PAY' ? 'Your Apple Pay profile is invalid' : 'Your Pay profile is invalid';
            $('#revv-validation-errors').remove();
            $ul = $('<ul>').css('color', 'red');
            $ul.append($('<li>').text(callout));
            $fix = $('<li>').text('Please fix the following');
            $fix_ul = $('<ul>');
            $.each(errors, function(i) {
              var $li, message;
              message = errors[i];
              $li = $('<li>').text(message);
              return $fix_ul.append($li);
            });
            $fix.append($fix_ul);
            $ul.append($fix);
            $row = $('#payment-request-button').parents('.row').first();
            $row.before($('<div id="revv-validation-errors" class="row">').append($('<div class="col-md-12">').append($ul)));
            if (ev.paymentResponse) {
              ev.paymentResponse.complete(fail_message);
            } else {
              ev.complete(fail_message);
            }
            if (App.currentPrButton.unmount !== void 0) {
              App.currentPrButton.unmount();
              App.setupPaymentRequest();
            }
          }
        });
        return paymentRequest.on('cancel', function(ev) {
          $('.stripe-card-token-field').val(null);
          return $('.btn-submit[data-toggle=wizard][data-target="#personal"]').show();
        });
      };
      App.setupPaymentRequest();
      $('.merchandise-campaign-callout').on('merchandiseShippingRequiredChanged', function() {
        if (App.currentPrButton) {
          if (App.currentPrButton.unmount !== void 0) {
            App.currentPrButton.unmount();
            return App.setupPaymentRequest();
          } else {
            App.paymentRequestCurrentOptions['requestShipping'] = App.requestShipping();
            App.paymentRequestCurrentOptions['shippingOptions'] = App.shippingOptions();
            return App.createPaymentRequest();
          }
        }
      });
      $('body').on('click', '#single-payment-request-continue-button', function(e) {
        if (App.paymentRequestEnabled) {
          $('.landing-page-details').show();
          $.each($('.payment-request-details').find('.employment-details').find('input[type=text], input[type=number], select'), function() {
            return $('.landing-page-employment-details').find('.employment-details').find('#' + this.id).prop('required', 'required').removeAttr('disabled').val(this.value);
          });
          if ($('.payment-request-details').find('.employment-details').find('.employment-form--hide').length === 1) {
            $('.landing-page-employment-details').find('.employment-details').find('input[type=checkbox]').click();
          }
          $.each($('.payment-request-details').find('.phone-mask'), function() {
            return $('.landing-page-personal-details').find('#' + this.id).val(this.value);
          });
          if ($('.payment-request-details').find('.btn-mobile-optin.active').length === 1) {
            $('.landing-page-personal-details').find('.btn-mobile-optin').find('input[type=checkbox]').click();
          }
          $('#single-payment-request-continue-button').hide();
          $('.employment-details:not(:visible), .payment-request-phone-line').find('input[type=text], input[type=number], select').removeProp('required').attr('disabled', true);
          $('.phone-line[is-required=true]').find('input[type=text], input[type=number], select').prop('required', 'required');
          $('.phone-line').find('input[type=text], input[type=number], select').removeAttr('disabled');
          $('.employment-details:visible').find('input[type=text], input[type=number], select').prop('required', 'required').removeAttr('disabled');
          $('.payment-request-details').remove();
          return e.preventDefault();
        }
      });
      return $('body').on('click', 'a[data-toggle=wizard][data-target="#personal"]', function(e) {
        var current_tab, valid;
        if (App.paymentRequestEnabled) {
          $.each($('.payment-request-details').find('.employment-details').find('input[type=text], input[type=number], select'), function() {
            return $('.landing-page-employment-details').find('.employment-details').find('#' + this.id).prop('required', 'required').removeAttr('disabled').val(this.value);
          });
          if ($('.payment-request-details').find('.employment-details').find('.employment-form--hide').length === 1) {
            $('.landing-page-employment-details').find('.employment-details').find('input[type=checkbox]').click();
          }
          $.each($('.payment-request-details').find('.phone-mask'), function() {
            return $('.landing-page-personal-details').find('#' + this.id).val(this.value);
          });
          if ($('.payment-request-details').find('.btn-mobile-optin.active').length === 1) {
            $('.landing-page-personal-details').find('.btn-mobile-optin').find('input[type=checkbox]').click();
          }
          $('.payment-request-phone-line').find('input[type=text], input[type=number], select').removeProp('required').attr('disabled', true);
          $('.employment-details:not(:visible), .payment-request-phone-line').find('input[type=text], input[type=number], select').removeProp('required').attr('disabled', true);
          $('.phone-line').find('input[type=text], input[type=number], select').removeAttr('disabled');
          $('.payment-request-details').remove();
          if ($(this).data('validate')) {
            current_tab = $(this).parents('.tab-pane').first();
            valid = App.elementValidatorFor(current_tab);
            if (valid) {
              $.each($(this).data('target').split(' '), function() {
                return $('.nav-tabs a[href="' + this + '"]').tab('show');
              });
            }
          } else {
            $.each($(this).data('target').split(' '), function() {
              return $('.nav-tabs a[href="' + this + '"]').tab('show');
            });
          }
          $('.employment-details:visible').find('input[type=text], input[type=number], select').prop('required', 'required').removeAttr('disabled');
          return $('[id$=first_name]').focus();
        }
      });
    };

    PaymentRequest.prototype.create = function() {
      console.log('create revv object');
      return window.Revv = function(accountId) {
        var createPaymentRequest;
        this.accountId = accountId;
        this.manualDisplay = true;
        App.paymentRequestMethods = [
          {
            supportedMethods: 'https://apple.com/apple-pay',
            data: {
              version: 3,
              merchantIdentifier: accountId,
              merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
              supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
              countryCode: 'US'
            }
          }
        ];
        App.paymentRequestCurrentOptions = {
          requestPayerName: true,
          requestPayerEmail: true,
          requestPayerPhone: false,
          requestShipping: false,
          shippingOptions: []
        };
        App.paymentRequestKnownListeners = [];
        App.paymentRequestCurrentDetails = {};
        App.createPaymentRequest = createPaymentRequest = function() {
          var i, listener;
          console.log([App.paymentRequestMethods, App.paymentRequestCurrentDetails, App.paymentRequestCurrentOptions]);
          App.paymentRequestObject = new window.PaymentRequest(App.paymentRequestMethods, App.paymentRequestCurrentDetails, App.paymentRequestCurrentOptions);
          i = 0;
          while (i < App.paymentRequestKnownListeners.length) {
            listener = App.paymentRequestKnownListeners[i];
            App.paymentRequestObject.addEventListener(listener[0], listener[1]);
            i++;
          }
          return App.paymentRequestObject;
        };
        this.paymentRequest = function(hash) {
          App.paymentRequestCurrentDetails = {
            total: {
              label: hash['total']['label'],
              amount: {
                value: App.toFloatDollars(hash['total']['amount']),
                currency: hash['currency'] || 'USD'
              }
            }
          };
          App.paymentRequestCurrentOptions = {
            requestBillingAddress: true,
            requestPayerName: hash['requestPayerName'],
            requestPayerEmail: hash['requestPayerEmail'],
            requestPayerPhone: hash['requestPayerPhone'],
            requestShipping: hash['requestShipping'],
            shippingOptions: hash['shippingOptions']
          };
          App.createPaymentRequest();
          return {
            canMakePayment: function() {
              return App.paymentRequestObject.canMakePayment();
            },
            on: function(ev, callback) {
              App.paymentRequestKnownListeners.push([ev, callback]);
              return App.paymentRequestObject.addEventListener(ev, callback);
            },
            update: function(hash) {
              App.paymentRequestCurrentDetails = {
                total: {
                  label: hash['total']['label'],
                  amount: {
                    value: App.toFloatDollars(hash['total']['amount']),
                    currency: hash['currency'] || 'USD'
                  }
                }
              };
              return createPaymentRequest();
            }
          };
        };
        this.show = function() {
          App.paymentRequestObject.onmerchantvalidation = function(ev) {
            var data;
            data = {
              domain: window.location.hostname,
              account_id: accountId,
              display_name: App.paymentRequestCurrentDetails['total']['label']
            };
            return $.ajax({
              url: $('meta[name=donor-endpoint]').attr('content') + '_session',
              headers: {
                'X-Revv-Apikey': $("meta[name=api-key]").attr('content')
              },
              dataType: 'JSON',
              xhrFields: {
                withCredentials: true
              },
              data: data,
              type: 'POST',
              success: function(response) {
                return ev.complete(JSON.parse(response.session.response));
              }
            });
          };
          App.paymentRequestObject.onpaymentconfirmed = function(ev) {
            var data, firstName, lastName;
            firstName = ev.payerName.split(' ')[0];
            lastName = ev.payerName.split(' ')[1];
            data = {
              email: ev.payerEmail,
              first_name: firstName,
              last_name: lastName,
              token: ev.details.token
            };
            console.log(data);
            return $.ajax({
              url: $('meta[name=donor-endpoint]').attr('content'),
              headers: {
                'X-Revv-Apikey': $("meta[name=api-key]").attr('content'),
                'X-CSRF-Token': $("meta[name=donor-endpoint-token]").attr('content')
              },
              dataType: 'JSON',
              xhrFields: {
                withCredentials: true
              },
              data: data,
              type: 'POST',
              success: function(response) {
                var prEvent;
                $('.moov-account-id').val(response.account_id);
                $('.moov-account-card-id').val(response.payment_method_id);
                prEvent = new Event("token");
                prEvent.token = {
                  id: response.payment_method_id,
                  card: {
                    name: ev.payerName,
                    address_line1: ev.details.billingContact.addressLines[0],
                    address_city: ev.details.billingContact.locality,
                    address_state: ev.details.billingContact.administrativeArea,
                    address_zip: ev.details.billingContact.postalCode
                  }
                };
                prEvent.shippingAddress = ev.shippingAddress;
                prEvent.payerEmail = ev.payerEmail;
                prEvent.payerName = ev.payerName;
                prEvent.payerPhone = ev.payerPhone;
                prEvent.paymentResponse = ev;
                return App.paymentRequestObject.dispatchEvent(prEvent);
              }
            });
          };
          return App.paymentRequestObject.show().then(function(ev) {
            return App.paymentRequestObject.onpaymentconfirmed(ev);
          })["catch"](function(err) {
            return App.createPaymentRequest();
          });
        };
        this.elements = function() {
          return {
            create: function(e) {
              return $('#payment-request-button');
            }
          };
        };
      };
    };

    return PaymentRequest;

  })();

}).call(this);
(function() {
  App.LandingPage.Profile = (function() {
    function Profile() {}

    Profile.prototype.setup = function() {
      App.existingProfile = void 0;
      App.storeProfileFields = function() {
        var existingProfile, formArray, i, json, name;
        formArray = $('.landing-page-personal-details input[data-name], .landing-page-employment-details input[data-name], .landing-page-personal-details select[data-name], .landing-page-employment-details select[data-name]');
        existingProfile = localStorage.getItem('__donorProfile');
        json = null;
        if (existingProfile) {
          App.existingProfile = json = JSON.parse(existingProfile);
        } else {
          json = {};
        }
        i = 0;
        while (i < formArray.length) {
          name = $(formArray[i]).data('name');
          if (Array.isArray(name)) {
            name = name[0];
          }
          if (formArray[i].value.length !== 0) {
            json[name] = formArray[i].value;
          }
          i++;
        }
        json['retired'] = $('.btn.btn-employment input[type=checkbox]').is(':checked');
        localStorage.setItem('__donorProfile', JSON.stringify(json));
        localStorage.setItem('__donorLastAmountAttempt', $('#donation_amount').val());
        if (json.firstname && (existingProfile && !JSON.parse(existingProfile)['firstname'])) {
          return new App.DataLayer().push({
            'event': app_platform + "Event",
            'category': App.dataLayerCategory(),
            'form': null,
            'action': 'temporary profile created',
            'label': ' ',
            'userAction': 't',
            'suppressTransactionID': true
          });
        }
      };
      App.profileElementValidatorFor = function(_this) {
        var valid;
        valid = true;
        $(_this).find('input[required], select[required], textarea[required]').each(function() {
          valid = valid && $(this).val().length !== 0;
          return true;
        });
        return valid;
      };
      App.resetForm = function() {
        localStorage.removeItem('__donorProfile');
        localStorage.setItem('__donorProfileSkip', 'true');
        return location.reload();
      };
      App.localStorageAvailable = function() {
        var e, test;
        test = 'test';
        try {
          localStorage.setItem(test, test);
          localStorage.removeItem(test);
          return true;
        } catch (error) {
          e = error;
          return false;
        }
        return true;
      };
      return (function() {
        var existingProfile, json, valid;
        if ($.urlParam('errors') && $.urlParam('errors').length !== 0) {
          return;
        }
        if (App.localStorageAvailable() === false) {
          return;
        }
        if ($('.rv-error-text').text().length !== 0) {
          localStorage.removeItem('__donorProfile');
          return;
        }
        existingProfile = localStorage.getItem('__donorProfile');
        if (existingProfile === null && $('.rv-type-checkout-page').length && $('meta[name="shopping-cart-profile"]').length && $('meta[name="shopping-cart-profile"]').attr('content').length && localStorage.getItem('__donorProfileSkip') === null) {
          existingProfile = $('meta[name="shopping-cart-profile"]').attr('content');
        }
        if (localStorage.getItem('__donorProfileSkip') !== null) {
          localStorage.removeItem('__donorProfileSkip');
        }
        if ($('.rv-entry-mode-true').length || $('meta[name=enable-pac-donations]').attr('content') === 'true' || $('meta[name=enable-business-donations]').attr('content') === 'true') {
          $('input, select').attr('autocomplete', 'off');
          return;
        }
        if (existingProfile) {
          json = JSON.parse(existingProfile);
          if (json['firstname']) {
            $('.welcome-donor-name').html(', ' + json['firstname']);
            $('.welcome-donor-reset').html('Not ' + json['firstname'] + '? Reset form here');
            $('.temporary-profiling-box').removeClass('d-none');
            $('.welcome-donor-reset-box').removeClass('d-none');
          }
          if (json['retired'] && !$('.btn.btn-employment input[type=checkbox]').is(':checked')) {
            $('.btn.btn-employment input[type=checkbox]').click();
          }
          if (json['zip']) {
            if (App.currentStripeCard) {
              App.currentStripeCard.update({
                value: {
                  postalCode: json['zip']
                }
              });
            }
            if (App.moovCardLink) {
              App.moovCardLink.billingAddress = {
                postalCode: json['zip']
              };
            }
          }
          $('.landing-page-personal-details input[data-name], .employment-details input[data-name], .landing-page-personal-details select[data-name], .employment-details select[data-name], .payment-request-details input[data-name], .payment-request-details select[data-name]').each(function() {
            var _this, name;
            _this = this;
            name = $(this).data('name');
            if (!Array.isArray(name)) {
              name = [name];
            }
            return name.forEach(function(current, index, array) {
              var value;
              value = json[current];
              if (value && value.length > 0) {
                return $(_this).val(value);
              }
            });
          });
          if ($('.rv-steps-single').length !== 0) {
            valid = App.profileElementValidatorFor($('.landing-page-personal-details'));
            valid = valid && App.profileElementValidatorFor($('.employment-details'));
            valid = valid && App.profileElementValidatorFor($('.additional-field-group'));
            if (valid) {
              $('body').addClass('with-temporary-profile');
              $('.landing-page-personal-details').addClass('d-none');
              $('.employment-details').addClass('d-none');
              return $('.temporary-profiling-input').val('true');
            }
          } else {
            valid = App.profileElementValidatorFor($('.landing-page-personal-details'));
            valid = valid && App.profileElementValidatorFor($('.employment-details'));
            valid = valid && App.profileElementValidatorFor($('.additional-field-group'));
            if (valid) {
              $('body').addClass('with-temporary-profile');
              $('[data-toggle=wizard][data-target="#personal"]').hide();
              $('#payment.tab-pane').addClass('active');
              return $('.temporary-profiling-input').val('true');
            }
          }
        }
      })();
    };

    return Profile;

  })();

}).call(this);
(function() {
  App.LandingPage.SubscribablePage = (function() {
    function SubscribablePage() {}

    SubscribablePage.prototype.setup = function() {
      $('.subscribable-option').on('click', function(e) {
        var amount, current_subscription_id, interval, is_subscribable_item_page, prorate, setting_id, text, upcomingData, url;
        interval = $(this).data('interval');
        setting_id = $(this).data('settingId');
        amount = $(this).data('amount');
        prorate = $(this).data('prorate');
        $("input[id*='subscribable_page_setting_id']").val(setting_id);
        $("input[id*='donor_selected_recurring_interval']").val(interval);
        $("input[id*='donation_amount']").val(amount);
        $("input[id*='proration_cost']").val('');
        current_subscription_id = $('.current-membership').data('subscription');
        upcomingData = {
          organization_uid: $("[name='organization-uid']").attr('content'),
          interval: interval,
          amount: amount * 100
        };
        if (current_subscription_id) {
          upcomingData['stripe_subscription_id'] = current_subscription_id;
          $("input[id*='stripe_subscription_id']").val(current_subscription_id);
          url = $('meta[name=preview-change-proration-domain]').attr('content');
          text = 'Total due today for changing membership:';
        } else if ((is_subscribable_item_page = $('.rv-type-subscribable-item-page').length || $('.upsell-page-subscribable-item-upsell').length)) {
          url = $('meta[name=preview-new-proration-domain]').attr('content');
          text = "Total due today for the rest of the " + interval + ":";
        }
        if (current_subscription_id || (is_subscribable_item_page && prorate === true)) {
          $('#donate-btn').attr('disabled', 'disabled');
          $('#accept-upsell-btn').attr('disabled', 'disabled');
          return $.ajax({
            url: url,
            dataType: 'JSON',
            xhrFields: {
              withCredentials: true
            },
            data: upcomingData,
            type: 'GET',
            success: function(response) {
              var amount_to_show;
              $('.donation-amount-label').text(text);
              amount_to_show = response.cost / 100 > 0 ? response.cost : 0;
              $('.donation-amount-value').text(amount_to_show / 100).formatCurrency({
                roundToDecimalPlace: 2,
                removeTrailingZerosOnDecimal: true
              });
              $("input[id*='proration_cost']").val(amount_to_show);
              $("input[id*='proration_date']").val(response.proration_date);
              $('#donate-btn').attr('disabled', false);
              return $('#accept-upsell-btn').attr('disabled', false);
            },
            error: function(response, error) {
              $('#donate-btn').attr('disabled', false);
              return $('#accept-upsell-btn').attr('disabled', false);
            }
          });
        }
      });
      if ($(".subscribable-option").length === 1) {
        return $(".subscribable-option")[0].click();
      }
    };

    return SubscribablePage;

  })();

}).call(this);
(function() {
  App.StripeIdToken = (function() {
    function StripeIdToken() {}

    StripeIdToken.prototype.setup = function() {
      var isValidSSN;
      isValidSSN = function(value, country) {
        var re;
        re = /^[0-9]{3}-[0-9]{2}-[0-9]{4}$/;
        if (country !== void 0 && country === 'CA') {
          re = /^[0-9]{3}-[0-9]{3}-[0-9]{3}$/;
        }
        return re.test(String(value));
      };
      return $('form').has('input[data-stripe=token]').on('submit', function(e) {
        var _this, stripe, stripe_pk;
        if ($("meta[name=stripe-publishable-key]").attr('content').length > 0) {
          stripe_pk = $("meta[name=stripe-publishable-key]").attr('content');
          _this = this;
          $('.ssn-error-text').removeClass('d-flex');
          if ($(_this).find('input[data-stripe=token]').length > 0 && $(_this).find('input[data-stripe=token]').val().length > 0) {
            stripe = Stripe(stripe_pk);
            e.preventDefault();
            return $(_this).find('input[data-stripe=token]').each(function() {
              var _this_input, country, fieldGroup, ssn;
              _this_input = this;
              country = $(_this_input).data('country');
              fieldGroup = $(_this_input).parents('.field-group');
              if ($(_this_input).val().length > 0) {
                if ($(_this_input).data('type') === 'ssn') {
                  ssn = $(_this_input).val();
                  if (isValidSSN(ssn, country)) {
                    if (country !== void 0 && country === 'CA') {
                      ssn = (ssn.substring(0, 3)) + "-" + (ssn.substring(4, 6)) + "-" + (ssn.substring(7, 9));
                    }
                    return stripe.createToken('pii', {
                      personal_id_number: ssn.replace(/\-/g, '')
                    }).then(function(result) {
                      var ssn_last_4;
                      if (fieldGroup.find('.ssn-last-4-extract').length > 0) {
                        ssn_last_4 = ssn.replace(/\-/g, '').slice(-4);
                        fieldGroup.find('.ssn-last-4-extract').val(ssn_last_4);
                        fieldGroup.find('input[type=hidden]:not(.ssn-last-4-extract)').val(result.token.id);
                      } else {
                        fieldGroup.find('input[type=hidden]').val(result.token.id);
                      }
                      return $(_this)[0].submit();
                    });
                  } else {
                    $('.ssn-error-text').addClass('d-flex');
                    return setTimeout(function() {
                      return $('form').find('input[type=submit]').removeAttr('disabled').prop("disabled", false);
                    }, 1000);
                  }
                }
              } else {
                return $(_this)[0].submit();
              }
            });
          }
        }
      });
    };

    return StripeIdToken;

  })();

}).call(this);
// This is a manifest file that'll be compiled into application.js, which will include all the files
// listed below.
//
// Any JavaScript/Coffee file within this directory, lib/assets/javascripts, vendor/assets/javascripts,
// or any plugin's vendor/assets/javascripts directory can be referenced here using a relative path.
//
// It's not advisable to add code directly here, but if you do, it'll appear at the bottom of the
// compiled file. JavaScript code in this file should be added after the last require_* statement.
//
// Read Sprockets README (https://github.com/rails/sprockets#sprockets-directives) for details
// about supported directives.
//





















//
// stubbed files below will NOT be included in the compiled application-landing-page.js
// this will improve performance and load less JS
//






















;
